import { DocumentTypeDecoration } from "@graphql-typed-document-node/core"
import {
  useMutation,
  useQuery,
  useSuspenseQuery,
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
  UseMutationOptions,
  UseQueryOptions,
  UseSuspenseQueryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
  UseSuspenseInfiniteQueryOptions,
} from "@tanstack/react-query"

import { publicFetcher } from "./fetcher"
import * as Types from "./public-types"

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>["__apiType"]>
  private value: string
  public __meta__?: Record<string, any> | undefined

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value)
    this.value = value
    this.__meta__ = __meta__
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value
  }
}
export const CompanyMergeHistoryRecordFragmentDoc = new TypedDocumentString(
  `
    fragment CompanyMergeHistoryRecord on CompanyMergeRecord {
  id
  absorbedName
  mergedAt
  source
  actor
  runId
  movedRows {
    table
    count
  }
  filledFields
  displayNameBefore
  displayNameAfter
  reversedAt
  reversalKind
  reversalReason
  reverseFirstId
}
    `,
  { fragmentName: "CompanyMergeHistoryRecord" },
)
export const UserRefFragmentDoc = new TypedDocumentString(
  `
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
    `,
  { fragmentName: "UserRef" },
)
export const BuildingOwnershipDetailFragmentDoc = new TypedDocumentString(
  `
    fragment BuildingOwnershipDetail on BuildingData {
  pid
  owners {
    id
    isPrimary
    hasRoofRights
    company {
      pid
      canonicalName
    }
  }
  reonomyPropertyDetails {
    fetchedAt
    owners {
      name
      isIndividual
      company {
        pid
        canonicalName
      }
      contacts {
        firstName
        lastName
        jobTitle
        emailAddresses
        phoneNumbers
        fullAddresses
      }
    }
  }
  latestAffiliationResult {
    payload
    modelName
    createdAt
    promptHash
    isCurrent
  }
  researchInFlight
  sitesWithContext {
    portfolio {
      pid
      title
    }
    org {
      pid
      name
    }
  }
  annotations {
    id
    createdAt
    noteText
    user {
      ...UserRef
    }
    companies {
      type
      company {
        pid
        canonicalName
      }
    }
  }
  ownershipLog {
    occurredAt
    source
    summary
    detail
    user {
      ...UserRef
    }
    companies {
      pid
      canonicalName
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BuildingOwnershipDetail" },
)
export const UtilityFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment UtilityFragment on Utility {
  id
  pid
  lseId
  name
  shortName
  numTariffs
  numCustomTariffs
  utilityApiName
  isPreferred
  notes
  states
  vnemSupported
  ftmBessProspectingSupported
  hasComplexSrecPricing
}
    `,
  { fragmentName: "UtilityFragment" },
)
export const SmallScenarioFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment SmallScenarioFragment on AdminScenario {
  id
  pid
  name
  dateCreated
  includeStorage
  isPrimary
  isMultifamily
  loadCategory
  solarLocation
  createdBy {
    ...UserRef
  }
  hiddenFromCustomers
  allowMulticonnect
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "SmallScenarioFragment" },
)
export const ScenarioFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment ScenarioFragment on AdminScenario {
  ...SmallScenarioFragment
  activeEtbReport {
    id
    projectName
  }
  helioscope {
    id
    pid
  }
}
    fragment SmallScenarioFragment on AdminScenario {
  id
  pid
  name
  dateCreated
  includeStorage
  isPrimary
  isMultifamily
  loadCategory
  solarLocation
  createdBy {
    ...UserRef
  }
  hiddenFromCustomers
  allowMulticonnect
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "ScenarioFragment" },
)
export const BasicOrgDetailsFragmentDoc = new TypedDocumentString(
  `
    fragment BasicOrgDetails on AdminOrg {
  id
  pid
  name
  slug
  rawSlug
  description
  logoUrl
  logomarkUrl
  hubspotId
}
    `,
  { fragmentName: "BasicOrgDetails" },
)
export const KwhFlowFragmentDoc = new TypedDocumentString(
  `
    fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
    `,
  { fragmentName: "KwhFlow" },
)
export const RevampSystemFragmentDoc = new TypedDocumentString(
  `
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
    `,
  { fragmentName: "RevampSystem" },
)
export const FinmodelCostFragmentDoc = new TypedDocumentString(
  `
    fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
    `,
  { fragmentName: "FinmodelCost" },
)
export const RevampFinmodelFragmentDoc = new TypedDocumentString(
  `
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}`,
  { fragmentName: "RevampFinmodel" },
)
export const AdminFinmodelDetailFragmentDoc = new TypedDocumentString(
  `
    fragment AdminFinmodelDetail on RevampFinmodel {
  __typename
  ...RevampFinmodel
  hasIntervalExport
  financing {
    id
    name
    type
    financierName
    financierPid
    isThirdParty
    status
  }
  scenario {
    pid
    name
    loadCategory
    includeStorage
    solarLocation
  }
  investorDetails {
    investorXlsxHref
    irrs {
      preTax
      postTaxItcDepr
    }
  }
  moreDetails {
    cashPurchaseNpv
    totalInstallCost
    pvCostPerWatt
    finmodelInputs
    finmodelOutputs
    luxId
    failureId
  }
  importReductionPct
}
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}`,
  { fragmentName: "AdminFinmodelDetail" },
)
export const UrdbUtilityFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment UrdbUtilityFragment on UrdbUtility {
  id
  eiaId
  name
  rateCount
  dateLastUpdated
  genabilityUtility {
    id
    name
  }
}
    `,
  { fragmentName: "UrdbUtilityFragment" },
)
export const UrdbRateFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment UrdbRateFragment on UrdbRate {
  id
  urdbId
  rateName
  sector
  approved
  isDefault
  effectiveDate
  endDate
  supersedesUrdbId
  hasEnergyRates
  hasTouEnergyRates
  hasTieredEnergyRates
  hasDemandRates
  hasFlatDemandRates
  fixedChargeFirstMeter
  fixedChargeUnits
  demandMin
  demandMax
  dgRules
}
    `,
  { fragmentName: "UrdbRateFragment" },
)
export const GenabilityMirrorUtilityRowFragmentDoc = new TypedDocumentString(
  `
    fragment GenabilityMirrorUtilityRow on GenabilityMirrorUtility {
  id
  lseId
  name
  code
  totalCustomers
  tariffCount
  fetchedAt
  dateCreated
  linkedUtility {
    id
    name
    shortName
  }
}
    `,
  { fragmentName: "GenabilityMirrorUtilityRow" },
)
export const GenabilityMirrorTariffRowFragmentDoc = new TypedDocumentString(
  `
    fragment GenabilityMirrorTariffRow on GenabilityMirrorTariff {
  id
  masterTariffId
  lseId
  tariffId
  name
  code
  customerClass
  utilityName
  revisionCount
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedTariff {
    id
    name
    code
  }
}
    `,
  { fragmentName: "GenabilityMirrorTariffRow" },
)
export const GenabilityMirrorRevisionRowFragmentDoc = new TypedDocumentString(
  `
    fragment GenabilityMirrorRevisionRow on GenabilityMirrorTariffRevision {
  id
  tariffId
  masterTariffId
  name
  code
  contentHash
  effectiveDate
  endDate
  payloadEffectiveDate
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedRevision {
    id
    tariffId
    startDate
  }
}
    `,
  { fragmentName: "GenabilityMirrorRevisionRow" },
)
export const CompanyDuplicateReviewGroupFragmentDoc = new TypedDocumentString(
  `
    fragment CompanyDuplicateReviewGroup on CompanyDuplicateGroup {
  id
  tier
  canonicalCompanyId
  proposedDisplayName
  merging
  hasPendingApproval
  stale
  lastSeenAt
  priority
  priorityPortfolios
  companies {
    id
    pid
    canonicalName
    createdAt
    aliases
    url
    orgPid
    orgName
    hubspotId
    reonomyOwners {
      id
      name
    }
    ownedBuildings
    leasedBuildings
    states
    possiblyTruncated
  }
  pairs {
    id
    companyAId
    companyBId
    conflictOrg
    conflictHubspot
    signals
    judge {
      verdict
      confidence
      relationship
      reasoning
    }
  }
}
    `,
  { fragmentName: "CompanyDuplicateReviewGroup" },
)
export const CompanyDuplicateRunSummaryFragmentDoc = new TypedDocumentString(
  `
    fragment CompanyDuplicateRunSummary on CompanyDuplicateRun {
  id
  dryRun
  status
  stage
  startedAt
  finishedAt
  pairsFormed
  pairsJudged
  pairsToJudge
  judgeFailures
  mergesDone
  mergeFailures
  errorMessage
  costUsd
  triggeredBy
  note
  autoMergeThreshold
  deterministicAutoMerge
  anchoredAutoMergeFloor
  pairsPerTier {
    label
    count
  }
  failedMerges {
    pairId
    error
  }
}
    `,
  { fragmentName: "CompanyDuplicateRunSummary" },
)
export const SiteLabelRefFragmentDoc = new TypedDocumentString(
  `
    fragment SiteLabelRef on SiteLabel {
  pid
  name
  themeColor
  isGlobal
  sites
}
    `,
  { fragmentName: "SiteLabelRef" },
)
export const SiteLabelDetailFragmentDoc = new TypedDocumentString(
  `
    fragment SiteLabelDetail on SiteLabel {
  ...SiteLabelRef
  createdBy {
    ...UserRef
  }
  perms {
    canAdd
    canRemove
  }
}
    fragment SiteLabelRef on SiteLabel {
  pid
  name
  themeColor
  isGlobal
  sites
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "SiteLabelDetail" },
)
export const PolicyBriefFragmentDoc = new TypedDocumentString(
  `
    fragment PolicyBrief on Policy {
  pid
  name
  publicName
  description
  publicDescription
  sources {
    text
    url
  }
  userVisible
  application {
    status
  }
}
    `,
  { fragmentName: "PolicyBrief" },
)
export const DecodedRateFragmentDoc = new TypedDocumentString(
  `
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
    `,
  { fragmentName: "DecodedRate" },
)
export const BillCostItemFragmentDoc = new TypedDocumentString(
  `
    fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}
    `,
  { fragmentName: "BillCostItem" },
)
export const BillSectionFragmentDoc = new TypedDocumentString(
  `
    fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...BillCostItem
  }
}
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}`,
  { fragmentName: "BillSection" },
)
export const FormattedBillFragmentDoc = new TypedDocumentString(
  `
    fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...BillCostItem
  }
}
fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}`,
  { fragmentName: "FormattedBill" },
)
export const SiteBauMeterFragmentDoc = new TypedDocumentString(
  `
    fragment SiteBauMeter on BauMeter {
  id
  pid
  userVisibleLabel
  loadOrigin
  loadCategory
  formattedBill {
    ...FormattedBill
  }
  tariff {
    id
    name
    code
  }
  rollup {
    season
    days
    numbers
  }
  intervals {
    maxKw
    year
    units
    totalKwh
  }
  tariffUsdPerKwh
  totalCost
  importsKwh
}
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...BillCostItem
  }
}
fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}
fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}`,
  { fragmentName: "SiteBauMeter" },
)
export const SiteMeterSetFragmentDoc = new TypedDocumentString(
  `
    fragment SiteMeterSet on RevampSite {
  meterSet {
    intervalsBauXlsxHref
    loadKwh
    loadMaxKw
    loadOrigin
    totalCost
    rollup {
      season
      days
      numbers
    }
    meters {
      ...SiteBauMeter
    }
  }
}
    fragment SiteBauMeter on BauMeter {
  id
  pid
  userVisibleLabel
  loadOrigin
  loadCategory
  formattedBill {
    ...FormattedBill
  }
  tariff {
    id
    name
    code
  }
  rollup {
    season
    days
    numbers
  }
  intervals {
    maxKw
    year
    units
    totalKwh
  }
  tariffUsdPerKwh
  totalCost
  importsKwh
}
fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...BillCostItem
  }
}
fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}
fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}`,
  { fragmentName: "SiteMeterSet" },
)
export const BatteryGroundLeaseFieldsFragmentDoc = new TypedDocumentString(
  `
    fragment BatteryGroundLeaseFields on BatteryGroundLease {
  annualRentUsd
  ratePerKwhPerYear
  energyKwh
  leasedSqft
  termYears
  escalator
  projections {
    __typename
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
    `,
  { fragmentName: "BatteryGroundLeaseFields" },
)
export const FootprintGeometryFragmentDoc = new TypedDocumentString(
  `
    fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
    `,
  { fragmentName: "FootprintGeometry" },
)
export const SiteMinimalFieldsFragmentDoc = new TypedDocumentString(
  `
    fragment SiteMinimalFields on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  portfolioManager
  numParkingSpaces
  buildingType
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  batteryMarketAvailable
  batteryGroundLease {
    ...BatteryGroundLeaseFields
  }
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  occupancyType
  leaseType
  tenantLeaseTerm
  buildingData {
    yearRenovated
    parentCompany
  }
  footprint {
    ...FootprintGeometry
  }
  thirdPartySupplier {
    pid
    name
  }
  targetUtility {
    lseId
    name
    pid
  }
}
    fragment BatteryGroundLeaseFields on BatteryGroundLease {
  annualRentUsd
  ratePerKwhPerYear
  energyKwh
  leasedSqft
  termYears
  escalator
  projections {
    __typename
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}`,
  { fragmentName: "SiteMinimalFields" },
)
export const ShineStatsFragmentDoc = new TypedDocumentString(
  `
    fragment ShineStats on ShineStats {
  numProperties
  pvKwInstalled
  totalCo2ReducedTonnes
  totalGrossRevenue
  totalPvProductionKwh
}
    `,
  { fragmentName: "ShineStats" },
)
export const ShineUnitFragmentDoc = new TypedDocumentString(
  `
    fragment ShineUnit on ShineUnit {
  duration
  start
  actualPvProductionKwh
  expectedPvProductionKwh
  actualGrossRevenue
  expectedGrossRevenue
  co2EmissionsReductionTonnes
}
    `,
  { fragmentName: "ShineUnit" },
)
export const CsvImportLabelsFragmentDoc = new TypedDocumentString(
  `
    fragment CsvImportLabels on CsvImport {
  pid
  labels {
    pid
    themeColor
    name
  }
}
    `,
  { fragmentName: "CsvImportLabels" },
)
export const FancyFloatFragmentDoc = new TypedDocumentString(
  `
    fragment FancyFloat on FancyFloat {
  source
  number
}
    `,
  { fragmentName: "FancyFloat" },
)
export const ProviderFragmentDoc = new TypedDocumentString(
  `
    fragment Provider on Provider {
  id
  createdAt
  user {
    ...UserRef
  }
  document {
    pid
    fileName
    contentType
    downloadHref
    viewHref
  }
  espmConsumption {
    id
    createdAt
    lastUpdatedAt
    externalConsumptionId
    externalData
    start
    end
    usageKwh
    costUsd
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "Provider" },
)
export const BillingMonthViewFragmentDoc = new TypedDocumentString(
  `
    fragment BillingMonthView on BillingMonthView {
  id
  start
  end
  usageKwh {
    ...FancyFloat
  }
  peakKw {
    ...FancyFloat
  }
  totalCostUsd {
    ...FancyFloat
  }
  supplyCostUsd {
    ...FancyFloat
  }
  transmissionCostUsd {
    ...FancyFloat
  }
  provider {
    ...Provider
  }
  supplyProvider {
    ...Provider
  }
}
    fragment FancyFloat on FancyFloat {
  source
  number
}
fragment Provider on Provider {
  id
  createdAt
  user {
    ...UserRef
  }
  document {
    pid
    fileName
    contentType
    downloadHref
    viewHref
  }
  espmConsumption {
    id
    createdAt
    lastUpdatedAt
    externalConsumptionId
    externalData
    start
    end
    usageKwh
    costUsd
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BillingMonthView" },
)
export const GroupedLineItemFragmentDoc = new TypedDocumentString(
  `
    fragment GroupedLineItem on GroupedLineItem {
  cost
  usage
  name
  rate
  group
  unit
}
    `,
  { fragmentName: "GroupedLineItem" },
)
export const BillGroupFragmentDoc = new TypedDocumentString(
  `
    fragment BillGroup on BillGroup {
  group
  items {
    ...GroupedLineItem
  }
}
    fragment GroupedLineItem on GroupedLineItem {
  cost
  usage
  name
  rate
  group
  unit
}`,
  { fragmentName: "BillGroup" },
)
export const BillingMeterProjectedFragmentDoc = new TypedDocumentString(
  `
    fragment BillingMeterProjected on Projected {
  year
  annualKwh
  peakKw
  annualCostUsd
  monthlyUsageKwh
  monthlyCostUsd
  kwh8760 {
    timeZone
    start
    year
  }
  costAndKwhDownloadHref
  tariff {
    id
    pid
    name
    code
  }
  monthlyBills {
    start
    end
    sections {
      ...BillGroup
    }
  }
}
    fragment BillGroup on BillGroup {
  group
  items {
    ...GroupedLineItem
  }
}
fragment GroupedLineItem on GroupedLineItem {
  cost
  usage
  name
  rate
  group
  unit
}`,
  { fragmentName: "BillingMeterProjected" },
)
export const BillingIntervalViewFragmentDoc = new TypedDocumentString(
  `
    fragment BillingIntervalView on BillingIntervalView {
  id
  start
  end
  timeZone
  units
  numbers
  timestamps
  document {
    id
    pid
    fileName
    downloadHref
  }
}
    `,
  { fragmentName: "BillingIntervalView" },
)
export const BillingMeterFragmentDoc = new TypedDocumentString(
  `
    fragment BillingMeter on BillingMeter {
  id
  pid
  view {
    name
    utilityMeterId
    supplyUtilityMeterId
    buildingType
    loadCategory
    isDisabled
    isArchived
    utility {
      pid
      name
      shortName
    }
    tariff {
      id
      pid
      name
      code
    }
    tenantDetails {
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    numBills
    numIntervals
    totalAnnualKwh {
      ...FancyFloat
    }
    peakAnnualKw {
      ...FancyFloat
    }
    weeklyRollup
    months {
      ...BillingMonthView
    }
    calc2 {
      __typename
      ... on E2Error {
        msg
      }
      ... on Projected {
        ...BillingMeterProjected
      }
    }
    intervals {
      ...BillingIntervalView
    }
  }
}
    fragment BillingMeterProjected on Projected {
  year
  annualKwh
  peakKw
  annualCostUsd
  monthlyUsageKwh
  monthlyCostUsd
  kwh8760 {
    timeZone
    start
    year
  }
  costAndKwhDownloadHref
  tariff {
    id
    pid
    name
    code
  }
  monthlyBills {
    start
    end
    sections {
      ...BillGroup
    }
  }
}
fragment BillGroup on BillGroup {
  group
  items {
    ...GroupedLineItem
  }
}
fragment GroupedLineItem on GroupedLineItem {
  cost
  usage
  name
  rate
  group
  unit
}
fragment BillingIntervalView on BillingIntervalView {
  id
  start
  end
  timeZone
  units
  numbers
  timestamps
  document {
    id
    pid
    fileName
    downloadHref
  }
}
fragment BillingMonthView on BillingMonthView {
  id
  start
  end
  usageKwh {
    ...FancyFloat
  }
  peakKw {
    ...FancyFloat
  }
  totalCostUsd {
    ...FancyFloat
  }
  supplyCostUsd {
    ...FancyFloat
  }
  transmissionCostUsd {
    ...FancyFloat
  }
  provider {
    ...Provider
  }
  supplyProvider {
    ...Provider
  }
}
fragment FancyFloat on FancyFloat {
  source
  number
}
fragment Provider on Provider {
  id
  createdAt
  user {
    ...UserRef
  }
  document {
    pid
    fileName
    contentType
    downloadHref
    viewHref
  }
  espmConsumption {
    id
    createdAt
    lastUpdatedAt
    externalConsumptionId
    externalData
    start
    end
    usageKwh
    costUsd
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BillingMeter" },
)
export const PublicTariffFragmentDoc = new TypedDocumentString(
  `
    fragment PublicTariff on PublicTariff {
  id
  pid
  name
  code
  tariffType
  selectionStatus
  isNem2
  isClosed
  isPrimaryConnection
  customTariffId
  notes
  updatedAt
  trashedAt
  requiresPv
  requiresStorage
  hasTouRates
  hasNetMetering
  customerLikelihood
  customerCount
  customerClass
  minMonthlyKw
  maxMonthlyKw
  minMonthlyKwh
  maxMonthlyKwh
  latestRateId
}
    `,
  { fragmentName: "PublicTariff" },
)
export const BasicBundleParentFragmentDoc = new TypedDocumentString(
  `
    fragment BasicBundleParent on BundleParent {
  id
  createdAt
  pid
  name
  type
  isExploratory
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  defaultRofoSigned
  hideAutoGeneratedPricingSummary
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  biddingOpenAt
  biddingClosedAt
  archivedAt
  salesOwner {
    ...UserRef
  }
  opsOwner {
    ...UserRef
  }
  recvr {
    pid
    name
  }
  canAddNewBids
  canEditBids
  allowDeveloperProjectNotesAndDocuments
  hiddenFromRecvr
  hiddenFromFinancier
  showAllBidsReport
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BasicBundleParent" },
)
export const PvSystemSpecFragmentDoc = new TypedDocumentString(
  `
    fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
    `,
  { fragmentName: "PvSystemSpec" },
)
export const SystemFragmentDoc = new TypedDocumentString(
  `
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
    fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}`,
  { fragmentName: "System" },
)
export const PpaBidSpecFragmentDoc = new TypedDocumentString(
  `
    fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "PpaBidSpec" },
)
export const SystemLiteFragmentDoc = new TypedDocumentString(
  `
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
    fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}`,
  { fragmentName: "SystemLite" },
)
export const CsBidSpecFragmentDoc = new TypedDocumentString(
  `
    fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "CsBidSpec" },
)
export const CashBidSpecFragmentDoc = new TypedDocumentString(
  `
    fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "CashBidSpec" },
)
export const InvestorBidSpecOptOutFragmentDoc = new TypedDocumentString(
  `
    fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "InvestorBidSpecOptOut" },
)
export const BidSpecFragmentDoc = new TypedDocumentString(
  `
    fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
    fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BidSpec" },
)
export const Helioscope2FragmentDoc = new TypedDocumentString(
  `
    fragment Helioscope2 on Helioscope2 {
  pid
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  groundPvKw
  dcNameplateKw
  acNameplateKw
  annualProductionAcKwh
  helioscopeKw
  pdfHref
  roofImageHref
  roofImageName
  roofImage {
    id
  }
}
    `,
  { fragmentName: "Helioscope2" },
)
export const BundleParentFragmentDoc = new TypedDocumentString(
  `
    fragment BundleParent on BundleParent {
  ...BasicBundleParent
  bundles {
    name
    pid
    financier {
      pid
      name
      orgSlug
    }
  }
  rows {
    specs {
      ...BidSpec
    }
    site {
      pid
      name
      streetAddress
      city
      state
      zipCode
    }
    notes
    status
    closedAt
    closeReason
    closeCustomReason
    salesOwner {
      pid
    }
    opsOwner {
      pid
    }
    awardedFinancier {
      pid
      name
      slug
      logoUrl
      logomarkUrl
      requiresChangeOrders
    }
    recommendedBid {
      ppa {
        pid
      }
      cs {
        pid
      }
    }
    energyProject {
      pid
      name
      closedAt
      changeOrderSent
      scopeOfWorkSent
      hostAwardSent
      lumenFeeUsdPerWatt
      lumenFeeUsdPerKwhStorage
      hubspotRecordId
      milestoneSummary {
        rofoSignedActual
        rofoSignedEstimated
        leaseSignedEstimated
        ixApprovedEstimated
        csProgramApprovedEstimated
        ntpEstimated
        codEstimated
      }
      projectSpecification {
        ppa {
          roofRentY1Usd
        }
        cs {
          roofRentY1Usd
        }
        systemSummary {
          pvSpecs {
            pvTypes
            sizeKwDc
            helioscope2 {
              ...Helioscope2
            }
          }
          batterySpecs {
            sizeKwh
          }
        }
      }
    }
  }
  documents {
    id
    pid
    tag
    fileName
    contentType
    dateCreated
    downloadHref
    canDelete
    ownerName
  }
  adminOnlyNote
  notionPageUrl
  bidRemindersSent
  lossNoticesSent
}
    fragment BasicBundleParent on BundleParent {
  id
  createdAt
  pid
  name
  type
  isExploratory
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  defaultRofoSigned
  hideAutoGeneratedPricingSummary
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  biddingOpenAt
  biddingClosedAt
  archivedAt
  salesOwner {
    ...UserRef
  }
  opsOwner {
    ...UserRef
  }
  recvr {
    pid
    name
  }
  canAddNewBids
  canEditBids
  allowDeveloperProjectNotesAndDocuments
  hiddenFromRecvr
  hiddenFromFinancier
  showAllBidsReport
}
fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment Helioscope2 on Helioscope2 {
  pid
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  groundPvKw
  dcNameplateKw
  acNameplateKw
  annualProductionAcKwh
  helioscopeKw
  pdfHref
  roofImageHref
  roofImageName
  roofImage {
    id
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BundleParent" },
)
export const BundleParentPropertyRowFragmentDoc = new TypedDocumentString(
  `
    fragment BundleParentPropertyRow on BundleParentRow {
  specs {
    ...BidSpec
  }
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
  }
  siteProjects {
    pid
    name
    finProductType
  }
  notes
  status
  closedAt
  closeReason
  closeCustomReason
  salesOwner {
    pid
  }
  opsOwner {
    pid
  }
  awardedFinancier {
    pid
    name
    slug
    logoUrl
    logomarkUrl
    requiresChangeOrders
  }
  recommendedBid {
    ppa {
      pid
    }
    cs {
      pid
    }
  }
  energyProject {
    pid
    name
    status
    closedAt
    archivedAt
    changeOrderSent
    scopeOfWorkSent
    hostAwardSent
    lumenFeeUsdPerWatt
    lumenFeeUsdPerKwhStorage
    hubspotRecordId
    milestoneSummary {
      rofoSignedActual
      rofoSignedEstimated
      leaseSignedEstimated
      ixApprovedEstimated
      csProgramApprovedEstimated
      ntpEstimated
      codEstimated
    }
    projectSpecification {
      ppa {
        roofRentY1Usd
      }
      cs {
        roofRentY1Usd
      }
      systemSummary {
        pvSpecs {
          pvTypes
          sizeKwDc
          sizeKwAc
          helioscope2 {
            ...Helioscope2
          }
        }
        batterySpecs {
          sizeKwh
          sizeKwDc
        }
      }
    }
  }
}
    fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment Helioscope2 on Helioscope2 {
  pid
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  groundPvKw
  dcNameplateKw
  acNameplateKw
  annualProductionAcKwh
  helioscopeKw
  pdfHref
  roofImageHref
  roofImageName
  roofImage {
    id
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BundleParentPropertyRow" },
)
export const CustomBidReportFragmentDoc = new TypedDocumentString(
  `
    fragment CustomBidReport on CustomBidReport {
  pid
  name
  notes
  buildingOwnerNotes
  visibleToDevelopers
  visibleToBuildingOwners
  bids {
    ppa {
      pid
      financier {
        orgSlug
      }
    }
    cs {
      pid
      financier {
        orgSlug
      }
    }
  }
}
    `,
  { fragmentName: "CustomBidReport" },
)
export const DocumentParseRequestFragmentDoc = new TypedDocumentString(
  `
    fragment DocumentParseRequest on DocumentParseRequest {
  id
  createdAt
  status
  errorClass
  errorMessage
  document {
    id
    pid
    fileName
    contentType
    downloadHref
    viewHref
    ownerName
    ownerEmail
    dateCreated
  }
  results {
    parse {
      id
      start
      end
      totalKwh
      peakKw
      totalCostUsd
      supplyCostUsd
      transmissionCostUsd
      meterId
      tariffLabel
      kind
    }
    target {
      id
      pid
      label
      utilityMeterId
    }
    kind
  }
}
    `,
  { fragmentName: "DocumentParseRequest" },
)
export const IntervalImportRequestFragmentDoc = new TypedDocumentString(
  `
    fragment IntervalImportRequest on IntervalImportRequestView {
  id
  status
  note
  errorClass
  errorMessage
  meterPid
  inputDocument {
    pid
    fileName
    downloadHref
  }
}
    `,
  { fragmentName: "IntervalImportRequest" },
)
export const InvestorOptOutFragmentDoc = new TypedDocumentString(
  `
    fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "InvestorOptOut" },
)
export const BundleListItemFragmentDoc = new TypedDocumentString(
  `
    fragment BundleListItem on Bundle {
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  archivedAt
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
    fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BundleListItem" },
)
export const BundleDetailFragmentDoc = new TypedDocumentString(
  `
    fragment BundleDetail on Bundle {
  ...BundleListItem
  intendsToBid
  opsOwner {
    ...UserRef
    meetingSchedulingUrl
  }
  parentRef {
    pid
  }
  documents {
    canDelete
    contentType
    dateCreated
    fileName
    ownerName
    pid
    tag
    downloadHref
    viewHref
    belongsToBundleParent
    investorBid {
      pid
      name
    }
    ownerOrg {
      orgSlug
    }
  }
  downloadBundleDocsZipHref
  downloadHelioscopesDotZipHref
  downloadBundleDotXlsxHref
  rows {
    site {
      pid
      state
    }
  }
}
    fragment BundleListItem on Bundle {
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  archivedAt
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BundleDetail" },
)
export const InvestorProfileFragmentDoc = new TypedDocumentString(
  `
    fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
    `,
  { fragmentName: "InvestorProfile" },
)
export const BoxplotStatsFragmentDoc = new TypedDocumentString(
  `
    fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
    `,
  { fragmentName: "BoxplotStats" },
)
export const CsSiteBidReportFragmentDoc = new TypedDocumentString(
  `
    fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
    fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`,
  { fragmentName: "CSSiteBidReport" },
)
export const PpaSiteBidReportFragmentDoc = new TypedDocumentString(
  `
    fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    ppaPriceEscalator
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      ppa {
        tenantFlows {
          totalUtilitySavings
        }
        ppaCostPerKwh
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
  ppaPriceY1Usd {
    ...BoxplotStats
  }
  lifetimeSavingsUsd {
    ...BoxplotStats
  }
}
    fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`,
  { fragmentName: "PPASiteBidReport" },
)
export const CashSiteBidReportFragmentDoc = new TypedDocumentString(
  `
    fragment CashSiteBidReport on CashSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    incentivesNotes
    interconnectionNotes
    otherNotes
    result {
      selfInvest {
        npv
        irrs {
          postTaxItcDepr
        }
        flows {
          totalCapex
          totalUtilitySavings
        }
      }
    }
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  totalLifetimeSavings {
    ...BoxplotStats
  }
  totalProjectCost {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
    fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`,
  { fragmentName: "CashSiteBidReport" },
)
export const BidReportFragmentDoc = new TypedDocumentString(
  `
    fragment BidReport on PackageBidReport {
  pid
  name
  notes
  visible
  totalDevelopers
  totalBids
  cs {
    ...CSSiteBidReport
  }
  ppa {
    ...PPASiteBidReport
  }
  cash {
    ...CashSiteBidReport
  }
}
    fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    ppaPriceEscalator
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      ppa {
        tenantFlows {
          totalUtilitySavings
        }
        ppaCostPerKwh
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
  ppaPriceY1Usd {
    ...BoxplotStats
  }
  lifetimeSavingsUsd {
    ...BoxplotStats
  }
}
fragment CashSiteBidReport on CashSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    incentivesNotes
    interconnectionNotes
    otherNotes
    result {
      selfInvest {
        npv
        irrs {
          postTaxItcDepr
        }
        flows {
          totalCapex
          totalUtilitySavings
        }
      }
    }
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  totalLifetimeSavings {
    ...BoxplotStats
  }
  totalProjectCost {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`,
  { fragmentName: "BidReport" },
)
export const BundleReportsFragmentDoc = new TypedDocumentString(
  `
    fragment BundleReports on Bundle {
  name
  bidReports {
    ...BidReport
  }
}
    fragment BidReport on PackageBidReport {
  pid
  name
  notes
  visible
  totalDevelopers
  totalBids
  cs {
    ...CSSiteBidReport
  }
  ppa {
    ...PPASiteBidReport
  }
  cash {
    ...CashSiteBidReport
  }
}
fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    ppaPriceEscalator
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      ppa {
        tenantFlows {
          totalUtilitySavings
        }
        ppaCostPerKwh
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
  ppaPriceY1Usd {
    ...BoxplotStats
  }
  lifetimeSavingsUsd {
    ...BoxplotStats
  }
}
fragment CashSiteBidReport on CashSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    incentivesNotes
    interconnectionNotes
    otherNotes
    result {
      selfInvest {
        npv
        irrs {
          postTaxItcDepr
        }
        flows {
          totalCapex
          totalUtilitySavings
        }
      }
    }
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  totalLifetimeSavings {
    ...BoxplotStats
  }
  totalProjectCost {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`,
  { fragmentName: "BundleReports" },
)
export const PpaInvestorBidFragmentDoc = new TypedDocumentString(
  `
    fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}`,
  { fragmentName: "PpaInvestorBid" },
)
export const RoofRentEconomicsFragmentDoc = new TypedDocumentString(
  `
    fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
    `,
  { fragmentName: "RoofRentEconomics" },
)
export const RevampCsBaseFragmentDoc = new TypedDocumentString(
  `
    fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}`,
  { fragmentName: "RevampCSBase" },
)
export const CsInvestorBidFragmentDoc = new TypedDocumentString(
  `
    fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}`,
  { fragmentName: "CsInvestorBid" },
)
export const CashInvestorBidFragmentDoc = new TypedDocumentString(
  `
    fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}`,
  { fragmentName: "CashInvestorBid" },
)
export const InvestorBidFragmentDoc = new TypedDocumentString(
  `
    fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
    fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}`,
  { fragmentName: "InvestorBid" },
)
export const BundleDetailRowFragmentDoc = new TypedDocumentString(
  `
    fragment BundleDetailRow on BundleRow {
  id
  projectPid
  projectName
  ppaType
  state
  reason
  customReason
  notes
  optOut {
    ...InvestorOptOut
  }
  bids {
    ...InvestorBid
  }
  specs {
    ...BidSpec
  }
}
    fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "BundleDetailRow" },
)
export const DocumentFragmentDoc = new TypedDocumentString(
  `
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}
    `,
  { fragmentName: "Document" },
)
export const BundleBidsFragmentDoc = new TypedDocumentString(
  `
    fragment BundleBids on Bundle {
  pid
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  allowDeveloperProjectNotesAndDocuments
  revenueMilestones {
    milestoneType
    percentage
  }
  type
  recvr {
    name
  }
  optOut {
    ...InvestorOptOut
  }
  rows {
    ...BundleDetailRow
    pid
    developerNotesContent
    developerDocuments {
      ...Document
      canDelete
    }
    site {
      pid
      name
      fullAddress
      streetAddress
      city
      state
      zipCode
      county
      cbsaName
      buildingData {
        ownerName
      }
      buildingType
      siteDetails {
        roofReplacedYr
        roofCondition
      }
      yearBuilt
      targetUtility {
        pid
        name
        hostingCapacityMaps {
          url
          requiresLogin
          types
        }
      }
      isEnergyCommunity
      isLowIncomeCommunity
      sqft
      roofSqft
      roofMaxPvKw
      numStories
      buildingClass
    }
  }
}
    fragment BundleDetailRow on BundleRow {
  id
  projectPid
  projectName
  ppaType
  state
  reason
  customReason
  notes
  optOut {
    ...InvestorOptOut
  }
  bids {
    ...InvestorBid
  }
  specs {
    ...BidSpec
  }
}
fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`,
  { fragmentName: "BundleBids" },
)
export const PvSystemSpecRowFragmentDoc = new TypedDocumentString(
  `
    fragment PvSystemSpecRow on PVSpec {
  id
  pvTypes
  sizeKwDc
  sizeKwAc
  roofImage {
    viewHref
    signedViewUrl
  }
}
    `,
  { fragmentName: "PvSystemSpecRow" },
)
export const SystemRowFragmentDoc = new TypedDocumentString(
  `
    fragment SystemRow on System {
  id
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpecRow
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
  }
}
    fragment PvSystemSpecRow on PVSpec {
  id
  pvTypes
  sizeKwDc
  sizeKwAc
  roofImage {
    viewHref
    signedViewUrl
  }
}`,
  { fragmentName: "SystemRow" },
)
export const SystemLiteRowFragmentDoc = new TypedDocumentString(
  `
    fragment SystemLiteRow on SystemLite {
  id
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpecRow
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
  }
}
    fragment PvSystemSpecRow on PVSpec {
  id
  pvTypes
  sizeKwDc
  sizeKwAc
  roofImage {
    viewHref
    signedViewUrl
  }
}`,
  { fragmentName: "SystemLiteRow" },
)
export const FootprintFragmentDoc = new TypedDocumentString(
  `
    fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}`,
  { fragmentName: "Footprint" },
)
export const RevampSiteBasicsFragmentDoc = new TypedDocumentString(
  `
    fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`,
  { fragmentName: "RevampSiteBasics" },
)
export const BillingMeterOptionFragmentDoc = new TypedDocumentString(
  `
    fragment BillingMeterOption on BillingMeter {
  pid
  bauMeterPid
  view {
    name
    isArchived
    calc2 {
      __typename
      ... on Projected {
        annualKwh
      }
    }
  }
}
    `,
  { fragmentName: "BillingMeterOption" },
)
export const BidDraftFileRefFragmentDoc = new TypedDocumentString(
  `
    fragment BidDraftFileRef on InvestorBidDraftFileRef {
  fileId
  fileName
  downloadHref
}
    `,
  { fragmentName: "BidDraftFileRef" },
)
export const BidDraftFragmentDoc = new TypedDocumentString(
  `
    fragment BidDraft on InvestorBidDraft {
  pid
  type
  specPid
  updatedAt
  updatedByName
  schemaVersion
  formValues
  fileRefs {
    ...BidDraftFileRef
  }
}
    fragment BidDraftFileRef on InvestorBidDraftFileRef {
  fileId
  fileName
  downloadHref
}`,
  { fragmentName: "BidDraft" },
)
export const BundleRowDetailFragmentDoc = new TypedDocumentString(
  `
    fragment BundleRowDetail on BundleRow {
  ...BundleDetailRow
  site {
    ...RevampSiteBasics
    meterSet {
      totalCost
      loadKwh
    }
    billingMeters {
      ...BillingMeterOption
    }
  }
  bidDrafts {
    ...BidDraft
  }
}
    fragment BillingMeterOption on BillingMeter {
  pid
  bauMeterPid
  view {
    name
    isArchived
    calc2 {
      __typename
      ... on Projected {
        annualKwh
      }
    }
  }
}
fragment BundleDetailRow on BundleRow {
  id
  projectPid
  projectName
  ppaType
  state
  reason
  customReason
  notes
  optOut {
    ...InvestorOptOut
  }
  bids {
    ...InvestorBid
  }
  specs {
    ...BidSpec
  }
}
fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment BidDraftFileRef on InvestorBidDraftFileRef {
  fileId
  fileName
  downloadHref
}
fragment BidDraft on InvestorBidDraft {
  pid
  type
  specPid
  updatedAt
  updatedByName
  schemaVersion
  formValues
  fileRefs {
    ...BidDraftFileRef
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`,
  { fragmentName: "BundleRowDetail" },
)
export const BundleRowDetailLiteFragmentDoc = new TypedDocumentString(
  `
    fragment BundleRowDetailLite on BundleRow {
  projectPid
  optOut {
    ...InvestorOptOut
  }
  site {
    ...RevampSiteBasics
  }
}
    fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`,
  { fragmentName: "BundleRowDetailLite" },
)
export const Helioscope2RefFragmentDoc = new TypedDocumentString(
  `
    fragment Helioscope2Ref on Helioscope2 {
  id
  pdf {
    downloadHref
  }
  roofImage {
    viewHref
  }
  productionCsv {
    downloadHref
  }
}
    `,
  { fragmentName: "Helioscope2Ref" },
)
export const BasicBidPackageFragmentDoc = new TypedDocumentString(
  `
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
    fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`,
  { fragmentName: "BasicBidPackage" },
)
export const PackageDocumentFragmentDoc = new TypedDocumentString(
  `
    fragment PackageDocument on Document {
  canDelete
  contentType
  dateCreated
  fileName
  ownerName
  pid
  tag
  downloadHref
  belongsToBundleParent
  ownerOrg {
    orgSlug
    name
  }
  investorBid {
    pid
    name
  }
}
    `,
  { fragmentName: "PackageDocument" },
)
export const BidPackageDetailFragmentDoc = new TypedDocumentString(
  `
    fragment BidPackageDetail on BidPackage {
  ...BasicBidPackage
  documents {
    ...PackageDocument
  }
  bidDocuments {
    ...PackageDocument
  }
}
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment PackageDocument on Document {
  canDelete
  contentType
  dateCreated
  fileName
  ownerName
  pid
  tag
  downloadHref
  belongsToBundleParent
  ownerOrg {
    orgSlug
    name
  }
  investorBid {
    pid
    name
  }
}`,
  { fragmentName: "BidPackageDetail" },
)
export const BidReviewFragmentDoc = new TypedDocumentString(
  `
    fragment BidReview on BidReview {
  recommendedBidPids
  reports {
    properties
    bidders
    uniqueBids
    avgRoofRentY1Usd
    isCustom
    report {
      ...BidReport
    }
  }
}
    fragment BidReport on PackageBidReport {
  pid
  name
  notes
  visible
  totalDevelopers
  totalBids
  cs {
    ...CSSiteBidReport
  }
  ppa {
    ...PPASiteBidReport
  }
  cash {
    ...CashSiteBidReport
  }
}
fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    ppaPriceEscalator
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      ppa {
        tenantFlows {
          totalUtilitySavings
        }
        ppaCostPerKwh
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
  ppaPriceY1Usd {
    ...BoxplotStats
  }
  lifetimeSavingsUsd {
    ...BoxplotStats
  }
}
fragment CashSiteBidReport on CashSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    incentivesNotes
    interconnectionNotes
    otherNotes
    result {
      selfInvest {
        npv
        irrs {
          postTaxItcDepr
        }
        flows {
          totalCapex
          totalUtilitySavings
        }
      }
    }
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  totalLifetimeSavings {
    ...BoxplotStats
  }
  totalProjectCost {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`,
  { fragmentName: "BidReview" },
)
export const RateBandFragmentDoc = new TypedDocumentString(
  `
    fragment RateBand on RateBand {
  tariffRateBandId
  rateUnit
  price
  isCredit
  applicabilityValue
}
    `,
  { fragmentName: "RateBand" },
)
export const SeasonFragmentDoc = new TypedDocumentString(
  `
    fragment Season on Season {
  seasonFromDay
  seasonFromMonth
  seasonToDay
  seasonToMonth
}
    `,
  { fragmentName: "Season" },
)
export const RateFragmentDoc = new TypedDocumentString(
  `
    fragment Rate on Rate {
  name
  groupName
  tariffRateId
  chargeClass
  chargeType
  transactionType
  chargePeriod
  applicabilityKey
  applicabilityValues
  quantityKey
  applicableBands {
    ...RateBand
  }
  bands {
    ...RateBand
  }
  tou {
    touName
    touType
    touPeriods {
      fromHour
      fromMinute
      fromDayOfWeek
      toHour
      toMinute
      toDayOfWeek
    }
    season {
      ...Season
    }
  }
  season {
    ...Season
  }
}
    fragment RateBand on RateBand {
  tariffRateBandId
  rateUnit
  price
  isCredit
  applicabilityValue
}
fragment Season on Season {
  seasonFromDay
  seasonFromMonth
  seasonToDay
  seasonToMonth
}`,
  { fragmentName: "Rate" },
)
export const CustomRateFragmentDoc = new TypedDocumentString(
  `
    fragment CustomRate on CustomRate {
  name
  chargeClass
  chargeType
  transactionType
  price
  rateUnit
  chargePeriod
}
    `,
  { fragmentName: "CustomRate" },
)
export const TariffOpFragmentDoc = new TypedDocumentString(
  `
    fragment TariffOp on AddRateSubRateOverrideProperty {
  __typename
  ... on AddRate {
    rate {
      ...CustomRate
    }
    op
  }
  ... on SubRate {
    chargeClass
    rateId
    op
  }
  ... on OverrideProperty {
    keyName
    propertyValue
    op
  }
}
    fragment CustomRate on CustomRate {
  name
  chargeClass
  chargeType
  transactionType
  price
  rateUnit
  chargePeriod
}`,
  { fragmentName: "TariffOp" },
)
export const ChangeLogEntryFragmentDoc = new TypedDocumentString(
  `
    fragment ChangeLogEntry on ChangeLogEntry {
  source
  sourceId
  oldValue
  newValue
  columnType
  changedAt
}
    `,
  { fragmentName: "ChangeLogEntry" },
)
export const FieldChangeLogHistoryFragmentDoc = new TypedDocumentString(
  `
    fragment FieldChangeLogHistory on FieldChangeLogHistory {
  latest {
    ...ChangeLogEntry
  }
  history {
    ...ChangeLogEntry
  }
}
    fragment ChangeLogEntry on ChangeLogEntry {
  source
  sourceId
  oldValue
  newValue
  columnType
  changedAt
}`,
  { fragmentName: "FieldChangeLogHistory" },
)
export const SiteFieldChangeLogFragmentDoc = new TypedDocumentString(
  `
    fragment SiteFieldChangeLog on SiteFieldChangeLog {
  occupancyType {
    ...FieldChangeLogHistory
  }
  leaseType {
    ...FieldChangeLogHistory
  }
  targetUtilityId {
    ...FieldChangeLogHistory
  }
  tenantLeaseTerm {
    ...FieldChangeLogHistory
  }
}
    fragment ChangeLogEntry on ChangeLogEntry {
  source
  sourceId
  oldValue
  newValue
  columnType
  changedAt
}
fragment FieldChangeLogHistory on FieldChangeLogHistory {
  latest {
    ...ChangeLogEntry
  }
  history {
    ...ChangeLogEntry
  }
}`,
  { fragmentName: "SiteFieldChangeLog" },
)
export const SiteDetailsFieldChangeLogFragmentDoc = new TypedDocumentString(
  `
    fragment SiteDetailsFieldChangeLog on SiteDetailsFieldChangeLog {
  buildingLegalName {
    ...FieldChangeLogHistory
  }
  constructionEmail {
    ...FieldChangeLogHistory
  }
  counselApproval {
    ...FieldChangeLogHistory
  }
  externalLeaseApprovers {
    ...FieldChangeLogHistory
  }
  isFannieMaeLender {
    ...FieldChangeLogHistory
  }
  isFmGlobalInsurer {
    ...FieldChangeLogHistory
  }
  creditRating {
    ...FieldChangeLogHistory
  }
  leaseApprovers {
    ...FieldChangeLogHistory
  }
  leaseLegalOwnerName {
    ...FieldChangeLogHistory
  }
  leasePropertyLegalDescription {
    ...FieldChangeLogHistory
  }
  leaseSignatory {
    ...FieldChangeLogHistory
  }
  needsTenantApproval {
    ...FieldChangeLogHistory
  }
  propertyFundName {
    ...FieldChangeLogHistory
  }
  propertyInsurer {
    ...FieldChangeLogHistory
  }
  propertyLenders {
    ...FieldChangeLogHistory
  }
  propertySiteVisitContacts {
    ...FieldChangeLogHistory
  }
  roofCondition {
    ...FieldChangeLogHistory
  }
  roofReplacedYr {
    ...FieldChangeLogHistory
  }
  roofRightsHolder {
    ...FieldChangeLogHistory
  }
  salePlannedYear {
    ...FieldChangeLogHistory
  }
  salePlans {
    ...FieldChangeLogHistory
  }
  signatureBlocks {
    ...FieldChangeLogHistory
  }
}
    fragment ChangeLogEntry on ChangeLogEntry {
  source
  sourceId
  oldValue
  newValue
  columnType
  changedAt
}
fragment FieldChangeLogHistory on FieldChangeLogHistory {
  latest {
    ...ChangeLogEntry
  }
  history {
    ...ChangeLogEntry
  }
}`,
  { fragmentName: "SiteDetailsFieldChangeLog" },
)
export const AwardedCsProjectRoofRentFragmentDoc = new TypedDocumentString(
  `
    fragment AwardedCsProjectRoofRent on EnergyProject {
  pid
  projectSpecification {
    cs {
      calc {
        roofRent {
          ...RoofRentEconomics
        }
      }
    }
  }
}
    fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}`,
  { fragmentName: "AwardedCsProjectRoofRent" },
)
export const EnergyProjectCsSpecificationFragmentDoc = new TypedDocumentString(
  `
    fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
  calc {
    roofRent {
      ...RoofRentEconomics
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}`,
  { fragmentName: "EnergyProjectCSSpecification" },
)
export const EnergyProjectPpaSpecificationFragmentDoc = new TypedDocumentString(
  `
    fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}`,
  { fragmentName: "EnergyProjectPPASpecification" },
)
export const EnergyProjectCashSpecificationFragmentDoc = new TypedDocumentString(
  `
    fragment EnergyProjectCashSpecification on EnergyProjectCashSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  system {
    ...System
  }
}
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}`,
  { fragmentName: "EnergyProjectCashSpecification" },
)
export const MilestoneSummaryFragmentDoc = new TypedDocumentString(
  `
    fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  workableIxResultsReceivedEstimated
  workableIxResultsReceivedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
    `,
  { fragmentName: "MilestoneSummary" },
)
export const NoteFragmentDoc = new TypedDocumentString(
  `
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "Note" },
)
export const ProgressNotesFragmentDoc = new TypedDocumentString(
  `
    fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "ProgressNotes" },
)
export const EnergyProjectFragmentDoc = new TypedDocumentString(
  `
    fragment EnergyProject on EnergyProject {
  pid
  name
  description
  hubspotRecordId
  hubspotRevenueRecordId
  status
  closedAt
  closeReason
  closeCustomReason
  lumenFeeUsdPerWatt
  lumenRevenue
  finproductType
  solarLocations
  includeStorage
  archivedAt
  archivedBy {
    ...UserRef
  }
  site {
    pid
    streetAddress
  }
  projectSpecification {
    cs {
      ...EnergyProjectCSSpecification
    }
    ppa {
      ...EnergyProjectPPASpecification
    }
    cash {
      ...EnergyProjectCashSpecification
    }
  }
  milestoneSummary {
    ...MilestoneSummary
  }
  developer {
    name
    pid
  }
  progressNotes {
    ...ProgressNotes
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  workableIxResultsReceivedEstimated
  workableIxResultsReceivedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
  calc {
    roofRent {
      ...RoofRentEconomics
    }
  }
}
fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
fragment EnergyProjectCashSpecification on EnergyProjectCashSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  system {
    ...System
  }
}
fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "EnergyProject" },
)
export const EnergyProjectTaskNoteFragmentDoc = new TypedDocumentString(
  `
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "EnergyProjectTaskNote" },
)
export const EnergyProjectTaskFragmentDoc = new TypedDocumentString(
  `
    fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "EnergyProjectTask" },
)
export const AdminEnergyProjectFragmentDoc = new TypedDocumentString(
  `
    fragment AdminEnergyProject on EnergyProject {
  ...EnergyProject
  lumenFeeUsdPerKwhStorage
  hostAwardSent
  changeOrderSent
  scopeOfWorkSent
  executionPlanTasks(previewAsAudience: $previewAsAudience) {
    ...EnergyProjectTask
  }
  revenueBreakdown {
    revenueMilestones {
      milestoneType
      percentage
    }
  }
  developer {
    slug
    requiresChangeOrders
  }
  bundleParentRow {
    id
    status
    notes
    parent {
      pid
      name
      recvr {
        slug
        name
      }
    }
  }
  awardedBid {
    ppa {
      pid
      name
      bidPackagePid
      bidPackageName
      bidPackageOrgSlug
      system {
        ...System
      }
    }
    cs {
      pid
      name
      bidPackagePid
      bidPackageName
      bidPackageOrgSlug
      systemLite {
        ...SystemLite
      }
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  workableIxResultsReceivedEstimated
  workableIxResultsReceivedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
  calc {
    roofRent {
      ...RoofRentEconomics
    }
  }
}
fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
fragment EnergyProjectCashSpecification on EnergyProjectCashSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  system {
    ...System
  }
}
fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment EnergyProject on EnergyProject {
  pid
  name
  description
  hubspotRecordId
  hubspotRevenueRecordId
  status
  closedAt
  closeReason
  closeCustomReason
  lumenFeeUsdPerWatt
  lumenRevenue
  finproductType
  solarLocations
  includeStorage
  archivedAt
  archivedBy {
    ...UserRef
  }
  site {
    pid
    streetAddress
  }
  projectSpecification {
    cs {
      ...EnergyProjectCSSpecification
    }
    ppa {
      ...EnergyProjectPPASpecification
    }
    cash {
      ...EnergyProjectCashSpecification
    }
  }
  milestoneSummary {
    ...MilestoneSummary
  }
  developer {
    name
    pid
  }
  progressNotes {
    ...ProgressNotes
  }
}
fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "AdminEnergyProject" },
)
export const OrgUserFragmentDoc = new TypedDocumentString(
  `
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "OrgUser" },
)
export const DirtyRowDataFragmentDoc = new TypedDocumentString(
  `
    fragment DirtyRowData on DirtyRowData {
  name
  assetManager
  portfolioManager
  customerId
  numStories
  streetAddress
  city
  state
  zipCode
  buildingType
  sqft
  yearBuilt
}
    `,
  { fragmentName: "DirtyRowData" },
)
export const CsvImportDetailFragmentDoc = new TypedDocumentString(
  `
    fragment CsvImportDetail on CsvImport {
  pid
  rawCsvWithPidsHref
  columnIds
  columnMappings {
    assetManagerColumnId
    portfolioManagerColumnId
    buildingTypeColumnId
    cityColumnId
    customerIdColumnId
    latitudeColumnId
    longitudeColumnId
    nameColumnId
    numStoriesColumnId
    numUnits1BdColumnId
    numUnits2BdColumnId
    numUnits3BdColumnId
    numUnits4BdColumnId
    numUnitsStudioColumnId
    numUnitsTotalColumnId
    sqftColumnId
    stateColumnId
    streetAddressColumnId
    yearBuiltColumnId
    zipCodeColumnId
  }
  buildingTypes
  buildingTypeMappings {
    office
    multifamily
    hotel
    hospital
    outpatient
    retailStandalone
    retailStripMall
    quickServiceRestaurant
    fullServiceRestaurant
    primarySchool
    secondarySchool
    singleFamilyDetached
    warehouse
    dataCenter
    coldStorage
    industrial
    parkingLot
    supermarket
  }
  guessedSchema {
    pid
    origin
  }
  labels {
    pid
    themeColor
    name
  }
  rows {
    id
    droppedAt
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    amendments {
      assetManager
      portfolioManager
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
    site {
      pid
    }
    dirty {
      ...DirtyRowData
    }
    errors {
      type
      message
    }
    warnings {
      type
      message
    }
  }
}
    fragment DirtyRowData on DirtyRowData {
  name
  assetManager
  portfolioManager
  customerId
  numStories
  streetAddress
  city
  state
  zipCode
  buildingType
  sqft
  yearBuilt
}`,
  { fragmentName: "CsvImportDetail" },
)
export const CeleryEnqueueResultFragmentDoc = new TypedDocumentString(
  `
    fragment CeleryEnqueueResult on CeleryTaskError {
  __typename
  ... on CeleryTask {
    taskId
  }
  ... on Error {
    message
  }
}
    `,
  { fragmentName: "CeleryEnqueueResult" },
)
export const SigningStateFieldsFragmentDoc = new TypedDocumentString(
  `
    fragment SigningStateFields on DocumentSigningState {
  signingPid
  status
  source
  markedBy
  executedOn
  note
  dateSent
  expiresOn
  awaiting {
    name
    hasViewed
  }
  declinedBy {
    name
  }
  signers {
    name
    email
    status
    hasViewed
    signsForLumen
    signaturePage
  }
}
    `,
  { fragmentName: "SigningStateFields" },
)
export const ProjectContractDocumentFieldsFragmentDoc = new TypedDocumentString(
  `
    fragment ProjectContractDocumentFields on ProjectContractDocument {
  documentPid
  fileName
  onFile
  signing {
    ...SigningStateFields
  }
}
    fragment SigningStateFields on DocumentSigningState {
  signingPid
  status
  source
  markedBy
  executedOn
  note
  dateSent
  expiresOn
  awaiting {
    name
    hasViewed
  }
  declinedBy {
    name
  }
  signers {
    name
    email
    status
    hasViewed
    signsForLumen
    signaturePage
  }
}`,
  { fragmentName: "ProjectContractDocumentFields" },
)
export const AiConversionFragmentDoc = new TypedDocumentString(
  `
    fragment AIConversion on AIConversion {
  id
  conversionKind
  modelName
  dateCreated
  inputDocument {
    pid
    fileName
    downloadHref
  }
  outputDocument {
    pid
    fileName
    downloadHref
  }
  conversation {
    id
    model
    exchanges {
      blocks {
        __typename
        ... on AIProseBlock {
          text
          reasoning
        }
        ... on AICodeExecution {
          toolName
          command
          path
          code
        }
      }
    }
  }
}
    `,
  { fragmentName: "AIConversion" },
)
export const AiExtractionResultFragmentDoc = new TypedDocumentString(
  `
    fragment AIExtractionResult on AIExtractionResult {
  id
  extractionKind
  modelName
  schemaVersion
  dateCreated
  analysis {
    name
    description
    features {
      name
      description
      dataFields {
        id
        label
        value
        type
        boundingBoxes {
          pageNumber
          left
          top
          right
          bottom
        }
      }
      boundingBoxes {
        pageNumber
        left
        top
        right
        bottom
      }
    }
  }
}
    `,
  { fragmentName: "AIExtractionResult" },
)
export const SiteRefFragmentDoc = new TypedDocumentString(
  `
    fragment SiteRef on SiteRef {
  pid
  name
  streetAddress
}
    `,
  { fragmentName: "SiteRef" },
)
export const ScenarioRefFragmentDoc = new TypedDocumentString(
  `
    fragment ScenarioRef on ScenarioRef {
  pid
  name
}
    `,
  { fragmentName: "ScenarioRef" },
)
export const FinmodelSourceFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment FinmodelSourceFragment on FinmodelSource {
  __typename
  ... on PVoptRef {
    id
  }
  ... on LuxRef {
    id
  }
  ... on ReoptRef {
    pid
  }
  ... on ETBReportRef {
    id
    projectName
  }
}
    `,
  { fragmentName: "FinmodelSourceFragment" },
)
export const FinmodelFailureDetailFragmentDoc = new TypedDocumentString(
  `
    fragment FinmodelFailureDetail on FinmodelFailure {
  id
  createdAt
  fmv
  name
  message
  isPvopt
  isLux
  site {
    ...SiteRef
  }
  scenario {
    ...ScenarioRef
  }
  source {
    ...FinmodelSourceFragment
  }
}
    fragment FinmodelSourceFragment on FinmodelSource {
  __typename
  ... on PVoptRef {
    id
  }
  ... on LuxRef {
    id
  }
  ... on ReoptRef {
    pid
  }
  ... on ETBReportRef {
    id
    projectName
  }
}
fragment SiteRef on SiteRef {
  pid
  name
  streetAddress
}
fragment ScenarioRef on ScenarioRef {
  pid
  name
}`,
  { fragmentName: "FinmodelFailureDetail" },
)
export const ResultRefFragmentDoc = new TypedDocumentString(
  `
    fragment ResultRef on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
}
    `,
  { fragmentName: "ResultRef" },
)
export const RevampCsFragmentDoc = new TypedDocumentString(
  `
    fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}`,
  { fragmentName: "RevampCS" },
)
export const HelioscopeFragmentDoc = new TypedDocumentString(
  `
    fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}
    `,
  { fragmentName: "Helioscope" },
)
export const ResultFragmentDoc = new TypedDocumentString(
  `
    fragment Result on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`,
  { fragmentName: "Result" },
)
export const RevampFinmodelSummaryFragmentDoc = new TypedDocumentString(
  `
    fragment RevampFinmodelSummary on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  offerStatus
  fmvStr
  goal
  isVnem
  isGreenLease
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      totalUtilitySavings
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanTermYears
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    termYears
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  systems {
    ...RevampSystem
  }
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}`,
  { fragmentName: "RevampFinmodelSummary" },
)
export const ResultSummaryFragmentDoc = new TypedDocumentString(
  `
    fragment ResultSummary on Result {
  id
  pid
  isPinned
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodelSummary
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment RevampFinmodelSummary on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  offerStatus
  fmvStr
  goal
  isVnem
  isGreenLease
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      totalUtilitySavings
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanTermYears
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    termYears
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  systems {
    ...RevampSystem
  }
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`,
  { fragmentName: "ResultSummary" },
)
export const BidRevampSiteFragmentDoc = new TypedDocumentString(
  `
    fragment BidRevampSite on RevampSite {
  pid
  name
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  portfolioManager
  buildingType
  meterSet {
    totalCost
    loadKwh
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  siteDetails {
    salePlans
    roofCondition
    roofReplacedYr
  }
  roofMaxPvKw
  buildingClass
  targetUtility {
    name
    pid
    id
  }
  ppaType
}
    `,
  { fragmentName: "BidRevampSite" },
)
export const AdminUserRefFragmentDoc = new TypedDocumentString(
  `
    fragment AdminUserRef on AdminUser {
  id
  pid
  email
  name
  isAdmin
  avatarUrl
}
    `,
  { fragmentName: "AdminUserRef" },
)
export const SiteVisualsFragmentDoc = new TypedDocumentString(
  `
    fragment SiteVisuals on SiteVisuals {
  solarPanels {
    latitude
    longitude
    angle
  }
}
    `,
  { fragmentName: "SiteVisuals" },
)
export const BasicEvCalcFragmentDoc = new TypedDocumentString(
  `
    fragment BasicEVCalc on EVCalc {
  id
  createdAt
  projectInputs {
    projectStartDate
    installType
    termYears
    chargerKw
    yr0NumTenantEvs
    discountRate
  }
  outputs {
    irr
    npv
    yr0NumTenantEvs
    breakevenYear
    numChargersDemandedSeries
    cashflowSeries
    incentiveUsd
  }
  numChargers
  optimalNumChargers
  npv
  markupUsdPerKwh
  tariffRate {
    parent {
      name
      code
    }
  }
  utility {
    shortName
  }
  timeZone
  dayRates
  seasonalDayRates {
    name
    startMonth
    endMonth
    dayRates
  }
  downloadHourlyRatesHref
  xlsx
  downloadXlsxHref
  optIters {
    numChargers
    npv
  }
  yr3Revenue
  yr3Cashflow
  yr3NumEvs
  parkingRatio
  year3Expenses {
    label
    value
  }
  installCosts {
    label
    value
  }
}
    `,
  { fragmentName: "BasicEVCalc" },
)
export const DocumentAnalysisRequestFragmentDoc = new TypedDocumentString(
  `
    fragment DocumentAnalysisRequest on DocumentAnalysisRequest {
  id
  createdAt
  status
  note
  errorClass
  errorMessage
}
    `,
  { fragmentName: "DocumentAnalysisRequest" },
)
export const BauTariffFragmentDoc = new TypedDocumentString(
  `
    fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}
    `,
  { fragmentName: "BauTariff" },
)
export const BauMeterFragmentDoc = new TypedDocumentString(
  `
    fragment BauMeter on BauMeter {
  id
  createdAt
  createdBy {
    email
  }
  label
  loadOrigin
  loadCategory
  meterSource
  utility {
    id
    name
  }
  tariff {
    ...BauTariff
  }
  tariffSetBy {
    pid
    email
  }
  underHumanControl
  tariffsUnderHumanControl
  postSolarTariff {
    ...BauTariff
  }
  postSolarTariffSetBy {
    pid
    email
  }
  postBatteryTariff {
    ...BauTariff
  }
  postBatteryTariffSetBy {
    pid
    email
  }
  archivedAt
  archivedBy {
    pid
    email
  }
  archivedReason
  rollup {
    season
    days
    numbers
  }
}
    fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}`,
  { fragmentName: "BauMeter" },
)
export const BauMeterIntervalsFragmentDoc = new TypedDocumentString(
  `
    fragment BauMeterIntervals on BauMeter {
  id
  tariffUsdPerKwh
  rollup {
    season
    days
    numbers
  }
  intervals {
    units
    year
    timeZone
    maxKw
    totalKwh
    kwh8760
  }
}
    `,
  { fragmentName: "BauMeterIntervals" },
)
export const SitesTableFragmentDoc = new TypedDocumentString(
  `
    fragment SitesTable on Site {
  pid
  labels {
    pid
    name
    themeColor
  }
  propertyName
  fullAddress
  state
  buildingType
  sqft
  roofSqft
  occupancyType
  leaseType
  metering
  mortgageLender
  roofReplacedYr
  hiddenFromCustomers
  portfolio {
    pid
    title
    org {
      slug
    }
  }
  roofImage {
    id
  }
  roofMaxPvKw
  carportMaxPvKw
  primaryScenario {
    pid
    solarLocation
    includeStorage
  }
  currentStatus {
    pid
    status
  }
  createdAt
}
    `,
  { fragmentName: "SitesTable" },
)
export const UserListUserFragmentDoc = new TypedDocumentString(
  `
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  jobFunctions
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  adminOrgRoles {
    org {
      pid
      name
      orgSlug
    }
    role
    grantsAllPortfolios
    orgIsDeveloper
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}
    `,
  { fragmentName: "UserListUser" },
)
export const ChangelogFragmentDoc = new TypedDocumentString(
  `
    fragment Changelog on Changelog {
  field
  updatedAt
  updatedBy {
    ...UserRef
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "Changelog" },
)
export const RuleValueFragmentDoc = new TypedDocumentString(
  `
    fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}
    `,
  { fragmentName: "RuleValue" },
)
export const PolicyConditionFragmentDoc = new TypedDocumentString(
  `
    fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
    `,
  { fragmentName: "PolicyCondition" },
)
export const RuleFragmentDoc = new TypedDocumentString(
  `
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
    fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}`,
  { fragmentName: "Rule" },
)
export const PolicyListItemFragmentDoc = new TypedDocumentString(
  `
    fragment PolicyListItem on Policy {
  pid
  name
  jurisdiction {
    scope
    name
  }
  status
  startDate
  endDate
  userVisible
  application {
    status
  }
  rules {
    ...Rule
  }
}
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}`,
  { fragmentName: "PolicyListItem" },
)
export const OverrideFragmentDoc = new TypedDocumentString(
  `
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}
    `,
  { fragmentName: "Override" },
)
export const FinRelFragmentDoc = new TypedDocumentString(
  `
    fragment FinRel on AdminFinRel {
  disabledAt
  finproduct {
    id
    type
    name
    financier {
      name
      pid
    }
  }
  recvr {
    pid
  }
}
    `,
  { fragmentName: "FinRel" },
)
export const AttachmentFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "AttachmentFragment" },
)
export const ScenarioHelioscopeFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment ScenarioHelioscopeFragment on Helioscope {
  id
  pid
  createdAt
  isTrashed
  roofPvKw
  carportPvKw
  pdf {
    ...AttachmentFragment
  }
  roofImage {
    ...AttachmentFragment
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}`,
  { fragmentName: "ScenarioHelioscopeFragment" },
)
export const ScenarioEtbFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment ScenarioEtbFragment on ETBReport {
  id
  dateCreated
  projectName
  roofPvKw
  carportPvKw
  groundPvKw
  batteryKw
  batteryKwh
}
    `,
  { fragmentName: "ScenarioEtbFragment" },
)
export const RateTableFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment RateTableFragment on Rate {
  tariffRateId
  name
  chargeClass
  chargeType
  chargePeriod
  transactionType
  applicabilityKey
  quantityKey
  variableLimitKey
  variableRateKey
  variableFactorKey
  isTou
  bands {
    tariffRateBandId
    rateUnit
    price
    isCredit
    hasConsumptionLimit
    consumptionUpperLimit
    demandUpperLimit
    propertyUpperLimit
    calculationFactor
    applicabilityValue
  }
  season {
    seasonFromMonth
    seasonToMonth
    seasonFromDay
    seasonToDay
  }
  tou {
    touName
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    touPeriods {
      fromDayOfWeek
      toDayOfWeek
      fromHour
      toHour
      fromMinute
      toMinute
    }
  }
}
    `,
  { fragmentName: "RateTableFragment" },
)
export const RevisionRateFragmentDoc = new TypedDocumentString(
  `
    fragment RevisionRate on Rate {
  ...RateTableFragment
  groupName
  tou {
    touType
  }
}
    fragment RateTableFragment on Rate {
  tariffRateId
  name
  chargeClass
  chargeType
  chargePeriod
  transactionType
  applicabilityKey
  quantityKey
  variableLimitKey
  variableRateKey
  variableFactorKey
  isTou
  bands {
    tariffRateBandId
    rateUnit
    price
    isCredit
    hasConsumptionLimit
    consumptionUpperLimit
    demandUpperLimit
    propertyUpperLimit
    calculationFactor
    applicabilityValue
  }
  season {
    seasonFromMonth
    seasonToMonth
    seasonFromDay
    seasonToDay
  }
  tou {
    touName
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    touPeriods {
      fromDayOfWeek
      toDayOfWeek
      fromHour
      toHour
      fromMinute
      toMinute
    }
  }
}`,
  { fragmentName: "RevisionRate" },
)
export const RevisionPropertyFragmentDoc = new TypedDocumentString(
  `
    fragment RevisionProperty on TariffProperty {
  keyName
  period
  displayName
  keyspace
  family
  description
  dataType
  propertyTypes
  operator
  propertyValue
  minValue
  maxValue
  choices {
    displayValue
    value
    dataValue
    likelihood
  }
  propertyValues
  formulaDetail
  isDefault
  quantityKey
}
    `,
  { fragmentName: "RevisionProperty" },
)
export const TariffDetailFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment TariffDetailFragment on TariffDetails {
  tariff {
    id
    code
    name
    customerClass
  }
  version {
    tariffId
    effectiveDate
    endDate
  }
  properties {
    ...RevisionProperty
  }
  rawJson
  rates {
    ...RateTableFragment
  }
}
    fragment RateTableFragment on Rate {
  tariffRateId
  name
  chargeClass
  chargeType
  chargePeriod
  transactionType
  applicabilityKey
  quantityKey
  variableLimitKey
  variableRateKey
  variableFactorKey
  isTou
  bands {
    tariffRateBandId
    rateUnit
    price
    isCredit
    hasConsumptionLimit
    consumptionUpperLimit
    demandUpperLimit
    propertyUpperLimit
    calculationFactor
    applicabilityValue
  }
  season {
    seasonFromMonth
    seasonToMonth
    seasonFromDay
    seasonToDay
  }
  tou {
    touName
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    touPeriods {
      fromDayOfWeek
      toDayOfWeek
      fromHour
      toHour
      fromMinute
      toMinute
    }
  }
}
fragment RevisionProperty on TariffProperty {
  keyName
  period
  displayName
  keyspace
  family
  description
  dataType
  propertyTypes
  operator
  propertyValue
  minValue
  maxValue
  choices {
    displayValue
    value
    dataValue
    likelihood
  }
  propertyValues
  formulaDetail
  isDefault
  quantityKey
}`,
  { fragmentName: "TariffDetailFragment" },
)
export const MeteringFragmentDoc = new TypedDocumentString(
  `
    fragment Metering on Site {
  metering
  meteringSetDate
  meteringSetBy {
    ...UserRef
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`,
  { fragmentName: "Metering" },
)
export const EtbScenarioRefFragmentDoc = new TypedDocumentString(
  `
    fragment ETBScenarioRef on ETBScenarioRef {
  pid
  name
  isPrimary
  solarLocation
  includeStorage
  loadCategory
}
    `,
  { fragmentName: "ETBScenarioRef" },
)
export const CostsFragmentFragmentDoc = new TypedDocumentString(
  `
    fragment CostsFragment on ETBCosts {
  totalCost
  totalIncentiveSavings
  netCost
  energyConsumptionKwhYear1
  netEnergyConsumptionKwhYear1
  utilityCostPerKwh
  averageLoadFactor
  energyCostBefore
  energyCostAfter
  energyCostSavings
  demandCostBefore
  demandCostAfter
  demandCostSavings
  totalCostBefore
  totalCostAfter
  totalCostSavings
  pvTotalCost
  pvNetCost
  pvPowerRatingKwDc
  pvPowerRatingKwAc
  pvDegradationRate
  pvGenerationKwhYear1
  pvKwhPerKwDc
  pvSavingsPerKwh
  pvGenerationPercentExported
  storageTotalCost
  storageNetCost
  storageCapacityKwh
  storageMaxDischargePowerKw
  storageTwoHourPowerRatingKw
  storageDegradationRate
  storageDischargeYear1
  storageConsumptionKwhYear1
  storageSavingsPerKw
  storageSavingsPerKwh
}
    `,
  { fragmentName: "CostsFragment" },
)
export const PvConfigFragmentDoc = new TypedDocumentString(
  `
    fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
    `,
  { fragmentName: "PVConfig" },
)
export const BatteryConfigFragmentDoc = new TypedDocumentString(
  `
    fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}
    `,
  { fragmentName: "BatteryConfig" },
)
export const SlimLuxFragmentDoc = new TypedDocumentString(
  `
    fragment SlimLux on Lux {
  id
  etbReport {
    id
  }
  createdAt
  fmvStr
  goal
  maxOffsetPct
  pvKw
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  battKw
  battKwh
  meterSet {
    id
  }
  meters {
    bauMeter {
      id
    }
    postSolarMeter {
      pv {
        ...PVConfig
      }
    }
    postBatteryMeter {
      batt {
        ...BatteryConfig
      }
    }
  }
}
    fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}`,
  { fragmentName: "SlimLux" },
)
export const CostSummaryFragmentDoc = new TypedDocumentString(
  `
    fragment CostSummary on CostSummary {
  totalCost
  energyCost
  demandCost
  fixedCost
  nbcCost
  exportBenefit
}
    `,
  { fragmentName: "CostSummary" },
)
export const LuxMeterFragmentDoc = new TypedDocumentString(
  `
    fragment LuxMeter on LuxMeter {
  id
  gridMeter {
    preSolar {
      costSummary {
        ...CostSummary
      }
    }
    postSolar {
      costSummary {
        ...CostSummary
      }
    }
    postBattery {
      costSummary {
        ...CostSummary
      }
    }
  }
  dayIntervals {
    pvToBldg
    battDischarge
    gridToBldg
  }
  bauMeter {
    id
    label
    importsKwh
    tariff {
      name
      code
    }
  }
  postSolarMeter {
    pv {
      ...PVConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
  postBatteryMeter {
    batt {
      ...BatteryConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
}
    fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}
fragment CostSummary on CostSummary {
  totalCost
  energyCost
  demandCost
  fixedCost
  nbcCost
  exportBenefit
}`,
  { fragmentName: "LuxMeter" },
)
export const LuxFragmentDoc = new TypedDocumentString(
  `
    fragment Lux on Lux {
  ...SlimLux
  meters {
    ...LuxMeter
  }
  iterations {
    meters {
      bauMeterId
      totalPvKw
    }
    metric
    postTaxItcDeprIrr
  }
}
    fragment SlimLux on Lux {
  id
  etbReport {
    id
  }
  createdAt
  fmvStr
  goal
  maxOffsetPct
  pvKw
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  battKw
  battKwh
  meterSet {
    id
  }
  meters {
    bauMeter {
      id
    }
    postSolarMeter {
      pv {
        ...PVConfig
      }
    }
    postBatteryMeter {
      batt {
        ...BatteryConfig
      }
    }
  }
}
fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}
fragment CostSummary on CostSummary {
  totalCost
  energyCost
  demandCost
  fixedCost
  nbcCost
  exportBenefit
}
fragment LuxMeter on LuxMeter {
  id
  gridMeter {
    preSolar {
      costSummary {
        ...CostSummary
      }
    }
    postSolar {
      costSummary {
        ...CostSummary
      }
    }
    postBattery {
      costSummary {
        ...CostSummary
      }
    }
  }
  dayIntervals {
    pvToBldg
    battDischarge
    gridToBldg
  }
  bauMeter {
    id
    label
    importsKwh
    tariff {
      name
      code
    }
  }
  postSolarMeter {
    pv {
      ...PVConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
  postBatteryMeter {
    batt {
      ...BatteryConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
}`,
  { fragmentName: "Lux" },
)
export const DeltaFragmentDoc = new TypedDocumentString(
  `
    fragment Delta on Delta {
  a
  b
  diff
  pct
}
    `,
  { fragmentName: "Delta" },
)
export const NameThatTariffResultFragmentDoc = new TypedDocumentString(
  `
    fragment NameThatTariffResult on NameThatTariffResult {
  notTheCorrectRateVersion
  rate {
    startDate
    endDate
    tariffId
  }
  expected {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  actual {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  delta {
    usd {
      ...Delta
    }
    kwh {
      ...Delta
    }
    kw {
      ...Delta
    }
  }
  document {
    pid
    contentType
    viewHref
  }
}
    fragment Delta on Delta {
  a
  b
  diff
  pct
}`,
  { fragmentName: "NameThatTariffResult" },
)
export const FtmBessProspectFragmentDoc = new TypedDocumentString(
  `
    fragment FtmBessProspect on FTMBessProspect {
  triageStatus
  verdict
  updatedAt
  triageStatusEdit {
    at
    by
  }
  criteria {
    key
    label
    order
    status
    notes
    contextualValue
    statusEdit {
      at
      by
    }
    notesEdit {
      at
      by
    }
  }
}
    `,
  { fragmentName: "FtmBessProspect" },
)
export const ExtractionResultDetailFragmentDoc = new TypedDocumentString(
  `
    fragment ExtractionResultDetail on MeetingNoteExtractionResult {
  pid
  createdAt
  processedAt
  taskCreations {
    pid
    status
    description
    projectStage
    deadline
    assigneeUser {
      name
      email
      isAdmin
    }
    assigneeGroup
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskAssigneeChanges {
    pid
    status
    task {
      pid
      description
    }
    assigneeUser {
      name
      email
      isAdmin
    }
    assigneeGroup
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskDueDateChanges {
    pid
    status
    task {
      pid
      description
    }
    deadline
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskCompletionChanges {
    pid
    status
    task {
      pid
      description
    }
    complete
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskAddNotes {
    pid
    status
    task {
      pid
      description
    }
    noteText
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskDeletions {
    pid
    status
    task {
      pid
      description
    }
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  milestoneUpdates {
    pid
    status
    milestoneType
    milestoneKind
    milestoneDate
    currentEstimatedDate
    currentActualDate
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
}
    `,
  { fragmentName: "ExtractionResultDetail" },
)
export const MutBillingMeterFragmentDoc = new TypedDocumentString(
  `
    fragment MutBillingMeter on BillingMeter {
  __typename
  ... on BillingMeter {
    pid
    label
    config {
      loadCategory
      buildingType
    }
  }
}
    `,
  { fragmentName: "MutBillingMeter" },
)
export const EtbExtractFragmentDoc = new TypedDocumentString(
  `
    fragment EtbExtract on ETBExtract {
  id
  document {
    id
    pid
    fileName
    downloadHref
  }
  pv {
    sizeKwDc
    sizeKwAc
    etbMeterId
    etbMeterName
  }
  battery {
    sizeKwDc
    sizeKwh
    etbMeterId
    etbMeterName
  }
}
    `,
  { fragmentName: "EtbExtract" },
)
export const AddOverrideDocument = new TypedDocumentString(`
    mutation AddOverride($sitePid: String, $portfolioPid: String, $orgPid: String, $finProductId: Int, $inputName: String!, $inputValue: String!) {
  admin {
    finmodel {
      addOverride(
        sitePid: $sitePid
        portfolioPid: $portfolioPid
        orgPid: $orgPid
        finproductId: $finProductId
        inputName: $inputName
        inputValue: $inputValue
      ) {
        __typename
        ... on Error {
          message
        }
        ... on FinmodelInputOverride {
          inputName
          inputValue
        }
      }
    }
  }
}
    `)

export const useAddOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddOverrideMutation,
    TError,
    Types.AddOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddOverrideMutation,
    TError,
    Types.AddOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["AddOverride"],
    mutationFn: (variables?: Types.AddOverrideMutationVariables) =>
      publicFetcher<Types.AddOverrideMutation, Types.AddOverrideMutationVariables>(
        AddOverrideDocument,
        variables,
      )(),
    ...options,
  })
}

useAddOverrideMutation.fetcher = (
  variables: Types.AddOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddOverrideMutation, Types.AddOverrideMutationVariables>(
    AddOverrideDocument,
    variables,
    options,
  )

export const DeleteOverrideDocument = new TypedDocumentString(`
    mutation DeleteOverride($sitePid: String, $portfolioPid: String, $orgPid: String, $finProductId: Int, $inputName: String!) {
  admin {
    finmodel {
      deleteOverride(
        sitePid: $sitePid
        portfolioPid: $portfolioPid
        orgPid: $orgPid
        finproductId: $finProductId
        inputName: $inputName
      ) {
        inputName
        inputValue
      }
    }
  }
}
    `)

export const useDeleteOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteOverrideMutation,
    TError,
    Types.DeleteOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteOverrideMutation,
    TError,
    Types.DeleteOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteOverride"],
    mutationFn: (variables?: Types.DeleteOverrideMutationVariables) =>
      publicFetcher<
        Types.DeleteOverrideMutation,
        Types.DeleteOverrideMutationVariables
      >(DeleteOverrideDocument, variables)(),
    ...options,
  })
}

useDeleteOverrideMutation.fetcher = (
  variables: Types.DeleteOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteOverrideMutation, Types.DeleteOverrideMutationVariables>(
    DeleteOverrideDocument,
    variables,
    options,
  )

export const SetEtbProjectNameDocument = new TypedDocumentString(`
    mutation SetEtbProjectName($etbId: Int!, $name: String!) {
  admin {
    etb(etbId: $etbId) {
      setProjectName(name: $name) {
        id
        projectName
      }
    }
  }
}
    `)

export const useSetEtbProjectNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbProjectNameMutation,
    TError,
    Types.SetEtbProjectNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbProjectNameMutation,
    TError,
    Types.SetEtbProjectNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbProjectName"],
    mutationFn: (variables?: Types.SetEtbProjectNameMutationVariables) =>
      publicFetcher<
        Types.SetEtbProjectNameMutation,
        Types.SetEtbProjectNameMutationVariables
      >(SetEtbProjectNameDocument, variables)(),
    ...options,
  })
}

useSetEtbProjectNameMutation.fetcher = (
  variables: Types.SetEtbProjectNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEtbProjectNameMutation,
    Types.SetEtbProjectNameMutationVariables
  >(SetEtbProjectNameDocument, variables, options)

export const SetEtbMeterTariffBeforeDocument = new TypedDocumentString(`
    mutation SetEtbMeterTariffBefore($etbMeterId: Int!, $tariffId: Int) {
  admin {
    etbMeter {
      setTariffBefore(id: $etbMeterId, tariffBeforeId: $tariffId) {
        id
        genabilityTariffBefore: tariffBefore {
          id
          name
          code
        }
      }
    }
  }
}
    `)

export const useSetEtbMeterTariffBeforeMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEtbMeterTariffBeforeMutation,
    TError,
    Types.SetEtbMeterTariffBeforeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterTariffBeforeMutation,
    TError,
    Types.SetEtbMeterTariffBeforeMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterTariffBefore"],
    mutationFn: (variables?: Types.SetEtbMeterTariffBeforeMutationVariables) =>
      publicFetcher<
        Types.SetEtbMeterTariffBeforeMutation,
        Types.SetEtbMeterTariffBeforeMutationVariables
      >(SetEtbMeterTariffBeforeDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterTariffBeforeMutation.fetcher = (
  variables: Types.SetEtbMeterTariffBeforeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEtbMeterTariffBeforeMutation,
    Types.SetEtbMeterTariffBeforeMutationVariables
  >(SetEtbMeterTariffBeforeDocument, variables, options)

export const SetEtbMeterTariffAfterDocument = new TypedDocumentString(`
    mutation SetEtbMeterTariffAfter($etbMeterId: Int!, $tariffId: Int) {
  admin {
    etbMeter {
      setTariffAfter(id: $etbMeterId, tariffAfterId: $tariffId) {
        id
        genabilityTariffAfter: tariffAfter {
          id
          name
          code
        }
      }
    }
  }
}
    `)

export const useSetEtbMeterTariffAfterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbMeterTariffAfterMutation,
    TError,
    Types.SetEtbMeterTariffAfterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterTariffAfterMutation,
    TError,
    Types.SetEtbMeterTariffAfterMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterTariffAfter"],
    mutationFn: (variables?: Types.SetEtbMeterTariffAfterMutationVariables) =>
      publicFetcher<
        Types.SetEtbMeterTariffAfterMutation,
        Types.SetEtbMeterTariffAfterMutationVariables
      >(SetEtbMeterTariffAfterDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterTariffAfterMutation.fetcher = (
  variables: Types.SetEtbMeterTariffAfterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEtbMeterTariffAfterMutation,
    Types.SetEtbMeterTariffAfterMutationVariables
  >(SetEtbMeterTariffAfterDocument, variables, options)

export const SetEtbMeterBauMeterDocument = new TypedDocumentString(`
    mutation SetEtbMeterBauMeter($etbMeterId: Int!, $bauMeterId: Int!) {
  admin {
    etbMeter {
      setBauMeter(id: $etbMeterId, bauMeterId: $bauMeterId) {
        id
      }
    }
  }
}
    `)

export const useSetEtbMeterBauMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbMeterBauMeterMutation,
    TError,
    Types.SetEtbMeterBauMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterBauMeterMutation,
    TError,
    Types.SetEtbMeterBauMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterBauMeter"],
    mutationFn: (variables?: Types.SetEtbMeterBauMeterMutationVariables) =>
      publicFetcher<
        Types.SetEtbMeterBauMeterMutation,
        Types.SetEtbMeterBauMeterMutationVariables
      >(SetEtbMeterBauMeterDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterBauMeterMutation.fetcher = (
  variables: Types.SetEtbMeterBauMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEtbMeterBauMeterMutation,
    Types.SetEtbMeterBauMeterMutationVariables
  >(SetEtbMeterBauMeterDocument, variables, options)

export const SetETbMeterPvTypeDocument = new TypedDocumentString(`
    mutation SetETbMeterPvType($etbMeterId: Int!, $pvType: PVType) {
  admin {
    etbMeter {
      setPvType(id: $etbMeterId, pvType: $pvType) {
        id
      }
    }
  }
}
    `)

export const useSetETbMeterPvTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetETbMeterPvTypeMutation,
    TError,
    Types.SetETbMeterPvTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetETbMeterPvTypeMutation,
    TError,
    Types.SetETbMeterPvTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetETbMeterPvType"],
    mutationFn: (variables?: Types.SetETbMeterPvTypeMutationVariables) =>
      publicFetcher<
        Types.SetETbMeterPvTypeMutation,
        Types.SetETbMeterPvTypeMutationVariables
      >(SetETbMeterPvTypeDocument, variables)(),
    ...options,
  })
}

useSetETbMeterPvTypeMutation.fetcher = (
  variables: Types.SetETbMeterPvTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetETbMeterPvTypeMutation,
    Types.SetETbMeterPvTypeMutationVariables
  >(SetETbMeterPvTypeDocument, variables, options)

export const SetScenarioEtbDocument = new TypedDocumentString(`
    mutation SetScenarioETB($scenarioPid: String!, $etbId: Int) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      setEtb(etbId: $etbId) {
        pid
        activeEtbReport {
          id
          projectName
        }
      }
    }
  }
}
    `)

export const useSetScenarioEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioEtbMutation,
    TError,
    Types.SetScenarioEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioEtbMutation,
    TError,
    Types.SetScenarioEtbMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioETB"],
    mutationFn: (variables?: Types.SetScenarioEtbMutationVariables) =>
      publicFetcher<
        Types.SetScenarioEtbMutation,
        Types.SetScenarioEtbMutationVariables
      >(SetScenarioEtbDocument, variables)(),
    ...options,
  })
}

useSetScenarioEtbMutation.fetcher = (
  variables: Types.SetScenarioEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetScenarioEtbMutation, Types.SetScenarioEtbMutationVariables>(
    SetScenarioEtbDocument,
    variables,
    options,
  )

export const AddEtbDocument = new TypedDocumentString(`
    mutation AddETB($sitePid: String!, $scenarioPid: String!, $loadOrigin: LoadOrigin!, $upload: Upload!, $roofPvKw: Float, $carportPvKw: Float, $makeActive: Boolean!) {
  admin {
    site {
      addEtb(
        sitePid: $sitePid
        scenarioPid: $scenarioPid
        loadOrigin: $loadOrigin
        upload: $upload
        roofPvKw: $roofPvKw
        carportPvKw: $carportPvKw
        makeActive: $makeActive
      ) {
        pid
      }
    }
  }
}
    `)

export const useAddEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddEtbMutation,
    TError,
    Types.AddEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddEtbMutation,
    TError,
    Types.AddEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddETB"],
    mutationFn: (variables?: Types.AddEtbMutationVariables) =>
      publicFetcher<Types.AddEtbMutation, Types.AddEtbMutationVariables>(
        AddEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useAddEtbMutation.fetcher = (
  variables: Types.AddEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddEtbMutation, Types.AddEtbMutationVariables>(
    AddEtbDocument,
    variables,
    options,
  )

export const CreatePropertyNoteDocument = new TypedDocumentString(`
    mutation CreatePropertyNote($sitePid: String!, $content: QuillDelta!, $hiddenFromCustomers: Boolean!, $tag: SiteNoteTag) {
  admin {
    note {
      new(
        sitePid: $sitePid
        content: $content
        hiddenFromCustomers: $hiddenFromCustomers
        tag: $tag
      ) {
        pid
      }
    }
  }
}
    `)

export const useCreatePropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePropertyNoteMutation,
    TError,
    Types.CreatePropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePropertyNoteMutation,
    TError,
    Types.CreatePropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePropertyNote"],
    mutationFn: (variables?: Types.CreatePropertyNoteMutationVariables) =>
      publicFetcher<
        Types.CreatePropertyNoteMutation,
        Types.CreatePropertyNoteMutationVariables
      >(CreatePropertyNoteDocument, variables)(),
    ...options,
  })
}

useCreatePropertyNoteMutation.fetcher = (
  variables: Types.CreatePropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreatePropertyNoteMutation,
    Types.CreatePropertyNoteMutationVariables
  >(CreatePropertyNoteDocument, variables, options)

export const EditPropertyNoteDocument = new TypedDocumentString(`
    mutation EditPropertyNote($notePid: String!, $content: QuillDelta!, $hiddenFromCustomers: Boolean!, $tag: SiteNoteTag) {
  admin {
    note {
      edit(
        notePid: $notePid
        content: $content
        hiddenFromCustomers: $hiddenFromCustomers
        tag: $tag
      ) {
        pid
      }
    }
  }
}
    `)

export const useEditPropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditPropertyNoteMutation,
    TError,
    Types.EditPropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditPropertyNoteMutation,
    TError,
    Types.EditPropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["EditPropertyNote"],
    mutationFn: (variables?: Types.EditPropertyNoteMutationVariables) =>
      publicFetcher<
        Types.EditPropertyNoteMutation,
        Types.EditPropertyNoteMutationVariables
      >(EditPropertyNoteDocument, variables)(),
    ...options,
  })
}

useEditPropertyNoteMutation.fetcher = (
  variables: Types.EditPropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditPropertyNoteMutation,
    Types.EditPropertyNoteMutationVariables
  >(EditPropertyNoteDocument, variables, options)

export const TrashPropertyNoteDocument = new TypedDocumentString(`
    mutation TrashPropertyNote($notePid: String!) {
  admin {
    note {
      trash(notePid: $notePid) {
        pid
      }
    }
  }
}
    `)

export const useTrashPropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashPropertyNoteMutation,
    TError,
    Types.TrashPropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashPropertyNoteMutation,
    TError,
    Types.TrashPropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["TrashPropertyNote"],
    mutationFn: (variables?: Types.TrashPropertyNoteMutationVariables) =>
      publicFetcher<
        Types.TrashPropertyNoteMutation,
        Types.TrashPropertyNoteMutationVariables
      >(TrashPropertyNoteDocument, variables)(),
    ...options,
  })
}

useTrashPropertyNoteMutation.fetcher = (
  variables: Types.TrashPropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TrashPropertyNoteMutation,
    Types.TrashPropertyNoteMutationVariables
  >(TrashPropertyNoteDocument, variables, options)

export const EditCsFinProductPricesDocument = new TypedDocumentString(`
    mutation EditCSFinProductPrices($id: Int!, $input: [CSPriceInput!]!) {
  admin {
    finproduct {
      editCsFinproductPrices(id: $id, input: $input) {
        id
      }
    }
  }
}
    `)

export const useEditCsFinProductPricesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditCsFinProductPricesMutation,
    TError,
    Types.EditCsFinProductPricesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditCsFinProductPricesMutation,
    TError,
    Types.EditCsFinProductPricesMutationVariables,
    TContext
  >({
    mutationKey: ["EditCSFinProductPrices"],
    mutationFn: (variables?: Types.EditCsFinProductPricesMutationVariables) =>
      publicFetcher<
        Types.EditCsFinProductPricesMutation,
        Types.EditCsFinProductPricesMutationVariables
      >(EditCsFinProductPricesDocument, variables)(),
    ...options,
  })
}

useEditCsFinProductPricesMutation.fetcher = (
  variables: Types.EditCsFinProductPricesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditCsFinProductPricesMutation,
    Types.EditCsFinProductPricesMutationVariables
  >(EditCsFinProductPricesDocument, variables, options)

export const EditBatteryFinProductPricesDocument = new TypedDocumentString(`
    mutation EditBatteryFinProductPrices($id: Int!, $input: [BatteryPriceInput!]!) {
  admin {
    finproduct {
      editBatteryFinproductPrices(id: $id, input: $input) {
        id
      }
    }
  }
}
    `)

export const useEditBatteryFinProductPricesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EditBatteryFinProductPricesMutation,
    TError,
    Types.EditBatteryFinProductPricesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBatteryFinProductPricesMutation,
    TError,
    Types.EditBatteryFinProductPricesMutationVariables,
    TContext
  >({
    mutationKey: ["EditBatteryFinProductPrices"],
    mutationFn: (variables?: Types.EditBatteryFinProductPricesMutationVariables) =>
      publicFetcher<
        Types.EditBatteryFinProductPricesMutation,
        Types.EditBatteryFinProductPricesMutationVariables
      >(EditBatteryFinProductPricesDocument, variables)(),
    ...options,
  })
}

useEditBatteryFinProductPricesMutation.fetcher = (
  variables: Types.EditBatteryFinProductPricesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBatteryFinProductPricesMutation,
    Types.EditBatteryFinProductPricesMutationVariables
  >(EditBatteryFinProductPricesDocument, variables, options)

export const UpdateHelioscopeAssumptionsDocument = new TypedDocumentString(`
    mutation UpdateHelioscopeAssumptions($helioscopePid: String!, $assumptions: HelioscopeAssumptionsInput!) {
  admin {
    helioscopes {
      updateHelioscopeAssumptions(
        helioscopePid: $helioscopePid
        assumptions: $assumptions
      ) {
        __typename
      }
    }
  }
}
    `)

export const useUpdateHelioscopeAssumptionsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateHelioscopeAssumptionsMutation,
    TError,
    Types.UpdateHelioscopeAssumptionsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateHelioscopeAssumptionsMutation,
    TError,
    Types.UpdateHelioscopeAssumptionsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateHelioscopeAssumptions"],
    mutationFn: (variables?: Types.UpdateHelioscopeAssumptionsMutationVariables) =>
      publicFetcher<
        Types.UpdateHelioscopeAssumptionsMutation,
        Types.UpdateHelioscopeAssumptionsMutationVariables
      >(UpdateHelioscopeAssumptionsDocument, variables)(),
    ...options,
  })
}

useUpdateHelioscopeAssumptionsMutation.fetcher = (
  variables: Types.UpdateHelioscopeAssumptionsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateHelioscopeAssumptionsMutation,
    Types.UpdateHelioscopeAssumptionsMutationVariables
  >(UpdateHelioscopeAssumptionsDocument, variables, options)

export const CreateHostingCapacityMapDocument = new TypedDocumentString(`
    mutation CreateHostingCapacityMap($utilityPid: String!, $hostingCapacityMap: HostingCapacityMapInputs!) {
  admin {
    hostingCapacityMaps {
      create(utilityPid: $utilityPid, inputs: $hostingCapacityMap)
    }
  }
}
    `)

export const useCreateHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateHostingCapacityMapMutation,
    TError,
    Types.CreateHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateHostingCapacityMapMutation,
    TError,
    Types.CreateHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["CreateHostingCapacityMap"],
    mutationFn: (variables?: Types.CreateHostingCapacityMapMutationVariables) =>
      publicFetcher<
        Types.CreateHostingCapacityMapMutation,
        Types.CreateHostingCapacityMapMutationVariables
      >(CreateHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useCreateHostingCapacityMapMutation.fetcher = (
  variables: Types.CreateHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateHostingCapacityMapMutation,
    Types.CreateHostingCapacityMapMutationVariables
  >(CreateHostingCapacityMapDocument, variables, options)

export const UpdateHostingCapacityMapDocument = new TypedDocumentString(`
    mutation UpdateHostingCapacityMap($mapId: Int!, $hostingCapacityMap: HostingCapacityMapInputs!) {
  admin {
    hostingCapacityMaps {
      update(mapId: $mapId, inputs: $hostingCapacityMap)
    }
  }
}
    `)

export const useUpdateHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateHostingCapacityMapMutation,
    TError,
    Types.UpdateHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateHostingCapacityMapMutation,
    TError,
    Types.UpdateHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateHostingCapacityMap"],
    mutationFn: (variables?: Types.UpdateHostingCapacityMapMutationVariables) =>
      publicFetcher<
        Types.UpdateHostingCapacityMapMutation,
        Types.UpdateHostingCapacityMapMutationVariables
      >(UpdateHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useUpdateHostingCapacityMapMutation.fetcher = (
  variables: Types.UpdateHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateHostingCapacityMapMutation,
    Types.UpdateHostingCapacityMapMutationVariables
  >(UpdateHostingCapacityMapDocument, variables, options)

export const DeleteHostingCapacityMapDocument = new TypedDocumentString(`
    mutation DeleteHostingCapacityMap($mapId: Int!) {
  admin {
    hostingCapacityMaps {
      delete(mapId: $mapId)
    }
  }
}
    `)

export const useDeleteHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.DeleteHostingCapacityMapMutation,
    TError,
    Types.DeleteHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteHostingCapacityMapMutation,
    TError,
    Types.DeleteHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteHostingCapacityMap"],
    mutationFn: (variables?: Types.DeleteHostingCapacityMapMutationVariables) =>
      publicFetcher<
        Types.DeleteHostingCapacityMapMutation,
        Types.DeleteHostingCapacityMapMutationVariables
      >(DeleteHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useDeleteHostingCapacityMapMutation.fetcher = (
  variables: Types.DeleteHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteHostingCapacityMapMutation,
    Types.DeleteHostingCapacityMapMutationVariables
  >(DeleteHostingCapacityMapDocument, variables, options)

export const UpdateProjectDocument = new TypedDocumentString(`
    mutation UpdateProject($projectId: Int!, $inputs: MarketplaceRowInputs!) {
  admin {
    marketplace {
      updateProject(projectId: $projectId, inputs: $inputs) {
        __typename
        ... on EnergyProject {
          id
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useUpdateProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateProjectMutation,
    TError,
    Types.UpdateProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateProjectMutation,
    TError,
    Types.UpdateProjectMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateProject"],
    mutationFn: (variables?: Types.UpdateProjectMutationVariables) =>
      publicFetcher<Types.UpdateProjectMutation, Types.UpdateProjectMutationVariables>(
        UpdateProjectDocument,
        variables,
      )(),
    ...options,
  })
}

useUpdateProjectMutation.fetcher = (
  variables: Types.UpdateProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UpdateProjectMutation, Types.UpdateProjectMutationVariables>(
    UpdateProjectDocument,
    variables,
    options,
  )

export const CreateSlackChannelDocument = new TypedDocumentString(`
    mutation CreateSlackChannel($input: CreateSlackChannelInput!) {
  admin {
    slackChannels {
      createSlackChannel(input: $input) {
        __typename
        ... on SlackChannelOutput {
          channel {
            pid
            channelName
            channelId
            note
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useCreateSlackChannelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateSlackChannelMutation,
    TError,
    Types.CreateSlackChannelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateSlackChannelMutation,
    TError,
    Types.CreateSlackChannelMutationVariables,
    TContext
  >({
    mutationKey: ["CreateSlackChannel"],
    mutationFn: (variables?: Types.CreateSlackChannelMutationVariables) =>
      publicFetcher<
        Types.CreateSlackChannelMutation,
        Types.CreateSlackChannelMutationVariables
      >(CreateSlackChannelDocument, variables)(),
    ...options,
  })
}

useCreateSlackChannelMutation.fetcher = (
  variables: Types.CreateSlackChannelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateSlackChannelMutation,
    Types.CreateSlackChannelMutationVariables
  >(CreateSlackChannelDocument, variables, options)

export const DeleteSlackChannelDocument = new TypedDocumentString(`
    mutation DeleteSlackChannel($channelPid: String!) {
  admin {
    slackChannel(channelPid: $channelPid) {
      deleteSlackChannel
    }
  }
}
    `)

export const useDeleteSlackChannelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteSlackChannelMutation,
    TError,
    Types.DeleteSlackChannelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteSlackChannelMutation,
    TError,
    Types.DeleteSlackChannelMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteSlackChannel"],
    mutationFn: (variables?: Types.DeleteSlackChannelMutationVariables) =>
      publicFetcher<
        Types.DeleteSlackChannelMutation,
        Types.DeleteSlackChannelMutationVariables
      >(DeleteSlackChannelDocument, variables)(),
    ...options,
  })
}

useDeleteSlackChannelMutation.fetcher = (
  variables: Types.DeleteSlackChannelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteSlackChannelMutation,
    Types.DeleteSlackChannelMutationVariables
  >(DeleteSlackChannelDocument, variables, options)

export const AssociateSlackChannelOrgDocument = new TypedDocumentString(`
    mutation AssociateSlackChannelOrg($channelPid: String!, $orgPid: String!) {
  admin {
    slackChannel(channelPid: $channelPid) {
      associateOrg(orgPid: $orgPid) {
        channel {
          pid
          orgs {
            pid
            name
          }
        }
      }
    }
  }
}
    `)

export const useAssociateSlackChannelOrgMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AssociateSlackChannelOrgMutation,
    TError,
    Types.AssociateSlackChannelOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AssociateSlackChannelOrgMutation,
    TError,
    Types.AssociateSlackChannelOrgMutationVariables,
    TContext
  >({
    mutationKey: ["AssociateSlackChannelOrg"],
    mutationFn: (variables?: Types.AssociateSlackChannelOrgMutationVariables) =>
      publicFetcher<
        Types.AssociateSlackChannelOrgMutation,
        Types.AssociateSlackChannelOrgMutationVariables
      >(AssociateSlackChannelOrgDocument, variables)(),
    ...options,
  })
}

useAssociateSlackChannelOrgMutation.fetcher = (
  variables: Types.AssociateSlackChannelOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AssociateSlackChannelOrgMutation,
    Types.AssociateSlackChannelOrgMutationVariables
  >(AssociateSlackChannelOrgDocument, variables, options)

export const UnassociateSlackChannelOrgDocument = new TypedDocumentString(`
    mutation UnassociateSlackChannelOrg($channelPid: String!, $orgPid: String!) {
  admin {
    slackChannel(channelPid: $channelPid) {
      unassociateOrg(orgPid: $orgPid) {
        channel {
          pid
          orgs {
            pid
            name
          }
        }
      }
    }
  }
}
    `)

export const useUnassociateSlackChannelOrgMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UnassociateSlackChannelOrgMutation,
    TError,
    Types.UnassociateSlackChannelOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UnassociateSlackChannelOrgMutation,
    TError,
    Types.UnassociateSlackChannelOrgMutationVariables,
    TContext
  >({
    mutationKey: ["UnassociateSlackChannelOrg"],
    mutationFn: (variables?: Types.UnassociateSlackChannelOrgMutationVariables) =>
      publicFetcher<
        Types.UnassociateSlackChannelOrgMutation,
        Types.UnassociateSlackChannelOrgMutationVariables
      >(UnassociateSlackChannelOrgDocument, variables)(),
    ...options,
  })
}

useUnassociateSlackChannelOrgMutation.fetcher = (
  variables: Types.UnassociateSlackChannelOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UnassociateSlackChannelOrgMutation,
    Types.UnassociateSlackChannelOrgMutationVariables
  >(UnassociateSlackChannelOrgDocument, variables, options)

export const AnalyzeDocumentDocument = new TypedDocumentString(`
    mutation AnalyzeDocument($pid: String!, $reprocessParsing: Boolean!) {
  admin {
    documentAi {
      analyzeDocument(pid: $pid, reprocessParsing: $reprocessParsing) {
        __typename
        ... on DocumentAnalysisRequests {
          requests {
            ...DocumentAnalysisRequest
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    fragment DocumentAnalysisRequest on DocumentAnalysisRequest {
  id
  createdAt
  status
  note
  errorClass
  errorMessage
}`)

export const useAnalyzeDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AnalyzeDocumentMutation,
    TError,
    Types.AnalyzeDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AnalyzeDocumentMutation,
    TError,
    Types.AnalyzeDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["AnalyzeDocument"],
    mutationFn: (variables?: Types.AnalyzeDocumentMutationVariables) =>
      publicFetcher<
        Types.AnalyzeDocumentMutation,
        Types.AnalyzeDocumentMutationVariables
      >(AnalyzeDocumentDocument, variables)(),
    ...options,
  })
}

useAnalyzeDocumentMutation.fetcher = (
  variables: Types.AnalyzeDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AnalyzeDocumentMutation, Types.AnalyzeDocumentMutationVariables>(
    AnalyzeDocumentDocument,
    variables,
    options,
  )

export const ReprocessDocumentsByTagDocument = new TypedDocumentString(`
    mutation ReprocessDocumentsByTag($tag: DocumentTag!, $workflow: AIExtractionWorkflow) {
  admin {
    documentAi {
      reprocessDocumentsByTag(tag: $tag, workflow: $workflow) {
        tag
        workflow
        matchedCount
        enqueuedCount
        skippedCount
      }
    }
  }
}
    `)

export const useReprocessDocumentsByTagMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ReprocessDocumentsByTagMutation,
    TError,
    Types.ReprocessDocumentsByTagMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ReprocessDocumentsByTagMutation,
    TError,
    Types.ReprocessDocumentsByTagMutationVariables,
    TContext
  >({
    mutationKey: ["ReprocessDocumentsByTag"],
    mutationFn: (variables?: Types.ReprocessDocumentsByTagMutationVariables) =>
      publicFetcher<
        Types.ReprocessDocumentsByTagMutation,
        Types.ReprocessDocumentsByTagMutationVariables
      >(ReprocessDocumentsByTagDocument, variables)(),
    ...options,
  })
}

useReprocessDocumentsByTagMutation.fetcher = (
  variables: Types.ReprocessDocumentsByTagMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ReprocessDocumentsByTagMutation,
    Types.ReprocessDocumentsByTagMutationVariables
  >(ReprocessDocumentsByTagDocument, variables, options)

export const AdminCreatePortfolioDocument = new TypedDocumentString(`
    mutation AdminCreatePortfolio($title: String!, $orgPid: String!) {
  admin {
    portfolio {
      new(title: $title, orgPid: $orgPid) {
        pid
        title
      }
    }
  }
}
    `)

export const useAdminCreatePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AdminCreatePortfolioMutation,
    TError,
    Types.AdminCreatePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminCreatePortfolioMutation,
    TError,
    Types.AdminCreatePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["AdminCreatePortfolio"],
    mutationFn: (variables?: Types.AdminCreatePortfolioMutationVariables) =>
      publicFetcher<
        Types.AdminCreatePortfolioMutation,
        Types.AdminCreatePortfolioMutationVariables
      >(AdminCreatePortfolioDocument, variables)(),
    ...options,
  })
}

useAdminCreatePortfolioMutation.fetcher = (
  variables: Types.AdminCreatePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCreatePortfolioMutation,
    Types.AdminCreatePortfolioMutationVariables
  >(AdminCreatePortfolioDocument, variables, options)

export const SetSitePortfolioDocument = new TypedDocumentString(`
    mutation SetSitePortfolio($sitePid: String!, $portfolioPid: String) {
  admin {
    site {
      setPortfolio(sitePid: $sitePid, portfolioPid: $portfolioPid) {
        pid
        portfolio {
          pid
          title
        }
      }
    }
  }
}
    `)

export const useSetSitePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSitePortfolioMutation,
    TError,
    Types.SetSitePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSitePortfolioMutation,
    TError,
    Types.SetSitePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["SetSitePortfolio"],
    mutationFn: (variables?: Types.SetSitePortfolioMutationVariables) =>
      publicFetcher<
        Types.SetSitePortfolioMutation,
        Types.SetSitePortfolioMutationVariables
      >(SetSitePortfolioDocument, variables)(),
    ...options,
  })
}

useSetSitePortfolioMutation.fetcher = (
  variables: Types.SetSitePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetSitePortfolioMutation,
    Types.SetSitePortfolioMutationVariables
  >(SetSitePortfolioDocument, variables, options)

export const SetBuildingTypeDocument = new TypedDocumentString(`
    mutation SetBuildingType($sitePid: String!, $value: BuildingType) {
  admin {
    site {
      setBuildingType(sitePid: $sitePid, value: $value) {
        pid
        buildingType
      }
    }
  }
}
    `)

export const useSetBuildingTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBuildingTypeMutation,
    TError,
    Types.SetBuildingTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBuildingTypeMutation,
    TError,
    Types.SetBuildingTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetBuildingType"],
    mutationFn: (variables?: Types.SetBuildingTypeMutationVariables) =>
      publicFetcher<
        Types.SetBuildingTypeMutation,
        Types.SetBuildingTypeMutationVariables
      >(SetBuildingTypeDocument, variables)(),
    ...options,
  })
}

useSetBuildingTypeMutation.fetcher = (
  variables: Types.SetBuildingTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetBuildingTypeMutation, Types.SetBuildingTypeMutationVariables>(
    SetBuildingTypeDocument,
    variables,
    options,
  )

export const SetCostarPropertyTypeDocument = new TypedDocumentString(`
    mutation SetCostarPropertyType($sitePid: String!, $value: CoStarPropertyType!) {
  admin {
    site {
      setCostarPropertyType(sitePid: $sitePid, value: $value) {
        pid
        costarPropertyType
      }
    }
  }
}
    `)

export const useSetCostarPropertyTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCostarPropertyTypeMutation,
    TError,
    Types.SetCostarPropertyTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCostarPropertyTypeMutation,
    TError,
    Types.SetCostarPropertyTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetCostarPropertyType"],
    mutationFn: (variables?: Types.SetCostarPropertyTypeMutationVariables) =>
      publicFetcher<
        Types.SetCostarPropertyTypeMutation,
        Types.SetCostarPropertyTypeMutationVariables
      >(SetCostarPropertyTypeDocument, variables)(),
    ...options,
  })
}

useSetCostarPropertyTypeMutation.fetcher = (
  variables: Types.SetCostarPropertyTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCostarPropertyTypeMutation,
    Types.SetCostarPropertyTypeMutationVariables
  >(SetCostarPropertyTypeDocument, variables, options)

export const SetCostarSecondaryTypeDocument = new TypedDocumentString(`
    mutation SetCostarSecondaryType($sitePid: String!, $value: CoStarSecondaryType) {
  admin {
    site {
      setCostarSecondaryType(sitePid: $sitePid, value: $value) {
        pid
        costarSecondaryType
      }
    }
  }
}
    `)

export const useSetCostarSecondaryTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCostarSecondaryTypeMutation,
    TError,
    Types.SetCostarSecondaryTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCostarSecondaryTypeMutation,
    TError,
    Types.SetCostarSecondaryTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetCostarSecondaryType"],
    mutationFn: (variables?: Types.SetCostarSecondaryTypeMutationVariables) =>
      publicFetcher<
        Types.SetCostarSecondaryTypeMutation,
        Types.SetCostarSecondaryTypeMutationVariables
      >(SetCostarSecondaryTypeDocument, variables)(),
    ...options,
  })
}

useSetCostarSecondaryTypeMutation.fetcher = (
  variables: Types.SetCostarSecondaryTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCostarSecondaryTypeMutation,
    Types.SetCostarSecondaryTypeMutationVariables
  >(SetCostarSecondaryTypeDocument, variables, options)

export const SetCapRateDocument = new TypedDocumentString(`
    mutation SetCapRate($sitePid: String!, $value: Float) {
  admin {
    site {
      setCapRate(sitePid: $sitePid, value: $value) {
        pid
        capRate
      }
    }
  }
}
    `)

export const useSetCapRateMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCapRateMutation,
    TError,
    Types.SetCapRateMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCapRateMutation,
    TError,
    Types.SetCapRateMutationVariables,
    TContext
  >({
    mutationKey: ["SetCapRate"],
    mutationFn: (variables?: Types.SetCapRateMutationVariables) =>
      publicFetcher<Types.SetCapRateMutation, Types.SetCapRateMutationVariables>(
        SetCapRateDocument,
        variables,
      )(),
    ...options,
  })
}

useSetCapRateMutation.fetcher = (
  variables: Types.SetCapRateMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetCapRateMutation, Types.SetCapRateMutationVariables>(
    SetCapRateDocument,
    variables,
    options,
  )

export const SetZipCodeDocument = new TypedDocumentString(`
    mutation SetZipCode($sitePid: String!, $value: String!) {
  admin {
    site {
      setZipCode(sitePid: $sitePid, value: $value) {
        pid
        zipCode
      }
    }
  }
}
    `)

export const useSetZipCodeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetZipCodeMutation,
    TError,
    Types.SetZipCodeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetZipCodeMutation,
    TError,
    Types.SetZipCodeMutationVariables,
    TContext
  >({
    mutationKey: ["SetZipCode"],
    mutationFn: (variables?: Types.SetZipCodeMutationVariables) =>
      publicFetcher<Types.SetZipCodeMutation, Types.SetZipCodeMutationVariables>(
        SetZipCodeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetZipCodeMutation.fetcher = (
  variables: Types.SetZipCodeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetZipCodeMutation, Types.SetZipCodeMutationVariables>(
    SetZipCodeDocument,
    variables,
    options,
  )

export const SetBuildingClassDocument = new TypedDocumentString(`
    mutation SetBuildingClass($sitePid: String!, $buildingClass: String) {
  admin {
    site {
      setBuildingClass(sitePid: $sitePid, buildingClass: $buildingClass)
    }
  }
}
    `)

export const useSetBuildingClassMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBuildingClassMutation,
    TError,
    Types.SetBuildingClassMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBuildingClassMutation,
    TError,
    Types.SetBuildingClassMutationVariables,
    TContext
  >({
    mutationKey: ["SetBuildingClass"],
    mutationFn: (variables?: Types.SetBuildingClassMutationVariables) =>
      publicFetcher<
        Types.SetBuildingClassMutation,
        Types.SetBuildingClassMutationVariables
      >(SetBuildingClassDocument, variables)(),
    ...options,
  })
}

useSetBuildingClassMutation.fetcher = (
  variables: Types.SetBuildingClassMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBuildingClassMutation,
    Types.SetBuildingClassMutationVariables
  >(SetBuildingClassDocument, variables, options)

export const SetGroundsFromParcelsDocument = new TypedDocumentString(`
    mutation SetGroundsFromParcels($sitePid: String!, $parcelPids: [String!]!) {
  site(sitePid: $sitePid) {
    setGroundsFromParcels(parcelPids: $parcelPids) {
      pid
    }
  }
}
    `)

export const useSetGroundsFromParcelsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetGroundsFromParcelsMutation,
    TError,
    Types.SetGroundsFromParcelsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetGroundsFromParcelsMutation,
    TError,
    Types.SetGroundsFromParcelsMutationVariables,
    TContext
  >({
    mutationKey: ["SetGroundsFromParcels"],
    mutationFn: (variables?: Types.SetGroundsFromParcelsMutationVariables) =>
      publicFetcher<
        Types.SetGroundsFromParcelsMutation,
        Types.SetGroundsFromParcelsMutationVariables
      >(SetGroundsFromParcelsDocument, variables)(),
    ...options,
  })
}

useSetGroundsFromParcelsMutation.fetcher = (
  variables: Types.SetGroundsFromParcelsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetGroundsFromParcelsMutation,
    Types.SetGroundsFromParcelsMutationVariables
  >(SetGroundsFromParcelsDocument, variables, options)

export const InitializeSiteGroundsDocument = new TypedDocumentString(`
    mutation InitializeSiteGrounds($sitePid: String!) {
  site(sitePid: $sitePid) {
    initializeSiteGrounds {
      taskId
    }
  }
}
    `)

export const useInitializeSiteGroundsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InitializeSiteGroundsMutation,
    TError,
    Types.InitializeSiteGroundsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InitializeSiteGroundsMutation,
    TError,
    Types.InitializeSiteGroundsMutationVariables,
    TContext
  >({
    mutationKey: ["InitializeSiteGrounds"],
    mutationFn: (variables?: Types.InitializeSiteGroundsMutationVariables) =>
      publicFetcher<
        Types.InitializeSiteGroundsMutation,
        Types.InitializeSiteGroundsMutationVariables
      >(InitializeSiteGroundsDocument, variables)(),
    ...options,
  })
}

useInitializeSiteGroundsMutation.fetcher = (
  variables: Types.InitializeSiteGroundsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InitializeSiteGroundsMutation,
    Types.InitializeSiteGroundsMutationVariables
  >(InitializeSiteGroundsDocument, variables, options)

export const SetSqftDocument = new TypedDocumentString(`
    mutation SetSqft($sitePid: String!, $value: Float!) {
  admin {
    site {
      setSqft(sitePid: $sitePid, value: $value) {
        pid
        sqft
      }
    }
  }
}
    `)

export const useSetSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSqftMutation,
    TError,
    Types.SetSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSqftMutation,
    TError,
    Types.SetSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetSqft"],
    mutationFn: (variables?: Types.SetSqftMutationVariables) =>
      publicFetcher<Types.SetSqftMutation, Types.SetSqftMutationVariables>(
        SetSqftDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSqftMutation.fetcher = (
  variables: Types.SetSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetSqftMutation, Types.SetSqftMutationVariables>(
    SetSqftDocument,
    variables,
    options,
  )

export const SetRoofSqftDocument = new TypedDocumentString(`
    mutation SetRoofSqft($sitePid: String!, $value: Float!, $sourceType: ChangeSourceType, $sourceId: String) {
  admin {
    site {
      setRoofSqft(
        sitePid: $sitePid
        value: $value
        sourceType: $sourceType
        sourceId: $sourceId
      ) {
        pid
        roofSqft
      }
    }
  }
}
    `)

export const useSetRoofSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetRoofSqftMutation,
    TError,
    Types.SetRoofSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetRoofSqftMutation,
    TError,
    Types.SetRoofSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetRoofSqft"],
    mutationFn: (variables?: Types.SetRoofSqftMutationVariables) =>
      publicFetcher<Types.SetRoofSqftMutation, Types.SetRoofSqftMutationVariables>(
        SetRoofSqftDocument,
        variables,
      )(),
    ...options,
  })
}

useSetRoofSqftMutation.fetcher = (
  variables: Types.SetRoofSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetRoofSqftMutation, Types.SetRoofSqftMutationVariables>(
    SetRoofSqftDocument,
    variables,
    options,
  )

export const SetYearBuiltDocument = new TypedDocumentString(`
    mutation SetYearBuilt($sitePid: String!, $value: Int) {
  admin {
    site {
      setYearBuilt(sitePid: $sitePid, yearBuilt: $value) {
        pid
      }
    }
  }
}
    `)

export const useSetYearBuiltMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetYearBuiltMutation,
    TError,
    Types.SetYearBuiltMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetYearBuiltMutation,
    TError,
    Types.SetYearBuiltMutationVariables,
    TContext
  >({
    mutationKey: ["SetYearBuilt"],
    mutationFn: (variables?: Types.SetYearBuiltMutationVariables) =>
      publicFetcher<Types.SetYearBuiltMutation, Types.SetYearBuiltMutationVariables>(
        SetYearBuiltDocument,
        variables,
      )(),
    ...options,
  })
}

useSetYearBuiltMutation.fetcher = (
  variables: Types.SetYearBuiltMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetYearBuiltMutation, Types.SetYearBuiltMutationVariables>(
    SetYearBuiltDocument,
    variables,
    options,
  )

export const SetNumStoriesDocument = new TypedDocumentString(`
    mutation SetNumStories($sitePid: String!, $value: Int) {
  admin {
    site {
      setNumStories(sitePid: $sitePid, numStories: $value) {
        pid
      }
    }
  }
}
    `)

export const useSetNumStoriesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetNumStoriesMutation,
    TError,
    Types.SetNumStoriesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetNumStoriesMutation,
    TError,
    Types.SetNumStoriesMutationVariables,
    TContext
  >({
    mutationKey: ["SetNumStories"],
    mutationFn: (variables?: Types.SetNumStoriesMutationVariables) =>
      publicFetcher<Types.SetNumStoriesMutation, Types.SetNumStoriesMutationVariables>(
        SetNumStoriesDocument,
        variables,
      )(),
    ...options,
  })
}

useSetNumStoriesMutation.fetcher = (
  variables: Types.SetNumStoriesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetNumStoriesMutation, Types.SetNumStoriesMutationVariables>(
    SetNumStoriesDocument,
    variables,
    options,
  )

export const SetNumParkingSpacesDocument = new TypedDocumentString(`
    mutation SetNumParkingSpaces($sitePid: String!, $value: Int) {
  admin {
    site {
      setNumParkingSpaces(sitePid: $sitePid, numParkingSpaces: $value) {
        pid
      }
    }
  }
}
    `)

export const useSetNumParkingSpacesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetNumParkingSpacesMutation,
    TError,
    Types.SetNumParkingSpacesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetNumParkingSpacesMutation,
    TError,
    Types.SetNumParkingSpacesMutationVariables,
    TContext
  >({
    mutationKey: ["SetNumParkingSpaces"],
    mutationFn: (variables?: Types.SetNumParkingSpacesMutationVariables) =>
      publicFetcher<
        Types.SetNumParkingSpacesMutation,
        Types.SetNumParkingSpacesMutationVariables
      >(SetNumParkingSpacesDocument, variables)(),
    ...options,
  })
}

useSetNumParkingSpacesMutation.fetcher = (
  variables: Types.SetNumParkingSpacesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetNumParkingSpacesMutation,
    Types.SetNumParkingSpacesMutationVariables
  >(SetNumParkingSpacesDocument, variables, options)

export const SetExistingSolarDocument = new TypedDocumentString(`
    mutation SetExistingSolar($sitePid: String!, $value: Boolean, $source: PhysicalCheckSource!) {
  admin {
    site {
      setExistingSolar(sitePid: $sitePid, value: $value, source: $source) {
        pid
        physicalCheck {
          existingSolar
        }
      }
    }
  }
}
    `)

export const useSetExistingSolarMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetExistingSolarMutation,
    TError,
    Types.SetExistingSolarMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetExistingSolarMutation,
    TError,
    Types.SetExistingSolarMutationVariables,
    TContext
  >({
    mutationKey: ["SetExistingSolar"],
    mutationFn: (variables?: Types.SetExistingSolarMutationVariables) =>
      publicFetcher<
        Types.SetExistingSolarMutation,
        Types.SetExistingSolarMutationVariables
      >(SetExistingSolarDocument, variables)(),
    ...options,
  })
}

useSetExistingSolarMutation.fetcher = (
  variables: Types.SetExistingSolarMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetExistingSolarMutation,
    Types.SetExistingSolarMutationVariables
  >(SetExistingSolarDocument, variables, options)

export const SetCustomerIdDocument = new TypedDocumentString(`
    mutation SetCustomerId($sitePid: String!, $customerId: String) {
  admin {
    site {
      setCustomerId(sitePid: $sitePid, customerId: $customerId)
    }
  }
}
    `)

export const useSetCustomerIdMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCustomerIdMutation,
    TError,
    Types.SetCustomerIdMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomerIdMutation,
    TError,
    Types.SetCustomerIdMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomerId"],
    mutationFn: (variables?: Types.SetCustomerIdMutationVariables) =>
      publicFetcher<Types.SetCustomerIdMutation, Types.SetCustomerIdMutationVariables>(
        SetCustomerIdDocument,
        variables,
      )(),
    ...options,
  })
}

useSetCustomerIdMutation.fetcher = (
  variables: Types.SetCustomerIdMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetCustomerIdMutation, Types.SetCustomerIdMutationVariables>(
    SetCustomerIdDocument,
    variables,
    options,
  )

export const SetAssetManagerDocument = new TypedDocumentString(`
    mutation SetAssetManager($sitePid: String!, $assetManager: String) {
  admin {
    site {
      setAssetManager(sitePid: $sitePid, assetManager: $assetManager)
    }
  }
}
    `)

export const useSetAssetManagerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetAssetManagerMutation,
    TError,
    Types.SetAssetManagerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetAssetManagerMutation,
    TError,
    Types.SetAssetManagerMutationVariables,
    TContext
  >({
    mutationKey: ["SetAssetManager"],
    mutationFn: (variables?: Types.SetAssetManagerMutationVariables) =>
      publicFetcher<
        Types.SetAssetManagerMutation,
        Types.SetAssetManagerMutationVariables
      >(SetAssetManagerDocument, variables)(),
    ...options,
  })
}

useSetAssetManagerMutation.fetcher = (
  variables: Types.SetAssetManagerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetAssetManagerMutation, Types.SetAssetManagerMutationVariables>(
    SetAssetManagerDocument,
    variables,
    options,
  )

export const SetPortfolioManagerDocument = new TypedDocumentString(`
    mutation SetPortfolioManager($sitePid: String!, $portfolioManager: String) {
  admin {
    site {
      setPortfolioManager(sitePid: $sitePid, portfolioManager: $portfolioManager)
    }
  }
}
    `)

export const useSetPortfolioManagerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPortfolioManagerMutation,
    TError,
    Types.SetPortfolioManagerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPortfolioManagerMutation,
    TError,
    Types.SetPortfolioManagerMutationVariables,
    TContext
  >({
    mutationKey: ["SetPortfolioManager"],
    mutationFn: (variables?: Types.SetPortfolioManagerMutationVariables) =>
      publicFetcher<
        Types.SetPortfolioManagerMutation,
        Types.SetPortfolioManagerMutationVariables
      >(SetPortfolioManagerDocument, variables)(),
    ...options,
  })
}

useSetPortfolioManagerMutation.fetcher = (
  variables: Types.SetPortfolioManagerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPortfolioManagerMutation,
    Types.SetPortfolioManagerMutationVariables
  >(SetPortfolioManagerDocument, variables, options)

export const TrashAttachmentDocument = new TypedDocumentString(`
    mutation TrashAttachment($pid: String!, $attachmentId: Int!) {
  admin {
    site {
      trashAttachment(pid: $pid, attachmentId: $attachmentId) {
        pid
        roofImage {
          id
          name
        }
        attachments {
          id
          trashedAt
          isTrashable
          isRoofImageCandidate
        }
      }
    }
  }
}
    `)

export const useTrashAttachmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashAttachmentMutation,
    TError,
    Types.TrashAttachmentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashAttachmentMutation,
    TError,
    Types.TrashAttachmentMutationVariables,
    TContext
  >({
    mutationKey: ["TrashAttachment"],
    mutationFn: (variables?: Types.TrashAttachmentMutationVariables) =>
      publicFetcher<
        Types.TrashAttachmentMutation,
        Types.TrashAttachmentMutationVariables
      >(TrashAttachmentDocument, variables)(),
    ...options,
  })
}

useTrashAttachmentMutation.fetcher = (
  variables: Types.TrashAttachmentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TrashAttachmentMutation, Types.TrashAttachmentMutationVariables>(
    TrashAttachmentDocument,
    variables,
    options,
  )

export const SetSiteHiddenFromCustomersDocument = new TypedDocumentString(`
    mutation SetSiteHiddenFromCustomers($sitePid: String!, $hiddenFromCustomers: Boolean!) {
  admin {
    site {
      setSiteHiddenFromCustomers(
        sitePid: $sitePid
        hiddenFromCustomers: $hiddenFromCustomers
      ) {
        pid
        hiddenFromCustomers
      }
    }
  }
}
    `)

export const useSetSiteHiddenFromCustomersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetSiteHiddenFromCustomersMutation,
    TError,
    Types.SetSiteHiddenFromCustomersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteHiddenFromCustomersMutation,
    TError,
    Types.SetSiteHiddenFromCustomersMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteHiddenFromCustomers"],
    mutationFn: (variables?: Types.SetSiteHiddenFromCustomersMutationVariables) =>
      publicFetcher<
        Types.SetSiteHiddenFromCustomersMutation,
        Types.SetSiteHiddenFromCustomersMutationVariables
      >(SetSiteHiddenFromCustomersDocument, variables)(),
    ...options,
  })
}

useSetSiteHiddenFromCustomersMutation.fetcher = (
  variables: Types.SetSiteHiddenFromCustomersMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetSiteHiddenFromCustomersMutation,
    Types.SetSiteHiddenFromCustomersMutationVariables
  >(SetSiteHiddenFromCustomersDocument, variables, options)

export const SetSiteGrandfatheredNem2Document = new TypedDocumentString(`
    mutation SetSiteGrandfatheredNem2($sitePid: String!, $grandfatheredNem2: Boolean!) {
  admin {
    site {
      setSiteGrandfatheredNem2(
        sitePid: $sitePid
        grandfatheredNem2: $grandfatheredNem2
      ) {
        pid
        grandfatheredNem2
      }
    }
  }
}
    `)

export const useSetSiteGrandfatheredNem2Mutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetSiteGrandfatheredNem2Mutation,
    TError,
    Types.SetSiteGrandfatheredNem2MutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteGrandfatheredNem2Mutation,
    TError,
    Types.SetSiteGrandfatheredNem2MutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteGrandfatheredNem2"],
    mutationFn: (variables?: Types.SetSiteGrandfatheredNem2MutationVariables) =>
      publicFetcher<
        Types.SetSiteGrandfatheredNem2Mutation,
        Types.SetSiteGrandfatheredNem2MutationVariables
      >(SetSiteGrandfatheredNem2Document, variables)(),
    ...options,
  })
}

useSetSiteGrandfatheredNem2Mutation.fetcher = (
  variables: Types.SetSiteGrandfatheredNem2MutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetSiteGrandfatheredNem2Mutation,
    Types.SetSiteGrandfatheredNem2MutationVariables
  >(SetSiteGrandfatheredNem2Document, variables, options)

export const SetSiteShareSavingsDocument = new TypedDocumentString(`
    mutation SetSiteShareSavings($sitePid: String!, $shareSavings: Boolean!) {
  admin {
    site {
      setSiteShareSavings(sitePid: $sitePid, shareSavings: $shareSavings) {
        pid
        shareSavings
      }
    }
  }
}
    `)

export const useSetSiteShareSavingsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteShareSavingsMutation,
    TError,
    Types.SetSiteShareSavingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteShareSavingsMutation,
    TError,
    Types.SetSiteShareSavingsMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteShareSavings"],
    mutationFn: (variables?: Types.SetSiteShareSavingsMutationVariables) =>
      publicFetcher<
        Types.SetSiteShareSavingsMutation,
        Types.SetSiteShareSavingsMutationVariables
      >(SetSiteShareSavingsDocument, variables)(),
    ...options,
  })
}

useSetSiteShareSavingsMutation.fetcher = (
  variables: Types.SetSiteShareSavingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetSiteShareSavingsMutation,
    Types.SetSiteShareSavingsMutationVariables
  >(SetSiteShareSavingsDocument, variables, options)

export const AttachFileDocument = new TypedDocumentString(`
    mutation AttachFile($pid: String!, $file: Upload!, $tag: String) {
  admin {
    site {
      attachFile(pid: $pid, upload: $file, tag: $tag) {
        __typename
        ... on Site {
          pid
          attachments {
            ...AttachmentFragment
          }
        }
        ... on DuplicatesAttachment {
          attachment {
            name
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}`)

export const useAttachFileMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AttachFileMutation,
    TError,
    Types.AttachFileMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AttachFileMutation,
    TError,
    Types.AttachFileMutationVariables,
    TContext
  >({
    mutationKey: ["AttachFile"],
    mutationFn: (variables?: Types.AttachFileMutationVariables) =>
      publicFetcher<Types.AttachFileMutation, Types.AttachFileMutationVariables>(
        AttachFileDocument,
        variables,
      )(),
    ...options,
  })
}

useAttachFileMutation.fetcher = (
  variables: Types.AttachFileMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AttachFileMutation, Types.AttachFileMutationVariables>(
    AttachFileDocument,
    variables,
    options,
  )

export const CreateUserDocument = new TypedDocumentString(`
    mutation CreateUser($email: String!, $name: String, $orgPid: String!, $portfolioPids: [String!]!, $sendInvite: Boolean!) {
  admin {
    user {
      new(
        email: $email
        name: $name
        orgPid: $orgPid
        portfolioPids: $portfolioPids
        sendInvite: $sendInvite
      ) {
        __typename
        ... on AdminUser {
          id
          email
          name
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useCreateUserMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateUserMutation,
    TError,
    Types.CreateUserMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateUserMutation,
    TError,
    Types.CreateUserMutationVariables,
    TContext
  >({
    mutationKey: ["CreateUser"],
    mutationFn: (variables?: Types.CreateUserMutationVariables) =>
      publicFetcher<Types.CreateUserMutation, Types.CreateUserMutationVariables>(
        CreateUserDocument,
        variables,
      )(),
    ...options,
  })
}

useCreateUserMutation.fetcher = (
  variables: Types.CreateUserMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreateUserMutation, Types.CreateUserMutationVariables>(
    CreateUserDocument,
    variables,
    options,
  )

export const CreateAdminUserDocument = new TypedDocumentString(`
    mutation CreateAdminUser($email: String!, $name: String) {
  admin {
    user {
      createAdmin(email: $email, name: $name) {
        __typename
        ... on AdminUser {
          id
          email
          name
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useCreateAdminUserMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateAdminUserMutation,
    TError,
    Types.CreateAdminUserMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateAdminUserMutation,
    TError,
    Types.CreateAdminUserMutationVariables,
    TContext
  >({
    mutationKey: ["CreateAdminUser"],
    mutationFn: (variables?: Types.CreateAdminUserMutationVariables) =>
      publicFetcher<
        Types.CreateAdminUserMutation,
        Types.CreateAdminUserMutationVariables
      >(CreateAdminUserDocument, variables)(),
    ...options,
  })
}

useCreateAdminUserMutation.fetcher = (
  variables: Types.CreateAdminUserMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreateAdminUserMutation, Types.CreateAdminUserMutationVariables>(
    CreateAdminUserDocument,
    variables,
    options,
  )

export const ChangeUserNameDocument = new TypedDocumentString(`
    mutation ChangeUserName($id: Int!, $name: String!) {
  admin {
    user {
      setName(id: $id, name: $name) {
        id
        name
      }
    }
  }
}
    `)

export const useChangeUserNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ChangeUserNameMutation,
    TError,
    Types.ChangeUserNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ChangeUserNameMutation,
    TError,
    Types.ChangeUserNameMutationVariables,
    TContext
  >({
    mutationKey: ["ChangeUserName"],
    mutationFn: (variables?: Types.ChangeUserNameMutationVariables) =>
      publicFetcher<
        Types.ChangeUserNameMutation,
        Types.ChangeUserNameMutationVariables
      >(ChangeUserNameDocument, variables)(),
    ...options,
  })
}

useChangeUserNameMutation.fetcher = (
  variables: Types.ChangeUserNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ChangeUserNameMutation, Types.ChangeUserNameMutationVariables>(
    ChangeUserNameDocument,
    variables,
    options,
  )

export const SetUserMeetingUrlDocument = new TypedDocumentString(`
    mutation SetUserMeetingUrl($id: Int!, $url: String!) {
  admin {
    user {
      setMeetingSchedulingUrl(userId: $id, url: $url) {
        __typename
        ... on Error {
          message
        }
        ... on AdminUser {
          id
          name
          email
          meetingSchedulingUrl
        }
      }
    }
  }
}
    `)

export const useSetUserMeetingUrlMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserMeetingUrlMutation,
    TError,
    Types.SetUserMeetingUrlMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserMeetingUrlMutation,
    TError,
    Types.SetUserMeetingUrlMutationVariables,
    TContext
  >({
    mutationKey: ["SetUserMeetingUrl"],
    mutationFn: (variables?: Types.SetUserMeetingUrlMutationVariables) =>
      publicFetcher<
        Types.SetUserMeetingUrlMutation,
        Types.SetUserMeetingUrlMutationVariables
      >(SetUserMeetingUrlDocument, variables)(),
    ...options,
  })
}

useSetUserMeetingUrlMutation.fetcher = (
  variables: Types.SetUserMeetingUrlMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetUserMeetingUrlMutation,
    Types.SetUserMeetingUrlMutationVariables
  >(SetUserMeetingUrlDocument, variables, options)

export const GiveMeAMagicLinkDocument = new TypedDocumentString(`
    mutation GiveMeAMagicLink($email: String!) {
  admin {
    user {
      giveMeAMagicLink(email: $email) {
        href
        expiresAt
      }
    }
  }
}
    `)

export const useGiveMeAMagicLinkMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.GiveMeAMagicLinkMutation,
    TError,
    Types.GiveMeAMagicLinkMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.GiveMeAMagicLinkMutation,
    TError,
    Types.GiveMeAMagicLinkMutationVariables,
    TContext
  >({
    mutationKey: ["GiveMeAMagicLink"],
    mutationFn: (variables?: Types.GiveMeAMagicLinkMutationVariables) =>
      publicFetcher<
        Types.GiveMeAMagicLinkMutation,
        Types.GiveMeAMagicLinkMutationVariables
      >(GiveMeAMagicLinkDocument, variables)(),
    ...options,
  })
}

useGiveMeAMagicLinkMutation.fetcher = (
  variables: Types.GiveMeAMagicLinkMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GiveMeAMagicLinkMutation,
    Types.GiveMeAMagicLinkMutationVariables
  >(GiveMeAMagicLinkDocument, variables, options)

export const AddUserToPortfolioDocument = new TypedDocumentString(`
    mutation AddUserToPortfolio($userId: Int!, $portfolioPid: String!, $sendEmail: Boolean!) {
  admin {
    user {
      addToPortfolio(
        userId: $userId
        portfolioPid: $portfolioPid
        sendEmail: $sendEmail
      ) {
        id
        pid
      }
    }
  }
}
    `)

export const useAddUserToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddUserToPortfolioMutation,
    TError,
    Types.AddUserToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddUserToPortfolioMutation,
    TError,
    Types.AddUserToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["AddUserToPortfolio"],
    mutationFn: (variables?: Types.AddUserToPortfolioMutationVariables) =>
      publicFetcher<
        Types.AddUserToPortfolioMutation,
        Types.AddUserToPortfolioMutationVariables
      >(AddUserToPortfolioDocument, variables)(),
    ...options,
  })
}

useAddUserToPortfolioMutation.fetcher = (
  variables: Types.AddUserToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddUserToPortfolioMutation,
    Types.AddUserToPortfolioMutationVariables
  >(AddUserToPortfolioDocument, variables, options)

export const AdminInviteToPortfolioDocument = new TypedDocumentString(`
    mutation AdminInviteToPortfolio($email: String!, $orgPid: String!, $portfolioPid: String!) {
  admin {
    user {
      inviteToPortfolio(email: $email, orgPid: $orgPid, portfolioPid: $portfolioPid)
    }
  }
}
    `)

export const useAdminInviteToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AdminInviteToPortfolioMutation,
    TError,
    Types.AdminInviteToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminInviteToPortfolioMutation,
    TError,
    Types.AdminInviteToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["AdminInviteToPortfolio"],
    mutationFn: (variables?: Types.AdminInviteToPortfolioMutationVariables) =>
      publicFetcher<
        Types.AdminInviteToPortfolioMutation,
        Types.AdminInviteToPortfolioMutationVariables
      >(AdminInviteToPortfolioDocument, variables)(),
    ...options,
  })
}

useAdminInviteToPortfolioMutation.fetcher = (
  variables: Types.AdminInviteToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInviteToPortfolioMutation,
    Types.AdminInviteToPortfolioMutationVariables
  >(AdminInviteToPortfolioDocument, variables, options)

export const RemoveFromPortfolioDocument = new TypedDocumentString(`
    mutation RemoveFromPortfolio($userId: Int!, $portfolioPid: String!) {
  admin {
    user {
      removeFromPortfolio(userId: $userId, portfolioPid: $portfolioPid) {
        id
        pid
      }
    }
  }
}
    `)

export const useRemoveFromPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveFromPortfolioMutation,
    TError,
    Types.RemoveFromPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveFromPortfolioMutation,
    TError,
    Types.RemoveFromPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveFromPortfolio"],
    mutationFn: (variables?: Types.RemoveFromPortfolioMutationVariables) =>
      publicFetcher<
        Types.RemoveFromPortfolioMutation,
        Types.RemoveFromPortfolioMutationVariables
      >(RemoveFromPortfolioDocument, variables)(),
    ...options,
  })
}

useRemoveFromPortfolioMutation.fetcher = (
  variables: Types.RemoveFromPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveFromPortfolioMutation,
    Types.RemoveFromPortfolioMutationVariables
  >(RemoveFromPortfolioDocument, variables, options)

export const SetRoofMaxPvKwDocument = new TypedDocumentString(`
    mutation SetRoofMaxPvKw($sitePid: String!, $value: Float) {
  admin {
    site {
      setRoofMaxPvKw(sitePid: $sitePid, value: $value) {
        pid
        roofMaxPvKw
      }
    }
  }
}
    `)

export const useSetRoofMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetRoofMaxPvKwMutation,
    TError,
    Types.SetRoofMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetRoofMaxPvKwMutation,
    TError,
    Types.SetRoofMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetRoofMaxPvKw"],
    mutationFn: (variables?: Types.SetRoofMaxPvKwMutationVariables) =>
      publicFetcher<
        Types.SetRoofMaxPvKwMutation,
        Types.SetRoofMaxPvKwMutationVariables
      >(SetRoofMaxPvKwDocument, variables)(),
    ...options,
  })
}

useSetRoofMaxPvKwMutation.fetcher = (
  variables: Types.SetRoofMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetRoofMaxPvKwMutation, Types.SetRoofMaxPvKwMutationVariables>(
    SetRoofMaxPvKwDocument,
    variables,
    options,
  )

export const SetCarportMaxPvKwDocument = new TypedDocumentString(`
    mutation SetCarportMaxPvKw($sitePid: String!, $value: Float) {
  admin {
    site {
      setCarportMaxPvKw(sitePid: $sitePid, value: $value) {
        pid
        carportMaxPvKw
      }
    }
  }
}
    `)

export const useSetCarportMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCarportMaxPvKwMutation,
    TError,
    Types.SetCarportMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCarportMaxPvKwMutation,
    TError,
    Types.SetCarportMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetCarportMaxPvKw"],
    mutationFn: (variables?: Types.SetCarportMaxPvKwMutationVariables) =>
      publicFetcher<
        Types.SetCarportMaxPvKwMutation,
        Types.SetCarportMaxPvKwMutationVariables
      >(SetCarportMaxPvKwDocument, variables)(),
    ...options,
  })
}

useSetCarportMaxPvKwMutation.fetcher = (
  variables: Types.SetCarportMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCarportMaxPvKwMutation,
    Types.SetCarportMaxPvKwMutationVariables
  >(SetCarportMaxPvKwDocument, variables, options)

export const SetGroundMaxPvKwDocument = new TypedDocumentString(`
    mutation SetGroundMaxPvKw($sitePid: String!, $value: Float) {
  admin {
    site {
      setGroundMaxPvKw(sitePid: $sitePid, value: $value) {
        pid
        groundMaxPvKw
      }
    }
  }
}
    `)

export const useSetGroundMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetGroundMaxPvKwMutation,
    TError,
    Types.SetGroundMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetGroundMaxPvKwMutation,
    TError,
    Types.SetGroundMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetGroundMaxPvKw"],
    mutationFn: (variables?: Types.SetGroundMaxPvKwMutationVariables) =>
      publicFetcher<
        Types.SetGroundMaxPvKwMutation,
        Types.SetGroundMaxPvKwMutationVariables
      >(SetGroundMaxPvKwDocument, variables)(),
    ...options,
  })
}

useSetGroundMaxPvKwMutation.fetcher = (
  variables: Types.SetGroundMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetGroundMaxPvKwMutation,
    Types.SetGroundMaxPvKwMutationVariables
  >(SetGroundMaxPvKwDocument, variables, options)

export const SetTariffFieldsDocument = new TypedDocumentString(`
    mutation SetTariffFields($id: Int!, $selectionStatus: TariffSelectionStatus!, $requiresPv: Boolean, $requiresStorage: Boolean) {
  admin {
    tariff {
      setFields(
        id: $id
        selectionStatus: $selectionStatus
        requiresPv: $requiresPv
        requiresStorage: $requiresStorage
      ) {
        id
        selectionStatus
        requiresPv
        requiresStorage
      }
    }
  }
}
    `)

export const useSetTariffFieldsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetTariffFieldsMutation,
    TError,
    Types.SetTariffFieldsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetTariffFieldsMutation,
    TError,
    Types.SetTariffFieldsMutationVariables,
    TContext
  >({
    mutationKey: ["SetTariffFields"],
    mutationFn: (variables?: Types.SetTariffFieldsMutationVariables) =>
      publicFetcher<
        Types.SetTariffFieldsMutation,
        Types.SetTariffFieldsMutationVariables
      >(SetTariffFieldsDocument, variables)(),
    ...options,
  })
}

useSetTariffFieldsMutation.fetcher = (
  variables: Types.SetTariffFieldsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetTariffFieldsMutation, Types.SetTariffFieldsMutationVariables>(
    SetTariffFieldsDocument,
    variables,
    options,
  )

export const CreateTariffDraftDocument = new TypedDocumentString(`
    mutation CreateTariffDraft($utilityPid: String!, $fromRevisionId: Int!, $effectiveDate: Date!) {
  createTariffDraft(
    utilityPid: $utilityPid
    fromRevisionId: $fromRevisionId
    effectiveDate: $effectiveDate
  ) {
    pid
  }
}
    `)

export const useCreateTariffDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateTariffDraftMutation,
    TError,
    Types.CreateTariffDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateTariffDraftMutation,
    TError,
    Types.CreateTariffDraftMutationVariables,
    TContext
  >({
    mutationKey: ["CreateTariffDraft"],
    mutationFn: (variables?: Types.CreateTariffDraftMutationVariables) =>
      publicFetcher<
        Types.CreateTariffDraftMutation,
        Types.CreateTariffDraftMutationVariables
      >(CreateTariffDraftDocument, variables)(),
    ...options,
  })
}

useCreateTariffDraftMutation.fetcher = (
  variables: Types.CreateTariffDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateTariffDraftMutation,
    Types.CreateTariffDraftMutationVariables
  >(CreateTariffDraftDocument, variables, options)

export const CopyTariffDocument = new TypedDocumentString(`
    mutation CopyTariff($fromRevisionId: Int!, $effectiveDate: Date!, $name: String!) {
  copyTariff(
    fromRevisionId: $fromRevisionId
    effectiveDate: $effectiveDate
    name: $name
  ) {
    pid
    tariff {
      id
    }
  }
}
    `)

export const useCopyTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CopyTariffMutation,
    TError,
    Types.CopyTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CopyTariffMutation,
    TError,
    Types.CopyTariffMutationVariables,
    TContext
  >({
    mutationKey: ["CopyTariff"],
    mutationFn: (variables?: Types.CopyTariffMutationVariables) =>
      publicFetcher<Types.CopyTariffMutation, Types.CopyTariffMutationVariables>(
        CopyTariffDocument,
        variables,
      )(),
    ...options,
  })
}

useCopyTariffMutation.fetcher = (
  variables: Types.CopyTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CopyTariffMutation, Types.CopyTariffMutationVariables>(
    CopyTariffDocument,
    variables,
    options,
  )

export const UpdateTariffDraftDocument = new TypedDocumentString(`
    mutation UpdateTariffDraft($pid: String!, $document: JSON!, $version: Int!, $effectiveDate: Date) {
  updateTariffDraft(
    pid: $pid
    document: $document
    version: $version
    effectiveDate: $effectiveDate
  ) {
    pid
    version
  }
}
    `)

export const useUpdateTariffDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateTariffDraftMutation,
    TError,
    Types.UpdateTariffDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateTariffDraftMutation,
    TError,
    Types.UpdateTariffDraftMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateTariffDraft"],
    mutationFn: (variables?: Types.UpdateTariffDraftMutationVariables) =>
      publicFetcher<
        Types.UpdateTariffDraftMutation,
        Types.UpdateTariffDraftMutationVariables
      >(UpdateTariffDraftDocument, variables)(),
    ...options,
  })
}

useUpdateTariffDraftMutation.fetcher = (
  variables: Types.UpdateTariffDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateTariffDraftMutation,
    Types.UpdateTariffDraftMutationVariables
  >(UpdateTariffDraftDocument, variables, options)

export const MarkTariffDraftReadyDocument = new TypedDocumentString(`
    mutation MarkTariffDraftReady($pid: String!) {
  markTariffDraftReady(pid: $pid) {
    pid
  }
}
    `)

export const useMarkTariffDraftReadyMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.MarkTariffDraftReadyMutation,
    TError,
    Types.MarkTariffDraftReadyMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MarkTariffDraftReadyMutation,
    TError,
    Types.MarkTariffDraftReadyMutationVariables,
    TContext
  >({
    mutationKey: ["MarkTariffDraftReady"],
    mutationFn: (variables?: Types.MarkTariffDraftReadyMutationVariables) =>
      publicFetcher<
        Types.MarkTariffDraftReadyMutation,
        Types.MarkTariffDraftReadyMutationVariables
      >(MarkTariffDraftReadyDocument, variables)(),
    ...options,
  })
}

useMarkTariffDraftReadyMutation.fetcher = (
  variables: Types.MarkTariffDraftReadyMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarkTariffDraftReadyMutation,
    Types.MarkTariffDraftReadyMutationVariables
  >(MarkTariffDraftReadyDocument, variables, options)

export const ReopenTariffDraftDocument = new TypedDocumentString(`
    mutation ReopenTariffDraft($pid: String!) {
  reopenTariffDraft(pid: $pid) {
    pid
  }
}
    `)

export const useReopenTariffDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ReopenTariffDraftMutation,
    TError,
    Types.ReopenTariffDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ReopenTariffDraftMutation,
    TError,
    Types.ReopenTariffDraftMutationVariables,
    TContext
  >({
    mutationKey: ["ReopenTariffDraft"],
    mutationFn: (variables?: Types.ReopenTariffDraftMutationVariables) =>
      publicFetcher<
        Types.ReopenTariffDraftMutation,
        Types.ReopenTariffDraftMutationVariables
      >(ReopenTariffDraftDocument, variables)(),
    ...options,
  })
}

useReopenTariffDraftMutation.fetcher = (
  variables: Types.ReopenTariffDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ReopenTariffDraftMutation,
    Types.ReopenTariffDraftMutationVariables
  >(ReopenTariffDraftDocument, variables, options)

export const PublishTariffDraftDocument = new TypedDocumentString(`
    mutation PublishTariffDraft($pid: String!) {
  publishTariffDraft(pid: $pid) {
    pid
    tariff {
      id
    }
    publishedRevision {
      id
    }
  }
}
    `)

export const usePublishTariffDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.PublishTariffDraftMutation,
    TError,
    Types.PublishTariffDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.PublishTariffDraftMutation,
    TError,
    Types.PublishTariffDraftMutationVariables,
    TContext
  >({
    mutationKey: ["PublishTariffDraft"],
    mutationFn: (variables?: Types.PublishTariffDraftMutationVariables) =>
      publicFetcher<
        Types.PublishTariffDraftMutation,
        Types.PublishTariffDraftMutationVariables
      >(PublishTariffDraftDocument, variables)(),
    ...options,
  })
}

usePublishTariffDraftMutation.fetcher = (
  variables: Types.PublishTariffDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PublishTariffDraftMutation,
    Types.PublishTariffDraftMutationVariables
  >(PublishTariffDraftDocument, variables, options)

export const ReturnTariffToAutomaticDocument = new TypedDocumentString(`
    mutation ReturnTariffToAutomatic($tariffPid: String!) {
  returnTariffToAutomatic(tariffPid: $tariffPid) {
    id
    ownership
  }
}
    `)

export const useReturnTariffToAutomaticMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ReturnTariffToAutomaticMutation,
    TError,
    Types.ReturnTariffToAutomaticMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ReturnTariffToAutomaticMutation,
    TError,
    Types.ReturnTariffToAutomaticMutationVariables,
    TContext
  >({
    mutationKey: ["ReturnTariffToAutomatic"],
    mutationFn: (variables?: Types.ReturnTariffToAutomaticMutationVariables) =>
      publicFetcher<
        Types.ReturnTariffToAutomaticMutation,
        Types.ReturnTariffToAutomaticMutationVariables
      >(ReturnTariffToAutomaticDocument, variables)(),
    ...options,
  })
}

useReturnTariffToAutomaticMutation.fetcher = (
  variables: Types.ReturnTariffToAutomaticMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ReturnTariffToAutomaticMutation,
    Types.ReturnTariffToAutomaticMutationVariables
  >(ReturnTariffToAutomaticDocument, variables, options)

export const ArchiveTariffDraftDocument = new TypedDocumentString(`
    mutation ArchiveTariffDraft($pid: String!) {
  archiveTariffDraft(pid: $pid) {
    pid
  }
}
    `)

export const useArchiveTariffDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveTariffDraftMutation,
    TError,
    Types.ArchiveTariffDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveTariffDraftMutation,
    TError,
    Types.ArchiveTariffDraftMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveTariffDraft"],
    mutationFn: (variables?: Types.ArchiveTariffDraftMutationVariables) =>
      publicFetcher<
        Types.ArchiveTariffDraftMutation,
        Types.ArchiveTariffDraftMutationVariables
      >(ArchiveTariffDraftDocument, variables)(),
    ...options,
  })
}

useArchiveTariffDraftMutation.fetcher = (
  variables: Types.ArchiveTariffDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ArchiveTariffDraftMutation,
    Types.ArchiveTariffDraftMutationVariables
  >(ArchiveTariffDraftDocument, variables, options)

export const UpdateTariffDocument = new TypedDocumentString(`
    mutation UpdateTariff($id: Int!, $input: UpdateTariffParentInput!) {
  admin {
    tariff {
      updateParent(id: $id, input: $input) {
        id
        name
        code
        tariffType
        customerClass
        minMonthlyKwh
        maxMonthlyKwh
        minMonthlyKw
        maxMonthlyKw
        selectionStatus
        requiresPv
        requiresStorage
        notes
        states
      }
    }
  }
}
    `)

export const useUpdateTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateTariffMutation,
    TError,
    Types.UpdateTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateTariffMutation,
    TError,
    Types.UpdateTariffMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateTariff"],
    mutationFn: (variables?: Types.UpdateTariffMutationVariables) =>
      publicFetcher<Types.UpdateTariffMutation, Types.UpdateTariffMutationVariables>(
        UpdateTariffDocument,
        variables,
      )(),
    ...options,
  })
}

useUpdateTariffMutation.fetcher = (
  variables: Types.UpdateTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UpdateTariffMutation, Types.UpdateTariffMutationVariables>(
    UpdateTariffDocument,
    variables,
    options,
  )

export const SetPortfolioMinKwDocument = new TypedDocumentString(`
    mutation SetPortfolioMinKw($portfolioPid: String!, $minKw: Float) {
  admin {
    portfolio {
      setPortfolioMinKw(portfolioPid: $portfolioPid, minKw: $minKw) {
        pid
        minKw
        minKwSetBy
        minKwSetDate
      }
    }
  }
}
    `)

export const useSetPortfolioMinKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPortfolioMinKwMutation,
    TError,
    Types.SetPortfolioMinKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPortfolioMinKwMutation,
    TError,
    Types.SetPortfolioMinKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetPortfolioMinKw"],
    mutationFn: (variables?: Types.SetPortfolioMinKwMutationVariables) =>
      publicFetcher<
        Types.SetPortfolioMinKwMutation,
        Types.SetPortfolioMinKwMutationVariables
      >(SetPortfolioMinKwDocument, variables)(),
    ...options,
  })
}

useSetPortfolioMinKwMutation.fetcher = (
  variables: Types.SetPortfolioMinKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPortfolioMinKwMutation,
    Types.SetPortfolioMinKwMutationVariables
  >(SetPortfolioMinKwDocument, variables, options)

export const AdminSetSiteStatusDocument = new TypedDocumentString(`
    mutation AdminSetSiteStatus($sitePid: String!, $status: SiteStatus!, $reason: StatusReason, $customReason: String, $unixtimeSec: Int, $solarLabelSource: HumanSolarLabelSource) {
  admin {
    site {
      setStatus(
        status: $status
        sitePid: $sitePid
        reason: $reason
        customReason: $customReason
        unixtimeSec: $unixtimeSec
        solarLabelSource: $solarLabelSource
      ) {
        pid
        currentStatus {
          pid
        }
      }
    }
  }
}
    `)

export const useAdminSetSiteStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AdminSetSiteStatusMutation,
    TError,
    Types.AdminSetSiteStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminSetSiteStatusMutation,
    TError,
    Types.AdminSetSiteStatusMutationVariables,
    TContext
  >({
    mutationKey: ["AdminSetSiteStatus"],
    mutationFn: (variables?: Types.AdminSetSiteStatusMutationVariables) =>
      publicFetcher<
        Types.AdminSetSiteStatusMutation,
        Types.AdminSetSiteStatusMutationVariables
      >(AdminSetSiteStatusDocument, variables)(),
    ...options,
  })
}

useAdminSetSiteStatusMutation.fetcher = (
  variables: Types.AdminSetSiteStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSetSiteStatusMutation,
    Types.AdminSetSiteStatusMutationVariables
  >(AdminSetSiteStatusDocument, variables, options)

export const SetPrimaryScenarioDocument = new TypedDocumentString(`
    mutation SetPrimaryScenario($scenarioPid: String!) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      setPrimary {
        pid
        scenarios {
          pid
          isPrimary
        }
      }
    }
  }
}
    `)

export const useSetPrimaryScenarioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPrimaryScenarioMutation,
    TError,
    Types.SetPrimaryScenarioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPrimaryScenarioMutation,
    TError,
    Types.SetPrimaryScenarioMutationVariables,
    TContext
  >({
    mutationKey: ["SetPrimaryScenario"],
    mutationFn: (variables?: Types.SetPrimaryScenarioMutationVariables) =>
      publicFetcher<
        Types.SetPrimaryScenarioMutation,
        Types.SetPrimaryScenarioMutationVariables
      >(SetPrimaryScenarioDocument, variables)(),
    ...options,
  })
}

useSetPrimaryScenarioMutation.fetcher = (
  variables: Types.SetPrimaryScenarioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPrimaryScenarioMutation,
    Types.SetPrimaryScenarioMutationVariables
  >(SetPrimaryScenarioDocument, variables, options)

export const SetScenarioNameDocument = new TypedDocumentString(`
    mutation SetScenarioName($scenarioPid: String!, $name: String!) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      setName(name: $name) {
        pid
        scenarios {
          pid
          name
        }
      }
    }
  }
}
    `)

export const useSetScenarioNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioNameMutation,
    TError,
    Types.SetScenarioNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioNameMutation,
    TError,
    Types.SetScenarioNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioName"],
    mutationFn: (variables?: Types.SetScenarioNameMutationVariables) =>
      publicFetcher<
        Types.SetScenarioNameMutation,
        Types.SetScenarioNameMutationVariables
      >(SetScenarioNameDocument, variables)(),
    ...options,
  })
}

useSetScenarioNameMutation.fetcher = (
  variables: Types.SetScenarioNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetScenarioNameMutation, Types.SetScenarioNameMutationVariables>(
    SetScenarioNameDocument,
    variables,
    options,
  )

export const AddScenarioDocument = new TypedDocumentString(`
    mutation AddScenario($sitePid: String!, $solarLocation: SolarLocation!, $includeStorage: Boolean!, $loadCategory: LoadCategory!, $name: String) {
  admin {
    site {
      addScenario(
        sitePid: $sitePid
        solarLocation: $solarLocation
        includeStorage: $includeStorage
        loadCategory: $loadCategory
        name: $name
      ) {
        __typename
        ... on ScenarioRef {
          pid
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useAddScenarioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddScenarioMutation,
    TError,
    Types.AddScenarioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddScenarioMutation,
    TError,
    Types.AddScenarioMutationVariables,
    TContext
  >({
    mutationKey: ["AddScenario"],
    mutationFn: (variables?: Types.AddScenarioMutationVariables) =>
      publicFetcher<Types.AddScenarioMutation, Types.AddScenarioMutationVariables>(
        AddScenarioDocument,
        variables,
      )(),
    ...options,
  })
}

useAddScenarioMutation.fetcher = (
  variables: Types.AddScenarioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddScenarioMutation, Types.AddScenarioMutationVariables>(
    AddScenarioDocument,
    variables,
    options,
  )

export const ToggleScenarioVisibilityDocument = new TypedDocumentString(`
    mutation ToggleScenarioVisibility($scenarioPid: String!, $hidden: Boolean!) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      toggleVisibility(hidden: $hidden) {
        pid
        hiddenFromCustomers
      }
    }
  }
}
    `)

export const useToggleScenarioVisibilityMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ToggleScenarioVisibilityMutation,
    TError,
    Types.ToggleScenarioVisibilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleScenarioVisibilityMutation,
    TError,
    Types.ToggleScenarioVisibilityMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleScenarioVisibility"],
    mutationFn: (variables?: Types.ToggleScenarioVisibilityMutationVariables) =>
      publicFetcher<
        Types.ToggleScenarioVisibilityMutation,
        Types.ToggleScenarioVisibilityMutationVariables
      >(ToggleScenarioVisibilityDocument, variables)(),
    ...options,
  })
}

useToggleScenarioVisibilityMutation.fetcher = (
  variables: Types.ToggleScenarioVisibilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ToggleScenarioVisibilityMutation,
    Types.ToggleScenarioVisibilityMutationVariables
  >(ToggleScenarioVisibilityDocument, variables, options)

export const ToggleMulticonnectDocument = new TypedDocumentString(`
    mutation ToggleMulticonnect($scenarioPid: String!, $allow: Boolean!) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      toggleMulticonnect(allow: $allow) {
        pid
        allowMulticonnect
      }
    }
  }
}
    `)

export const useToggleMulticonnectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ToggleMulticonnectMutation,
    TError,
    Types.ToggleMulticonnectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleMulticonnectMutation,
    TError,
    Types.ToggleMulticonnectMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleMulticonnect"],
    mutationFn: (variables?: Types.ToggleMulticonnectMutationVariables) =>
      publicFetcher<
        Types.ToggleMulticonnectMutation,
        Types.ToggleMulticonnectMutationVariables
      >(ToggleMulticonnectDocument, variables)(),
    ...options,
  })
}

useToggleMulticonnectMutation.fetcher = (
  variables: Types.ToggleMulticonnectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ToggleMulticonnectMutation,
    Types.ToggleMulticonnectMutationVariables
  >(ToggleMulticonnectDocument, variables, options)

export const SetTargetUtilityDocument = new TypedDocumentString(`
    mutation SetTargetUtility($sitePid: String!, $utilityId: Int!) {
  admin {
    site {
      setTargetUtility(sitePid: $sitePid, utilityId: $utilityId) {
        pid
        targetUtility {
          id
          name
        }
        targetUtilitySetBy {
          ...UserRef
        }
        dateTargetUtilitySet
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useSetTargetUtilityMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetTargetUtilityMutation,
    TError,
    Types.SetTargetUtilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetTargetUtilityMutation,
    TError,
    Types.SetTargetUtilityMutationVariables,
    TContext
  >({
    mutationKey: ["SetTargetUtility"],
    mutationFn: (variables?: Types.SetTargetUtilityMutationVariables) =>
      publicFetcher<
        Types.SetTargetUtilityMutation,
        Types.SetTargetUtilityMutationVariables
      >(SetTargetUtilityDocument, variables)(),
    ...options,
  })
}

useSetTargetUtilityMutation.fetcher = (
  variables: Types.SetTargetUtilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetTargetUtilityMutation,
    Types.SetTargetUtilityMutationVariables
  >(SetTargetUtilityDocument, variables, options)

export const ReassessUtilitiesDocument = new TypedDocumentString(`
    mutation ReassessUtilities($sitePids: [String!]!, $force: Boolean! = false) {
  admin {
    site {
      reassessUtilities(sitePids: $sitePids, force: $force) {
        matchedCount
        enqueuedCount
      }
    }
  }
}
    `)

export const useReassessUtilitiesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ReassessUtilitiesMutation,
    TError,
    Types.ReassessUtilitiesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ReassessUtilitiesMutation,
    TError,
    Types.ReassessUtilitiesMutationVariables,
    TContext
  >({
    mutationKey: ["ReassessUtilities"],
    mutationFn: (variables?: Types.ReassessUtilitiesMutationVariables) =>
      publicFetcher<
        Types.ReassessUtilitiesMutation,
        Types.ReassessUtilitiesMutationVariables
      >(ReassessUtilitiesDocument, variables)(),
    ...options,
  })
}

useReassessUtilitiesMutation.fetcher = (
  variables: Types.ReassessUtilitiesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ReassessUtilitiesMutation,
    Types.ReassessUtilitiesMutationVariables
  >(ReassessUtilitiesDocument, variables, options)

export const SetThirdPartySupplierDocument = new TypedDocumentString(`
    mutation SetThirdPartySupplier($sitePid: String!, $thirdPartySupplierId: Int) {
  admin {
    site {
      setThirdPartySupplier(
        sitePid: $sitePid
        thirdPartySupplierId: $thirdPartySupplierId
      ) {
        pid
        thirdPartySupplier {
          id
          name
        }
      }
    }
  }
}
    `)

export const useSetThirdPartySupplierMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetThirdPartySupplierMutation,
    TError,
    Types.SetThirdPartySupplierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetThirdPartySupplierMutation,
    TError,
    Types.SetThirdPartySupplierMutationVariables,
    TContext
  >({
    mutationKey: ["SetThirdPartySupplier"],
    mutationFn: (variables?: Types.SetThirdPartySupplierMutationVariables) =>
      publicFetcher<
        Types.SetThirdPartySupplierMutation,
        Types.SetThirdPartySupplierMutationVariables
      >(SetThirdPartySupplierDocument, variables)(),
    ...options,
  })
}

useSetThirdPartySupplierMutation.fetcher = (
  variables: Types.SetThirdPartySupplierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetThirdPartySupplierMutation,
    Types.SetThirdPartySupplierMutationVariables
  >(SetThirdPartySupplierDocument, variables, options)

export const SetLeaseTypeDocument = new TypedDocumentString(`
    mutation SetLeaseType($sitePids: [String!]!, $leaseType: LeaseType) {
  admin {
    site {
      setLeaseType(sitePids: $sitePids, leaseType: $leaseType)
    }
  }
}
    `)

export const useSetLeaseTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLeaseTypeMutation,
    TError,
    Types.SetLeaseTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLeaseTypeMutation,
    TError,
    Types.SetLeaseTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLeaseType"],
    mutationFn: (variables?: Types.SetLeaseTypeMutationVariables) =>
      publicFetcher<Types.SetLeaseTypeMutation, Types.SetLeaseTypeMutationVariables>(
        SetLeaseTypeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLeaseTypeMutation.fetcher = (
  variables: Types.SetLeaseTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetLeaseTypeMutation, Types.SetLeaseTypeMutationVariables>(
    SetLeaseTypeDocument,
    variables,
    options,
  )

export const AdminSetMeteringDocument = new TypedDocumentString(`
    mutation AdminSetMetering($sitePids: [String!]!, $metering: Metering) {
  admin {
    site {
      setMetering(sitePids: $sitePids, metering: $metering)
    }
  }
}
    `)

export const useAdminSetMeteringMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AdminSetMeteringMutation,
    TError,
    Types.AdminSetMeteringMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminSetMeteringMutation,
    TError,
    Types.AdminSetMeteringMutationVariables,
    TContext
  >({
    mutationKey: ["AdminSetMetering"],
    mutationFn: (variables?: Types.AdminSetMeteringMutationVariables) =>
      publicFetcher<
        Types.AdminSetMeteringMutation,
        Types.AdminSetMeteringMutationVariables
      >(AdminSetMeteringDocument, variables)(),
    ...options,
  })
}

useAdminSetMeteringMutation.fetcher = (
  variables: Types.AdminSetMeteringMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSetMeteringMutation,
    Types.AdminSetMeteringMutationVariables
  >(AdminSetMeteringDocument, variables, options)

export const SetOccupancyTypeDocument = new TypedDocumentString(`
    mutation SetOccupancyType($sitePids: [String!]!, $occupancyType: BuildingOccupancyType) {
  admin {
    site {
      setOccupancyType(sitePids: $sitePids, occupancyType: $occupancyType)
    }
  }
}
    `)

export const useSetOccupancyTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOccupancyTypeMutation,
    TError,
    Types.SetOccupancyTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOccupancyTypeMutation,
    TError,
    Types.SetOccupancyTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetOccupancyType"],
    mutationFn: (variables?: Types.SetOccupancyTypeMutationVariables) =>
      publicFetcher<
        Types.SetOccupancyTypeMutation,
        Types.SetOccupancyTypeMutationVariables
      >(SetOccupancyTypeDocument, variables)(),
    ...options,
  })
}

useSetOccupancyTypeMutation.fetcher = (
  variables: Types.SetOccupancyTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetOccupancyTypeMutation,
    Types.SetOccupancyTypeMutationVariables
  >(SetOccupancyTypeDocument, variables, options)

export const RemoveUserFromPortfolioDocument = new TypedDocumentString(`
    mutation RemoveUserFromPortfolio($portfolioPid: String!, $userId: Int!) {
  admin {
    portfolio {
      removeUserFromPortfolio(portfolioPid: $portfolioPid, userId: $userId) {
        pid
        users {
          id
          email
        }
      }
    }
  }
}
    `)

export const useRemoveUserFromPortfolioMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveUserFromPortfolioMutation,
    TError,
    Types.RemoveUserFromPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveUserFromPortfolioMutation,
    TError,
    Types.RemoveUserFromPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveUserFromPortfolio"],
    mutationFn: (variables?: Types.RemoveUserFromPortfolioMutationVariables) =>
      publicFetcher<
        Types.RemoveUserFromPortfolioMutation,
        Types.RemoveUserFromPortfolioMutationVariables
      >(RemoveUserFromPortfolioDocument, variables)(),
    ...options,
  })
}

useRemoveUserFromPortfolioMutation.fetcher = (
  variables: Types.RemoveUserFromPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveUserFromPortfolioMutation,
    Types.RemoveUserFromPortfolioMutationVariables
  >(RemoveUserFromPortfolioDocument, variables, options)

export const RenamePortfolioDocument = new TypedDocumentString(`
    mutation RenamePortfolio($portfolioPid: String!, $newName: String!) {
  admin {
    portfolio {
      rename(portfolioPid: $portfolioPid, newName: $newName) {
        pid
        title
      }
    }
  }
}
    `)

export const useRenamePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RenamePortfolioMutation,
    TError,
    Types.RenamePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RenamePortfolioMutation,
    TError,
    Types.RenamePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RenamePortfolio"],
    mutationFn: (variables?: Types.RenamePortfolioMutationVariables) =>
      publicFetcher<
        Types.RenamePortfolioMutation,
        Types.RenamePortfolioMutationVariables
      >(RenamePortfolioDocument, variables)(),
    ...options,
  })
}

useRenamePortfolioMutation.fetcher = (
  variables: Types.RenamePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RenamePortfolioMutation, Types.RenamePortfolioMutationVariables>(
    RenamePortfolioDocument,
    variables,
    options,
  )

export const UtilityApiNameDocument = new TypedDocumentString(`
    mutation UtilityApiName($utilityId: Int!, $utilityApiName: String) {
  admin {
    utility {
      setUtilityApiName(id: $utilityId, utilityApiName: $utilityApiName) {
        id
        utilityApiName
      }
    }
  }
}
    `)

export const useUtilityApiNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityApiNameMutation,
    TError,
    Types.UtilityApiNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityApiNameMutation,
    TError,
    Types.UtilityApiNameMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityApiName"],
    mutationFn: (variables?: Types.UtilityApiNameMutationVariables) =>
      publicFetcher<
        Types.UtilityApiNameMutation,
        Types.UtilityApiNameMutationVariables
      >(UtilityApiNameDocument, variables)(),
    ...options,
  })
}

useUtilityApiNameMutation.fetcher = (
  variables: Types.UtilityApiNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityApiNameMutation, Types.UtilityApiNameMutationVariables>(
    UtilityApiNameDocument,
    variables,
    options,
  )

export const UtilityIsPreferredDocument = new TypedDocumentString(`
    mutation UtilityIsPreferred($utilityId: Int!, $isPreferred: Boolean) {
  admin {
    utility {
      setIsPreferred(id: $utilityId, isPreferred: $isPreferred) {
        id
        isPreferred
      }
    }
  }
}
    `)

export const useUtilityIsPreferredMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityIsPreferredMutation,
    TError,
    Types.UtilityIsPreferredMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityIsPreferredMutation,
    TError,
    Types.UtilityIsPreferredMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityIsPreferred"],
    mutationFn: (variables?: Types.UtilityIsPreferredMutationVariables) =>
      publicFetcher<
        Types.UtilityIsPreferredMutation,
        Types.UtilityIsPreferredMutationVariables
      >(UtilityIsPreferredDocument, variables)(),
    ...options,
  })
}

useUtilityIsPreferredMutation.fetcher = (
  variables: Types.UtilityIsPreferredMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UtilityIsPreferredMutation,
    Types.UtilityIsPreferredMutationVariables
  >(UtilityIsPreferredDocument, variables, options)

export const UtilityNotesDocument = new TypedDocumentString(`
    mutation UtilityNotes($utilityId: Int!, $notes: String) {
  admin {
    utility {
      setNotes(id: $utilityId, notes: $notes) {
        id
        notes
      }
    }
  }
}
    `)

export const useUtilityNotesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityNotesMutation,
    TError,
    Types.UtilityNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityNotesMutation,
    TError,
    Types.UtilityNotesMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityNotes"],
    mutationFn: (variables?: Types.UtilityNotesMutationVariables) =>
      publicFetcher<Types.UtilityNotesMutation, Types.UtilityNotesMutationVariables>(
        UtilityNotesDocument,
        variables,
      )(),
    ...options,
  })
}

useUtilityNotesMutation.fetcher = (
  variables: Types.UtilityNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityNotesMutation, Types.UtilityNotesMutationVariables>(
    UtilityNotesDocument,
    variables,
    options,
  )

export const UtilityStatesDocument = new TypedDocumentString(`
    mutation UtilityStates($utilityId: Int!, $states: [USState!]!) {
  admin {
    utility {
      setStates(id: $utilityId, states: $states) {
        id
        states
      }
    }
  }
}
    `)

export const useUtilityStatesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityStatesMutation,
    TError,
    Types.UtilityStatesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityStatesMutation,
    TError,
    Types.UtilityStatesMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityStates"],
    mutationFn: (variables?: Types.UtilityStatesMutationVariables) =>
      publicFetcher<Types.UtilityStatesMutation, Types.UtilityStatesMutationVariables>(
        UtilityStatesDocument,
        variables,
      )(),
    ...options,
  })
}

useUtilityStatesMutation.fetcher = (
  variables: Types.UtilityStatesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityStatesMutation, Types.UtilityStatesMutationVariables>(
    UtilityStatesDocument,
    variables,
    options,
  )

export const UtilityVnemSupportedDocument = new TypedDocumentString(`
    mutation UtilityVnemSupported($utilityId: Int!, $vnemSupported: Boolean!) {
  admin {
    utility {
      setVnemSupported(id: $utilityId, vnemSupported: $vnemSupported) {
        id
        vnemSupported
      }
    }
  }
}
    `)

export const useUtilityVnemSupportedMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityVnemSupportedMutation,
    TError,
    Types.UtilityVnemSupportedMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityVnemSupportedMutation,
    TError,
    Types.UtilityVnemSupportedMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityVnemSupported"],
    mutationFn: (variables?: Types.UtilityVnemSupportedMutationVariables) =>
      publicFetcher<
        Types.UtilityVnemSupportedMutation,
        Types.UtilityVnemSupportedMutationVariables
      >(UtilityVnemSupportedDocument, variables)(),
    ...options,
  })
}

useUtilityVnemSupportedMutation.fetcher = (
  variables: Types.UtilityVnemSupportedMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UtilityVnemSupportedMutation,
    Types.UtilityVnemSupportedMutationVariables
  >(UtilityVnemSupportedDocument, variables, options)

export const UtilityFtmBessProspectingSupportedDocument = new TypedDocumentString(`
    mutation UtilityFtmBessProspectingSupported($utilityId: Int!, $ftmBessProspectingSupported: Boolean!) {
  admin {
    utility {
      setFtmBessProspectingSupported(
        id: $utilityId
        ftmBessProspectingSupported: $ftmBessProspectingSupported
      ) {
        id
        ftmBessProspectingSupported
      }
    }
  }
}
    `)

export const useUtilityFtmBessProspectingSupportedMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UtilityFtmBessProspectingSupportedMutation,
    TError,
    Types.UtilityFtmBessProspectingSupportedMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityFtmBessProspectingSupportedMutation,
    TError,
    Types.UtilityFtmBessProspectingSupportedMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityFtmBessProspectingSupported"],
    mutationFn: (
      variables?: Types.UtilityFtmBessProspectingSupportedMutationVariables,
    ) =>
      publicFetcher<
        Types.UtilityFtmBessProspectingSupportedMutation,
        Types.UtilityFtmBessProspectingSupportedMutationVariables
      >(UtilityFtmBessProspectingSupportedDocument, variables)(),
    ...options,
  })
}

useUtilityFtmBessProspectingSupportedMutation.fetcher = (
  variables: Types.UtilityFtmBessProspectingSupportedMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UtilityFtmBessProspectingSupportedMutation,
    Types.UtilityFtmBessProspectingSupportedMutationVariables
  >(UtilityFtmBessProspectingSupportedDocument, variables, options)

export const UtilityCodeDocument = new TypedDocumentString(`
    mutation UtilityCode($utilityId: Int!, $code: String) {
  admin {
    utility {
      setCode(id: $utilityId, code: $code) {
        id
        code
      }
    }
  }
}
    `)

export const useUtilityCodeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityCodeMutation,
    TError,
    Types.UtilityCodeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityCodeMutation,
    TError,
    Types.UtilityCodeMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityCode"],
    mutationFn: (variables?: Types.UtilityCodeMutationVariables) =>
      publicFetcher<Types.UtilityCodeMutation, Types.UtilityCodeMutationVariables>(
        UtilityCodeDocument,
        variables,
      )(),
    ...options,
  })
}

useUtilityCodeMutation.fetcher = (
  variables: Types.UtilityCodeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityCodeMutation, Types.UtilityCodeMutationVariables>(
    UtilityCodeDocument,
    variables,
    options,
  )

export const UtilityEiaGridEscDocument = new TypedDocumentString(`
    mutation UtilityEiaGridEsc($utilityId: Int!, $eiaGridEscId: Int) {
  admin {
    utility {
      setEiaGridEscId(id: $utilityId, eiaGridEscId: $eiaGridEscId) {
        id
        eiaGridEsc {
          eiaId
        }
      }
    }
  }
}
    `)

export const useUtilityEiaGridEscMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityEiaGridEscMutation,
    TError,
    Types.UtilityEiaGridEscMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityEiaGridEscMutation,
    TError,
    Types.UtilityEiaGridEscMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityEiaGridEsc"],
    mutationFn: (variables?: Types.UtilityEiaGridEscMutationVariables) =>
      publicFetcher<
        Types.UtilityEiaGridEscMutation,
        Types.UtilityEiaGridEscMutationVariables
      >(UtilityEiaGridEscDocument, variables)(),
    ...options,
  })
}

useUtilityEiaGridEscMutation.fetcher = (
  variables: Types.UtilityEiaGridEscMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UtilityEiaGridEscMutation,
    Types.UtilityEiaGridEscMutationVariables
  >(UtilityEiaGridEscDocument, variables, options)

export const SetScenarioHelioscopeDocument = new TypedDocumentString(`
    mutation SetScenarioHelioscope($scenarioPid: String!, $helioscopePid: String, $finproductId: Int, $finproductType: FinProductType) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      setHelioscope(
        helioscopePid: $helioscopePid
        finproductId: $finproductId
        finproductType: $finproductType
      ) {
        __typename
        pid
      }
    }
  }
}
    `)

export const useSetScenarioHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioHelioscopeMutation,
    TError,
    Types.SetScenarioHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioHelioscopeMutation,
    TError,
    Types.SetScenarioHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioHelioscope"],
    mutationFn: (variables?: Types.SetScenarioHelioscopeMutationVariables) =>
      publicFetcher<
        Types.SetScenarioHelioscopeMutation,
        Types.SetScenarioHelioscopeMutationVariables
      >(SetScenarioHelioscopeDocument, variables)(),
    ...options,
  })
}

useSetScenarioHelioscopeMutation.fetcher = (
  variables: Types.SetScenarioHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetScenarioHelioscopeMutation,
    Types.SetScenarioHelioscopeMutationVariables
  >(SetScenarioHelioscopeDocument, variables, options)

export const TrashHelioscopeDocument = new TypedDocumentString(`
    mutation TrashHelioscope($sitePid: String!, $helioscopePid: String!) {
  admin {
    site {
      trashHelioscope(sitePid: $sitePid, helioscopePid: $helioscopePid) {
        pid
      }
    }
  }
}
    `)

export const useTrashHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashHelioscopeMutation,
    TError,
    Types.TrashHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashHelioscopeMutation,
    TError,
    Types.TrashHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["TrashHelioscope"],
    mutationFn: (variables?: Types.TrashHelioscopeMutationVariables) =>
      publicFetcher<
        Types.TrashHelioscopeMutation,
        Types.TrashHelioscopeMutationVariables
      >(TrashHelioscopeDocument, variables)(),
    ...options,
  })
}

useTrashHelioscopeMutation.fetcher = (
  variables: Types.TrashHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TrashHelioscopeMutation, Types.TrashHelioscopeMutationVariables>(
    TrashHelioscopeDocument,
    variables,
    options,
  )

export const SetOrgNameDocument = new TypedDocumentString(`
    mutation setOrgName($pid: String!, $value: String!) {
  admin {
    orgs {
      setName(pid: $pid, name: $value) {
        pid
        name
      }
    }
  }
}
    `)

export const useSetOrgNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgNameMutation,
    TError,
    Types.SetOrgNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgNameMutation,
    TError,
    Types.SetOrgNameMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgName"],
    mutationFn: (variables?: Types.SetOrgNameMutationVariables) =>
      publicFetcher<Types.SetOrgNameMutation, Types.SetOrgNameMutationVariables>(
        SetOrgNameDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgNameMutation.fetcher = (
  variables: Types.SetOrgNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetOrgNameMutation, Types.SetOrgNameMutationVariables>(
    SetOrgNameDocument,
    variables,
    options,
  )

export const SetOrgSlugDocument = new TypedDocumentString(`
    mutation setOrgSlug($pid: String!, $value: String!) {
  admin {
    orgs {
      setSlug(pid: $pid, slug: $value) {
        pid
        slug
      }
    }
  }
}
    `)

export const useSetOrgSlugMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgSlugMutation,
    TError,
    Types.SetOrgSlugMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgSlugMutation,
    TError,
    Types.SetOrgSlugMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgSlug"],
    mutationFn: (variables?: Types.SetOrgSlugMutationVariables) =>
      publicFetcher<Types.SetOrgSlugMutation, Types.SetOrgSlugMutationVariables>(
        SetOrgSlugDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgSlugMutation.fetcher = (
  variables: Types.SetOrgSlugMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetOrgSlugMutation, Types.SetOrgSlugMutationVariables>(
    SetOrgSlugDocument,
    variables,
    options,
  )

export const SetOrgLogoDocument = new TypedDocumentString(`
    mutation setOrgLogo($pid: String!, $upload: Upload!) {
  admin {
    orgs {
      setLogo(pid: $pid, upload: $upload) {
        pid
        slug
        logoUrl
      }
    }
  }
}
    `)

export const useSetOrgLogoMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgLogoMutation,
    TError,
    Types.SetOrgLogoMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgLogoMutation,
    TError,
    Types.SetOrgLogoMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgLogo"],
    mutationFn: (variables?: Types.SetOrgLogoMutationVariables) =>
      publicFetcher<Types.SetOrgLogoMutation, Types.SetOrgLogoMutationVariables>(
        SetOrgLogoDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgLogoMutation.fetcher = (
  variables: Types.SetOrgLogoMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetOrgLogoMutation, Types.SetOrgLogoMutationVariables>(
    SetOrgLogoDocument,
    variables,
    options,
  )

export const SetOrgLogomarkDocument = new TypedDocumentString(`
    mutation setOrgLogomark($pid: String!, $upload: Upload!) {
  admin {
    orgs {
      setLogomark(pid: $pid, upload: $upload) {
        pid
        slug
        logoUrl
      }
    }
  }
}
    `)

export const useSetOrgLogomarkMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgLogomarkMutation,
    TError,
    Types.SetOrgLogomarkMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgLogomarkMutation,
    TError,
    Types.SetOrgLogomarkMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgLogomark"],
    mutationFn: (variables?: Types.SetOrgLogomarkMutationVariables) =>
      publicFetcher<
        Types.SetOrgLogomarkMutation,
        Types.SetOrgLogomarkMutationVariables
      >(SetOrgLogomarkDocument, variables)(),
    ...options,
  })
}

useSetOrgLogomarkMutation.fetcher = (
  variables: Types.SetOrgLogomarkMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetOrgLogomarkMutation, Types.SetOrgLogomarkMutationVariables>(
    SetOrgLogomarkDocument,
    variables,
    options,
  )

export const SetOrgAllowlistDocument = new TypedDocumentString(`
    mutation SetOrgAllowlist($orgPid: String!, $domains: [String!]) {
  admin {
    orgs {
      setAllowedEmailDomains(pid: $orgPid, allowedEmailDomains: $domains) {
        pid
        allowedEmailDomains
      }
    }
  }
}
    `)

export const useSetOrgAllowlistMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgAllowlistMutation,
    TError,
    Types.SetOrgAllowlistMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgAllowlistMutation,
    TError,
    Types.SetOrgAllowlistMutationVariables,
    TContext
  >({
    mutationKey: ["SetOrgAllowlist"],
    mutationFn: (variables?: Types.SetOrgAllowlistMutationVariables) =>
      publicFetcher<
        Types.SetOrgAllowlistMutation,
        Types.SetOrgAllowlistMutationVariables
      >(SetOrgAllowlistDocument, variables)(),
    ...options,
  })
}

useSetOrgAllowlistMutation.fetcher = (
  variables: Types.SetOrgAllowlistMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetOrgAllowlistMutation, Types.SetOrgAllowlistMutationVariables>(
    SetOrgAllowlistDocument,
    variables,
    options,
  )

export const SetOrgIsDeveloperDocument = new TypedDocumentString(`
    mutation setOrgIsDeveloper($pid: String!, $value: Boolean!) {
  admin {
    orgs {
      setIsDeveloper(pid: $pid, isDeveloper: $value) {
        pid
        isDeveloper
      }
    }
  }
}
    `)

export const useSetOrgIsDeveloperMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgIsDeveloperMutation,
    TError,
    Types.SetOrgIsDeveloperMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgIsDeveloperMutation,
    TError,
    Types.SetOrgIsDeveloperMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgIsDeveloper"],
    mutationFn: (variables?: Types.SetOrgIsDeveloperMutationVariables) =>
      publicFetcher<
        Types.SetOrgIsDeveloperMutation,
        Types.SetOrgIsDeveloperMutationVariables
      >(SetOrgIsDeveloperDocument, variables)(),
    ...options,
  })
}

useSetOrgIsDeveloperMutation.fetcher = (
  variables: Types.SetOrgIsDeveloperMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetOrgIsDeveloperMutation,
    Types.SetOrgIsDeveloperMutationVariables
  >(SetOrgIsDeveloperDocument, variables, options)

export const SetOrgIsInternalDocument = new TypedDocumentString(`
    mutation setOrgIsInternal($pid: String!, $value: Boolean!) {
  admin {
    orgs {
      setIsInternal(pid: $pid, isInternal: $value) {
        pid
        isInternal
      }
    }
  }
}
    `)

export const useSetOrgIsInternalMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgIsInternalMutation,
    TError,
    Types.SetOrgIsInternalMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgIsInternalMutation,
    TError,
    Types.SetOrgIsInternalMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgIsInternal"],
    mutationFn: (variables?: Types.SetOrgIsInternalMutationVariables) =>
      publicFetcher<
        Types.SetOrgIsInternalMutation,
        Types.SetOrgIsInternalMutationVariables
      >(SetOrgIsInternalDocument, variables)(),
    ...options,
  })
}

useSetOrgIsInternalMutation.fetcher = (
  variables: Types.SetOrgIsInternalMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetOrgIsInternalMutation,
    Types.SetOrgIsInternalMutationVariables
  >(SetOrgIsInternalDocument, variables, options)

export const SetOrgIsChannelPartnerDocument = new TypedDocumentString(`
    mutation setOrgIsChannelPartner($pid: String!, $value: Boolean!) {
  admin {
    orgs {
      setIsChannelPartner(pid: $pid, isChannelPartner: $value) {
        pid
        isChannelPartner
      }
    }
  }
}
    `)

export const useSetOrgIsChannelPartnerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgIsChannelPartnerMutation,
    TError,
    Types.SetOrgIsChannelPartnerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgIsChannelPartnerMutation,
    TError,
    Types.SetOrgIsChannelPartnerMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgIsChannelPartner"],
    mutationFn: (variables?: Types.SetOrgIsChannelPartnerMutationVariables) =>
      publicFetcher<
        Types.SetOrgIsChannelPartnerMutation,
        Types.SetOrgIsChannelPartnerMutationVariables
      >(SetOrgIsChannelPartnerDocument, variables)(),
    ...options,
  })
}

useSetOrgIsChannelPartnerMutation.fetcher = (
  variables: Types.SetOrgIsChannelPartnerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetOrgIsChannelPartnerMutation,
    Types.SetOrgIsChannelPartnerMutationVariables
  >(SetOrgIsChannelPartnerDocument, variables, options)

export const MergeOrgIntoDocument = new TypedDocumentString(`
    mutation MergeOrgInto($pid: String!, $intoPid: String!) {
  admin {
    orgs {
      mergeInto(pid: $pid, intoPid: $intoPid) {
        pid
        portfolios {
          pid
          title
        }
      }
    }
  }
}
    `)

export const useMergeOrgIntoMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.MergeOrgIntoMutation,
    TError,
    Types.MergeOrgIntoMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MergeOrgIntoMutation,
    TError,
    Types.MergeOrgIntoMutationVariables,
    TContext
  >({
    mutationKey: ["MergeOrgInto"],
    mutationFn: (variables?: Types.MergeOrgIntoMutationVariables) =>
      publicFetcher<Types.MergeOrgIntoMutation, Types.MergeOrgIntoMutationVariables>(
        MergeOrgIntoDocument,
        variables,
      )(),
    ...options,
  })
}

useMergeOrgIntoMutation.fetcher = (
  variables: Types.MergeOrgIntoMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MergeOrgIntoMutation, Types.MergeOrgIntoMutationVariables>(
    MergeOrgIntoDocument,
    variables,
    options,
  )

export const SetOrgHubspotIdDocument = new TypedDocumentString(`
    mutation setOrgHubspotId($pid: String!, $hubspotId: String) {
  admin {
    orgs {
      setHubspotId(pid: $pid, hubspotId: $hubspotId) {
        pid
        hubspotId
      }
    }
  }
}
    `)

export const useSetOrgHubspotIdMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgHubspotIdMutation,
    TError,
    Types.SetOrgHubspotIdMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgHubspotIdMutation,
    TError,
    Types.SetOrgHubspotIdMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgHubspotId"],
    mutationFn: (variables?: Types.SetOrgHubspotIdMutationVariables) =>
      publicFetcher<
        Types.SetOrgHubspotIdMutation,
        Types.SetOrgHubspotIdMutationVariables
      >(SetOrgHubspotIdDocument, variables)(),
    ...options,
  })
}

useSetOrgHubspotIdMutation.fetcher = (
  variables: Types.SetOrgHubspotIdMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetOrgHubspotIdMutation, Types.SetOrgHubspotIdMutationVariables>(
    SetOrgHubspotIdDocument,
    variables,
    options,
  )

export const CreateOrgDocument = new TypedDocumentString(`
    mutation createOrg($name: String!, $logo: Upload!, $logomark: Upload, $isDeveloper: Boolean!) {
  admin {
    orgs {
      createOrg(
        name: $name
        logo: $logo
        logomark: $logomark
        isDeveloper: $isDeveloper
      ) {
        pid
        name
        slug
        logoUrl
        logomarkUrl
        isDeveloper
      }
    }
  }
}
    `)

export const useCreateOrgMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateOrgMutation,
    TError,
    Types.CreateOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateOrgMutation,
    TError,
    Types.CreateOrgMutationVariables,
    TContext
  >({
    mutationKey: ["createOrg"],
    mutationFn: (variables?: Types.CreateOrgMutationVariables) =>
      publicFetcher<Types.CreateOrgMutation, Types.CreateOrgMutationVariables>(
        CreateOrgDocument,
        variables,
      )(),
    ...options,
  })
}

useCreateOrgMutation.fetcher = (
  variables: Types.CreateOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreateOrgMutation, Types.CreateOrgMutationVariables>(
    CreateOrgDocument,
    variables,
    options,
  )

export const SetUserAvatarDocument = new TypedDocumentString(`
    mutation setUserAvatar($userId: Int!, $upload: Upload!) {
  admin {
    user {
      setAvatar(userId: $userId, upload: $upload) {
        avatarUrl
      }
    }
  }
}
    `)

export const useSetUserAvatarMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserAvatarMutation,
    TError,
    Types.SetUserAvatarMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserAvatarMutation,
    TError,
    Types.SetUserAvatarMutationVariables,
    TContext
  >({
    mutationKey: ["setUserAvatar"],
    mutationFn: (variables?: Types.SetUserAvatarMutationVariables) =>
      publicFetcher<Types.SetUserAvatarMutation, Types.SetUserAvatarMutationVariables>(
        SetUserAvatarDocument,
        variables,
      )(),
    ...options,
  })
}

useSetUserAvatarMutation.fetcher = (
  variables: Types.SetUserAvatarMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetUserAvatarMutation, Types.SetUserAvatarMutationVariables>(
    SetUserAvatarDocument,
    variables,
    options,
  )

export const UpdateUserOrgMembershipDocument = new TypedDocumentString(`
    mutation updateUserOrgMembership($userId: Int!, $orgPid: String!) {
  admin {
    user {
      updateOrgMembership(userId: $userId, orgPid: $orgPid) {
        ...UserListUser
      }
    }
  }
}
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  jobFunctions
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  adminOrgRoles {
    org {
      pid
      name
      orgSlug
    }
    role
    grantsAllPortfolios
    orgIsDeveloper
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}`)

export const useUpdateUserOrgMembershipMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateUserOrgMembershipMutation,
    TError,
    Types.UpdateUserOrgMembershipMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateUserOrgMembershipMutation,
    TError,
    Types.UpdateUserOrgMembershipMutationVariables,
    TContext
  >({
    mutationKey: ["updateUserOrgMembership"],
    mutationFn: (variables?: Types.UpdateUserOrgMembershipMutationVariables) =>
      publicFetcher<
        Types.UpdateUserOrgMembershipMutation,
        Types.UpdateUserOrgMembershipMutationVariables
      >(UpdateUserOrgMembershipDocument, variables)(),
    ...options,
  })
}

useUpdateUserOrgMembershipMutation.fetcher = (
  variables: Types.UpdateUserOrgMembershipMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateUserOrgMembershipMutation,
    Types.UpdateUserOrgMembershipMutationVariables
  >(UpdateUserOrgMembershipDocument, variables, options)

export const RemoveUserOrgMembershipDocument = new TypedDocumentString(`
    mutation removeUserOrgMembership($userId: Int!) {
  admin {
    user {
      removeOrgMembership(userId: $userId) {
        ...UserListUser
      }
    }
  }
}
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  jobFunctions
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  adminOrgRoles {
    org {
      pid
      name
      orgSlug
    }
    role
    grantsAllPortfolios
    orgIsDeveloper
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}`)

export const useRemoveUserOrgMembershipMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveUserOrgMembershipMutation,
    TError,
    Types.RemoveUserOrgMembershipMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveUserOrgMembershipMutation,
    TError,
    Types.RemoveUserOrgMembershipMutationVariables,
    TContext
  >({
    mutationKey: ["removeUserOrgMembership"],
    mutationFn: (variables?: Types.RemoveUserOrgMembershipMutationVariables) =>
      publicFetcher<
        Types.RemoveUserOrgMembershipMutation,
        Types.RemoveUserOrgMembershipMutationVariables
      >(RemoveUserOrgMembershipDocument, variables)(),
    ...options,
  })
}

useRemoveUserOrgMembershipMutation.fetcher = (
  variables: Types.RemoveUserOrgMembershipMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveUserOrgMembershipMutation,
    Types.RemoveUserOrgMembershipMutationVariables
  >(RemoveUserOrgMembershipDocument, variables, options)

export const SetScenarioConfigDocument = new TypedDocumentString(`
    mutation SetScenarioConfig($scenarioPid: String!, $finproductId: Int, $finproductType: FinProductType, $helioscopePid: String, $goal: PVoptGoal, $roofKw: Float, $carportKw: Float, $groundKw: Float, $ignorePhysicalBounds: Boolean = false, $batteryProduct: BatteryProductInput) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      setConfig(
        finproductId: $finproductId
        finproductType: $finproductType
        goal: $goal
        roofKw: $roofKw
        carportKw: $carportKw
        groundKw: $groundKw
        ignorePhysicalBounds: $ignorePhysicalBounds
        helioscopePid: $helioscopePid
        batteryProduct: $batteryProduct
      ) {
        pid
      }
    }
  }
}
    `)

export const useSetScenarioConfigMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioConfigMutation,
    TError,
    Types.SetScenarioConfigMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioConfigMutation,
    TError,
    Types.SetScenarioConfigMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioConfig"],
    mutationFn: (variables?: Types.SetScenarioConfigMutationVariables) =>
      publicFetcher<
        Types.SetScenarioConfigMutation,
        Types.SetScenarioConfigMutationVariables
      >(SetScenarioConfigDocument, variables)(),
    ...options,
  })
}

useSetScenarioConfigMutation.fetcher = (
  variables: Types.SetScenarioConfigMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetScenarioConfigMutation,
    Types.SetScenarioConfigMutationVariables
  >(SetScenarioConfigDocument, variables, options)

export const RemoveScenarioConfigDocument = new TypedDocumentString(`
    mutation RemoveScenarioConfig($scenarioPid: String!, $scenarioConfigId: Int!) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      removeConfig(scenarioConfigId: $scenarioConfigId)
    }
  }
}
    `)

export const useRemoveScenarioConfigMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveScenarioConfigMutation,
    TError,
    Types.RemoveScenarioConfigMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveScenarioConfigMutation,
    TError,
    Types.RemoveScenarioConfigMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveScenarioConfig"],
    mutationFn: (variables?: Types.RemoveScenarioConfigMutationVariables) =>
      publicFetcher<
        Types.RemoveScenarioConfigMutation,
        Types.RemoveScenarioConfigMutationVariables
      >(RemoveScenarioConfigDocument, variables)(),
    ...options,
  })
}

useRemoveScenarioConfigMutation.fetcher = (
  variables: Types.RemoveScenarioConfigMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveScenarioConfigMutation,
    Types.RemoveScenarioConfigMutationVariables
  >(RemoveScenarioConfigDocument, variables, options)

export const RunScenarioLuxDocument = new TypedDocumentString(`
    mutation RunScenarioLux($scenarioPid: String!) {
  admin {
    scenario(scenarioPid: $scenarioPid) {
      runLux {
        __typename
        ... on Error {
          message
        }
        ... on ScenarioRef {
          pid
        }
      }
    }
  }
}
    `)

export const useRunScenarioLuxMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunScenarioLuxMutation,
    TError,
    Types.RunScenarioLuxMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunScenarioLuxMutation,
    TError,
    Types.RunScenarioLuxMutationVariables,
    TContext
  >({
    mutationKey: ["RunScenarioLux"],
    mutationFn: (variables?: Types.RunScenarioLuxMutationVariables) =>
      publicFetcher<
        Types.RunScenarioLuxMutation,
        Types.RunScenarioLuxMutationVariables
      >(RunScenarioLuxDocument, variables)(),
    ...options,
  })
}

useRunScenarioLuxMutation.fetcher = (
  variables: Types.RunScenarioLuxMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RunScenarioLuxMutation, Types.RunScenarioLuxMutationVariables>(
    RunScenarioLuxDocument,
    variables,
    options,
  )

export const RunLuxScenariosDocument = new TypedDocumentString(`
    mutation RunLuxScenarios($sitePid: String!) {
  admin {
    site {
      runLux(sitePid: $sitePid)
    }
  }
}
    `)

export const useRunLuxScenariosMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxScenariosMutation,
    TError,
    Types.RunLuxScenariosMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxScenariosMutation,
    TError,
    Types.RunLuxScenariosMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxScenarios"],
    mutationFn: (variables?: Types.RunLuxScenariosMutationVariables) =>
      publicFetcher<
        Types.RunLuxScenariosMutation,
        Types.RunLuxScenariosMutationVariables
      >(RunLuxScenariosDocument, variables)(),
    ...options,
  })
}

useRunLuxScenariosMutation.fetcher = (
  variables: Types.RunLuxScenariosMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RunLuxScenariosMutation, Types.RunLuxScenariosMutationVariables>(
    RunLuxScenariosDocument,
    variables,
    options,
  )

export const RunLuxOrgDocument = new TypedDocumentString(`
    mutation RunLuxOrg($pid: String!) {
  admin {
    orgs {
      runLux(pid: $pid)
    }
  }
}
    `)

export const useRunLuxOrgMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxOrgMutation,
    TError,
    Types.RunLuxOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxOrgMutation,
    TError,
    Types.RunLuxOrgMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxOrg"],
    mutationFn: (variables?: Types.RunLuxOrgMutationVariables) =>
      publicFetcher<Types.RunLuxOrgMutation, Types.RunLuxOrgMutationVariables>(
        RunLuxOrgDocument,
        variables,
      )(),
    ...options,
  })
}

useRunLuxOrgMutation.fetcher = (
  variables: Types.RunLuxOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RunLuxOrgMutation, Types.RunLuxOrgMutationVariables>(
    RunLuxOrgDocument,
    variables,
    options,
  )

export const RunLuxPortfolioDocument = new TypedDocumentString(`
    mutation RunLuxPortfolio($portfolioPid: String!) {
  admin {
    portfolio {
      runLux(portfolioPid: $portfolioPid)
    }
  }
}
    `)

export const useRunLuxPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxPortfolioMutation,
    TError,
    Types.RunLuxPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxPortfolioMutation,
    TError,
    Types.RunLuxPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxPortfolio"],
    mutationFn: (variables?: Types.RunLuxPortfolioMutationVariables) =>
      publicFetcher<
        Types.RunLuxPortfolioMutation,
        Types.RunLuxPortfolioMutationVariables
      >(RunLuxPortfolioDocument, variables)(),
    ...options,
  })
}

useRunLuxPortfolioMutation.fetcher = (
  variables: Types.RunLuxPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RunLuxPortfolioMutation, Types.RunLuxPortfolioMutationVariables>(
    RunLuxPortfolioDocument,
    variables,
    options,
  )

export const RunLuxFinproductDocument = new TypedDocumentString(`
    mutation RunLuxFinproduct($id: Int!) {
  admin {
    finproduct {
      runLux(id: $id)
    }
  }
}
    `)

export const useRunLuxFinproductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxFinproductMutation,
    TError,
    Types.RunLuxFinproductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxFinproductMutation,
    TError,
    Types.RunLuxFinproductMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxFinproduct"],
    mutationFn: (variables?: Types.RunLuxFinproductMutationVariables) =>
      publicFetcher<
        Types.RunLuxFinproductMutation,
        Types.RunLuxFinproductMutationVariables
      >(RunLuxFinproductDocument, variables)(),
    ...options,
  })
}

useRunLuxFinproductMutation.fetcher = (
  variables: Types.RunLuxFinproductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RunLuxFinproductMutation,
    Types.RunLuxFinproductMutationVariables
  >(RunLuxFinproductDocument, variables, options)

export const SetBauMeterTariffDocument = new TypedDocumentString(`
    mutation SetBauMeterTariff($input: SetBauMeterTariffInput!) {
  admin {
    site {
      setBauMeterTariff(input: $input) {
        pid
      }
    }
  }
}
    `)

export const useSetBauMeterTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBauMeterTariffMutation,
    TError,
    Types.SetBauMeterTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBauMeterTariffMutation,
    TError,
    Types.SetBauMeterTariffMutationVariables,
    TContext
  >({
    mutationKey: ["SetBauMeterTariff"],
    mutationFn: (variables?: Types.SetBauMeterTariffMutationVariables) =>
      publicFetcher<
        Types.SetBauMeterTariffMutation,
        Types.SetBauMeterTariffMutationVariables
      >(SetBauMeterTariffDocument, variables)(),
    ...options,
  })
}

useSetBauMeterTariffMutation.fetcher = (
  variables: Types.SetBauMeterTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBauMeterTariffMutation,
    Types.SetBauMeterTariffMutationVariables
  >(SetBauMeterTariffDocument, variables, options)

export const SetBauMeterLabelDocument = new TypedDocumentString(`
    mutation SetBauMeterLabel($input: SetBauMeterLabelInput!) {
  admin {
    site {
      setBauMeterLabel(input: $input) {
        pid
      }
    }
  }
}
    `)

export const useSetBauMeterLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBauMeterLabelMutation,
    TError,
    Types.SetBauMeterLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBauMeterLabelMutation,
    TError,
    Types.SetBauMeterLabelMutationVariables,
    TContext
  >({
    mutationKey: ["SetBauMeterLabel"],
    mutationFn: (variables?: Types.SetBauMeterLabelMutationVariables) =>
      publicFetcher<
        Types.SetBauMeterLabelMutation,
        Types.SetBauMeterLabelMutationVariables
      >(SetBauMeterLabelDocument, variables)(),
    ...options,
  })
}

useSetBauMeterLabelMutation.fetcher = (
  variables: Types.SetBauMeterLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBauMeterLabelMutation,
    Types.SetBauMeterLabelMutationVariables
  >(SetBauMeterLabelDocument, variables, options)

export const OrgFinRelToggleDocument = new TypedDocumentString(`
    mutation OrgFinRelToggle($orgPid: String!, $finproductId: Int!, $enable: Boolean!) {
  admin {
    orgs {
      finrelToggle(orgPid: $orgPid, finproductId: $finproductId, enable: $enable) {
        ...FinRel
      }
    }
  }
}
    fragment FinRel on AdminFinRel {
  disabledAt
  finproduct {
    id
    type
    name
    financier {
      name
      pid
    }
  }
  recvr {
    pid
  }
}`)

export const useOrgFinRelToggleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.OrgFinRelToggleMutation,
    TError,
    Types.OrgFinRelToggleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.OrgFinRelToggleMutation,
    TError,
    Types.OrgFinRelToggleMutationVariables,
    TContext
  >({
    mutationKey: ["OrgFinRelToggle"],
    mutationFn: (variables?: Types.OrgFinRelToggleMutationVariables) =>
      publicFetcher<
        Types.OrgFinRelToggleMutation,
        Types.OrgFinRelToggleMutationVariables
      >(OrgFinRelToggleDocument, variables)(),
    ...options,
  })
}

useOrgFinRelToggleMutation.fetcher = (
  variables: Types.OrgFinRelToggleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgFinRelToggleMutation, Types.OrgFinRelToggleMutationVariables>(
    OrgFinRelToggleDocument,
    variables,
    options,
  )

export const SetLatitudeDocument = new TypedDocumentString(`
    mutation SetLatitude($sitePid: String!, $value: Float!) {
  admin {
    site {
      setLatitude(sitePid: $sitePid, value: $value)
    }
  }
}
    `)

export const useSetLatitudeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLatitudeMutation,
    TError,
    Types.SetLatitudeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLatitudeMutation,
    TError,
    Types.SetLatitudeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLatitude"],
    mutationFn: (variables?: Types.SetLatitudeMutationVariables) =>
      publicFetcher<Types.SetLatitudeMutation, Types.SetLatitudeMutationVariables>(
        SetLatitudeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLatitudeMutation.fetcher = (
  variables: Types.SetLatitudeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetLatitudeMutation, Types.SetLatitudeMutationVariables>(
    SetLatitudeDocument,
    variables,
    options,
  )

export const SetLongitudeDocument = new TypedDocumentString(`
    mutation SetLongitude($sitePid: String!, $value: Float!) {
  admin {
    site {
      setLongitude(sitePid: $sitePid, value: $value)
    }
  }
}
    `)

export const useSetLongitudeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLongitudeMutation,
    TError,
    Types.SetLongitudeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLongitudeMutation,
    TError,
    Types.SetLongitudeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLongitude"],
    mutationFn: (variables?: Types.SetLongitudeMutationVariables) =>
      publicFetcher<Types.SetLongitudeMutation, Types.SetLongitudeMutationVariables>(
        SetLongitudeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLongitudeMutation.fetcher = (
  variables: Types.SetLongitudeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetLongitudeMutation, Types.SetLongitudeMutationVariables>(
    SetLongitudeDocument,
    variables,
    options,
  )

export const CreateCsFinProductDocument = new TypedDocumentString(`
    mutation CreateCsFinProduct($input: CSFinProductInput!) {
  admin {
    finproduct {
      createCsFinproduct(input: $input) {
        id
      }
    }
  }
}
    `)

export const useCreateCsFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCsFinProductMutation,
    TError,
    Types.CreateCsFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCsFinProductMutation,
    TError,
    Types.CreateCsFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCsFinProduct"],
    mutationFn: (variables?: Types.CreateCsFinProductMutationVariables) =>
      publicFetcher<
        Types.CreateCsFinProductMutation,
        Types.CreateCsFinProductMutationVariables
      >(CreateCsFinProductDocument, variables)(),
    ...options,
  })
}

useCreateCsFinProductMutation.fetcher = (
  variables: Types.CreateCsFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCsFinProductMutation,
    Types.CreateCsFinProductMutationVariables
  >(CreateCsFinProductDocument, variables, options)

export const CreatePpaFinProductDocument = new TypedDocumentString(`
    mutation CreatePpaFinProduct($input: PPAFinProductInput!) {
  admin {
    finproduct {
      createPpaFinproduct(input: $input) {
        id
      }
    }
  }
}
    `)

export const useCreatePpaFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePpaFinProductMutation,
    TError,
    Types.CreatePpaFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePpaFinProductMutation,
    TError,
    Types.CreatePpaFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePpaFinProduct"],
    mutationFn: (variables?: Types.CreatePpaFinProductMutationVariables) =>
      publicFetcher<
        Types.CreatePpaFinProductMutation,
        Types.CreatePpaFinProductMutationVariables
      >(CreatePpaFinProductDocument, variables)(),
    ...options,
  })
}

useCreatePpaFinProductMutation.fetcher = (
  variables: Types.CreatePpaFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreatePpaFinProductMutation,
    Types.CreatePpaFinProductMutationVariables
  >(CreatePpaFinProductDocument, variables, options)

export const CreateLoanFinProductDocument = new TypedDocumentString(`
    mutation CreateLoanFinProduct($input: LoanFinProductInput!) {
  admin {
    finproduct {
      createLoanFinproduct(input: $input) {
        id
      }
    }
  }
}
    `)

export const useCreateLoanFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateLoanFinProductMutation,
    TError,
    Types.CreateLoanFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateLoanFinProductMutation,
    TError,
    Types.CreateLoanFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateLoanFinProduct"],
    mutationFn: (variables?: Types.CreateLoanFinProductMutationVariables) =>
      publicFetcher<
        Types.CreateLoanFinProductMutation,
        Types.CreateLoanFinProductMutationVariables
      >(CreateLoanFinProductDocument, variables)(),
    ...options,
  })
}

useCreateLoanFinProductMutation.fetcher = (
  variables: Types.CreateLoanFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateLoanFinProductMutation,
    Types.CreateLoanFinProductMutationVariables
  >(CreateLoanFinProductDocument, variables, options)

export const FinProductSetNameDocument = new TypedDocumentString(`
    mutation FinProductSetName($id: Int!, $value: String!) {
  admin {
    finproduct {
      setName(id: $id, name: $value) {
        id
      }
    }
  }
}
    `)

export const useFinProductSetNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetNameMutation,
    TError,
    Types.FinProductSetNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetNameMutation,
    TError,
    Types.FinProductSetNameMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetName"],
    mutationFn: (variables?: Types.FinProductSetNameMutationVariables) =>
      publicFetcher<
        Types.FinProductSetNameMutation,
        Types.FinProductSetNameMutationVariables
      >(FinProductSetNameDocument, variables)(),
    ...options,
  })
}

useFinProductSetNameMutation.fetcher = (
  variables: Types.FinProductSetNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinProductSetNameMutation,
    Types.FinProductSetNameMutationVariables
  >(FinProductSetNameDocument, variables, options)

export const FinProductSetStatusDocument = new TypedDocumentString(`
    mutation FinProductSetStatus($id: Int!, $value: FinProductStatus) {
  admin {
    finproduct {
      setStatus(id: $id, status: $value) {
        id
      }
    }
  }
}
    `)

export const useFinProductSetStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetStatusMutation,
    TError,
    Types.FinProductSetStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetStatusMutation,
    TError,
    Types.FinProductSetStatusMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetStatus"],
    mutationFn: (variables?: Types.FinProductSetStatusMutationVariables) =>
      publicFetcher<
        Types.FinProductSetStatusMutation,
        Types.FinProductSetStatusMutationVariables
      >(FinProductSetStatusDocument, variables)(),
    ...options,
  })
}

useFinProductSetStatusMutation.fetcher = (
  variables: Types.FinProductSetStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinProductSetStatusMutation,
    Types.FinProductSetStatusMutationVariables
  >(FinProductSetStatusDocument, variables, options)

export const FinProductSetDisabledDocument = new TypedDocumentString(`
    mutation FinProductSetDisabled($id: Int!, $value: Boolean!) {
  admin {
    finproduct {
      setDisabled(id: $id, disabled: $value) {
        id
      }
    }
  }
}
    `)

export const useFinProductSetDisabledMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetDisabledMutation,
    TError,
    Types.FinProductSetDisabledMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetDisabledMutation,
    TError,
    Types.FinProductSetDisabledMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetDisabled"],
    mutationFn: (variables?: Types.FinProductSetDisabledMutationVariables) =>
      publicFetcher<
        Types.FinProductSetDisabledMutation,
        Types.FinProductSetDisabledMutationVariables
      >(FinProductSetDisabledDocument, variables)(),
    ...options,
  })
}

useFinProductSetDisabledMutation.fetcher = (
  variables: Types.FinProductSetDisabledMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinProductSetDisabledMutation,
    Types.FinProductSetDisabledMutationVariables
  >(FinProductSetDisabledDocument, variables, options)

export const FinProductSetIncludeStorageDocument = new TypedDocumentString(`
    mutation FinProductSetIncludeStorage($finproductId: Int!, $enable: Boolean!) {
  admin {
    finproduct {
      setIncludeStorage(id: $finproductId, includeStorage: $enable) {
        id
      }
    }
  }
}
    `)

export const useFinProductSetIncludeStorageMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.FinProductSetIncludeStorageMutation,
    TError,
    Types.FinProductSetIncludeStorageMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetIncludeStorageMutation,
    TError,
    Types.FinProductSetIncludeStorageMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetIncludeStorage"],
    mutationFn: (variables?: Types.FinProductSetIncludeStorageMutationVariables) =>
      publicFetcher<
        Types.FinProductSetIncludeStorageMutation,
        Types.FinProductSetIncludeStorageMutationVariables
      >(FinProductSetIncludeStorageDocument, variables)(),
    ...options,
  })
}

useFinProductSetIncludeStorageMutation.fetcher = (
  variables: Types.FinProductSetIncludeStorageMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinProductSetIncludeStorageMutation,
    Types.FinProductSetIncludeStorageMutationVariables
  >(FinProductSetIncludeStorageDocument, variables, options)

export const SiteToggleFinProductDocument = new TypedDocumentString(`
    mutation SiteToggleFinProduct($sitePid: String!, $finproductId: Int!, $enabled: Boolean!) {
  admin {
    site {
      toggleFinproduct(
        sitePid: $sitePid
        finproductId: $finproductId
        enabled: $enabled
      )
    }
  }
}
    `)

export const useSiteToggleFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SiteToggleFinProductMutation,
    TError,
    Types.SiteToggleFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SiteToggleFinProductMutation,
    TError,
    Types.SiteToggleFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["SiteToggleFinProduct"],
    mutationFn: (variables?: Types.SiteToggleFinProductMutationVariables) =>
      publicFetcher<
        Types.SiteToggleFinProductMutation,
        Types.SiteToggleFinProductMutationVariables
      >(SiteToggleFinProductDocument, variables)(),
    ...options,
  })
}

useSiteToggleFinProductMutation.fetcher = (
  variables: Types.SiteToggleFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteToggleFinProductMutation,
    Types.SiteToggleFinProductMutationVariables
  >(SiteToggleFinProductDocument, variables, options)

export const ToggleHideFakePanelsDocument = new TypedDocumentString(`
    mutation ToggleHideFakePanels($sitePid: String!, $hide: Boolean!) {
  admin {
    site {
      toggleHideFakePanels(sitePid: $sitePid, hide: $hide)
    }
  }
}
    `)

export const useToggleHideFakePanelsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ToggleHideFakePanelsMutation,
    TError,
    Types.ToggleHideFakePanelsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleHideFakePanelsMutation,
    TError,
    Types.ToggleHideFakePanelsMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleHideFakePanels"],
    mutationFn: (variables?: Types.ToggleHideFakePanelsMutationVariables) =>
      publicFetcher<
        Types.ToggleHideFakePanelsMutation,
        Types.ToggleHideFakePanelsMutationVariables
      >(ToggleHideFakePanelsDocument, variables)(),
    ...options,
  })
}

useToggleHideFakePanelsMutation.fetcher = (
  variables: Types.ToggleHideFakePanelsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ToggleHideFakePanelsMutation,
    Types.ToggleHideFakePanelsMutationVariables
  >(ToggleHideFakePanelsDocument, variables, options)

export const SetHelioscopeAllocationDocument = new TypedDocumentString(`
    mutation SetHelioscopeAllocation($helioscopeId: Int!, $allocations: [Allocation!]!) {
  admin {
    site {
      setHelioscopeAllocation(helioscopeId: $helioscopeId, allocations: $allocations)
    }
  }
}
    `)

export const useSetHelioscopeAllocationMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetHelioscopeAllocationMutation,
    TError,
    Types.SetHelioscopeAllocationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetHelioscopeAllocationMutation,
    TError,
    Types.SetHelioscopeAllocationMutationVariables,
    TContext
  >({
    mutationKey: ["SetHelioscopeAllocation"],
    mutationFn: (variables?: Types.SetHelioscopeAllocationMutationVariables) =>
      publicFetcher<
        Types.SetHelioscopeAllocationMutation,
        Types.SetHelioscopeAllocationMutationVariables
      >(SetHelioscopeAllocationDocument, variables)(),
    ...options,
  })
}

useSetHelioscopeAllocationMutation.fetcher = (
  variables: Types.SetHelioscopeAllocationMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetHelioscopeAllocationMutation,
    Types.SetHelioscopeAllocationMutationVariables
  >(SetHelioscopeAllocationDocument, variables, options)

export const UpdateMultifamilyUnitsDocument = new TypedDocumentString(`
    mutation UpdateMultifamilyUnits($sitePid: String!, $input: MFUnitBreakdownInput!) {
  mfParams {
    updateMfUnits(sitePid: $sitePid, input: $input)
  }
}
    `)

export const useUpdateMultifamilyUnitsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateMultifamilyUnitsMutation,
    TError,
    Types.UpdateMultifamilyUnitsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateMultifamilyUnitsMutation,
    TError,
    Types.UpdateMultifamilyUnitsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateMultifamilyUnits"],
    mutationFn: (variables?: Types.UpdateMultifamilyUnitsMutationVariables) =>
      publicFetcher<
        Types.UpdateMultifamilyUnitsMutation,
        Types.UpdateMultifamilyUnitsMutationVariables
      >(UpdateMultifamilyUnitsDocument, variables)(),
    ...options,
  })
}

useUpdateMultifamilyUnitsMutation.fetcher = (
  variables: Types.UpdateMultifamilyUnitsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateMultifamilyUnitsMutation,
    Types.UpdateMultifamilyUnitsMutationVariables
  >(UpdateMultifamilyUnitsDocument, variables, options)

export const SetUserEnabledDocument = new TypedDocumentString(`
    mutation SetUserEnabled($userId: Int!, $isEnabled: Boolean!) {
  admin {
    user {
      setIsEnabled(userId: $userId, isEnabled: $isEnabled) {
        __typename
        isEnabled
        lastSeenAt
      }
    }
  }
}
    `)

export const useSetUserEnabledMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserEnabledMutation,
    TError,
    Types.SetUserEnabledMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserEnabledMutation,
    TError,
    Types.SetUserEnabledMutationVariables,
    TContext
  >({
    mutationKey: ["SetUserEnabled"],
    mutationFn: (variables?: Types.SetUserEnabledMutationVariables) =>
      publicFetcher<
        Types.SetUserEnabledMutation,
        Types.SetUserEnabledMutationVariables
      >(SetUserEnabledDocument, variables)(),
    ...options,
  })
}

useSetUserEnabledMutation.fetcher = (
  variables: Types.SetUserEnabledMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetUserEnabledMutation, Types.SetUserEnabledMutationVariables>(
    SetUserEnabledDocument,
    variables,
    options,
  )

export const SetUserJobFunctionsDocument = new TypedDocumentString(`
    mutation SetUserJobFunctions($userId: Int!, $jobFunctions: [JobFunction!]!) {
  admin {
    user {
      setJobFunctions(userId: $userId, jobFunctions: $jobFunctions) {
        __typename
        id
        jobFunctions
      }
    }
  }
}
    `)

export const useSetUserJobFunctionsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserJobFunctionsMutation,
    TError,
    Types.SetUserJobFunctionsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserJobFunctionsMutation,
    TError,
    Types.SetUserJobFunctionsMutationVariables,
    TContext
  >({
    mutationKey: ["SetUserJobFunctions"],
    mutationFn: (variables?: Types.SetUserJobFunctionsMutationVariables) =>
      publicFetcher<
        Types.SetUserJobFunctionsMutation,
        Types.SetUserJobFunctionsMutationVariables
      >(SetUserJobFunctionsDocument, variables)(),
    ...options,
  })
}

useSetUserJobFunctionsMutation.fetcher = (
  variables: Types.SetUserJobFunctionsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetUserJobFunctionsMutation,
    Types.SetUserJobFunctionsMutationVariables
  >(SetUserJobFunctionsDocument, variables, options)

export const ClearSiteDrawingsDocument = new TypedDocumentString(`
    mutation ClearSiteDrawings($input: ClearDrawingsInput!) {
  admin {
    site {
      clearDrawings(input: $input)
    }
  }
}
    `)

export const useClearSiteDrawingsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ClearSiteDrawingsMutation,
    TError,
    Types.ClearSiteDrawingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ClearSiteDrawingsMutation,
    TError,
    Types.ClearSiteDrawingsMutationVariables,
    TContext
  >({
    mutationKey: ["ClearSiteDrawings"],
    mutationFn: (variables?: Types.ClearSiteDrawingsMutationVariables) =>
      publicFetcher<
        Types.ClearSiteDrawingsMutation,
        Types.ClearSiteDrawingsMutationVariables
      >(ClearSiteDrawingsDocument, variables)(),
    ...options,
  })
}

useClearSiteDrawingsMutation.fetcher = (
  variables: Types.ClearSiteDrawingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ClearSiteDrawingsMutation,
    Types.ClearSiteDrawingsMutationVariables
  >(ClearSiteDrawingsDocument, variables, options)

export const RevertSiteToEstimatesDocument = new TypedDocumentString(`
    mutation RevertSiteToEstimates($input: RevertToEstimatesInput!) {
  admin {
    site {
      revertToEstimates(input: $input)
    }
  }
}
    `)

export const useRevertSiteToEstimatesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RevertSiteToEstimatesMutation,
    TError,
    Types.RevertSiteToEstimatesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RevertSiteToEstimatesMutation,
    TError,
    Types.RevertSiteToEstimatesMutationVariables,
    TContext
  >({
    mutationKey: ["RevertSiteToEstimates"],
    mutationFn: (variables?: Types.RevertSiteToEstimatesMutationVariables) =>
      publicFetcher<
        Types.RevertSiteToEstimatesMutation,
        Types.RevertSiteToEstimatesMutationVariables
      >(RevertSiteToEstimatesDocument, variables)(),
    ...options,
  })
}

useRevertSiteToEstimatesMutation.fetcher = (
  variables: Types.RevertSiteToEstimatesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevertSiteToEstimatesMutation,
    Types.RevertSiteToEstimatesMutationVariables
  >(RevertSiteToEstimatesDocument, variables, options)

export const PromoteTheFinmodelAnywayDocument = new TypedDocumentString(`
    mutation PromoteTheFinmodelAnyway($finmodelId: Int!) {
  guardrailOverrides {
    promoteTheFinmodelAnyway(finmodelId: $finmodelId)
  }
}
    `)

export const usePromoteTheFinmodelAnywayMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.PromoteTheFinmodelAnywayMutation,
    TError,
    Types.PromoteTheFinmodelAnywayMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.PromoteTheFinmodelAnywayMutation,
    TError,
    Types.PromoteTheFinmodelAnywayMutationVariables,
    TContext
  >({
    mutationKey: ["PromoteTheFinmodelAnyway"],
    mutationFn: (variables?: Types.PromoteTheFinmodelAnywayMutationVariables) =>
      publicFetcher<
        Types.PromoteTheFinmodelAnywayMutation,
        Types.PromoteTheFinmodelAnywayMutationVariables
      >(PromoteTheFinmodelAnywayDocument, variables)(),
    ...options,
  })
}

usePromoteTheFinmodelAnywayMutation.fetcher = (
  variables: Types.PromoteTheFinmodelAnywayMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PromoteTheFinmodelAnywayMutation,
    Types.PromoteTheFinmodelAnywayMutationVariables
  >(PromoteTheFinmodelAnywayDocument, variables, options)

export const FindBuildingFootprintsDocument = new TypedDocumentString(`
    mutation FindBuildingFootprints($sitePid: String!, $strategy: FootprintStrategy!) {
  admin {
    site {
      findBuildingFootprints(sitePid: $sitePid, strategy: $strategy) {
        ... on Error {
          __typename
          message
        }
      }
    }
  }
}
    `)

export const useFindBuildingFootprintsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FindBuildingFootprintsMutation,
    TError,
    Types.FindBuildingFootprintsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FindBuildingFootprintsMutation,
    TError,
    Types.FindBuildingFootprintsMutationVariables,
    TContext
  >({
    mutationKey: ["FindBuildingFootprints"],
    mutationFn: (variables?: Types.FindBuildingFootprintsMutationVariables) =>
      publicFetcher<
        Types.FindBuildingFootprintsMutation,
        Types.FindBuildingFootprintsMutationVariables
      >(FindBuildingFootprintsDocument, variables)(),
    ...options,
  })
}

useFindBuildingFootprintsMutation.fetcher = (
  variables: Types.FindBuildingFootprintsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FindBuildingFootprintsMutation,
    Types.FindBuildingFootprintsMutationVariables
  >(FindBuildingFootprintsDocument, variables, options)

export const SetSolarDetectionHumanLabelDocument = new TypedDocumentString(`
    mutation SetSolarDetectionHumanLabel($sitePid: String!, $hasSolar: Boolean!) {
  admin {
    solarDetection {
      setSolarDetectionHumanLabel(sitePid: $sitePid, hasSolar: $hasSolar) {
        id
        humanLabel {
          id
          hasSolar
          source
          createdAt
          userEmail
        }
      }
    }
  }
}
    `)

export const useSetSolarDetectionHumanLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetSolarDetectionHumanLabelMutation,
    TError,
    Types.SetSolarDetectionHumanLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSolarDetectionHumanLabelMutation,
    TError,
    Types.SetSolarDetectionHumanLabelMutationVariables,
    TContext
  >({
    mutationKey: ["SetSolarDetectionHumanLabel"],
    mutationFn: (variables?: Types.SetSolarDetectionHumanLabelMutationVariables) =>
      publicFetcher<
        Types.SetSolarDetectionHumanLabelMutation,
        Types.SetSolarDetectionHumanLabelMutationVariables
      >(SetSolarDetectionHumanLabelDocument, variables)(),
    ...options,
  })
}

useSetSolarDetectionHumanLabelMutation.fetcher = (
  variables: Types.SetSolarDetectionHumanLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetSolarDetectionHumanLabelMutation,
    Types.SetSolarDetectionHumanLabelMutationVariables
  >(SetSolarDetectionHumanLabelDocument, variables, options)

export const ClearSolarDetectionHumanLabelDocument = new TypedDocumentString(`
    mutation ClearSolarDetectionHumanLabel($resultId: Int!) {
  admin {
    solarDetection {
      clearSolarDetectionHumanLabel(resultId: $resultId) {
        id
        humanLabel {
          id
        }
      }
    }
  }
}
    `)

export const useClearSolarDetectionHumanLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ClearSolarDetectionHumanLabelMutation,
    TError,
    Types.ClearSolarDetectionHumanLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ClearSolarDetectionHumanLabelMutation,
    TError,
    Types.ClearSolarDetectionHumanLabelMutationVariables,
    TContext
  >({
    mutationKey: ["ClearSolarDetectionHumanLabel"],
    mutationFn: (variables?: Types.ClearSolarDetectionHumanLabelMutationVariables) =>
      publicFetcher<
        Types.ClearSolarDetectionHumanLabelMutation,
        Types.ClearSolarDetectionHumanLabelMutationVariables
      >(ClearSolarDetectionHumanLabelDocument, variables)(),
    ...options,
  })
}

useClearSolarDetectionHumanLabelMutation.fetcher = (
  variables: Types.ClearSolarDetectionHumanLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ClearSolarDetectionHumanLabelMutation,
    Types.ClearSolarDetectionHumanLabelMutationVariables
  >(ClearSolarDetectionHumanLabelDocument, variables, options)

export const TriggerSolarDetectionDocument = new TypedDocumentString(`
    mutation TriggerSolarDetection($sitePid: String!) {
  admin {
    solarDetection {
      triggerSolarDetection(sitePid: $sitePid)
    }
  }
}
    `)

export const useTriggerSolarDetectionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TriggerSolarDetectionMutation,
    TError,
    Types.TriggerSolarDetectionMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TriggerSolarDetectionMutation,
    TError,
    Types.TriggerSolarDetectionMutationVariables,
    TContext
  >({
    mutationKey: ["TriggerSolarDetection"],
    mutationFn: (variables?: Types.TriggerSolarDetectionMutationVariables) =>
      publicFetcher<
        Types.TriggerSolarDetectionMutation,
        Types.TriggerSolarDetectionMutationVariables
      >(TriggerSolarDetectionDocument, variables)(),
    ...options,
  })
}

useTriggerSolarDetectionMutation.fetcher = (
  variables: Types.TriggerSolarDetectionMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TriggerSolarDetectionMutation,
    Types.TriggerSolarDetectionMutationVariables
  >(TriggerSolarDetectionDocument, variables, options)

export const AdminCreateAndApplySiteLabelDocument = new TypedDocumentString(`
    mutation AdminCreateAndApplySiteLabel($input: CreateAndApplySiteLabelInput!) {
  admin {
    site {
      labels {
        createAndApplySiteLabel(input: $input) {
          __typename
          ... on Error {
            message
          }
          ... on CreateAndApplySiteLabelOutput {
            label {
              pid
            }
          }
        }
      }
    }
  }
}
    `)

export const useAdminCreateAndApplySiteLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AdminCreateAndApplySiteLabelMutation,
    TError,
    Types.AdminCreateAndApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminCreateAndApplySiteLabelMutation,
    TError,
    Types.AdminCreateAndApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["AdminCreateAndApplySiteLabel"],
    mutationFn: (variables?: Types.AdminCreateAndApplySiteLabelMutationVariables) =>
      publicFetcher<
        Types.AdminCreateAndApplySiteLabelMutation,
        Types.AdminCreateAndApplySiteLabelMutationVariables
      >(AdminCreateAndApplySiteLabelDocument, variables)(),
    ...options,
  })
}

useAdminCreateAndApplySiteLabelMutation.fetcher = (
  variables: Types.AdminCreateAndApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCreateAndApplySiteLabelMutation,
    Types.AdminCreateAndApplySiteLabelMutationVariables
  >(AdminCreateAndApplySiteLabelDocument, variables, options)

export const AdminDeleteSiteLabelDocument = new TypedDocumentString(`
    mutation AdminDeleteSiteLabel($input: DeleteLabelInput!) {
  admin {
    site {
      labels {
        deleteLabel(input: $input) {
          __typename
          ... on Error {
            message
          }
        }
      }
    }
  }
}
    `)

export const useAdminDeleteSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AdminDeleteSiteLabelMutation,
    TError,
    Types.AdminDeleteSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminDeleteSiteLabelMutation,
    TError,
    Types.AdminDeleteSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["AdminDeleteSiteLabel"],
    mutationFn: (variables?: Types.AdminDeleteSiteLabelMutationVariables) =>
      publicFetcher<
        Types.AdminDeleteSiteLabelMutation,
        Types.AdminDeleteSiteLabelMutationVariables
      >(AdminDeleteSiteLabelDocument, variables)(),
    ...options,
  })
}

useAdminDeleteSiteLabelMutation.fetcher = (
  variables: Types.AdminDeleteSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminDeleteSiteLabelMutation,
    Types.AdminDeleteSiteLabelMutationVariables
  >(AdminDeleteSiteLabelDocument, variables, options)

export const ApplySiteLabelDocument = new TypedDocumentString(`
    mutation ApplySiteLabel($input: ApplyLabelToSiteInput!) {
  admin {
    site {
      labels {
        applyLabelToSite(input: $input) {
          __typename
          ... on Error {
            message
          }
        }
      }
    }
  }
}
    `)

export const useApplySiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ApplySiteLabelMutation,
    TError,
    Types.ApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApplySiteLabelMutation,
    TError,
    Types.ApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["ApplySiteLabel"],
    mutationFn: (variables?: Types.ApplySiteLabelMutationVariables) =>
      publicFetcher<
        Types.ApplySiteLabelMutation,
        Types.ApplySiteLabelMutationVariables
      >(ApplySiteLabelDocument, variables)(),
    ...options,
  })
}

useApplySiteLabelMutation.fetcher = (
  variables: Types.ApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ApplySiteLabelMutation, Types.ApplySiteLabelMutationVariables>(
    ApplySiteLabelDocument,
    variables,
    options,
  )

export const AdminChangeSiteLabelColorDocument = new TypedDocumentString(`
    mutation AdminChangeSiteLabelColor($input: ChangeSiteLabelColorInput!) {
  admin {
    site {
      labels {
        changeSiteLabelColor(input: $input) {
          __typename
          ... on Error {
            message
          }
          ... on ChangeSiteLabelColorOutput {
            label {
              pid
              themeColor
            }
          }
        }
      }
    }
  }
}
    `)

export const useAdminChangeSiteLabelColorMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AdminChangeSiteLabelColorMutation,
    TError,
    Types.AdminChangeSiteLabelColorMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminChangeSiteLabelColorMutation,
    TError,
    Types.AdminChangeSiteLabelColorMutationVariables,
    TContext
  >({
    mutationKey: ["AdminChangeSiteLabelColor"],
    mutationFn: (variables?: Types.AdminChangeSiteLabelColorMutationVariables) =>
      publicFetcher<
        Types.AdminChangeSiteLabelColorMutation,
        Types.AdminChangeSiteLabelColorMutationVariables
      >(AdminChangeSiteLabelColorDocument, variables)(),
    ...options,
  })
}

useAdminChangeSiteLabelColorMutation.fetcher = (
  variables: Types.AdminChangeSiteLabelColorMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminChangeSiteLabelColorMutation,
    Types.AdminChangeSiteLabelColorMutationVariables
  >(AdminChangeSiteLabelColorDocument, variables, options)

export const SetFtmBessTriageStatusDocument = new TypedDocumentString(`
    mutation SetFtmBessTriageStatus($sitePid: String!, $status: TriageStatus!) {
  admin {
    site {
      setFtmBessTriageStatus(sitePid: $sitePid, status: $status) {
        ...FtmBessProspect
      }
    }
  }
}
    fragment FtmBessProspect on FTMBessProspect {
  triageStatus
  verdict
  updatedAt
  triageStatusEdit {
    at
    by
  }
  criteria {
    key
    label
    order
    status
    notes
    contextualValue
    statusEdit {
      at
      by
    }
    notesEdit {
      at
      by
    }
  }
}`)

export const useSetFtmBessTriageStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetFtmBessTriageStatusMutation,
    TError,
    Types.SetFtmBessTriageStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetFtmBessTriageStatusMutation,
    TError,
    Types.SetFtmBessTriageStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetFtmBessTriageStatus"],
    mutationFn: (variables?: Types.SetFtmBessTriageStatusMutationVariables) =>
      publicFetcher<
        Types.SetFtmBessTriageStatusMutation,
        Types.SetFtmBessTriageStatusMutationVariables
      >(SetFtmBessTriageStatusDocument, variables)(),
    ...options,
  })
}

useSetFtmBessTriageStatusMutation.fetcher = (
  variables: Types.SetFtmBessTriageStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetFtmBessTriageStatusMutation,
    Types.SetFtmBessTriageStatusMutationVariables
  >(SetFtmBessTriageStatusDocument, variables, options)

export const SetFtmBessCriterionDocument = new TypedDocumentString(`
    mutation SetFtmBessCriterion($sitePid: String!, $criterion: FTMBessCriterion!, $status: CriterionStatus, $notes: String) {
  admin {
    site {
      setFtmBessCriterion(
        sitePid: $sitePid
        criterion: $criterion
        status: $status
        notes: $notes
      ) {
        ...FtmBessProspect
      }
    }
  }
}
    fragment FtmBessProspect on FTMBessProspect {
  triageStatus
  verdict
  updatedAt
  triageStatusEdit {
    at
    by
  }
  criteria {
    key
    label
    order
    status
    notes
    contextualValue
    statusEdit {
      at
      by
    }
    notesEdit {
      at
      by
    }
  }
}`)

export const useSetFtmBessCriterionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetFtmBessCriterionMutation,
    TError,
    Types.SetFtmBessCriterionMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetFtmBessCriterionMutation,
    TError,
    Types.SetFtmBessCriterionMutationVariables,
    TContext
  >({
    mutationKey: ["SetFtmBessCriterion"],
    mutationFn: (variables?: Types.SetFtmBessCriterionMutationVariables) =>
      publicFetcher<
        Types.SetFtmBessCriterionMutation,
        Types.SetFtmBessCriterionMutationVariables
      >(SetFtmBessCriterionDocument, variables)(),
    ...options,
  })
}

useSetFtmBessCriterionMutation.fetcher = (
  variables: Types.SetFtmBessCriterionMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetFtmBessCriterionMutation,
    Types.SetFtmBessCriterionMutationVariables
  >(SetFtmBessCriterionDocument, variables, options)

export const CreateMeetingNoteDocument = new TypedDocumentString(`
    mutation CreateMeetingNote($orgSlug: String!, $input: NewMeetingNoteInput!) {
  orgBySlug(slug: $orgSlug) {
    meetingNotes {
      create(input: $input) {
        pid
      }
    }
  }
}
    `)

export const useCreateMeetingNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateMeetingNoteMutation,
    TError,
    Types.CreateMeetingNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateMeetingNoteMutation,
    TError,
    Types.CreateMeetingNoteMutationVariables,
    TContext
  >({
    mutationKey: ["CreateMeetingNote"],
    mutationFn: (variables?: Types.CreateMeetingNoteMutationVariables) =>
      publicFetcher<
        Types.CreateMeetingNoteMutation,
        Types.CreateMeetingNoteMutationVariables
      >(CreateMeetingNoteDocument, variables)(),
    ...options,
  })
}

useCreateMeetingNoteMutation.fetcher = (
  variables: Types.CreateMeetingNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateMeetingNoteMutation,
    Types.CreateMeetingNoteMutationVariables
  >(CreateMeetingNoteDocument, variables, options)

export const EditMeetingNoteDocument = new TypedDocumentString(`
    mutation EditMeetingNote($orgSlug: String!, $pid: String!, $input: EditMeetingNoteInput!) {
  orgBySlug(slug: $orgSlug) {
    meetingNotes {
      edit(pid: $pid, input: $input) {
        pid
      }
    }
  }
}
    `)

export const useEditMeetingNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditMeetingNoteMutation,
    TError,
    Types.EditMeetingNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditMeetingNoteMutation,
    TError,
    Types.EditMeetingNoteMutationVariables,
    TContext
  >({
    mutationKey: ["EditMeetingNote"],
    mutationFn: (variables?: Types.EditMeetingNoteMutationVariables) =>
      publicFetcher<
        Types.EditMeetingNoteMutation,
        Types.EditMeetingNoteMutationVariables
      >(EditMeetingNoteDocument, variables)(),
    ...options,
  })
}

useEditMeetingNoteMutation.fetcher = (
  variables: Types.EditMeetingNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EditMeetingNoteMutation, Types.EditMeetingNoteMutationVariables>(
    EditMeetingNoteDocument,
    variables,
    options,
  )

export const TrashMeetingNoteDocument = new TypedDocumentString(`
    mutation TrashMeetingNote($orgSlug: String!, $pid: String!) {
  orgBySlug(slug: $orgSlug) {
    meetingNotes {
      trash(pid: $pid) {
        pid
      }
    }
  }
}
    `)

export const useTrashMeetingNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashMeetingNoteMutation,
    TError,
    Types.TrashMeetingNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashMeetingNoteMutation,
    TError,
    Types.TrashMeetingNoteMutationVariables,
    TContext
  >({
    mutationKey: ["TrashMeetingNote"],
    mutationFn: (variables?: Types.TrashMeetingNoteMutationVariables) =>
      publicFetcher<
        Types.TrashMeetingNoteMutation,
        Types.TrashMeetingNoteMutationVariables
      >(TrashMeetingNoteDocument, variables)(),
    ...options,
  })
}

useTrashMeetingNoteMutation.fetcher = (
  variables: Types.TrashMeetingNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TrashMeetingNoteMutation,
    Types.TrashMeetingNoteMutationVariables
  >(TrashMeetingNoteDocument, variables, options)

export const TriggerMeetingNoteSyncDocument = new TypedDocumentString(`
    mutation TriggerMeetingNoteSync {
  admin {
    meetingNoteSync {
      trigger {
        running
        finishedAt
        status
      }
    }
  }
}
    `)

export const useTriggerMeetingNoteSyncMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TriggerMeetingNoteSyncMutation,
    TError,
    Types.TriggerMeetingNoteSyncMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TriggerMeetingNoteSyncMutation,
    TError,
    Types.TriggerMeetingNoteSyncMutationVariables,
    TContext
  >({
    mutationKey: ["TriggerMeetingNoteSync"],
    mutationFn: (variables?: Types.TriggerMeetingNoteSyncMutationVariables) =>
      publicFetcher<
        Types.TriggerMeetingNoteSyncMutation,
        Types.TriggerMeetingNoteSyncMutationVariables
      >(TriggerMeetingNoteSyncDocument, variables)(),
    ...options,
  })
}

useTriggerMeetingNoteSyncMutation.fetcher = (
  variables?: Types.TriggerMeetingNoteSyncMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TriggerMeetingNoteSyncMutation,
    Types.TriggerMeetingNoteSyncMutationVariables
  >(TriggerMeetingNoteSyncDocument, variables, options)

export const SetPinnedResultDocument = new TypedDocumentString(`
    mutation SetPinnedResult($resultPid: String!, $pinned: Boolean!) {
  setPinnedResult(resultPid: $resultPid, pinned: $pinned)
}
    `)

export const useSetPinnedResultMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPinnedResultMutation,
    TError,
    Types.SetPinnedResultMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPinnedResultMutation,
    TError,
    Types.SetPinnedResultMutationVariables,
    TContext
  >({
    mutationKey: ["SetPinnedResult"],
    mutationFn: (variables?: Types.SetPinnedResultMutationVariables) =>
      publicFetcher<
        Types.SetPinnedResultMutation,
        Types.SetPinnedResultMutationVariables
      >(SetPinnedResultDocument, variables)(),
    ...options,
  })
}

useSetPinnedResultMutation.fetcher = (
  variables: Types.SetPinnedResultMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetPinnedResultMutation, Types.SetPinnedResultMutationVariables>(
    SetPinnedResultDocument,
    variables,
    options,
  )

export const TriggerMeetingNoteExtractionDocument = new TypedDocumentString(`
    mutation TriggerMeetingNoteExtraction($orgSlug: String!, $meetingNotePid: String!) {
  orgBySlug(slug: $orgSlug) {
    meetingNotes {
      triggerExtraction(meetingNotePid: $meetingNotePid) {
        __typename
        ... on CeleryTask {
          taskId
          state
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useTriggerMeetingNoteExtractionMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.TriggerMeetingNoteExtractionMutation,
    TError,
    Types.TriggerMeetingNoteExtractionMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TriggerMeetingNoteExtractionMutation,
    TError,
    Types.TriggerMeetingNoteExtractionMutationVariables,
    TContext
  >({
    mutationKey: ["TriggerMeetingNoteExtraction"],
    mutationFn: (variables?: Types.TriggerMeetingNoteExtractionMutationVariables) =>
      publicFetcher<
        Types.TriggerMeetingNoteExtractionMutation,
        Types.TriggerMeetingNoteExtractionMutationVariables
      >(TriggerMeetingNoteExtractionDocument, variables)(),
    ...options,
  })
}

useTriggerMeetingNoteExtractionMutation.fetcher = (
  variables: Types.TriggerMeetingNoteExtractionMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TriggerMeetingNoteExtractionMutation,
    Types.TriggerMeetingNoteExtractionMutationVariables
  >(TriggerMeetingNoteExtractionDocument, variables, options)

export const LinkGenabilityLseToUtilityDocument = new TypedDocumentString(`
    mutation LinkGenabilityLseToUtility($lseId: Int!, $utilityPid: String!) {
  genability {
    linkLseToUtility(lseId: $lseId, utilityPid: $utilityPid) {
      id
      pid
      name
    }
  }
}
    `)

export const useLinkGenabilityLseToUtilityMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.LinkGenabilityLseToUtilityMutation,
    TError,
    Types.LinkGenabilityLseToUtilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LinkGenabilityLseToUtilityMutation,
    TError,
    Types.LinkGenabilityLseToUtilityMutationVariables,
    TContext
  >({
    mutationKey: ["LinkGenabilityLseToUtility"],
    mutationFn: (variables?: Types.LinkGenabilityLseToUtilityMutationVariables) =>
      publicFetcher<
        Types.LinkGenabilityLseToUtilityMutation,
        Types.LinkGenabilityLseToUtilityMutationVariables
      >(LinkGenabilityLseToUtilityDocument, variables)(),
    ...options,
  })
}

useLinkGenabilityLseToUtilityMutation.fetcher = (
  variables: Types.LinkGenabilityLseToUtilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.LinkGenabilityLseToUtilityMutation,
    Types.LinkGenabilityLseToUtilityMutationVariables
  >(LinkGenabilityLseToUtilityDocument, variables, options)

export const CreateUtilityFromGenabilityLseDocument = new TypedDocumentString(`
    mutation CreateUtilityFromGenabilityLse($lseId: Int!, $name: String, $shortName: String) {
  genability {
    createUtilityFromLse(lseId: $lseId, name: $name, shortName: $shortName) {
      id
      pid
      name
    }
  }
}
    `)

export const useCreateUtilityFromGenabilityLseMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateUtilityFromGenabilityLseMutation,
    TError,
    Types.CreateUtilityFromGenabilityLseMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateUtilityFromGenabilityLseMutation,
    TError,
    Types.CreateUtilityFromGenabilityLseMutationVariables,
    TContext
  >({
    mutationKey: ["CreateUtilityFromGenabilityLse"],
    mutationFn: (variables?: Types.CreateUtilityFromGenabilityLseMutationVariables) =>
      publicFetcher<
        Types.CreateUtilityFromGenabilityLseMutation,
        Types.CreateUtilityFromGenabilityLseMutationVariables
      >(CreateUtilityFromGenabilityLseDocument, variables)(),
    ...options,
  })
}

useCreateUtilityFromGenabilityLseMutation.fetcher = (
  variables: Types.CreateUtilityFromGenabilityLseMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateUtilityFromGenabilityLseMutation,
    Types.CreateUtilityFromGenabilityLseMutationVariables
  >(CreateUtilityFromGenabilityLseDocument, variables, options)

export const UnmergeCompanyMergeDocument = new TypedDocumentString(`
    mutation UnmergeCompanyMerge($recordId: Int!, $kind: CompanyMergeReversalKind!, $reason: String!) {
  admin {
    companies {
      unmergeCompanyMerge(recordId: $recordId, kind: $kind, reason: $reason) {
        __typename
        ... on CompanyUnmergeResult {
          company {
            pid
            canonicalName
          }
          warnings
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useUnmergeCompanyMergeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UnmergeCompanyMergeMutation,
    TError,
    Types.UnmergeCompanyMergeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UnmergeCompanyMergeMutation,
    TError,
    Types.UnmergeCompanyMergeMutationVariables,
    TContext
  >({
    mutationKey: ["UnmergeCompanyMerge"],
    mutationFn: (variables?: Types.UnmergeCompanyMergeMutationVariables) =>
      publicFetcher<
        Types.UnmergeCompanyMergeMutation,
        Types.UnmergeCompanyMergeMutationVariables
      >(UnmergeCompanyMergeDocument, variables)(),
    ...options,
  })
}

useUnmergeCompanyMergeMutation.fetcher = (
  variables: Types.UnmergeCompanyMergeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UnmergeCompanyMergeMutation,
    Types.UnmergeCompanyMergeMutationVariables
  >(UnmergeCompanyMergeDocument, variables, options)

export const ApproveCompanyDuplicatesDocument = new TypedDocumentString(`
    mutation ApproveCompanyDuplicates($input: CompanyDuplicateApprovalInput!) {
  admin {
    companies {
      approveDuplicates(input: $input) {
        __typename
        ... on CompanyDuplicateReviewResult {
          background
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useApproveCompanyDuplicatesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ApproveCompanyDuplicatesMutation,
    TError,
    Types.ApproveCompanyDuplicatesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApproveCompanyDuplicatesMutation,
    TError,
    Types.ApproveCompanyDuplicatesMutationVariables,
    TContext
  >({
    mutationKey: ["ApproveCompanyDuplicates"],
    mutationFn: (variables?: Types.ApproveCompanyDuplicatesMutationVariables) =>
      publicFetcher<
        Types.ApproveCompanyDuplicatesMutation,
        Types.ApproveCompanyDuplicatesMutationVariables
      >(ApproveCompanyDuplicatesDocument, variables)(),
    ...options,
  })
}

useApproveCompanyDuplicatesMutation.fetcher = (
  variables: Types.ApproveCompanyDuplicatesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ApproveCompanyDuplicatesMutation,
    Types.ApproveCompanyDuplicatesMutationVariables
  >(ApproveCompanyDuplicatesDocument, variables, options)

export const RejectCompanyDuplicatesDocument = new TypedDocumentString(`
    mutation RejectCompanyDuplicates($pairIds: [Int!]!, $companyIds: [Int!]!) {
  admin {
    companies {
      rejectDuplicates(pairIds: $pairIds, companyIds: $companyIds) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useRejectCompanyDuplicatesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RejectCompanyDuplicatesMutation,
    TError,
    Types.RejectCompanyDuplicatesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RejectCompanyDuplicatesMutation,
    TError,
    Types.RejectCompanyDuplicatesMutationVariables,
    TContext
  >({
    mutationKey: ["RejectCompanyDuplicates"],
    mutationFn: (variables?: Types.RejectCompanyDuplicatesMutationVariables) =>
      publicFetcher<
        Types.RejectCompanyDuplicatesMutation,
        Types.RejectCompanyDuplicatesMutationVariables
      >(RejectCompanyDuplicatesDocument, variables)(),
    ...options,
  })
}

useRejectCompanyDuplicatesMutation.fetcher = (
  variables: Types.RejectCompanyDuplicatesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RejectCompanyDuplicatesMutation,
    Types.RejectCompanyDuplicatesMutationVariables
  >(RejectCompanyDuplicatesDocument, variables, options)

export const StartCompanyDuplicateDetectionRunDocument = new TypedDocumentString(`
    mutation StartCompanyDuplicateDetectionRun($input: CompanyDuplicateRunInput!) {
  admin {
    companies {
      startCompanyDuplicateDetectionRun(input: $input) {
        __typename
        ... on CompanyDuplicateRun {
          id
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useStartCompanyDuplicateDetectionRunMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.StartCompanyDuplicateDetectionRunMutation,
    TError,
    Types.StartCompanyDuplicateDetectionRunMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.StartCompanyDuplicateDetectionRunMutation,
    TError,
    Types.StartCompanyDuplicateDetectionRunMutationVariables,
    TContext
  >({
    mutationKey: ["StartCompanyDuplicateDetectionRun"],
    mutationFn: (
      variables?: Types.StartCompanyDuplicateDetectionRunMutationVariables,
    ) =>
      publicFetcher<
        Types.StartCompanyDuplicateDetectionRunMutation,
        Types.StartCompanyDuplicateDetectionRunMutationVariables
      >(StartCompanyDuplicateDetectionRunDocument, variables)(),
    ...options,
  })
}

useStartCompanyDuplicateDetectionRunMutation.fetcher = (
  variables: Types.StartCompanyDuplicateDetectionRunMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.StartCompanyDuplicateDetectionRunMutation,
    Types.StartCompanyDuplicateDetectionRunMutationVariables
  >(StartCompanyDuplicateDetectionRunDocument, variables, options)

export const CancelCompanyDuplicateDetectionRunDocument = new TypedDocumentString(`
    mutation CancelCompanyDuplicateDetectionRun($id: Int!) {
  admin {
    companies {
      cancelCompanyDuplicateDetectionRun(id: $id) {
        __typename
        ... on CompanyDuplicateRun {
          id
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useCancelCompanyDuplicateDetectionRunMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CancelCompanyDuplicateDetectionRunMutation,
    TError,
    Types.CancelCompanyDuplicateDetectionRunMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CancelCompanyDuplicateDetectionRunMutation,
    TError,
    Types.CancelCompanyDuplicateDetectionRunMutationVariables,
    TContext
  >({
    mutationKey: ["CancelCompanyDuplicateDetectionRun"],
    mutationFn: (
      variables?: Types.CancelCompanyDuplicateDetectionRunMutationVariables,
    ) =>
      publicFetcher<
        Types.CancelCompanyDuplicateDetectionRunMutation,
        Types.CancelCompanyDuplicateDetectionRunMutationVariables
      >(CancelCompanyDuplicateDetectionRunDocument, variables)(),
    ...options,
  })
}

useCancelCompanyDuplicateDetectionRunMutation.fetcher = (
  variables: Types.CancelCompanyDuplicateDetectionRunMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CancelCompanyDuplicateDetectionRunMutation,
    Types.CancelCompanyDuplicateDetectionRunMutationVariables
  >(CancelCompanyDuplicateDetectionRunDocument, variables, options)

export const SetBatteryAssumptionsDocument = new TypedDocumentString(`
    mutation SetBatteryAssumptions($sitePid: String!, $durationHours: Float!, $maxMw: Float, $resetMaxMw: Boolean! = false) {
  site(sitePid: $sitePid) {
    setBatteryAssumptions(
      durationHours: $durationHours
      maxMw: $maxMw
      resetMaxMw: $resetMaxMw
    ) {
      pid
    }
  }
}
    `)

export const useSetBatteryAssumptionsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBatteryAssumptionsMutation,
    TError,
    Types.SetBatteryAssumptionsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBatteryAssumptionsMutation,
    TError,
    Types.SetBatteryAssumptionsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBatteryAssumptions"],
    mutationFn: (variables?: Types.SetBatteryAssumptionsMutationVariables) =>
      publicFetcher<
        Types.SetBatteryAssumptionsMutation,
        Types.SetBatteryAssumptionsMutationVariables
      >(SetBatteryAssumptionsDocument, variables)(),
    ...options,
  })
}

useSetBatteryAssumptionsMutation.fetcher = (
  variables: Types.SetBatteryAssumptionsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBatteryAssumptionsMutation,
    Types.SetBatteryAssumptionsMutationVariables
  >(SetBatteryAssumptionsDocument, variables, options)

export const SetTariffAlwaysTrackedDocument = new TypedDocumentString(`
    mutation SetTariffAlwaysTracked($id: Int!, $tracked: Boolean!) {
  admin {
    tariff {
      setAlwaysTracked(id: $id, tracked: $tracked) {
        id
        tracking {
          alwaysTrackedSince
          lastAssignedAt
        }
      }
    }
  }
}
    `)

export const useSetTariffAlwaysTrackedMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetTariffAlwaysTrackedMutation,
    TError,
    Types.SetTariffAlwaysTrackedMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetTariffAlwaysTrackedMutation,
    TError,
    Types.SetTariffAlwaysTrackedMutationVariables,
    TContext
  >({
    mutationKey: ["SetTariffAlwaysTracked"],
    mutationFn: (variables?: Types.SetTariffAlwaysTrackedMutationVariables) =>
      publicFetcher<
        Types.SetTariffAlwaysTrackedMutation,
        Types.SetTariffAlwaysTrackedMutationVariables
      >(SetTariffAlwaysTrackedDocument, variables)(),
    ...options,
  })
}

useSetTariffAlwaysTrackedMutation.fetcher = (
  variables: Types.SetTariffAlwaysTrackedMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetTariffAlwaysTrackedMutation,
    Types.SetTariffAlwaysTrackedMutationVariables
  >(SetTariffAlwaysTrackedDocument, variables, options)

export const AdminSitesTableViewDocument = new TypedDocumentString(`
    query AdminSitesTableView($filterBy: AdminSitesTableFilter, $sortBy: [AdminSitesTableSort!]) {
  admin {
    allSites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useAdminSitesTableViewQuery = <
  TData = Types.AdminSitesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableView"]
        : ["AdminSitesTableView", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableViewQuery,
      Types.AdminSitesTableViewQueryVariables
    >(AdminSitesTableViewDocument, variables),
    ...options,
  })
}

useAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminSitesTableView"] : ["AdminSitesTableView", variables]

useAdminSitesTableViewQuery.rootKey = "AdminSitesTableView"

export const useSuspenseAdminSitesTableViewQuery = <
  TData = Types.AdminSitesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableView"]
        : ["AdminSitesTableView", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableViewQuery,
      Types.AdminSitesTableViewQueryVariables
    >(AdminSitesTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminSitesTableView"] : ["AdminSitesTableView", variables]

useSuspenseAdminSitesTableViewQuery.rootKey = "AdminSitesTableView"

export const useInfiniteAdminSitesTableViewQuery = <
  TData = InfiniteData<Types.AdminSitesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableView.infinite"]
            : ["AdminSitesTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableViewQuery,
            Types.AdminSitesTableViewQueryVariables
          >(AdminSitesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableView.infinite"]
    : ["AdminSitesTableView.infinite", variables]

useInfiniteAdminSitesTableViewQuery.rootKey = "AdminSitesTableView.infinite"

export const useSuspenseInfiniteAdminSitesTableViewQuery = <
  TData = InfiniteData<Types.AdminSitesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableView.infinite"]
            : ["AdminSitesTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableViewQuery,
            Types.AdminSitesTableViewQueryVariables
          >(AdminSitesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableView.infinite"]
    : ["AdminSitesTableView.infinite", variables]

useSuspenseInfiniteAdminSitesTableViewQuery.rootKey = "AdminSitesTableView.infinite"

useAdminSitesTableViewQuery.fetcher = (
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSitesTableViewQuery,
    Types.AdminSitesTableViewQueryVariables
  >(AdminSitesTableViewDocument, variables, options)

export const AdminSitesTableStatsDocument = new TypedDocumentString(`
    query AdminSitesTableStats {
  admin {
    allSites {
      view {
        stats {
          orgNames
          portfolios
          utilityNames
          cities
          globalLabelNames
        }
      }
    }
  }
}
    `)

export const useAdminSitesTableStatsQuery = <
  TData = Types.AdminSitesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableStats"]
        : ["AdminSitesTableStats", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableStatsQuery,
      Types.AdminSitesTableStatsQueryVariables
    >(AdminSitesTableStatsDocument, variables),
    ...options,
  })
}

useAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats"]
    : ["AdminSitesTableStats", variables]

useAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats"

export const useSuspenseAdminSitesTableStatsQuery = <
  TData = Types.AdminSitesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableStats"]
        : ["AdminSitesTableStats", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableStatsQuery,
      Types.AdminSitesTableStatsQueryVariables
    >(AdminSitesTableStatsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats"]
    : ["AdminSitesTableStats", variables]

useSuspenseAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats"

export const useInfiniteAdminSitesTableStatsQuery = <
  TData = InfiniteData<Types.AdminSitesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableStats.infinite"]
            : ["AdminSitesTableStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableStatsQuery,
            Types.AdminSitesTableStatsQueryVariables
          >(AdminSitesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats.infinite"]
    : ["AdminSitesTableStats.infinite", variables]

useInfiniteAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats.infinite"

export const useSuspenseInfiniteAdminSitesTableStatsQuery = <
  TData = InfiniteData<Types.AdminSitesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableStats.infinite"]
            : ["AdminSitesTableStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableStatsQuery,
            Types.AdminSitesTableStatsQueryVariables
          >(AdminSitesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats.infinite"]
    : ["AdminSitesTableStats.infinite", variables]

useSuspenseInfiniteAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats.infinite"

useAdminSitesTableStatsQuery.fetcher = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSitesTableStatsQuery,
    Types.AdminSitesTableStatsQueryVariables
  >(AdminSitesTableStatsDocument, variables, options)

export const AdminSitesTableRowsDocument = new TypedDocumentString(`
    query AdminSitesTableRows($keys: [Int!]!) {
  admin {
    allSites {
      rows(keys: $keys) {
        id
        orgName
        orgPid
        orgSlug
        portfolioPid
        portfolioName
        sitePid
        createdAt
        status
        statusReason
        statusCustomReason
        name
        customerId
        streetAddress
        city
        state
        zipCode
        roofMaxPvKw
        utilityId
        utilityShortName
        utilityName
        buildingFootprintSqft
        freeSpaceSqft
        buildingType
        inWetland
        siteLabels {
          pid
          name
          themeColor
          isGlobal
        }
      }
    }
  }
}
    `)

export const useAdminSitesTableRowsQuery = <
  TData = Types.AdminSitesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableRowsQuery, TError, TData>({
    queryKey: ["AdminSitesTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableRowsQuery,
      Types.AdminSitesTableRowsQueryVariables
    >(AdminSitesTableRowsDocument, variables),
    ...options,
  })
}

useAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows", variables]

useAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows"

export const useSuspenseAdminSitesTableRowsQuery = <
  TData = Types.AdminSitesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableRowsQuery, TError, TData>({
    queryKey: ["AdminSitesTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableRowsQuery,
      Types.AdminSitesTableRowsQueryVariables
    >(AdminSitesTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows", variables]

useSuspenseAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows"

export const useInfiniteAdminSitesTableRowsQuery = <
  TData = InfiniteData<Types.AdminSitesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSitesTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableRowsQuery,
            Types.AdminSitesTableRowsQueryVariables
          >(AdminSitesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows.infinite", variables]

useInfiniteAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows.infinite"

export const useSuspenseInfiniteAdminSitesTableRowsQuery = <
  TData = InfiniteData<Types.AdminSitesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSitesTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableRowsQuery,
            Types.AdminSitesTableRowsQueryVariables
          >(AdminSitesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows.infinite", variables]

useSuspenseInfiniteAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows.infinite"

useAdminSitesTableRowsQuery.fetcher = (
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSitesTableRowsQuery,
    Types.AdminSitesTableRowsQueryVariables
  >(AdminSitesTableRowsDocument, variables, options)

export const GroundsReviewTableRowsDocument = new TypedDocumentString(`
    query GroundsReviewTableRows($keys: [Int!]!) {
  admin {
    allSites {
      rows(keys: $keys) {
        id
        sitePid
        name
        streetAddress
        orgPid
        orgName
        city
        state
        zipCode
        utilityName
        freeSpaceSqft
        hasGroundsShape
      }
    }
  }
}
    `)

export const useGroundsReviewTableRowsQuery = <
  TData = Types.GroundsReviewTableRowsQuery,
  TError = unknown,
>(
  variables: Types.GroundsReviewTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GroundsReviewTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GroundsReviewTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GroundsReviewTableRowsQuery, TError, TData>({
    queryKey: ["GroundsReviewTableRows", variables],
    queryFn: publicFetcher<
      Types.GroundsReviewTableRowsQuery,
      Types.GroundsReviewTableRowsQueryVariables
    >(GroundsReviewTableRowsDocument, variables),
    ...options,
  })
}

useGroundsReviewTableRowsQuery.getKey = (
  variables: Types.GroundsReviewTableRowsQueryVariables,
) => ["GroundsReviewTableRows", variables]

useGroundsReviewTableRowsQuery.rootKey = "GroundsReviewTableRows"

export const useSuspenseGroundsReviewTableRowsQuery = <
  TData = Types.GroundsReviewTableRowsQuery,
  TError = unknown,
>(
  variables: Types.GroundsReviewTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GroundsReviewTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GroundsReviewTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GroundsReviewTableRowsQuery, TError, TData>({
    queryKey: ["GroundsReviewTableRows", variables],
    queryFn: publicFetcher<
      Types.GroundsReviewTableRowsQuery,
      Types.GroundsReviewTableRowsQueryVariables
    >(GroundsReviewTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseGroundsReviewTableRowsQuery.getKey = (
  variables: Types.GroundsReviewTableRowsQueryVariables,
) => ["GroundsReviewTableRows", variables]

useSuspenseGroundsReviewTableRowsQuery.rootKey = "GroundsReviewTableRows"

export const useInfiniteGroundsReviewTableRowsQuery = <
  TData = InfiniteData<Types.GroundsReviewTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.GroundsReviewTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GroundsReviewTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GroundsReviewTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GroundsReviewTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GroundsReviewTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GroundsReviewTableRowsQuery,
            Types.GroundsReviewTableRowsQueryVariables
          >(GroundsReviewTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGroundsReviewTableRowsQuery.getKey = (
  variables: Types.GroundsReviewTableRowsQueryVariables,
) => ["GroundsReviewTableRows.infinite", variables]

useInfiniteGroundsReviewTableRowsQuery.rootKey = "GroundsReviewTableRows.infinite"

export const useSuspenseInfiniteGroundsReviewTableRowsQuery = <
  TData = InfiniteData<Types.GroundsReviewTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.GroundsReviewTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GroundsReviewTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GroundsReviewTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GroundsReviewTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GroundsReviewTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GroundsReviewTableRowsQuery,
            Types.GroundsReviewTableRowsQueryVariables
          >(GroundsReviewTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGroundsReviewTableRowsQuery.getKey = (
  variables: Types.GroundsReviewTableRowsQueryVariables,
) => ["GroundsReviewTableRows.infinite", variables]

useSuspenseInfiniteGroundsReviewTableRowsQuery.rootKey =
  "GroundsReviewTableRows.infinite"

useGroundsReviewTableRowsQuery.fetcher = (
  variables: Types.GroundsReviewTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GroundsReviewTableRowsQuery,
    Types.GroundsReviewTableRowsQueryVariables
  >(GroundsReviewTableRowsDocument, variables, options)

export const UtilityAssignmentTableRowsDocument = new TypedDocumentString(`
    query UtilityAssignmentTableRows($keys: [Int!]!) {
  admin {
    allSites {
      rows(keys: $keys) {
        id
        sitePid
        orgName
        orgPid
        streetAddress
        city
        state
        zipCode
        utilityId
        utilityName
        utilityCandidateCount
        utilityCandidates
        utilityIsGuess
      }
    }
  }
}
    `)

export const useUtilityAssignmentTableRowsQuery = <
  TData = Types.UtilityAssignmentTableRowsQuery,
  TError = unknown,
>(
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityAssignmentTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.UtilityAssignmentTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.UtilityAssignmentTableRowsQuery, TError, TData>({
    queryKey: ["UtilityAssignmentTableRows", variables],
    queryFn: publicFetcher<
      Types.UtilityAssignmentTableRowsQuery,
      Types.UtilityAssignmentTableRowsQueryVariables
    >(UtilityAssignmentTableRowsDocument, variables),
    ...options,
  })
}

useUtilityAssignmentTableRowsQuery.getKey = (
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
) => ["UtilityAssignmentTableRows", variables]

useUtilityAssignmentTableRowsQuery.rootKey = "UtilityAssignmentTableRows"

export const useSuspenseUtilityAssignmentTableRowsQuery = <
  TData = Types.UtilityAssignmentTableRowsQuery,
  TError = unknown,
>(
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityAssignmentTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityAssignmentTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityAssignmentTableRowsQuery, TError, TData>({
    queryKey: ["UtilityAssignmentTableRows", variables],
    queryFn: publicFetcher<
      Types.UtilityAssignmentTableRowsQuery,
      Types.UtilityAssignmentTableRowsQueryVariables
    >(UtilityAssignmentTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseUtilityAssignmentTableRowsQuery.getKey = (
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
) => ["UtilityAssignmentTableRows", variables]

useSuspenseUtilityAssignmentTableRowsQuery.rootKey = "UtilityAssignmentTableRows"

export const useInfiniteUtilityAssignmentTableRowsQuery = <
  TData = InfiniteData<Types.UtilityAssignmentTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityAssignmentTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityAssignmentTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityAssignmentTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityAssignmentTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UtilityAssignmentTableRowsQuery,
            Types.UtilityAssignmentTableRowsQueryVariables
          >(UtilityAssignmentTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityAssignmentTableRowsQuery.getKey = (
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
) => ["UtilityAssignmentTableRows.infinite", variables]

useInfiniteUtilityAssignmentTableRowsQuery.rootKey =
  "UtilityAssignmentTableRows.infinite"

export const useSuspenseInfiniteUtilityAssignmentTableRowsQuery = <
  TData = InfiniteData<Types.UtilityAssignmentTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.UtilityAssignmentTableRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityAssignmentTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityAssignmentTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityAssignmentTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UtilityAssignmentTableRowsQuery,
            Types.UtilityAssignmentTableRowsQueryVariables
          >(UtilityAssignmentTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityAssignmentTableRowsQuery.getKey = (
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
) => ["UtilityAssignmentTableRows.infinite", variables]

useSuspenseInfiniteUtilityAssignmentTableRowsQuery.rootKey =
  "UtilityAssignmentTableRows.infinite"

useUtilityAssignmentTableRowsQuery.fetcher = (
  variables: Types.UtilityAssignmentTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UtilityAssignmentTableRowsQuery,
    Types.UtilityAssignmentTableRowsQueryVariables
  >(UtilityAssignmentTableRowsDocument, variables, options)

export const UtilityAssignmentTableStatsDocument = new TypedDocumentString(`
    query UtilityAssignmentTableStats {
  admin {
    allSites {
      view {
        stats {
          utilityNames
          utilityCandidateNames
          cities
        }
      }
    }
  }
}
    `)

export const useUtilityAssignmentTableStatsQuery = <
  TData = Types.UtilityAssignmentTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.UtilityAssignmentTableStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityAssignmentTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.UtilityAssignmentTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.UtilityAssignmentTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UtilityAssignmentTableStats"]
        : ["UtilityAssignmentTableStats", variables],
    queryFn: publicFetcher<
      Types.UtilityAssignmentTableStatsQuery,
      Types.UtilityAssignmentTableStatsQueryVariables
    >(UtilityAssignmentTableStatsDocument, variables),
    ...options,
  })
}

useUtilityAssignmentTableStatsQuery.getKey = (
  variables?: Types.UtilityAssignmentTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["UtilityAssignmentTableStats"]
    : ["UtilityAssignmentTableStats", variables]

useUtilityAssignmentTableStatsQuery.rootKey = "UtilityAssignmentTableStats"

export const useSuspenseUtilityAssignmentTableStatsQuery = <
  TData = Types.UtilityAssignmentTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.UtilityAssignmentTableStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityAssignmentTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityAssignmentTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityAssignmentTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UtilityAssignmentTableStats"]
        : ["UtilityAssignmentTableStats", variables],
    queryFn: publicFetcher<
      Types.UtilityAssignmentTableStatsQuery,
      Types.UtilityAssignmentTableStatsQueryVariables
    >(UtilityAssignmentTableStatsDocument, variables),
    ...options,
  })
}

useSuspenseUtilityAssignmentTableStatsQuery.getKey = (
  variables?: Types.UtilityAssignmentTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["UtilityAssignmentTableStats"]
    : ["UtilityAssignmentTableStats", variables]

useSuspenseUtilityAssignmentTableStatsQuery.rootKey = "UtilityAssignmentTableStats"

export const useInfiniteUtilityAssignmentTableStatsQuery = <
  TData = InfiniteData<Types.UtilityAssignmentTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityAssignmentTableStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityAssignmentTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityAssignmentTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityAssignmentTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityAssignmentTableStats.infinite"]
            : ["UtilityAssignmentTableStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UtilityAssignmentTableStatsQuery,
            Types.UtilityAssignmentTableStatsQueryVariables
          >(UtilityAssignmentTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityAssignmentTableStatsQuery.getKey = (
  variables?: Types.UtilityAssignmentTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["UtilityAssignmentTableStats.infinite"]
    : ["UtilityAssignmentTableStats.infinite", variables]

useInfiniteUtilityAssignmentTableStatsQuery.rootKey =
  "UtilityAssignmentTableStats.infinite"

export const useSuspenseInfiniteUtilityAssignmentTableStatsQuery = <
  TData = InfiniteData<Types.UtilityAssignmentTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityAssignmentTableStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.UtilityAssignmentTableStatsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityAssignmentTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.UtilityAssignmentTableStatsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityAssignmentTableStats.infinite"]
            : ["UtilityAssignmentTableStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UtilityAssignmentTableStatsQuery,
            Types.UtilityAssignmentTableStatsQueryVariables
          >(UtilityAssignmentTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityAssignmentTableStatsQuery.getKey = (
  variables?: Types.UtilityAssignmentTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["UtilityAssignmentTableStats.infinite"]
    : ["UtilityAssignmentTableStats.infinite", variables]

useSuspenseInfiniteUtilityAssignmentTableStatsQuery.rootKey =
  "UtilityAssignmentTableStats.infinite"

useUtilityAssignmentTableStatsQuery.fetcher = (
  variables?: Types.UtilityAssignmentTableStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UtilityAssignmentTableStatsQuery,
    Types.UtilityAssignmentTableStatsQueryVariables
  >(UtilityAssignmentTableStatsDocument, variables, options)

export const SiteUtilityCandidatesDocument = new TypedDocumentString(`
    query SiteUtilityCandidates($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      probableUtilities {
        id
        name
      }
    }
  }
}
    `)

export const useSiteUtilityCandidatesQuery = <
  TData = Types.SiteUtilityCandidatesQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityCandidatesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteUtilityCandidatesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteUtilityCandidatesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteUtilityCandidatesQuery, TError, TData>({
    queryKey: ["SiteUtilityCandidates", variables],
    queryFn: publicFetcher<
      Types.SiteUtilityCandidatesQuery,
      Types.SiteUtilityCandidatesQueryVariables
    >(SiteUtilityCandidatesDocument, variables),
    ...options,
  })
}

useSiteUtilityCandidatesQuery.getKey = (
  variables: Types.SiteUtilityCandidatesQueryVariables,
) => ["SiteUtilityCandidates", variables]

useSiteUtilityCandidatesQuery.rootKey = "SiteUtilityCandidates"

export const useSuspenseSiteUtilityCandidatesQuery = <
  TData = Types.SiteUtilityCandidatesQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityCandidatesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteUtilityCandidatesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteUtilityCandidatesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteUtilityCandidatesQuery, TError, TData>({
    queryKey: ["SiteUtilityCandidates", variables],
    queryFn: publicFetcher<
      Types.SiteUtilityCandidatesQuery,
      Types.SiteUtilityCandidatesQueryVariables
    >(SiteUtilityCandidatesDocument, variables),
    ...options,
  })
}

useSuspenseSiteUtilityCandidatesQuery.getKey = (
  variables: Types.SiteUtilityCandidatesQueryVariables,
) => ["SiteUtilityCandidates", variables]

useSuspenseSiteUtilityCandidatesQuery.rootKey = "SiteUtilityCandidates"

export const useInfiniteSiteUtilityCandidatesQuery = <
  TData = InfiniteData<Types.SiteUtilityCandidatesQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityCandidatesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteUtilityCandidatesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteUtilityCandidatesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteUtilityCandidatesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtilityCandidates.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteUtilityCandidatesQuery,
            Types.SiteUtilityCandidatesQueryVariables
          >(SiteUtilityCandidatesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteUtilityCandidatesQuery.getKey = (
  variables: Types.SiteUtilityCandidatesQueryVariables,
) => ["SiteUtilityCandidates.infinite", variables]

useInfiniteSiteUtilityCandidatesQuery.rootKey = "SiteUtilityCandidates.infinite"

export const useSuspenseInfiniteSiteUtilityCandidatesQuery = <
  TData = InfiniteData<Types.SiteUtilityCandidatesQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityCandidatesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteUtilityCandidatesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteUtilityCandidatesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteUtilityCandidatesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtilityCandidates.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteUtilityCandidatesQuery,
            Types.SiteUtilityCandidatesQueryVariables
          >(SiteUtilityCandidatesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteUtilityCandidatesQuery.getKey = (
  variables: Types.SiteUtilityCandidatesQueryVariables,
) => ["SiteUtilityCandidates.infinite", variables]

useSuspenseInfiniteSiteUtilityCandidatesQuery.rootKey = "SiteUtilityCandidates.infinite"

useSiteUtilityCandidatesQuery.fetcher = (
  variables: Types.SiteUtilityCandidatesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteUtilityCandidatesQuery,
    Types.SiteUtilityCandidatesQueryVariables
  >(SiteUtilityCandidatesDocument, variables, options)

export const AdminBuildingsTableViewDocument = new TypedDocumentString(`
    query AdminBuildingsTableView($buildingFilter: BuildingFilter, $filterBy: AdminBuildingsTableFilter, $sortBy: [AdminBuildingsTableSort!]) {
  admin {
    allBuildings(buildingFilter: $buildingFilter) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useAdminBuildingsTableViewQuery = <
  TData = Types.AdminBuildingsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBuildingsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBuildingsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBuildingsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBuildingsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBuildingsTableView"]
        : ["AdminBuildingsTableView", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingsTableViewQuery,
      Types.AdminBuildingsTableViewQueryVariables
    >(AdminBuildingsTableViewDocument, variables),
    ...options,
  })
}

useAdminBuildingsTableViewQuery.getKey = (
  variables?: Types.AdminBuildingsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingsTableView"]
    : ["AdminBuildingsTableView", variables]

useAdminBuildingsTableViewQuery.rootKey = "AdminBuildingsTableView"

export const useSuspenseAdminBuildingsTableViewQuery = <
  TData = Types.AdminBuildingsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBuildingsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBuildingsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBuildingsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBuildingsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBuildingsTableView"]
        : ["AdminBuildingsTableView", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingsTableViewQuery,
      Types.AdminBuildingsTableViewQueryVariables
    >(AdminBuildingsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBuildingsTableViewQuery.getKey = (
  variables?: Types.AdminBuildingsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingsTableView"]
    : ["AdminBuildingsTableView", variables]

useSuspenseAdminBuildingsTableViewQuery.rootKey = "AdminBuildingsTableView"

export const useInfiniteAdminBuildingsTableViewQuery = <
  TData = InfiniteData<Types.AdminBuildingsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBuildingsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBuildingsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBuildingsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBuildingsTableView.infinite"]
            : ["AdminBuildingsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingsTableViewQuery,
            Types.AdminBuildingsTableViewQueryVariables
          >(AdminBuildingsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBuildingsTableViewQuery.getKey = (
  variables?: Types.AdminBuildingsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingsTableView.infinite"]
    : ["AdminBuildingsTableView.infinite", variables]

useInfiniteAdminBuildingsTableViewQuery.rootKey = "AdminBuildingsTableView.infinite"

export const useSuspenseInfiniteAdminBuildingsTableViewQuery = <
  TData = InfiniteData<Types.AdminBuildingsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBuildingsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBuildingsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBuildingsTableView.infinite"]
            : ["AdminBuildingsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingsTableViewQuery,
            Types.AdminBuildingsTableViewQueryVariables
          >(AdminBuildingsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBuildingsTableViewQuery.getKey = (
  variables?: Types.AdminBuildingsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingsTableView.infinite"]
    : ["AdminBuildingsTableView.infinite", variables]

useSuspenseInfiniteAdminBuildingsTableViewQuery.rootKey =
  "AdminBuildingsTableView.infinite"

useAdminBuildingsTableViewQuery.fetcher = (
  variables?: Types.AdminBuildingsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBuildingsTableViewQuery,
    Types.AdminBuildingsTableViewQueryVariables
  >(AdminBuildingsTableViewDocument, variables, options)

export const AdminBuildingsTableRowsDocument = new TypedDocumentString(`
    query AdminBuildingsTableRows($buildingFilter: BuildingFilter, $keys: [String!]!) {
  admin {
    allBuildings(buildingFilter: $buildingFilter) {
      rows(keys: $keys) {
        pid
        createdAt
        name
        streetAddress
        city
        state
        zipCode
        yearBuilt
        numStories
        buildingType
        inWetland
        roofSqft
        sqft
        searchScore
        siteCount
        ownerCount
        tenantCount
        relationshipId
        relationshipIsPrimary
      }
    }
  }
}
    `)

export const useAdminBuildingsTableRowsQuery = <
  TData = Types.AdminBuildingsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBuildingsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBuildingsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBuildingsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBuildingsTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingsTableRowsQuery,
      Types.AdminBuildingsTableRowsQueryVariables
    >(AdminBuildingsTableRowsDocument, variables),
    ...options,
  })
}

useAdminBuildingsTableRowsQuery.getKey = (
  variables: Types.AdminBuildingsTableRowsQueryVariables,
) => ["AdminBuildingsTableRows", variables]

useAdminBuildingsTableRowsQuery.rootKey = "AdminBuildingsTableRows"

export const useSuspenseAdminBuildingsTableRowsQuery = <
  TData = Types.AdminBuildingsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBuildingsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBuildingsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBuildingsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBuildingsTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingsTableRowsQuery,
      Types.AdminBuildingsTableRowsQueryVariables
    >(AdminBuildingsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBuildingsTableRowsQuery.getKey = (
  variables: Types.AdminBuildingsTableRowsQueryVariables,
) => ["AdminBuildingsTableRows", variables]

useSuspenseAdminBuildingsTableRowsQuery.rootKey = "AdminBuildingsTableRows"

export const useInfiniteAdminBuildingsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBuildingsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBuildingsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBuildingsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBuildingsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBuildingsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingsTableRowsQuery,
            Types.AdminBuildingsTableRowsQueryVariables
          >(AdminBuildingsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBuildingsTableRowsQuery.getKey = (
  variables: Types.AdminBuildingsTableRowsQueryVariables,
) => ["AdminBuildingsTableRows.infinite", variables]

useInfiniteAdminBuildingsTableRowsQuery.rootKey = "AdminBuildingsTableRows.infinite"

export const useSuspenseInfiniteAdminBuildingsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBuildingsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBuildingsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBuildingsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBuildingsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingsTableRowsQuery,
            Types.AdminBuildingsTableRowsQueryVariables
          >(AdminBuildingsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBuildingsTableRowsQuery.getKey = (
  variables: Types.AdminBuildingsTableRowsQueryVariables,
) => ["AdminBuildingsTableRows.infinite", variables]

useSuspenseInfiniteAdminBuildingsTableRowsQuery.rootKey =
  "AdminBuildingsTableRows.infinite"

useAdminBuildingsTableRowsQuery.fetcher = (
  variables: Types.AdminBuildingsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBuildingsTableRowsQuery,
    Types.AdminBuildingsTableRowsQueryVariables
  >(AdminBuildingsTableRowsDocument, variables, options)

export const AdminBuildingOwnershipTableViewDocument = new TypedDocumentString(`
    query AdminBuildingOwnershipTableView($filterBy: BuildingOwnershipTableFilter, $sortBy: [BuildingOwnershipTableSort!]) {
  admin {
    buildingOwnership {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          withAnnotationsCount
          withAffiliationResultsCount
          withReonomyDetailsCount
        }
      }
    }
  }
}
    `)

export const useAdminBuildingOwnershipTableViewQuery = <
  TData = Types.AdminBuildingOwnershipTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBuildingOwnershipTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBuildingOwnershipTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBuildingOwnershipTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBuildingOwnershipTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBuildingOwnershipTableView"]
        : ["AdminBuildingOwnershipTableView", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingOwnershipTableViewQuery,
      Types.AdminBuildingOwnershipTableViewQueryVariables
    >(AdminBuildingOwnershipTableViewDocument, variables),
    ...options,
  })
}

useAdminBuildingOwnershipTableViewQuery.getKey = (
  variables?: Types.AdminBuildingOwnershipTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingOwnershipTableView"]
    : ["AdminBuildingOwnershipTableView", variables]

useAdminBuildingOwnershipTableViewQuery.rootKey = "AdminBuildingOwnershipTableView"

export const useSuspenseAdminBuildingOwnershipTableViewQuery = <
  TData = Types.AdminBuildingOwnershipTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBuildingOwnershipTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBuildingOwnershipTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBuildingOwnershipTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBuildingOwnershipTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBuildingOwnershipTableView"]
        : ["AdminBuildingOwnershipTableView", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingOwnershipTableViewQuery,
      Types.AdminBuildingOwnershipTableViewQueryVariables
    >(AdminBuildingOwnershipTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBuildingOwnershipTableViewQuery.getKey = (
  variables?: Types.AdminBuildingOwnershipTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingOwnershipTableView"]
    : ["AdminBuildingOwnershipTableView", variables]

useSuspenseAdminBuildingOwnershipTableViewQuery.rootKey =
  "AdminBuildingOwnershipTableView"

export const useInfiniteAdminBuildingOwnershipTableViewQuery = <
  TData = InfiniteData<Types.AdminBuildingOwnershipTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBuildingOwnershipTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBuildingOwnershipTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBuildingOwnershipTableView.infinite"]
            : ["AdminBuildingOwnershipTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingOwnershipTableViewQuery,
            Types.AdminBuildingOwnershipTableViewQueryVariables
          >(AdminBuildingOwnershipTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBuildingOwnershipTableViewQuery.getKey = (
  variables?: Types.AdminBuildingOwnershipTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingOwnershipTableView.infinite"]
    : ["AdminBuildingOwnershipTableView.infinite", variables]

useInfiniteAdminBuildingOwnershipTableViewQuery.rootKey =
  "AdminBuildingOwnershipTableView.infinite"

export const useSuspenseInfiniteAdminBuildingOwnershipTableViewQuery = <
  TData = InfiniteData<Types.AdminBuildingOwnershipTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipTableViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminBuildingOwnershipTableViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBuildingOwnershipTableView.infinite"]
            : ["AdminBuildingOwnershipTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingOwnershipTableViewQuery,
            Types.AdminBuildingOwnershipTableViewQueryVariables
          >(AdminBuildingOwnershipTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBuildingOwnershipTableViewQuery.getKey = (
  variables?: Types.AdminBuildingOwnershipTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBuildingOwnershipTableView.infinite"]
    : ["AdminBuildingOwnershipTableView.infinite", variables]

useSuspenseInfiniteAdminBuildingOwnershipTableViewQuery.rootKey =
  "AdminBuildingOwnershipTableView.infinite"

useAdminBuildingOwnershipTableViewQuery.fetcher = (
  variables?: Types.AdminBuildingOwnershipTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBuildingOwnershipTableViewQuery,
    Types.AdminBuildingOwnershipTableViewQueryVariables
  >(AdminBuildingOwnershipTableViewDocument, variables, options)

export const AdminBuildingOwnershipTableRowsDocument = new TypedDocumentString(`
    query AdminBuildingOwnershipTableRows($keys: [String!]!) {
  admin {
    buildingOwnership {
      rows(keys: $keys) {
        pid
        streetAddress
        city
        state
        zipCode
        roofSqft
        primaryOwnerName
        orgCount
        hasAnnotations
        hasAffiliationResults
        hasReonomyDetails
        owners {
          companyName
          isPrimary
          companyPid
        }
        sites {
          sitePid
          orgName
        }
      }
    }
  }
}
    `)

export const useAdminBuildingOwnershipTableRowsQuery = <
  TData = Types.AdminBuildingOwnershipTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBuildingOwnershipTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBuildingOwnershipTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBuildingOwnershipTableRowsQuery, TError, TData>({
    queryKey: ["AdminBuildingOwnershipTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingOwnershipTableRowsQuery,
      Types.AdminBuildingOwnershipTableRowsQueryVariables
    >(AdminBuildingOwnershipTableRowsDocument, variables),
    ...options,
  })
}

useAdminBuildingOwnershipTableRowsQuery.getKey = (
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
) => ["AdminBuildingOwnershipTableRows", variables]

useAdminBuildingOwnershipTableRowsQuery.rootKey = "AdminBuildingOwnershipTableRows"

export const useSuspenseAdminBuildingOwnershipTableRowsQuery = <
  TData = Types.AdminBuildingOwnershipTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBuildingOwnershipTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBuildingOwnershipTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBuildingOwnershipTableRowsQuery, TError, TData>({
    queryKey: ["AdminBuildingOwnershipTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingOwnershipTableRowsQuery,
      Types.AdminBuildingOwnershipTableRowsQueryVariables
    >(AdminBuildingOwnershipTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBuildingOwnershipTableRowsQuery.getKey = (
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
) => ["AdminBuildingOwnershipTableRows", variables]

useSuspenseAdminBuildingOwnershipTableRowsQuery.rootKey =
  "AdminBuildingOwnershipTableRows"

export const useInfiniteAdminBuildingOwnershipTableRowsQuery = <
  TData = InfiniteData<Types.AdminBuildingOwnershipTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBuildingOwnershipTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBuildingOwnershipTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBuildingOwnershipTableRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingOwnershipTableRowsQuery,
            Types.AdminBuildingOwnershipTableRowsQueryVariables
          >(AdminBuildingOwnershipTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBuildingOwnershipTableRowsQuery.getKey = (
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
) => ["AdminBuildingOwnershipTableRows.infinite", variables]

useInfiniteAdminBuildingOwnershipTableRowsQuery.rootKey =
  "AdminBuildingOwnershipTableRows.infinite"

export const useSuspenseInfiniteAdminBuildingOwnershipTableRowsQuery = <
  TData = InfiniteData<Types.AdminBuildingOwnershipTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipTableRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminBuildingOwnershipTableRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBuildingOwnershipTableRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingOwnershipTableRowsQuery,
            Types.AdminBuildingOwnershipTableRowsQueryVariables
          >(AdminBuildingOwnershipTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBuildingOwnershipTableRowsQuery.getKey = (
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
) => ["AdminBuildingOwnershipTableRows.infinite", variables]

useSuspenseInfiniteAdminBuildingOwnershipTableRowsQuery.rootKey =
  "AdminBuildingOwnershipTableRows.infinite"

useAdminBuildingOwnershipTableRowsQuery.fetcher = (
  variables: Types.AdminBuildingOwnershipTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBuildingOwnershipTableRowsQuery,
    Types.AdminBuildingOwnershipTableRowsQueryVariables
  >(AdminBuildingOwnershipTableRowsDocument, variables, options)

export const AdminBuildingExpandedRowDocument = new TypedDocumentString(`
    query AdminBuildingExpandedRow($pid: String!) {
  admin {
    buildingData(pid: $pid) {
      pid
      latitude
      longitude
      buildingFootprintGeojson
      sitesWithContext {
        site {
          pid
          name
        }
        portfolio {
          pid
          title
        }
        org {
          pid
          name
        }
      }
    }
  }
}
    `)

export const useAdminBuildingExpandedRowQuery = <
  TData = Types.AdminBuildingExpandedRowQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingExpandedRowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBuildingExpandedRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBuildingExpandedRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBuildingExpandedRowQuery, TError, TData>({
    queryKey: ["AdminBuildingExpandedRow", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingExpandedRowQuery,
      Types.AdminBuildingExpandedRowQueryVariables
    >(AdminBuildingExpandedRowDocument, variables),
    ...options,
  })
}

useAdminBuildingExpandedRowQuery.getKey = (
  variables: Types.AdminBuildingExpandedRowQueryVariables,
) => ["AdminBuildingExpandedRow", variables]

useAdminBuildingExpandedRowQuery.rootKey = "AdminBuildingExpandedRow"

export const useSuspenseAdminBuildingExpandedRowQuery = <
  TData = Types.AdminBuildingExpandedRowQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingExpandedRowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBuildingExpandedRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBuildingExpandedRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBuildingExpandedRowQuery, TError, TData>({
    queryKey: ["AdminBuildingExpandedRow", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingExpandedRowQuery,
      Types.AdminBuildingExpandedRowQueryVariables
    >(AdminBuildingExpandedRowDocument, variables),
    ...options,
  })
}

useSuspenseAdminBuildingExpandedRowQuery.getKey = (
  variables: Types.AdminBuildingExpandedRowQueryVariables,
) => ["AdminBuildingExpandedRow", variables]

useSuspenseAdminBuildingExpandedRowQuery.rootKey = "AdminBuildingExpandedRow"

export const useInfiniteAdminBuildingExpandedRowQuery = <
  TData = InfiniteData<Types.AdminBuildingExpandedRowQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingExpandedRowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBuildingExpandedRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBuildingExpandedRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBuildingExpandedRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBuildingExpandedRow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingExpandedRowQuery,
            Types.AdminBuildingExpandedRowQueryVariables
          >(AdminBuildingExpandedRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBuildingExpandedRowQuery.getKey = (
  variables: Types.AdminBuildingExpandedRowQueryVariables,
) => ["AdminBuildingExpandedRow.infinite", variables]

useInfiniteAdminBuildingExpandedRowQuery.rootKey = "AdminBuildingExpandedRow.infinite"

export const useSuspenseInfiniteAdminBuildingExpandedRowQuery = <
  TData = InfiniteData<Types.AdminBuildingExpandedRowQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingExpandedRowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBuildingExpandedRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingExpandedRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBuildingExpandedRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBuildingExpandedRow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingExpandedRowQuery,
            Types.AdminBuildingExpandedRowQueryVariables
          >(AdminBuildingExpandedRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBuildingExpandedRowQuery.getKey = (
  variables: Types.AdminBuildingExpandedRowQueryVariables,
) => ["AdminBuildingExpandedRow.infinite", variables]

useSuspenseInfiniteAdminBuildingExpandedRowQuery.rootKey =
  "AdminBuildingExpandedRow.infinite"

useAdminBuildingExpandedRowQuery.fetcher = (
  variables: Types.AdminBuildingExpandedRowQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBuildingExpandedRowQuery,
    Types.AdminBuildingExpandedRowQueryVariables
  >(AdminBuildingExpandedRowDocument, variables, options)

export const AdminBuildingDetailDocument = new TypedDocumentString(`
    query AdminBuildingDetail($pid: String!) {
  admin {
    buildingData(pid: $pid) {
      id
      pid
      propertyName
      streetAddress
      city
      state
      zipCode
      county
      latitude
      longitude
      yearBuilt
      buildingType
      sqft
      roofSqft
      numStories
      buildingFootprintGeojson
      reonomyPropertyLink
      sitesWithContext {
        site {
          pid
          name
        }
        portfolio {
          pid
          title
        }
        org {
          pid
          name
        }
      }
    }
  }
}
    `)

export const useAdminBuildingDetailQuery = <
  TData = Types.AdminBuildingDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBuildingDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBuildingDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBuildingDetailQuery, TError, TData>({
    queryKey: ["AdminBuildingDetail", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingDetailQuery,
      Types.AdminBuildingDetailQueryVariables
    >(AdminBuildingDetailDocument, variables),
    ...options,
  })
}

useAdminBuildingDetailQuery.getKey = (
  variables: Types.AdminBuildingDetailQueryVariables,
) => ["AdminBuildingDetail", variables]

useAdminBuildingDetailQuery.rootKey = "AdminBuildingDetail"

export const useSuspenseAdminBuildingDetailQuery = <
  TData = Types.AdminBuildingDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBuildingDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBuildingDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBuildingDetailQuery, TError, TData>({
    queryKey: ["AdminBuildingDetail", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingDetailQuery,
      Types.AdminBuildingDetailQueryVariables
    >(AdminBuildingDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminBuildingDetailQuery.getKey = (
  variables: Types.AdminBuildingDetailQueryVariables,
) => ["AdminBuildingDetail", variables]

useSuspenseAdminBuildingDetailQuery.rootKey = "AdminBuildingDetail"

export const useInfiniteAdminBuildingDetailQuery = <
  TData = InfiniteData<Types.AdminBuildingDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBuildingDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBuildingDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBuildingDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBuildingDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingDetailQuery,
            Types.AdminBuildingDetailQueryVariables
          >(AdminBuildingDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBuildingDetailQuery.getKey = (
  variables: Types.AdminBuildingDetailQueryVariables,
) => ["AdminBuildingDetail.infinite", variables]

useInfiniteAdminBuildingDetailQuery.rootKey = "AdminBuildingDetail.infinite"

export const useSuspenseInfiniteAdminBuildingDetailQuery = <
  TData = InfiniteData<Types.AdminBuildingDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBuildingDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBuildingDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBuildingDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingDetailQuery,
            Types.AdminBuildingDetailQueryVariables
          >(AdminBuildingDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBuildingDetailQuery.getKey = (
  variables: Types.AdminBuildingDetailQueryVariables,
) => ["AdminBuildingDetail.infinite", variables]

useSuspenseInfiniteAdminBuildingDetailQuery.rootKey = "AdminBuildingDetail.infinite"

useAdminBuildingDetailQuery.fetcher = (
  variables: Types.AdminBuildingDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBuildingDetailQuery,
    Types.AdminBuildingDetailQueryVariables
  >(AdminBuildingDetailDocument, variables, options)

export const AdminCompaniesTableViewDocument = new TypedDocumentString(`
    query AdminCompaniesTableView($filter: CompaniesFilter, $filterBy: CompaniesTableFilter, $sortBy: [CompaniesTableSort!]) {
  admin {
    companies {
      table(companyFilter: $filter) {
        view(filterBy: $filterBy, sortBy: $sortBy) {
          keys
          count
        }
      }
    }
  }
}
    `)

export const useAdminCompaniesTableViewQuery = <
  TData = Types.AdminCompaniesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompaniesTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompaniesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompaniesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompaniesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompaniesTableView"]
        : ["AdminCompaniesTableView", variables],
    queryFn: publicFetcher<
      Types.AdminCompaniesTableViewQuery,
      Types.AdminCompaniesTableViewQueryVariables
    >(AdminCompaniesTableViewDocument, variables),
    ...options,
  })
}

useAdminCompaniesTableViewQuery.getKey = (
  variables?: Types.AdminCompaniesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableView"]
    : ["AdminCompaniesTableView", variables]

useAdminCompaniesTableViewQuery.rootKey = "AdminCompaniesTableView"

export const useSuspenseAdminCompaniesTableViewQuery = <
  TData = Types.AdminCompaniesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompaniesTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompaniesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompaniesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompaniesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompaniesTableView"]
        : ["AdminCompaniesTableView", variables],
    queryFn: publicFetcher<
      Types.AdminCompaniesTableViewQuery,
      Types.AdminCompaniesTableViewQueryVariables
    >(AdminCompaniesTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompaniesTableViewQuery.getKey = (
  variables?: Types.AdminCompaniesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableView"]
    : ["AdminCompaniesTableView", variables]

useSuspenseAdminCompaniesTableViewQuery.rootKey = "AdminCompaniesTableView"

export const useInfiniteAdminCompaniesTableViewQuery = <
  TData = InfiniteData<Types.AdminCompaniesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompaniesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompaniesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompaniesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompaniesTableView.infinite"]
            : ["AdminCompaniesTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompaniesTableViewQuery,
            Types.AdminCompaniesTableViewQueryVariables
          >(AdminCompaniesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompaniesTableViewQuery.getKey = (
  variables?: Types.AdminCompaniesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableView.infinite"]
    : ["AdminCompaniesTableView.infinite", variables]

useInfiniteAdminCompaniesTableViewQuery.rootKey = "AdminCompaniesTableView.infinite"

export const useSuspenseInfiniteAdminCompaniesTableViewQuery = <
  TData = InfiniteData<Types.AdminCompaniesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminCompaniesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompaniesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCompaniesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompaniesTableView.infinite"]
            : ["AdminCompaniesTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompaniesTableViewQuery,
            Types.AdminCompaniesTableViewQueryVariables
          >(AdminCompaniesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompaniesTableViewQuery.getKey = (
  variables?: Types.AdminCompaniesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableView.infinite"]
    : ["AdminCompaniesTableView.infinite", variables]

useSuspenseInfiniteAdminCompaniesTableViewQuery.rootKey =
  "AdminCompaniesTableView.infinite"

useAdminCompaniesTableViewQuery.fetcher = (
  variables?: Types.AdminCompaniesTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompaniesTableViewQuery,
    Types.AdminCompaniesTableViewQueryVariables
  >(AdminCompaniesTableViewDocument, variables, options)

export const AdminCompaniesTableStatsDocument = new TypedDocumentString(`
    query AdminCompaniesTableStats {
  admin {
    companies {
      table {
        view {
          stats {
            orgNames
          }
        }
      }
    }
  }
}
    `)

export const useAdminCompaniesTableStatsQuery = <
  TData = Types.AdminCompaniesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompaniesTableStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompaniesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompaniesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompaniesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompaniesTableStats"]
        : ["AdminCompaniesTableStats", variables],
    queryFn: publicFetcher<
      Types.AdminCompaniesTableStatsQuery,
      Types.AdminCompaniesTableStatsQueryVariables
    >(AdminCompaniesTableStatsDocument, variables),
    ...options,
  })
}

useAdminCompaniesTableStatsQuery.getKey = (
  variables?: Types.AdminCompaniesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableStats"]
    : ["AdminCompaniesTableStats", variables]

useAdminCompaniesTableStatsQuery.rootKey = "AdminCompaniesTableStats"

export const useSuspenseAdminCompaniesTableStatsQuery = <
  TData = Types.AdminCompaniesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompaniesTableStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompaniesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompaniesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompaniesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompaniesTableStats"]
        : ["AdminCompaniesTableStats", variables],
    queryFn: publicFetcher<
      Types.AdminCompaniesTableStatsQuery,
      Types.AdminCompaniesTableStatsQueryVariables
    >(AdminCompaniesTableStatsDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompaniesTableStatsQuery.getKey = (
  variables?: Types.AdminCompaniesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableStats"]
    : ["AdminCompaniesTableStats", variables]

useSuspenseAdminCompaniesTableStatsQuery.rootKey = "AdminCompaniesTableStats"

export const useInfiniteAdminCompaniesTableStatsQuery = <
  TData = InfiniteData<Types.AdminCompaniesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompaniesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompaniesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompaniesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompaniesTableStats.infinite"]
            : ["AdminCompaniesTableStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompaniesTableStatsQuery,
            Types.AdminCompaniesTableStatsQueryVariables
          >(AdminCompaniesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompaniesTableStatsQuery.getKey = (
  variables?: Types.AdminCompaniesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableStats.infinite"]
    : ["AdminCompaniesTableStats.infinite", variables]

useInfiniteAdminCompaniesTableStatsQuery.rootKey = "AdminCompaniesTableStats.infinite"

export const useSuspenseInfiniteAdminCompaniesTableStatsQuery = <
  TData = InfiniteData<Types.AdminCompaniesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminCompaniesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompaniesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCompaniesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompaniesTableStats.infinite"]
            : ["AdminCompaniesTableStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompaniesTableStatsQuery,
            Types.AdminCompaniesTableStatsQueryVariables
          >(AdminCompaniesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompaniesTableStatsQuery.getKey = (
  variables?: Types.AdminCompaniesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompaniesTableStats.infinite"]
    : ["AdminCompaniesTableStats.infinite", variables]

useSuspenseInfiniteAdminCompaniesTableStatsQuery.rootKey =
  "AdminCompaniesTableStats.infinite"

useAdminCompaniesTableStatsQuery.fetcher = (
  variables?: Types.AdminCompaniesTableStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompaniesTableStatsQuery,
    Types.AdminCompaniesTableStatsQueryVariables
  >(AdminCompaniesTableStatsDocument, variables, options)

export const AdminCompaniesTableRowsDocument = new TypedDocumentString(`
    query AdminCompaniesTableRows($filter: CompaniesFilter, $keys: [String!]!) {
  admin {
    companies {
      table(companyFilter: $filter) {
        rows(keys: $keys) {
          pid
          createdAt
          canonicalName
          knownAliases
          orgPid
          orgName
          buildingsOwned
          buildingsLeased
          relationshipId
          relationshipIsPrimary
        }
      }
    }
  }
}
    `)

export const useAdminCompaniesTableRowsQuery = <
  TData = Types.AdminCompaniesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompaniesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompaniesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompaniesTableRowsQuery, TError, TData>({
    queryKey: ["AdminCompaniesTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminCompaniesTableRowsQuery,
      Types.AdminCompaniesTableRowsQueryVariables
    >(AdminCompaniesTableRowsDocument, variables),
    ...options,
  })
}

useAdminCompaniesTableRowsQuery.getKey = (
  variables: Types.AdminCompaniesTableRowsQueryVariables,
) => ["AdminCompaniesTableRows", variables]

useAdminCompaniesTableRowsQuery.rootKey = "AdminCompaniesTableRows"

export const useSuspenseAdminCompaniesTableRowsQuery = <
  TData = Types.AdminCompaniesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompaniesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompaniesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompaniesTableRowsQuery, TError, TData>({
    queryKey: ["AdminCompaniesTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminCompaniesTableRowsQuery,
      Types.AdminCompaniesTableRowsQueryVariables
    >(AdminCompaniesTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompaniesTableRowsQuery.getKey = (
  variables: Types.AdminCompaniesTableRowsQueryVariables,
) => ["AdminCompaniesTableRows", variables]

useSuspenseAdminCompaniesTableRowsQuery.rootKey = "AdminCompaniesTableRows"

export const useInfiniteAdminCompaniesTableRowsQuery = <
  TData = InfiniteData<Types.AdminCompaniesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompaniesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompaniesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompaniesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompaniesTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompaniesTableRowsQuery,
            Types.AdminCompaniesTableRowsQueryVariables
          >(AdminCompaniesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompaniesTableRowsQuery.getKey = (
  variables: Types.AdminCompaniesTableRowsQueryVariables,
) => ["AdminCompaniesTableRows.infinite", variables]

useInfiniteAdminCompaniesTableRowsQuery.rootKey = "AdminCompaniesTableRows.infinite"

export const useSuspenseInfiniteAdminCompaniesTableRowsQuery = <
  TData = InfiniteData<Types.AdminCompaniesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompaniesTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminCompaniesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompaniesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCompaniesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompaniesTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompaniesTableRowsQuery,
            Types.AdminCompaniesTableRowsQueryVariables
          >(AdminCompaniesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompaniesTableRowsQuery.getKey = (
  variables: Types.AdminCompaniesTableRowsQueryVariables,
) => ["AdminCompaniesTableRows.infinite", variables]

useSuspenseInfiniteAdminCompaniesTableRowsQuery.rootKey =
  "AdminCompaniesTableRows.infinite"

useAdminCompaniesTableRowsQuery.fetcher = (
  variables: Types.AdminCompaniesTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompaniesTableRowsQuery,
    Types.AdminCompaniesTableRowsQueryVariables
  >(AdminCompaniesTableRowsDocument, variables, options)

export const AdminCompanyDetailDocument = new TypedDocumentString(`
    query AdminCompanyDetail($pid: String!) {
  admin {
    companies {
      one(pid: $pid) {
        pid
        canonicalName
        description
        reonomyOwnerId
        url
        hubspotId
        mergeHistory {
          ...CompanyMergeHistoryRecord
        }
        org {
          pid
          name
        }
        aliases {
          id
          name
          reonomyOwnerId
        }
      }
    }
  }
}
    fragment CompanyMergeHistoryRecord on CompanyMergeRecord {
  id
  absorbedName
  mergedAt
  source
  actor
  runId
  movedRows {
    table
    count
  }
  filledFields
  displayNameBefore
  displayNameAfter
  reversedAt
  reversalKind
  reversalReason
  reverseFirstId
}`)

export const useAdminCompanyDetailQuery = <
  TData = Types.AdminCompanyDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminCompanyDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyDetailQuery, TError, TData>({
    queryKey: ["AdminCompanyDetail", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDetailQuery,
      Types.AdminCompanyDetailQueryVariables
    >(AdminCompanyDetailDocument, variables),
    ...options,
  })
}

useAdminCompanyDetailQuery.getKey = (
  variables: Types.AdminCompanyDetailQueryVariables,
) => ["AdminCompanyDetail", variables]

useAdminCompanyDetailQuery.rootKey = "AdminCompanyDetail"

export const useSuspenseAdminCompanyDetailQuery = <
  TData = Types.AdminCompanyDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompanyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompanyDetailQuery, TError, TData>({
    queryKey: ["AdminCompanyDetail", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDetailQuery,
      Types.AdminCompanyDetailQueryVariables
    >(AdminCompanyDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompanyDetailQuery.getKey = (
  variables: Types.AdminCompanyDetailQueryVariables,
) => ["AdminCompanyDetail", variables]

useSuspenseAdminCompanyDetailQuery.rootKey = "AdminCompanyDetail"

export const useInfiniteAdminCompanyDetailQuery = <
  TData = InfiniteData<Types.AdminCompanyDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompanyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompanyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompanyDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDetailQuery,
            Types.AdminCompanyDetailQueryVariables
          >(AdminCompanyDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyDetailQuery.getKey = (
  variables: Types.AdminCompanyDetailQueryVariables,
) => ["AdminCompanyDetail.infinite", variables]

useInfiniteAdminCompanyDetailQuery.rootKey = "AdminCompanyDetail.infinite"

export const useSuspenseInfiniteAdminCompanyDetailQuery = <
  TData = InfiniteData<Types.AdminCompanyDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminCompanyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCompanyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompanyDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDetailQuery,
            Types.AdminCompanyDetailQueryVariables
          >(AdminCompanyDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyDetailQuery.getKey = (
  variables: Types.AdminCompanyDetailQueryVariables,
) => ["AdminCompanyDetail.infinite", variables]

useSuspenseInfiniteAdminCompanyDetailQuery.rootKey = "AdminCompanyDetail.infinite"

useAdminCompanyDetailQuery.fetcher = (
  variables: Types.AdminCompanyDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminCompanyDetailQuery, Types.AdminCompanyDetailQueryVariables>(
    AdminCompanyDetailDocument,
    variables,
    options,
  )

export const AdminCompanyAiAnalysisDocument = new TypedDocumentString(`
    query AdminCompanyAIAnalysis($pid: String!, $dimension: Dimension) {
  admin {
    companies {
      one(pid: $pid) {
        aiAnalysisResults(dimension: $dimension) {
          id
          dateCreated
          analysisKind
          modelName
          schemaVersion
          extractionJson
        }
      }
    }
  }
}
    `)

export const useAdminCompanyAiAnalysisQuery = <
  TData = Types.AdminCompanyAiAnalysisQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyAiAnalysisQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompanyAiAnalysisQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyAiAnalysisQuery, TError, TData>({
    queryKey: ["AdminCompanyAIAnalysis", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyAiAnalysisQuery,
      Types.AdminCompanyAiAnalysisQueryVariables
    >(AdminCompanyAiAnalysisDocument, variables),
    ...options,
  })
}

useAdminCompanyAiAnalysisQuery.getKey = (
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
) => ["AdminCompanyAIAnalysis", variables]

useAdminCompanyAiAnalysisQuery.rootKey = "AdminCompanyAIAnalysis"

export const useSuspenseAdminCompanyAiAnalysisQuery = <
  TData = Types.AdminCompanyAiAnalysisQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompanyAiAnalysisQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyAiAnalysisQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompanyAiAnalysisQuery, TError, TData>({
    queryKey: ["AdminCompanyAIAnalysis", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyAiAnalysisQuery,
      Types.AdminCompanyAiAnalysisQueryVariables
    >(AdminCompanyAiAnalysisDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompanyAiAnalysisQuery.getKey = (
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
) => ["AdminCompanyAIAnalysis", variables]

useSuspenseAdminCompanyAiAnalysisQuery.rootKey = "AdminCompanyAIAnalysis"

export const useInfiniteAdminCompanyAiAnalysisQuery = <
  TData = InfiniteData<Types.AdminCompanyAiAnalysisQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompanyAiAnalysisQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyAiAnalysisQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompanyAiAnalysisQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompanyAIAnalysis.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyAiAnalysisQuery,
            Types.AdminCompanyAiAnalysisQueryVariables
          >(AdminCompanyAiAnalysisDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyAiAnalysisQuery.getKey = (
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
) => ["AdminCompanyAIAnalysis.infinite", variables]

useInfiniteAdminCompanyAiAnalysisQuery.rootKey = "AdminCompanyAIAnalysis.infinite"

export const useSuspenseInfiniteAdminCompanyAiAnalysisQuery = <
  TData = InfiniteData<Types.AdminCompanyAiAnalysisQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminCompanyAiAnalysisQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyAiAnalysisQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCompanyAiAnalysisQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompanyAIAnalysis.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyAiAnalysisQuery,
            Types.AdminCompanyAiAnalysisQueryVariables
          >(AdminCompanyAiAnalysisDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyAiAnalysisQuery.getKey = (
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
) => ["AdminCompanyAIAnalysis.infinite", variables]

useSuspenseInfiniteAdminCompanyAiAnalysisQuery.rootKey =
  "AdminCompanyAIAnalysis.infinite"

useAdminCompanyAiAnalysisQuery.fetcher = (
  variables: Types.AdminCompanyAiAnalysisQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompanyAiAnalysisQuery,
    Types.AdminCompanyAiAnalysisQueryVariables
  >(AdminCompanyAiAnalysisDocument, variables, options)

export const AdminCompanyLatestAnalysisRequestDocument = new TypedDocumentString(`
    query AdminCompanyLatestAnalysisRequest($pid: String!, $dimension: Dimension!) {
  admin {
    companies {
      one(pid: $pid) {
        latestAnalysisRequest(dimension: $dimension) {
          id
          createdAt
          analysisKind
          status
          note
          errorClass
          errorMessage
          totalRuntime
        }
      }
    }
  }
}
    `)

export const useAdminCompanyLatestAnalysisRequestQuery = <
  TData = Types.AdminCompanyLatestAnalysisRequestQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyLatestAnalysisRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyLatestAnalysisRequestQuery, TError, TData>({
    queryKey: ["AdminCompanyLatestAnalysisRequest", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      Types.AdminCompanyLatestAnalysisRequestQueryVariables
    >(AdminCompanyLatestAnalysisRequestDocument, variables),
    ...options,
  })
}

useAdminCompanyLatestAnalysisRequestQuery.getKey = (
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
) => ["AdminCompanyLatestAnalysisRequest", variables]

useAdminCompanyLatestAnalysisRequestQuery.rootKey = "AdminCompanyLatestAnalysisRequest"

export const useSuspenseAdminCompanyLatestAnalysisRequestQuery = <
  TData = Types.AdminCompanyLatestAnalysisRequestQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompanyLatestAnalysisRequestQuery, TError, TData>({
    queryKey: ["AdminCompanyLatestAnalysisRequest", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      Types.AdminCompanyLatestAnalysisRequestQueryVariables
    >(AdminCompanyLatestAnalysisRequestDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompanyLatestAnalysisRequestQuery.getKey = (
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
) => ["AdminCompanyLatestAnalysisRequest", variables]

useSuspenseAdminCompanyLatestAnalysisRequestQuery.rootKey =
  "AdminCompanyLatestAnalysisRequest"

export const useInfiniteAdminCompanyLatestAnalysisRequestQuery = <
  TData = InfiniteData<Types.AdminCompanyLatestAnalysisRequestQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompanyLatestAnalysisRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminCompanyLatestAnalysisRequest.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyLatestAnalysisRequestQuery,
            Types.AdminCompanyLatestAnalysisRequestQueryVariables
          >(AdminCompanyLatestAnalysisRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyLatestAnalysisRequestQuery.getKey = (
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
) => ["AdminCompanyLatestAnalysisRequest.infinite", variables]

useInfiniteAdminCompanyLatestAnalysisRequestQuery.rootKey =
  "AdminCompanyLatestAnalysisRequest.infinite"

export const useSuspenseInfiniteAdminCompanyLatestAnalysisRequestQuery = <
  TData = InfiniteData<Types.AdminCompanyLatestAnalysisRequestQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyLatestAnalysisRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminCompanyLatestAnalysisRequestQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminCompanyLatestAnalysisRequest.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyLatestAnalysisRequestQuery,
            Types.AdminCompanyLatestAnalysisRequestQueryVariables
          >(AdminCompanyLatestAnalysisRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyLatestAnalysisRequestQuery.getKey = (
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
) => ["AdminCompanyLatestAnalysisRequest.infinite", variables]

useSuspenseInfiniteAdminCompanyLatestAnalysisRequestQuery.rootKey =
  "AdminCompanyLatestAnalysisRequest.infinite"

useAdminCompanyLatestAnalysisRequestQuery.fetcher = (
  variables: Types.AdminCompanyLatestAnalysisRequestQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompanyLatestAnalysisRequestQuery,
    Types.AdminCompanyLatestAnalysisRequestQueryVariables
  >(AdminCompanyLatestAnalysisRequestDocument, variables, options)

export const AdminBidsTableViewDocument = new TypedDocumentString(`
    query AdminBidsTableView($filterBy: AdminBidsTableFilter, $sortBy: [AdminBidsTableSort!]) {
  admin {
    allBids {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          buildingOwnerNames
          financierNames
        }
      }
    }
  }
}
    `)

export const useAdminBidsTableViewQuery = <
  TData = Types.AdminBidsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBidsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBidsTableView"]
        : ["AdminBidsTableView", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableViewQuery,
      Types.AdminBidsTableViewQueryVariables
    >(AdminBidsTableViewDocument, variables),
    ...options,
  })
}

useAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminBidsTableView"] : ["AdminBidsTableView", variables]

useAdminBidsTableViewQuery.rootKey = "AdminBidsTableView"

export const useSuspenseAdminBidsTableViewQuery = <
  TData = Types.AdminBidsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBidsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBidsTableView"]
        : ["AdminBidsTableView", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableViewQuery,
      Types.AdminBidsTableViewQueryVariables
    >(AdminBidsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminBidsTableView"] : ["AdminBidsTableView", variables]

useSuspenseAdminBidsTableViewQuery.rootKey = "AdminBidsTableView"

export const useInfiniteAdminBidsTableViewQuery = <
  TData = InfiniteData<Types.AdminBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBidsTableView.infinite"]
            : ["AdminBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableViewQuery,
            Types.AdminBidsTableViewQueryVariables
          >(AdminBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableView.infinite"]
    : ["AdminBidsTableView.infinite", variables]

useInfiniteAdminBidsTableViewQuery.rootKey = "AdminBidsTableView.infinite"

export const useSuspenseInfiniteAdminBidsTableViewQuery = <
  TData = InfiniteData<Types.AdminBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBidsTableView.infinite"]
            : ["AdminBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableViewQuery,
            Types.AdminBidsTableViewQueryVariables
          >(AdminBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableView.infinite"]
    : ["AdminBidsTableView.infinite", variables]

useSuspenseInfiniteAdminBidsTableViewQuery.rootKey = "AdminBidsTableView.infinite"

useAdminBidsTableViewQuery.fetcher = (
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminBidsTableViewQuery, Types.AdminBidsTableViewQueryVariables>(
    AdminBidsTableViewDocument,
    variables,
    options,
  )

export const AdminBidsTableRowsDocument = new TypedDocumentString(`
    query AdminBidsTableRows($keys: [Int!]!) {
  admin {
    allBids {
      rows(keys: $keys) {
        id
        bidPackagePid
        bundlePid
        bidPackageName
        name
        createdAt
        isAwarded
        isCustom
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        estimatedCod
        estimatedNtp
        csEstimatedAwardDate
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        buildingOwnerPid
        buildingOwnerName
        financierPid
        financierName
        sitePid
        siteName
        streetAddress
        city
        state
        zipCode
        utilityId
        utilityName
        utilityShortName
        termYears
        roofRentY1Usd
        roofRentEscalator
        buildingOwnerExtraPaymentsUsd
        extraPaymentsNotes
        buildingOwnerNoiLifetimeUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostUtilityMinUsd
        interconnectionCostNonUtilityUsd
        interconnectionCostUsdPerWatt
        interconnectionNotes
        itcPercentageAssumption
        recOwner
        incentivesNotes
        otherNotes
        finmodelId
      }
    }
  }
}
    `)

export const useAdminBidsTableRowsQuery = <
  TData = Types.AdminBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBidsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBidsTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableRowsQuery,
      Types.AdminBidsTableRowsQueryVariables
    >(AdminBidsTableRowsDocument, variables),
    ...options,
  })
}

useAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows", variables]

useAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows"

export const useSuspenseAdminBidsTableRowsQuery = <
  TData = Types.AdminBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBidsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBidsTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableRowsQuery,
      Types.AdminBidsTableRowsQueryVariables
    >(AdminBidsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows", variables]

useSuspenseAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows"

export const useInfiniteAdminBidsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableRowsQuery,
            Types.AdminBidsTableRowsQueryVariables
          >(AdminBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows.infinite", variables]

useInfiniteAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows.infinite"

export const useSuspenseInfiniteAdminBidsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableRowsQuery,
            Types.AdminBidsTableRowsQueryVariables
          >(AdminBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows.infinite", variables]

useSuspenseInfiniteAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows.infinite"

useAdminBidsTableRowsQuery.fetcher = (
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminBidsTableRowsQuery, Types.AdminBidsTableRowsQueryVariables>(
    AdminBidsTableRowsDocument,
    variables,
    options,
  )

export const AdminPortfolioListDocument = new TypedDocumentString(`
    query AdminPortfolioList {
  admin {
    portfolios {
      pid
      title
      numSites
      org {
        pid
        name
        logomarkUrl
      }
    }
  }
}
    `)

export const useAdminPortfolioListQuery = <
  TData = Types.AdminPortfolioListQuery,
  TError = unknown,
>(
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminPortfolioListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminPortfolioList"]
        : ["AdminPortfolioList", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioListQuery,
      Types.AdminPortfolioListQueryVariables
    >(AdminPortfolioListDocument, variables),
    ...options,
  })
}

useAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined ? ["AdminPortfolioList"] : ["AdminPortfolioList", variables]

useAdminPortfolioListQuery.rootKey = "AdminPortfolioList"

export const useSuspenseAdminPortfolioListQuery = <
  TData = Types.AdminPortfolioListQuery,
  TError = unknown,
>(
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminPortfolioListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminPortfolioList"]
        : ["AdminPortfolioList", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioListQuery,
      Types.AdminPortfolioListQueryVariables
    >(AdminPortfolioListDocument, variables),
    ...options,
  })
}

useSuspenseAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined ? ["AdminPortfolioList"] : ["AdminPortfolioList", variables]

useSuspenseAdminPortfolioListQuery.rootKey = "AdminPortfolioList"

export const useInfiniteAdminPortfolioListQuery = <
  TData = InfiniteData<Types.AdminPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminPortfolioList.infinite"]
            : ["AdminPortfolioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioListQuery,
            Types.AdminPortfolioListQueryVariables
          >(AdminPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioList.infinite"]
    : ["AdminPortfolioList.infinite", variables]

useInfiniteAdminPortfolioListQuery.rootKey = "AdminPortfolioList.infinite"

export const useSuspenseInfiniteAdminPortfolioListQuery = <
  TData = InfiniteData<Types.AdminPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminPortfolioList.infinite"]
            : ["AdminPortfolioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioListQuery,
            Types.AdminPortfolioListQueryVariables
          >(AdminPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioList.infinite"]
    : ["AdminPortfolioList.infinite", variables]

useSuspenseInfiniteAdminPortfolioListQuery.rootKey = "AdminPortfolioList.infinite"

useAdminPortfolioListQuery.fetcher = (
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminPortfolioListQuery, Types.AdminPortfolioListQueryVariables>(
    AdminPortfolioListDocument,
    variables,
    options,
  )

export const AdminOrgPortfolioListDocument = new TypedDocumentString(`
    query AdminOrgPortfolioList($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      portfolioRows {
        pid
        title
        numSites
      }
    }
  }
}
    `)

export const useAdminOrgPortfolioListQuery = <
  TData = Types.AdminOrgPortfolioListQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgPortfolioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminOrgPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgPortfolioListQuery, TError, TData>({
    queryKey: ["AdminOrgPortfolioList", variables],
    queryFn: publicFetcher<
      Types.AdminOrgPortfolioListQuery,
      Types.AdminOrgPortfolioListQueryVariables
    >(AdminOrgPortfolioListDocument, variables),
    ...options,
  })
}

useAdminOrgPortfolioListQuery.getKey = (
  variables: Types.AdminOrgPortfolioListQueryVariables,
) => ["AdminOrgPortfolioList", variables]

useAdminOrgPortfolioListQuery.rootKey = "AdminOrgPortfolioList"

export const useSuspenseAdminOrgPortfolioListQuery = <
  TData = Types.AdminOrgPortfolioListQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgPortfolioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgPortfolioListQuery, TError, TData>({
    queryKey: ["AdminOrgPortfolioList", variables],
    queryFn: publicFetcher<
      Types.AdminOrgPortfolioListQuery,
      Types.AdminOrgPortfolioListQueryVariables
    >(AdminOrgPortfolioListDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgPortfolioListQuery.getKey = (
  variables: Types.AdminOrgPortfolioListQueryVariables,
) => ["AdminOrgPortfolioList", variables]

useSuspenseAdminOrgPortfolioListQuery.rootKey = "AdminOrgPortfolioList"

export const useInfiniteAdminOrgPortfolioListQuery = <
  TData = InfiniteData<Types.AdminOrgPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgPortfolioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgPortfolioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminOrgPortfolioListQuery,
            Types.AdminOrgPortfolioListQueryVariables
          >(AdminOrgPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgPortfolioListQuery.getKey = (
  variables: Types.AdminOrgPortfolioListQueryVariables,
) => ["AdminOrgPortfolioList.infinite", variables]

useInfiniteAdminOrgPortfolioListQuery.rootKey = "AdminOrgPortfolioList.infinite"

export const useSuspenseInfiniteAdminOrgPortfolioListQuery = <
  TData = InfiniteData<Types.AdminOrgPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgPortfolioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgPortfolioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminOrgPortfolioListQuery,
            Types.AdminOrgPortfolioListQueryVariables
          >(AdminOrgPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgPortfolioListQuery.getKey = (
  variables: Types.AdminOrgPortfolioListQueryVariables,
) => ["AdminOrgPortfolioList.infinite", variables]

useSuspenseInfiniteAdminOrgPortfolioListQuery.rootKey = "AdminOrgPortfolioList.infinite"

useAdminOrgPortfolioListQuery.fetcher = (
  variables: Types.AdminOrgPortfolioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminOrgPortfolioListQuery,
    Types.AdminOrgPortfolioListQueryVariables
  >(AdminOrgPortfolioListDocument, variables, options)

export const AdminOrgBySlugDocument = new TypedDocumentString(`
    query AdminOrgBySlug($slug: String, $pid: String) {
  admin {
    orgBySlug(slug: $slug, pid: $pid) {
      pid
      slug
    }
  }
}
    `)

export const useAdminOrgBySlugQuery = <
  TData = Types.AdminOrgBySlugQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables],
    queryFn: publicFetcher<
      Types.AdminOrgBySlugQuery,
      Types.AdminOrgBySlugQueryVariables
    >(AdminOrgBySlugDocument, variables),
    ...options,
  })
}

useAdminOrgBySlugQuery.getKey = (variables?: Types.AdminOrgBySlugQueryVariables) =>
  variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables]

useAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug"

export const useSuspenseAdminOrgBySlugQuery = <
  TData = Types.AdminOrgBySlugQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables],
    queryFn: publicFetcher<
      Types.AdminOrgBySlugQuery,
      Types.AdminOrgBySlugQueryVariables
    >(AdminOrgBySlugDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) => (variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables])

useSuspenseAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug"

export const useInfiniteAdminOrgBySlugQuery = <
  TData = InfiniteData<Types.AdminOrgBySlugQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgBySlug.infinite"]
            : ["AdminOrgBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
            AdminOrgBySlugDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlug.infinite"]
    : ["AdminOrgBySlug.infinite", variables]

useInfiniteAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug.infinite"

export const useSuspenseInfiniteAdminOrgBySlugQuery = <
  TData = InfiniteData<Types.AdminOrgBySlugQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgBySlug.infinite"]
            : ["AdminOrgBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
            AdminOrgBySlugDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlug.infinite"]
    : ["AdminOrgBySlug.infinite", variables]

useSuspenseInfiniteAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug.infinite"

useAdminOrgBySlugQuery.fetcher = (
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
    AdminOrgBySlugDocument,
    variables,
    options,
  )

export const OrgsAndPortfoliosDocument = new TypedDocumentString(`
    query OrgsAndPortfolios {
  admin {
    orgs {
      pid
      name
    }
    portfolios {
      pid
      title
      numSites
      org {
        pid
      }
    }
  }
}
    `)

export const useOrgsAndPortfoliosQuery = <
  TData = Types.OrgsAndPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsAndPortfoliosQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsAndPortfolios"]
        : ["OrgsAndPortfolios", variables],
    queryFn: publicFetcher<
      Types.OrgsAndPortfoliosQuery,
      Types.OrgsAndPortfoliosQueryVariables
    >(OrgsAndPortfoliosDocument, variables),
    ...options,
  })
}

useOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined ? ["OrgsAndPortfolios"] : ["OrgsAndPortfolios", variables]

useOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios"

export const useSuspenseOrgsAndPortfoliosQuery = <
  TData = Types.OrgsAndPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsAndPortfoliosQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsAndPortfolios"]
        : ["OrgsAndPortfolios", variables],
    queryFn: publicFetcher<
      Types.OrgsAndPortfoliosQuery,
      Types.OrgsAndPortfoliosQueryVariables
    >(OrgsAndPortfoliosDocument, variables),
    ...options,
  })
}

useSuspenseOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined ? ["OrgsAndPortfolios"] : ["OrgsAndPortfolios", variables]

useSuspenseOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios"

export const useInfiniteOrgsAndPortfoliosQuery = <
  TData = InfiniteData<Types.OrgsAndPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.OrgsAndPortfoliosQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsAndPortfoliosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsAndPortfolios.infinite"]
            : ["OrgsAndPortfolios.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgsAndPortfoliosQuery,
            Types.OrgsAndPortfoliosQueryVariables
          >(OrgsAndPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfolios.infinite"]
    : ["OrgsAndPortfolios.infinite", variables]

useInfiniteOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios.infinite"

export const useSuspenseInfiniteOrgsAndPortfoliosQuery = <
  TData = InfiniteData<Types.OrgsAndPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.OrgsAndPortfoliosQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsAndPortfoliosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsAndPortfolios.infinite"]
            : ["OrgsAndPortfolios.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgsAndPortfoliosQuery,
            Types.OrgsAndPortfoliosQueryVariables
          >(OrgsAndPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfolios.infinite"]
    : ["OrgsAndPortfolios.infinite", variables]

useSuspenseInfiniteOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios.infinite"

useOrgsAndPortfoliosQuery.fetcher = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgsAndPortfoliosQuery, Types.OrgsAndPortfoliosQueryVariables>(
    OrgsAndPortfoliosDocument,
    variables,
    options,
  )

export const GetEnergyProductionDocument = new TypedDocumentString(`
    query GetEnergyProduction {
  admin {
    energy {
      sites {
        name
        daysActive
        kwhLifetime
      }
      kwhPerDay {
        day
        kwh
      }
      currentTime
      kwhAllTime
      kwhToday
      projectedNextInterval
    }
  }
}
    `)

export const useGetEnergyProductionQuery = <
  TData = Types.GetEnergyProductionQuery,
  TError = unknown,
>(
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetEnergyProductionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetEnergyProduction"]
        : ["GetEnergyProduction", variables],
    queryFn: publicFetcher<
      Types.GetEnergyProductionQuery,
      Types.GetEnergyProductionQueryVariables
    >(GetEnergyProductionDocument, variables),
    ...options,
  })
}

useGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined ? ["GetEnergyProduction"] : ["GetEnergyProduction", variables]

useGetEnergyProductionQuery.rootKey = "GetEnergyProduction"

export const useSuspenseGetEnergyProductionQuery = <
  TData = Types.GetEnergyProductionQuery,
  TError = unknown,
>(
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetEnergyProductionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetEnergyProduction"]
        : ["GetEnergyProduction", variables],
    queryFn: publicFetcher<
      Types.GetEnergyProductionQuery,
      Types.GetEnergyProductionQueryVariables
    >(GetEnergyProductionDocument, variables),
    ...options,
  })
}

useSuspenseGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined ? ["GetEnergyProduction"] : ["GetEnergyProduction", variables]

useSuspenseGetEnergyProductionQuery.rootKey = "GetEnergyProduction"

export const useInfiniteGetEnergyProductionQuery = <
  TData = InfiniteData<Types.GetEnergyProductionQuery>,
  TError = unknown,
>(
  variables: Types.GetEnergyProductionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetEnergyProductionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetEnergyProduction.infinite"]
            : ["GetEnergyProduction.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetEnergyProductionQuery,
            Types.GetEnergyProductionQueryVariables
          >(GetEnergyProductionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProduction.infinite"]
    : ["GetEnergyProduction.infinite", variables]

useInfiniteGetEnergyProductionQuery.rootKey = "GetEnergyProduction.infinite"

export const useSuspenseInfiniteGetEnergyProductionQuery = <
  TData = InfiniteData<Types.GetEnergyProductionQuery>,
  TError = unknown,
>(
  variables: Types.GetEnergyProductionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetEnergyProductionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetEnergyProduction.infinite"]
            : ["GetEnergyProduction.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetEnergyProductionQuery,
            Types.GetEnergyProductionQueryVariables
          >(GetEnergyProductionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProduction.infinite"]
    : ["GetEnergyProduction.infinite", variables]

useSuspenseInfiniteGetEnergyProductionQuery.rootKey = "GetEnergyProduction.infinite"

useGetEnergyProductionQuery.fetcher = (
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GetEnergyProductionQuery,
    Types.GetEnergyProductionQueryVariables
  >(GetEnergyProductionDocument, variables, options)

export const MultiPortfolioSitesDocument = new TypedDocumentString(`
    query MultiPortfolioSites($portfolioPids: [String!]!, $statuses: [SiteStatus!]!) {
  admin {
    sitesInPortfolios(portfolioPids: $portfolioPids, statuses: $statuses) {
      ...SitesTable
      portfolio {
        pid
        title
      }
    }
  }
}
    fragment SitesTable on Site {
  pid
  labels {
    pid
    name
    themeColor
  }
  propertyName
  fullAddress
  state
  buildingType
  sqft
  roofSqft
  occupancyType
  leaseType
  metering
  mortgageLender
  roofReplacedYr
  hiddenFromCustomers
  portfolio {
    pid
    title
    org {
      slug
    }
  }
  roofImage {
    id
  }
  roofMaxPvKw
  carportMaxPvKw
  primaryScenario {
    pid
    solarLocation
    includeStorage
  }
  currentStatus {
    pid
    status
  }
  createdAt
}`)

export const useMultiPortfolioSitesQuery = <
  TData = Types.MultiPortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MultiPortfolioSitesQuery, TError, TData>({
    queryKey: ["MultiPortfolioSites", variables],
    queryFn: publicFetcher<
      Types.MultiPortfolioSitesQuery,
      Types.MultiPortfolioSitesQueryVariables
    >(MultiPortfolioSitesDocument, variables),
    ...options,
  })
}

useMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites", variables]

useMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites"

export const useSuspenseMultiPortfolioSitesQuery = <
  TData = Types.MultiPortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiPortfolioSitesQuery, TError, TData>({
    queryKey: ["MultiPortfolioSites", variables],
    queryFn: publicFetcher<
      Types.MultiPortfolioSitesQuery,
      Types.MultiPortfolioSitesQueryVariables
    >(MultiPortfolioSitesDocument, variables),
    ...options,
  })
}

useSuspenseMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites", variables]

useSuspenseMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites"

export const useInfiniteMultiPortfolioSitesQuery = <
  TData = InfiniteData<Types.MultiPortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiPortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiPortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiPortfolioSitesQuery,
            Types.MultiPortfolioSitesQueryVariables
          >(MultiPortfolioSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites.infinite", variables]

useInfiniteMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites.infinite"

export const useSuspenseInfiniteMultiPortfolioSitesQuery = <
  TData = InfiniteData<Types.MultiPortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiPortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiPortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiPortfolioSitesQuery,
            Types.MultiPortfolioSitesQueryVariables
          >(MultiPortfolioSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites.infinite", variables]

useSuspenseInfiniteMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites.infinite"

useMultiPortfolioSitesQuery.fetcher = (
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MultiPortfolioSitesQuery,
    Types.MultiPortfolioSitesQueryVariables
  >(MultiPortfolioSitesDocument, variables, options)

export const PortfolioSitesDocument = new TypedDocumentString(`
    query PortfolioSites($sitePids: [String!]!) {
  admin {
    sites(sitePids: $sitePids) {
      ...SitesTable
    }
  }
}
    fragment SitesTable on Site {
  pid
  labels {
    pid
    name
    themeColor
  }
  propertyName
  fullAddress
  state
  buildingType
  sqft
  roofSqft
  occupancyType
  leaseType
  metering
  mortgageLender
  roofReplacedYr
  hiddenFromCustomers
  portfolio {
    pid
    title
    org {
      slug
    }
  }
  roofImage {
    id
  }
  roofMaxPvKw
  carportMaxPvKw
  primaryScenario {
    pid
    solarLocation
    includeStorage
  }
  currentStatus {
    pid
    status
  }
  createdAt
}`)

export const usePortfolioSitesQuery = <
  TData = Types.PortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioSitesQuery, TError, TData>({
    queryKey: ["PortfolioSites", variables],
    queryFn: publicFetcher<
      Types.PortfolioSitesQuery,
      Types.PortfolioSitesQueryVariables
    >(PortfolioSitesDocument, variables),
    ...options,
  })
}

usePortfolioSitesQuery.getKey = (variables: Types.PortfolioSitesQueryVariables) => [
  "PortfolioSites",
  variables,
]

usePortfolioSitesQuery.rootKey = "PortfolioSites"

export const useSuspensePortfolioSitesQuery = <
  TData = Types.PortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioSitesQuery, TError, TData>({
    queryKey: ["PortfolioSites", variables],
    queryFn: publicFetcher<
      Types.PortfolioSitesQuery,
      Types.PortfolioSitesQueryVariables
    >(PortfolioSitesDocument, variables),
    ...options,
  })
}

useSuspensePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSites", variables]

useSuspensePortfolioSitesQuery.rootKey = "PortfolioSites"

export const useInfinitePortfolioSitesQuery = <
  TData = InfiniteData<Types.PortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
            PortfolioSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSites.infinite", variables]

useInfinitePortfolioSitesQuery.rootKey = "PortfolioSites.infinite"

export const useSuspenseInfinitePortfolioSitesQuery = <
  TData = InfiniteData<Types.PortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
            PortfolioSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSites.infinite", variables]

useSuspenseInfinitePortfolioSitesQuery.rootKey = "PortfolioSites.infinite"

usePortfolioSitesQuery.fetcher = (
  variables: Types.PortfolioSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
    PortfolioSitesDocument,
    variables,
    options,
  )

export const SiteBuildingDataDocument = new TypedDocumentString(`
    query SiteBuildingData($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      displayName
      fullAddress
      portfolio {
        org {
          pid
          name
        }
      }
      customerId
      assetManager
      portfolioManager
      propertyName
      capRate
      buildingClass
      streetAddress
      city
      state
      zipCode
      county
      latitude
      longitude
      sqft
      yearBuilt
      numStories
      numParkingSpaces
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
      occupancyType
      leaseType
      metering
      meteringSetBy {
        ...UserRef
      }
      meteringSetDate
      buildingType
      costarPropertyType
      costarSecondaryType
      mappedBuildingType
      buildingClass
      leaseType
      occupancyType
      mortgageLender
      roofReplacedYr
      roofCondition
      cbsaId
      cbsaName
      timeZone
      roofSqft
      roofMaxPvKw
      carportMaxPvKw
      groundMaxPvKw
      computedRoofMaxPvKw
      computedCarportMaxPvKw
      computedGroundMaxPvKw
      footprint {
        ...FootprintGeometry
      }
      buildingData {
        id
        pid
        affordableType
        source
        sourceId
        streetAddress
        city
        state
        zipCode
        county
        latitude
        longitude
        sqft
        totalBuildings
        numStories
        numParkingSpaces
        costarPropertyType
        costarSecondaryType
        buildingType
        yearBuilt
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
        yearRenovated
        parentCompany
        buildingClass
        buildingOpex
        energyStar
        leedCertified
        lastSalePrice
        electricPowerRating
        zoning
        propertyName
        capRate
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
        numVacantUnitsStudio
        numVacantUnits1Bd
        numVacantUnits2Bd
        numVacantUnits3Bd
        numVacantUnits4Bd
        avgSqftStudio
        avgSqft1Bd
        avgSqft2Bd
        avgSqft3Bd
        avgSqft4Bd
        numElevators
        taxIdPrintFormat
        parcelNumberMin
        parcelNumberMax
        rentType
        reportedOwnerName
        reportedContactName
        reportedOwnerAddress
        reportedOwnerCity
        reportedOwnerState
        reportedOwnerZipCode
        reportedOwnerPhone
        ownerName
        ownerFirstName
        ownerLastName
        contactAddress
        contactCity
        contactState
        contactZipCode
        contactPhone
        buildingClass
        ...BuildingOwnershipDetail
      }
    }
  }
}
    fragment BuildingOwnershipDetail on BuildingData {
  pid
  owners {
    id
    isPrimary
    hasRoofRights
    company {
      pid
      canonicalName
    }
  }
  reonomyPropertyDetails {
    fetchedAt
    owners {
      name
      isIndividual
      company {
        pid
        canonicalName
      }
      contacts {
        firstName
        lastName
        jobTitle
        emailAddresses
        phoneNumbers
        fullAddresses
      }
    }
  }
  latestAffiliationResult {
    payload
    modelName
    createdAt
    promptHash
    isCurrent
  }
  researchInFlight
  sitesWithContext {
    portfolio {
      pid
      title
    }
    org {
      pid
      name
    }
  }
  annotations {
    id
    createdAt
    noteText
    user {
      ...UserRef
    }
    companies {
      type
      company {
        pid
        canonicalName
      }
    }
  }
  ownershipLog {
    occurredAt
    source
    summary
    detail
    user {
      ...UserRef
    }
    companies {
      pid
      canonicalName
    }
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}`)

export const useSiteBuildingDataQuery = <
  TData = Types.SiteBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBuildingDataQuery, TError, TData>({
    queryKey: ["SiteBuildingData", variables],
    queryFn: publicFetcher<
      Types.SiteBuildingDataQuery,
      Types.SiteBuildingDataQueryVariables
    >(SiteBuildingDataDocument, variables),
    ...options,
  })
}

useSiteBuildingDataQuery.getKey = (variables: Types.SiteBuildingDataQueryVariables) => [
  "SiteBuildingData",
  variables,
]

useSiteBuildingDataQuery.rootKey = "SiteBuildingData"

export const useSuspenseSiteBuildingDataQuery = <
  TData = Types.SiteBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBuildingDataQuery, TError, TData>({
    queryKey: ["SiteBuildingData", variables],
    queryFn: publicFetcher<
      Types.SiteBuildingDataQuery,
      Types.SiteBuildingDataQueryVariables
    >(SiteBuildingDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingData", variables]

useSuspenseSiteBuildingDataQuery.rootKey = "SiteBuildingData"

export const useInfiniteSiteBuildingDataQuery = <
  TData = InfiniteData<Types.SiteBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBuildingData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBuildingDataQuery,
            Types.SiteBuildingDataQueryVariables
          >(SiteBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingData.infinite", variables]

useInfiniteSiteBuildingDataQuery.rootKey = "SiteBuildingData.infinite"

export const useSuspenseInfiniteSiteBuildingDataQuery = <
  TData = InfiniteData<Types.SiteBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBuildingData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBuildingDataQuery,
            Types.SiteBuildingDataQueryVariables
          >(SiteBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingData.infinite", variables]

useSuspenseInfiniteSiteBuildingDataQuery.rootKey = "SiteBuildingData.infinite"

useSiteBuildingDataQuery.fetcher = (
  variables: Types.SiteBuildingDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBuildingDataQuery, Types.SiteBuildingDataQueryVariables>(
    SiteBuildingDataDocument,
    variables,
    options,
  )

export const AdminBuildingOwnershipDetailDocument = new TypedDocumentString(`
    query AdminBuildingOwnershipDetail($pid: String!) {
  admin {
    buildingData(pid: $pid) {
      ...BuildingOwnershipDetail
    }
  }
}
    fragment BuildingOwnershipDetail on BuildingData {
  pid
  owners {
    id
    isPrimary
    hasRoofRights
    company {
      pid
      canonicalName
    }
  }
  reonomyPropertyDetails {
    fetchedAt
    owners {
      name
      isIndividual
      company {
        pid
        canonicalName
      }
      contacts {
        firstName
        lastName
        jobTitle
        emailAddresses
        phoneNumbers
        fullAddresses
      }
    }
  }
  latestAffiliationResult {
    payload
    modelName
    createdAt
    promptHash
    isCurrent
  }
  researchInFlight
  sitesWithContext {
    portfolio {
      pid
      title
    }
    org {
      pid
      name
    }
  }
  annotations {
    id
    createdAt
    noteText
    user {
      ...UserRef
    }
    companies {
      type
      company {
        pid
        canonicalName
      }
    }
  }
  ownershipLog {
    occurredAt
    source
    summary
    detail
    user {
      ...UserRef
    }
    companies {
      pid
      canonicalName
    }
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAdminBuildingOwnershipDetailQuery = <
  TData = Types.AdminBuildingOwnershipDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBuildingOwnershipDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBuildingOwnershipDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBuildingOwnershipDetailQuery, TError, TData>({
    queryKey: ["AdminBuildingOwnershipDetail", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingOwnershipDetailQuery,
      Types.AdminBuildingOwnershipDetailQueryVariables
    >(AdminBuildingOwnershipDetailDocument, variables),
    ...options,
  })
}

useAdminBuildingOwnershipDetailQuery.getKey = (
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
) => ["AdminBuildingOwnershipDetail", variables]

useAdminBuildingOwnershipDetailQuery.rootKey = "AdminBuildingOwnershipDetail"

export const useSuspenseAdminBuildingOwnershipDetailQuery = <
  TData = Types.AdminBuildingOwnershipDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBuildingOwnershipDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBuildingOwnershipDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBuildingOwnershipDetailQuery, TError, TData>({
    queryKey: ["AdminBuildingOwnershipDetail", variables],
    queryFn: publicFetcher<
      Types.AdminBuildingOwnershipDetailQuery,
      Types.AdminBuildingOwnershipDetailQueryVariables
    >(AdminBuildingOwnershipDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminBuildingOwnershipDetailQuery.getKey = (
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
) => ["AdminBuildingOwnershipDetail", variables]

useSuspenseAdminBuildingOwnershipDetailQuery.rootKey = "AdminBuildingOwnershipDetail"

export const useInfiniteAdminBuildingOwnershipDetailQuery = <
  TData = InfiniteData<Types.AdminBuildingOwnershipDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBuildingOwnershipDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBuildingOwnershipDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBuildingOwnershipDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingOwnershipDetailQuery,
            Types.AdminBuildingOwnershipDetailQueryVariables
          >(AdminBuildingOwnershipDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBuildingOwnershipDetailQuery.getKey = (
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
) => ["AdminBuildingOwnershipDetail.infinite", variables]

useInfiniteAdminBuildingOwnershipDetailQuery.rootKey =
  "AdminBuildingOwnershipDetail.infinite"

export const useSuspenseInfiniteAdminBuildingOwnershipDetailQuery = <
  TData = InfiniteData<Types.AdminBuildingOwnershipDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBuildingOwnershipDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminBuildingOwnershipDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBuildingOwnershipDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBuildingOwnershipDetailQuery,
            Types.AdminBuildingOwnershipDetailQueryVariables
          >(AdminBuildingOwnershipDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBuildingOwnershipDetailQuery.getKey = (
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
) => ["AdminBuildingOwnershipDetail.infinite", variables]

useSuspenseInfiniteAdminBuildingOwnershipDetailQuery.rootKey =
  "AdminBuildingOwnershipDetail.infinite"

useAdminBuildingOwnershipDetailQuery.fetcher = (
  variables: Types.AdminBuildingOwnershipDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBuildingOwnershipDetailQuery,
    Types.AdminBuildingOwnershipDetailQueryVariables
  >(AdminBuildingOwnershipDetailDocument, variables, options)

export const SitePossibleTariffsDocument = new TypedDocumentString(`
    query SitePossibleTariffs($pid: String!, $loadCategory: LoadCategory, $preferred: Boolean! = true, $includeExcluded: Boolean! = false, $includeCustom: Boolean! = false) {
  admin {
    site(pid: $pid) {
      pid
      possibleTariffs(
        loadCategory: $loadCategory
        preferred: $preferred
        includeExcluded: $includeExcluded
        includeCustom: $includeCustom
      ) {
        id
        name
        code
        tariffType
        customerClass
        customerCount
        customerLikelihood
        masterTariffId
        selectionStatus
        hasNetMetering
        requiresPv
        requiresStorage
        minMonthlyKw
        maxMonthlyKw
        minMonthlyKwh
        maxMonthlyKwh
      }
    }
  }
}
    `)

export const useSitePossibleTariffsQuery = <
  TData = Types.SitePossibleTariffsQuery,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SitePossibleTariffsQuery, TError, TData>({
    queryKey: ["SitePossibleTariffs", variables],
    queryFn: publicFetcher<
      Types.SitePossibleTariffsQuery,
      Types.SitePossibleTariffsQueryVariables
    >(SitePossibleTariffsDocument, variables),
    ...options,
  })
}

useSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs", variables]

useSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs"

export const useSuspenseSitePossibleTariffsQuery = <
  TData = Types.SitePossibleTariffsQuery,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePossibleTariffsQuery, TError, TData>({
    queryKey: ["SitePossibleTariffs", variables],
    queryFn: publicFetcher<
      Types.SitePossibleTariffsQuery,
      Types.SitePossibleTariffsQueryVariables
    >(SitePossibleTariffsDocument, variables),
    ...options,
  })
}

useSuspenseSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs", variables]

useSuspenseSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs"

export const useInfiniteSitePossibleTariffsQuery = <
  TData = InfiniteData<Types.SitePossibleTariffsQuery>,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePossibleTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePossibleTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePossibleTariffsQuery,
            Types.SitePossibleTariffsQueryVariables
          >(SitePossibleTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs.infinite", variables]

useInfiniteSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs.infinite"

export const useSuspenseInfiniteSitePossibleTariffsQuery = <
  TData = InfiniteData<Types.SitePossibleTariffsQuery>,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePossibleTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePossibleTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePossibleTariffsQuery,
            Types.SitePossibleTariffsQueryVariables
          >(SitePossibleTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs.infinite", variables]

useSuspenseInfiniteSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs.infinite"

useSitePossibleTariffsQuery.fetcher = (
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SitePossibleTariffsQuery,
    Types.SitePossibleTariffsQueryVariables
  >(SitePossibleTariffsDocument, variables, options)

export const GetSiteAdminDocument = new TypedDocumentString(`
    query GetSiteAdmin($pid: String!) {
  admin {
    site(pid: $pid) {
      id
      pid
      fullAddress
      hiddenFromCustomers
      hideFakePanels
      batteryDurationHours
      batteryMaxMw
      batteryUtilityMaxMw
      portfolio {
        pid
        title
        org {
          pid
          slug
        }
      }
      buildingData {
        id
        pid
        reonomyPropertyLink
      }
      footprint {
        ...Footprint
      }
      buildingFootprintNeedsReview
      physicalCheck {
        existingSolar
        badRoof
      }
      currentPolicyChecks {
        productType
        isGood
        disqualReason
        violatedPolicy {
          pid
          name
        }
      }
      roofSqft
      roofMaxPvKw
      computedRoofMaxPvKw
      groundMaxPvKw
      carportMaxPvKw
      buildingFootprintSqft
      buildingFootprintCount
      roofObstructionsSqft
      roofUsableSqft
      roofImage {
        id
        name
      }
      attachments {
        ...AttachmentFragment
      }
      targetUtility {
        pid
      }
      ftmBessProspect {
        ...FtmBessProspect
      }
      duplicateSites {
        pid
        displayName
        portfolio {
          pid
          title
          org {
            pid
            slug
            name
          }
        }
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}
fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}
fragment FtmBessProspect on FTMBessProspect {
  triageStatus
  verdict
  updatedAt
  triageStatusEdit {
    at
    by
  }
  criteria {
    key
    label
    order
    status
    notes
    contextualValue
    statusEdit {
      at
      by
    }
    notesEdit {
      at
      by
    }
  }
}`)

export const useGetSiteAdminQuery = <TData = Types.GetSiteAdminQuery, TError = unknown>(
  variables: Types.GetSiteAdminQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetSiteAdminQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetSiteAdminQuery, TError, TData>({
    queryKey: ["GetSiteAdmin", variables],
    queryFn: publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
      GetSiteAdminDocument,
      variables,
    ),
    ...options,
  })
}

useGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdmin",
  variables,
]

useGetSiteAdminQuery.rootKey = "GetSiteAdmin"

export const useSuspenseGetSiteAdminQuery = <
  TData = Types.GetSiteAdminQuery,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetSiteAdminQuery, TError, TData>({
    queryKey: ["GetSiteAdmin", variables],
    queryFn: publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
      GetSiteAdminDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdmin",
  variables,
]

useSuspenseGetSiteAdminQuery.rootKey = "GetSiteAdmin"

export const useInfiniteGetSiteAdminQuery = <
  TData = InfiniteData<Types.GetSiteAdminQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetSiteAdminQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteAdmin.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
            GetSiteAdminDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdmin.infinite",
  variables,
]

useInfiniteGetSiteAdminQuery.rootKey = "GetSiteAdmin.infinite"

export const useSuspenseInfiniteGetSiteAdminQuery = <
  TData = InfiniteData<Types.GetSiteAdminQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetSiteAdminQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteAdmin.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
            GetSiteAdminDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetSiteAdminQuery.getKey = (
  variables: Types.GetSiteAdminQueryVariables,
) => ["GetSiteAdmin.infinite", variables]

useSuspenseInfiniteGetSiteAdminQuery.rootKey = "GetSiteAdmin.infinite"

useGetSiteAdminQuery.fetcher = (
  variables: Types.GetSiteAdminQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
    GetSiteAdminDocument,
    variables,
    options,
  )

export const UserListDocument = new TypedDocumentString(`
    query UserList {
  admin {
    users {
      ...UserListUser
    }
  }
}
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  jobFunctions
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  adminOrgRoles {
    org {
      pid
      name
      orgSlug
    }
    role
    grantsAllPortfolios
    orgIsDeveloper
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}`)

export const useUserListQuery = <TData = Types.UserListQuery, TError = unknown>(
  variables?: Types.UserListQueryVariables,
  options?: Omit<UseQueryOptions<Types.UserListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UserListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UserList"] : ["UserList", variables],
    queryFn: publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
      UserListDocument,
      variables,
    ),
    ...options,
  })
}

useUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList"] : ["UserList", variables]

useUserListQuery.rootKey = "UserList"

export const useSuspenseUserListQuery = <TData = Types.UserListQuery, TError = unknown>(
  variables?: Types.UserListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UserListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UserList"] : ["UserList", variables],
    queryFn: publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
      UserListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList"] : ["UserList", variables]

useSuspenseUserListQuery.rootKey = "UserList"

export const useInfiniteUserListQuery = <
  TData = InfiniteData<Types.UserListQuery>,
  TError = unknown,
>(
  variables: Types.UserListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UserListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UserList.infinite"]
            : ["UserList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
            UserListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList.infinite"] : ["UserList.infinite", variables]

useInfiniteUserListQuery.rootKey = "UserList.infinite"

export const useSuspenseInfiniteUserListQuery = <
  TData = InfiniteData<Types.UserListQuery>,
  TError = unknown,
>(
  variables: Types.UserListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UserListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UserListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UserList.infinite"]
            : ["UserList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
            UserListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList.infinite"] : ["UserList.infinite", variables]

useSuspenseInfiniteUserListQuery.rootKey = "UserList.infinite"

useUserListQuery.fetcher = (
  variables?: Types.UserListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
    UserListDocument,
    variables,
    options,
  )

export const PortfolioDetailDocument = new TypedDocumentString(`
    query PortfolioDetail($pid: String!) {
  admin {
    portfolio(pid: $pid) {
      pid
      title
      logoUrl
      minKw
      minKwSetBy
      minKwSetDate
      view {
        sitePids
      }
      org {
        pid
        name
        slug
      }
    }
  }
}
    `)

export const usePortfolioDetailQuery = <
  TData = Types.PortfolioDetailQuery,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioDetailQuery, TError, TData>({
    queryKey: ["PortfolioDetail", variables],
    queryFn: publicFetcher<
      Types.PortfolioDetailQuery,
      Types.PortfolioDetailQueryVariables
    >(PortfolioDetailDocument, variables),
    ...options,
  })
}

usePortfolioDetailQuery.getKey = (variables: Types.PortfolioDetailQueryVariables) => [
  "PortfolioDetail",
  variables,
]

usePortfolioDetailQuery.rootKey = "PortfolioDetail"

export const useSuspensePortfolioDetailQuery = <
  TData = Types.PortfolioDetailQuery,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioDetailQuery, TError, TData>({
    queryKey: ["PortfolioDetail", variables],
    queryFn: publicFetcher<
      Types.PortfolioDetailQuery,
      Types.PortfolioDetailQueryVariables
    >(PortfolioDetailDocument, variables),
    ...options,
  })
}

useSuspensePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetail", variables]

useSuspensePortfolioDetailQuery.rootKey = "PortfolioDetail"

export const useInfinitePortfolioDetailQuery = <
  TData = InfiniteData<Types.PortfolioDetailQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioDetailQuery,
            Types.PortfolioDetailQueryVariables
          >(PortfolioDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetail.infinite", variables]

useInfinitePortfolioDetailQuery.rootKey = "PortfolioDetail.infinite"

export const useSuspenseInfinitePortfolioDetailQuery = <
  TData = InfiniteData<Types.PortfolioDetailQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioDetailQuery,
            Types.PortfolioDetailQueryVariables
          >(PortfolioDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetail.infinite", variables]

useSuspenseInfinitePortfolioDetailQuery.rootKey = "PortfolioDetail.infinite"

usePortfolioDetailQuery.fetcher = (
  variables: Types.PortfolioDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PortfolioDetailQuery, Types.PortfolioDetailQueryVariables>(
    PortfolioDetailDocument,
    variables,
    options,
  )

export const GenabilityTerritoriesDocument = new TypedDocumentString(`
    query GenabilityTerritories {
  admin {
    tariffs {
      genabilityTerritories {
        id
        territoryId
        state
        lseId
        deregResidential
        deregCommercial
        itemTypes
        usageType
        dateCreated
        dateLastUpdated
        utility {
          id
          name
          shortName
        }
      }
    }
  }
}
    `)

export const useGenabilityTerritoriesQuery = <
  TData = Types.GenabilityTerritoriesQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityTerritoriesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GenabilityTerritoriesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GenabilityTerritoriesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GenabilityTerritoriesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityTerritories"]
        : ["GenabilityTerritories", variables],
    queryFn: publicFetcher<
      Types.GenabilityTerritoriesQuery,
      Types.GenabilityTerritoriesQueryVariables
    >(GenabilityTerritoriesDocument, variables),
    ...options,
  })
}

useGenabilityTerritoriesQuery.getKey = (
  variables?: Types.GenabilityTerritoriesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityTerritories"]
    : ["GenabilityTerritories", variables]

useGenabilityTerritoriesQuery.rootKey = "GenabilityTerritories"

export const useSuspenseGenabilityTerritoriesQuery = <
  TData = Types.GenabilityTerritoriesQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityTerritoriesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GenabilityTerritoriesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GenabilityTerritoriesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GenabilityTerritoriesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityTerritories"]
        : ["GenabilityTerritories", variables],
    queryFn: publicFetcher<
      Types.GenabilityTerritoriesQuery,
      Types.GenabilityTerritoriesQueryVariables
    >(GenabilityTerritoriesDocument, variables),
    ...options,
  })
}

useSuspenseGenabilityTerritoriesQuery.getKey = (
  variables?: Types.GenabilityTerritoriesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityTerritories"]
    : ["GenabilityTerritories", variables]

useSuspenseGenabilityTerritoriesQuery.rootKey = "GenabilityTerritories"

export const useInfiniteGenabilityTerritoriesQuery = <
  TData = InfiniteData<Types.GenabilityTerritoriesQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityTerritoriesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GenabilityTerritoriesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GenabilityTerritoriesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GenabilityTerritoriesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityTerritories.infinite"]
            : ["GenabilityTerritories.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityTerritoriesQuery,
            Types.GenabilityTerritoriesQueryVariables
          >(GenabilityTerritoriesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGenabilityTerritoriesQuery.getKey = (
  variables?: Types.GenabilityTerritoriesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityTerritories.infinite"]
    : ["GenabilityTerritories.infinite", variables]

useInfiniteGenabilityTerritoriesQuery.rootKey = "GenabilityTerritories.infinite"

export const useSuspenseInfiniteGenabilityTerritoriesQuery = <
  TData = InfiniteData<Types.GenabilityTerritoriesQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityTerritoriesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GenabilityTerritoriesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GenabilityTerritoriesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GenabilityTerritoriesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityTerritories.infinite"]
            : ["GenabilityTerritories.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityTerritoriesQuery,
            Types.GenabilityTerritoriesQueryVariables
          >(GenabilityTerritoriesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGenabilityTerritoriesQuery.getKey = (
  variables?: Types.GenabilityTerritoriesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityTerritories.infinite"]
    : ["GenabilityTerritories.infinite", variables]

useSuspenseInfiniteGenabilityTerritoriesQuery.rootKey = "GenabilityTerritories.infinite"

useGenabilityTerritoriesQuery.fetcher = (
  variables?: Types.GenabilityTerritoriesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenabilityTerritoriesQuery,
    Types.GenabilityTerritoriesQueryVariables
  >(GenabilityTerritoriesDocument, variables, options)

export const EiaGridEscalatorsDocument = new TypedDocumentString(`
    query EIAGridEscalators {
  admin {
    utilities {
      eiaGridEscalators {
        eiaId
        cagrBlended
        fromYear
        toYear
        createdAt
        updatedAt
        utilities {
          id
          name
          shortName
        }
      }
    }
  }
}
    `)

export const useEiaGridEscalatorsQuery = <
  TData = Types.EiaGridEscalatorsQuery,
  TError = unknown,
>(
  variables?: Types.EiaGridEscalatorsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.EiaGridEscalatorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.EiaGridEscalatorsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EiaGridEscalatorsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["EIAGridEscalators"]
        : ["EIAGridEscalators", variables],
    queryFn: publicFetcher<
      Types.EiaGridEscalatorsQuery,
      Types.EiaGridEscalatorsQueryVariables
    >(EiaGridEscalatorsDocument, variables),
    ...options,
  })
}

useEiaGridEscalatorsQuery.getKey = (
  variables?: Types.EiaGridEscalatorsQueryVariables,
) =>
  variables === undefined ? ["EIAGridEscalators"] : ["EIAGridEscalators", variables]

useEiaGridEscalatorsQuery.rootKey = "EIAGridEscalators"

export const useSuspenseEiaGridEscalatorsQuery = <
  TData = Types.EiaGridEscalatorsQuery,
  TError = unknown,
>(
  variables?: Types.EiaGridEscalatorsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EiaGridEscalatorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.EiaGridEscalatorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EiaGridEscalatorsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["EIAGridEscalators"]
        : ["EIAGridEscalators", variables],
    queryFn: publicFetcher<
      Types.EiaGridEscalatorsQuery,
      Types.EiaGridEscalatorsQueryVariables
    >(EiaGridEscalatorsDocument, variables),
    ...options,
  })
}

useSuspenseEiaGridEscalatorsQuery.getKey = (
  variables?: Types.EiaGridEscalatorsQueryVariables,
) =>
  variables === undefined ? ["EIAGridEscalators"] : ["EIAGridEscalators", variables]

useSuspenseEiaGridEscalatorsQuery.rootKey = "EIAGridEscalators"

export const useInfiniteEiaGridEscalatorsQuery = <
  TData = InfiniteData<Types.EiaGridEscalatorsQuery>,
  TError = unknown,
>(
  variables: Types.EiaGridEscalatorsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EiaGridEscalatorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.EiaGridEscalatorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EiaGridEscalatorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["EIAGridEscalators.infinite"]
            : ["EIAGridEscalators.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.EiaGridEscalatorsQuery,
            Types.EiaGridEscalatorsQueryVariables
          >(EiaGridEscalatorsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEiaGridEscalatorsQuery.getKey = (
  variables?: Types.EiaGridEscalatorsQueryVariables,
) =>
  variables === undefined
    ? ["EIAGridEscalators.infinite"]
    : ["EIAGridEscalators.infinite", variables]

useInfiniteEiaGridEscalatorsQuery.rootKey = "EIAGridEscalators.infinite"

export const useSuspenseInfiniteEiaGridEscalatorsQuery = <
  TData = InfiniteData<Types.EiaGridEscalatorsQuery>,
  TError = unknown,
>(
  variables: Types.EiaGridEscalatorsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EiaGridEscalatorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EiaGridEscalatorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EiaGridEscalatorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["EIAGridEscalators.infinite"]
            : ["EIAGridEscalators.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.EiaGridEscalatorsQuery,
            Types.EiaGridEscalatorsQueryVariables
          >(EiaGridEscalatorsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEiaGridEscalatorsQuery.getKey = (
  variables?: Types.EiaGridEscalatorsQueryVariables,
) =>
  variables === undefined
    ? ["EIAGridEscalators.infinite"]
    : ["EIAGridEscalators.infinite", variables]

useSuspenseInfiniteEiaGridEscalatorsQuery.rootKey = "EIAGridEscalators.infinite"

useEiaGridEscalatorsQuery.fetcher = (
  variables?: Types.EiaGridEscalatorsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EiaGridEscalatorsQuery, Types.EiaGridEscalatorsQueryVariables>(
    EiaGridEscalatorsDocument,
    variables,
    options,
  )

export const UtilityListDocument = new TypedDocumentString(`
    query UtilityList($zipCode: String) {
  admin {
    utilities {
      all(zipCode: $zipCode) {
        ...UtilityFragment
      }
    }
  }
}
    fragment UtilityFragment on Utility {
  id
  pid
  lseId
  name
  shortName
  numTariffs
  numCustomTariffs
  utilityApiName
  isPreferred
  notes
  states
  vnemSupported
  ftmBessProspectingSupported
  hasComplexSrecPricing
}`)

export const useUtilityListQuery = <TData = Types.UtilityListQuery, TError = unknown>(
  variables?: Types.UtilityListQueryVariables,
  options?: Omit<UseQueryOptions<Types.UtilityListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.UtilityListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UtilityList"] : ["UtilityList", variables],
    queryFn: publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
      UtilityListDocument,
      variables,
    ),
    ...options,
  })
}

useUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined ? ["UtilityList"] : ["UtilityList", variables]

useUtilityListQuery.rootKey = "UtilityList"

export const useSuspenseUtilityListQuery = <
  TData = Types.UtilityListQuery,
  TError = unknown,
>(
  variables?: Types.UtilityListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UtilityList"] : ["UtilityList", variables],
    queryFn: publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
      UtilityListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined ? ["UtilityList"] : ["UtilityList", variables]

useSuspenseUtilityListQuery.rootKey = "UtilityList"

export const useInfiniteUtilityListQuery = <
  TData = InfiniteData<Types.UtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UtilityListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityList.infinite"]
            : ["UtilityList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
            UtilityListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined
    ? ["UtilityList.infinite"]
    : ["UtilityList.infinite", variables]

useInfiniteUtilityListQuery.rootKey = "UtilityList.infinite"

export const useSuspenseInfiniteUtilityListQuery = <
  TData = InfiniteData<Types.UtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UtilityListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityList.infinite"]
            : ["UtilityList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
            UtilityListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityListQuery.getKey = (
  variables?: Types.UtilityListQueryVariables,
) =>
  variables === undefined
    ? ["UtilityList.infinite"]
    : ["UtilityList.infinite", variables]

useSuspenseInfiniteUtilityListQuery.rootKey = "UtilityList.infinite"

useUtilityListQuery.fetcher = (
  variables?: Types.UtilityListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
    UtilityListDocument,
    variables,
    options,
  )

export const UtilityDetailDocument = new TypedDocumentString(`
    query UtilityDetail($id: Int!) {
  admin {
    utilities {
      utility(id: $id) {
        ...UtilityFragment
        code
        eiaGridEsc {
          eiaId
          cagrBlended
          fromYear
          toYear
          updatedAt
        }
        csPrices {
          usdPerKwPerYear
          escalator
          termYears
          minPvKw
          maxPvKw
          state
          pvType
          finProductId
          finProductName
          financierOrgPid
          finProductStatus
        }
        complexSrecPricing {
          id
          state
          location
          sizeMinKw
          sizeMinInclusive
          sizeMaxKw
          sizeMaxInclusive
          discount
          prices {
            year
            usdPerMwh
          }
        }
      }
    }
  }
}
    fragment UtilityFragment on Utility {
  id
  pid
  lseId
  name
  shortName
  numTariffs
  numCustomTariffs
  utilityApiName
  isPreferred
  notes
  states
  vnemSupported
  ftmBessProspectingSupported
  hasComplexSrecPricing
}`)

export const useUtilityDetailQuery = <
  TData = Types.UtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilityDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityDetailQuery, TError, TData>({
    queryKey: ["UtilityDetail", variables],
    queryFn: publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
      UtilityDetailDocument,
      variables,
    ),
    ...options,
  })
}

useUtilityDetailQuery.getKey = (variables: Types.UtilityDetailQueryVariables) => [
  "UtilityDetail",
  variables,
]

useUtilityDetailQuery.rootKey = "UtilityDetail"

export const useSuspenseUtilityDetailQuery = <
  TData = Types.UtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityDetailQuery, TError, TData>({
    queryKey: ["UtilityDetail", variables],
    queryFn: publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
      UtilityDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetail", variables]

useSuspenseUtilityDetailQuery.rootKey = "UtilityDetail"

export const useInfiniteUtilityDetailQuery = <
  TData = InfiniteData<Types.UtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
            UtilityDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetail.infinite", variables]

useInfiniteUtilityDetailQuery.rootKey = "UtilityDetail.infinite"

export const useSuspenseInfiniteUtilityDetailQuery = <
  TData = InfiniteData<Types.UtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
            UtilityDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetail.infinite", variables]

useSuspenseInfiniteUtilityDetailQuery.rootKey = "UtilityDetail.infinite"

useUtilityDetailQuery.fetcher = (
  variables: Types.UtilityDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
    UtilityDetailDocument,
    variables,
    options,
  )

export const TariffListDocument = new TypedDocumentString(`
    query TariffList($id: Int!) {
  admin {
    utilities {
      utility(id: $id) {
        id
        shortName
        name
        tariffs {
          id
          masterTariffId
          code
          name
          tariffType
          customerClass
          selectionStatus
          requiresPv
          requiresStorage
          customerLikelihood
          hasNetMetering
          hasTouRates
          hasRates
          numSites
          minMonthlyKwh
          maxMonthlyKwh
          minMonthlyKw
          maxMonthlyKw
          isSiteSpecific
          states
          site {
            pid
            name
            streetAddress
            orgSlug
          }
        }
      }
    }
  }
}
    `)

export const useTariffListQuery = <TData = Types.TariffListQuery, TError = unknown>(
  variables: Types.TariffListQueryVariables,
  options?: Omit<UseQueryOptions<Types.TariffListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffListQuery, TError, TData>({
    queryKey: ["TariffList", variables],
    queryFn: publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
      TariffListDocument,
      variables,
    ),
    ...options,
  })
}

useTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffList",
  variables,
]

useTariffListQuery.rootKey = "TariffList"

export const useSuspenseTariffListQuery = <
  TData = Types.TariffListQuery,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffListQuery, TError, TData>({
    queryKey: ["TariffList", variables],
    queryFn: publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
      TariffListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffList",
  variables,
]

useSuspenseTariffListQuery.rootKey = "TariffList"

export const useInfiniteTariffListQuery = <
  TData = InfiniteData<Types.TariffListQuery>,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
            TariffListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffList.infinite",
  variables,
]

useInfiniteTariffListQuery.rootKey = "TariffList.infinite"

export const useSuspenseInfiniteTariffListQuery = <
  TData = InfiniteData<Types.TariffListQuery>,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
            TariffListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffListQuery.getKey = (
  variables: Types.TariffListQueryVariables,
) => ["TariffList.infinite", variables]

useSuspenseInfiniteTariffListQuery.rootKey = "TariffList.infinite"

useTariffListQuery.fetcher = (
  variables: Types.TariffListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
    TariffListDocument,
    variables,
    options,
  )

export const UtilitySiteCountsDocument = new TypedDocumentString(`
    query UtilitySiteCounts {
  admin {
    utilities {
      siteCounts {
        utilityId
        numSites
      }
    }
  }
}
    `)

export const useUtilitySiteCountsQuery = <
  TData = Types.UtilitySiteCountsQuery,
  TError = unknown,
>(
  variables?: Types.UtilitySiteCountsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilitySiteCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilitySiteCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilitySiteCountsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UtilitySiteCounts"]
        : ["UtilitySiteCounts", variables],
    queryFn: publicFetcher<
      Types.UtilitySiteCountsQuery,
      Types.UtilitySiteCountsQueryVariables
    >(UtilitySiteCountsDocument, variables),
    ...options,
  })
}

useUtilitySiteCountsQuery.getKey = (
  variables?: Types.UtilitySiteCountsQueryVariables,
) =>
  variables === undefined ? ["UtilitySiteCounts"] : ["UtilitySiteCounts", variables]

useUtilitySiteCountsQuery.rootKey = "UtilitySiteCounts"

export const useSuspenseUtilitySiteCountsQuery = <
  TData = Types.UtilitySiteCountsQuery,
  TError = unknown,
>(
  variables?: Types.UtilitySiteCountsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilitySiteCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilitySiteCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilitySiteCountsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UtilitySiteCounts"]
        : ["UtilitySiteCounts", variables],
    queryFn: publicFetcher<
      Types.UtilitySiteCountsQuery,
      Types.UtilitySiteCountsQueryVariables
    >(UtilitySiteCountsDocument, variables),
    ...options,
  })
}

useSuspenseUtilitySiteCountsQuery.getKey = (
  variables?: Types.UtilitySiteCountsQueryVariables,
) =>
  variables === undefined ? ["UtilitySiteCounts"] : ["UtilitySiteCounts", variables]

useSuspenseUtilitySiteCountsQuery.rootKey = "UtilitySiteCounts"

export const useInfiniteUtilitySiteCountsQuery = <
  TData = InfiniteData<Types.UtilitySiteCountsQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySiteCountsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilitySiteCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilitySiteCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilitySiteCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilitySiteCounts.infinite"]
            : ["UtilitySiteCounts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UtilitySiteCountsQuery,
            Types.UtilitySiteCountsQueryVariables
          >(UtilitySiteCountsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilitySiteCountsQuery.getKey = (
  variables?: Types.UtilitySiteCountsQueryVariables,
) =>
  variables === undefined
    ? ["UtilitySiteCounts.infinite"]
    : ["UtilitySiteCounts.infinite", variables]

useInfiniteUtilitySiteCountsQuery.rootKey = "UtilitySiteCounts.infinite"

export const useSuspenseInfiniteUtilitySiteCountsQuery = <
  TData = InfiniteData<Types.UtilitySiteCountsQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySiteCountsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilitySiteCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilitySiteCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilitySiteCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilitySiteCounts.infinite"]
            : ["UtilitySiteCounts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UtilitySiteCountsQuery,
            Types.UtilitySiteCountsQueryVariables
          >(UtilitySiteCountsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilitySiteCountsQuery.getKey = (
  variables?: Types.UtilitySiteCountsQueryVariables,
) =>
  variables === undefined
    ? ["UtilitySiteCounts.infinite"]
    : ["UtilitySiteCounts.infinite", variables]

useSuspenseInfiniteUtilitySiteCountsQuery.rootKey = "UtilitySiteCounts.infinite"

useUtilitySiteCountsQuery.fetcher = (
  variables?: Types.UtilitySiteCountsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilitySiteCountsQuery, Types.UtilitySiteCountsQueryVariables>(
    UtilitySiteCountsDocument,
    variables,
    options,
  )

export const TariffDetailDocument = new TypedDocumentString(`
    query TariffDetail($tariffId: Int!) {
  admin {
    tariffs {
      tariff(id: $tariffId) {
        id
        pid
        masterTariffId
        code
        name
        tariffType
        customerClass
        minMonthlyKwh
        maxMonthlyKwh
        minMonthlyKw
        maxMonthlyKw
        hasTouRates
        hasNetMetering
        isPrimaryConnection
        selectionStatus
        ownership
        notes
        requiresPv
        requiresStorage
        states
        tracking {
          alwaysTrackedSince
          lastAssignedAt
        }
        utility {
          id
          pid
          name
          shortName
        }
        rateDetails {
          id
          startDate
          endDate
          version {
            effectiveDate
            endDate
          }
          sourceRefs {
            source
            sourceRef
          }
        }
      }
    }
  }
}
    `)

export const useTariffDetailQuery = <TData = Types.TariffDetailQuery, TError = unknown>(
  variables: Types.TariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TariffDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffDetailQuery, TError, TData>({
    queryKey: ["TariffDetail", variables],
    queryFn: publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
      TariffDetailDocument,
      variables,
    ),
    ...options,
  })
}

useTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetail",
  variables,
]

useTariffDetailQuery.rootKey = "TariffDetail"

export const useSuspenseTariffDetailQuery = <
  TData = Types.TariffDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffDetailQuery, TError, TData>({
    queryKey: ["TariffDetail", variables],
    queryFn: publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
      TariffDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetail",
  variables,
]

useSuspenseTariffDetailQuery.rootKey = "TariffDetail"

export const useInfiniteTariffDetailQuery = <
  TData = InfiniteData<Types.TariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
            TariffDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetail.infinite",
  variables,
]

useInfiniteTariffDetailQuery.rootKey = "TariffDetail.infinite"

export const useSuspenseInfiniteTariffDetailQuery = <
  TData = InfiniteData<Types.TariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
            TariffDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffDetailQuery.getKey = (
  variables: Types.TariffDetailQueryVariables,
) => ["TariffDetail.infinite", variables]

useSuspenseInfiniteTariffDetailQuery.rootKey = "TariffDetail.infinite"

useTariffDetailQuery.fetcher = (
  variables: Types.TariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
    TariffDetailDocument,
    variables,
    options,
  )

export const TariffDraftsDocument = new TypedDocumentString(`
    query TariffDrafts($utilityPid: String!) {
  tariffDrafts(utilityPid: $utilityPid, status: [DRAFT, IN_REVIEW]) {
    pid
    status
    origin
    effectiveDate
    tariff {
      id
    }
    clonedFrom {
      id
    }
  }
}
    `)

export const useTariffDraftsQuery = <TData = Types.TariffDraftsQuery, TError = unknown>(
  variables: Types.TariffDraftsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffDraftsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TariffDraftsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffDraftsQuery, TError, TData>({
    queryKey: ["TariffDrafts", variables],
    queryFn: publicFetcher<Types.TariffDraftsQuery, Types.TariffDraftsQueryVariables>(
      TariffDraftsDocument,
      variables,
    ),
    ...options,
  })
}

useTariffDraftsQuery.getKey = (variables: Types.TariffDraftsQueryVariables) => [
  "TariffDrafts",
  variables,
]

useTariffDraftsQuery.rootKey = "TariffDrafts"

export const useSuspenseTariffDraftsQuery = <
  TData = Types.TariffDraftsQuery,
  TError = unknown,
>(
  variables: Types.TariffDraftsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffDraftsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffDraftsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffDraftsQuery, TError, TData>({
    queryKey: ["TariffDrafts", variables],
    queryFn: publicFetcher<Types.TariffDraftsQuery, Types.TariffDraftsQueryVariables>(
      TariffDraftsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffDraftsQuery.getKey = (variables: Types.TariffDraftsQueryVariables) => [
  "TariffDrafts",
  variables,
]

useSuspenseTariffDraftsQuery.rootKey = "TariffDrafts"

export const useInfiniteTariffDraftsQuery = <
  TData = InfiniteData<Types.TariffDraftsQuery>,
  TError = unknown,
>(
  variables: Types.TariffDraftsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffDraftsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffDraftsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffDraftsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDrafts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDraftsQuery, Types.TariffDraftsQueryVariables>(
            TariffDraftsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffDraftsQuery.getKey = (variables: Types.TariffDraftsQueryVariables) => [
  "TariffDrafts.infinite",
  variables,
]

useInfiniteTariffDraftsQuery.rootKey = "TariffDrafts.infinite"

export const useSuspenseInfiniteTariffDraftsQuery = <
  TData = InfiniteData<Types.TariffDraftsQuery>,
  TError = unknown,
>(
  variables: Types.TariffDraftsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffDraftsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffDraftsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffDraftsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDrafts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDraftsQuery, Types.TariffDraftsQueryVariables>(
            TariffDraftsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffDraftsQuery.getKey = (
  variables: Types.TariffDraftsQueryVariables,
) => ["TariffDrafts.infinite", variables]

useSuspenseInfiniteTariffDraftsQuery.rootKey = "TariffDrafts.infinite"

useTariffDraftsQuery.fetcher = (
  variables: Types.TariffDraftsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffDraftsQuery, Types.TariffDraftsQueryVariables>(
    TariffDraftsDocument,
    variables,
    options,
  )

export const UpstreamTariffDraftsAwaitingReviewDocument = new TypedDocumentString(`
    query UpstreamTariffDraftsAwaitingReview {
  upstreamTariffDraftsAwaitingReview {
    pid
    status
    effectiveDate
    updatedAt
    upstreamSource
    upstreamRef
    utility {
      id
      shortName
    }
    tariff {
      id
      code
      name
    }
    clonedFrom {
      id
    }
  }
}
    `)

export const useUpstreamTariffDraftsAwaitingReviewQuery = <
  TData = Types.UpstreamTariffDraftsAwaitingReviewQuery,
  TError = unknown,
>(
  variables?: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UpstreamTariffDraftsAwaitingReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.UpstreamTariffDraftsAwaitingReviewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UpstreamTariffDraftsAwaitingReview"]
        : ["UpstreamTariffDraftsAwaitingReview", variables],
    queryFn: publicFetcher<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      Types.UpstreamTariffDraftsAwaitingReviewQueryVariables
    >(UpstreamTariffDraftsAwaitingReviewDocument, variables),
    ...options,
  })
}

useUpstreamTariffDraftsAwaitingReviewQuery.getKey = (
  variables?: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
) =>
  variables === undefined
    ? ["UpstreamTariffDraftsAwaitingReview"]
    : ["UpstreamTariffDraftsAwaitingReview", variables]

useUpstreamTariffDraftsAwaitingReviewQuery.rootKey =
  "UpstreamTariffDraftsAwaitingReview"

export const useSuspenseUpstreamTariffDraftsAwaitingReviewQuery = <
  TData = Types.UpstreamTariffDraftsAwaitingReviewQuery,
  TError = unknown,
>(
  variables?: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UpstreamTariffDraftsAwaitingReviewQuery, TError, TData>(
    {
      queryKey:
        variables === undefined
          ? ["UpstreamTariffDraftsAwaitingReview"]
          : ["UpstreamTariffDraftsAwaitingReview", variables],
      queryFn: publicFetcher<
        Types.UpstreamTariffDraftsAwaitingReviewQuery,
        Types.UpstreamTariffDraftsAwaitingReviewQueryVariables
      >(UpstreamTariffDraftsAwaitingReviewDocument, variables),
      ...options,
    },
  )
}

useSuspenseUpstreamTariffDraftsAwaitingReviewQuery.getKey = (
  variables?: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
) =>
  variables === undefined
    ? ["UpstreamTariffDraftsAwaitingReview"]
    : ["UpstreamTariffDraftsAwaitingReview", variables]

useSuspenseUpstreamTariffDraftsAwaitingReviewQuery.rootKey =
  "UpstreamTariffDraftsAwaitingReview"

export const useInfiniteUpstreamTariffDraftsAwaitingReviewQuery = <
  TData = InfiniteData<Types.UpstreamTariffDraftsAwaitingReviewQuery>,
  TError = unknown,
>(
  variables: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UpstreamTariffDraftsAwaitingReviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UpstreamTariffDraftsAwaitingReview.infinite"]
            : ["UpstreamTariffDraftsAwaitingReview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UpstreamTariffDraftsAwaitingReviewQuery,
            Types.UpstreamTariffDraftsAwaitingReviewQueryVariables
          >(UpstreamTariffDraftsAwaitingReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUpstreamTariffDraftsAwaitingReviewQuery.getKey = (
  variables?: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
) =>
  variables === undefined
    ? ["UpstreamTariffDraftsAwaitingReview.infinite"]
    : ["UpstreamTariffDraftsAwaitingReview.infinite", variables]

useInfiniteUpstreamTariffDraftsAwaitingReviewQuery.rootKey =
  "UpstreamTariffDraftsAwaitingReview.infinite"

export const useSuspenseInfiniteUpstreamTariffDraftsAwaitingReviewQuery = <
  TData = InfiniteData<Types.UpstreamTariffDraftsAwaitingReviewQuery>,
  TError = unknown,
>(
  variables: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UpstreamTariffDraftsAwaitingReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.UpstreamTariffDraftsAwaitingReviewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UpstreamTariffDraftsAwaitingReview.infinite"]
            : ["UpstreamTariffDraftsAwaitingReview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UpstreamTariffDraftsAwaitingReviewQuery,
            Types.UpstreamTariffDraftsAwaitingReviewQueryVariables
          >(UpstreamTariffDraftsAwaitingReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUpstreamTariffDraftsAwaitingReviewQuery.getKey = (
  variables?: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
) =>
  variables === undefined
    ? ["UpstreamTariffDraftsAwaitingReview.infinite"]
    : ["UpstreamTariffDraftsAwaitingReview.infinite", variables]

useSuspenseInfiniteUpstreamTariffDraftsAwaitingReviewQuery.rootKey =
  "UpstreamTariffDraftsAwaitingReview.infinite"

useUpstreamTariffDraftsAwaitingReviewQuery.fetcher = (
  variables?: Types.UpstreamTariffDraftsAwaitingReviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpstreamTariffDraftsAwaitingReviewQuery,
    Types.UpstreamTariffDraftsAwaitingReviewQueryVariables
  >(UpstreamTariffDraftsAwaitingReviewDocument, variables, options)

export const TariffDraftDocument = new TypedDocumentString(`
    query TariffDraft($pid: String!) {
  tariffDraft(pid: $pid) {
    pid
    status
    origin
    upstreamSource
    upstreamRef
    version
    effectiveDate
    notes
    updatedAt
    document
    utility {
      id
      pid
      name
    }
    tariff {
      id
      code
    }
    clonedFrom {
      id
    }
    publishedRevision {
      id
    }
    findings {
      level
      section
      message
      rateId
      key
    }
  }
}
    `)

export const useTariffDraftQuery = <TData = Types.TariffDraftQuery, TError = unknown>(
  variables: Types.TariffDraftQueryVariables,
  options?: Omit<UseQueryOptions<Types.TariffDraftQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TariffDraftQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffDraftQuery, TError, TData>({
    queryKey: ["TariffDraft", variables],
    queryFn: publicFetcher<Types.TariffDraftQuery, Types.TariffDraftQueryVariables>(
      TariffDraftDocument,
      variables,
    ),
    ...options,
  })
}

useTariffDraftQuery.getKey = (variables: Types.TariffDraftQueryVariables) => [
  "TariffDraft",
  variables,
]

useTariffDraftQuery.rootKey = "TariffDraft"

export const useSuspenseTariffDraftQuery = <
  TData = Types.TariffDraftQuery,
  TError = unknown,
>(
  variables: Types.TariffDraftQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffDraftQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffDraftQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffDraftQuery, TError, TData>({
    queryKey: ["TariffDraft", variables],
    queryFn: publicFetcher<Types.TariffDraftQuery, Types.TariffDraftQueryVariables>(
      TariffDraftDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffDraftQuery.getKey = (variables: Types.TariffDraftQueryVariables) => [
  "TariffDraft",
  variables,
]

useSuspenseTariffDraftQuery.rootKey = "TariffDraft"

export const useInfiniteTariffDraftQuery = <
  TData = InfiniteData<Types.TariffDraftQuery>,
  TError = unknown,
>(
  variables: Types.TariffDraftQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffDraftQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffDraftQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffDraftQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDraft.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDraftQuery, Types.TariffDraftQueryVariables>(
            TariffDraftDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffDraftQuery.getKey = (variables: Types.TariffDraftQueryVariables) => [
  "TariffDraft.infinite",
  variables,
]

useInfiniteTariffDraftQuery.rootKey = "TariffDraft.infinite"

export const useSuspenseInfiniteTariffDraftQuery = <
  TData = InfiniteData<Types.TariffDraftQuery>,
  TError = unknown,
>(
  variables: Types.TariffDraftQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffDraftQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffDraftQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffDraftQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDraft.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDraftQuery, Types.TariffDraftQueryVariables>(
            TariffDraftDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffDraftQuery.getKey = (
  variables: Types.TariffDraftQueryVariables,
) => ["TariffDraft.infinite", variables]

useSuspenseInfiniteTariffDraftQuery.rootKey = "TariffDraft.infinite"

useTariffDraftQuery.fetcher = (
  variables: Types.TariffDraftQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffDraftQuery, Types.TariffDraftQueryVariables>(
    TariffDraftDocument,
    variables,
    options,
  )

export const TariffRevisionDetailDocument = new TypedDocumentString(`
    query TariffRevisionDetail($revisionId: Int!) {
  admin {
    tariffs {
      revision(id: $revisionId) {
        version {
          tariffId
          effectiveDate
          endDate
        }
        properties {
          ...RevisionProperty
        }
        rawJson
        allRates {
          ...RevisionRate
        }
        id
        startDate
        endDate
        sourceRefs {
          source
          sourceRef
        }
        tariff {
          id
          masterTariffId
          code
          name
          customerClass
          utility {
            id
            shortName
          }
        }
      }
    }
  }
}
    fragment RevisionRate on Rate {
  ...RateTableFragment
  groupName
  tou {
    touType
  }
}
fragment RateTableFragment on Rate {
  tariffRateId
  name
  chargeClass
  chargeType
  chargePeriod
  transactionType
  applicabilityKey
  quantityKey
  variableLimitKey
  variableRateKey
  variableFactorKey
  isTou
  bands {
    tariffRateBandId
    rateUnit
    price
    isCredit
    hasConsumptionLimit
    consumptionUpperLimit
    demandUpperLimit
    propertyUpperLimit
    calculationFactor
    applicabilityValue
  }
  season {
    seasonFromMonth
    seasonToMonth
    seasonFromDay
    seasonToDay
  }
  tou {
    touName
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    touPeriods {
      fromDayOfWeek
      toDayOfWeek
      fromHour
      toHour
      fromMinute
      toMinute
    }
  }
}
fragment RevisionProperty on TariffProperty {
  keyName
  period
  displayName
  keyspace
  family
  description
  dataType
  propertyTypes
  operator
  propertyValue
  minValue
  maxValue
  choices {
    displayValue
    value
    dataValue
    likelihood
  }
  propertyValues
  formulaDetail
  isDefault
  quantityKey
}`)

export const useTariffRevisionDetailQuery = <
  TData = Types.TariffRevisionDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRevisionDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffRevisionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.TariffRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.TariffRevisionDetailQuery, TError, TData>({
    queryKey: ["TariffRevisionDetail", variables],
    queryFn: publicFetcher<
      Types.TariffRevisionDetailQuery,
      Types.TariffRevisionDetailQueryVariables
    >(TariffRevisionDetailDocument, variables),
    ...options,
  })
}

useTariffRevisionDetailQuery.getKey = (
  variables: Types.TariffRevisionDetailQueryVariables,
) => ["TariffRevisionDetail", variables]

useTariffRevisionDetailQuery.rootKey = "TariffRevisionDetail"

export const useSuspenseTariffRevisionDetailQuery = <
  TData = Types.TariffRevisionDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRevisionDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffRevisionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffRevisionDetailQuery, TError, TData>({
    queryKey: ["TariffRevisionDetail", variables],
    queryFn: publicFetcher<
      Types.TariffRevisionDetailQuery,
      Types.TariffRevisionDetailQueryVariables
    >(TariffRevisionDetailDocument, variables),
    ...options,
  })
}

useSuspenseTariffRevisionDetailQuery.getKey = (
  variables: Types.TariffRevisionDetailQueryVariables,
) => ["TariffRevisionDetail", variables]

useSuspenseTariffRevisionDetailQuery.rootKey = "TariffRevisionDetail"

export const useInfiniteTariffRevisionDetailQuery = <
  TData = InfiniteData<Types.TariffRevisionDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRevisionDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffRevisionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffRevisionDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRevisionDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.TariffRevisionDetailQuery,
            Types.TariffRevisionDetailQueryVariables
          >(TariffRevisionDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffRevisionDetailQuery.getKey = (
  variables: Types.TariffRevisionDetailQueryVariables,
) => ["TariffRevisionDetail.infinite", variables]

useInfiniteTariffRevisionDetailQuery.rootKey = "TariffRevisionDetail.infinite"

export const useSuspenseInfiniteTariffRevisionDetailQuery = <
  TData = InfiniteData<Types.TariffRevisionDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRevisionDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffRevisionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffRevisionDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRevisionDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.TariffRevisionDetailQuery,
            Types.TariffRevisionDetailQueryVariables
          >(TariffRevisionDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffRevisionDetailQuery.getKey = (
  variables: Types.TariffRevisionDetailQueryVariables,
) => ["TariffRevisionDetail.infinite", variables]

useSuspenseInfiniteTariffRevisionDetailQuery.rootKey = "TariffRevisionDetail.infinite"

useTariffRevisionDetailQuery.fetcher = (
  variables: Types.TariffRevisionDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TariffRevisionDetailQuery,
    Types.TariffRevisionDetailQueryVariables
  >(TariffRevisionDetailDocument, variables, options)

export const SiteHeaderDocument = new TypedDocumentString(`
    query SiteHeader($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      id
      pid
      labels {
        pid
        name
        themeColor
      }
      customerId
      propertyName
      streetAddress
      city
      state
      zipCode
      portfolio {
        pid
        title
        org {
          pid
          name
          slug
        }
      }
      status
      hiddenFromCustomers
      statusCheckPassed
      policyViolations {
        policy {
          pid
        }
      }
    }
  }
}
    `)

export const useSiteHeaderQuery = <TData = Types.SiteHeaderQuery, TError = unknown>(
  variables: Types.SiteHeaderQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteHeaderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteHeaderQuery, TError, TData>({
    queryKey: ["SiteHeader", variables],
    queryFn: publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
      SiteHeaderDocument,
      variables,
    ),
    ...options,
  })
}

useSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeader",
  variables,
]

useSiteHeaderQuery.rootKey = "SiteHeader"

export const useSuspenseSiteHeaderQuery = <
  TData = Types.SiteHeaderQuery,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteHeaderQuery, TError, TData>({
    queryKey: ["SiteHeader", variables],
    queryFn: publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
      SiteHeaderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeader",
  variables,
]

useSuspenseSiteHeaderQuery.rootKey = "SiteHeader"

export const useInfiniteSiteHeaderQuery = <
  TData = InfiniteData<Types.SiteHeaderQuery>,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteHeaderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteHeader.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
            SiteHeaderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeader.infinite",
  variables,
]

useInfiniteSiteHeaderQuery.rootKey = "SiteHeader.infinite"

export const useSuspenseInfiniteSiteHeaderQuery = <
  TData = InfiniteData<Types.SiteHeaderQuery>,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteHeaderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteHeaderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteHeader.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
            SiteHeaderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteHeaderQuery.getKey = (
  variables: Types.SiteHeaderQueryVariables,
) => ["SiteHeader.infinite", variables]

useSuspenseInfiniteSiteHeaderQuery.rootKey = "SiteHeader.infinite"

useSiteHeaderQuery.fetcher = (
  variables: Types.SiteHeaderQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
    SiteHeaderDocument,
    variables,
    options,
  )

export const GetEtbDetailDocument = new TypedDocumentString(`
    query GetEtbDetail($sitePid: String!, $etbId: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      activeMeters {
        id
        meters {
          ...BauMeter
        }
      }
      etbReport(id: $etbId) {
        id
        dateCreated
        projectName
        proposalDate
        taxType
        ownerSector
        federalIncomeTaxRate
        stateIncomeTaxRate
        discountRate
        electricityEscalationRate
        scenario {
          ...ETBScenarioRef
        }
        combinedCosts {
          ...CostsFragment
        }
        meters {
          id
          name
          streetAddress
          city
          state
          zipCode
          utilityName
          genabilityUtility: utility {
            id
            name
            shortName
          }
          tariffFullDescriptionBefore
          genabilityTariffBefore: tariffBefore {
            id
            name
            code
          }
          tariffFullDescriptionAfter
          genabilityTariffAfter: tariffAfter {
            id
            name
            code
          }
          costs {
            ...CostsFragment
          }
          pv {
            cost
            powerRatingKwDc
            powerRatingKwAc
            degradationRate
            tilt
            azimuth
          }
          pvType
          bauMeter {
            id
            archivedAt
          }
        }
        transactions {
          name
          termYears
          irr10Year
          irr20Year
          irrTerm
          lcoeBefore
          lcoeAfter
          lcoePv
          npvTerm
          paybackYears
          roiTerm
          demandSavingsTerm
          energySavingsTerm
          totalSavingsTerm
          pvGenerationKwhTerm
          storageDischargeKwhTerm
          storageConsumptionKwh
          totalCost
          totalIncentives
          netCost
          upfrontCost
          ppaTerm
          ppaStartingRate
          ppaEscalationRate
          series {
            name
            series
          }
        }
        roofPvKw
        carportPvKw
      }
    }
  }
}
    fragment BauMeter on BauMeter {
  id
  createdAt
  createdBy {
    email
  }
  label
  loadOrigin
  loadCategory
  meterSource
  utility {
    id
    name
  }
  tariff {
    ...BauTariff
  }
  tariffSetBy {
    pid
    email
  }
  underHumanControl
  tariffsUnderHumanControl
  postSolarTariff {
    ...BauTariff
  }
  postSolarTariffSetBy {
    pid
    email
  }
  postBatteryTariff {
    ...BauTariff
  }
  postBatteryTariffSetBy {
    pid
    email
  }
  archivedAt
  archivedBy {
    pid
    email
  }
  archivedReason
  rollup {
    season
    days
    numbers
  }
}
fragment ETBScenarioRef on ETBScenarioRef {
  pid
  name
  isPrimary
  solarLocation
  includeStorage
  loadCategory
}
fragment CostsFragment on ETBCosts {
  totalCost
  totalIncentiveSavings
  netCost
  energyConsumptionKwhYear1
  netEnergyConsumptionKwhYear1
  utilityCostPerKwh
  averageLoadFactor
  energyCostBefore
  energyCostAfter
  energyCostSavings
  demandCostBefore
  demandCostAfter
  demandCostSavings
  totalCostBefore
  totalCostAfter
  totalCostSavings
  pvTotalCost
  pvNetCost
  pvPowerRatingKwDc
  pvPowerRatingKwAc
  pvDegradationRate
  pvGenerationKwhYear1
  pvKwhPerKwDc
  pvSavingsPerKwh
  pvGenerationPercentExported
  storageTotalCost
  storageNetCost
  storageCapacityKwh
  storageMaxDischargePowerKw
  storageTwoHourPowerRatingKw
  storageDegradationRate
  storageDischargeYear1
  storageConsumptionKwhYear1
  storageSavingsPerKw
  storageSavingsPerKwh
}
fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}`)

export const useGetEtbDetailQuery = <TData = Types.GetEtbDetailQuery, TError = unknown>(
  variables: Types.GetEtbDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetEtbDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetEtbDetailQuery, TError, TData>({
    queryKey: ["GetEtbDetail", variables],
    queryFn: publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
      GetEtbDetailDocument,
      variables,
    ),
    ...options,
  })
}

useGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetail",
  variables,
]

useGetEtbDetailQuery.rootKey = "GetEtbDetail"

export const useSuspenseGetEtbDetailQuery = <
  TData = Types.GetEtbDetailQuery,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetEtbDetailQuery, TError, TData>({
    queryKey: ["GetEtbDetail", variables],
    queryFn: publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
      GetEtbDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetail",
  variables,
]

useSuspenseGetEtbDetailQuery.rootKey = "GetEtbDetail"

export const useInfiniteGetEtbDetailQuery = <
  TData = InfiniteData<Types.GetEtbDetailQuery>,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetEtbDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetEtbDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
            GetEtbDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetail.infinite",
  variables,
]

useInfiniteGetEtbDetailQuery.rootKey = "GetEtbDetail.infinite"

export const useSuspenseInfiniteGetEtbDetailQuery = <
  TData = InfiniteData<Types.GetEtbDetailQuery>,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetEtbDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetEtbDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
            GetEtbDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetEtbDetailQuery.getKey = (
  variables: Types.GetEtbDetailQueryVariables,
) => ["GetEtbDetail.infinite", variables]

useSuspenseInfiniteGetEtbDetailQuery.rootKey = "GetEtbDetail.infinite"

useGetEtbDetailQuery.fetcher = (
  variables: Types.GetEtbDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
    GetEtbDetailDocument,
    variables,
    options,
  )

export const EtbIndexDocument = new TypedDocumentString(`
    query EtbIndex($pid: String!) {
  admin {
    site(pid: $pid) {
      pid
      etbReports {
        id
        projectName
        dateCreated
        proposalDate
        scenario {
          ...ETBScenarioRef
        }
      }
      etbScenarioOptions {
        ...ETBScenarioRef
      }
    }
  }
}
    fragment ETBScenarioRef on ETBScenarioRef {
  pid
  name
  isPrimary
  solarLocation
  includeStorage
  loadCategory
}`)

export const useEtbIndexQuery = <TData = Types.EtbIndexQuery, TError = unknown>(
  variables: Types.EtbIndexQueryVariables,
  options?: Omit<UseQueryOptions<Types.EtbIndexQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EtbIndexQuery, TError, TData>({
    queryKey: ["EtbIndex", variables],
    queryFn: publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
      EtbIndexDocument,
      variables,
    ),
    ...options,
  })
}

useEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex",
  variables,
]

useEtbIndexQuery.rootKey = "EtbIndex"

export const useSuspenseEtbIndexQuery = <TData = Types.EtbIndexQuery, TError = unknown>(
  variables: Types.EtbIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EtbIndexQuery, TError, TData>({
    queryKey: ["EtbIndex", variables],
    queryFn: publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
      EtbIndexDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex",
  variables,
]

useSuspenseEtbIndexQuery.rootKey = "EtbIndex"

export const useInfiniteEtbIndexQuery = <
  TData = InfiniteData<Types.EtbIndexQuery>,
  TError = unknown,
>(
  variables: Types.EtbIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EtbIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EtbIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
            EtbIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex.infinite",
  variables,
]

useInfiniteEtbIndexQuery.rootKey = "EtbIndex.infinite"

export const useSuspenseInfiniteEtbIndexQuery = <
  TData = InfiniteData<Types.EtbIndexQuery>,
  TError = unknown,
>(
  variables: Types.EtbIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EtbIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EtbIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EtbIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
            EtbIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex.infinite",
  variables,
]

useSuspenseInfiniteEtbIndexQuery.rootKey = "EtbIndex.infinite"

useEtbIndexQuery.fetcher = (
  variables: Types.EtbIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
    EtbIndexDocument,
    variables,
    options,
  )

export const HelioscopeIndexDocument = new TypedDocumentString(`
    query HelioscopeIndex($pid: String!) {
  admin {
    site(pid: $pid) {
      pid
      activeMeters {
        meters {
          label
        }
      }
      helioscopes {
        id
        pid
        kind
        createdAt
        createdBy {
          ...UserRef
        }
        isTrashed
        roofPvKw
        carportPvKw
        carportPvThreeHighKw
        carportPvSixHighKw
        groundPvKw
        annualProductionAcKwh
        monthlyProductionAcKwh
        systemProductivityKwhPerKw
        allocations
        pvSpecs {
          pvType
          sizeKw
        }
        pdf {
          ...AttachmentFragment
        }
        roofImage {
          ...AttachmentFragment
        }
        roofImageHref
        productionCsv {
          ...AttachmentFragment
        }
        dcNameplateKw
        acNameplateKw
        loadRatio
        moduleType
        inverterType
        rackingTiltDegrees
        rackingManufacturer
        rackingConnectionType
        notes
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}`)

export const useHelioscopeIndexQuery = <
  TData = Types.HelioscopeIndexQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HelioscopeIndexQuery, TError, TData>({
    queryKey: ["HelioscopeIndex", variables],
    queryFn: publicFetcher<
      Types.HelioscopeIndexQuery,
      Types.HelioscopeIndexQueryVariables
    >(HelioscopeIndexDocument, variables),
    ...options,
  })
}

useHelioscopeIndexQuery.getKey = (variables: Types.HelioscopeIndexQueryVariables) => [
  "HelioscopeIndex",
  variables,
]

useHelioscopeIndexQuery.rootKey = "HelioscopeIndex"

export const useSuspenseHelioscopeIndexQuery = <
  TData = Types.HelioscopeIndexQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HelioscopeIndexQuery, TError, TData>({
    queryKey: ["HelioscopeIndex", variables],
    queryFn: publicFetcher<
      Types.HelioscopeIndexQuery,
      Types.HelioscopeIndexQueryVariables
    >(HelioscopeIndexDocument, variables),
    ...options,
  })
}

useSuspenseHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndex", variables]

useSuspenseHelioscopeIndexQuery.rootKey = "HelioscopeIndex"

export const useInfiniteHelioscopeIndexQuery = <
  TData = InfiniteData<Types.HelioscopeIndexQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HelioscopeIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeIndexQuery,
            Types.HelioscopeIndexQueryVariables
          >(HelioscopeIndexDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndex.infinite", variables]

useInfiniteHelioscopeIndexQuery.rootKey = "HelioscopeIndex.infinite"

export const useSuspenseInfiniteHelioscopeIndexQuery = <
  TData = InfiniteData<Types.HelioscopeIndexQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HelioscopeIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeIndexQuery,
            Types.HelioscopeIndexQueryVariables
          >(HelioscopeIndexDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndex.infinite", variables]

useSuspenseInfiniteHelioscopeIndexQuery.rootKey = "HelioscopeIndex.infinite"

useHelioscopeIndexQuery.fetcher = (
  variables: Types.HelioscopeIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HelioscopeIndexQuery, Types.HelioscopeIndexQueryVariables>(
    HelioscopeIndexDocument,
    variables,
    options,
  )

export const HelioscopeUploadDocument = new TypedDocumentString(`
    query HelioscopeUpload($pid: String!) {
  admin {
    site(pid: $pid) {
      pid
      scenarios {
        ...SmallScenarioFragment
      }
    }
  }
}
    fragment SmallScenarioFragment on AdminScenario {
  id
  pid
  name
  dateCreated
  includeStorage
  isPrimary
  isMultifamily
  loadCategory
  solarLocation
  createdBy {
    ...UserRef
  }
  hiddenFromCustomers
  allowMulticonnect
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useHelioscopeUploadQuery = <
  TData = Types.HelioscopeUploadQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HelioscopeUploadQuery, TError, TData>({
    queryKey: ["HelioscopeUpload", variables],
    queryFn: publicFetcher<
      Types.HelioscopeUploadQuery,
      Types.HelioscopeUploadQueryVariables
    >(HelioscopeUploadDocument, variables),
    ...options,
  })
}

useHelioscopeUploadQuery.getKey = (variables: Types.HelioscopeUploadQueryVariables) => [
  "HelioscopeUpload",
  variables,
]

useHelioscopeUploadQuery.rootKey = "HelioscopeUpload"

export const useSuspenseHelioscopeUploadQuery = <
  TData = Types.HelioscopeUploadQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HelioscopeUploadQuery, TError, TData>({
    queryKey: ["HelioscopeUpload", variables],
    queryFn: publicFetcher<
      Types.HelioscopeUploadQuery,
      Types.HelioscopeUploadQueryVariables
    >(HelioscopeUploadDocument, variables),
    ...options,
  })
}

useSuspenseHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUpload", variables]

useSuspenseHelioscopeUploadQuery.rootKey = "HelioscopeUpload"

export const useInfiniteHelioscopeUploadQuery = <
  TData = InfiniteData<Types.HelioscopeUploadQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HelioscopeUploadQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeUpload.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeUploadQuery,
            Types.HelioscopeUploadQueryVariables
          >(HelioscopeUploadDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUpload.infinite", variables]

useInfiniteHelioscopeUploadQuery.rootKey = "HelioscopeUpload.infinite"

export const useSuspenseInfiniteHelioscopeUploadQuery = <
  TData = InfiniteData<Types.HelioscopeUploadQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HelioscopeUploadQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeUpload.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeUploadQuery,
            Types.HelioscopeUploadQueryVariables
          >(HelioscopeUploadDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUpload.infinite", variables]

useSuspenseInfiniteHelioscopeUploadQuery.rootKey = "HelioscopeUpload.infinite"

useHelioscopeUploadQuery.fetcher = (
  variables: Types.HelioscopeUploadQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HelioscopeUploadQuery, Types.HelioscopeUploadQueryVariables>(
    HelioscopeUploadDocument,
    variables,
    options,
  )

export const LuxDetailDocument = new TypedDocumentString(`
    query LuxDetail($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      lux(id: $id) {
        ...Lux
      }
    }
  }
}
    fragment SlimLux on Lux {
  id
  etbReport {
    id
  }
  createdAt
  fmvStr
  goal
  maxOffsetPct
  pvKw
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  battKw
  battKwh
  meterSet {
    id
  }
  meters {
    bauMeter {
      id
    }
    postSolarMeter {
      pv {
        ...PVConfig
      }
    }
    postBatteryMeter {
      batt {
        ...BatteryConfig
      }
    }
  }
}
fragment Lux on Lux {
  ...SlimLux
  meters {
    ...LuxMeter
  }
  iterations {
    meters {
      bauMeterId
      totalPvKw
    }
    metric
    postTaxItcDeprIrr
  }
}
fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}
fragment CostSummary on CostSummary {
  totalCost
  energyCost
  demandCost
  fixedCost
  nbcCost
  exportBenefit
}
fragment LuxMeter on LuxMeter {
  id
  gridMeter {
    preSolar {
      costSummary {
        ...CostSummary
      }
    }
    postSolar {
      costSummary {
        ...CostSummary
      }
    }
    postBattery {
      costSummary {
        ...CostSummary
      }
    }
  }
  dayIntervals {
    pvToBldg
    battDischarge
    gridToBldg
  }
  bauMeter {
    id
    label
    importsKwh
    tariff {
      name
      code
    }
  }
  postSolarMeter {
    pv {
      ...PVConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
  postBatteryMeter {
    batt {
      ...BatteryConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
}`)

export const useLuxDetailQuery = <TData = Types.LuxDetailQuery, TError = unknown>(
  variables: Types.LuxDetailQueryVariables,
  options?: Omit<UseQueryOptions<Types.LuxDetailQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.LuxDetailQuery, TError, TData>({
    queryKey: ["LuxDetail", variables],
    queryFn: publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
      LuxDetailDocument,
      variables,
    ),
    ...options,
  })
}

useLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetail",
  variables,
]

useLuxDetailQuery.rootKey = "LuxDetail"

export const useSuspenseLuxDetailQuery = <
  TData = Types.LuxDetailQuery,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.LuxDetailQuery, TError, TData>({
    queryKey: ["LuxDetail", variables],
    queryFn: publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
      LuxDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetail",
  variables,
]

useSuspenseLuxDetailQuery.rootKey = "LuxDetail"

export const useInfiniteLuxDetailQuery = <
  TData = InfiniteData<Types.LuxDetailQuery>,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.LuxDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["LuxDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
            LuxDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetail.infinite",
  variables,
]

useInfiniteLuxDetailQuery.rootKey = "LuxDetail.infinite"

export const useSuspenseInfiniteLuxDetailQuery = <
  TData = InfiniteData<Types.LuxDetailQuery>,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.LuxDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.LuxDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["LuxDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
            LuxDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteLuxDetailQuery.getKey = (
  variables: Types.LuxDetailQueryVariables,
) => ["LuxDetail.infinite", variables]

useSuspenseInfiniteLuxDetailQuery.rootKey = "LuxDetail.infinite"

useLuxDetailQuery.fetcher = (
  variables: Types.LuxDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
    LuxDetailDocument,
    variables,
    options,
  )

export const GetPortfolioOverridesDocument = new TypedDocumentString(`
    query GetPortfolioOverrides($portfolioPid: String!) {
  admin {
    portfolio(pid: $portfolioPid) {
      pid
      overrides {
        ...Override
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}`)

export const useGetPortfolioOverridesQuery = <
  TData = Types.GetPortfolioOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetPortfolioOverridesQuery, TError, TData>({
    queryKey: ["GetPortfolioOverrides", variables],
    queryFn: publicFetcher<
      Types.GetPortfolioOverridesQuery,
      Types.GetPortfolioOverridesQueryVariables
    >(GetPortfolioOverridesDocument, variables),
    ...options,
  })
}

useGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides", variables]

useGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides"

export const useSuspenseGetPortfolioOverridesQuery = <
  TData = Types.GetPortfolioOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetPortfolioOverridesQuery, TError, TData>({
    queryKey: ["GetPortfolioOverrides", variables],
    queryFn: publicFetcher<
      Types.GetPortfolioOverridesQuery,
      Types.GetPortfolioOverridesQueryVariables
    >(GetPortfolioOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides", variables]

useSuspenseGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides"

export const useInfiniteGetPortfolioOverridesQuery = <
  TData = InfiniteData<Types.GetPortfolioOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetPortfolioOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetPortfolioOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetPortfolioOverridesQuery,
            Types.GetPortfolioOverridesQueryVariables
          >(GetPortfolioOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides.infinite", variables]

useInfiniteGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides.infinite"

export const useSuspenseInfiniteGetPortfolioOverridesQuery = <
  TData = InfiniteData<Types.GetPortfolioOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetPortfolioOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetPortfolioOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetPortfolioOverridesQuery,
            Types.GetPortfolioOverridesQueryVariables
          >(GetPortfolioOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides.infinite", variables]

useSuspenseInfiniteGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides.infinite"

useGetPortfolioOverridesQuery.fetcher = (
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GetPortfolioOverridesQuery,
    Types.GetPortfolioOverridesQueryVariables
  >(GetPortfolioOverridesDocument, variables, options)

export const GetSiteOverridesDocument = new TypedDocumentString(`
    query GetSiteOverrides($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      overrides {
        ...Override
      }
      portfolio {
        pid
        overrides {
          ...Override
        }
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}`)

export const useGetSiteOverridesQuery = <
  TData = Types.GetSiteOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetSiteOverridesQuery, TError, TData>({
    queryKey: ["GetSiteOverrides", variables],
    queryFn: publicFetcher<
      Types.GetSiteOverridesQuery,
      Types.GetSiteOverridesQueryVariables
    >(GetSiteOverridesDocument, variables),
    ...options,
  })
}

useGetSiteOverridesQuery.getKey = (variables: Types.GetSiteOverridesQueryVariables) => [
  "GetSiteOverrides",
  variables,
]

useGetSiteOverridesQuery.rootKey = "GetSiteOverrides"

export const useSuspenseGetSiteOverridesQuery = <
  TData = Types.GetSiteOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetSiteOverridesQuery, TError, TData>({
    queryKey: ["GetSiteOverrides", variables],
    queryFn: publicFetcher<
      Types.GetSiteOverridesQuery,
      Types.GetSiteOverridesQueryVariables
    >(GetSiteOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverrides", variables]

useSuspenseGetSiteOverridesQuery.rootKey = "GetSiteOverrides"

export const useInfiniteGetSiteOverridesQuery = <
  TData = InfiniteData<Types.GetSiteOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetSiteOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetSiteOverridesQuery,
            Types.GetSiteOverridesQueryVariables
          >(GetSiteOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverrides.infinite", variables]

useInfiniteGetSiteOverridesQuery.rootKey = "GetSiteOverrides.infinite"

export const useSuspenseInfiniteGetSiteOverridesQuery = <
  TData = InfiniteData<Types.GetSiteOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetSiteOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetSiteOverridesQuery,
            Types.GetSiteOverridesQueryVariables
          >(GetSiteOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverrides.infinite", variables]

useSuspenseInfiniteGetSiteOverridesQuery.rootKey = "GetSiteOverrides.infinite"

useGetSiteOverridesQuery.fetcher = (
  variables: Types.GetSiteOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetSiteOverridesQuery, Types.GetSiteOverridesQueryVariables>(
    GetSiteOverridesDocument,
    variables,
    options,
  )

export const ResolvedOverridesDocument = new TypedDocumentString(`
    query ResolvedOverrides($sitePid: String!, $finproductId: Int) {
  admin {
    site(pid: $sitePid) {
      resolvedOverrides(finproductId: $finproductId) {
        ...Override
      }
    }
  }
}
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}`)

export const useResolvedOverridesQuery = <
  TData = Types.ResolvedOverridesQuery,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResolvedOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResolvedOverridesQuery, TError, TData>({
    queryKey: ["ResolvedOverrides", variables],
    queryFn: publicFetcher<
      Types.ResolvedOverridesQuery,
      Types.ResolvedOverridesQueryVariables
    >(ResolvedOverridesDocument, variables),
    ...options,
  })
}

useResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverrides", variables]

useResolvedOverridesQuery.rootKey = "ResolvedOverrides"

export const useSuspenseResolvedOverridesQuery = <
  TData = Types.ResolvedOverridesQuery,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResolvedOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResolvedOverridesQuery, TError, TData>({
    queryKey: ["ResolvedOverrides", variables],
    queryFn: publicFetcher<
      Types.ResolvedOverridesQuery,
      Types.ResolvedOverridesQueryVariables
    >(ResolvedOverridesDocument, variables),
    ...options,
  })
}

useSuspenseResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverrides", variables]

useSuspenseResolvedOverridesQuery.rootKey = "ResolvedOverrides"

export const useInfiniteResolvedOverridesQuery = <
  TData = InfiniteData<Types.ResolvedOverridesQuery>,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResolvedOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResolvedOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResolvedOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResolvedOverridesQuery,
            Types.ResolvedOverridesQueryVariables
          >(ResolvedOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverrides.infinite", variables]

useInfiniteResolvedOverridesQuery.rootKey = "ResolvedOverrides.infinite"

export const useSuspenseInfiniteResolvedOverridesQuery = <
  TData = InfiniteData<Types.ResolvedOverridesQuery>,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResolvedOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResolvedOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResolvedOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResolvedOverridesQuery,
            Types.ResolvedOverridesQueryVariables
          >(ResolvedOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverrides.infinite", variables]

useSuspenseInfiniteResolvedOverridesQuery.rootKey = "ResolvedOverrides.infinite"

useResolvedOverridesQuery.fetcher = (
  variables: Types.ResolvedOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResolvedOverridesQuery, Types.ResolvedOverridesQueryVariables>(
    ResolvedOverridesDocument,
    variables,
    options,
  )

export const GetFinancingGiverOrgOverridesDocument = new TypedDocumentString(`
    query GetFinancingGiverOrgOverrides($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      pid
      overrides {
        ...Override
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}`)

export const useGetFinancingGiverOrgOverridesQuery = <
  TData = Types.GetFinancingGiverOrgOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>({
    queryKey: ["GetFinancingGiverOrgOverrides", variables],
    queryFn: publicFetcher<
      Types.GetFinancingGiverOrgOverridesQuery,
      Types.GetFinancingGiverOrgOverridesQueryVariables
    >(GetFinancingGiverOrgOverridesDocument, variables),
    ...options,
  })
}

useGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides", variables]

useGetFinancingGiverOrgOverridesQuery.rootKey = "GetFinancingGiverOrgOverrides"

export const useSuspenseGetFinancingGiverOrgOverridesQuery = <
  TData = Types.GetFinancingGiverOrgOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>({
    queryKey: ["GetFinancingGiverOrgOverrides", variables],
    queryFn: publicFetcher<
      Types.GetFinancingGiverOrgOverridesQuery,
      Types.GetFinancingGiverOrgOverridesQueryVariables
    >(GetFinancingGiverOrgOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides", variables]

useSuspenseGetFinancingGiverOrgOverridesQuery.rootKey = "GetFinancingGiverOrgOverrides"

export const useInfiniteGetFinancingGiverOrgOverridesQuery = <
  TData = InfiniteData<Types.GetFinancingGiverOrgOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetFinancingGiverOrgOverrides.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetFinancingGiverOrgOverridesQuery,
            Types.GetFinancingGiverOrgOverridesQueryVariables
          >(GetFinancingGiverOrgOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides.infinite", variables]

useInfiniteGetFinancingGiverOrgOverridesQuery.rootKey =
  "GetFinancingGiverOrgOverrides.infinite"

export const useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery = <
  TData = InfiniteData<Types.GetFinancingGiverOrgOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.GetFinancingGiverOrgOverridesQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetFinancingGiverOrgOverrides.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetFinancingGiverOrgOverridesQuery,
            Types.GetFinancingGiverOrgOverridesQueryVariables
          >(GetFinancingGiverOrgOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides.infinite", variables]

useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery.rootKey =
  "GetFinancingGiverOrgOverrides.infinite"

useGetFinancingGiverOrgOverridesQuery.fetcher = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GetFinancingGiverOrgOverridesQuery,
    Types.GetFinancingGiverOrgOverridesQueryVariables
  >(GetFinancingGiverOrgOverridesDocument, variables, options)

export const FinproductOverridesDocument = new TypedDocumentString(`
    query FinproductOverrides($finProductId: Int!) {
  admin {
    finproduct(finproductId: $finProductId) {
      id
      finmodelInputOverrides {
        ...Override
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}`)

export const useFinproductOverridesQuery = <
  TData = Types.FinproductOverridesQuery,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinproductOverridesQuery, TError, TData>({
    queryKey: ["FinproductOverrides", variables],
    queryFn: publicFetcher<
      Types.FinproductOverridesQuery,
      Types.FinproductOverridesQueryVariables
    >(FinproductOverridesDocument, variables),
    ...options,
  })
}

useFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides", variables]

useFinproductOverridesQuery.rootKey = "FinproductOverrides"

export const useSuspenseFinproductOverridesQuery = <
  TData = Types.FinproductOverridesQuery,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinproductOverridesQuery, TError, TData>({
    queryKey: ["FinproductOverrides", variables],
    queryFn: publicFetcher<
      Types.FinproductOverridesQuery,
      Types.FinproductOverridesQueryVariables
    >(FinproductOverridesDocument, variables),
    ...options,
  })
}

useSuspenseFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides", variables]

useSuspenseFinproductOverridesQuery.rootKey = "FinproductOverrides"

export const useInfiniteFinproductOverridesQuery = <
  TData = InfiniteData<Types.FinproductOverridesQuery>,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinproductOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinproductOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinproductOverridesQuery,
            Types.FinproductOverridesQueryVariables
          >(FinproductOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides.infinite", variables]

useInfiniteFinproductOverridesQuery.rootKey = "FinproductOverrides.infinite"

export const useSuspenseInfiniteFinproductOverridesQuery = <
  TData = InfiniteData<Types.FinproductOverridesQuery>,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinproductOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinproductOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinproductOverridesQuery,
            Types.FinproductOverridesQueryVariables
          >(FinproductOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides.infinite", variables]

useSuspenseInfiniteFinproductOverridesQuery.rootKey = "FinproductOverrides.infinite"

useFinproductOverridesQuery.fetcher = (
  variables: Types.FinproductOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinproductOverridesQuery,
    Types.FinproductOverridesQueryVariables
  >(FinproductOverridesDocument, variables, options)

export const SiteUtilityDocument = new TypedDocumentString(`
    query SiteUtility($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      zipCode
      thirdPartySupplier {
        id
        name
      }
      targetUtility {
        id
        name
        shortName
      }
      targetUtilitySetBy {
        ...UserRef
      }
      dateTargetUtilitySet
      utilityName
      probableUtilities {
        id
        name
      }
      isMultifamily
      ...Metering
      customTariffs {
        id
        name
        code
        parent {
          id
        }
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment Metering on Site {
  metering
  meteringSetDate
  meteringSetBy {
    ...UserRef
  }
}`)

export const useSiteUtilityQuery = <TData = Types.SiteUtilityQuery, TError = unknown>(
  variables: Types.SiteUtilityQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteUtilityQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteUtilityQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteUtilityQuery, TError, TData>({
    queryKey: ["SiteUtility", variables],
    queryFn: publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
      SiteUtilityDocument,
      variables,
    ),
    ...options,
  })
}

useSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtility",
  variables,
]

useSiteUtilityQuery.rootKey = "SiteUtility"

export const useSuspenseSiteUtilityQuery = <
  TData = Types.SiteUtilityQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteUtilityQuery, TError, TData>({
    queryKey: ["SiteUtility", variables],
    queryFn: publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
      SiteUtilityDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtility",
  variables,
]

useSuspenseSiteUtilityQuery.rootKey = "SiteUtility"

export const useInfiniteSiteUtilityQuery = <
  TData = InfiniteData<Types.SiteUtilityQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteUtilityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtility.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
            SiteUtilityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtility.infinite",
  variables,
]

useInfiniteSiteUtilityQuery.rootKey = "SiteUtility.infinite"

export const useSuspenseInfiniteSiteUtilityQuery = <
  TData = InfiniteData<Types.SiteUtilityQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteUtilityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtility.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
            SiteUtilityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteUtilityQuery.getKey = (
  variables: Types.SiteUtilityQueryVariables,
) => ["SiteUtility.infinite", variables]

useSuspenseInfiniteSiteUtilityQuery.rootKey = "SiteUtility.infinite"

useSiteUtilityQuery.fetcher = (
  variables: Types.SiteUtilityQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
    SiteUtilityDocument,
    variables,
    options,
  )

export const UtilitySearchDocument = new TypedDocumentString(`
    query UtilitySearch($name: String!) {
  utilitiesByName(name: $name) {
    __typename
    ... on UtilityList {
      refs {
        id
        name
      }
    }
    ... on Error {
      message
    }
  }
}
    `)

export const useUtilitySearchQuery = <
  TData = Types.UtilitySearchQuery,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilitySearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilitySearchQuery, TError, TData>({
    queryKey: ["UtilitySearch", variables],
    queryFn: publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
      UtilitySearchDocument,
      variables,
    ),
    ...options,
  })
}

useUtilitySearchQuery.getKey = (variables: Types.UtilitySearchQueryVariables) => [
  "UtilitySearch",
  variables,
]

useUtilitySearchQuery.rootKey = "UtilitySearch"

export const useSuspenseUtilitySearchQuery = <
  TData = Types.UtilitySearchQuery,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilitySearchQuery, TError, TData>({
    queryKey: ["UtilitySearch", variables],
    queryFn: publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
      UtilitySearchDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearch", variables]

useSuspenseUtilitySearchQuery.rootKey = "UtilitySearch"

export const useInfiniteUtilitySearchQuery = <
  TData = InfiniteData<Types.UtilitySearchQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilitySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilitySearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
            UtilitySearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearch.infinite", variables]

useInfiniteUtilitySearchQuery.rootKey = "UtilitySearch.infinite"

export const useSuspenseInfiniteUtilitySearchQuery = <
  TData = InfiniteData<Types.UtilitySearchQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilitySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilitySearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
            UtilitySearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearch.infinite", variables]

useSuspenseInfiniteUtilitySearchQuery.rootKey = "UtilitySearch.infinite"

useUtilitySearchQuery.fetcher = (
  variables: Types.UtilitySearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
    UtilitySearchDocument,
    variables,
    options,
  )

export const TariffRateDetailDocument = new TypedDocumentString(`
    query TariffRateDetail($sitePid: String!, $tariffParentId: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      tariffDetail(tariffParentId: $tariffParentId) {
        ...TariffDetailFragment
      }
    }
  }
}
    fragment RateTableFragment on Rate {
  tariffRateId
  name
  chargeClass
  chargeType
  chargePeriod
  transactionType
  applicabilityKey
  quantityKey
  variableLimitKey
  variableRateKey
  variableFactorKey
  isTou
  bands {
    tariffRateBandId
    rateUnit
    price
    isCredit
    hasConsumptionLimit
    consumptionUpperLimit
    demandUpperLimit
    propertyUpperLimit
    calculationFactor
    applicabilityValue
  }
  season {
    seasonFromMonth
    seasonToMonth
    seasonFromDay
    seasonToDay
  }
  tou {
    touName
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    touPeriods {
      fromDayOfWeek
      toDayOfWeek
      fromHour
      toHour
      fromMinute
      toMinute
    }
  }
}
fragment TariffDetailFragment on TariffDetails {
  tariff {
    id
    code
    name
    customerClass
  }
  version {
    tariffId
    effectiveDate
    endDate
  }
  properties {
    ...RevisionProperty
  }
  rawJson
  rates {
    ...RateTableFragment
  }
}
fragment RevisionProperty on TariffProperty {
  keyName
  period
  displayName
  keyspace
  family
  description
  dataType
  propertyTypes
  operator
  propertyValue
  minValue
  maxValue
  choices {
    displayValue
    value
    dataValue
    likelihood
  }
  propertyValues
  formulaDetail
  isDefault
  quantityKey
}`)

export const useTariffRateDetailQuery = <
  TData = Types.TariffRateDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TariffRateDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffRateDetailQuery, TError, TData>({
    queryKey: ["TariffRateDetail", variables],
    queryFn: publicFetcher<
      Types.TariffRateDetailQuery,
      Types.TariffRateDetailQueryVariables
    >(TariffRateDetailDocument, variables),
    ...options,
  })
}

useTariffRateDetailQuery.getKey = (variables: Types.TariffRateDetailQueryVariables) => [
  "TariffRateDetail",
  variables,
]

useTariffRateDetailQuery.rootKey = "TariffRateDetail"

export const useSuspenseTariffRateDetailQuery = <
  TData = Types.TariffRateDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffRateDetailQuery, TError, TData>({
    queryKey: ["TariffRateDetail", variables],
    queryFn: publicFetcher<
      Types.TariffRateDetailQuery,
      Types.TariffRateDetailQueryVariables
    >(TariffRateDetailDocument, variables),
    ...options,
  })
}

useSuspenseTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetail", variables]

useSuspenseTariffRateDetailQuery.rootKey = "TariffRateDetail"

export const useInfiniteTariffRateDetailQuery = <
  TData = InfiniteData<Types.TariffRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRateDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.TariffRateDetailQuery,
            Types.TariffRateDetailQueryVariables
          >(TariffRateDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetail.infinite", variables]

useInfiniteTariffRateDetailQuery.rootKey = "TariffRateDetail.infinite"

export const useSuspenseInfiniteTariffRateDetailQuery = <
  TData = InfiniteData<Types.TariffRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRateDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.TariffRateDetailQuery,
            Types.TariffRateDetailQueryVariables
          >(TariffRateDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetail.infinite", variables]

useSuspenseInfiniteTariffRateDetailQuery.rootKey = "TariffRateDetail.infinite"

useTariffRateDetailQuery.fetcher = (
  variables: Types.TariffRateDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffRateDetailQuery, Types.TariffRateDetailQueryVariables>(
    TariffRateDetailDocument,
    variables,
    options,
  )

export const AdminSearchDocument = new TypedDocumentString(`
    query AdminSearch($q: String!) {
  admin {
    search(q: $q) {
      id
      pid
      name
      fullAddress
      portfolio {
        pid
        title
      }
    }
  }
}
    `)

export const useAdminSearchQuery = <TData = Types.AdminSearchQuery, TError = unknown>(
  variables: Types.AdminSearchQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminSearchQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminSearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminSearchQuery, TError, TData>({
    queryKey: ["AdminSearch", variables],
    queryFn: publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
      AdminSearchDocument,
      variables,
    ),
    ...options,
  })
}

useAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearch",
  variables,
]

useAdminSearchQuery.rootKey = "AdminSearch"

export const useSuspenseAdminSearchQuery = <
  TData = Types.AdminSearchQuery,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSearchQuery, TError, TData>({
    queryKey: ["AdminSearch", variables],
    queryFn: publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
      AdminSearchDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearch",
  variables,
]

useSuspenseAdminSearchQuery.rootKey = "AdminSearch"

export const useInfiniteAdminSearchQuery = <
  TData = InfiniteData<Types.AdminSearchQuery>,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
            AdminSearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearch.infinite",
  variables,
]

useInfiniteAdminSearchQuery.rootKey = "AdminSearch.infinite"

export const useSuspenseInfiniteAdminSearchQuery = <
  TData = InfiniteData<Types.AdminSearchQuery>,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
            AdminSearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSearchQuery.getKey = (
  variables: Types.AdminSearchQueryVariables,
) => ["AdminSearch.infinite", variables]

useSuspenseInfiniteAdminSearchQuery.rootKey = "AdminSearch.infinite"

useAdminSearchQuery.fetcher = (
  variables: Types.AdminSearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
    AdminSearchDocument,
    variables,
    options,
  )

export const AdminScenarioListDocument = new TypedDocumentString(`
    query AdminScenarioList($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      scenarios {
        ...ScenarioFragment
      }
    }
  }
}
    fragment SmallScenarioFragment on AdminScenario {
  id
  pid
  name
  dateCreated
  includeStorage
  isPrimary
  isMultifamily
  loadCategory
  solarLocation
  createdBy {
    ...UserRef
  }
  hiddenFromCustomers
  allowMulticonnect
}
fragment ScenarioFragment on AdminScenario {
  ...SmallScenarioFragment
  activeEtbReport {
    id
    projectName
  }
  helioscope {
    id
    pid
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAdminScenarioListQuery = <
  TData = Types.AdminScenarioListQuery,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminScenarioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminScenarioListQuery, TError, TData>({
    queryKey: ["AdminScenarioList", variables],
    queryFn: publicFetcher<
      Types.AdminScenarioListQuery,
      Types.AdminScenarioListQueryVariables
    >(AdminScenarioListDocument, variables),
    ...options,
  })
}

useAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioList", variables]

useAdminScenarioListQuery.rootKey = "AdminScenarioList"

export const useSuspenseAdminScenarioListQuery = <
  TData = Types.AdminScenarioListQuery,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminScenarioListQuery, TError, TData>({
    queryKey: ["AdminScenarioList", variables],
    queryFn: publicFetcher<
      Types.AdminScenarioListQuery,
      Types.AdminScenarioListQueryVariables
    >(AdminScenarioListDocument, variables),
    ...options,
  })
}

useSuspenseAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioList", variables]

useSuspenseAdminScenarioListQuery.rootKey = "AdminScenarioList"

export const useInfiniteAdminScenarioListQuery = <
  TData = InfiniteData<Types.AdminScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminScenarioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminScenarioListQuery,
            Types.AdminScenarioListQueryVariables
          >(AdminScenarioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioList.infinite", variables]

useInfiniteAdminScenarioListQuery.rootKey = "AdminScenarioList.infinite"

export const useSuspenseInfiniteAdminScenarioListQuery = <
  TData = InfiniteData<Types.AdminScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminScenarioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminScenarioListQuery,
            Types.AdminScenarioListQueryVariables
          >(AdminScenarioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioList.infinite", variables]

useSuspenseInfiniteAdminScenarioListQuery.rootKey = "AdminScenarioList.infinite"

useAdminScenarioListQuery.fetcher = (
  variables: Types.AdminScenarioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminScenarioListQuery, Types.AdminScenarioListQueryVariables>(
    AdminScenarioListDocument,
    variables,
    options,
  )

export const ScenarioDetailDocument = new TypedDocumentString(`
    query ScenarioDetail($sitePid: String!, $scenarioPid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      attachments {
        ...AttachmentFragment
      }
      helioscopes {
        ...ScenarioHelioscopeFragment
      }
      etbReports {
        ...ScenarioEtbFragment
      }
      scenario(pid: $scenarioPid) {
        ...ScenarioFragment
        etbs {
          id
          projectName
          dateCreated
        }
        configs {
          id
          createdAt
          finproduct {
            __typename
            financier {
              pid
              name
            }
            id
            createdAt
            name
            type
          }
          finproductType
          goal
          roofKw
          carportKw
          groundKw
          ignorePhysicalBounds
          battery {
            sizeKw
            sizeKwh
            vendor
            equipmentCost
            laborCost
          }
          helioscope {
            ...ScenarioHelioscopeFragment
          }
          etb {
            ...ScenarioEtbFragment
          }
        }
      }
    }
  }
}
    fragment SmallScenarioFragment on AdminScenario {
  id
  pid
  name
  dateCreated
  includeStorage
  isPrimary
  isMultifamily
  loadCategory
  solarLocation
  createdBy {
    ...UserRef
  }
  hiddenFromCustomers
  allowMulticonnect
}
fragment ScenarioFragment on AdminScenario {
  ...SmallScenarioFragment
  activeEtbReport {
    id
    projectName
  }
  helioscope {
    id
    pid
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}
fragment ScenarioHelioscopeFragment on Helioscope {
  id
  pid
  createdAt
  isTrashed
  roofPvKw
  carportPvKw
  pdf {
    ...AttachmentFragment
  }
  roofImage {
    ...AttachmentFragment
  }
}
fragment ScenarioEtbFragment on ETBReport {
  id
  dateCreated
  projectName
  roofPvKw
  carportPvKw
  groundPvKw
  batteryKw
  batteryKwh
}`)

export const useScenarioDetailQuery = <
  TData = Types.ScenarioDetailQuery,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ScenarioDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ScenarioDetailQuery, TError, TData>({
    queryKey: ["ScenarioDetail", variables],
    queryFn: publicFetcher<
      Types.ScenarioDetailQuery,
      Types.ScenarioDetailQueryVariables
    >(ScenarioDetailDocument, variables),
    ...options,
  })
}

useScenarioDetailQuery.getKey = (variables: Types.ScenarioDetailQueryVariables) => [
  "ScenarioDetail",
  variables,
]

useScenarioDetailQuery.rootKey = "ScenarioDetail"

export const useSuspenseScenarioDetailQuery = <
  TData = Types.ScenarioDetailQuery,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ScenarioDetailQuery, TError, TData>({
    queryKey: ["ScenarioDetail", variables],
    queryFn: publicFetcher<
      Types.ScenarioDetailQuery,
      Types.ScenarioDetailQueryVariables
    >(ScenarioDetailDocument, variables),
    ...options,
  })
}

useSuspenseScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetail", variables]

useSuspenseScenarioDetailQuery.rootKey = "ScenarioDetail"

export const useInfiniteScenarioDetailQuery = <
  TData = InfiniteData<Types.ScenarioDetailQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ScenarioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
            ScenarioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetail.infinite", variables]

useInfiniteScenarioDetailQuery.rootKey = "ScenarioDetail.infinite"

export const useSuspenseInfiniteScenarioDetailQuery = <
  TData = InfiniteData<Types.ScenarioDetailQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ScenarioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
            ScenarioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetail.infinite", variables]

useSuspenseInfiniteScenarioDetailQuery.rootKey = "ScenarioDetail.infinite"

useScenarioDetailQuery.fetcher = (
  variables: Types.ScenarioDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
    ScenarioDetailDocument,
    variables,
    options,
  )

export const SiteChangelogDocument = new TypedDocumentString(`
    query SiteChangelog($sitePid: String!) {
  admin {
    changelogs {
      site(sitePid: $sitePid) {
        id
        tableName
        transactionId
        dateCreated
        columnName
        oldVal
        newVal
        valueRedacted
        changedBy {
          ...UserRef
        }
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useSiteChangelogQuery = <
  TData = Types.SiteChangelogQuery,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteChangelogQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteChangelogQuery, TError, TData>({
    queryKey: ["SiteChangelog", variables],
    queryFn: publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
      SiteChangelogDocument,
      variables,
    ),
    ...options,
  })
}

useSiteChangelogQuery.getKey = (variables: Types.SiteChangelogQueryVariables) => [
  "SiteChangelog",
  variables,
]

useSiteChangelogQuery.rootKey = "SiteChangelog"

export const useSuspenseSiteChangelogQuery = <
  TData = Types.SiteChangelogQuery,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteChangelogQuery, TError, TData>({
    queryKey: ["SiteChangelog", variables],
    queryFn: publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
      SiteChangelogDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelog", variables]

useSuspenseSiteChangelogQuery.rootKey = "SiteChangelog"

export const useInfiniteSiteChangelogQuery = <
  TData = InfiniteData<Types.SiteChangelogQuery>,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteChangelogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteChangelog.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
            SiteChangelogDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelog.infinite", variables]

useInfiniteSiteChangelogQuery.rootKey = "SiteChangelog.infinite"

export const useSuspenseInfiniteSiteChangelogQuery = <
  TData = InfiniteData<Types.SiteChangelogQuery>,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteChangelogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteChangelog.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
            SiteChangelogDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelog.infinite", variables]

useSuspenseInfiniteSiteChangelogQuery.rootKey = "SiteChangelog.infinite"

useSiteChangelogQuery.fetcher = (
  variables: Types.SiteChangelogQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
    SiteChangelogDocument,
    variables,
    options,
  )

export const SiteStatusCheckDocument = new TypedDocumentString(`
    query SiteStatusCheck($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      statusCheck {
        desc
        hint
        status
      }
    }
  }
}
    `)

export const useSiteStatusCheckQuery = <
  TData = Types.SiteStatusCheckQuery,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteStatusCheckQuery, TError, TData>({
    queryKey: ["SiteStatusCheck", variables],
    queryFn: publicFetcher<
      Types.SiteStatusCheckQuery,
      Types.SiteStatusCheckQueryVariables
    >(SiteStatusCheckDocument, variables),
    ...options,
  })
}

useSiteStatusCheckQuery.getKey = (variables: Types.SiteStatusCheckQueryVariables) => [
  "SiteStatusCheck",
  variables,
]

useSiteStatusCheckQuery.rootKey = "SiteStatusCheck"

export const useSuspenseSiteStatusCheckQuery = <
  TData = Types.SiteStatusCheckQuery,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteStatusCheckQuery, TError, TData>({
    queryKey: ["SiteStatusCheck", variables],
    queryFn: publicFetcher<
      Types.SiteStatusCheckQuery,
      Types.SiteStatusCheckQueryVariables
    >(SiteStatusCheckDocument, variables),
    ...options,
  })
}

useSuspenseSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheck", variables]

useSuspenseSiteStatusCheckQuery.rootKey = "SiteStatusCheck"

export const useInfiniteSiteStatusCheckQuery = <
  TData = InfiniteData<Types.SiteStatusCheckQuery>,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteStatusCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteStatusCheck.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteStatusCheckQuery,
            Types.SiteStatusCheckQueryVariables
          >(SiteStatusCheckDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheck.infinite", variables]

useInfiniteSiteStatusCheckQuery.rootKey = "SiteStatusCheck.infinite"

export const useSuspenseInfiniteSiteStatusCheckQuery = <
  TData = InfiniteData<Types.SiteStatusCheckQuery>,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteStatusCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteStatusCheck.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteStatusCheckQuery,
            Types.SiteStatusCheckQueryVariables
          >(SiteStatusCheckDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheck.infinite", variables]

useSuspenseInfiniteSiteStatusCheckQuery.rootKey = "SiteStatusCheck.infinite"

useSiteStatusCheckQuery.fetcher = (
  variables: Types.SiteStatusCheckQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteStatusCheckQuery, Types.SiteStatusCheckQueryVariables>(
    SiteStatusCheckDocument,
    variables,
    options,
  )

export const FailuresDocument = new TypedDocumentString(`
    query Failures($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      failures {
        __typename
        ... on FinmodelFailure {
          id
          createdAt
          name
          message
          scenario {
            ...ScenarioRef
          }
        }
        ... on GuessTariffFailure {
          createdAt
          name
          message
        }
        ... on BuildingFootprintFailure {
          createdAt
          name
          message
        }
        ... on LoadEstimateFailure {
          createdAt: dateCreated
          errorClass
          errorMessage
        }
        ... on ParcelDataFailure {
          createdAt
          name
          message
        }
      }
    }
  }
}
    fragment ScenarioRef on ScenarioRef {
  pid
  name
}`)

export const useFailuresQuery = <TData = Types.FailuresQuery, TError = unknown>(
  variables: Types.FailuresQueryVariables,
  options?: Omit<UseQueryOptions<Types.FailuresQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FailuresQuery, TError, TData>({
    queryKey: ["Failures", variables],
    queryFn: publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
      FailuresDocument,
      variables,
    ),
    ...options,
  })
}

useFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures",
  variables,
]

useFailuresQuery.rootKey = "Failures"

export const useSuspenseFailuresQuery = <TData = Types.FailuresQuery, TError = unknown>(
  variables: Types.FailuresQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FailuresQuery, TError, TData>({
    queryKey: ["Failures", variables],
    queryFn: publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
      FailuresDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures",
  variables,
]

useSuspenseFailuresQuery.rootKey = "Failures"

export const useInfiniteFailuresQuery = <
  TData = InfiniteData<Types.FailuresQuery>,
  TError = unknown,
>(
  variables: Types.FailuresQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FailuresQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Failures.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
            FailuresDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures.infinite",
  variables,
]

useInfiniteFailuresQuery.rootKey = "Failures.infinite"

export const useSuspenseInfiniteFailuresQuery = <
  TData = InfiniteData<Types.FailuresQuery>,
  TError = unknown,
>(
  variables: Types.FailuresQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FailuresQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FailuresQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Failures.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
            FailuresDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures.infinite",
  variables,
]

useSuspenseInfiniteFailuresQuery.rootKey = "Failures.infinite"

useFailuresQuery.fetcher = (
  variables: Types.FailuresQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
    FailuresDocument,
    variables,
    options,
  )

export const OrgsDocument = new TypedDocumentString(`
    query Orgs {
  admin {
    orgs {
      pid
      name
    }
  }
}
    `)

export const useOrgsQuery = <TData = Types.OrgsQuery, TError = unknown>(
  variables?: Types.OrgsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Orgs"] : ["Orgs", variables],
    queryFn: publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
      OrgsDocument,
      variables,
    ),
    ...options,
  })
}

useOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs"] : ["Orgs", variables]

useOrgsQuery.rootKey = "Orgs"

export const useSuspenseOrgsQuery = <TData = Types.OrgsQuery, TError = unknown>(
  variables?: Types.OrgsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Orgs"] : ["Orgs", variables],
    queryFn: publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
      OrgsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs"] : ["Orgs", variables]

useSuspenseOrgsQuery.rootKey = "Orgs"

export const useInfiniteOrgsQuery = <
  TData = InfiniteData<Types.OrgsQuery>,
  TError = unknown,
>(
  variables: Types.OrgsQueryVariables,
  options: Omit<UseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Orgs.infinite"]
            : ["Orgs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(OrgsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs.infinite"] : ["Orgs.infinite", variables]

useInfiniteOrgsQuery.rootKey = "Orgs.infinite"

export const useSuspenseInfiniteOrgsQuery = <
  TData = InfiniteData<Types.OrgsQuery>,
  TError = unknown,
>(
  variables: Types.OrgsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Orgs.infinite"]
            : ["Orgs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(OrgsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs.infinite"] : ["Orgs.infinite", variables]

useSuspenseInfiniteOrgsQuery.rootKey = "Orgs.infinite"

useOrgsQuery.fetcher = (
  variables?: Types.OrgsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
    OrgsDocument,
    variables,
    options,
  )

export const OrgsDetailListDocument = new TypedDocumentString(`
    query OrgsDetailList {
  admin {
    orgs {
      ...BasicOrgDetails
    }
    financierOrgs {
      ...BasicOrgDetails
    }
  }
}
    fragment BasicOrgDetails on AdminOrg {
  id
  pid
  name
  slug
  rawSlug
  description
  logoUrl
  logomarkUrl
  hubspotId
}`)

export const useOrgsDetailListQuery = <
  TData = Types.OrgsDetailListQuery,
  TError = unknown,
>(
  variables?: Types.OrgsDetailListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgsDetailListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsDetailListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables],
    queryFn: publicFetcher<
      Types.OrgsDetailListQuery,
      Types.OrgsDetailListQueryVariables
    >(OrgsDetailListDocument, variables),
    ...options,
  })
}

useOrgsDetailListQuery.getKey = (variables?: Types.OrgsDetailListQueryVariables) =>
  variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables]

useOrgsDetailListQuery.rootKey = "OrgsDetailList"

export const useSuspenseOrgsDetailListQuery = <
  TData = Types.OrgsDetailListQuery,
  TError = unknown,
>(
  variables?: Types.OrgsDetailListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsDetailListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables],
    queryFn: publicFetcher<
      Types.OrgsDetailListQuery,
      Types.OrgsDetailListQueryVariables
    >(OrgsDetailListDocument, variables),
    ...options,
  })
}

useSuspenseOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) => (variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables])

useSuspenseOrgsDetailListQuery.rootKey = "OrgsDetailList"

export const useInfiniteOrgsDetailListQuery = <
  TData = InfiniteData<Types.OrgsDetailListQuery>,
  TError = unknown,
>(
  variables: Types.OrgsDetailListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsDetailListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsDetailList.infinite"]
            : ["OrgsDetailList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
            OrgsDetailListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailList.infinite"]
    : ["OrgsDetailList.infinite", variables]

useInfiniteOrgsDetailListQuery.rootKey = "OrgsDetailList.infinite"

export const useSuspenseInfiniteOrgsDetailListQuery = <
  TData = InfiniteData<Types.OrgsDetailListQuery>,
  TError = unknown,
>(
  variables: Types.OrgsDetailListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsDetailListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsDetailList.infinite"]
            : ["OrgsDetailList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
            OrgsDetailListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailList.infinite"]
    : ["OrgsDetailList.infinite", variables]

useSuspenseInfiniteOrgsDetailListQuery.rootKey = "OrgsDetailList.infinite"

useOrgsDetailListQuery.fetcher = (
  variables?: Types.OrgsDetailListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
    OrgsDetailListDocument,
    variables,
    options,
  )

export const AdminSlackChannelsDocument = new TypedDocumentString(`
    query AdminSlackChannels {
  admin {
    slackChannels {
      pid
      channelName
      channelId
      note
      orgs {
        pid
        name
      }
    }
  }
}
    `)

export const useAdminSlackChannelsQuery = <
  TData = Types.AdminSlackChannelsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSlackChannelsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSlackChannelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminSlackChannelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminSlackChannelsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSlackChannels"]
        : ["AdminSlackChannels", variables],
    queryFn: publicFetcher<
      Types.AdminSlackChannelsQuery,
      Types.AdminSlackChannelsQueryVariables
    >(AdminSlackChannelsDocument, variables),
    ...options,
  })
}

useAdminSlackChannelsQuery.getKey = (
  variables?: Types.AdminSlackChannelsQueryVariables,
) =>
  variables === undefined ? ["AdminSlackChannels"] : ["AdminSlackChannels", variables]

useAdminSlackChannelsQuery.rootKey = "AdminSlackChannels"

export const useSuspenseAdminSlackChannelsQuery = <
  TData = Types.AdminSlackChannelsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSlackChannelsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSlackChannelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSlackChannelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSlackChannelsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSlackChannels"]
        : ["AdminSlackChannels", variables],
    queryFn: publicFetcher<
      Types.AdminSlackChannelsQuery,
      Types.AdminSlackChannelsQueryVariables
    >(AdminSlackChannelsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSlackChannelsQuery.getKey = (
  variables?: Types.AdminSlackChannelsQueryVariables,
) =>
  variables === undefined ? ["AdminSlackChannels"] : ["AdminSlackChannels", variables]

useSuspenseAdminSlackChannelsQuery.rootKey = "AdminSlackChannels"

export const useInfiniteAdminSlackChannelsQuery = <
  TData = InfiniteData<Types.AdminSlackChannelsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSlackChannelsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSlackChannelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSlackChannelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSlackChannelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSlackChannels.infinite"]
            : ["AdminSlackChannels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSlackChannelsQuery,
            Types.AdminSlackChannelsQueryVariables
          >(AdminSlackChannelsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSlackChannelsQuery.getKey = (
  variables?: Types.AdminSlackChannelsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSlackChannels.infinite"]
    : ["AdminSlackChannels.infinite", variables]

useInfiniteAdminSlackChannelsQuery.rootKey = "AdminSlackChannels.infinite"

export const useSuspenseInfiniteAdminSlackChannelsQuery = <
  TData = InfiniteData<Types.AdminSlackChannelsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSlackChannelsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSlackChannelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSlackChannelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSlackChannelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSlackChannels.infinite"]
            : ["AdminSlackChannels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSlackChannelsQuery,
            Types.AdminSlackChannelsQueryVariables
          >(AdminSlackChannelsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSlackChannelsQuery.getKey = (
  variables?: Types.AdminSlackChannelsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSlackChannels.infinite"]
    : ["AdminSlackChannels.infinite", variables]

useSuspenseInfiniteAdminSlackChannelsQuery.rootKey = "AdminSlackChannels.infinite"

useAdminSlackChannelsQuery.fetcher = (
  variables?: Types.AdminSlackChannelsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminSlackChannelsQuery, Types.AdminSlackChannelsQueryVariables>(
    AdminSlackChannelsDocument,
    variables,
    options,
  )

export const SiteIraIncentiveDataDocument = new TypedDocumentString(`
    query SiteIraIncentiveData($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      isLowIncomeCommunity
      isEnergyCommunity
    }
  }
}
    `)

export const useSiteIraIncentiveDataQuery = <
  TData = Types.SiteIraIncentiveDataQuery,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteIraIncentiveDataQuery, TError, TData>({
    queryKey: ["SiteIraIncentiveData", variables],
    queryFn: publicFetcher<
      Types.SiteIraIncentiveDataQuery,
      Types.SiteIraIncentiveDataQueryVariables
    >(SiteIraIncentiveDataDocument, variables),
    ...options,
  })
}

useSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData", variables]

useSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData"

export const useSuspenseSiteIraIncentiveDataQuery = <
  TData = Types.SiteIraIncentiveDataQuery,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteIraIncentiveDataQuery, TError, TData>({
    queryKey: ["SiteIraIncentiveData", variables],
    queryFn: publicFetcher<
      Types.SiteIraIncentiveDataQuery,
      Types.SiteIraIncentiveDataQueryVariables
    >(SiteIraIncentiveDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData", variables]

useSuspenseSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData"

export const useInfiniteSiteIraIncentiveDataQuery = <
  TData = InfiniteData<Types.SiteIraIncentiveDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteIraIncentiveDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIraIncentiveData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIraIncentiveDataQuery,
            Types.SiteIraIncentiveDataQueryVariables
          >(SiteIraIncentiveDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData.infinite", variables]

useInfiniteSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData.infinite"

export const useSuspenseInfiniteSiteIraIncentiveDataQuery = <
  TData = InfiniteData<Types.SiteIraIncentiveDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteIraIncentiveDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIraIncentiveData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIraIncentiveDataQuery,
            Types.SiteIraIncentiveDataQueryVariables
          >(SiteIraIncentiveDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData.infinite", variables]

useSuspenseInfiniteSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData.infinite"

useSiteIraIncentiveDataQuery.fetcher = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteIraIncentiveDataQuery,
    Types.SiteIraIncentiveDataQueryVariables
  >(SiteIraIncentiveDataDocument, variables, options)

export const SiteLeaseTypeDocument = new TypedDocumentString(`
    query SiteLeaseType($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      leaseType
    }
  }
}
    `)

export const useSiteLeaseTypeQuery = <
  TData = Types.SiteLeaseTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteLeaseTypeQuery, TError, TData>({
    queryKey: ["SiteLeaseType", variables],
    queryFn: publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
      SiteLeaseTypeDocument,
      variables,
    ),
    ...options,
  })
}

useSiteLeaseTypeQuery.getKey = (variables: Types.SiteLeaseTypeQueryVariables) => [
  "SiteLeaseType",
  variables,
]

useSiteLeaseTypeQuery.rootKey = "SiteLeaseType"

export const useSuspenseSiteLeaseTypeQuery = <
  TData = Types.SiteLeaseTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteLeaseTypeQuery, TError, TData>({
    queryKey: ["SiteLeaseType", variables],
    queryFn: publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
      SiteLeaseTypeDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseType", variables]

useSuspenseSiteLeaseTypeQuery.rootKey = "SiteLeaseType"

export const useInfiniteSiteLeaseTypeQuery = <
  TData = InfiniteData<Types.SiteLeaseTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteLeaseTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLeaseType.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
            SiteLeaseTypeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseType.infinite", variables]

useInfiniteSiteLeaseTypeQuery.rootKey = "SiteLeaseType.infinite"

export const useSuspenseInfiniteSiteLeaseTypeQuery = <
  TData = InfiniteData<Types.SiteLeaseTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteLeaseTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLeaseType.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
            SiteLeaseTypeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseType.infinite", variables]

useSuspenseInfiniteSiteLeaseTypeQuery.rootKey = "SiteLeaseType.infinite"

useSiteLeaseTypeQuery.fetcher = (
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
    SiteLeaseTypeDocument,
    variables,
    options,
  )

export const SiteOccupancyTypeDocument = new TypedDocumentString(`
    query SiteOccupancyType($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      occupancyType
    }
  }
}
    `)

export const useSiteOccupancyTypeQuery = <
  TData = Types.SiteOccupancyTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteOccupancyTypeQuery, TError, TData>({
    queryKey: ["SiteOccupancyType", variables],
    queryFn: publicFetcher<
      Types.SiteOccupancyTypeQuery,
      Types.SiteOccupancyTypeQueryVariables
    >(SiteOccupancyTypeDocument, variables),
    ...options,
  })
}

useSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType", variables]

useSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType"

export const useSuspenseSiteOccupancyTypeQuery = <
  TData = Types.SiteOccupancyTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteOccupancyTypeQuery, TError, TData>({
    queryKey: ["SiteOccupancyType", variables],
    queryFn: publicFetcher<
      Types.SiteOccupancyTypeQuery,
      Types.SiteOccupancyTypeQueryVariables
    >(SiteOccupancyTypeDocument, variables),
    ...options,
  })
}

useSuspenseSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType", variables]

useSuspenseSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType"

export const useInfiniteSiteOccupancyTypeQuery = <
  TData = InfiniteData<Types.SiteOccupancyTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteOccupancyTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteOccupancyType.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteOccupancyTypeQuery,
            Types.SiteOccupancyTypeQueryVariables
          >(SiteOccupancyTypeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType.infinite", variables]

useInfiniteSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType.infinite"

export const useSuspenseInfiniteSiteOccupancyTypeQuery = <
  TData = InfiniteData<Types.SiteOccupancyTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteOccupancyTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteOccupancyType.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteOccupancyTypeQuery,
            Types.SiteOccupancyTypeQueryVariables
          >(SiteOccupancyTypeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType.infinite", variables]

useSuspenseInfiniteSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType.infinite"

useSiteOccupancyTypeQuery.fetcher = (
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteOccupancyTypeQuery, Types.SiteOccupancyTypeQueryVariables>(
    SiteOccupancyTypeDocument,
    variables,
    options,
  )

export const SiteShareSavingsDocument = new TypedDocumentString(`
    query SiteShareSavings($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      shareSavings
    }
  }
}
    `)

export const useSiteShareSavingsQuery = <
  TData = Types.SiteShareSavingsQuery,
  TError = unknown,
>(
  variables: Types.SiteShareSavingsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteShareSavingsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteShareSavingsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteShareSavingsQuery, TError, TData>({
    queryKey: ["SiteShareSavings", variables],
    queryFn: publicFetcher<
      Types.SiteShareSavingsQuery,
      Types.SiteShareSavingsQueryVariables
    >(SiteShareSavingsDocument, variables),
    ...options,
  })
}

useSiteShareSavingsQuery.getKey = (variables: Types.SiteShareSavingsQueryVariables) => [
  "SiteShareSavings",
  variables,
]

useSiteShareSavingsQuery.rootKey = "SiteShareSavings"

export const useSuspenseSiteShareSavingsQuery = <
  TData = Types.SiteShareSavingsQuery,
  TError = unknown,
>(
  variables: Types.SiteShareSavingsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteShareSavingsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteShareSavingsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteShareSavingsQuery, TError, TData>({
    queryKey: ["SiteShareSavings", variables],
    queryFn: publicFetcher<
      Types.SiteShareSavingsQuery,
      Types.SiteShareSavingsQueryVariables
    >(SiteShareSavingsDocument, variables),
    ...options,
  })
}

useSuspenseSiteShareSavingsQuery.getKey = (
  variables: Types.SiteShareSavingsQueryVariables,
) => ["SiteShareSavings", variables]

useSuspenseSiteShareSavingsQuery.rootKey = "SiteShareSavings"

export const useInfiniteSiteShareSavingsQuery = <
  TData = InfiniteData<Types.SiteShareSavingsQuery>,
  TError = unknown,
>(
  variables: Types.SiteShareSavingsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteShareSavingsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteShareSavingsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteShareSavingsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteShareSavings.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteShareSavingsQuery,
            Types.SiteShareSavingsQueryVariables
          >(SiteShareSavingsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteShareSavingsQuery.getKey = (
  variables: Types.SiteShareSavingsQueryVariables,
) => ["SiteShareSavings.infinite", variables]

useInfiniteSiteShareSavingsQuery.rootKey = "SiteShareSavings.infinite"

export const useSuspenseInfiniteSiteShareSavingsQuery = <
  TData = InfiniteData<Types.SiteShareSavingsQuery>,
  TError = unknown,
>(
  variables: Types.SiteShareSavingsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteShareSavingsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteShareSavingsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteShareSavingsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteShareSavings.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteShareSavingsQuery,
            Types.SiteShareSavingsQueryVariables
          >(SiteShareSavingsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteShareSavingsQuery.getKey = (
  variables: Types.SiteShareSavingsQueryVariables,
) => ["SiteShareSavings.infinite", variables]

useSuspenseInfiniteSiteShareSavingsQuery.rootKey = "SiteShareSavings.infinite"

useSiteShareSavingsQuery.fetcher = (
  variables: Types.SiteShareSavingsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteShareSavingsQuery, Types.SiteShareSavingsQueryVariables>(
    SiteShareSavingsDocument,
    variables,
    options,
  )

export const AdminOrgDetailDocument = new TypedDocumentString(`
    query AdminOrgDetail($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      ...BasicOrgDetails
      portfolios {
        pid
        title
      }
      allowedEmailDomains
      isDeveloper
      isInternal
      isChannelPartner
      hasUsers
      InvestorProfile {
        platformStatus
        osaVariant
      }
      labels {
        pid
        name
        themeColor
        isGlobal
      }
      slackChannels {
        pid
        channelName
        channelId
        note
      }
      financing {
        ...FinRel
      }
    }
  }
}
    fragment BasicOrgDetails on AdminOrg {
  id
  pid
  name
  slug
  rawSlug
  description
  logoUrl
  logomarkUrl
  hubspotId
}
fragment FinRel on AdminFinRel {
  disabledAt
  finproduct {
    id
    type
    name
    financier {
      name
      pid
    }
  }
  recvr {
    pid
  }
}`)

export const useAdminOrgDetailQuery = <
  TData = Types.AdminOrgDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminOrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgDetailQuery, TError, TData>({
    queryKey: ["AdminOrgDetail", variables],
    queryFn: publicFetcher<
      Types.AdminOrgDetailQuery,
      Types.AdminOrgDetailQueryVariables
    >(AdminOrgDetailDocument, variables),
    ...options,
  })
}

useAdminOrgDetailQuery.getKey = (variables: Types.AdminOrgDetailQueryVariables) => [
  "AdminOrgDetail",
  variables,
]

useAdminOrgDetailQuery.rootKey = "AdminOrgDetail"

export const useSuspenseAdminOrgDetailQuery = <
  TData = Types.AdminOrgDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgDetailQuery, TError, TData>({
    queryKey: ["AdminOrgDetail", variables],
    queryFn: publicFetcher<
      Types.AdminOrgDetailQuery,
      Types.AdminOrgDetailQueryVariables
    >(AdminOrgDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgDetailQuery.getKey = (
  variables: Types.AdminOrgDetailQueryVariables,
) => ["AdminOrgDetail", variables]

useSuspenseAdminOrgDetailQuery.rootKey = "AdminOrgDetail"

export const useInfiniteAdminOrgDetailQuery = <
  TData = InfiniteData<Types.AdminOrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgDetailQuery, Types.AdminOrgDetailQueryVariables>(
            AdminOrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgDetailQuery.getKey = (
  variables: Types.AdminOrgDetailQueryVariables,
) => ["AdminOrgDetail.infinite", variables]

useInfiniteAdminOrgDetailQuery.rootKey = "AdminOrgDetail.infinite"

export const useSuspenseInfiniteAdminOrgDetailQuery = <
  TData = InfiniteData<Types.AdminOrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgDetailQuery, Types.AdminOrgDetailQueryVariables>(
            AdminOrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgDetailQuery.getKey = (
  variables: Types.AdminOrgDetailQueryVariables,
) => ["AdminOrgDetail.infinite", variables]

useSuspenseInfiniteAdminOrgDetailQuery.rootKey = "AdminOrgDetail.infinite"

useAdminOrgDetailQuery.fetcher = (
  variables: Types.AdminOrgDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminOrgDetailQuery, Types.AdminOrgDetailQueryVariables>(
    AdminOrgDetailDocument,
    variables,
    options,
  )

export const AdminOrgFinproductsDocument = new TypedDocumentString(`
    query AdminOrgFinproducts($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      id
      pid
      finproducts {
        __typename
        financier {
          pid
          name
        }
        id
        createdAt
        name
        type
        activeOverrides {
          ...Override
        }
        targetOrg {
          pid
          name
        }
        targetSite {
          pid
          name
          streetAddress
        }
        disabledAt
        includeStorage
        status
        csPrices {
          utility {
            name
            id
            shortName
          }
          state
          usdPerKwPerYear
          escalator
          termYears
          minPvKw
          maxPvKw
          pvType
        }
        batteryPrices {
          id
          utility {
            id
            name
            shortName
          }
          maxMw
          usdPerKwhPerYear
          termYears
          escalator
          updatedAt
        }
      }
    }
  }
}
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}`)

export const useAdminOrgFinproductsQuery = <
  TData = Types.AdminOrgFinproductsQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgFinproductsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgFinproductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminOrgFinproductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgFinproductsQuery, TError, TData>({
    queryKey: ["AdminOrgFinproducts", variables],
    queryFn: publicFetcher<
      Types.AdminOrgFinproductsQuery,
      Types.AdminOrgFinproductsQueryVariables
    >(AdminOrgFinproductsDocument, variables),
    ...options,
  })
}

useAdminOrgFinproductsQuery.getKey = (
  variables: Types.AdminOrgFinproductsQueryVariables,
) => ["AdminOrgFinproducts", variables]

useAdminOrgFinproductsQuery.rootKey = "AdminOrgFinproducts"

export const useSuspenseAdminOrgFinproductsQuery = <
  TData = Types.AdminOrgFinproductsQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgFinproductsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgFinproductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgFinproductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgFinproductsQuery, TError, TData>({
    queryKey: ["AdminOrgFinproducts", variables],
    queryFn: publicFetcher<
      Types.AdminOrgFinproductsQuery,
      Types.AdminOrgFinproductsQueryVariables
    >(AdminOrgFinproductsDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgFinproductsQuery.getKey = (
  variables: Types.AdminOrgFinproductsQueryVariables,
) => ["AdminOrgFinproducts", variables]

useSuspenseAdminOrgFinproductsQuery.rootKey = "AdminOrgFinproducts"

export const useInfiniteAdminOrgFinproductsQuery = <
  TData = InfiniteData<Types.AdminOrgFinproductsQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgFinproductsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgFinproductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgFinproductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgFinproductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgFinproducts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminOrgFinproductsQuery,
            Types.AdminOrgFinproductsQueryVariables
          >(AdminOrgFinproductsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgFinproductsQuery.getKey = (
  variables: Types.AdminOrgFinproductsQueryVariables,
) => ["AdminOrgFinproducts.infinite", variables]

useInfiniteAdminOrgFinproductsQuery.rootKey = "AdminOrgFinproducts.infinite"

export const useSuspenseInfiniteAdminOrgFinproductsQuery = <
  TData = InfiniteData<Types.AdminOrgFinproductsQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgFinproductsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgFinproductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgFinproductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgFinproductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgFinproducts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminOrgFinproductsQuery,
            Types.AdminOrgFinproductsQueryVariables
          >(AdminOrgFinproductsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgFinproductsQuery.getKey = (
  variables: Types.AdminOrgFinproductsQueryVariables,
) => ["AdminOrgFinproducts.infinite", variables]

useSuspenseInfiniteAdminOrgFinproductsQuery.rootKey = "AdminOrgFinproducts.infinite"

useAdminOrgFinproductsQuery.fetcher = (
  variables: Types.AdminOrgFinproductsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminOrgFinproductsQuery,
    Types.AdminOrgFinproductsQueryVariables
  >(AdminOrgFinproductsDocument, variables, options)

export const AdminOrgUsersDocument = new TypedDocumentString(`
    query AdminOrgUsers($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      pid
      name
      logoUrl
      logomarkUrl
      users {
        ...UserListUser
      }
    }
  }
}
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  jobFunctions
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  adminOrgRoles {
    org {
      pid
      name
      orgSlug
    }
    role
    grantsAllPortfolios
    orgIsDeveloper
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}`)

export const useAdminOrgUsersQuery = <
  TData = Types.AdminOrgUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminOrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgUsersQuery, TError, TData>({
    queryKey: ["AdminOrgUsers", variables],
    queryFn: publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
      AdminOrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useAdminOrgUsersQuery.getKey = (variables: Types.AdminOrgUsersQueryVariables) => [
  "AdminOrgUsers",
  variables,
]

useAdminOrgUsersQuery.rootKey = "AdminOrgUsers"

export const useSuspenseAdminOrgUsersQuery = <
  TData = Types.AdminOrgUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgUsersQuery, TError, TData>({
    queryKey: ["AdminOrgUsers", variables],
    queryFn: publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
      AdminOrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminOrgUsersQuery.getKey = (
  variables: Types.AdminOrgUsersQueryVariables,
) => ["AdminOrgUsers", variables]

useSuspenseAdminOrgUsersQuery.rootKey = "AdminOrgUsers"

export const useInfiniteAdminOrgUsersQuery = <
  TData = InfiniteData<Types.AdminOrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
            AdminOrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgUsersQuery.getKey = (
  variables: Types.AdminOrgUsersQueryVariables,
) => ["AdminOrgUsers.infinite", variables]

useInfiniteAdminOrgUsersQuery.rootKey = "AdminOrgUsers.infinite"

export const useSuspenseInfiniteAdminOrgUsersQuery = <
  TData = InfiniteData<Types.AdminOrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
            AdminOrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgUsersQuery.getKey = (
  variables: Types.AdminOrgUsersQueryVariables,
) => ["AdminOrgUsers.infinite", variables]

useSuspenseInfiniteAdminOrgUsersQuery.rootKey = "AdminOrgUsers.infinite"

useAdminOrgUsersQuery.fetcher = (
  variables: Types.AdminOrgUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
    AdminOrgUsersDocument,
    variables,
    options,
  )

export const SitePoliciesDocument = new TypedDocumentString(`
    query SitePolicies($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      state
      targetUtility {
        id
        name
        shortName
      }
      policies {
        ...PolicyListItem
      }
    }
  }
}
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}
fragment PolicyListItem on Policy {
  pid
  name
  jurisdiction {
    scope
    name
  }
  status
  startDate
  endDate
  userVisible
  application {
    status
  }
  rules {
    ...Rule
  }
}`)

export const useSitePoliciesQuery = <TData = Types.SitePoliciesQuery, TError = unknown>(
  variables: Types.SitePoliciesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SitePoliciesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SitePoliciesQuery, TError, TData>({
    queryKey: ["SitePolicies", variables],
    queryFn: publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
      SitePoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePolicies",
  variables,
]

useSitePoliciesQuery.rootKey = "SitePolicies"

export const useSuspenseSitePoliciesQuery = <
  TData = Types.SitePoliciesQuery,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePoliciesQuery, TError, TData>({
    queryKey: ["SitePolicies", variables],
    queryFn: publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
      SitePoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePolicies",
  variables,
]

useSuspenseSitePoliciesQuery.rootKey = "SitePolicies"

export const useInfiniteSitePoliciesQuery = <
  TData = InfiniteData<Types.SitePoliciesQuery>,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicies.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
            SitePoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePolicies.infinite",
  variables,
]

useInfiniteSitePoliciesQuery.rootKey = "SitePolicies.infinite"

export const useSuspenseInfiniteSitePoliciesQuery = <
  TData = InfiniteData<Types.SitePoliciesQuery>,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicies.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
            SitePoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePoliciesQuery.getKey = (
  variables: Types.SitePoliciesQueryVariables,
) => ["SitePolicies.infinite", variables]

useSuspenseInfiniteSitePoliciesQuery.rootKey = "SitePolicies.infinite"

useSitePoliciesQuery.fetcher = (
  variables: Types.SitePoliciesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
    SitePoliciesDocument,
    variables,
    options,
  )

export const AllPoliciesDocument = new TypedDocumentString(`
    query AllPolicies {
  admin {
    policies {
      all {
        ...PolicyListItem
      }
    }
  }
}
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}
fragment PolicyListItem on Policy {
  pid
  name
  jurisdiction {
    scope
    name
  }
  status
  startDate
  endDate
  userVisible
  application {
    status
  }
  rules {
    ...Rule
  }
}`)

export const useAllPoliciesQuery = <TData = Types.AllPoliciesQuery, TError = unknown>(
  variables?: Types.AllPoliciesQueryVariables,
  options?: Omit<UseQueryOptions<Types.AllPoliciesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AllPoliciesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AllPoliciesQuery, TError, TData>({
    queryKey: variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables],
    queryFn: publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
      AllPoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables]

useAllPoliciesQuery.rootKey = "AllPolicies"

export const useSuspenseAllPoliciesQuery = <
  TData = Types.AllPoliciesQuery,
  TError = unknown,
>(
  variables?: Types.AllPoliciesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AllPoliciesQuery, TError, TData>({
    queryKey: variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables],
    queryFn: publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
      AllPoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables]

useSuspenseAllPoliciesQuery.rootKey = "AllPolicies"

export const useInfiniteAllPoliciesQuery = <
  TData = InfiniteData<Types.AllPoliciesQuery>,
  TError = unknown,
>(
  variables: Types.AllPoliciesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AllPoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllPolicies.infinite"]
            : ["AllPolicies.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
            AllPoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined
    ? ["AllPolicies.infinite"]
    : ["AllPolicies.infinite", variables]

useInfiniteAllPoliciesQuery.rootKey = "AllPolicies.infinite"

export const useSuspenseInfiniteAllPoliciesQuery = <
  TData = InfiniteData<Types.AllPoliciesQuery>,
  TError = unknown,
>(
  variables: Types.AllPoliciesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AllPoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllPolicies.infinite"]
            : ["AllPolicies.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
            AllPoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAllPoliciesQuery.getKey = (
  variables?: Types.AllPoliciesQueryVariables,
) =>
  variables === undefined
    ? ["AllPolicies.infinite"]
    : ["AllPolicies.infinite", variables]

useSuspenseInfiniteAllPoliciesQuery.rootKey = "AllPolicies.infinite"

useAllPoliciesQuery.fetcher = (
  variables?: Types.AllPoliciesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
    AllPoliciesDocument,
    variables,
    options,
  )

export const PolicyDetailDocument = new TypedDocumentString(`
    query PolicyDetail($policyPid: String!) {
  admin {
    policies {
      policy(policyPid: $policyPid) {
        pid
        name
        userVisible
        description
        publicDescription
        status
        startDate
        endDate
        application {
          status
        }
        jurisdiction {
          scope
          name
        }
        rules {
          ...Rule
        }
        conditions {
          ...PolicyCondition
        }
        sources {
          text
          url
        }
      }
    }
  }
}
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}`)

export const usePolicyDetailQuery = <TData = Types.PolicyDetailQuery, TError = unknown>(
  variables: Types.PolicyDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PolicyDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PolicyDetailQuery, TError, TData>({
    queryKey: ["PolicyDetail", variables],
    queryFn: publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
      PolicyDetailDocument,
      variables,
    ),
    ...options,
  })
}

usePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetail",
  variables,
]

usePolicyDetailQuery.rootKey = "PolicyDetail"

export const useSuspensePolicyDetailQuery = <
  TData = Types.PolicyDetailQuery,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PolicyDetailQuery, TError, TData>({
    queryKey: ["PolicyDetail", variables],
    queryFn: publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
      PolicyDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspensePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetail",
  variables,
]

useSuspensePolicyDetailQuery.rootKey = "PolicyDetail"

export const useInfinitePolicyDetailQuery = <
  TData = InfiniteData<Types.PolicyDetailQuery>,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PolicyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PolicyDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
            PolicyDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetail.infinite",
  variables,
]

useInfinitePolicyDetailQuery.rootKey = "PolicyDetail.infinite"

export const useSuspenseInfinitePolicyDetailQuery = <
  TData = InfiniteData<Types.PolicyDetailQuery>,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PolicyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PolicyDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
            PolicyDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePolicyDetailQuery.getKey = (
  variables: Types.PolicyDetailQueryVariables,
) => ["PolicyDetail.infinite", variables]

useSuspenseInfinitePolicyDetailQuery.rootKey = "PolicyDetail.infinite"

usePolicyDetailQuery.fetcher = (
  variables: Types.PolicyDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
    PolicyDetailDocument,
    variables,
    options,
  )

export const SitePolicyViolationsDocument = new TypedDocumentString(`
    query SitePolicyViolations($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      policyViolations {
        policy {
          pid
          name
        }
        violatedRule {
          id
          description
          units
          value {
            ...RuleValue
          }
        }
        value {
          ...RuleValue
        }
      }
    }
  }
}
    fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}`)

export const useSitePolicyViolationsQuery = <
  TData = Types.SitePolicyViolationsQuery,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SitePolicyViolationsQuery, TError, TData>({
    queryKey: ["SitePolicyViolations", variables],
    queryFn: publicFetcher<
      Types.SitePolicyViolationsQuery,
      Types.SitePolicyViolationsQueryVariables
    >(SitePolicyViolationsDocument, variables),
    ...options,
  })
}

useSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations", variables]

useSitePolicyViolationsQuery.rootKey = "SitePolicyViolations"

export const useSuspenseSitePolicyViolationsQuery = <
  TData = Types.SitePolicyViolationsQuery,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePolicyViolationsQuery, TError, TData>({
    queryKey: ["SitePolicyViolations", variables],
    queryFn: publicFetcher<
      Types.SitePolicyViolationsQuery,
      Types.SitePolicyViolationsQueryVariables
    >(SitePolicyViolationsDocument, variables),
    ...options,
  })
}

useSuspenseSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations", variables]

useSuspenseSitePolicyViolationsQuery.rootKey = "SitePolicyViolations"

export const useInfiniteSitePolicyViolationsQuery = <
  TData = InfiniteData<Types.SitePolicyViolationsQuery>,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePolicyViolationsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicyViolations.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePolicyViolationsQuery,
            Types.SitePolicyViolationsQueryVariables
          >(SitePolicyViolationsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations.infinite", variables]

useInfiniteSitePolicyViolationsQuery.rootKey = "SitePolicyViolations.infinite"

export const useSuspenseInfiniteSitePolicyViolationsQuery = <
  TData = InfiniteData<Types.SitePolicyViolationsQuery>,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePolicyViolationsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicyViolations.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePolicyViolationsQuery,
            Types.SitePolicyViolationsQueryVariables
          >(SitePolicyViolationsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations.infinite", variables]

useSuspenseInfiniteSitePolicyViolationsQuery.rootKey = "SitePolicyViolations.infinite"

useSitePolicyViolationsQuery.fetcher = (
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SitePolicyViolationsQuery,
    Types.SitePolicyViolationsQueryVariables
  >(SitePolicyViolationsDocument, variables, options)

export const MultiMeterDocument = new TypedDocumentString(`
    query MultiMeter($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      usingBillingMeters
      orgSlug
      grandfatheredNem2
      bauMeters {
        ...BauMeter
        hasTruth
      }
      activeMeters {
        id
        createdAt
        createdBy {
          pid
          email
        }
        meters {
          ...BauMeter
        }
      }
      thirdPartySupplier {
        id
        name
      }
      targetUtility {
        id
        name
        shortName
      }
      targetUtilitySetBy {
        ...UserRef
      }
      dateTargetUtilitySet
      utilityName
      probableUtilities {
        id
        name
      }
      customTariffs {
        id
        name
        code
        parent {
          id
        }
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment BauMeter on BauMeter {
  id
  createdAt
  createdBy {
    email
  }
  label
  loadOrigin
  loadCategory
  meterSource
  utility {
    id
    name
  }
  tariff {
    ...BauTariff
  }
  tariffSetBy {
    pid
    email
  }
  underHumanControl
  tariffsUnderHumanControl
  postSolarTariff {
    ...BauTariff
  }
  postSolarTariffSetBy {
    pid
    email
  }
  postBatteryTariff {
    ...BauTariff
  }
  postBatteryTariffSetBy {
    pid
    email
  }
  archivedAt
  archivedBy {
    pid
    email
  }
  archivedReason
  rollup {
    season
    days
    numbers
  }
}
fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}`)

export const useMultiMeterQuery = <TData = Types.MultiMeterQuery, TError = unknown>(
  variables: Types.MultiMeterQueryVariables,
  options?: Omit<UseQueryOptions<Types.MultiMeterQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterQuery, TError, TData>({
    queryKey: ["MultiMeter", variables],
    queryFn: publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
      MultiMeterDocument,
      variables,
    ),
    ...options,
  })
}

useMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeter",
  variables,
]

useMultiMeterQuery.rootKey = "MultiMeter"

export const useSuspenseMultiMeterQuery = <
  TData = Types.MultiMeterQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterQuery, TError, TData>({
    queryKey: ["MultiMeter", variables],
    queryFn: publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
      MultiMeterDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeter",
  variables,
]

useSuspenseMultiMeterQuery.rootKey = "MultiMeter"

export const useInfiniteMultiMeterQuery = <
  TData = InfiniteData<Types.MultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeter.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
            MultiMeterDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeter.infinite",
  variables,
]

useInfiniteMultiMeterQuery.rootKey = "MultiMeter.infinite"

export const useSuspenseInfiniteMultiMeterQuery = <
  TData = InfiniteData<Types.MultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeter.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
            MultiMeterDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterQuery.getKey = (
  variables: Types.MultiMeterQueryVariables,
) => ["MultiMeter.infinite", variables]

useSuspenseInfiniteMultiMeterQuery.rootKey = "MultiMeter.infinite"

useMultiMeterQuery.fetcher = (
  variables: Types.MultiMeterQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
    MultiMeterDocument,
    variables,
    options,
  )

export const BauMeterIntervalsDocument = new TypedDocumentString(`
    query BauMeterIntervals($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      bauMeters {
        ...BauMeterIntervals
      }
    }
  }
}
    fragment BauMeterIntervals on BauMeter {
  id
  tariffUsdPerKwh
  rollup {
    season
    days
    numbers
  }
  intervals {
    units
    year
    timeZone
    maxKw
    totalKwh
    kwh8760
  }
}`)

export const useBauMeterIntervalsQuery = <
  TData = Types.BauMeterIntervalsQuery,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterIntervalsQuery, TError, TData>({
    queryKey: ["BauMeterIntervals", variables],
    queryFn: publicFetcher<
      Types.BauMeterIntervalsQuery,
      Types.BauMeterIntervalsQueryVariables
    >(BauMeterIntervalsDocument, variables),
    ...options,
  })
}

useBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals", variables]

useBauMeterIntervalsQuery.rootKey = "BauMeterIntervals"

export const useSuspenseBauMeterIntervalsQuery = <
  TData = Types.BauMeterIntervalsQuery,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterIntervalsQuery, TError, TData>({
    queryKey: ["BauMeterIntervals", variables],
    queryFn: publicFetcher<
      Types.BauMeterIntervalsQuery,
      Types.BauMeterIntervalsQueryVariables
    >(BauMeterIntervalsDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals", variables]

useSuspenseBauMeterIntervalsQuery.rootKey = "BauMeterIntervals"

export const useInfiniteBauMeterIntervalsQuery = <
  TData = InfiniteData<Types.BauMeterIntervalsQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterIntervalsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterIntervals.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterIntervalsQuery,
            Types.BauMeterIntervalsQueryVariables
          >(BauMeterIntervalsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals.infinite", variables]

useInfiniteBauMeterIntervalsQuery.rootKey = "BauMeterIntervals.infinite"

export const useSuspenseInfiniteBauMeterIntervalsQuery = <
  TData = InfiniteData<Types.BauMeterIntervalsQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BauMeterIntervalsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterIntervals.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterIntervalsQuery,
            Types.BauMeterIntervalsQueryVariables
          >(BauMeterIntervalsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals.infinite", variables]

useSuspenseInfiniteBauMeterIntervalsQuery.rootKey = "BauMeterIntervals.infinite"

useBauMeterIntervalsQuery.fetcher = (
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BauMeterIntervalsQuery, Types.BauMeterIntervalsQueryVariables>(
    BauMeterIntervalsDocument,
    variables,
    options,
  )

export const MeterSetDocument = new TypedDocumentString(`
    query MeterSet($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      meterSet(id: $id) {
        id
        meters {
          hasTruth
          ...BauMeter
          ...BauMeterIntervals
        }
      }
      customTariffs {
        id
        name
        code
        parent {
          id
        }
      }
    }
  }
}
    fragment BauMeter on BauMeter {
  id
  createdAt
  createdBy {
    email
  }
  label
  loadOrigin
  loadCategory
  meterSource
  utility {
    id
    name
  }
  tariff {
    ...BauTariff
  }
  tariffSetBy {
    pid
    email
  }
  underHumanControl
  tariffsUnderHumanControl
  postSolarTariff {
    ...BauTariff
  }
  postSolarTariffSetBy {
    pid
    email
  }
  postBatteryTariff {
    ...BauTariff
  }
  postBatteryTariffSetBy {
    pid
    email
  }
  archivedAt
  archivedBy {
    pid
    email
  }
  archivedReason
  rollup {
    season
    days
    numbers
  }
}
fragment BauMeterIntervals on BauMeter {
  id
  tariffUsdPerKwh
  rollup {
    season
    days
    numbers
  }
  intervals {
    units
    year
    timeZone
    maxKw
    totalKwh
    kwh8760
  }
}
fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}`)

export const useMeterSetQuery = <TData = Types.MeterSetQuery, TError = unknown>(
  variables: Types.MeterSetQueryVariables,
  options?: Omit<UseQueryOptions<Types.MeterSetQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MeterSetQuery, TError, TData>({
    queryKey: ["MeterSet", variables],
    queryFn: publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
      MeterSetDocument,
      variables,
    ),
    ...options,
  })
}

useMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet",
  variables,
]

useMeterSetQuery.rootKey = "MeterSet"

export const useSuspenseMeterSetQuery = <TData = Types.MeterSetQuery, TError = unknown>(
  variables: Types.MeterSetQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeterSetQuery, TError, TData>({
    queryKey: ["MeterSet", variables],
    queryFn: publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
      MeterSetDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet",
  variables,
]

useSuspenseMeterSetQuery.rootKey = "MeterSet"

export const useInfiniteMeterSetQuery = <
  TData = InfiniteData<Types.MeterSetQuery>,
  TError = unknown,
>(
  variables: Types.MeterSetQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeterSetQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeterSet.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
            MeterSetDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet.infinite",
  variables,
]

useInfiniteMeterSetQuery.rootKey = "MeterSet.infinite"

export const useSuspenseInfiniteMeterSetQuery = <
  TData = InfiniteData<Types.MeterSetQuery>,
  TError = unknown,
>(
  variables: Types.MeterSetQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeterSetQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeterSetQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeterSet.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
            MeterSetDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet.infinite",
  variables,
]

useSuspenseInfiniteMeterSetQuery.rootKey = "MeterSet.infinite"

useMeterSetQuery.fetcher = (
  variables: Types.MeterSetQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
    MeterSetDocument,
    variables,
    options,
  )

export const AdminNotesDocument = new TypedDocumentString(`
    query AdminNotes($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      notes {
        pid
        text
        content
        tag
        createdAt
        createdBy {
          ...UserRef
        }
        updatedAt
        updatedBy {
          ...UserRef
        }
        trashedAt
        trashedBy {
          ...UserRef
        }
        hiddenFromCustomers
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAdminNotesQuery = <TData = Types.AdminNotesQuery, TError = unknown>(
  variables: Types.AdminNotesQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminNotesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminNotesQuery, TError, TData>({
    queryKey: ["AdminNotes", variables],
    queryFn: publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
      AdminNotesDocument,
      variables,
    ),
    ...options,
  })
}

useAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotes",
  variables,
]

useAdminNotesQuery.rootKey = "AdminNotes"

export const useSuspenseAdminNotesQuery = <
  TData = Types.AdminNotesQuery,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminNotesQuery, TError, TData>({
    queryKey: ["AdminNotes", variables],
    queryFn: publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
      AdminNotesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotes",
  variables,
]

useSuspenseAdminNotesQuery.rootKey = "AdminNotes"

export const useInfiniteAdminNotesQuery = <
  TData = InfiniteData<Types.AdminNotesQuery>,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminNotesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminNotes.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
            AdminNotesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotes.infinite",
  variables,
]

useInfiniteAdminNotesQuery.rootKey = "AdminNotes.infinite"

export const useSuspenseInfiniteAdminNotesQuery = <
  TData = InfiniteData<Types.AdminNotesQuery>,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminNotesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminNotesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminNotes.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
            AdminNotesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminNotesQuery.getKey = (
  variables: Types.AdminNotesQueryVariables,
) => ["AdminNotes.infinite", variables]

useSuspenseInfiniteAdminNotesQuery.rootKey = "AdminNotes.infinite"

useAdminNotesQuery.fetcher = (
  variables: Types.AdminNotesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
    AdminNotesDocument,
    variables,
    options,
  )

export const BatchPhysicalCheckDocument = new TypedDocumentString(`
    query BatchPhysicalCheck($orgPid: String!, $sitePids: [String!]!, $siteFields: [SiteChangelogColumn!]!, $buildingDataFields: [BuildingDataChangelogColumn!]!) {
  admin {
    org(orgPid: $orgPid) {
      pid
      name
    }
    sites(sitePids: $sitePids) {
      pid
      portfolio {
        name
      }
      changelog(fields: $siteFields) {
        all {
          column
          obj {
            ...Changelog
          }
        }
      }
      buildingData {
        changelog(fields: $buildingDataFields) {
          all {
            column
            obj {
              ...Changelog
            }
          }
        }
      }
      notes {
        pid
        text
        content
        createdAt
        createdBy {
          ...UserRef
        }
        updatedAt
        updatedBy {
          ...UserRef
        }
      }
      currentStatus {
        pid
        status
      }
      slopedRoof
      propertyName
      numberOfBuildings
      sqft
      numStories
      fullAddress
      roofMaxPvKw
      computedRoofMaxPvKw
      explainRoofHeuristic
      explainSlopedHeuristic
      computedCarportMaxPvKw
      explainCarportHeuristic
      explainCarportParkingSpaceHeuristic
      computedGroundMaxPvKw
      explainGroundHeuristic
      impliedCarportLinearFt
      impliedUsableRoofSqft
      impliedUsableRoofSqftExplanation
      roofSqft
      usableRoofPct
      impliedUsableRoofPct
      usableRoofSqft
      setbackRoofSqft
      roofKeepoutsSqft
      groundAvailableSqft
      groundMaxPvKw
      usableCarportFt
      numParkingSpaces
      carportMaxPvKw
      batteryMaxMw
      batteryPadMw
      existingSolarDetectionScore
      physicalCheck {
        existingSolar
      }
      footprint {
        ...FootprintGeometry
        bounds
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Changelog on Changelog {
  field
  updatedAt
  updatedBy {
    ...UserRef
  }
}`)

export const useBatchPhysicalCheckQuery = <
  TData = Types.BatchPhysicalCheckQuery,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BatchPhysicalCheckQuery, TError, TData>({
    queryKey: ["BatchPhysicalCheck", variables],
    queryFn: publicFetcher<
      Types.BatchPhysicalCheckQuery,
      Types.BatchPhysicalCheckQueryVariables
    >(BatchPhysicalCheckDocument, variables),
    ...options,
  })
}

useBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck", variables]

useBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck"

export const useSuspenseBatchPhysicalCheckQuery = <
  TData = Types.BatchPhysicalCheckQuery,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BatchPhysicalCheckQuery, TError, TData>({
    queryKey: ["BatchPhysicalCheck", variables],
    queryFn: publicFetcher<
      Types.BatchPhysicalCheckQuery,
      Types.BatchPhysicalCheckQueryVariables
    >(BatchPhysicalCheckDocument, variables),
    ...options,
  })
}

useSuspenseBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck", variables]

useSuspenseBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck"

export const useInfiniteBatchPhysicalCheckQuery = <
  TData = InfiniteData<Types.BatchPhysicalCheckQuery>,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BatchPhysicalCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BatchPhysicalCheck.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatchPhysicalCheckQuery,
            Types.BatchPhysicalCheckQueryVariables
          >(BatchPhysicalCheckDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck.infinite", variables]

useInfiniteBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck.infinite"

export const useSuspenseInfiniteBatchPhysicalCheckQuery = <
  TData = InfiniteData<Types.BatchPhysicalCheckQuery>,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BatchPhysicalCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BatchPhysicalCheck.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatchPhysicalCheckQuery,
            Types.BatchPhysicalCheckQueryVariables
          >(BatchPhysicalCheckDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck.infinite", variables]

useSuspenseInfiniteBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck.infinite"

useBatchPhysicalCheckQuery.fetcher = (
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BatchPhysicalCheckQuery, Types.BatchPhysicalCheckQueryVariables>(
    BatchPhysicalCheckDocument,
    variables,
    options,
  )

export const ClosestParcelsDocument = new TypedDocumentString(`
    query ClosestParcels($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      closestSites {
        pid
        name
        fullAddress
        footprint {
          groundsShapeGeojson
        }
      }
    }
  }
}
    `)

export const useClosestParcelsQuery = <
  TData = Types.ClosestParcelsQuery,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ClosestParcelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ClosestParcelsQuery, TError, TData>({
    queryKey: ["ClosestParcels", variables],
    queryFn: publicFetcher<
      Types.ClosestParcelsQuery,
      Types.ClosestParcelsQueryVariables
    >(ClosestParcelsDocument, variables),
    ...options,
  })
}

useClosestParcelsQuery.getKey = (variables: Types.ClosestParcelsQueryVariables) => [
  "ClosestParcels",
  variables,
]

useClosestParcelsQuery.rootKey = "ClosestParcels"

export const useSuspenseClosestParcelsQuery = <
  TData = Types.ClosestParcelsQuery,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ClosestParcelsQuery, TError, TData>({
    queryKey: ["ClosestParcels", variables],
    queryFn: publicFetcher<
      Types.ClosestParcelsQuery,
      Types.ClosestParcelsQueryVariables
    >(ClosestParcelsDocument, variables),
    ...options,
  })
}

useSuspenseClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcels", variables]

useSuspenseClosestParcelsQuery.rootKey = "ClosestParcels"

export const useInfiniteClosestParcelsQuery = <
  TData = InfiniteData<Types.ClosestParcelsQuery>,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ClosestParcelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ClosestParcels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
            ClosestParcelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcels.infinite", variables]

useInfiniteClosestParcelsQuery.rootKey = "ClosestParcels.infinite"

export const useSuspenseInfiniteClosestParcelsQuery = <
  TData = InfiniteData<Types.ClosestParcelsQuery>,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ClosestParcelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ClosestParcels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
            ClosestParcelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcels.infinite", variables]

useSuspenseInfiniteClosestParcelsQuery.rootKey = "ClosestParcels.infinite"

useClosestParcelsQuery.fetcher = (
  variables: Types.ClosestParcelsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
    ClosestParcelsDocument,
    variables,
    options,
  )

export const GeocodedLocationDocument = new TypedDocumentString(`
    query GeocodedLocation($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      fullAddress
      footprint {
        geocodedLocation {
          latitude
          longitude
        }
      }
    }
  }
}
    `)

export const useGeocodedLocationQuery = <
  TData = Types.GeocodedLocationQuery,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GeocodedLocationQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GeocodedLocationQuery, TError, TData>({
    queryKey: ["GeocodedLocation", variables],
    queryFn: publicFetcher<
      Types.GeocodedLocationQuery,
      Types.GeocodedLocationQueryVariables
    >(GeocodedLocationDocument, variables),
    ...options,
  })
}

useGeocodedLocationQuery.getKey = (variables: Types.GeocodedLocationQueryVariables) => [
  "GeocodedLocation",
  variables,
]

useGeocodedLocationQuery.rootKey = "GeocodedLocation"

export const useSuspenseGeocodedLocationQuery = <
  TData = Types.GeocodedLocationQuery,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GeocodedLocationQuery, TError, TData>({
    queryKey: ["GeocodedLocation", variables],
    queryFn: publicFetcher<
      Types.GeocodedLocationQuery,
      Types.GeocodedLocationQueryVariables
    >(GeocodedLocationDocument, variables),
    ...options,
  })
}

useSuspenseGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocation", variables]

useSuspenseGeocodedLocationQuery.rootKey = "GeocodedLocation"

export const useInfiniteGeocodedLocationQuery = <
  TData = InfiniteData<Types.GeocodedLocationQuery>,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GeocodedLocationQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GeocodedLocation.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GeocodedLocationQuery,
            Types.GeocodedLocationQueryVariables
          >(GeocodedLocationDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocation.infinite", variables]

useInfiniteGeocodedLocationQuery.rootKey = "GeocodedLocation.infinite"

export const useSuspenseInfiniteGeocodedLocationQuery = <
  TData = InfiniteData<Types.GeocodedLocationQuery>,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GeocodedLocationQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GeocodedLocation.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GeocodedLocationQuery,
            Types.GeocodedLocationQueryVariables
          >(GeocodedLocationDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocation.infinite", variables]

useSuspenseInfiniteGeocodedLocationQuery.rootKey = "GeocodedLocation.infinite"

useGeocodedLocationQuery.fetcher = (
  variables: Types.GeocodedLocationQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GeocodedLocationQuery, Types.GeocodedLocationQueryVariables>(
    GeocodedLocationDocument,
    variables,
    options,
  )

export const ActiveResultListDocument = new TypedDocumentString(`
    query ActiveResultList($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      activeResults {
        id
        pid
        createdAt
        scenario {
          pid
          name
          solarLocation
          loadCategory
          includeStorage
        }
        financing {
          id
          name
          type
          financierName
          financierPid
          financierLogo
          financierLogomark
          isThirdParty
          status
        }
        result {
          __typename
          ... on PhysicalCheck {
            createdAt
            roofIsGood
            carportIsGood
            existingSolar
            badRoof
            groundmountIsGood
          }
          ... on PolicyCheck {
            createdAt
            productType
            isGood
            disqualReason
          }
          ... on RevampFinmodelFailure {
            id
            createdAt
            fmvStr
            details {
              label
              description
            }
          }
          ... on RevampFinmodel {
            id
            fmvStr
            systems {
              ...RevampSystem
            }
            buildingOwner {
              noiAnnual
              noiLifetime
            }
            moreDetails {
              pvCostPerWatt
              storageCostPerKwh
              totalInstallCost
            }
          }
          ... on RevampCS {
            ...RevampCS
          }
          ... on Viability {
            isAllowed
            viabilityType: type
            reason
            level
          }
        }
      }
    }
  }
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}`)

export const useActiveResultListQuery = <
  TData = Types.ActiveResultListQuery,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveResultListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveResultListQuery, TError, TData>({
    queryKey: ["ActiveResultList", variables],
    queryFn: publicFetcher<
      Types.ActiveResultListQuery,
      Types.ActiveResultListQueryVariables
    >(ActiveResultListDocument, variables),
    ...options,
  })
}

useActiveResultListQuery.getKey = (variables: Types.ActiveResultListQueryVariables) => [
  "ActiveResultList",
  variables,
]

useActiveResultListQuery.rootKey = "ActiveResultList"

export const useSuspenseActiveResultListQuery = <
  TData = Types.ActiveResultListQuery,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveResultListQuery, TError, TData>({
    queryKey: ["ActiveResultList", variables],
    queryFn: publicFetcher<
      Types.ActiveResultListQuery,
      Types.ActiveResultListQueryVariables
    >(ActiveResultListDocument, variables),
    ...options,
  })
}

useSuspenseActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultList", variables]

useSuspenseActiveResultListQuery.rootKey = "ActiveResultList"

export const useInfiniteActiveResultListQuery = <
  TData = InfiniteData<Types.ActiveResultListQuery>,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveResultListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveResultList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ActiveResultListQuery,
            Types.ActiveResultListQueryVariables
          >(ActiveResultListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultList.infinite", variables]

useInfiniteActiveResultListQuery.rootKey = "ActiveResultList.infinite"

export const useSuspenseInfiniteActiveResultListQuery = <
  TData = InfiniteData<Types.ActiveResultListQuery>,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveResultListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveResultList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ActiveResultListQuery,
            Types.ActiveResultListQueryVariables
          >(ActiveResultListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultList.infinite", variables]

useSuspenseInfiniteActiveResultListQuery.rootKey = "ActiveResultList.infinite"

useActiveResultListQuery.fetcher = (
  variables: Types.ActiveResultListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ActiveResultListQuery, Types.ActiveResultListQueryVariables>(
    ActiveResultListDocument,
    variables,
    options,
  )

export const AdminResultDetailDocument = new TypedDocumentString(`
    query AdminResultDetail($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      result(id: $id) {
        ...Result
        result {
          ... on RevampFinmodel {
            moreDetails {
              pvCostPerWatt
              storageCostPerKwh
              totalInstallCost
            }
          }
        }
      }
    }
  }
}
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment Result on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`)

export const useAdminResultDetailQuery = <
  TData = Types.AdminResultDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminResultDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminResultDetailQuery, TError, TData>({
    queryKey: ["AdminResultDetail", variables],
    queryFn: publicFetcher<
      Types.AdminResultDetailQuery,
      Types.AdminResultDetailQueryVariables
    >(AdminResultDetailDocument, variables),
    ...options,
  })
}

useAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetail", variables]

useAdminResultDetailQuery.rootKey = "AdminResultDetail"

export const useSuspenseAdminResultDetailQuery = <
  TData = Types.AdminResultDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminResultDetailQuery, TError, TData>({
    queryKey: ["AdminResultDetail", variables],
    queryFn: publicFetcher<
      Types.AdminResultDetailQuery,
      Types.AdminResultDetailQueryVariables
    >(AdminResultDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetail", variables]

useSuspenseAdminResultDetailQuery.rootKey = "AdminResultDetail"

export const useInfiniteAdminResultDetailQuery = <
  TData = InfiniteData<Types.AdminResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminResultDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminResultDetailQuery,
            Types.AdminResultDetailQueryVariables
          >(AdminResultDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetail.infinite", variables]

useInfiniteAdminResultDetailQuery.rootKey = "AdminResultDetail.infinite"

export const useSuspenseInfiniteAdminResultDetailQuery = <
  TData = InfiniteData<Types.AdminResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminResultDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminResultDetailQuery,
            Types.AdminResultDetailQueryVariables
          >(AdminResultDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetail.infinite", variables]

useSuspenseInfiniteAdminResultDetailQuery.rootKey = "AdminResultDetail.infinite"

useAdminResultDetailQuery.fetcher = (
  variables: Types.AdminResultDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminResultDetailQuery, Types.AdminResultDetailQueryVariables>(
    AdminResultDetailDocument,
    variables,
    options,
  )

export const ResultTableDocument = new TypedDocumentString(`
    query ResultTable($slug: String!, $filterBy: ResultTableFilter, $sortBy: [ResultTableSort!]) {
  admin {
    orgBySlug(slug: $slug) {
      resultTable {
        view(filterBy: $filterBy, sortBy: $sortBy) {
          keys
        }
      }
    }
  }
}
    `)

export const useResultTableQuery = <TData = Types.ResultTableQuery, TError = unknown>(
  variables: Types.ResultTableQueryVariables,
  options?: Omit<UseQueryOptions<Types.ResultTableQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ResultTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultTableQuery, TError, TData>({
    queryKey: ["ResultTable", variables],
    queryFn: publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
      ResultTableDocument,
      variables,
    ),
    ...options,
  })
}

useResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTable",
  variables,
]

useResultTableQuery.rootKey = "ResultTable"

export const useSuspenseResultTableQuery = <
  TData = Types.ResultTableQuery,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultTableQuery, TError, TData>({
    queryKey: ["ResultTable", variables],
    queryFn: publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
      ResultTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTable",
  variables,
]

useSuspenseResultTableQuery.rootKey = "ResultTable"

export const useInfiniteResultTableQuery = <
  TData = InfiniteData<Types.ResultTableQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
            ResultTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTable.infinite",
  variables,
]

useInfiniteResultTableQuery.rootKey = "ResultTable.infinite"

export const useSuspenseInfiniteResultTableQuery = <
  TData = InfiniteData<Types.ResultTableQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
            ResultTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultTableQuery.getKey = (
  variables: Types.ResultTableQueryVariables,
) => ["ResultTable.infinite", variables]

useSuspenseInfiniteResultTableQuery.rootKey = "ResultTable.infinite"

useResultTableQuery.fetcher = (
  variables: Types.ResultTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
    ResultTableDocument,
    variables,
    options,
  )

export const ResultTableRowsDocument = new TypedDocumentString(`
    query ResultTableRows($slug: String!, $ids: [Int!]!) {
  admin {
    orgBySlug(slug: $slug) {
      resultTable {
        rows(keys: $ids) {
          id
          createdAt
          sitePid
          siteName
          siteStreetAddress
          siteCity
          siteState
          siteStatus
          siteHiddenFromCustomers
          scenarioPid
          scenarioName
          scenarioSolarLocation
          scenarioIncludeStorage
          finproductType
          financierName
          financierPid
          finmodelId
          finmodelFmv
          finmodelNoi
          failureId
          failureFmv
          failureName
          failureMessage
          policyCheckIsGood
          physicalCheckRoofIsGood
          physicalCheckCarportIsGood
          physicalCheckGroundmountIsGood
        }
      }
    }
  }
}
    `)

export const useResultTableRowsQuery = <
  TData = Types.ResultTableRowsQuery,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultTableRowsQuery, TError, TData>({
    queryKey: ["ResultTableRows", variables],
    queryFn: publicFetcher<
      Types.ResultTableRowsQuery,
      Types.ResultTableRowsQueryVariables
    >(ResultTableRowsDocument, variables),
    ...options,
  })
}

useResultTableRowsQuery.getKey = (variables: Types.ResultTableRowsQueryVariables) => [
  "ResultTableRows",
  variables,
]

useResultTableRowsQuery.rootKey = "ResultTableRows"

export const useSuspenseResultTableRowsQuery = <
  TData = Types.ResultTableRowsQuery,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultTableRowsQuery, TError, TData>({
    queryKey: ["ResultTableRows", variables],
    queryFn: publicFetcher<
      Types.ResultTableRowsQuery,
      Types.ResultTableRowsQueryVariables
    >(ResultTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRows", variables]

useSuspenseResultTableRowsQuery.rootKey = "ResultTableRows"

export const useInfiniteResultTableRowsQuery = <
  TData = InfiniteData<Types.ResultTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResultTableRowsQuery,
            Types.ResultTableRowsQueryVariables
          >(ResultTableRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRows.infinite", variables]

useInfiniteResultTableRowsQuery.rootKey = "ResultTableRows.infinite"

export const useSuspenseInfiniteResultTableRowsQuery = <
  TData = InfiniteData<Types.ResultTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResultTableRowsQuery,
            Types.ResultTableRowsQueryVariables
          >(ResultTableRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRows.infinite", variables]

useSuspenseInfiniteResultTableRowsQuery.rootKey = "ResultTableRows.infinite"

useResultTableRowsQuery.fetcher = (
  variables: Types.ResultTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultTableRowsQuery, Types.ResultTableRowsQueryVariables>(
    ResultTableRowsDocument,
    variables,
    options,
  )

export const FinmodelTableDocument = new TypedDocumentString(`
    query FinmodelTable($sitePid: String!, $filterBy: FinmodelTableFilter, $sortBy: [FinmodelTableSort!]) {
  admin {
    site(pid: $sitePid) {
      finmodelTable {
        view(filterBy: $filterBy, sortBy: $sortBy) {
          keys
        }
      }
    }
  }
}
    `)

export const useFinmodelTableQuery = <
  TData = Types.FinmodelTableQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelTableQuery, TError, TData>({
    queryKey: ["FinmodelTable", variables],
    queryFn: publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
      FinmodelTableDocument,
      variables,
    ),
    ...options,
  })
}

useFinmodelTableQuery.getKey = (variables: Types.FinmodelTableQueryVariables) => [
  "FinmodelTable",
  variables,
]

useFinmodelTableQuery.rootKey = "FinmodelTable"

export const useSuspenseFinmodelTableQuery = <
  TData = Types.FinmodelTableQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelTableQuery, TError, TData>({
    queryKey: ["FinmodelTable", variables],
    queryFn: publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
      FinmodelTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTable", variables]

useSuspenseFinmodelTableQuery.rootKey = "FinmodelTable"

export const useInfiniteFinmodelTableQuery = <
  TData = InfiniteData<Types.FinmodelTableQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
            FinmodelTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTable.infinite", variables]

useInfiniteFinmodelTableQuery.rootKey = "FinmodelTable.infinite"

export const useSuspenseInfiniteFinmodelTableQuery = <
  TData = InfiniteData<Types.FinmodelTableQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
            FinmodelTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTable.infinite", variables]

useSuspenseInfiniteFinmodelTableQuery.rootKey = "FinmodelTable.infinite"

useFinmodelTableQuery.fetcher = (
  variables: Types.FinmodelTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
    FinmodelTableDocument,
    variables,
    options,
  )

export const FinmodelTableRowsDocument = new TypedDocumentString(`
    query FinmodelTableRows($sitePid: String!, $ids: [Int!]!) {
  admin {
    site(pid: $sitePid) {
      finmodelTable {
        rows(keys: $ids) {
          id
          createdAt
          name
          fmvStr
          type
          buildingOwnerNoiLifetimeUsd
          scenarioPid
          scenarioName
          scenarioSolarLocation
          scenarioIncludeStorage
        }
      }
    }
  }
}
    `)

export const useFinmodelTableRowsQuery = <
  TData = Types.FinmodelTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelTableRowsQuery, TError, TData>({
    queryKey: ["FinmodelTableRows", variables],
    queryFn: publicFetcher<
      Types.FinmodelTableRowsQuery,
      Types.FinmodelTableRowsQueryVariables
    >(FinmodelTableRowsDocument, variables),
    ...options,
  })
}

useFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows", variables]

useFinmodelTableRowsQuery.rootKey = "FinmodelTableRows"

export const useSuspenseFinmodelTableRowsQuery = <
  TData = Types.FinmodelTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelTableRowsQuery, TError, TData>({
    queryKey: ["FinmodelTableRows", variables],
    queryFn: publicFetcher<
      Types.FinmodelTableRowsQuery,
      Types.FinmodelTableRowsQueryVariables
    >(FinmodelTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows", variables]

useSuspenseFinmodelTableRowsQuery.rootKey = "FinmodelTableRows"

export const useInfiniteFinmodelTableRowsQuery = <
  TData = InfiniteData<Types.FinmodelTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelTableRowsQuery,
            Types.FinmodelTableRowsQueryVariables
          >(FinmodelTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows.infinite", variables]

useInfiniteFinmodelTableRowsQuery.rootKey = "FinmodelTableRows.infinite"

export const useSuspenseInfiniteFinmodelTableRowsQuery = <
  TData = InfiniteData<Types.FinmodelTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelTableRowsQuery,
            Types.FinmodelTableRowsQueryVariables
          >(FinmodelTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows.infinite", variables]

useSuspenseInfiniteFinmodelTableRowsQuery.rootKey = "FinmodelTableRows.infinite"

useFinmodelTableRowsQuery.fetcher = (
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelTableRowsQuery, Types.FinmodelTableRowsQueryVariables>(
    FinmodelTableRowsDocument,
    variables,
    options,
  )

export const FinmodelDetail2Document = new TypedDocumentString(`
    query FinmodelDetail2($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      finmodel(id: $id) {
        ...AdminFinmodelDetail
      }
    }
  }
}
    fragment AdminFinmodelDetail on RevampFinmodel {
  __typename
  ...RevampFinmodel
  hasIntervalExport
  financing {
    id
    name
    type
    financierName
    financierPid
    isThirdParty
    status
  }
  scenario {
    pid
    name
    loadCategory
    includeStorage
    solarLocation
  }
  investorDetails {
    investorXlsxHref
    irrs {
      preTax
      postTaxItcDepr
    }
  }
  moreDetails {
    cashPurchaseNpv
    totalInstallCost
    pvCostPerWatt
    finmodelInputs
    finmodelOutputs
    luxId
    failureId
  }
  importReductionPct
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}`)

export const useFinmodelDetail2Query = <
  TData = Types.FinmodelDetail2Query,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelDetail2Query, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelDetail2Query, TError, TData>({
    queryKey: ["FinmodelDetail2", variables],
    queryFn: publicFetcher<
      Types.FinmodelDetail2Query,
      Types.FinmodelDetail2QueryVariables
    >(FinmodelDetail2Document, variables),
    ...options,
  })
}

useFinmodelDetail2Query.getKey = (variables: Types.FinmodelDetail2QueryVariables) => [
  "FinmodelDetail2",
  variables,
]

useFinmodelDetail2Query.rootKey = "FinmodelDetail2"

export const useSuspenseFinmodelDetail2Query = <
  TData = Types.FinmodelDetail2Query,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelDetail2Query, TError, TData>({
    queryKey: ["FinmodelDetail2", variables],
    queryFn: publicFetcher<
      Types.FinmodelDetail2Query,
      Types.FinmodelDetail2QueryVariables
    >(FinmodelDetail2Document, variables),
    ...options,
  })
}

useSuspenseFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2", variables]

useSuspenseFinmodelDetail2Query.rootKey = "FinmodelDetail2"

export const useInfiniteFinmodelDetail2Query = <
  TData = InfiniteData<Types.FinmodelDetail2Query>,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelDetail2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelDetail2.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelDetail2Query,
            Types.FinmodelDetail2QueryVariables
          >(FinmodelDetail2Document, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2.infinite", variables]

useInfiniteFinmodelDetail2Query.rootKey = "FinmodelDetail2.infinite"

export const useSuspenseInfiniteFinmodelDetail2Query = <
  TData = InfiniteData<Types.FinmodelDetail2Query>,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelDetail2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelDetail2.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelDetail2Query,
            Types.FinmodelDetail2QueryVariables
          >(FinmodelDetail2Document, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2.infinite", variables]

useSuspenseInfiniteFinmodelDetail2Query.rootKey = "FinmodelDetail2.infinite"

useFinmodelDetail2Query.fetcher = (
  variables: Types.FinmodelDetail2QueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelDetail2Query, Types.FinmodelDetail2QueryVariables>(
    FinmodelDetail2Document,
    variables,
    options,
  )

export const FinmodelLineageDocument = new TypedDocumentString(`
    query FinmodelLineage($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      finmodel(id: $id) {
        id
        moreDetails {
          lineage {
            ...AdminFinmodelDetail
          }
        }
      }
    }
  }
}
    fragment AdminFinmodelDetail on RevampFinmodel {
  __typename
  ...RevampFinmodel
  hasIntervalExport
  financing {
    id
    name
    type
    financierName
    financierPid
    isThirdParty
    status
  }
  scenario {
    pid
    name
    loadCategory
    includeStorage
    solarLocation
  }
  investorDetails {
    investorXlsxHref
    irrs {
      preTax
      postTaxItcDepr
    }
  }
  moreDetails {
    cashPurchaseNpv
    totalInstallCost
    pvCostPerWatt
    finmodelInputs
    finmodelOutputs
    luxId
    failureId
  }
  importReductionPct
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}`)

export const useFinmodelLineageQuery = <
  TData = Types.FinmodelLineageQuery,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelLineageQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelLineageQuery, TError, TData>({
    queryKey: ["FinmodelLineage", variables],
    queryFn: publicFetcher<
      Types.FinmodelLineageQuery,
      Types.FinmodelLineageQueryVariables
    >(FinmodelLineageDocument, variables),
    ...options,
  })
}

useFinmodelLineageQuery.getKey = (variables: Types.FinmodelLineageQueryVariables) => [
  "FinmodelLineage",
  variables,
]

useFinmodelLineageQuery.rootKey = "FinmodelLineage"

export const useSuspenseFinmodelLineageQuery = <
  TData = Types.FinmodelLineageQuery,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelLineageQuery, TError, TData>({
    queryKey: ["FinmodelLineage", variables],
    queryFn: publicFetcher<
      Types.FinmodelLineageQuery,
      Types.FinmodelLineageQueryVariables
    >(FinmodelLineageDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineage", variables]

useSuspenseFinmodelLineageQuery.rootKey = "FinmodelLineage"

export const useInfiniteFinmodelLineageQuery = <
  TData = InfiniteData<Types.FinmodelLineageQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelLineageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelLineage.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelLineageQuery,
            Types.FinmodelLineageQueryVariables
          >(FinmodelLineageDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineage.infinite", variables]

useInfiniteFinmodelLineageQuery.rootKey = "FinmodelLineage.infinite"

export const useSuspenseInfiniteFinmodelLineageQuery = <
  TData = InfiniteData<Types.FinmodelLineageQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelLineageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelLineage.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelLineageQuery,
            Types.FinmodelLineageQueryVariables
          >(FinmodelLineageDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineage.infinite", variables]

useSuspenseInfiniteFinmodelLineageQuery.rootKey = "FinmodelLineage.infinite"

useFinmodelLineageQuery.fetcher = (
  variables: Types.FinmodelLineageQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelLineageQuery, Types.FinmodelLineageQueryVariables>(
    FinmodelLineageDocument,
    variables,
    options,
  )

export const SiteLabelsDocument = new TypedDocumentString(`
    query SiteLabels($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      labels {
        pid
        name
        themeColor
      }
      portfolio {
        pid
        org {
          labels {
            pid
            name
            themeColor
          }
        }
      }
    }
  }
}
    `)

export const useSiteLabelsQuery = <TData = Types.SiteLabelsQuery, TError = unknown>(
  variables: Types.SiteLabelsQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteLabelsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteLabelsQuery, TError, TData>({
    queryKey: ["SiteLabels", variables],
    queryFn: publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
      SiteLabelsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabels",
  variables,
]

useSiteLabelsQuery.rootKey = "SiteLabels"

export const useSuspenseSiteLabelsQuery = <
  TData = Types.SiteLabelsQuery,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteLabelsQuery, TError, TData>({
    queryKey: ["SiteLabels", variables],
    queryFn: publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
      SiteLabelsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabels",
  variables,
]

useSuspenseSiteLabelsQuery.rootKey = "SiteLabels"

export const useInfiniteSiteLabelsQuery = <
  TData = InfiniteData<Types.SiteLabelsQuery>,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteLabelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLabels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
            SiteLabelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabels.infinite",
  variables,
]

useInfiniteSiteLabelsQuery.rootKey = "SiteLabels.infinite"

export const useSuspenseInfiniteSiteLabelsQuery = <
  TData = InfiniteData<Types.SiteLabelsQuery>,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteLabelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteLabelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLabels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
            SiteLabelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteLabelsQuery.getKey = (
  variables: Types.SiteLabelsQueryVariables,
) => ["SiteLabels.infinite", variables]

useSuspenseInfiniteSiteLabelsQuery.rootKey = "SiteLabels.infinite"

useSiteLabelsQuery.fetcher = (
  variables: Types.SiteLabelsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
    SiteLabelsDocument,
    variables,
    options,
  )

export const SiteResultConfigDocument = new TypedDocumentString(`
    query SiteResultConfig($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      resultConfig {
        finproduct {
          id
          type
          name
          financier {
            name
            pid
          }
        }
        disabledAt
        finrelDisabledAt
      }
    }
  }
}
    `)

export const useSiteResultConfigQuery = <
  TData = Types.SiteResultConfigQuery,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteResultConfigQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteResultConfigQuery, TError, TData>({
    queryKey: ["SiteResultConfig", variables],
    queryFn: publicFetcher<
      Types.SiteResultConfigQuery,
      Types.SiteResultConfigQueryVariables
    >(SiteResultConfigDocument, variables),
    ...options,
  })
}

useSiteResultConfigQuery.getKey = (variables: Types.SiteResultConfigQueryVariables) => [
  "SiteResultConfig",
  variables,
]

useSiteResultConfigQuery.rootKey = "SiteResultConfig"

export const useSuspenseSiteResultConfigQuery = <
  TData = Types.SiteResultConfigQuery,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteResultConfigQuery, TError, TData>({
    queryKey: ["SiteResultConfig", variables],
    queryFn: publicFetcher<
      Types.SiteResultConfigQuery,
      Types.SiteResultConfigQueryVariables
    >(SiteResultConfigDocument, variables),
    ...options,
  })
}

useSuspenseSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfig", variables]

useSuspenseSiteResultConfigQuery.rootKey = "SiteResultConfig"

export const useInfiniteSiteResultConfigQuery = <
  TData = InfiniteData<Types.SiteResultConfigQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteResultConfigQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResultConfig.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteResultConfigQuery,
            Types.SiteResultConfigQueryVariables
          >(SiteResultConfigDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfig.infinite", variables]

useInfiniteSiteResultConfigQuery.rootKey = "SiteResultConfig.infinite"

export const useSuspenseInfiniteSiteResultConfigQuery = <
  TData = InfiniteData<Types.SiteResultConfigQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteResultConfigQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResultConfig.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteResultConfigQuery,
            Types.SiteResultConfigQueryVariables
          >(SiteResultConfigDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfig.infinite", variables]

useSuspenseInfiniteSiteResultConfigQuery.rootKey = "SiteResultConfig.infinite"

useSiteResultConfigQuery.fetcher = (
  variables: Types.SiteResultConfigQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteResultConfigQuery, Types.SiteResultConfigQueryVariables>(
    SiteResultConfigDocument,
    variables,
    options,
  )

export const SiteEspmDocument = new TypedDocumentString(`
    query SiteEspm($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      espm {
        id
        createdAt
        lastUpdatedAt
        externalPropertyId
        xml
        accounts {
          id
          createdAt
          lastUpdatedAt
          externalAccountId
          xml
        }
        espmMeters {
          id
          createdAt
          lastUpdatedAt
          externalMeterId
          xml
          unitOfMeasure
          meterType
          espmConsumptions {
            id
            createdAt
            lastUpdatedAt
            externalConsumptionId
            xml
            start
            end
            usageKwh
            costUsd
          }
        }
      }
    }
  }
}
    `)

export const useSiteEspmQuery = <TData = Types.SiteEspmQuery, TError = unknown>(
  variables: Types.SiteEspmQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteEspmQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEspmQuery, TError, TData>({
    queryKey: ["SiteEspm", variables],
    queryFn: publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
      SiteEspmDocument,
      variables,
    ),
    ...options,
  })
}

useSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm",
  variables,
]

useSiteEspmQuery.rootKey = "SiteEspm"

export const useSuspenseSiteEspmQuery = <TData = Types.SiteEspmQuery, TError = unknown>(
  variables: Types.SiteEspmQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEspmQuery, TError, TData>({
    queryKey: ["SiteEspm", variables],
    queryFn: publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
      SiteEspmDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm",
  variables,
]

useSuspenseSiteEspmQuery.rootKey = "SiteEspm"

export const useInfiniteSiteEspmQuery = <
  TData = InfiniteData<Types.SiteEspmQuery>,
  TError = unknown,
>(
  variables: Types.SiteEspmQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEspmQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEspm.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
            SiteEspmDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm.infinite",
  variables,
]

useInfiniteSiteEspmQuery.rootKey = "SiteEspm.infinite"

export const useSuspenseInfiniteSiteEspmQuery = <
  TData = InfiniteData<Types.SiteEspmQuery>,
  TError = unknown,
>(
  variables: Types.SiteEspmQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEspmQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEspmQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEspm.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
            SiteEspmDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm.infinite",
  variables,
]

useSuspenseInfiniteSiteEspmQuery.rootKey = "SiteEspm.infinite"

useSiteEspmQuery.fetcher = (
  variables: Types.SiteEspmQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
    SiteEspmDocument,
    variables,
    options,
  )

export const SolarCaptchaSitesDocument = new TypedDocumentString(`
    query SolarCaptchaSites($sitePids: [String!]!) {
  admin {
    sites(sitePids: $sitePids) {
      pid
      fullAddress
      staticMapUrl
      existingSolarDetectionScore
      latitude
      longitude
      orgSlug
      latestSolarDetectionLabel {
        id
        hasSolar
        source
        createdAt
        userEmail
      }
    }
  }
}
    `)

export const useSolarCaptchaSitesQuery = <
  TData = Types.SolarCaptchaSitesQuery,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SolarCaptchaSitesQuery, TError, TData>({
    queryKey: ["SolarCaptchaSites", variables],
    queryFn: publicFetcher<
      Types.SolarCaptchaSitesQuery,
      Types.SolarCaptchaSitesQueryVariables
    >(SolarCaptchaSitesDocument, variables),
    ...options,
  })
}

useSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites", variables]

useSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites"

export const useSuspenseSolarCaptchaSitesQuery = <
  TData = Types.SolarCaptchaSitesQuery,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SolarCaptchaSitesQuery, TError, TData>({
    queryKey: ["SolarCaptchaSites", variables],
    queryFn: publicFetcher<
      Types.SolarCaptchaSitesQuery,
      Types.SolarCaptchaSitesQueryVariables
    >(SolarCaptchaSitesDocument, variables),
    ...options,
  })
}

useSuspenseSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites", variables]

useSuspenseSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites"

export const useInfiniteSolarCaptchaSitesQuery = <
  TData = InfiniteData<Types.SolarCaptchaSitesQuery>,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SolarCaptchaSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarCaptchaSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SolarCaptchaSitesQuery,
            Types.SolarCaptchaSitesQueryVariables
          >(SolarCaptchaSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites.infinite", variables]

useInfiniteSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites.infinite"

export const useSuspenseInfiniteSolarCaptchaSitesQuery = <
  TData = InfiniteData<Types.SolarCaptchaSitesQuery>,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SolarCaptchaSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarCaptchaSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SolarCaptchaSitesQuery,
            Types.SolarCaptchaSitesQueryVariables
          >(SolarCaptchaSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites.infinite", variables]

useSuspenseInfiniteSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites.infinite"

useSolarCaptchaSitesQuery.fetcher = (
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SolarCaptchaSitesQuery, Types.SolarCaptchaSitesQueryVariables>(
    SolarCaptchaSitesDocument,
    variables,
    options,
  )

export const PreviewBuildingFootprintsDocument = new TypedDocumentString(`
    query PreviewBuildingFootprints($sitePid: String!, $strategy: FootprintStrategy!) {
  admin {
    site(pid: $sitePid) {
      previewBuildingFootprints(strategy: $strategy) {
        __typename
        ... on Footprint {
          latitude
          longitude
          bounds
          roofShape
          roofShapeGeojson
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const usePreviewBuildingFootprintsQuery = <
  TData = Types.PreviewBuildingFootprintsQuery,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>({
    queryKey: ["PreviewBuildingFootprints", variables],
    queryFn: publicFetcher<
      Types.PreviewBuildingFootprintsQuery,
      Types.PreviewBuildingFootprintsQueryVariables
    >(PreviewBuildingFootprintsDocument, variables),
    ...options,
  })
}

usePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints", variables]

usePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints"

export const useSuspensePreviewBuildingFootprintsQuery = <
  TData = Types.PreviewBuildingFootprintsQuery,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>({
    queryKey: ["PreviewBuildingFootprints", variables],
    queryFn: publicFetcher<
      Types.PreviewBuildingFootprintsQuery,
      Types.PreviewBuildingFootprintsQueryVariables
    >(PreviewBuildingFootprintsDocument, variables),
    ...options,
  })
}

useSuspensePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints", variables]

useSuspensePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints"

export const useInfinitePreviewBuildingFootprintsQuery = <
  TData = InfiniteData<Types.PreviewBuildingFootprintsQuery>,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PreviewBuildingFootprints.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PreviewBuildingFootprintsQuery,
            Types.PreviewBuildingFootprintsQueryVariables
          >(PreviewBuildingFootprintsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints.infinite", variables]

useInfinitePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints.infinite"

export const useSuspenseInfinitePreviewBuildingFootprintsQuery = <
  TData = InfiniteData<Types.PreviewBuildingFootprintsQuery>,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PreviewBuildingFootprints.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PreviewBuildingFootprintsQuery,
            Types.PreviewBuildingFootprintsQueryVariables
          >(PreviewBuildingFootprintsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints.infinite", variables]

useSuspenseInfinitePreviewBuildingFootprintsQuery.rootKey =
  "PreviewBuildingFootprints.infinite"

usePreviewBuildingFootprintsQuery.fetcher = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PreviewBuildingFootprintsQuery,
    Types.PreviewBuildingFootprintsQueryVariables
  >(PreviewBuildingFootprintsDocument, variables, options)

export const ListHostingCapacityMapsDocument = new TypedDocumentString(`
    query ListHostingCapacityMaps {
  admin {
    hostingCapacityMaps {
      all {
        id
        utility {
          id
          pid
          name
          shortName
        }
        url
        requiresLogin
        types
      }
    }
  }
}
    `)

export const useListHostingCapacityMapsQuery = <
  TData = Types.ListHostingCapacityMapsQuery,
  TError = unknown,
>(
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ListHostingCapacityMapsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ListHostingCapacityMaps"]
        : ["ListHostingCapacityMaps", variables],
    queryFn: publicFetcher<
      Types.ListHostingCapacityMapsQuery,
      Types.ListHostingCapacityMapsQueryVariables
    >(ListHostingCapacityMapsDocument, variables),
    ...options,
  })
}

useListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps"]
    : ["ListHostingCapacityMaps", variables]

useListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps"

export const useSuspenseListHostingCapacityMapsQuery = <
  TData = Types.ListHostingCapacityMapsQuery,
  TError = unknown,
>(
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ListHostingCapacityMapsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ListHostingCapacityMaps"]
        : ["ListHostingCapacityMaps", variables],
    queryFn: publicFetcher<
      Types.ListHostingCapacityMapsQuery,
      Types.ListHostingCapacityMapsQueryVariables
    >(ListHostingCapacityMapsDocument, variables),
    ...options,
  })
}

useSuspenseListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps"]
    : ["ListHostingCapacityMaps", variables]

useSuspenseListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps"

export const useInfiniteListHostingCapacityMapsQuery = <
  TData = InfiniteData<Types.ListHostingCapacityMapsQuery>,
  TError = unknown,
>(
  variables: Types.ListHostingCapacityMapsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ListHostingCapacityMapsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ListHostingCapacityMaps.infinite"]
            : ["ListHostingCapacityMaps.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ListHostingCapacityMapsQuery,
            Types.ListHostingCapacityMapsQueryVariables
          >(ListHostingCapacityMapsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps.infinite"]
    : ["ListHostingCapacityMaps.infinite", variables]

useInfiniteListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps.infinite"

export const useSuspenseInfiniteListHostingCapacityMapsQuery = <
  TData = InfiniteData<Types.ListHostingCapacityMapsQuery>,
  TError = unknown,
>(
  variables: Types.ListHostingCapacityMapsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ListHostingCapacityMapsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ListHostingCapacityMaps.infinite"]
            : ["ListHostingCapacityMaps.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ListHostingCapacityMapsQuery,
            Types.ListHostingCapacityMapsQueryVariables
          >(ListHostingCapacityMapsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps.infinite"]
    : ["ListHostingCapacityMaps.infinite", variables]

useSuspenseInfiniteListHostingCapacityMapsQuery.rootKey =
  "ListHostingCapacityMaps.infinite"

useListHostingCapacityMapsQuery.fetcher = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ListHostingCapacityMapsQuery,
    Types.ListHostingCapacityMapsQueryVariables
  >(ListHostingCapacityMapsDocument, variables, options)

export const AdminMarketplaceFacetsDocument = new TypedDocumentString(`
    query AdminMarketplaceFacets {
  admin {
    marketplace {
      view {
        stats {
          buildingOwners
          assetManagers
          portfolioManagers
          bidPackageNames
          financiers
          portfolios
          salesOwners
          opsOwners
          utilityNames
        }
      }
    }
  }
}
    `)

export const useAdminMarketplaceFacetsQuery = <
  TData = Types.AdminMarketplaceFacetsQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceFacetsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceFacets"]
        : ["AdminMarketplaceFacets", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceFacetsQuery,
      Types.AdminMarketplaceFacetsQueryVariables
    >(AdminMarketplaceFacetsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceFacetsQuery.getKey = (
  variables?: Types.AdminMarketplaceFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceFacets"]
    : ["AdminMarketplaceFacets", variables]

useAdminMarketplaceFacetsQuery.rootKey = "AdminMarketplaceFacets"

export const useSuspenseAdminMarketplaceFacetsQuery = <
  TData = Types.AdminMarketplaceFacetsQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceFacetsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceFacets"]
        : ["AdminMarketplaceFacets", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceFacetsQuery,
      Types.AdminMarketplaceFacetsQueryVariables
    >(AdminMarketplaceFacetsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceFacetsQuery.getKey = (
  variables?: Types.AdminMarketplaceFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceFacets"]
    : ["AdminMarketplaceFacets", variables]

useSuspenseAdminMarketplaceFacetsQuery.rootKey = "AdminMarketplaceFacets"

export const useInfiniteAdminMarketplaceFacetsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceFacetsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceFacets.infinite"]
            : ["AdminMarketplaceFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceFacetsQuery,
            Types.AdminMarketplaceFacetsQueryVariables
          >(AdminMarketplaceFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceFacetsQuery.getKey = (
  variables?: Types.AdminMarketplaceFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceFacets.infinite"]
    : ["AdminMarketplaceFacets.infinite", variables]

useInfiniteAdminMarketplaceFacetsQuery.rootKey = "AdminMarketplaceFacets.infinite"

export const useSuspenseInfiniteAdminMarketplaceFacetsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceFacetsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMarketplaceFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMarketplaceFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceFacets.infinite"]
            : ["AdminMarketplaceFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceFacetsQuery,
            Types.AdminMarketplaceFacetsQueryVariables
          >(AdminMarketplaceFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceFacetsQuery.getKey = (
  variables?: Types.AdminMarketplaceFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceFacets.infinite"]
    : ["AdminMarketplaceFacets.infinite", variables]

useSuspenseInfiniteAdminMarketplaceFacetsQuery.rootKey =
  "AdminMarketplaceFacets.infinite"

useAdminMarketplaceFacetsQuery.fetcher = (
  variables?: Types.AdminMarketplaceFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceFacetsQuery,
    Types.AdminMarketplaceFacetsQueryVariables
  >(AdminMarketplaceFacetsDocument, variables, options)

export const AdminMarketplaceViewDocument = new TypedDocumentString(`
    query AdminMarketplaceView($filterBy: AdminMarketplaceTableFilter, $sortBy: [AdminMarketplaceTableSort!]) {
  admin {
    marketplace {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useAdminMarketplaceViewQuery = <
  TData = Types.AdminMarketplaceViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceView"]
        : ["AdminMarketplaceView", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceViewQuery,
      Types.AdminMarketplaceViewQueryVariables
    >(AdminMarketplaceViewDocument, variables),
    ...options,
  })
}

useAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView"]
    : ["AdminMarketplaceView", variables]

useAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView"

export const useSuspenseAdminMarketplaceViewQuery = <
  TData = Types.AdminMarketplaceViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceView"]
        : ["AdminMarketplaceView", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceViewQuery,
      Types.AdminMarketplaceViewQueryVariables
    >(AdminMarketplaceViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView"]
    : ["AdminMarketplaceView", variables]

useSuspenseAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView"

export const useInfiniteAdminMarketplaceViewQuery = <
  TData = InfiniteData<Types.AdminMarketplaceViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceView.infinite"]
            : ["AdminMarketplaceView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceViewQuery,
            Types.AdminMarketplaceViewQueryVariables
          >(AdminMarketplaceViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView.infinite"]
    : ["AdminMarketplaceView.infinite", variables]

useInfiniteAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView.infinite"

export const useSuspenseInfiniteAdminMarketplaceViewQuery = <
  TData = InfiniteData<Types.AdminMarketplaceViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMarketplaceViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceView.infinite"]
            : ["AdminMarketplaceView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceViewQuery,
            Types.AdminMarketplaceViewQueryVariables
          >(AdminMarketplaceViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView.infinite"]
    : ["AdminMarketplaceView.infinite", variables]

useSuspenseInfiniteAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView.infinite"

useAdminMarketplaceViewQuery.fetcher = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceViewQuery,
    Types.AdminMarketplaceViewQueryVariables
  >(AdminMarketplaceViewDocument, variables, options)

export const AdminMarketplaceRowsDocument = new TypedDocumentString(`
    query AdminMarketplaceRows($keys: [Int!]!) {
  admin {
    marketplace {
      rows(keys: $keys) {
        id
        projectPid
        projectName
        orgSlug
        orgName
        portfolioName
        sitePid
        bidPackagePid
        bidPackageName
        bidPackagePidFromBid
        bidPackageNameFromBid
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        portfolioManager
        projectStatus
        projectIsClosed
        projectClosedAt
        projectCloseReason
        projectCloseCustomReason
        isAwarded
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        salePlannedYear
        salePlans
        buildingLegalName
        propertyFundName
        leaseSignatory
        counselApproval
        leaseApprovers
        externalLeaseApprovers
        needsTenantApproval
        roofRightsHolder
        propertyLenders
        propertyInsurer
        constructionEmail
        propertySiteVisitContacts
        leasePropertyLegalDescription
        leaseLegalOwnerName
        signatureBlocks
        isFannieMaeLender
        isFmGlobalInsurer
        utilityId
        utilityName
        finproductType
        specFinproductType
        biddingOpenAt
        biddingClosedAt
        bidCount
        bidderCount
        awardedTo
        roofRentY1Usd
        noiLifetimeUsd
        roofRentEscalator
        pvSizeKwDc
        lumenRevenue
        lumenFeeUsdPerWatt
        lumenFeeUsdPerKwhStorage
        hubspotRecordId
        hubspotRevenueRecordId
        salesOwnerEmail
        salesOwnerPid
        opsOwnerEmail
        opsOwnerPid
        rofoSignedDate
        rofoSignedEstimated
        rofoSignedActual
        preBidMeetingEstimated
        preBidMeetingActual
        preBidMeetingDate
        biddingOpenedEstimated
        biddingOpenedActual
        biddingOpenedDate
        biddingClosedEstimated
        biddingClosedActual
        biddingClosedDate
        bidsPresentedEstimated
        bidsPresentedActual
        bidsPresentedDate
        ixSubmittedEstimated
        ixSubmittedActual
        ixSubmittedDate
        workableIxResultsReceivedEstimated
        workableIxResultsReceivedActual
        workableIxResultsReceivedDate
        ixApprovedEstimated
        ixApprovedActual
        ixApprovedDate
        csProgramSubmittedEstimated
        csProgramSubmittedActual
        csProgramSubmittedDate
        csProgramApprovedEstimated
        csProgramApprovedActual
        csProgramApprovedDate
        awardedToDeveloperEstimated
        awardedToDeveloperActual
        awardedToDeveloperDate
        leaseSignedEstimated
        leaseSignedActual
        leaseSignedDate
        ntpEstimated
        ntpActual
        ntpDate
        codEstimated
        codActual
        codDate
        projectPid
        progressNotes {
          ...ProgressNotes
        }
        progressPriority
        hostAwardSent
        changeOrderSent
        scopeOfWorkSent
      }
    }
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAdminMarketplaceRowsQuery = <
  TData = Types.AdminMarketplaceRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceRowsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceRows", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceRowsQuery,
      Types.AdminMarketplaceRowsQueryVariables
    >(AdminMarketplaceRowsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows", variables]

useAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows"

export const useSuspenseAdminMarketplaceRowsQuery = <
  TData = Types.AdminMarketplaceRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceRowsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceRows", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceRowsQuery,
      Types.AdminMarketplaceRowsQueryVariables
    >(AdminMarketplaceRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows", variables]

useSuspenseAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows"

export const useInfiniteAdminMarketplaceRowsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminMarketplaceRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceRowsQuery,
            Types.AdminMarketplaceRowsQueryVariables
          >(AdminMarketplaceRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows.infinite", variables]

useInfiniteAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows.infinite"

export const useSuspenseInfiniteAdminMarketplaceRowsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMarketplaceRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminMarketplaceRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceRowsQuery,
            Types.AdminMarketplaceRowsQueryVariables
          >(AdminMarketplaceRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows.infinite", variables]

useSuspenseInfiniteAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows.infinite"

useAdminMarketplaceRowsQuery.fetcher = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceRowsQuery,
    Types.AdminMarketplaceRowsQueryVariables
  >(AdminMarketplaceRowsDocument, variables, options)

export const AdminMarketplaceProjectDocumentsDocument = new TypedDocumentString(`
    query AdminMarketplaceProjectDocuments($projectPids: [String!]!) {
  admin {
    marketplaceProjectDocuments(
      projectPids: $projectPids
      includeTags: [INVESTOR__HOST_AWARD, INVESTOR__CHANGE_ORDER, INVESTOR__SCOPE_OF_WORK]
    ) {
      projectPid
      documents {
        pid
        fileName
        downloadHref
        tag
      }
    }
  }
}
    `)

export const useAdminMarketplaceProjectDocumentsQuery = <
  TData = Types.AdminMarketplaceProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceProjectDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceProjectDocumentsQuery,
      Types.AdminMarketplaceProjectDocumentsQueryVariables
    >(AdminMarketplaceProjectDocumentsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceProjectDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
) => ["AdminMarketplaceProjectDocuments", variables]

useAdminMarketplaceProjectDocumentsQuery.rootKey = "AdminMarketplaceProjectDocuments"

export const useSuspenseAdminMarketplaceProjectDocumentsQuery = <
  TData = Types.AdminMarketplaceProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceProjectDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceProjectDocumentsQuery,
      Types.AdminMarketplaceProjectDocumentsQueryVariables
    >(AdminMarketplaceProjectDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceProjectDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
) => ["AdminMarketplaceProjectDocuments", variables]

useSuspenseAdminMarketplaceProjectDocumentsQuery.rootKey =
  "AdminMarketplaceProjectDocuments"

export const useInfiniteAdminMarketplaceProjectDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceProjectDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceProjectDocumentsQuery,
            Types.AdminMarketplaceProjectDocumentsQueryVariables
          >(AdminMarketplaceProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceProjectDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
) => ["AdminMarketplaceProjectDocuments.infinite", variables]

useInfiniteAdminMarketplaceProjectDocumentsQuery.rootKey =
  "AdminMarketplaceProjectDocuments.infinite"

export const useSuspenseInfiniteAdminMarketplaceProjectDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceProjectDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminMarketplaceProjectDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceProjectDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceProjectDocumentsQuery,
            Types.AdminMarketplaceProjectDocumentsQueryVariables
          >(AdminMarketplaceProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceProjectDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
) => ["AdminMarketplaceProjectDocuments.infinite", variables]

useSuspenseInfiniteAdminMarketplaceProjectDocumentsQuery.rootKey =
  "AdminMarketplaceProjectDocuments.infinite"

useAdminMarketplaceProjectDocumentsQuery.fetcher = (
  variables: Types.AdminMarketplaceProjectDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceProjectDocumentsQuery,
    Types.AdminMarketplaceProjectDocumentsQueryVariables
  >(AdminMarketplaceProjectDocumentsDocument, variables, options)

export const AdminMarketplaceSiteDocumentsDocument = new TypedDocumentString(`
    query AdminMarketplaceSiteDocuments($keys: [Int!]!) {
  admin {
    bundleRowParentDocuments(
      bundleParentRowIds: $keys
      includeTags: [
        SITE__ROOF
        SITE__ROOF_WARRANTY
        SITE__STRUCTURAL_DRAWINGS
        SITE__ELECTRICAL_DIAGRAMS
        SITE__WIRING_INSTRUCTIONS
      ]
    ) {
      sitePid
      documents {
        pid
        fileName
        downloadHref
        tag
      }
    }
  }
}
    `)

export const useAdminMarketplaceSiteDocumentsQuery = <
  TData = Types.AdminMarketplaceSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceSiteDocumentsQuery,
      Types.AdminMarketplaceSiteDocumentsQueryVariables
    >(AdminMarketplaceSiteDocumentsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments", variables]

useAdminMarketplaceSiteDocumentsQuery.rootKey = "AdminMarketplaceSiteDocuments"

export const useSuspenseAdminMarketplaceSiteDocumentsQuery = <
  TData = Types.AdminMarketplaceSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceSiteDocumentsQuery,
      Types.AdminMarketplaceSiteDocumentsQueryVariables
    >(AdminMarketplaceSiteDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments", variables]

useSuspenseAdminMarketplaceSiteDocumentsQuery.rootKey = "AdminMarketplaceSiteDocuments"

export const useInfiniteAdminMarketplaceSiteDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceSiteDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceSiteDocumentsQuery,
            Types.AdminMarketplaceSiteDocumentsQueryVariables
          >(AdminMarketplaceSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments.infinite", variables]

useInfiniteAdminMarketplaceSiteDocumentsQuery.rootKey =
  "AdminMarketplaceSiteDocuments.infinite"

export const useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminMarketplaceSiteDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceSiteDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceSiteDocumentsQuery,
            Types.AdminMarketplaceSiteDocumentsQueryVariables
          >(AdminMarketplaceSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments.infinite", variables]

useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery.rootKey =
  "AdminMarketplaceSiteDocuments.infinite"

useAdminMarketplaceSiteDocumentsQuery.fetcher = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceSiteDocumentsQuery,
    Types.AdminMarketplaceSiteDocumentsQueryVariables
  >(AdminMarketplaceSiteDocumentsDocument, variables, options)

export const ActiveAdminsDocument = new TypedDocumentString(`
    query ActiveAdmins($jobFunction: JobFunction) {
  admin {
    activeAdmins(jobFunction: $jobFunction) {
      pid
      isAdmin
      name
      email
      jobFunctions
    }
  }
}
    `)

export const useActiveAdminsQuery = <TData = Types.ActiveAdminsQuery, TError = unknown>(
  variables?: Types.ActiveAdminsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveAdminsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveAdminsQuery, TError, TData>({
    queryKey: variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables],
    queryFn: publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
      ActiveAdminsDocument,
      variables,
    ),
    ...options,
  })
}

useActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables]

useActiveAdminsQuery.rootKey = "ActiveAdmins"

export const useSuspenseActiveAdminsQuery = <
  TData = Types.ActiveAdminsQuery,
  TError = unknown,
>(
  variables?: Types.ActiveAdminsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveAdminsQuery, TError, TData>({
    queryKey: variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables],
    queryFn: publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
      ActiveAdminsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables]

useSuspenseActiveAdminsQuery.rootKey = "ActiveAdmins"

export const useInfiniteActiveAdminsQuery = <
  TData = InfiniteData<Types.ActiveAdminsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveAdminsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveAdminsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ActiveAdmins.infinite"]
            : ["ActiveAdmins.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
            ActiveAdminsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined
    ? ["ActiveAdmins.infinite"]
    : ["ActiveAdmins.infinite", variables]

useInfiniteActiveAdminsQuery.rootKey = "ActiveAdmins.infinite"

export const useSuspenseInfiniteActiveAdminsQuery = <
  TData = InfiniteData<Types.ActiveAdminsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveAdminsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveAdminsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ActiveAdmins.infinite"]
            : ["ActiveAdmins.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
            ActiveAdminsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveAdminsQuery.getKey = (
  variables?: Types.ActiveAdminsQueryVariables,
) =>
  variables === undefined
    ? ["ActiveAdmins.infinite"]
    : ["ActiveAdmins.infinite", variables]

useSuspenseInfiniteActiveAdminsQuery.rootKey = "ActiveAdmins.infinite"

useActiveAdminsQuery.fetcher = (
  variables?: Types.ActiveAdminsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
    ActiveAdminsDocument,
    variables,
    options,
  )

export const AdminUserDetailDocument = new TypedDocumentString(`
    query AdminUserDetail($userId: Int!) {
  admin {
    user(userId: $userId) {
      ...UserListUser
      pageViews {
        pathname
        createdAt
      }
    }
  }
}
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  jobFunctions
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  adminOrgRoles {
    org {
      pid
      name
      orgSlug
    }
    role
    grantsAllPortfolios
    orgIsDeveloper
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}`)

export const useAdminUserDetailQuery = <
  TData = Types.AdminUserDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminUserDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminUserDetailQuery, TError, TData>({
    queryKey: ["AdminUserDetail", variables],
    queryFn: publicFetcher<
      Types.AdminUserDetailQuery,
      Types.AdminUserDetailQueryVariables
    >(AdminUserDetailDocument, variables),
    ...options,
  })
}

useAdminUserDetailQuery.getKey = (variables: Types.AdminUserDetailQueryVariables) => [
  "AdminUserDetail",
  variables,
]

useAdminUserDetailQuery.rootKey = "AdminUserDetail"

export const useSuspenseAdminUserDetailQuery = <
  TData = Types.AdminUserDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUserDetailQuery, TError, TData>({
    queryKey: ["AdminUserDetail", variables],
    queryFn: publicFetcher<
      Types.AdminUserDetailQuery,
      Types.AdminUserDetailQueryVariables
    >(AdminUserDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetail", variables]

useSuspenseAdminUserDetailQuery.rootKey = "AdminUserDetail"

export const useInfiniteAdminUserDetailQuery = <
  TData = InfiniteData<Types.AdminUserDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUserDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUserDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUserDetailQuery,
            Types.AdminUserDetailQueryVariables
          >(AdminUserDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetail.infinite", variables]

useInfiniteAdminUserDetailQuery.rootKey = "AdminUserDetail.infinite"

export const useSuspenseInfiniteAdminUserDetailQuery = <
  TData = InfiniteData<Types.AdminUserDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUserDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUserDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUserDetailQuery,
            Types.AdminUserDetailQueryVariables
          >(AdminUserDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetail.infinite", variables]

useSuspenseInfiniteAdminUserDetailQuery.rootKey = "AdminUserDetail.infinite"

useAdminUserDetailQuery.fetcher = (
  variables: Types.AdminUserDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminUserDetailQuery, Types.AdminUserDetailQueryVariables>(
    AdminUserDetailDocument,
    variables,
    options,
  )

export const AdminAllDocumentsViewDocument = new TypedDocumentString(`
    query AdminAllDocumentsView($filterBy: AdminAllDocumentsTableFilter, $sortBy: [AdminAllDocumentsTableSort!], $sitePid: String) {
  admin {
    allDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerOrgNames
          developerOrgNames
          createdByNames
          contentTypes
        }
      }
    }
  }
}
    `)

export const useAdminAllDocumentsViewQuery = <
  TData = Types.AdminAllDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminAllDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminAllDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminAllDocumentsView"]
        : ["AdminAllDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsViewQuery,
      Types.AdminAllDocumentsViewQueryVariables
    >(AdminAllDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsView"]
    : ["AdminAllDocumentsView", variables]

useAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView"

export const useSuspenseAdminAllDocumentsViewQuery = <
  TData = Types.AdminAllDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminAllDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminAllDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminAllDocumentsView"]
        : ["AdminAllDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsViewQuery,
      Types.AdminAllDocumentsViewQueryVariables
    >(AdminAllDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsView"]
    : ["AdminAllDocumentsView", variables]

useSuspenseAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView"

export const useInfiniteAdminAllDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminAllDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminAllDocumentsView.infinite"]
            : ["AdminAllDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsViewQuery,
            Types.AdminAllDocumentsViewQueryVariables
          >(AdminAllDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsView.infinite"]
    : ["AdminAllDocumentsView.infinite", variables]

useInfiniteAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView.infinite"

export const useSuspenseInfiniteAdminAllDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminAllDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminAllDocumentsView.infinite"]
            : ["AdminAllDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsViewQuery,
            Types.AdminAllDocumentsViewQueryVariables
          >(AdminAllDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsView.infinite"]
    : ["AdminAllDocumentsView.infinite", variables]

useSuspenseInfiniteAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView.infinite"

useAdminAllDocumentsViewQuery.fetcher = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminAllDocumentsViewQuery,
    Types.AdminAllDocumentsViewQueryVariables
  >(AdminAllDocumentsViewDocument, variables, options)

export const AdminAllDocumentsRowsDocument = new TypedDocumentString(`
    query AdminAllDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    allDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        contentType
        fileSize
        pdfTitle
        pdfPageCount
        pdfHasTextLayer
        pdfParseError
        dateCreated
        createdByName
        sitePid
        siteAddress
        bundlePid
        bundleName
        bundleParentPid
        bundleParentName
        investorBidPid
        investorBidName
        ownerOrgName
        developerOrgName
        hasAiAnalysis
      }
    }
  }
}
    `)

export const useAdminAllDocumentsRowsQuery = <
  TData = Types.AdminAllDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminAllDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsRowsQuery,
      Types.AdminAllDocumentsRowsQueryVariables
    >(AdminAllDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRows", variables]

useAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows"

export const useSuspenseAdminAllDocumentsRowsQuery = <
  TData = Types.AdminAllDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminAllDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsRowsQuery,
      Types.AdminAllDocumentsRowsQueryVariables
    >(AdminAllDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRows", variables]

useSuspenseAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows"

export const useInfiniteAdminAllDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminAllDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsRowsQuery,
            Types.AdminAllDocumentsRowsQueryVariables
          >(AdminAllDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRows.infinite", variables]

useInfiniteAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows.infinite"

export const useSuspenseInfiniteAdminAllDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminAllDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsRowsQuery,
            Types.AdminAllDocumentsRowsQueryVariables
          >(AdminAllDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows.infinite"

useAdminAllDocumentsRowsQuery.fetcher = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminAllDocumentsRowsQuery,
    Types.AdminAllDocumentsRowsQueryVariables
  >(AdminAllDocumentsRowsDocument, variables, options)

export const AdminLeaseAnalysisSchemaDocument = new TypedDocumentString(`
    query AdminLeaseAnalysisSchema {
  admin {
    leaseAnalysisSchema {
      id
      label
      fields {
        id
        label
        render
      }
    }
  }
}
    `)

export const useAdminLeaseAnalysisSchemaQuery = <
  TData = Types.AdminLeaseAnalysisSchemaQuery,
  TError = unknown,
>(
  variables?: Types.AdminLeaseAnalysisSchemaQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminLeaseAnalysisSchemaQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminLeaseAnalysisSchema"]
        : ["AdminLeaseAnalysisSchema", variables],
    queryFn: publicFetcher<
      Types.AdminLeaseAnalysisSchemaQuery,
      Types.AdminLeaseAnalysisSchemaQueryVariables
    >(AdminLeaseAnalysisSchemaDocument, variables),
    ...options,
  })
}

useAdminLeaseAnalysisSchemaQuery.getKey = (
  variables?: Types.AdminLeaseAnalysisSchemaQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysisSchema"]
    : ["AdminLeaseAnalysisSchema", variables]

useAdminLeaseAnalysisSchemaQuery.rootKey = "AdminLeaseAnalysisSchema"

export const useSuspenseAdminLeaseAnalysisSchemaQuery = <
  TData = Types.AdminLeaseAnalysisSchemaQuery,
  TError = unknown,
>(
  variables?: Types.AdminLeaseAnalysisSchemaQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminLeaseAnalysisSchemaQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminLeaseAnalysisSchema"]
        : ["AdminLeaseAnalysisSchema", variables],
    queryFn: publicFetcher<
      Types.AdminLeaseAnalysisSchemaQuery,
      Types.AdminLeaseAnalysisSchemaQueryVariables
    >(AdminLeaseAnalysisSchemaDocument, variables),
    ...options,
  })
}

useSuspenseAdminLeaseAnalysisSchemaQuery.getKey = (
  variables?: Types.AdminLeaseAnalysisSchemaQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysisSchema"]
    : ["AdminLeaseAnalysisSchema", variables]

useSuspenseAdminLeaseAnalysisSchemaQuery.rootKey = "AdminLeaseAnalysisSchema"

export const useInfiniteAdminLeaseAnalysisSchemaQuery = <
  TData = InfiniteData<Types.AdminLeaseAnalysisSchemaQuery>,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysisSchemaQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminLeaseAnalysisSchemaQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminLeaseAnalysisSchema.infinite"]
            : ["AdminLeaseAnalysisSchema.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminLeaseAnalysisSchemaQuery,
            Types.AdminLeaseAnalysisSchemaQueryVariables
          >(AdminLeaseAnalysisSchemaDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminLeaseAnalysisSchemaQuery.getKey = (
  variables?: Types.AdminLeaseAnalysisSchemaQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysisSchema.infinite"]
    : ["AdminLeaseAnalysisSchema.infinite", variables]

useInfiniteAdminLeaseAnalysisSchemaQuery.rootKey = "AdminLeaseAnalysisSchema.infinite"

export const useSuspenseInfiniteAdminLeaseAnalysisSchemaQuery = <
  TData = InfiniteData<Types.AdminLeaseAnalysisSchemaQuery>,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysisSchemaQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminLeaseAnalysisSchemaQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminLeaseAnalysisSchemaQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminLeaseAnalysisSchema.infinite"]
            : ["AdminLeaseAnalysisSchema.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminLeaseAnalysisSchemaQuery,
            Types.AdminLeaseAnalysisSchemaQueryVariables
          >(AdminLeaseAnalysisSchemaDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminLeaseAnalysisSchemaQuery.getKey = (
  variables?: Types.AdminLeaseAnalysisSchemaQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysisSchema.infinite"]
    : ["AdminLeaseAnalysisSchema.infinite", variables]

useSuspenseInfiniteAdminLeaseAnalysisSchemaQuery.rootKey =
  "AdminLeaseAnalysisSchema.infinite"

useAdminLeaseAnalysisSchemaQuery.fetcher = (
  variables?: Types.AdminLeaseAnalysisSchemaQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminLeaseAnalysisSchemaQuery,
    Types.AdminLeaseAnalysisSchemaQueryVariables
  >(AdminLeaseAnalysisSchemaDocument, variables, options)

export const AdminLeaseAnalysesViewDocument = new TypedDocumentString(`
    query AdminLeaseAnalysesView($filterBy: AdminLeaseAnalysesTableFilter, $sortBy: [AdminLeaseAnalysesTableSort!]) {
  admin {
    leaseAnalyses {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerOrgNames
          developerOrgNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminLeaseAnalysesViewQuery = <
  TData = Types.AdminLeaseAnalysesViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminLeaseAnalysesViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminLeaseAnalysesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminLeaseAnalysesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminLeaseAnalysesViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminLeaseAnalysesView"]
        : ["AdminLeaseAnalysesView", variables],
    queryFn: publicFetcher<
      Types.AdminLeaseAnalysesViewQuery,
      Types.AdminLeaseAnalysesViewQueryVariables
    >(AdminLeaseAnalysesViewDocument, variables),
    ...options,
  })
}

useAdminLeaseAnalysesViewQuery.getKey = (
  variables?: Types.AdminLeaseAnalysesViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysesView"]
    : ["AdminLeaseAnalysesView", variables]

useAdminLeaseAnalysesViewQuery.rootKey = "AdminLeaseAnalysesView"

export const useSuspenseAdminLeaseAnalysesViewQuery = <
  TData = Types.AdminLeaseAnalysesViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminLeaseAnalysesViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminLeaseAnalysesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminLeaseAnalysesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminLeaseAnalysesViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminLeaseAnalysesView"]
        : ["AdminLeaseAnalysesView", variables],
    queryFn: publicFetcher<
      Types.AdminLeaseAnalysesViewQuery,
      Types.AdminLeaseAnalysesViewQueryVariables
    >(AdminLeaseAnalysesViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminLeaseAnalysesViewQuery.getKey = (
  variables?: Types.AdminLeaseAnalysesViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysesView"]
    : ["AdminLeaseAnalysesView", variables]

useSuspenseAdminLeaseAnalysesViewQuery.rootKey = "AdminLeaseAnalysesView"

export const useInfiniteAdminLeaseAnalysesViewQuery = <
  TData = InfiniteData<Types.AdminLeaseAnalysesViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysesViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminLeaseAnalysesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminLeaseAnalysesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminLeaseAnalysesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminLeaseAnalysesView.infinite"]
            : ["AdminLeaseAnalysesView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminLeaseAnalysesViewQuery,
            Types.AdminLeaseAnalysesViewQueryVariables
          >(AdminLeaseAnalysesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminLeaseAnalysesViewQuery.getKey = (
  variables?: Types.AdminLeaseAnalysesViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysesView.infinite"]
    : ["AdminLeaseAnalysesView.infinite", variables]

useInfiniteAdminLeaseAnalysesViewQuery.rootKey = "AdminLeaseAnalysesView.infinite"

export const useSuspenseInfiniteAdminLeaseAnalysesViewQuery = <
  TData = InfiniteData<Types.AdminLeaseAnalysesViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysesViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminLeaseAnalysesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminLeaseAnalysesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminLeaseAnalysesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminLeaseAnalysesView.infinite"]
            : ["AdminLeaseAnalysesView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminLeaseAnalysesViewQuery,
            Types.AdminLeaseAnalysesViewQueryVariables
          >(AdminLeaseAnalysesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminLeaseAnalysesViewQuery.getKey = (
  variables?: Types.AdminLeaseAnalysesViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminLeaseAnalysesView.infinite"]
    : ["AdminLeaseAnalysesView.infinite", variables]

useSuspenseInfiniteAdminLeaseAnalysesViewQuery.rootKey =
  "AdminLeaseAnalysesView.infinite"

useAdminLeaseAnalysesViewQuery.fetcher = (
  variables?: Types.AdminLeaseAnalysesViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminLeaseAnalysesViewQuery,
    Types.AdminLeaseAnalysesViewQueryVariables
  >(AdminLeaseAnalysesViewDocument, variables, options)

export const AdminLeaseAnalysesRowsDocument = new TypedDocumentString(`
    query AdminLeaseAnalysesRows($keys: [Int!]!) {
  admin {
    leaseAnalyses {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        ownerOrgName
        developerOrgName
        sitePid
        siteAddress
        siteState
        projectNames
        hasExtraction
        extractionJson
      }
    }
  }
}
    `)

export const useAdminLeaseAnalysesRowsQuery = <
  TData = Types.AdminLeaseAnalysesRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminLeaseAnalysesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminLeaseAnalysesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminLeaseAnalysesRowsQuery, TError, TData>({
    queryKey: ["AdminLeaseAnalysesRows", variables],
    queryFn: publicFetcher<
      Types.AdminLeaseAnalysesRowsQuery,
      Types.AdminLeaseAnalysesRowsQueryVariables
    >(AdminLeaseAnalysesRowsDocument, variables),
    ...options,
  })
}

useAdminLeaseAnalysesRowsQuery.getKey = (
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
) => ["AdminLeaseAnalysesRows", variables]

useAdminLeaseAnalysesRowsQuery.rootKey = "AdminLeaseAnalysesRows"

export const useSuspenseAdminLeaseAnalysesRowsQuery = <
  TData = Types.AdminLeaseAnalysesRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminLeaseAnalysesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminLeaseAnalysesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminLeaseAnalysesRowsQuery, TError, TData>({
    queryKey: ["AdminLeaseAnalysesRows", variables],
    queryFn: publicFetcher<
      Types.AdminLeaseAnalysesRowsQuery,
      Types.AdminLeaseAnalysesRowsQueryVariables
    >(AdminLeaseAnalysesRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminLeaseAnalysesRowsQuery.getKey = (
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
) => ["AdminLeaseAnalysesRows", variables]

useSuspenseAdminLeaseAnalysesRowsQuery.rootKey = "AdminLeaseAnalysesRows"

export const useInfiniteAdminLeaseAnalysesRowsQuery = <
  TData = InfiniteData<Types.AdminLeaseAnalysesRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminLeaseAnalysesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminLeaseAnalysesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminLeaseAnalysesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminLeaseAnalysesRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminLeaseAnalysesRowsQuery,
            Types.AdminLeaseAnalysesRowsQueryVariables
          >(AdminLeaseAnalysesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminLeaseAnalysesRowsQuery.getKey = (
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
) => ["AdminLeaseAnalysesRows.infinite", variables]

useInfiniteAdminLeaseAnalysesRowsQuery.rootKey = "AdminLeaseAnalysesRows.infinite"

export const useSuspenseInfiniteAdminLeaseAnalysesRowsQuery = <
  TData = InfiniteData<Types.AdminLeaseAnalysesRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminLeaseAnalysesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminLeaseAnalysesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminLeaseAnalysesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminLeaseAnalysesRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminLeaseAnalysesRowsQuery,
            Types.AdminLeaseAnalysesRowsQueryVariables
          >(AdminLeaseAnalysesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminLeaseAnalysesRowsQuery.getKey = (
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
) => ["AdminLeaseAnalysesRows.infinite", variables]

useSuspenseInfiniteAdminLeaseAnalysesRowsQuery.rootKey =
  "AdminLeaseAnalysesRows.infinite"

useAdminLeaseAnalysesRowsQuery.fetcher = (
  variables: Types.AdminLeaseAnalysesRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminLeaseAnalysesRowsQuery,
    Types.AdminLeaseAnalysesRowsQueryVariables
  >(AdminLeaseAnalysesRowsDocument, variables, options)

export const AdminProjectDocumentsViewDocument = new TypedDocumentString(`
    query AdminProjectDocumentsView($filterBy: AdminProjectDocumentsTableFilter, $sortBy: [AdminProjectDocumentsTableSort!], $sitePid: String) {
  admin {
    projectDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerNames
          developerNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminProjectDocumentsViewQuery = <
  TData = Types.AdminProjectDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProjectDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminProjectDocumentsView"]
        : ["AdminProjectDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsViewQuery,
      Types.AdminProjectDocumentsViewQueryVariables
    >(AdminProjectDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsView"]
    : ["AdminProjectDocumentsView", variables]

useAdminProjectDocumentsViewQuery.rootKey = "AdminProjectDocumentsView"

export const useSuspenseAdminProjectDocumentsViewQuery = <
  TData = Types.AdminProjectDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProjectDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminProjectDocumentsView"]
        : ["AdminProjectDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsViewQuery,
      Types.AdminProjectDocumentsViewQueryVariables
    >(AdminProjectDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsView"]
    : ["AdminProjectDocumentsView", variables]

useSuspenseAdminProjectDocumentsViewQuery.rootKey = "AdminProjectDocumentsView"

export const useInfiniteAdminProjectDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProjectDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminProjectDocumentsView.infinite"]
            : ["AdminProjectDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsViewQuery,
            Types.AdminProjectDocumentsViewQueryVariables
          >(AdminProjectDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsView.infinite"]
    : ["AdminProjectDocumentsView.infinite", variables]

useInfiniteAdminProjectDocumentsViewQuery.rootKey = "AdminProjectDocumentsView.infinite"

export const useSuspenseInfiniteAdminProjectDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminProjectDocumentsView.infinite"]
            : ["AdminProjectDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsViewQuery,
            Types.AdminProjectDocumentsViewQueryVariables
          >(AdminProjectDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsView.infinite"]
    : ["AdminProjectDocumentsView.infinite", variables]

useSuspenseInfiniteAdminProjectDocumentsViewQuery.rootKey =
  "AdminProjectDocumentsView.infinite"

useAdminProjectDocumentsViewQuery.fetcher = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProjectDocumentsViewQuery,
    Types.AdminProjectDocumentsViewQueryVariables
  >(AdminProjectDocumentsViewDocument, variables, options)

export const AdminProjectDocumentsRowsDocument = new TypedDocumentString(`
    query AdminProjectDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    projectDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        projectPid
        projectName
        projectStatus
        sitePid
        siteAddress
        ownerName
        developerName
        viewableByDeveloper
        viewableByBuildingOwner
        createdByName
        hasAiAnalysis
      }
    }
  }
}
    `)

export const useAdminProjectDocumentsRowsQuery = <
  TData = Types.AdminProjectDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProjectDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminProjectDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsRowsQuery,
      Types.AdminProjectDocumentsRowsQueryVariables
    >(AdminProjectDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRows", variables]

useAdminProjectDocumentsRowsQuery.rootKey = "AdminProjectDocumentsRows"

export const useSuspenseAdminProjectDocumentsRowsQuery = <
  TData = Types.AdminProjectDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProjectDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminProjectDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsRowsQuery,
      Types.AdminProjectDocumentsRowsQueryVariables
    >(AdminProjectDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRows", variables]

useSuspenseAdminProjectDocumentsRowsQuery.rootKey = "AdminProjectDocumentsRows"

export const useInfiniteAdminProjectDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProjectDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminProjectDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsRowsQuery,
            Types.AdminProjectDocumentsRowsQueryVariables
          >(AdminProjectDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRows.infinite", variables]

useInfiniteAdminProjectDocumentsRowsQuery.rootKey = "AdminProjectDocumentsRows.infinite"

export const useSuspenseInfiniteAdminProjectDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminProjectDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsRowsQuery,
            Types.AdminProjectDocumentsRowsQueryVariables
          >(AdminProjectDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminProjectDocumentsRowsQuery.rootKey =
  "AdminProjectDocumentsRows.infinite"

useAdminProjectDocumentsRowsQuery.fetcher = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProjectDocumentsRowsQuery,
    Types.AdminProjectDocumentsRowsQueryVariables
  >(AdminProjectDocumentsRowsDocument, variables, options)

export const AdminSiteDocumentsViewDocument = new TypedDocumentString(`
    query AdminSiteDocumentsView($filterBy: AdminSiteDocumentsTableFilter, $sortBy: [AdminSiteDocumentsTableSort!], $sitePid: String) {
  admin {
    siteDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminSiteDocumentsViewQuery = <
  TData = Types.AdminSiteDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSiteDocumentsView"]
        : ["AdminSiteDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsViewQuery,
      Types.AdminSiteDocumentsViewQueryVariables
    >(AdminSiteDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsView"]
    : ["AdminSiteDocumentsView", variables]

useAdminSiteDocumentsViewQuery.rootKey = "AdminSiteDocumentsView"

export const useSuspenseAdminSiteDocumentsViewQuery = <
  TData = Types.AdminSiteDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSiteDocumentsView"]
        : ["AdminSiteDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsViewQuery,
      Types.AdminSiteDocumentsViewQueryVariables
    >(AdminSiteDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsView"]
    : ["AdminSiteDocumentsView", variables]

useSuspenseAdminSiteDocumentsViewQuery.rootKey = "AdminSiteDocumentsView"

export const useInfiniteAdminSiteDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSiteDocumentsView.infinite"]
            : ["AdminSiteDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsViewQuery,
            Types.AdminSiteDocumentsViewQueryVariables
          >(AdminSiteDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsView.infinite"]
    : ["AdminSiteDocumentsView.infinite", variables]

useInfiniteAdminSiteDocumentsViewQuery.rootKey = "AdminSiteDocumentsView.infinite"

export const useSuspenseInfiniteAdminSiteDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSiteDocumentsView.infinite"]
            : ["AdminSiteDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsViewQuery,
            Types.AdminSiteDocumentsViewQueryVariables
          >(AdminSiteDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsView.infinite"]
    : ["AdminSiteDocumentsView.infinite", variables]

useSuspenseInfiniteAdminSiteDocumentsViewQuery.rootKey =
  "AdminSiteDocumentsView.infinite"

useAdminSiteDocumentsViewQuery.fetcher = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSiteDocumentsViewQuery,
    Types.AdminSiteDocumentsViewQueryVariables
  >(AdminSiteDocumentsViewDocument, variables, options)

export const AdminSiteDocumentsRowsDocument = new TypedDocumentString(`
    query AdminSiteDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    siteDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        sitePid
        siteAddress
        siteCity
        siteState
        siteZip
        ownerName
      }
    }
  }
}
    `)

export const useAdminSiteDocumentsRowsQuery = <
  TData = Types.AdminSiteDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminSiteDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsRowsQuery,
      Types.AdminSiteDocumentsRowsQueryVariables
    >(AdminSiteDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRows", variables]

useAdminSiteDocumentsRowsQuery.rootKey = "AdminSiteDocumentsRows"

export const useSuspenseAdminSiteDocumentsRowsQuery = <
  TData = Types.AdminSiteDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminSiteDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsRowsQuery,
      Types.AdminSiteDocumentsRowsQueryVariables
    >(AdminSiteDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRows", variables]

useSuspenseAdminSiteDocumentsRowsQuery.rootKey = "AdminSiteDocumentsRows"

export const useInfiniteAdminSiteDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsRowsQuery,
            Types.AdminSiteDocumentsRowsQueryVariables
          >(AdminSiteDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRows.infinite", variables]

useInfiniteAdminSiteDocumentsRowsQuery.rootKey = "AdminSiteDocumentsRows.infinite"

export const useSuspenseInfiniteAdminSiteDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsRowsQuery,
            Types.AdminSiteDocumentsRowsQueryVariables
          >(AdminSiteDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminSiteDocumentsRowsQuery.rootKey =
  "AdminSiteDocumentsRows.infinite"

useAdminSiteDocumentsRowsQuery.fetcher = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSiteDocumentsRowsQuery,
    Types.AdminSiteDocumentsRowsQueryVariables
  >(AdminSiteDocumentsRowsDocument, variables, options)

export const AdminUtilityDocumentsViewDocument = new TypedDocumentString(`
    query AdminUtilityDocumentsView($filterBy: AdminUtilityDocumentsTableFilter, $sortBy: [AdminUtilityDocumentsTableSort!], $utilityPid: String) {
  admin {
    utilityDocuments(utilityPid: $utilityPid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          utilityNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminUtilityDocumentsViewQuery = <
  TData = Types.AdminUtilityDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminUtilityDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminUtilityDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminUtilityDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminUtilityDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminUtilityDocumentsView"]
        : ["AdminUtilityDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminUtilityDocumentsViewQuery,
      Types.AdminUtilityDocumentsViewQueryVariables
    >(AdminUtilityDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminUtilityDocumentsViewQuery.getKey = (
  variables?: Types.AdminUtilityDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminUtilityDocumentsView"]
    : ["AdminUtilityDocumentsView", variables]

useAdminUtilityDocumentsViewQuery.rootKey = "AdminUtilityDocumentsView"

export const useSuspenseAdminUtilityDocumentsViewQuery = <
  TData = Types.AdminUtilityDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminUtilityDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUtilityDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminUtilityDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUtilityDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminUtilityDocumentsView"]
        : ["AdminUtilityDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminUtilityDocumentsViewQuery,
      Types.AdminUtilityDocumentsViewQueryVariables
    >(AdminUtilityDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminUtilityDocumentsViewQuery.getKey = (
  variables?: Types.AdminUtilityDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminUtilityDocumentsView"]
    : ["AdminUtilityDocumentsView", variables]

useSuspenseAdminUtilityDocumentsViewQuery.rootKey = "AdminUtilityDocumentsView"

export const useInfiniteAdminUtilityDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminUtilityDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminUtilityDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUtilityDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminUtilityDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUtilityDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUtilityDocumentsView.infinite"]
            : ["AdminUtilityDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUtilityDocumentsViewQuery,
            Types.AdminUtilityDocumentsViewQueryVariables
          >(AdminUtilityDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUtilityDocumentsViewQuery.getKey = (
  variables?: Types.AdminUtilityDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminUtilityDocumentsView.infinite"]
    : ["AdminUtilityDocumentsView.infinite", variables]

useInfiniteAdminUtilityDocumentsViewQuery.rootKey = "AdminUtilityDocumentsView.infinite"

export const useSuspenseInfiniteAdminUtilityDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminUtilityDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminUtilityDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminUtilityDocumentsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUtilityDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUtilityDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUtilityDocumentsView.infinite"]
            : ["AdminUtilityDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUtilityDocumentsViewQuery,
            Types.AdminUtilityDocumentsViewQueryVariables
          >(AdminUtilityDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUtilityDocumentsViewQuery.getKey = (
  variables?: Types.AdminUtilityDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminUtilityDocumentsView.infinite"]
    : ["AdminUtilityDocumentsView.infinite", variables]

useSuspenseInfiniteAdminUtilityDocumentsViewQuery.rootKey =
  "AdminUtilityDocumentsView.infinite"

useAdminUtilityDocumentsViewQuery.fetcher = (
  variables?: Types.AdminUtilityDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminUtilityDocumentsViewQuery,
    Types.AdminUtilityDocumentsViewQueryVariables
  >(AdminUtilityDocumentsViewDocument, variables, options)

export const AdminUtilityDocumentsRowsDocument = new TypedDocumentString(`
    query AdminUtilityDocumentsRows($keys: [Int!]!, $utilityPid: String) {
  admin {
    utilityDocuments(utilityPid: $utilityPid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        utilityPid
        utilityName
      }
    }
  }
}
    `)

export const useAdminUtilityDocumentsRowsQuery = <
  TData = Types.AdminUtilityDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminUtilityDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminUtilityDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminUtilityDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminUtilityDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminUtilityDocumentsRowsQuery,
      Types.AdminUtilityDocumentsRowsQueryVariables
    >(AdminUtilityDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminUtilityDocumentsRowsQuery.getKey = (
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
) => ["AdminUtilityDocumentsRows", variables]

useAdminUtilityDocumentsRowsQuery.rootKey = "AdminUtilityDocumentsRows"

export const useSuspenseAdminUtilityDocumentsRowsQuery = <
  TData = Types.AdminUtilityDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUtilityDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminUtilityDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUtilityDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminUtilityDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminUtilityDocumentsRowsQuery,
      Types.AdminUtilityDocumentsRowsQueryVariables
    >(AdminUtilityDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminUtilityDocumentsRowsQuery.getKey = (
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
) => ["AdminUtilityDocumentsRows", variables]

useSuspenseAdminUtilityDocumentsRowsQuery.rootKey = "AdminUtilityDocumentsRows"

export const useInfiniteAdminUtilityDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminUtilityDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUtilityDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminUtilityDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUtilityDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUtilityDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUtilityDocumentsRowsQuery,
            Types.AdminUtilityDocumentsRowsQueryVariables
          >(AdminUtilityDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUtilityDocumentsRowsQuery.getKey = (
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
) => ["AdminUtilityDocumentsRows.infinite", variables]

useInfiniteAdminUtilityDocumentsRowsQuery.rootKey = "AdminUtilityDocumentsRows.infinite"

export const useSuspenseInfiniteAdminUtilityDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminUtilityDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminUtilityDocumentsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUtilityDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUtilityDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUtilityDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUtilityDocumentsRowsQuery,
            Types.AdminUtilityDocumentsRowsQueryVariables
          >(AdminUtilityDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUtilityDocumentsRowsQuery.getKey = (
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
) => ["AdminUtilityDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminUtilityDocumentsRowsQuery.rootKey =
  "AdminUtilityDocumentsRows.infinite"

useAdminUtilityDocumentsRowsQuery.fetcher = (
  variables: Types.AdminUtilityDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminUtilityDocumentsRowsQuery,
    Types.AdminUtilityDocumentsRowsQueryVariables
  >(AdminUtilityDocumentsRowsDocument, variables, options)

export const AdminTariffDocumentsViewDocument = new TypedDocumentString(`
    query AdminTariffDocumentsView($filterBy: AdminTariffDocumentsTableFilter, $sortBy: [AdminTariffDocumentsTableSort!], $tariffPid: String) {
  admin {
    tariffDocuments(tariffPid: $tariffPid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          utilityNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminTariffDocumentsViewQuery = <
  TData = Types.AdminTariffDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminTariffDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminTariffDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminTariffDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminTariffDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminTariffDocumentsView"]
        : ["AdminTariffDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminTariffDocumentsViewQuery,
      Types.AdminTariffDocumentsViewQueryVariables
    >(AdminTariffDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminTariffDocumentsViewQuery.getKey = (
  variables?: Types.AdminTariffDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminTariffDocumentsView"]
    : ["AdminTariffDocumentsView", variables]

useAdminTariffDocumentsViewQuery.rootKey = "AdminTariffDocumentsView"

export const useSuspenseAdminTariffDocumentsViewQuery = <
  TData = Types.AdminTariffDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminTariffDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminTariffDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminTariffDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminTariffDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminTariffDocumentsView"]
        : ["AdminTariffDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminTariffDocumentsViewQuery,
      Types.AdminTariffDocumentsViewQueryVariables
    >(AdminTariffDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminTariffDocumentsViewQuery.getKey = (
  variables?: Types.AdminTariffDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminTariffDocumentsView"]
    : ["AdminTariffDocumentsView", variables]

useSuspenseAdminTariffDocumentsViewQuery.rootKey = "AdminTariffDocumentsView"

export const useInfiniteAdminTariffDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminTariffDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminTariffDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminTariffDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminTariffDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminTariffDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminTariffDocumentsView.infinite"]
            : ["AdminTariffDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminTariffDocumentsViewQuery,
            Types.AdminTariffDocumentsViewQueryVariables
          >(AdminTariffDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminTariffDocumentsViewQuery.getKey = (
  variables?: Types.AdminTariffDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminTariffDocumentsView.infinite"]
    : ["AdminTariffDocumentsView.infinite", variables]

useInfiniteAdminTariffDocumentsViewQuery.rootKey = "AdminTariffDocumentsView.infinite"

export const useSuspenseInfiniteAdminTariffDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminTariffDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminTariffDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminTariffDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminTariffDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminTariffDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminTariffDocumentsView.infinite"]
            : ["AdminTariffDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminTariffDocumentsViewQuery,
            Types.AdminTariffDocumentsViewQueryVariables
          >(AdminTariffDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminTariffDocumentsViewQuery.getKey = (
  variables?: Types.AdminTariffDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminTariffDocumentsView.infinite"]
    : ["AdminTariffDocumentsView.infinite", variables]

useSuspenseInfiniteAdminTariffDocumentsViewQuery.rootKey =
  "AdminTariffDocumentsView.infinite"

useAdminTariffDocumentsViewQuery.fetcher = (
  variables?: Types.AdminTariffDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminTariffDocumentsViewQuery,
    Types.AdminTariffDocumentsViewQueryVariables
  >(AdminTariffDocumentsViewDocument, variables, options)

export const AdminTariffDocumentsRowsDocument = new TypedDocumentString(`
    query AdminTariffDocumentsRows($keys: [Int!]!, $tariffPid: String) {
  admin {
    tariffDocuments(tariffPid: $tariffPid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        tariffPid
        tariffName
        tariffCode
        utilityPid
        utilityName
      }
    }
  }
}
    `)

export const useAdminTariffDocumentsRowsQuery = <
  TData = Types.AdminTariffDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminTariffDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminTariffDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminTariffDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminTariffDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminTariffDocumentsRowsQuery,
      Types.AdminTariffDocumentsRowsQueryVariables
    >(AdminTariffDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminTariffDocumentsRowsQuery.getKey = (
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
) => ["AdminTariffDocumentsRows", variables]

useAdminTariffDocumentsRowsQuery.rootKey = "AdminTariffDocumentsRows"

export const useSuspenseAdminTariffDocumentsRowsQuery = <
  TData = Types.AdminTariffDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminTariffDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminTariffDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminTariffDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminTariffDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminTariffDocumentsRowsQuery,
      Types.AdminTariffDocumentsRowsQueryVariables
    >(AdminTariffDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminTariffDocumentsRowsQuery.getKey = (
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
) => ["AdminTariffDocumentsRows", variables]

useSuspenseAdminTariffDocumentsRowsQuery.rootKey = "AdminTariffDocumentsRows"

export const useInfiniteAdminTariffDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminTariffDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminTariffDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminTariffDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminTariffDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminTariffDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminTariffDocumentsRowsQuery,
            Types.AdminTariffDocumentsRowsQueryVariables
          >(AdminTariffDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminTariffDocumentsRowsQuery.getKey = (
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
) => ["AdminTariffDocumentsRows.infinite", variables]

useInfiniteAdminTariffDocumentsRowsQuery.rootKey = "AdminTariffDocumentsRows.infinite"

export const useSuspenseInfiniteAdminTariffDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminTariffDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminTariffDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminTariffDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminTariffDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminTariffDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminTariffDocumentsRowsQuery,
            Types.AdminTariffDocumentsRowsQueryVariables
          >(AdminTariffDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminTariffDocumentsRowsQuery.getKey = (
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
) => ["AdminTariffDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminTariffDocumentsRowsQuery.rootKey =
  "AdminTariffDocumentsRows.infinite"

useAdminTariffDocumentsRowsQuery.fetcher = (
  variables: Types.AdminTariffDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminTariffDocumentsRowsQuery,
    Types.AdminTariffDocumentsRowsQueryVariables
  >(AdminTariffDocumentsRowsDocument, variables, options)

export const AdminBundleDocumentsViewDocument = new TypedDocumentString(`
    query AdminBundleDocumentsView($filterBy: AdminBundleDocumentsTableFilter, $sortBy: [AdminBundleDocumentsTableSort!], $sitePid: String) {
  admin {
    bundleDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          receiverNames
          developerNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminBundleDocumentsViewQuery = <
  TData = Types.AdminBundleDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleDocumentsView"]
        : ["AdminBundleDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsViewQuery,
      Types.AdminBundleDocumentsViewQueryVariables
    >(AdminBundleDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsView"]
    : ["AdminBundleDocumentsView", variables]

useAdminBundleDocumentsViewQuery.rootKey = "AdminBundleDocumentsView"

export const useSuspenseAdminBundleDocumentsViewQuery = <
  TData = Types.AdminBundleDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleDocumentsView"]
        : ["AdminBundleDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsViewQuery,
      Types.AdminBundleDocumentsViewQueryVariables
    >(AdminBundleDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsView"]
    : ["AdminBundleDocumentsView", variables]

useSuspenseAdminBundleDocumentsViewQuery.rootKey = "AdminBundleDocumentsView"

export const useInfiniteAdminBundleDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleDocumentsView.infinite"]
            : ["AdminBundleDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsViewQuery,
            Types.AdminBundleDocumentsViewQueryVariables
          >(AdminBundleDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsView.infinite"]
    : ["AdminBundleDocumentsView.infinite", variables]

useInfiniteAdminBundleDocumentsViewQuery.rootKey = "AdminBundleDocumentsView.infinite"

export const useSuspenseInfiniteAdminBundleDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleDocumentsView.infinite"]
            : ["AdminBundleDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsViewQuery,
            Types.AdminBundleDocumentsViewQueryVariables
          >(AdminBundleDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsView.infinite"]
    : ["AdminBundleDocumentsView.infinite", variables]

useSuspenseInfiniteAdminBundleDocumentsViewQuery.rootKey =
  "AdminBundleDocumentsView.infinite"

useAdminBundleDocumentsViewQuery.fetcher = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleDocumentsViewQuery,
    Types.AdminBundleDocumentsViewQueryVariables
  >(AdminBundleDocumentsViewDocument, variables, options)

export const AdminBundleDocumentsRowsDocument = new TypedDocumentString(`
    query AdminBundleDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    bundleDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        bundlePid
        bundleName
        bundleType
        receiverName
        developerName
      }
    }
  }
}
    `)

export const useAdminBundleDocumentsRowsQuery = <
  TData = Types.AdminBundleDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsRowsQuery,
      Types.AdminBundleDocumentsRowsQueryVariables
    >(AdminBundleDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRows", variables]

useAdminBundleDocumentsRowsQuery.rootKey = "AdminBundleDocumentsRows"

export const useSuspenseAdminBundleDocumentsRowsQuery = <
  TData = Types.AdminBundleDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsRowsQuery,
      Types.AdminBundleDocumentsRowsQueryVariables
    >(AdminBundleDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRows", variables]

useSuspenseAdminBundleDocumentsRowsQuery.rootKey = "AdminBundleDocumentsRows"

export const useInfiniteAdminBundleDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBundleDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsRowsQuery,
            Types.AdminBundleDocumentsRowsQueryVariables
          >(AdminBundleDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRows.infinite", variables]

useInfiniteAdminBundleDocumentsRowsQuery.rootKey = "AdminBundleDocumentsRows.infinite"

export const useSuspenseInfiniteAdminBundleDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBundleDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsRowsQuery,
            Types.AdminBundleDocumentsRowsQueryVariables
          >(AdminBundleDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminBundleDocumentsRowsQuery.rootKey =
  "AdminBundleDocumentsRows.infinite"

useAdminBundleDocumentsRowsQuery.fetcher = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleDocumentsRowsQuery,
    Types.AdminBundleDocumentsRowsQueryVariables
  >(AdminBundleDocumentsRowsDocument, variables, options)

export const AdminBundleParentDocumentsViewDocument = new TypedDocumentString(`
    query AdminBundleParentDocumentsView($filterBy: AdminBundleParentDocumentsTableFilter, $sortBy: [AdminBundleParentDocumentsTableSort!], $sitePid: String) {
  admin {
    bundleParentDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminBundleParentDocumentsViewQuery = <
  TData = Types.AdminBundleParentDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleParentDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleParentDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleParentDocumentsView"]
        : ["AdminBundleParentDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsViewQuery,
      Types.AdminBundleParentDocumentsViewQueryVariables
    >(AdminBundleParentDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsView"]
    : ["AdminBundleParentDocumentsView", variables]

useAdminBundleParentDocumentsViewQuery.rootKey = "AdminBundleParentDocumentsView"

export const useSuspenseAdminBundleParentDocumentsViewQuery = <
  TData = Types.AdminBundleParentDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleParentDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleParentDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleParentDocumentsView"]
        : ["AdminBundleParentDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsViewQuery,
      Types.AdminBundleParentDocumentsViewQueryVariables
    >(AdminBundleParentDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsView"]
    : ["AdminBundleParentDocumentsView", variables]

useSuspenseAdminBundleParentDocumentsViewQuery.rootKey =
  "AdminBundleParentDocumentsView"

export const useInfiniteAdminBundleParentDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleParentDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleParentDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleParentDocumentsView.infinite"]
            : ["AdminBundleParentDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsViewQuery,
            Types.AdminBundleParentDocumentsViewQueryVariables
          >(AdminBundleParentDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsView.infinite"]
    : ["AdminBundleParentDocumentsView.infinite", variables]

useInfiniteAdminBundleParentDocumentsViewQuery.rootKey =
  "AdminBundleParentDocumentsView.infinite"

export const useSuspenseInfiniteAdminBundleParentDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminBundleParentDocumentsViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleParentDocumentsView.infinite"]
            : ["AdminBundleParentDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsViewQuery,
            Types.AdminBundleParentDocumentsViewQueryVariables
          >(AdminBundleParentDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsView.infinite"]
    : ["AdminBundleParentDocumentsView.infinite", variables]

useSuspenseInfiniteAdminBundleParentDocumentsViewQuery.rootKey =
  "AdminBundleParentDocumentsView.infinite"

useAdminBundleParentDocumentsViewQuery.fetcher = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleParentDocumentsViewQuery,
    Types.AdminBundleParentDocumentsViewQueryVariables
  >(AdminBundleParentDocumentsViewDocument, variables, options)

export const AdminBundleParentDocumentsRowsDocument = new TypedDocumentString(`
    query AdminBundleParentDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    bundleParentDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        bundleParentPid
        bundleParentName
        bundleParentType
        ownerName
      }
    }
  }
}
    `)

export const useAdminBundleParentDocumentsRowsQuery = <
  TData = Types.AdminBundleParentDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleParentDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsRowsQuery,
      Types.AdminBundleParentDocumentsRowsQueryVariables
    >(AdminBundleParentDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRows", variables]

useAdminBundleParentDocumentsRowsQuery.rootKey = "AdminBundleParentDocumentsRows"

export const useSuspenseAdminBundleParentDocumentsRowsQuery = <
  TData = Types.AdminBundleParentDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleParentDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsRowsQuery,
      Types.AdminBundleParentDocumentsRowsQueryVariables
    >(AdminBundleParentDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRows", variables]

useSuspenseAdminBundleParentDocumentsRowsQuery.rootKey =
  "AdminBundleParentDocumentsRows"

export const useInfiniteAdminBundleParentDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBundleParentDocumentsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsRowsQuery,
            Types.AdminBundleParentDocumentsRowsQueryVariables
          >(AdminBundleParentDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRows.infinite", variables]

useInfiniteAdminBundleParentDocumentsRowsQuery.rootKey =
  "AdminBundleParentDocumentsRows.infinite"

export const useSuspenseInfiniteAdminBundleParentDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminBundleParentDocumentsRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBundleParentDocumentsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsRowsQuery,
            Types.AdminBundleParentDocumentsRowsQueryVariables
          >(AdminBundleParentDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminBundleParentDocumentsRowsQuery.rootKey =
  "AdminBundleParentDocumentsRows.infinite"

useAdminBundleParentDocumentsRowsQuery.fetcher = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleParentDocumentsRowsQuery,
    Types.AdminBundleParentDocumentsRowsQueryVariables
  >(AdminBundleParentDocumentsRowsDocument, variables, options)

export const AdminInvestorBidDocumentsViewDocument = new TypedDocumentString(`
    query AdminInvestorBidDocumentsView($filterBy: AdminInvestorBidDocumentsTableFilter, $sortBy: [AdminInvestorBidDocumentsTableSort!], $sitePid: String) {
  admin {
    investorBidDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          receiverNames
          developerNames
          createdByNames
        }
      }
    }
  }
}
    `)

export const useAdminInvestorBidDocumentsViewQuery = <
  TData = Types.AdminInvestorBidDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminInvestorBidDocumentsView"]
        : ["AdminInvestorBidDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsViewQuery,
      Types.AdminInvestorBidDocumentsViewQueryVariables
    >(AdminInvestorBidDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsView"]
    : ["AdminInvestorBidDocumentsView", variables]

useAdminInvestorBidDocumentsViewQuery.rootKey = "AdminInvestorBidDocumentsView"

export const useSuspenseAdminInvestorBidDocumentsViewQuery = <
  TData = Types.AdminInvestorBidDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminInvestorBidDocumentsView"]
        : ["AdminInvestorBidDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsViewQuery,
      Types.AdminInvestorBidDocumentsViewQueryVariables
    >(AdminInvestorBidDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsView"]
    : ["AdminInvestorBidDocumentsView", variables]

useSuspenseAdminInvestorBidDocumentsViewQuery.rootKey = "AdminInvestorBidDocumentsView"

export const useInfiniteAdminInvestorBidDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInvestorBidDocumentsView.infinite"]
            : ["AdminInvestorBidDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsViewQuery,
            Types.AdminInvestorBidDocumentsViewQueryVariables
          >(AdminInvestorBidDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsView.infinite"]
    : ["AdminInvestorBidDocumentsView.infinite", variables]

useInfiniteAdminInvestorBidDocumentsViewQuery.rootKey =
  "AdminInvestorBidDocumentsView.infinite"

export const useSuspenseInfiniteAdminInvestorBidDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInvestorBidDocumentsViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInvestorBidDocumentsView.infinite"]
            : ["AdminInvestorBidDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsViewQuery,
            Types.AdminInvestorBidDocumentsViewQueryVariables
          >(AdminInvestorBidDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsView.infinite"]
    : ["AdminInvestorBidDocumentsView.infinite", variables]

useSuspenseInfiniteAdminInvestorBidDocumentsViewQuery.rootKey =
  "AdminInvestorBidDocumentsView.infinite"

useAdminInvestorBidDocumentsViewQuery.fetcher = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInvestorBidDocumentsViewQuery,
    Types.AdminInvestorBidDocumentsViewQueryVariables
  >(AdminInvestorBidDocumentsViewDocument, variables, options)

export const AdminInvestorBidDocumentsRowsDocument = new TypedDocumentString(`
    query AdminInvestorBidDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    investorBidDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        investorBidPid
        investorBidName
        bundlePid
        bundleName
        receiverName
        developerName
      }
    }
  }
}
    `)

export const useAdminInvestorBidDocumentsRowsQuery = <
  TData = Types.AdminInvestorBidDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminInvestorBidDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsRowsQuery,
      Types.AdminInvestorBidDocumentsRowsQueryVariables
    >(AdminInvestorBidDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRows", variables]

useAdminInvestorBidDocumentsRowsQuery.rootKey = "AdminInvestorBidDocumentsRows"

export const useSuspenseAdminInvestorBidDocumentsRowsQuery = <
  TData = Types.AdminInvestorBidDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminInvestorBidDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsRowsQuery,
      Types.AdminInvestorBidDocumentsRowsQueryVariables
    >(AdminInvestorBidDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRows", variables]

useSuspenseAdminInvestorBidDocumentsRowsQuery.rootKey = "AdminInvestorBidDocumentsRows"

export const useInfiniteAdminInvestorBidDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInvestorBidDocumentsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsRowsQuery,
            Types.AdminInvestorBidDocumentsRowsQueryVariables
          >(AdminInvestorBidDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRows.infinite", variables]

useInfiniteAdminInvestorBidDocumentsRowsQuery.rootKey =
  "AdminInvestorBidDocumentsRows.infinite"

export const useSuspenseInfiniteAdminInvestorBidDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInvestorBidDocumentsRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInvestorBidDocumentsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsRowsQuery,
            Types.AdminInvestorBidDocumentsRowsQueryVariables
          >(AdminInvestorBidDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRows.infinite", variables]

useSuspenseInfiniteAdminInvestorBidDocumentsRowsQuery.rootKey =
  "AdminInvestorBidDocumentsRows.infinite"

useAdminInvestorBidDocumentsRowsQuery.fetcher = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInvestorBidDocumentsRowsQuery,
    Types.AdminInvestorBidDocumentsRowsQueryVariables
  >(AdminInvestorBidDocumentsRowsDocument, variables, options)

export const AdminMyDocumentsDocument = new TypedDocumentString(`
    query AdminMyDocuments {
  admin {
    myDocuments {
      ...Document
      fileSize
      pdfBasicMetadata {
        pageCount
        title
        hasTextLayer
        parseError
      }
      canDelete
      isAnalyzable
      latestAnalysisRequest {
        ...DocumentAnalysisRequest
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}
fragment DocumentAnalysisRequest on DocumentAnalysisRequest {
  id
  createdAt
  status
  note
  errorClass
  errorMessage
}`)

export const useAdminMyDocumentsQuery = <
  TData = Types.AdminMyDocumentsQuery,
  TError = unknown,
>(
  variables?: Types.AdminMyDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMyDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminMyDocumentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminMyDocumentsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AdminMyDocuments"] : ["AdminMyDocuments", variables],
    queryFn: publicFetcher<
      Types.AdminMyDocumentsQuery,
      Types.AdminMyDocumentsQueryVariables
    >(AdminMyDocumentsDocument, variables),
    ...options,
  })
}

useAdminMyDocumentsQuery.getKey = (variables?: Types.AdminMyDocumentsQueryVariables) =>
  variables === undefined ? ["AdminMyDocuments"] : ["AdminMyDocuments", variables]

useAdminMyDocumentsQuery.rootKey = "AdminMyDocuments"

export const useSuspenseAdminMyDocumentsQuery = <
  TData = Types.AdminMyDocumentsQuery,
  TError = unknown,
>(
  variables?: Types.AdminMyDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMyDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMyDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMyDocumentsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AdminMyDocuments"] : ["AdminMyDocuments", variables],
    queryFn: publicFetcher<
      Types.AdminMyDocumentsQuery,
      Types.AdminMyDocumentsQueryVariables
    >(AdminMyDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMyDocumentsQuery.getKey = (
  variables?: Types.AdminMyDocumentsQueryVariables,
) => (variables === undefined ? ["AdminMyDocuments"] : ["AdminMyDocuments", variables])

useSuspenseAdminMyDocumentsQuery.rootKey = "AdminMyDocuments"

export const useInfiniteAdminMyDocumentsQuery = <
  TData = InfiniteData<Types.AdminMyDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMyDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMyDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMyDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMyDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMyDocuments.infinite"]
            : ["AdminMyDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMyDocumentsQuery,
            Types.AdminMyDocumentsQueryVariables
          >(AdminMyDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMyDocumentsQuery.getKey = (
  variables?: Types.AdminMyDocumentsQueryVariables,
) =>
  variables === undefined
    ? ["AdminMyDocuments.infinite"]
    : ["AdminMyDocuments.infinite", variables]

useInfiniteAdminMyDocumentsQuery.rootKey = "AdminMyDocuments.infinite"

export const useSuspenseInfiniteAdminMyDocumentsQuery = <
  TData = InfiniteData<Types.AdminMyDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMyDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMyDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMyDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMyDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMyDocuments.infinite"]
            : ["AdminMyDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMyDocumentsQuery,
            Types.AdminMyDocumentsQueryVariables
          >(AdminMyDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMyDocumentsQuery.getKey = (
  variables?: Types.AdminMyDocumentsQueryVariables,
) =>
  variables === undefined
    ? ["AdminMyDocuments.infinite"]
    : ["AdminMyDocuments.infinite", variables]

useSuspenseInfiniteAdminMyDocumentsQuery.rootKey = "AdminMyDocuments.infinite"

useAdminMyDocumentsQuery.fetcher = (
  variables?: Types.AdminMyDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminMyDocumentsQuery, Types.AdminMyDocumentsQueryVariables>(
    AdminMyDocumentsDocument,
    variables,
    options,
  )

export const SearchEntitiesDocument = new TypedDocumentString(`
    query SearchEntities($searchTerm: String!) {
  admin {
    searchEntities(searchTerm: $searchTerm) {
      pid
      entityType
      display
      score
    }
  }
}
    `)

export const useSearchEntitiesQuery = <
  TData = Types.SearchEntitiesQuery,
  TError = unknown,
>(
  variables: Types.SearchEntitiesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SearchEntitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SearchEntitiesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SearchEntitiesQuery, TError, TData>({
    queryKey: ["SearchEntities", variables],
    queryFn: publicFetcher<
      Types.SearchEntitiesQuery,
      Types.SearchEntitiesQueryVariables
    >(SearchEntitiesDocument, variables),
    ...options,
  })
}

useSearchEntitiesQuery.getKey = (variables: Types.SearchEntitiesQueryVariables) => [
  "SearchEntities",
  variables,
]

useSearchEntitiesQuery.rootKey = "SearchEntities"

export const useSuspenseSearchEntitiesQuery = <
  TData = Types.SearchEntitiesQuery,
  TError = unknown,
>(
  variables: Types.SearchEntitiesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SearchEntitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SearchEntitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SearchEntitiesQuery, TError, TData>({
    queryKey: ["SearchEntities", variables],
    queryFn: publicFetcher<
      Types.SearchEntitiesQuery,
      Types.SearchEntitiesQueryVariables
    >(SearchEntitiesDocument, variables),
    ...options,
  })
}

useSuspenseSearchEntitiesQuery.getKey = (
  variables: Types.SearchEntitiesQueryVariables,
) => ["SearchEntities", variables]

useSuspenseSearchEntitiesQuery.rootKey = "SearchEntities"

export const useInfiniteSearchEntitiesQuery = <
  TData = InfiniteData<Types.SearchEntitiesQuery>,
  TError = unknown,
>(
  variables: Types.SearchEntitiesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SearchEntitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SearchEntitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SearchEntitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SearchEntities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SearchEntitiesQuery, Types.SearchEntitiesQueryVariables>(
            SearchEntitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSearchEntitiesQuery.getKey = (
  variables: Types.SearchEntitiesQueryVariables,
) => ["SearchEntities.infinite", variables]

useInfiniteSearchEntitiesQuery.rootKey = "SearchEntities.infinite"

export const useSuspenseInfiniteSearchEntitiesQuery = <
  TData = InfiniteData<Types.SearchEntitiesQuery>,
  TError = unknown,
>(
  variables: Types.SearchEntitiesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SearchEntitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SearchEntitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SearchEntitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SearchEntities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SearchEntitiesQuery, Types.SearchEntitiesQueryVariables>(
            SearchEntitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSearchEntitiesQuery.getKey = (
  variables: Types.SearchEntitiesQueryVariables,
) => ["SearchEntities.infinite", variables]

useSuspenseInfiniteSearchEntitiesQuery.rootKey = "SearchEntities.infinite"

useSearchEntitiesQuery.fetcher = (
  variables: Types.SearchEntitiesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SearchEntitiesQuery, Types.SearchEntitiesQueryVariables>(
    SearchEntitiesDocument,
    variables,
    options,
  )

export const AdminOrgsForCompanySelectDocument = new TypedDocumentString(`
    query AdminOrgsForCompanySelect {
  admin {
    orgs {
      pid
      name
    }
  }
}
    `)

export const useAdminOrgsForCompanySelectQuery = <
  TData = Types.AdminOrgsForCompanySelectQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgsForCompanySelectQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgsForCompanySelectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminOrgsForCompanySelectQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgsForCompanySelectQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminOrgsForCompanySelect"]
        : ["AdminOrgsForCompanySelect", variables],
    queryFn: publicFetcher<
      Types.AdminOrgsForCompanySelectQuery,
      Types.AdminOrgsForCompanySelectQueryVariables
    >(AdminOrgsForCompanySelectDocument, variables),
    ...options,
  })
}

useAdminOrgsForCompanySelectQuery.getKey = (
  variables?: Types.AdminOrgsForCompanySelectQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgsForCompanySelect"]
    : ["AdminOrgsForCompanySelect", variables]

useAdminOrgsForCompanySelectQuery.rootKey = "AdminOrgsForCompanySelect"

export const useSuspenseAdminOrgsForCompanySelectQuery = <
  TData = Types.AdminOrgsForCompanySelectQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgsForCompanySelectQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgsForCompanySelectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgsForCompanySelectQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgsForCompanySelectQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminOrgsForCompanySelect"]
        : ["AdminOrgsForCompanySelect", variables],
    queryFn: publicFetcher<
      Types.AdminOrgsForCompanySelectQuery,
      Types.AdminOrgsForCompanySelectQueryVariables
    >(AdminOrgsForCompanySelectDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgsForCompanySelectQuery.getKey = (
  variables?: Types.AdminOrgsForCompanySelectQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgsForCompanySelect"]
    : ["AdminOrgsForCompanySelect", variables]

useSuspenseAdminOrgsForCompanySelectQuery.rootKey = "AdminOrgsForCompanySelect"

export const useInfiniteAdminOrgsForCompanySelectQuery = <
  TData = InfiniteData<Types.AdminOrgsForCompanySelectQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgsForCompanySelectQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgsForCompanySelectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgsForCompanySelectQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgsForCompanySelectQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgsForCompanySelect.infinite"]
            : ["AdminOrgsForCompanySelect.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminOrgsForCompanySelectQuery,
            Types.AdminOrgsForCompanySelectQueryVariables
          >(AdminOrgsForCompanySelectDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgsForCompanySelectQuery.getKey = (
  variables?: Types.AdminOrgsForCompanySelectQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgsForCompanySelect.infinite"]
    : ["AdminOrgsForCompanySelect.infinite", variables]

useInfiniteAdminOrgsForCompanySelectQuery.rootKey = "AdminOrgsForCompanySelect.infinite"

export const useSuspenseInfiniteAdminOrgsForCompanySelectQuery = <
  TData = InfiniteData<Types.AdminOrgsForCompanySelectQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgsForCompanySelectQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgsForCompanySelectQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgsForCompanySelectQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgsForCompanySelectQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgsForCompanySelect.infinite"]
            : ["AdminOrgsForCompanySelect.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminOrgsForCompanySelectQuery,
            Types.AdminOrgsForCompanySelectQueryVariables
          >(AdminOrgsForCompanySelectDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgsForCompanySelectQuery.getKey = (
  variables?: Types.AdminOrgsForCompanySelectQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgsForCompanySelect.infinite"]
    : ["AdminOrgsForCompanySelect.infinite", variables]

useSuspenseInfiniteAdminOrgsForCompanySelectQuery.rootKey =
  "AdminOrgsForCompanySelect.infinite"

useAdminOrgsForCompanySelectQuery.fetcher = (
  variables?: Types.AdminOrgsForCompanySelectQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminOrgsForCompanySelectQuery,
    Types.AdminOrgsForCompanySelectQueryVariables
  >(AdminOrgsForCompanySelectDocument, variables, options)

export const AdminCsvImportDetailDocument = new TypedDocumentString(`
    query AdminCsvImportDetail($csvImportPid: String!) {
  admin {
    csvImports {
      one(pid: $csvImportPid) {
        ...CsvImportDetail
      }
    }
  }
}
    fragment DirtyRowData on DirtyRowData {
  name
  assetManager
  portfolioManager
  customerId
  numStories
  streetAddress
  city
  state
  zipCode
  buildingType
  sqft
  yearBuilt
}
fragment CsvImportDetail on CsvImport {
  pid
  rawCsvWithPidsHref
  columnIds
  columnMappings {
    assetManagerColumnId
    portfolioManagerColumnId
    buildingTypeColumnId
    cityColumnId
    customerIdColumnId
    latitudeColumnId
    longitudeColumnId
    nameColumnId
    numStoriesColumnId
    numUnits1BdColumnId
    numUnits2BdColumnId
    numUnits3BdColumnId
    numUnits4BdColumnId
    numUnitsStudioColumnId
    numUnitsTotalColumnId
    sqftColumnId
    stateColumnId
    streetAddressColumnId
    yearBuiltColumnId
    zipCodeColumnId
  }
  buildingTypes
  buildingTypeMappings {
    office
    multifamily
    hotel
    hospital
    outpatient
    retailStandalone
    retailStripMall
    quickServiceRestaurant
    fullServiceRestaurant
    primarySchool
    secondarySchool
    singleFamilyDetached
    warehouse
    dataCenter
    coldStorage
    industrial
    parkingLot
    supermarket
  }
  guessedSchema {
    pid
    origin
  }
  labels {
    pid
    themeColor
    name
  }
  rows {
    id
    droppedAt
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    amendments {
      assetManager
      portfolioManager
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
    site {
      pid
    }
    dirty {
      ...DirtyRowData
    }
    errors {
      type
      message
    }
    warnings {
      type
      message
    }
  }
}`)

export const useAdminCsvImportDetailQuery = <
  TData = Types.AdminCsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminCsvImportDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCsvImportDetailQuery, TError, TData>({
    queryKey: ["AdminCsvImportDetail", variables],
    queryFn: publicFetcher<
      Types.AdminCsvImportDetailQuery,
      Types.AdminCsvImportDetailQueryVariables
    >(AdminCsvImportDetailDocument, variables),
    ...options,
  })
}

useAdminCsvImportDetailQuery.getKey = (
  variables: Types.AdminCsvImportDetailQueryVariables,
) => ["AdminCsvImportDetail", variables]

useAdminCsvImportDetailQuery.rootKey = "AdminCsvImportDetail"

export const useSuspenseAdminCsvImportDetailQuery = <
  TData = Types.AdminCsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminCsvImportDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCsvImportDetailQuery, TError, TData>({
    queryKey: ["AdminCsvImportDetail", variables],
    queryFn: publicFetcher<
      Types.AdminCsvImportDetailQuery,
      Types.AdminCsvImportDetailQueryVariables
    >(AdminCsvImportDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminCsvImportDetailQuery.getKey = (
  variables: Types.AdminCsvImportDetailQueryVariables,
) => ["AdminCsvImportDetail", variables]

useSuspenseAdminCsvImportDetailQuery.rootKey = "AdminCsvImportDetail"

export const useInfiniteAdminCsvImportDetailQuery = <
  TData = InfiniteData<Types.AdminCsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminCsvImportDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCsvImportDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCsvImportDetailQuery,
            Types.AdminCsvImportDetailQueryVariables
          >(AdminCsvImportDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCsvImportDetailQuery.getKey = (
  variables: Types.AdminCsvImportDetailQueryVariables,
) => ["AdminCsvImportDetail.infinite", variables]

useInfiniteAdminCsvImportDetailQuery.rootKey = "AdminCsvImportDetail.infinite"

export const useSuspenseInfiniteAdminCsvImportDetailQuery = <
  TData = InfiniteData<Types.AdminCsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminCsvImportDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminCsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCsvImportDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCsvImportDetailQuery,
            Types.AdminCsvImportDetailQueryVariables
          >(AdminCsvImportDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCsvImportDetailQuery.getKey = (
  variables: Types.AdminCsvImportDetailQueryVariables,
) => ["AdminCsvImportDetail.infinite", variables]

useSuspenseInfiniteAdminCsvImportDetailQuery.rootKey = "AdminCsvImportDetail.infinite"

useAdminCsvImportDetailQuery.fetcher = (
  variables: Types.AdminCsvImportDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCsvImportDetailQuery,
    Types.AdminCsvImportDetailQueryVariables
  >(AdminCsvImportDetailDocument, variables, options)

export const FlowerUrlDocument = new TypedDocumentString(`
    query FlowerUrl {
  celery {
    flowerUrl
  }
}
    `)

export const useFlowerUrlQuery = <TData = Types.FlowerUrlQuery, TError = unknown>(
  variables?: Types.FlowerUrlQueryVariables,
  options?: Omit<UseQueryOptions<Types.FlowerUrlQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.FlowerUrlQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FlowerUrlQuery, TError, TData>({
    queryKey: variables === undefined ? ["FlowerUrl"] : ["FlowerUrl", variables],
    queryFn: publicFetcher<Types.FlowerUrlQuery, Types.FlowerUrlQueryVariables>(
      FlowerUrlDocument,
      variables,
    ),
    ...options,
  })
}

useFlowerUrlQuery.getKey = (variables?: Types.FlowerUrlQueryVariables) =>
  variables === undefined ? ["FlowerUrl"] : ["FlowerUrl", variables]

useFlowerUrlQuery.rootKey = "FlowerUrl"

export const useSuspenseFlowerUrlQuery = <
  TData = Types.FlowerUrlQuery,
  TError = unknown,
>(
  variables?: Types.FlowerUrlQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FlowerUrlQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.FlowerUrlQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FlowerUrlQuery, TError, TData>({
    queryKey: variables === undefined ? ["FlowerUrl"] : ["FlowerUrl", variables],
    queryFn: publicFetcher<Types.FlowerUrlQuery, Types.FlowerUrlQueryVariables>(
      FlowerUrlDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseFlowerUrlQuery.getKey = (variables?: Types.FlowerUrlQueryVariables) =>
  variables === undefined ? ["FlowerUrl"] : ["FlowerUrl", variables]

useSuspenseFlowerUrlQuery.rootKey = "FlowerUrl"

export const useInfiniteFlowerUrlQuery = <
  TData = InfiniteData<Types.FlowerUrlQuery>,
  TError = unknown,
>(
  variables: Types.FlowerUrlQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FlowerUrlQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.FlowerUrlQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FlowerUrlQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FlowerUrl.infinite"]
            : ["FlowerUrl.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FlowerUrlQuery, Types.FlowerUrlQueryVariables>(
            FlowerUrlDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFlowerUrlQuery.getKey = (variables?: Types.FlowerUrlQueryVariables) =>
  variables === undefined ? ["FlowerUrl.infinite"] : ["FlowerUrl.infinite", variables]

useInfiniteFlowerUrlQuery.rootKey = "FlowerUrl.infinite"

export const useSuspenseInfiniteFlowerUrlQuery = <
  TData = InfiniteData<Types.FlowerUrlQuery>,
  TError = unknown,
>(
  variables: Types.FlowerUrlQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FlowerUrlQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FlowerUrlQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FlowerUrlQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FlowerUrl.infinite"]
            : ["FlowerUrl.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FlowerUrlQuery, Types.FlowerUrlQueryVariables>(
            FlowerUrlDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFlowerUrlQuery.getKey = (
  variables?: Types.FlowerUrlQueryVariables,
) =>
  variables === undefined ? ["FlowerUrl.infinite"] : ["FlowerUrl.infinite", variables]

useSuspenseInfiniteFlowerUrlQuery.rootKey = "FlowerUrl.infinite"

useFlowerUrlQuery.fetcher = (
  variables?: Types.FlowerUrlQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FlowerUrlQuery, Types.FlowerUrlQueryVariables>(
    FlowerUrlDocument,
    variables,
    options,
  )

export const CeleryTaskCountsDocument = new TypedDocumentString(`
    query CeleryTaskCounts {
  celery {
    taskCounts {
      name
      runningCount
      pendingCount
      successCount
      failureCount
      knownFailureCount
      canceledCount
      lostCount
    }
  }
}
    `)

export const useCeleryTaskCountsQuery = <
  TData = Types.CeleryTaskCountsQuery,
  TError = unknown,
>(
  variables?: Types.CeleryTaskCountsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CeleryTaskCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CeleryTaskCountsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["CeleryTaskCounts"] : ["CeleryTaskCounts", variables],
    queryFn: publicFetcher<
      Types.CeleryTaskCountsQuery,
      Types.CeleryTaskCountsQueryVariables
    >(CeleryTaskCountsDocument, variables),
    ...options,
  })
}

useCeleryTaskCountsQuery.getKey = (variables?: Types.CeleryTaskCountsQueryVariables) =>
  variables === undefined ? ["CeleryTaskCounts"] : ["CeleryTaskCounts", variables]

useCeleryTaskCountsQuery.rootKey = "CeleryTaskCounts"

export const useSuspenseCeleryTaskCountsQuery = <
  TData = Types.CeleryTaskCountsQuery,
  TError = unknown,
>(
  variables?: Types.CeleryTaskCountsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CeleryTaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CeleryTaskCountsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["CeleryTaskCounts"] : ["CeleryTaskCounts", variables],
    queryFn: publicFetcher<
      Types.CeleryTaskCountsQuery,
      Types.CeleryTaskCountsQueryVariables
    >(CeleryTaskCountsDocument, variables),
    ...options,
  })
}

useSuspenseCeleryTaskCountsQuery.getKey = (
  variables?: Types.CeleryTaskCountsQueryVariables,
) => (variables === undefined ? ["CeleryTaskCounts"] : ["CeleryTaskCounts", variables])

useSuspenseCeleryTaskCountsQuery.rootKey = "CeleryTaskCounts"

export const useInfiniteCeleryTaskCountsQuery = <
  TData = InfiniteData<Types.CeleryTaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.CeleryTaskCountsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CeleryTaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CeleryTaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CeleryTaskCounts.infinite"]
            : ["CeleryTaskCounts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CeleryTaskCountsQuery,
            Types.CeleryTaskCountsQueryVariables
          >(CeleryTaskCountsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCeleryTaskCountsQuery.getKey = (
  variables?: Types.CeleryTaskCountsQueryVariables,
) =>
  variables === undefined
    ? ["CeleryTaskCounts.infinite"]
    : ["CeleryTaskCounts.infinite", variables]

useInfiniteCeleryTaskCountsQuery.rootKey = "CeleryTaskCounts.infinite"

export const useSuspenseInfiniteCeleryTaskCountsQuery = <
  TData = InfiniteData<Types.CeleryTaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.CeleryTaskCountsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CeleryTaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CeleryTaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CeleryTaskCounts.infinite"]
            : ["CeleryTaskCounts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CeleryTaskCountsQuery,
            Types.CeleryTaskCountsQueryVariables
          >(CeleryTaskCountsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCeleryTaskCountsQuery.getKey = (
  variables?: Types.CeleryTaskCountsQueryVariables,
) =>
  variables === undefined
    ? ["CeleryTaskCounts.infinite"]
    : ["CeleryTaskCounts.infinite", variables]

useSuspenseInfiniteCeleryTaskCountsQuery.rootKey = "CeleryTaskCounts.infinite"

useCeleryTaskCountsQuery.fetcher = (
  variables?: Types.CeleryTaskCountsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CeleryTaskCountsQuery, Types.CeleryTaskCountsQueryVariables>(
    CeleryTaskCountsDocument,
    variables,
    options,
  )

export const SiteCeleryTaskCountsDocument = new TypedDocumentString(`
    query SiteCeleryTaskCounts($sitePid: String!) {
  celery {
    taskCounts(sitePid: $sitePid) {
      name
      runningCount
      pendingCount
      successCount
      failureCount
      knownFailureCount
      canceledCount
      lostCount
    }
  }
}
    `)

export const useSiteCeleryTaskCountsQuery = <
  TData = Types.SiteCeleryTaskCountsQuery,
  TError = unknown,
>(
  variables: Types.SiteCeleryTaskCountsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteCeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteCeleryTaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteCeleryTaskCountsQuery, TError, TData>({
    queryKey: ["SiteCeleryTaskCounts", variables],
    queryFn: publicFetcher<
      Types.SiteCeleryTaskCountsQuery,
      Types.SiteCeleryTaskCountsQueryVariables
    >(SiteCeleryTaskCountsDocument, variables),
    ...options,
  })
}

useSiteCeleryTaskCountsQuery.getKey = (
  variables: Types.SiteCeleryTaskCountsQueryVariables,
) => ["SiteCeleryTaskCounts", variables]

useSiteCeleryTaskCountsQuery.rootKey = "SiteCeleryTaskCounts"

export const useSuspenseSiteCeleryTaskCountsQuery = <
  TData = Types.SiteCeleryTaskCountsQuery,
  TError = unknown,
>(
  variables: Types.SiteCeleryTaskCountsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteCeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteCeleryTaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteCeleryTaskCountsQuery, TError, TData>({
    queryKey: ["SiteCeleryTaskCounts", variables],
    queryFn: publicFetcher<
      Types.SiteCeleryTaskCountsQuery,
      Types.SiteCeleryTaskCountsQueryVariables
    >(SiteCeleryTaskCountsDocument, variables),
    ...options,
  })
}

useSuspenseSiteCeleryTaskCountsQuery.getKey = (
  variables: Types.SiteCeleryTaskCountsQueryVariables,
) => ["SiteCeleryTaskCounts", variables]

useSuspenseSiteCeleryTaskCountsQuery.rootKey = "SiteCeleryTaskCounts"

export const useInfiniteSiteCeleryTaskCountsQuery = <
  TData = InfiniteData<Types.SiteCeleryTaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.SiteCeleryTaskCountsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteCeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteCeleryTaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteCeleryTaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteCeleryTaskCounts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteCeleryTaskCountsQuery,
            Types.SiteCeleryTaskCountsQueryVariables
          >(SiteCeleryTaskCountsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteCeleryTaskCountsQuery.getKey = (
  variables: Types.SiteCeleryTaskCountsQueryVariables,
) => ["SiteCeleryTaskCounts.infinite", variables]

useInfiniteSiteCeleryTaskCountsQuery.rootKey = "SiteCeleryTaskCounts.infinite"

export const useSuspenseInfiniteSiteCeleryTaskCountsQuery = <
  TData = InfiniteData<Types.SiteCeleryTaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.SiteCeleryTaskCountsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteCeleryTaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteCeleryTaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteCeleryTaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteCeleryTaskCounts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteCeleryTaskCountsQuery,
            Types.SiteCeleryTaskCountsQueryVariables
          >(SiteCeleryTaskCountsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteCeleryTaskCountsQuery.getKey = (
  variables: Types.SiteCeleryTaskCountsQueryVariables,
) => ["SiteCeleryTaskCounts.infinite", variables]

useSuspenseInfiniteSiteCeleryTaskCountsQuery.rootKey = "SiteCeleryTaskCounts.infinite"

useSiteCeleryTaskCountsQuery.fetcher = (
  variables: Types.SiteCeleryTaskCountsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteCeleryTaskCountsQuery,
    Types.SiteCeleryTaskCountsQueryVariables
  >(SiteCeleryTaskCountsDocument, variables, options)

export const FinmodelVersionsDocument = new TypedDocumentString(`
    query FinmodelVersions {
  admin {
    finmodelVersions {
      id
      createdAt
      ppaFmv
      loanFmv
      selfFmv
      leaseFmv
    }
  }
}
    `)

export const useFinmodelVersionsQuery = <
  TData = Types.FinmodelVersionsQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelVersionsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelVersionsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelVersionsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["FinmodelVersions"] : ["FinmodelVersions", variables],
    queryFn: publicFetcher<
      Types.FinmodelVersionsQuery,
      Types.FinmodelVersionsQueryVariables
    >(FinmodelVersionsDocument, variables),
    ...options,
  })
}

useFinmodelVersionsQuery.getKey = (variables?: Types.FinmodelVersionsQueryVariables) =>
  variables === undefined ? ["FinmodelVersions"] : ["FinmodelVersions", variables]

useFinmodelVersionsQuery.rootKey = "FinmodelVersions"

export const useSuspenseFinmodelVersionsQuery = <
  TData = Types.FinmodelVersionsQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelVersionsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelVersionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelVersionsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["FinmodelVersions"] : ["FinmodelVersions", variables],
    queryFn: publicFetcher<
      Types.FinmodelVersionsQuery,
      Types.FinmodelVersionsQueryVariables
    >(FinmodelVersionsDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelVersionsQuery.getKey = (
  variables?: Types.FinmodelVersionsQueryVariables,
) => (variables === undefined ? ["FinmodelVersions"] : ["FinmodelVersions", variables])

useSuspenseFinmodelVersionsQuery.rootKey = "FinmodelVersions"

export const useInfiniteFinmodelVersionsQuery = <
  TData = InfiniteData<Types.FinmodelVersionsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelVersionsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelVersionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelVersionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelVersions.infinite"]
            : ["FinmodelVersions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelVersionsQuery,
            Types.FinmodelVersionsQueryVariables
          >(FinmodelVersionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelVersionsQuery.getKey = (
  variables?: Types.FinmodelVersionsQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelVersions.infinite"]
    : ["FinmodelVersions.infinite", variables]

useInfiniteFinmodelVersionsQuery.rootKey = "FinmodelVersions.infinite"

export const useSuspenseInfiniteFinmodelVersionsQuery = <
  TData = InfiniteData<Types.FinmodelVersionsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelVersionsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelVersionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelVersionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelVersions.infinite"]
            : ["FinmodelVersions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelVersionsQuery,
            Types.FinmodelVersionsQueryVariables
          >(FinmodelVersionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelVersionsQuery.getKey = (
  variables?: Types.FinmodelVersionsQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelVersions.infinite"]
    : ["FinmodelVersions.infinite", variables]

useSuspenseInfiniteFinmodelVersionsQuery.rootKey = "FinmodelVersions.infinite"

useFinmodelVersionsQuery.fetcher = (
  variables?: Types.FinmodelVersionsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelVersionsQuery, Types.FinmodelVersionsQueryVariables>(
    FinmodelVersionsDocument,
    variables,
    options,
  )

export const JobActivitySummaryDocument = new TypedDocumentString(`
    query JobActivitySummary($since: DateTime!) {
  jobActivity {
    summary(since: $since) {
      csPriceChanges {
        date
        utilityName
        finproductName
        columnName
        oldValue
        newValue
        changedBy
      }
      csProgramAdditions {
        kind
        name
        state
        createdAt
      }
      finmodelOverrideChanges {
        scope
        targetName
        inputName
        inputValue
        reason
        createdAt
        createdBy
      }
      manualReluxRequests {
        portfolioName
        requestedAt
      }
      sitesAddedByPortfolio {
        portfolioName
        orgName
        sitesAdded
      }
    }
  }
}
    `)

export const useJobActivitySummaryQuery = <
  TData = Types.JobActivitySummaryQuery,
  TError = unknown,
>(
  variables: Types.JobActivitySummaryQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.JobActivitySummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.JobActivitySummaryQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.JobActivitySummaryQuery, TError, TData>({
    queryKey: ["JobActivitySummary", variables],
    queryFn: publicFetcher<
      Types.JobActivitySummaryQuery,
      Types.JobActivitySummaryQueryVariables
    >(JobActivitySummaryDocument, variables),
    ...options,
  })
}

useJobActivitySummaryQuery.getKey = (
  variables: Types.JobActivitySummaryQueryVariables,
) => ["JobActivitySummary", variables]

useJobActivitySummaryQuery.rootKey = "JobActivitySummary"

export const useSuspenseJobActivitySummaryQuery = <
  TData = Types.JobActivitySummaryQuery,
  TError = unknown,
>(
  variables: Types.JobActivitySummaryQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.JobActivitySummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.JobActivitySummaryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.JobActivitySummaryQuery, TError, TData>({
    queryKey: ["JobActivitySummary", variables],
    queryFn: publicFetcher<
      Types.JobActivitySummaryQuery,
      Types.JobActivitySummaryQueryVariables
    >(JobActivitySummaryDocument, variables),
    ...options,
  })
}

useSuspenseJobActivitySummaryQuery.getKey = (
  variables: Types.JobActivitySummaryQueryVariables,
) => ["JobActivitySummary", variables]

useSuspenseJobActivitySummaryQuery.rootKey = "JobActivitySummary"

export const useInfiniteJobActivitySummaryQuery = <
  TData = InfiniteData<Types.JobActivitySummaryQuery>,
  TError = unknown,
>(
  variables: Types.JobActivitySummaryQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.JobActivitySummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.JobActivitySummaryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.JobActivitySummaryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["JobActivitySummary.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.JobActivitySummaryQuery,
            Types.JobActivitySummaryQueryVariables
          >(JobActivitySummaryDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteJobActivitySummaryQuery.getKey = (
  variables: Types.JobActivitySummaryQueryVariables,
) => ["JobActivitySummary.infinite", variables]

useInfiniteJobActivitySummaryQuery.rootKey = "JobActivitySummary.infinite"

export const useSuspenseInfiniteJobActivitySummaryQuery = <
  TData = InfiniteData<Types.JobActivitySummaryQuery>,
  TError = unknown,
>(
  variables: Types.JobActivitySummaryQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.JobActivitySummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.JobActivitySummaryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.JobActivitySummaryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["JobActivitySummary.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.JobActivitySummaryQuery,
            Types.JobActivitySummaryQueryVariables
          >(JobActivitySummaryDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteJobActivitySummaryQuery.getKey = (
  variables: Types.JobActivitySummaryQueryVariables,
) => ["JobActivitySummary.infinite", variables]

useSuspenseInfiniteJobActivitySummaryQuery.rootKey = "JobActivitySummary.infinite"

useJobActivitySummaryQuery.fetcher = (
  variables: Types.JobActivitySummaryQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.JobActivitySummaryQuery, Types.JobActivitySummaryQueryVariables>(
    JobActivitySummaryDocument,
    variables,
    options,
  )

export const AdminRebuildProximityCacheDocument = new TypedDocumentString(`
    mutation AdminRebuildProximityCache {
  admin {
    interconnectionSegments {
      rebuildProximityCache {
        taskId
        state
      }
    }
  }
}
    `)

export const useAdminRebuildProximityCacheMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AdminRebuildProximityCacheMutation,
    TError,
    Types.AdminRebuildProximityCacheMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AdminRebuildProximityCacheMutation,
    TError,
    Types.AdminRebuildProximityCacheMutationVariables,
    TContext
  >({
    mutationKey: ["AdminRebuildProximityCache"],
    mutationFn: (variables?: Types.AdminRebuildProximityCacheMutationVariables) =>
      publicFetcher<
        Types.AdminRebuildProximityCacheMutation,
        Types.AdminRebuildProximityCacheMutationVariables
      >(AdminRebuildProximityCacheDocument, variables)(),
    ...options,
  })
}

useAdminRebuildProximityCacheMutation.fetcher = (
  variables?: Types.AdminRebuildProximityCacheMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminRebuildProximityCacheMutation,
    Types.AdminRebuildProximityCacheMutationVariables
  >(AdminRebuildProximityCacheDocument, variables, options)

export const AdminInterconnectionSegmentsTableViewDocument = new TypedDocumentString(`
    query AdminInterconnectionSegmentsTableView($filter: InterconnectionSegmentsFilter, $filterBy: InterconnectionSegmentsTableFilter, $sortBy: [InterconnectionSegmentsTableSort!]) {
  admin {
    interconnectionSegments {
      table(segmentsFilter: $filter) {
        view(filterBy: $filterBy, sortBy: $sortBy) {
          keys
          count
        }
      }
    }
  }
}
    `)

export const useAdminInterconnectionSegmentsTableViewQuery = <
  TData = Types.AdminInterconnectionSegmentsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInterconnectionSegmentsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInterconnectionSegmentsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminInterconnectionSegmentsTableView"]
        : ["AdminInterconnectionSegmentsTableView", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      Types.AdminInterconnectionSegmentsTableViewQueryVariables
    >(AdminInterconnectionSegmentsTableViewDocument, variables),
    ...options,
  })
}

useAdminInterconnectionSegmentsTableViewQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableView"]
    : ["AdminInterconnectionSegmentsTableView", variables]

useAdminInterconnectionSegmentsTableViewQuery.rootKey =
  "AdminInterconnectionSegmentsTableView"

export const useSuspenseAdminInterconnectionSegmentsTableViewQuery = <
  TData = Types.AdminInterconnectionSegmentsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<
    Types.AdminInterconnectionSegmentsTableViewQuery,
    TError,
    TData
  >({
    queryKey:
      variables === undefined
        ? ["AdminInterconnectionSegmentsTableView"]
        : ["AdminInterconnectionSegmentsTableView", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      Types.AdminInterconnectionSegmentsTableViewQueryVariables
    >(AdminInterconnectionSegmentsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminInterconnectionSegmentsTableViewQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableView"]
    : ["AdminInterconnectionSegmentsTableView", variables]

useSuspenseAdminInterconnectionSegmentsTableViewQuery.rootKey =
  "AdminInterconnectionSegmentsTableView"

export const useInfiniteAdminInterconnectionSegmentsTableViewQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<
    Types.AdminInterconnectionSegmentsTableViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInterconnectionSegmentsTableView.infinite"]
            : ["AdminInterconnectionSegmentsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentsTableViewQuery,
            Types.AdminInterconnectionSegmentsTableViewQueryVariables
          >(AdminInterconnectionSegmentsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInterconnectionSegmentsTableViewQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableView.infinite"]
    : ["AdminInterconnectionSegmentsTableView.infinite", variables]

useInfiniteAdminInterconnectionSegmentsTableViewQuery.rootKey =
  "AdminInterconnectionSegmentsTableView.infinite"

export const useSuspenseInfiniteAdminInterconnectionSegmentsTableViewQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInterconnectionSegmentsTableViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInterconnectionSegmentsTableView.infinite"]
            : ["AdminInterconnectionSegmentsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentsTableViewQuery,
            Types.AdminInterconnectionSegmentsTableViewQueryVariables
          >(AdminInterconnectionSegmentsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInterconnectionSegmentsTableViewQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableView.infinite"]
    : ["AdminInterconnectionSegmentsTableView.infinite", variables]

useSuspenseInfiniteAdminInterconnectionSegmentsTableViewQuery.rootKey =
  "AdminInterconnectionSegmentsTableView.infinite"

useAdminInterconnectionSegmentsTableViewQuery.fetcher = (
  variables?: Types.AdminInterconnectionSegmentsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInterconnectionSegmentsTableViewQuery,
    Types.AdminInterconnectionSegmentsTableViewQueryVariables
  >(AdminInterconnectionSegmentsTableViewDocument, variables, options)

export const AdminInterconnectionSegmentsTableFacetsDocument = new TypedDocumentString(`
    query AdminInterconnectionSegmentsTableFacets {
  admin {
    interconnectionSegments {
      table {
        view {
          stats {
            substations
            utilityNames
          }
        }
      }
    }
  }
}
    `)

export const useAdminInterconnectionSegmentsTableFacetsQuery = <
  TData = Types.AdminInterconnectionSegmentsTableFacetsQuery,
  TError = unknown,
>(
  variables?: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInterconnectionSegmentsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInterconnectionSegmentsTableFacetsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminInterconnectionSegmentsTableFacets"]
        : ["AdminInterconnectionSegmentsTableFacets", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      Types.AdminInterconnectionSegmentsTableFacetsQueryVariables
    >(AdminInterconnectionSegmentsTableFacetsDocument, variables),
    ...options,
  })
}

useAdminInterconnectionSegmentsTableFacetsQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableFacets"]
    : ["AdminInterconnectionSegmentsTableFacets", variables]

useAdminInterconnectionSegmentsTableFacetsQuery.rootKey =
  "AdminInterconnectionSegmentsTableFacets"

export const useSuspenseAdminInterconnectionSegmentsTableFacetsQuery = <
  TData = Types.AdminInterconnectionSegmentsTableFacetsQuery,
  TError = unknown,
>(
  variables?: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<
    Types.AdminInterconnectionSegmentsTableFacetsQuery,
    TError,
    TData
  >({
    queryKey:
      variables === undefined
        ? ["AdminInterconnectionSegmentsTableFacets"]
        : ["AdminInterconnectionSegmentsTableFacets", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      Types.AdminInterconnectionSegmentsTableFacetsQueryVariables
    >(AdminInterconnectionSegmentsTableFacetsDocument, variables),
    ...options,
  })
}

useSuspenseAdminInterconnectionSegmentsTableFacetsQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableFacets"]
    : ["AdminInterconnectionSegmentsTableFacets", variables]

useSuspenseAdminInterconnectionSegmentsTableFacetsQuery.rootKey =
  "AdminInterconnectionSegmentsTableFacets"

export const useInfiniteAdminInterconnectionSegmentsTableFacetsQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentsTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<
    Types.AdminInterconnectionSegmentsTableFacetsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInterconnectionSegmentsTableFacets.infinite"]
            : ["AdminInterconnectionSegmentsTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentsTableFacetsQuery,
            Types.AdminInterconnectionSegmentsTableFacetsQueryVariables
          >(AdminInterconnectionSegmentsTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInterconnectionSegmentsTableFacetsQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableFacets.infinite"]
    : ["AdminInterconnectionSegmentsTableFacets.infinite", variables]

useInfiniteAdminInterconnectionSegmentsTableFacetsQuery.rootKey =
  "AdminInterconnectionSegmentsTableFacets.infinite"

export const useSuspenseInfiniteAdminInterconnectionSegmentsTableFacetsQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentsTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInterconnectionSegmentsTableFacetsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInterconnectionSegmentsTableFacets.infinite"]
            : ["AdminInterconnectionSegmentsTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentsTableFacetsQuery,
            Types.AdminInterconnectionSegmentsTableFacetsQueryVariables
          >(AdminInterconnectionSegmentsTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInterconnectionSegmentsTableFacetsQuery.getKey = (
  variables?: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["AdminInterconnectionSegmentsTableFacets.infinite"]
    : ["AdminInterconnectionSegmentsTableFacets.infinite", variables]

useSuspenseInfiniteAdminInterconnectionSegmentsTableFacetsQuery.rootKey =
  "AdminInterconnectionSegmentsTableFacets.infinite"

useAdminInterconnectionSegmentsTableFacetsQuery.fetcher = (
  variables?: Types.AdminInterconnectionSegmentsTableFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInterconnectionSegmentsTableFacetsQuery,
    Types.AdminInterconnectionSegmentsTableFacetsQueryVariables
  >(AdminInterconnectionSegmentsTableFacetsDocument, variables, options)

export const AdminInterconnectionSegmentsTableRowsDocument = new TypedDocumentString(`
    query AdminInterconnectionSegmentsTableRows($filter: InterconnectionSegmentsFilter, $keys: [String!]!) {
  admin {
    interconnectionSegments {
      table(segmentsFilter: $filter) {
        rows(keys: $keys) {
          pk
          feeder
          segmentId
          utilityId
          substation
          pvVoltageKv
          pvHostingCapacityKw
          pvFeederQueuedCapacityKw
          pvSubstationHostingCapacityKw
          pvSubstationQueuedCapacityKw
          netPvHostingCapacityKw
          storageChargeCapacityKw
          storageDischargeCapacityKw
          utilityName
        }
      }
    }
  }
}
    `)

export const useAdminInterconnectionSegmentsTableRowsQuery = <
  TData = Types.AdminInterconnectionSegmentsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInterconnectionSegmentsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInterconnectionSegmentsTableRowsQuery, TError, TData>({
    queryKey: ["AdminInterconnectionSegmentsTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      Types.AdminInterconnectionSegmentsTableRowsQueryVariables
    >(AdminInterconnectionSegmentsTableRowsDocument, variables),
    ...options,
  })
}

useAdminInterconnectionSegmentsTableRowsQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
) => ["AdminInterconnectionSegmentsTableRows", variables]

useAdminInterconnectionSegmentsTableRowsQuery.rootKey =
  "AdminInterconnectionSegmentsTableRows"

export const useSuspenseAdminInterconnectionSegmentsTableRowsQuery = <
  TData = Types.AdminInterconnectionSegmentsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<
    Types.AdminInterconnectionSegmentsTableRowsQuery,
    TError,
    TData
  >({
    queryKey: ["AdminInterconnectionSegmentsTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      Types.AdminInterconnectionSegmentsTableRowsQueryVariables
    >(AdminInterconnectionSegmentsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminInterconnectionSegmentsTableRowsQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
) => ["AdminInterconnectionSegmentsTableRows", variables]

useSuspenseAdminInterconnectionSegmentsTableRowsQuery.rootKey =
  "AdminInterconnectionSegmentsTableRows"

export const useInfiniteAdminInterconnectionSegmentsTableRowsQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<
    Types.AdminInterconnectionSegmentsTableRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInterconnectionSegmentsTableRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentsTableRowsQuery,
            Types.AdminInterconnectionSegmentsTableRowsQueryVariables
          >(AdminInterconnectionSegmentsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInterconnectionSegmentsTableRowsQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
) => ["AdminInterconnectionSegmentsTableRows.infinite", variables]

useInfiniteAdminInterconnectionSegmentsTableRowsQuery.rootKey =
  "AdminInterconnectionSegmentsTableRows.infinite"

export const useSuspenseInfiniteAdminInterconnectionSegmentsTableRowsQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInterconnectionSegmentsTableRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInterconnectionSegmentsTableRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentsTableRowsQuery,
            Types.AdminInterconnectionSegmentsTableRowsQueryVariables
          >(AdminInterconnectionSegmentsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInterconnectionSegmentsTableRowsQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
) => ["AdminInterconnectionSegmentsTableRows.infinite", variables]

useSuspenseInfiniteAdminInterconnectionSegmentsTableRowsQuery.rootKey =
  "AdminInterconnectionSegmentsTableRows.infinite"

useAdminInterconnectionSegmentsTableRowsQuery.fetcher = (
  variables: Types.AdminInterconnectionSegmentsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInterconnectionSegmentsTableRowsQuery,
    Types.AdminInterconnectionSegmentsTableRowsQueryVariables
  >(AdminInterconnectionSegmentsTableRowsDocument, variables, options)

export const AdminInterconnectionSegmentDetailDocument = new TypedDocumentString(`
    query AdminInterconnectionSegmentDetail($pk: String!) {
  admin {
    interconnectionSegments {
      one(pk: $pk) {
        pk
        segmentId
        shapeGeojson
        centroidLatitude
        centroidLongitude
        utilityId
        utilityShortName
        feeder
        substation
        pvVoltageRaw
        pvVoltageKv
        storageVoltage
        pvObjectId
        storageObjectId
        pvHostingCapacityKw
        pvMinHostingCapacityKw
        pvMaxHostingCapacityKw
        pvMinConstrainingMetric
        pvMaxConstrainingMetric
        pvFeederInstalledCapacityKw
        pvFeederQueuedCapacityKw
        pvSubstationHostingCapacityKw
        pvSubstationQueuedCapacityKw
        pvSubstationQueuedApplicationsCount
        storageChargeCapacityKw
        storageMinChargeKw
        storageMaxChargeKw
        storageDischargeCapacityKw
        storageMinDischargeKw
        storageMaxDischargeKw
        storageChargeThermalLimitKw
        storageDischargeThermalLimitKw
        storageChargeAntiIslandLimitKw
        storageDischargeAntiIslandLimitKw
        pvNotes
        storageNotes
      }
    }
  }
}
    `)

export const useAdminInterconnectionSegmentDetailQuery = <
  TData = Types.AdminInterconnectionSegmentDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInterconnectionSegmentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInterconnectionSegmentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInterconnectionSegmentDetailQuery, TError, TData>({
    queryKey: ["AdminInterconnectionSegmentDetail", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentDetailQuery,
      Types.AdminInterconnectionSegmentDetailQueryVariables
    >(AdminInterconnectionSegmentDetailDocument, variables),
    ...options,
  })
}

useAdminInterconnectionSegmentDetailQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
) => ["AdminInterconnectionSegmentDetail", variables]

useAdminInterconnectionSegmentDetailQuery.rootKey = "AdminInterconnectionSegmentDetail"

export const useSuspenseAdminInterconnectionSegmentDetailQuery = <
  TData = Types.AdminInterconnectionSegmentDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInterconnectionSegmentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminInterconnectionSegmentDetailQuery, TError, TData>({
    queryKey: ["AdminInterconnectionSegmentDetail", variables],
    queryFn: publicFetcher<
      Types.AdminInterconnectionSegmentDetailQuery,
      Types.AdminInterconnectionSegmentDetailQueryVariables
    >(AdminInterconnectionSegmentDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminInterconnectionSegmentDetailQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
) => ["AdminInterconnectionSegmentDetail", variables]

useSuspenseAdminInterconnectionSegmentDetailQuery.rootKey =
  "AdminInterconnectionSegmentDetail"

export const useInfiniteAdminInterconnectionSegmentDetailQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminInterconnectionSegmentDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInterconnectionSegmentDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentDetailQuery,
            Types.AdminInterconnectionSegmentDetailQueryVariables
          >(AdminInterconnectionSegmentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInterconnectionSegmentDetailQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
) => ["AdminInterconnectionSegmentDetail.infinite", variables]

useInfiniteAdminInterconnectionSegmentDetailQuery.rootKey =
  "AdminInterconnectionSegmentDetail.infinite"

export const useSuspenseInfiniteAdminInterconnectionSegmentDetailQuery = <
  TData = InfiniteData<Types.AdminInterconnectionSegmentDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInterconnectionSegmentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInterconnectionSegmentDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInterconnectionSegmentDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInterconnectionSegmentDetailQuery,
            Types.AdminInterconnectionSegmentDetailQueryVariables
          >(AdminInterconnectionSegmentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInterconnectionSegmentDetailQuery.getKey = (
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
) => ["AdminInterconnectionSegmentDetail.infinite", variables]

useSuspenseInfiniteAdminInterconnectionSegmentDetailQuery.rootKey =
  "AdminInterconnectionSegmentDetail.infinite"

useAdminInterconnectionSegmentDetailQuery.fetcher = (
  variables: Types.AdminInterconnectionSegmentDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInterconnectionSegmentDetailQuery,
    Types.AdminInterconnectionSegmentDetailQueryVariables
  >(AdminInterconnectionSegmentDetailDocument, variables, options)

export const AdminSubstationDetailDocument = new TypedDocumentString(`
    query AdminSubstationDetail($utilityId: Int!, $substation: String!) {
  admin {
    interconnectionSegments {
      substation(utilityId: $utilityId, substation: $substation) {
        utilityId
        utilityName
        utilityShortName
        substation
        feederCount
        pvHostingCapacityKw
        pvQueuedCapacityKw
        pvQueuedApplicationsCount
        centroidLatitude
        centroidLongitude
        feeders {
          pk
          feeder
          segmentId
          pvVoltageRaw
          pvVoltageKv
          shapeGeojson
          pvHostingCapacityKw
          pvFeederInstalledCapacityKw
          pvFeederQueuedCapacityKw
          storageChargeCapacityKw
          storageDischargeCapacityKw
        }
      }
    }
  }
}
    `)

export const useAdminSubstationDetailQuery = <
  TData = Types.AdminSubstationDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminSubstationDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSubstationDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSubstationDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSubstationDetailQuery, TError, TData>({
    queryKey: ["AdminSubstationDetail", variables],
    queryFn: publicFetcher<
      Types.AdminSubstationDetailQuery,
      Types.AdminSubstationDetailQueryVariables
    >(AdminSubstationDetailDocument, variables),
    ...options,
  })
}

useAdminSubstationDetailQuery.getKey = (
  variables: Types.AdminSubstationDetailQueryVariables,
) => ["AdminSubstationDetail", variables]

useAdminSubstationDetailQuery.rootKey = "AdminSubstationDetail"

export const useSuspenseAdminSubstationDetailQuery = <
  TData = Types.AdminSubstationDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminSubstationDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSubstationDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSubstationDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSubstationDetailQuery, TError, TData>({
    queryKey: ["AdminSubstationDetail", variables],
    queryFn: publicFetcher<
      Types.AdminSubstationDetailQuery,
      Types.AdminSubstationDetailQueryVariables
    >(AdminSubstationDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminSubstationDetailQuery.getKey = (
  variables: Types.AdminSubstationDetailQueryVariables,
) => ["AdminSubstationDetail", variables]

useSuspenseAdminSubstationDetailQuery.rootKey = "AdminSubstationDetail"

export const useInfiniteAdminSubstationDetailQuery = <
  TData = InfiniteData<Types.AdminSubstationDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminSubstationDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSubstationDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSubstationDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSubstationDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSubstationDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSubstationDetailQuery,
            Types.AdminSubstationDetailQueryVariables
          >(AdminSubstationDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSubstationDetailQuery.getKey = (
  variables: Types.AdminSubstationDetailQueryVariables,
) => ["AdminSubstationDetail.infinite", variables]

useInfiniteAdminSubstationDetailQuery.rootKey = "AdminSubstationDetail.infinite"

export const useSuspenseInfiniteAdminSubstationDetailQuery = <
  TData = InfiniteData<Types.AdminSubstationDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminSubstationDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSubstationDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSubstationDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSubstationDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSubstationDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSubstationDetailQuery,
            Types.AdminSubstationDetailQueryVariables
          >(AdminSubstationDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSubstationDetailQuery.getKey = (
  variables: Types.AdminSubstationDetailQueryVariables,
) => ["AdminSubstationDetail.infinite", variables]

useSuspenseInfiniteAdminSubstationDetailQuery.rootKey = "AdminSubstationDetail.infinite"

useAdminSubstationDetailQuery.fetcher = (
  variables: Types.AdminSubstationDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSubstationDetailQuery,
    Types.AdminSubstationDetailQueryVariables
  >(AdminSubstationDetailDocument, variables, options)

export const AdminSegmentNearbySitesDocument = new TypedDocumentString(`
    query AdminSegmentNearbySites($pk: String!, $radiusMiles: Float!) {
  admin {
    interconnectionSegments {
      nearbySites(pk: $pk, radiusMiles: $radiusMiles) {
        sitePid
        name
        streetAddress
        city
        state
        orgName
        orgPid
        portfolioName
        portfolioPid
        latitude
        longitude
        shapeGeojson
        roofMaxPvKw
        distanceMiles
        buildingType
        utilityName
        freeSpaceSqft
        siteLabels {
          pid
          name
          themeColor
          isGlobal
        }
      }
    }
  }
}
    `)

export const useAdminSegmentNearbySitesQuery = <
  TData = Types.AdminSegmentNearbySitesQuery,
  TError = unknown,
>(
  variables: Types.AdminSegmentNearbySitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSegmentNearbySitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSegmentNearbySitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSegmentNearbySitesQuery, TError, TData>({
    queryKey: ["AdminSegmentNearbySites", variables],
    queryFn: publicFetcher<
      Types.AdminSegmentNearbySitesQuery,
      Types.AdminSegmentNearbySitesQueryVariables
    >(AdminSegmentNearbySitesDocument, variables),
    ...options,
  })
}

useAdminSegmentNearbySitesQuery.getKey = (
  variables: Types.AdminSegmentNearbySitesQueryVariables,
) => ["AdminSegmentNearbySites", variables]

useAdminSegmentNearbySitesQuery.rootKey = "AdminSegmentNearbySites"

export const useSuspenseAdminSegmentNearbySitesQuery = <
  TData = Types.AdminSegmentNearbySitesQuery,
  TError = unknown,
>(
  variables: Types.AdminSegmentNearbySitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSegmentNearbySitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSegmentNearbySitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSegmentNearbySitesQuery, TError, TData>({
    queryKey: ["AdminSegmentNearbySites", variables],
    queryFn: publicFetcher<
      Types.AdminSegmentNearbySitesQuery,
      Types.AdminSegmentNearbySitesQueryVariables
    >(AdminSegmentNearbySitesDocument, variables),
    ...options,
  })
}

useSuspenseAdminSegmentNearbySitesQuery.getKey = (
  variables: Types.AdminSegmentNearbySitesQueryVariables,
) => ["AdminSegmentNearbySites", variables]

useSuspenseAdminSegmentNearbySitesQuery.rootKey = "AdminSegmentNearbySites"

export const useInfiniteAdminSegmentNearbySitesQuery = <
  TData = InfiniteData<Types.AdminSegmentNearbySitesQuery>,
  TError = unknown,
>(
  variables: Types.AdminSegmentNearbySitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSegmentNearbySitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSegmentNearbySitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSegmentNearbySitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSegmentNearbySites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSegmentNearbySitesQuery,
            Types.AdminSegmentNearbySitesQueryVariables
          >(AdminSegmentNearbySitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSegmentNearbySitesQuery.getKey = (
  variables: Types.AdminSegmentNearbySitesQueryVariables,
) => ["AdminSegmentNearbySites.infinite", variables]

useInfiniteAdminSegmentNearbySitesQuery.rootKey = "AdminSegmentNearbySites.infinite"

export const useSuspenseInfiniteAdminSegmentNearbySitesQuery = <
  TData = InfiniteData<Types.AdminSegmentNearbySitesQuery>,
  TError = unknown,
>(
  variables: Types.AdminSegmentNearbySitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSegmentNearbySitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSegmentNearbySitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSegmentNearbySitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSegmentNearbySites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSegmentNearbySitesQuery,
            Types.AdminSegmentNearbySitesQueryVariables
          >(AdminSegmentNearbySitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSegmentNearbySitesQuery.getKey = (
  variables: Types.AdminSegmentNearbySitesQueryVariables,
) => ["AdminSegmentNearbySites.infinite", variables]

useSuspenseInfiniteAdminSegmentNearbySitesQuery.rootKey =
  "AdminSegmentNearbySites.infinite"

useAdminSegmentNearbySitesQuery.fetcher = (
  variables: Types.AdminSegmentNearbySitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSegmentNearbySitesQuery,
    Types.AdminSegmentNearbySitesQueryVariables
  >(AdminSegmentNearbySitesDocument, variables, options)

export const FinmodelFailureIndexDocument = new TypedDocumentString(`
    query FinmodelFailureIndex {
  finmodelFailures {
    finmodelFailureIndex {
      fmv
      name
      failureCount
    }
  }
}
    `)

export const useFinmodelFailureIndexQuery = <
  TData = Types.FinmodelFailureIndexQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureIndexQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FinmodelFailureIndex"]
        : ["FinmodelFailureIndex", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureIndexQuery,
      Types.FinmodelFailureIndexQueryVariables
    >(FinmodelFailureIndexDocument, variables),
    ...options,
  })
}

useFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex"]
    : ["FinmodelFailureIndex", variables]

useFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex"

export const useSuspenseFinmodelFailureIndexQuery = <
  TData = Types.FinmodelFailureIndexQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureIndexQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FinmodelFailureIndex"]
        : ["FinmodelFailureIndex", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureIndexQuery,
      Types.FinmodelFailureIndexQueryVariables
    >(FinmodelFailureIndexDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex"]
    : ["FinmodelFailureIndex", variables]

useSuspenseFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex"

export const useInfiniteFinmodelFailureIndexQuery = <
  TData = InfiniteData<Types.FinmodelFailureIndexQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelFailureIndex.infinite"]
            : ["FinmodelFailureIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureIndexQuery,
            Types.FinmodelFailureIndexQueryVariables
          >(FinmodelFailureIndexDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex.infinite"]
    : ["FinmodelFailureIndex.infinite", variables]

useInfiniteFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex.infinite"

export const useSuspenseInfiniteFinmodelFailureIndexQuery = <
  TData = InfiniteData<Types.FinmodelFailureIndexQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelFailureIndex.infinite"]
            : ["FinmodelFailureIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureIndexQuery,
            Types.FinmodelFailureIndexQueryVariables
          >(FinmodelFailureIndexDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex.infinite"]
    : ["FinmodelFailureIndex.infinite", variables]

useSuspenseInfiniteFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex.infinite"

useFinmodelFailureIndexQuery.fetcher = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinmodelFailureIndexQuery,
    Types.FinmodelFailureIndexQueryVariables
  >(FinmodelFailureIndexDocument, variables, options)

export const FinmodelFailureViewDocument = new TypedDocumentString(`
    query FinmodelFailureView($filterBy: FinmodelFailureTableFilter!, $sortBy: [FinmodelFailureTableSort!]) {
  finmodelFailures {
    finmodelFailures {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
      }
    }
  }
}
    `)

export const useFinmodelFailureViewQuery = <
  TData = Types.FinmodelFailureViewQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureViewQuery, TError, TData>({
    queryKey: ["FinmodelFailureView", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureViewQuery,
      Types.FinmodelFailureViewQueryVariables
    >(FinmodelFailureViewDocument, variables),
    ...options,
  })
}

useFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView", variables]

useFinmodelFailureViewQuery.rootKey = "FinmodelFailureView"

export const useSuspenseFinmodelFailureViewQuery = <
  TData = Types.FinmodelFailureViewQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureViewQuery, TError, TData>({
    queryKey: ["FinmodelFailureView", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureViewQuery,
      Types.FinmodelFailureViewQueryVariables
    >(FinmodelFailureViewDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView", variables]

useSuspenseFinmodelFailureViewQuery.rootKey = "FinmodelFailureView"

export const useInfiniteFinmodelFailureViewQuery = <
  TData = InfiniteData<Types.FinmodelFailureViewQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureViewQuery,
            Types.FinmodelFailureViewQueryVariables
          >(FinmodelFailureViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView.infinite", variables]

useInfiniteFinmodelFailureViewQuery.rootKey = "FinmodelFailureView.infinite"

export const useSuspenseInfiniteFinmodelFailureViewQuery = <
  TData = InfiniteData<Types.FinmodelFailureViewQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureViewQuery,
            Types.FinmodelFailureViewQueryVariables
          >(FinmodelFailureViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView.infinite", variables]

useSuspenseInfiniteFinmodelFailureViewQuery.rootKey = "FinmodelFailureView.infinite"

useFinmodelFailureViewQuery.fetcher = (
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinmodelFailureViewQuery,
    Types.FinmodelFailureViewQueryVariables
  >(FinmodelFailureViewDocument, variables, options)

export const FinmodelFailureRowDocument = new TypedDocumentString(`
    query FinmodelFailureRow($keys: [Int!]!) {
  finmodelFailures {
    finmodelFailures {
      rows(keys: $keys) {
        id
        createdAt
        fmv
        name
        message
      }
    }
  }
}
    `)

export const useFinmodelFailureRowQuery = <
  TData = Types.FinmodelFailureRowQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureRowQuery, TError, TData>({
    queryKey: ["FinmodelFailureRow", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureRowQuery,
      Types.FinmodelFailureRowQueryVariables
    >(FinmodelFailureRowDocument, variables),
    ...options,
  })
}

useFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow", variables]

useFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow"

export const useSuspenseFinmodelFailureRowQuery = <
  TData = Types.FinmodelFailureRowQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureRowQuery, TError, TData>({
    queryKey: ["FinmodelFailureRow", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureRowQuery,
      Types.FinmodelFailureRowQueryVariables
    >(FinmodelFailureRowDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow", variables]

useSuspenseFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow"

export const useInfiniteFinmodelFailureRowQuery = <
  TData = InfiniteData<Types.FinmodelFailureRowQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureRow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureRowQuery,
            Types.FinmodelFailureRowQueryVariables
          >(FinmodelFailureRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow.infinite", variables]

useInfiniteFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow.infinite"

export const useSuspenseInfiniteFinmodelFailureRowQuery = <
  TData = InfiniteData<Types.FinmodelFailureRowQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureRow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureRowQuery,
            Types.FinmodelFailureRowQueryVariables
          >(FinmodelFailureRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow.infinite", variables]

useSuspenseInfiniteFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow.infinite"

useFinmodelFailureRowQuery.fetcher = (
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelFailureRowQuery, Types.FinmodelFailureRowQueryVariables>(
    FinmodelFailureRowDocument,
    variables,
    options,
  )

export const AdminSiteNearbyFeedersDocument = new TypedDocumentString(`
    query AdminSiteNearbyFeeders($sitePid: String!, $radiusMiles: Float!) {
  admin {
    interconnectionSegments {
      nearbyFeeders(sitePid: $sitePid, radiusMiles: $radiusMiles) {
        pk
        feeder
        segmentId
        utilityShortName
        substation
        pvVoltageRaw
        pvVoltageKv
        distanceMiles
        netAvailableKw
        shapeGeojson
      }
    }
  }
}
    `)

export const useAdminSiteNearbyFeedersQuery = <
  TData = Types.AdminSiteNearbyFeedersQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteNearbyFeedersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSiteNearbyFeedersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteNearbyFeedersQuery, TError, TData>({
    queryKey: ["AdminSiteNearbyFeeders", variables],
    queryFn: publicFetcher<
      Types.AdminSiteNearbyFeedersQuery,
      Types.AdminSiteNearbyFeedersQueryVariables
    >(AdminSiteNearbyFeedersDocument, variables),
    ...options,
  })
}

useAdminSiteNearbyFeedersQuery.getKey = (
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
) => ["AdminSiteNearbyFeeders", variables]

useAdminSiteNearbyFeedersQuery.rootKey = "AdminSiteNearbyFeeders"

export const useSuspenseAdminSiteNearbyFeedersQuery = <
  TData = Types.AdminSiteNearbyFeedersQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteNearbyFeedersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteNearbyFeedersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteNearbyFeedersQuery, TError, TData>({
    queryKey: ["AdminSiteNearbyFeeders", variables],
    queryFn: publicFetcher<
      Types.AdminSiteNearbyFeedersQuery,
      Types.AdminSiteNearbyFeedersQueryVariables
    >(AdminSiteNearbyFeedersDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteNearbyFeedersQuery.getKey = (
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
) => ["AdminSiteNearbyFeeders", variables]

useSuspenseAdminSiteNearbyFeedersQuery.rootKey = "AdminSiteNearbyFeeders"

export const useInfiniteAdminSiteNearbyFeedersQuery = <
  TData = InfiniteData<Types.AdminSiteNearbyFeedersQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteNearbyFeedersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteNearbyFeedersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteNearbyFeedersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteNearbyFeeders.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteNearbyFeedersQuery,
            Types.AdminSiteNearbyFeedersQueryVariables
          >(AdminSiteNearbyFeedersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteNearbyFeedersQuery.getKey = (
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
) => ["AdminSiteNearbyFeeders.infinite", variables]

useInfiniteAdminSiteNearbyFeedersQuery.rootKey = "AdminSiteNearbyFeeders.infinite"

export const useSuspenseInfiniteAdminSiteNearbyFeedersQuery = <
  TData = InfiniteData<Types.AdminSiteNearbyFeedersQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteNearbyFeedersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteNearbyFeedersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteNearbyFeedersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteNearbyFeeders.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteNearbyFeedersQuery,
            Types.AdminSiteNearbyFeedersQueryVariables
          >(AdminSiteNearbyFeedersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteNearbyFeedersQuery.getKey = (
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
) => ["AdminSiteNearbyFeeders.infinite", variables]

useSuspenseInfiniteAdminSiteNearbyFeedersQuery.rootKey =
  "AdminSiteNearbyFeeders.infinite"

useAdminSiteNearbyFeedersQuery.fetcher = (
  variables: Types.AdminSiteNearbyFeedersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSiteNearbyFeedersQuery,
    Types.AdminSiteNearbyFeedersQueryVariables
  >(AdminSiteNearbyFeedersDocument, variables, options)

export const FinmodelFailureDetailDocument = new TypedDocumentString(`
    query FinmodelFailureDetail($id: Int!) {
  finmodelFailures {
    finmodelFailure(id: $id) {
      ...FinmodelFailureDetail
      finmodel {
        id
        fmvStr
      }
    }
  }
}
    fragment FinmodelFailureDetail on FinmodelFailure {
  id
  createdAt
  fmv
  name
  message
  isPvopt
  isLux
  site {
    ...SiteRef
  }
  scenario {
    ...ScenarioRef
  }
  source {
    ...FinmodelSourceFragment
  }
}
fragment FinmodelSourceFragment on FinmodelSource {
  __typename
  ... on PVoptRef {
    id
  }
  ... on LuxRef {
    id
  }
  ... on ReoptRef {
    pid
  }
  ... on ETBReportRef {
    id
    projectName
  }
}
fragment SiteRef on SiteRef {
  pid
  name
  streetAddress
}
fragment ScenarioRef on ScenarioRef {
  pid
  name
}`)

export const useFinmodelFailureDetailQuery = <
  TData = Types.FinmodelFailureDetailQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureDetailQuery, TError, TData>({
    queryKey: ["FinmodelFailureDetail", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureDetailQuery,
      Types.FinmodelFailureDetailQueryVariables
    >(FinmodelFailureDetailDocument, variables),
    ...options,
  })
}

useFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail", variables]

useFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail"

export const useSuspenseFinmodelFailureDetailQuery = <
  TData = Types.FinmodelFailureDetailQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureDetailQuery, TError, TData>({
    queryKey: ["FinmodelFailureDetail", variables],
    queryFn: publicFetcher<
      Types.FinmodelFailureDetailQuery,
      Types.FinmodelFailureDetailQueryVariables
    >(FinmodelFailureDetailDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail", variables]

useSuspenseFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail"

export const useInfiniteFinmodelFailureDetailQuery = <
  TData = InfiniteData<Types.FinmodelFailureDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureDetailQuery,
            Types.FinmodelFailureDetailQueryVariables
          >(FinmodelFailureDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail.infinite", variables]

useInfiniteFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail.infinite"

export const useSuspenseInfiniteFinmodelFailureDetailQuery = <
  TData = InfiniteData<Types.FinmodelFailureDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelFailureDetailQuery,
            Types.FinmodelFailureDetailQueryVariables
          >(FinmodelFailureDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail.infinite", variables]

useSuspenseInfiniteFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail.infinite"

useFinmodelFailureDetailQuery.fetcher = (
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinmodelFailureDetailQuery,
    Types.FinmodelFailureDetailQueryVariables
  >(FinmodelFailureDetailDocument, variables, options)

export const AdminMultiMeterDetailDocument = new TypedDocumentString(`
    query AdminMultiMeterDetail($id: Int!) {
  adminMeters {
    bauMeter(id: $id) {
      id
      formattedBill {
        ...FormattedBill
      }
      formattedPostBill {
        ...FormattedBill
      }
      tariff {
        name
        code
      }
      tariffUsdPerKwh
      postTariff {
        name
        code
      }
    }
  }
}
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...BillCostItem
  }
}
fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}
fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}`)

export const useAdminMultiMeterDetailQuery = <
  TData = Types.AdminMultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminMultiMeterDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMultiMeterDetailQuery, TError, TData>({
    queryKey: ["AdminMultiMeterDetail", variables],
    queryFn: publicFetcher<
      Types.AdminMultiMeterDetailQuery,
      Types.AdminMultiMeterDetailQueryVariables
    >(AdminMultiMeterDetailDocument, variables),
    ...options,
  })
}

useAdminMultiMeterDetailQuery.getKey = (
  variables: Types.AdminMultiMeterDetailQueryVariables,
) => ["AdminMultiMeterDetail", variables]

useAdminMultiMeterDetailQuery.rootKey = "AdminMultiMeterDetail"

export const useSuspenseAdminMultiMeterDetailQuery = <
  TData = Types.AdminMultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminMultiMeterDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMultiMeterDetailQuery, TError, TData>({
    queryKey: ["AdminMultiMeterDetail", variables],
    queryFn: publicFetcher<
      Types.AdminMultiMeterDetailQuery,
      Types.AdminMultiMeterDetailQueryVariables
    >(AdminMultiMeterDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminMultiMeterDetailQuery.getKey = (
  variables: Types.AdminMultiMeterDetailQueryVariables,
) => ["AdminMultiMeterDetail", variables]

useSuspenseAdminMultiMeterDetailQuery.rootKey = "AdminMultiMeterDetail"

export const useInfiniteAdminMultiMeterDetailQuery = <
  TData = InfiniteData<Types.AdminMultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminMultiMeterDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminMultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMultiMeterDetailQuery,
            Types.AdminMultiMeterDetailQueryVariables
          >(AdminMultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMultiMeterDetailQuery.getKey = (
  variables: Types.AdminMultiMeterDetailQueryVariables,
) => ["AdminMultiMeterDetail.infinite", variables]

useInfiniteAdminMultiMeterDetailQuery.rootKey = "AdminMultiMeterDetail.infinite"

export const useSuspenseInfiniteAdminMultiMeterDetailQuery = <
  TData = InfiniteData<Types.AdminMultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminMultiMeterDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminMultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMultiMeterDetailQuery,
            Types.AdminMultiMeterDetailQueryVariables
          >(AdminMultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMultiMeterDetailQuery.getKey = (
  variables: Types.AdminMultiMeterDetailQueryVariables,
) => ["AdminMultiMeterDetail.infinite", variables]

useSuspenseInfiniteAdminMultiMeterDetailQuery.rootKey = "AdminMultiMeterDetail.infinite"

useAdminMultiMeterDetailQuery.fetcher = (
  variables: Types.AdminMultiMeterDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMultiMeterDetailQuery,
    Types.AdminMultiMeterDetailQueryVariables
  >(AdminMultiMeterDetailDocument, variables, options)

export const BauMeterNameThatTariffDetailDocument = new TypedDocumentString(`
    query BauMeterNameThatTariffDetail($id: Int!, $tariffId: Int!, $documentBillingMonthId: Int!) {
  adminMeters {
    bauMeter(id: $id) {
      nameThatTariffDetail(
        tariffId: $tariffId
        documentBillingMonthId: $documentBillingMonthId
      ) {
        tariff {
          id
          masterTariffId
          code
          name
        }
        result {
          ...NameThatTariffResult
        }
        groups {
          group
          items {
            cost
            usage
            name
            rate
          }
        }
      }
    }
  }
}
    fragment NameThatTariffResult on NameThatTariffResult {
  notTheCorrectRateVersion
  rate {
    startDate
    endDate
    tariffId
  }
  expected {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  actual {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  delta {
    usd {
      ...Delta
    }
    kwh {
      ...Delta
    }
    kw {
      ...Delta
    }
  }
  document {
    pid
    contentType
    viewHref
  }
}
fragment Delta on Delta {
  a
  b
  diff
  pct
}`)

export const useBauMeterNameThatTariffDetailQuery = <
  TData = Types.BauMeterNameThatTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>({
    queryKey: ["BauMeterNameThatTariffDetail", variables],
    queryFn: publicFetcher<
      Types.BauMeterNameThatTariffDetailQuery,
      Types.BauMeterNameThatTariffDetailQueryVariables
    >(BauMeterNameThatTariffDetailDocument, variables),
    ...options,
  })
}

useBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail", variables]

useBauMeterNameThatTariffDetailQuery.rootKey = "BauMeterNameThatTariffDetail"

export const useSuspenseBauMeterNameThatTariffDetailQuery = <
  TData = Types.BauMeterNameThatTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>({
    queryKey: ["BauMeterNameThatTariffDetail", variables],
    queryFn: publicFetcher<
      Types.BauMeterNameThatTariffDetailQuery,
      Types.BauMeterNameThatTariffDetailQueryVariables
    >(BauMeterNameThatTariffDetailDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail", variables]

useSuspenseBauMeterNameThatTariffDetailQuery.rootKey = "BauMeterNameThatTariffDetail"

export const useInfiniteBauMeterNameThatTariffDetailQuery = <
  TData = InfiniteData<Types.BauMeterNameThatTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterNameThatTariffDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterNameThatTariffDetailQuery,
            Types.BauMeterNameThatTariffDetailQueryVariables
          >(BauMeterNameThatTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail.infinite", variables]

useInfiniteBauMeterNameThatTariffDetailQuery.rootKey =
  "BauMeterNameThatTariffDetail.infinite"

export const useSuspenseInfiniteBauMeterNameThatTariffDetailQuery = <
  TData = InfiniteData<Types.BauMeterNameThatTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.BauMeterNameThatTariffDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterNameThatTariffDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterNameThatTariffDetailQuery,
            Types.BauMeterNameThatTariffDetailQueryVariables
          >(BauMeterNameThatTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail.infinite", variables]

useSuspenseInfiniteBauMeterNameThatTariffDetailQuery.rootKey =
  "BauMeterNameThatTariffDetail.infinite"

useBauMeterNameThatTariffDetailQuery.fetcher = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BauMeterNameThatTariffDetailQuery,
    Types.BauMeterNameThatTariffDetailQueryVariables
  >(BauMeterNameThatTariffDetailDocument, variables, options)

export const Xl2RenderDocument = new TypedDocumentString(`
    query Xl2Render($flavor: XlFlavor) {
  xl2 {
    xl2(flavor: $flavor)
  }
}
    `)

export const useXl2RenderQuery = <TData = Types.Xl2RenderQuery, TError = unknown>(
  variables?: Types.Xl2RenderQueryVariables,
  options?: Omit<UseQueryOptions<Types.Xl2RenderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.Xl2RenderQuery, TError, TData>({
    queryKey: variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables],
    queryFn: publicFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
      Xl2RenderDocument,
      variables,
    ),
    ...options,
  })
}

useXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables]

useXl2RenderQuery.rootKey = "Xl2Render"

export const useSuspenseXl2RenderQuery = <
  TData = Types.Xl2RenderQuery,
  TError = unknown,
>(
  variables?: Types.Xl2RenderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.Xl2RenderQuery, TError, TData>({
    queryKey: variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables],
    queryFn: publicFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
      Xl2RenderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables]

useSuspenseXl2RenderQuery.rootKey = "Xl2Render"

export const useInfiniteXl2RenderQuery = <
  TData = InfiniteData<Types.Xl2RenderQuery>,
  TError = unknown,
>(
  variables: Types.Xl2RenderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.Xl2RenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Render.infinite"]
            : ["Xl2Render.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
            Xl2RenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2Render.infinite"] : ["Xl2Render.infinite", variables]

useInfiniteXl2RenderQuery.rootKey = "Xl2Render.infinite"

export const useSuspenseInfiniteXl2RenderQuery = <
  TData = InfiniteData<Types.Xl2RenderQuery>,
  TError = unknown,
>(
  variables: Types.Xl2RenderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.Xl2RenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.Xl2RenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Render.infinite"]
            : ["Xl2Render.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
            Xl2RenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteXl2RenderQuery.getKey = (
  variables?: Types.Xl2RenderQueryVariables,
) =>
  variables === undefined ? ["Xl2Render.infinite"] : ["Xl2Render.infinite", variables]

useSuspenseInfiniteXl2RenderQuery.rootKey = "Xl2Render.infinite"

useXl2RenderQuery.fetcher = (
  variables?: Types.Xl2RenderQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
    Xl2RenderDocument,
    variables,
    options,
  )

export const Xl2FlavorsDocument = new TypedDocumentString(`
    query Xl2Flavors {
  xl2 {
    xl2Flavors
  }
}
    `)

export const useXl2FlavorsQuery = <TData = Types.Xl2FlavorsQuery, TError = unknown>(
  variables?: Types.Xl2FlavorsQueryVariables,
  options?: Omit<UseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.Xl2FlavorsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Xl2Flavors"] : ["Xl2Flavors", variables],
    queryFn: publicFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
      Xl2FlavorsDocument,
      variables,
    ),
    ...options,
  })
}

useXl2FlavorsQuery.getKey = (variables?: Types.Xl2FlavorsQueryVariables) =>
  variables === undefined ? ["Xl2Flavors"] : ["Xl2Flavors", variables]

useXl2FlavorsQuery.rootKey = "Xl2Flavors"

export const useSuspenseXl2FlavorsQuery = <
  TData = Types.Xl2FlavorsQuery,
  TError = unknown,
>(
  variables?: Types.Xl2FlavorsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.Xl2FlavorsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Xl2Flavors"] : ["Xl2Flavors", variables],
    queryFn: publicFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
      Xl2FlavorsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseXl2FlavorsQuery.getKey = (variables?: Types.Xl2FlavorsQueryVariables) =>
  variables === undefined ? ["Xl2Flavors"] : ["Xl2Flavors", variables]

useSuspenseXl2FlavorsQuery.rootKey = "Xl2Flavors"

export const useInfiniteXl2FlavorsQuery = <
  TData = InfiniteData<Types.Xl2FlavorsQuery>,
  TError = unknown,
>(
  variables: Types.Xl2FlavorsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.Xl2FlavorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.Xl2FlavorsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.Xl2FlavorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Flavors.infinite"]
            : ["Xl2Flavors.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
            Xl2FlavorsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteXl2FlavorsQuery.getKey = (variables?: Types.Xl2FlavorsQueryVariables) =>
  variables === undefined ? ["Xl2Flavors.infinite"] : ["Xl2Flavors.infinite", variables]

useInfiniteXl2FlavorsQuery.rootKey = "Xl2Flavors.infinite"

export const useSuspenseInfiniteXl2FlavorsQuery = <
  TData = InfiniteData<Types.Xl2FlavorsQuery>,
  TError = unknown,
>(
  variables: Types.Xl2FlavorsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.Xl2FlavorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.Xl2FlavorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.Xl2FlavorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Flavors.infinite"]
            : ["Xl2Flavors.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
            Xl2FlavorsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteXl2FlavorsQuery.getKey = (
  variables?: Types.Xl2FlavorsQueryVariables,
) =>
  variables === undefined ? ["Xl2Flavors.infinite"] : ["Xl2Flavors.infinite", variables]

useSuspenseInfiniteXl2FlavorsQuery.rootKey = "Xl2Flavors.infinite"

useXl2FlavorsQuery.fetcher = (
  variables?: Types.Xl2FlavorsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
    Xl2FlavorsDocument,
    variables,
    options,
  )

export const BauMeterBillComparisonDocument = new TypedDocumentString(`
    query BauMeterBillComparison($sitePid: String!, $meterId: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      customTariffs {
        id
        name
        code
        parent {
          id
        }
      }
    }
  }
  adminMeters {
    bauMeter(id: $meterId) {
      loadCategory
      tariff {
        id
        code
        name
      }
      documentBillingMonths {
        id
        start
        end
      }
    }
  }
}
    `)

export const useBauMeterBillComparisonQuery = <
  TData = Types.BauMeterBillComparisonQuery,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterBillComparisonQuery, TError, TData>({
    queryKey: ["BauMeterBillComparison", variables],
    queryFn: publicFetcher<
      Types.BauMeterBillComparisonQuery,
      Types.BauMeterBillComparisonQueryVariables
    >(BauMeterBillComparisonDocument, variables),
    ...options,
  })
}

useBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison", variables]

useBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison"

export const useSuspenseBauMeterBillComparisonQuery = <
  TData = Types.BauMeterBillComparisonQuery,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterBillComparisonQuery, TError, TData>({
    queryKey: ["BauMeterBillComparison", variables],
    queryFn: publicFetcher<
      Types.BauMeterBillComparisonQuery,
      Types.BauMeterBillComparisonQueryVariables
    >(BauMeterBillComparisonDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison", variables]

useSuspenseBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison"

export const useInfiniteBauMeterBillComparisonQuery = <
  TData = InfiniteData<Types.BauMeterBillComparisonQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterBillComparisonQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterBillComparison.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterBillComparisonQuery,
            Types.BauMeterBillComparisonQueryVariables
          >(BauMeterBillComparisonDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison.infinite", variables]

useInfiniteBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison.infinite"

export const useSuspenseInfiniteBauMeterBillComparisonQuery = <
  TData = InfiniteData<Types.BauMeterBillComparisonQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BauMeterBillComparisonQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterBillComparison.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterBillComparisonQuery,
            Types.BauMeterBillComparisonQueryVariables
          >(BauMeterBillComparisonDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison.infinite", variables]

useSuspenseInfiniteBauMeterBillComparisonQuery.rootKey =
  "BauMeterBillComparison.infinite"

useBauMeterBillComparisonQuery.fetcher = (
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BauMeterBillComparisonQuery,
    Types.BauMeterBillComparisonQueryVariables
  >(BauMeterBillComparisonDocument, variables, options)

export const AdminDocumentDocument = new TypedDocumentString(`
    query AdminDocument($pid: String!) {
  admin {
    document(pid: $pid) {
      ...Document
      signedViewUrl
      isAnalyzable
      csvPeek
      latestAnalysisRequest {
        ...DocumentAnalysisRequest
      }
      aiExtractionResults {
        ...AIExtractionResult
      }
      aiConversions {
        ...AIConversion
      }
    }
  }
}
    fragment AIConversion on AIConversion {
  id
  conversionKind
  modelName
  dateCreated
  inputDocument {
    pid
    fileName
    downloadHref
  }
  outputDocument {
    pid
    fileName
    downloadHref
  }
  conversation {
    id
    model
    exchanges {
      blocks {
        __typename
        ... on AIProseBlock {
          text
          reasoning
        }
        ... on AICodeExecution {
          toolName
          command
          path
          code
        }
      }
    }
  }
}
fragment AIExtractionResult on AIExtractionResult {
  id
  extractionKind
  modelName
  schemaVersion
  dateCreated
  analysis {
    name
    description
    features {
      name
      description
      dataFields {
        id
        label
        value
        type
        boundingBoxes {
          pageNumber
          left
          top
          right
          bottom
        }
      }
      boundingBoxes {
        pageNumber
        left
        top
        right
        bottom
      }
    }
  }
}
fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}
fragment DocumentAnalysisRequest on DocumentAnalysisRequest {
  id
  createdAt
  status
  note
  errorClass
  errorMessage
}`)

export const useAdminDocumentQuery = <
  TData = Types.AdminDocumentQuery,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminDocumentQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminDocumentQuery, TError, TData>({
    queryKey: ["AdminDocument", variables],
    queryFn: publicFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
      AdminDocumentDocument,
      variables,
    ),
    ...options,
  })
}

useAdminDocumentQuery.getKey = (variables: Types.AdminDocumentQueryVariables) => [
  "AdminDocument",
  variables,
]

useAdminDocumentQuery.rootKey = "AdminDocument"

export const useSuspenseAdminDocumentQuery = <
  TData = Types.AdminDocumentQuery,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminDocumentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminDocumentQuery, TError, TData>({
    queryKey: ["AdminDocument", variables],
    queryFn: publicFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
      AdminDocumentDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminDocumentQuery.getKey = (
  variables: Types.AdminDocumentQueryVariables,
) => ["AdminDocument", variables]

useSuspenseAdminDocumentQuery.rootKey = "AdminDocument"

export const useInfiniteAdminDocumentQuery = <
  TData = InfiniteData<Types.AdminDocumentQuery>,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminDocumentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminDocumentQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminDocument.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
            AdminDocumentDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminDocumentQuery.getKey = (
  variables: Types.AdminDocumentQueryVariables,
) => ["AdminDocument.infinite", variables]

useInfiniteAdminDocumentQuery.rootKey = "AdminDocument.infinite"

export const useSuspenseInfiniteAdminDocumentQuery = <
  TData = InfiniteData<Types.AdminDocumentQuery>,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminDocumentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminDocumentQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminDocument.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
            AdminDocumentDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminDocumentQuery.getKey = (
  variables: Types.AdminDocumentQueryVariables,
) => ["AdminDocument.infinite", variables]

useSuspenseInfiniteAdminDocumentQuery.rootKey = "AdminDocument.infinite"

useAdminDocumentQuery.fetcher = (
  variables: Types.AdminDocumentQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
    AdminDocumentDocument,
    variables,
    options,
  )

export const AdminSiteBillingMetersDocument = new TypedDocumentString(`
    query AdminSiteBillingMeters($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      billingMeters {
        id
        pid
        label
        createdAt
        archivedAt
        archivedReason
        archivedBy {
          pid
          email
        }
        disabledAt
        disabledReason
        disabledBy {
          pid
          email
        }
        utilityMeterId
        supplyUtilityMeterId
        config {
          bauMeter {
            id
          }
        }
        view {
          name
          isArchived
          isDisabled
          loadCategory
          buildingType
          totalAnnualKwh {
            number
            source
          }
          peakAnnualKw {
            number
            source
          }
          numBills
          numIntervals
          utility {
            pid
            name
            shortName
          }
          tariff {
            id
            pid
            name
            code
          }
        }
      }
    }
  }
}
    `)

export const useAdminSiteBillingMetersQuery = <
  TData = Types.AdminSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteBillingMetersQuery, TError, TData>({
    queryKey: ["AdminSiteBillingMeters", variables],
    queryFn: publicFetcher<
      Types.AdminSiteBillingMetersQuery,
      Types.AdminSiteBillingMetersQueryVariables
    >(AdminSiteBillingMetersDocument, variables),
    ...options,
  })
}

useAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMeters", variables]

useAdminSiteBillingMetersQuery.rootKey = "AdminSiteBillingMeters"

export const useSuspenseAdminSiteBillingMetersQuery = <
  TData = Types.AdminSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteBillingMetersQuery, TError, TData>({
    queryKey: ["AdminSiteBillingMeters", variables],
    queryFn: publicFetcher<
      Types.AdminSiteBillingMetersQuery,
      Types.AdminSiteBillingMetersQueryVariables
    >(AdminSiteBillingMetersDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMeters", variables]

useSuspenseAdminSiteBillingMetersQuery.rootKey = "AdminSiteBillingMeters"

export const useInfiniteAdminSiteBillingMetersQuery = <
  TData = InfiniteData<Types.AdminSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteBillingMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteBillingMetersQuery,
            Types.AdminSiteBillingMetersQueryVariables
          >(AdminSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMeters.infinite", variables]

useInfiniteAdminSiteBillingMetersQuery.rootKey = "AdminSiteBillingMeters.infinite"

export const useSuspenseInfiniteAdminSiteBillingMetersQuery = <
  TData = InfiniteData<Types.AdminSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteBillingMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteBillingMetersQuery,
            Types.AdminSiteBillingMetersQueryVariables
          >(AdminSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMeters.infinite", variables]

useSuspenseInfiniteAdminSiteBillingMetersQuery.rootKey =
  "AdminSiteBillingMeters.infinite"

useAdminSiteBillingMetersQuery.fetcher = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSiteBillingMetersQuery,
    Types.AdminSiteBillingMetersQueryVariables
  >(AdminSiteBillingMetersDocument, variables, options)

export const SolarDetectionResultsDocument = new TypedDocumentString(`
    query SolarDetectionResults($sort: SolarDetectionSort!, $labelFilter: SolarDetectionLabelFilter!, $minScore: Int, $maxScore: Int, $orgPid: String, $limit: Int!, $offset: Int!) {
  admin {
    solarDetectionResults(
      sort: $sort
      labelFilter: $labelFilter
      minScore: $minScore
      maxScore: $maxScore
      orgPid: $orgPid
      limit: $limit
      offset: $offset
    ) {
      total
      results {
        id
        createdAt
        score
        explanation
        sitePid
        siteAddress
        buildingPid
        orgPid
        orgName
        portfolioPid
        portfolioName
        images {
          id
          zoom
          url
        }
        humanLabel {
          id
          hasSolar
          source
          createdAt
          userEmail
        }
      }
    }
  }
}
    `)

export const useSolarDetectionResultsQuery = <
  TData = Types.SolarDetectionResultsQuery,
  TError = unknown,
>(
  variables: Types.SolarDetectionResultsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SolarDetectionResultsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SolarDetectionResultsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SolarDetectionResultsQuery, TError, TData>({
    queryKey: ["SolarDetectionResults", variables],
    queryFn: publicFetcher<
      Types.SolarDetectionResultsQuery,
      Types.SolarDetectionResultsQueryVariables
    >(SolarDetectionResultsDocument, variables),
    ...options,
  })
}

useSolarDetectionResultsQuery.getKey = (
  variables: Types.SolarDetectionResultsQueryVariables,
) => ["SolarDetectionResults", variables]

useSolarDetectionResultsQuery.rootKey = "SolarDetectionResults"

export const useSuspenseSolarDetectionResultsQuery = <
  TData = Types.SolarDetectionResultsQuery,
  TError = unknown,
>(
  variables: Types.SolarDetectionResultsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SolarDetectionResultsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SolarDetectionResultsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SolarDetectionResultsQuery, TError, TData>({
    queryKey: ["SolarDetectionResults", variables],
    queryFn: publicFetcher<
      Types.SolarDetectionResultsQuery,
      Types.SolarDetectionResultsQueryVariables
    >(SolarDetectionResultsDocument, variables),
    ...options,
  })
}

useSuspenseSolarDetectionResultsQuery.getKey = (
  variables: Types.SolarDetectionResultsQueryVariables,
) => ["SolarDetectionResults", variables]

useSuspenseSolarDetectionResultsQuery.rootKey = "SolarDetectionResults"

export const useInfiniteSolarDetectionResultsQuery = <
  TData = InfiniteData<Types.SolarDetectionResultsQuery>,
  TError = unknown,
>(
  variables: Types.SolarDetectionResultsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SolarDetectionResultsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SolarDetectionResultsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SolarDetectionResultsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarDetectionResults.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SolarDetectionResultsQuery,
            Types.SolarDetectionResultsQueryVariables
          >(SolarDetectionResultsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSolarDetectionResultsQuery.getKey = (
  variables: Types.SolarDetectionResultsQueryVariables,
) => ["SolarDetectionResults.infinite", variables]

useInfiniteSolarDetectionResultsQuery.rootKey = "SolarDetectionResults.infinite"

export const useSuspenseInfiniteSolarDetectionResultsQuery = <
  TData = InfiniteData<Types.SolarDetectionResultsQuery>,
  TError = unknown,
>(
  variables: Types.SolarDetectionResultsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SolarDetectionResultsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SolarDetectionResultsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SolarDetectionResultsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarDetectionResults.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SolarDetectionResultsQuery,
            Types.SolarDetectionResultsQueryVariables
          >(SolarDetectionResultsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSolarDetectionResultsQuery.getKey = (
  variables: Types.SolarDetectionResultsQueryVariables,
) => ["SolarDetectionResults.infinite", variables]

useSuspenseInfiniteSolarDetectionResultsQuery.rootKey = "SolarDetectionResults.infinite"

useSolarDetectionResultsQuery.fetcher = (
  variables: Types.SolarDetectionResultsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SolarDetectionResultsQuery,
    Types.SolarDetectionResultsQueryVariables
  >(SolarDetectionResultsDocument, variables, options)

export const SiteSolarDetectionDocument = new TypedDocumentString(`
    query SiteSolarDetection($sitePid: String!) {
  admin {
    solarDetectionResults(sitePid: $sitePid, limit: 1) {
      results {
        id
        createdAt
        score
        explanation
        sitePid
        siteAddress
        buildingPid
        images {
          id
          zoom
          url
        }
        humanLabel {
          id
          hasSolar
          source
          createdAt
          userEmail
        }
      }
    }
  }
}
    `)

export const useSiteSolarDetectionQuery = <
  TData = Types.SiteSolarDetectionQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarDetectionQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteSolarDetectionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteSolarDetectionQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteSolarDetectionQuery, TError, TData>({
    queryKey: ["SiteSolarDetection", variables],
    queryFn: publicFetcher<
      Types.SiteSolarDetectionQuery,
      Types.SiteSolarDetectionQueryVariables
    >(SiteSolarDetectionDocument, variables),
    ...options,
  })
}

useSiteSolarDetectionQuery.getKey = (
  variables: Types.SiteSolarDetectionQueryVariables,
) => ["SiteSolarDetection", variables]

useSiteSolarDetectionQuery.rootKey = "SiteSolarDetection"

export const useSuspenseSiteSolarDetectionQuery = <
  TData = Types.SiteSolarDetectionQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarDetectionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteSolarDetectionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteSolarDetectionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteSolarDetectionQuery, TError, TData>({
    queryKey: ["SiteSolarDetection", variables],
    queryFn: publicFetcher<
      Types.SiteSolarDetectionQuery,
      Types.SiteSolarDetectionQueryVariables
    >(SiteSolarDetectionDocument, variables),
    ...options,
  })
}

useSuspenseSiteSolarDetectionQuery.getKey = (
  variables: Types.SiteSolarDetectionQueryVariables,
) => ["SiteSolarDetection", variables]

useSuspenseSiteSolarDetectionQuery.rootKey = "SiteSolarDetection"

export const useInfiniteSiteSolarDetectionQuery = <
  TData = InfiniteData<Types.SiteSolarDetectionQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarDetectionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteSolarDetectionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteSolarDetectionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteSolarDetectionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteSolarDetection.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarDetectionQuery,
            Types.SiteSolarDetectionQueryVariables
          >(SiteSolarDetectionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteSolarDetectionQuery.getKey = (
  variables: Types.SiteSolarDetectionQueryVariables,
) => ["SiteSolarDetection.infinite", variables]

useInfiniteSiteSolarDetectionQuery.rootKey = "SiteSolarDetection.infinite"

export const useSuspenseInfiniteSiteSolarDetectionQuery = <
  TData = InfiniteData<Types.SiteSolarDetectionQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarDetectionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteSolarDetectionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteSolarDetectionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteSolarDetectionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteSolarDetection.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarDetectionQuery,
            Types.SiteSolarDetectionQueryVariables
          >(SiteSolarDetectionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteSolarDetectionQuery.getKey = (
  variables: Types.SiteSolarDetectionQueryVariables,
) => ["SiteSolarDetection.infinite", variables]

useSuspenseInfiniteSiteSolarDetectionQuery.rootKey = "SiteSolarDetection.infinite"

useSiteSolarDetectionQuery.fetcher = (
  variables: Types.SiteSolarDetectionQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteSolarDetectionQuery, Types.SiteSolarDetectionQueryVariables>(
    SiteSolarDetectionDocument,
    variables,
    options,
  )

export const AdminProspectingSegmentsDocument = new TypedDocumentString(`
    query AdminProspectingSegments($filters: ProspectingFilter!) {
  admin {
    interconnectionSegments {
      prospectingSegments(filters: $filters) {
        pk
        feeder
        segmentId
        substation
        utilityShortName
        pvVoltageKv
        pvVoltageRaw
        pvHostingCapacityKw
        netPvHostingCapacityKw
        storageChargeCapacityKw
        storageDischargeCapacityKw
        shapeGeojson
        pvFeederInstalledCapacityKw
        pvFeederQueuedCapacityKw
        pvSubstationHostingCapacityKw
        pvSubstationQueuedCapacityKw
        pvSubstationQueuedApplicationsCount
        pvMinHostingCapacityKw
        pvMaxHostingCapacityKw
        storageVoltage
        storageMinChargeKw
        storageMaxChargeKw
        storageMinDischargeKw
        storageMaxDischargeKw
        centroidLatitude
        centroidLongitude
      }
    }
  }
}
    `)

export const useAdminProspectingSegmentsQuery = <
  TData = Types.AdminProspectingSegmentsQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingSegmentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProspectingSegmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProspectingSegmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProspectingSegmentsQuery, TError, TData>({
    queryKey: ["AdminProspectingSegments", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingSegmentsQuery,
      Types.AdminProspectingSegmentsQueryVariables
    >(AdminProspectingSegmentsDocument, variables),
    ...options,
  })
}

useAdminProspectingSegmentsQuery.getKey = (
  variables: Types.AdminProspectingSegmentsQueryVariables,
) => ["AdminProspectingSegments", variables]

useAdminProspectingSegmentsQuery.rootKey = "AdminProspectingSegments"

export const useSuspenseAdminProspectingSegmentsQuery = <
  TData = Types.AdminProspectingSegmentsQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingSegmentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProspectingSegmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProspectingSegmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProspectingSegmentsQuery, TError, TData>({
    queryKey: ["AdminProspectingSegments", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingSegmentsQuery,
      Types.AdminProspectingSegmentsQueryVariables
    >(AdminProspectingSegmentsDocument, variables),
    ...options,
  })
}

useSuspenseAdminProspectingSegmentsQuery.getKey = (
  variables: Types.AdminProspectingSegmentsQueryVariables,
) => ["AdminProspectingSegments", variables]

useSuspenseAdminProspectingSegmentsQuery.rootKey = "AdminProspectingSegments"

export const useInfiniteAdminProspectingSegmentsQuery = <
  TData = InfiniteData<Types.AdminProspectingSegmentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingSegmentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProspectingSegmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProspectingSegmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProspectingSegmentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminProspectingSegments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingSegmentsQuery,
            Types.AdminProspectingSegmentsQueryVariables
          >(AdminProspectingSegmentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProspectingSegmentsQuery.getKey = (
  variables: Types.AdminProspectingSegmentsQueryVariables,
) => ["AdminProspectingSegments.infinite", variables]

useInfiniteAdminProspectingSegmentsQuery.rootKey = "AdminProspectingSegments.infinite"

export const useSuspenseInfiniteAdminProspectingSegmentsQuery = <
  TData = InfiniteData<Types.AdminProspectingSegmentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingSegmentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminProspectingSegmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingSegmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminProspectingSegmentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminProspectingSegments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingSegmentsQuery,
            Types.AdminProspectingSegmentsQueryVariables
          >(AdminProspectingSegmentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProspectingSegmentsQuery.getKey = (
  variables: Types.AdminProspectingSegmentsQueryVariables,
) => ["AdminProspectingSegments.infinite", variables]

useSuspenseInfiniteAdminProspectingSegmentsQuery.rootKey =
  "AdminProspectingSegments.infinite"

useAdminProspectingSegmentsQuery.fetcher = (
  variables: Types.AdminProspectingSegmentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProspectingSegmentsQuery,
    Types.AdminProspectingSegmentsQueryVariables
  >(AdminProspectingSegmentsDocument, variables, options)

export const AdminProspectingBuildingsViewDocument = new TypedDocumentString(`
    query AdminProspectingBuildingsView($filters: ProspectingFilter!, $filterBy: ProspectingBuildingsTableFilter, $sortBy: [ProspectingBuildingsTableSort!]) {
  admin {
    interconnectionSegments {
      prospectingBuildings(filters: $filters) {
        view(filterBy: $filterBy, sortBy: $sortBy) {
          keys
          count
          stats {
            orgNames
            portfolioNames
            ownerNames
          }
        }
      }
    }
  }
}
    `)

export const useAdminProspectingBuildingsViewQuery = <
  TData = Types.AdminProspectingBuildingsViewQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProspectingBuildingsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProspectingBuildingsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProspectingBuildingsViewQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingsView", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingsViewQuery,
      Types.AdminProspectingBuildingsViewQueryVariables
    >(AdminProspectingBuildingsViewDocument, variables),
    ...options,
  })
}

useAdminProspectingBuildingsViewQuery.getKey = (
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
) => ["AdminProspectingBuildingsView", variables]

useAdminProspectingBuildingsViewQuery.rootKey = "AdminProspectingBuildingsView"

export const useSuspenseAdminProspectingBuildingsViewQuery = <
  TData = Types.AdminProspectingBuildingsViewQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProspectingBuildingsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProspectingBuildingsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProspectingBuildingsViewQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingsView", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingsViewQuery,
      Types.AdminProspectingBuildingsViewQueryVariables
    >(AdminProspectingBuildingsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminProspectingBuildingsViewQuery.getKey = (
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
) => ["AdminProspectingBuildingsView", variables]

useSuspenseAdminProspectingBuildingsViewQuery.rootKey = "AdminProspectingBuildingsView"

export const useInfiniteAdminProspectingBuildingsViewQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProspectingBuildingsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProspectingBuildingsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingsView.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingsViewQuery,
            Types.AdminProspectingBuildingsViewQueryVariables
          >(AdminProspectingBuildingsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProspectingBuildingsViewQuery.getKey = (
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
) => ["AdminProspectingBuildingsView.infinite", variables]

useInfiniteAdminProspectingBuildingsViewQuery.rootKey =
  "AdminProspectingBuildingsView.infinite"

export const useSuspenseInfiniteAdminProspectingBuildingsViewQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminProspectingBuildingsViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingsView.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingsViewQuery,
            Types.AdminProspectingBuildingsViewQueryVariables
          >(AdminProspectingBuildingsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProspectingBuildingsViewQuery.getKey = (
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
) => ["AdminProspectingBuildingsView.infinite", variables]

useSuspenseInfiniteAdminProspectingBuildingsViewQuery.rootKey =
  "AdminProspectingBuildingsView.infinite"

useAdminProspectingBuildingsViewQuery.fetcher = (
  variables: Types.AdminProspectingBuildingsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProspectingBuildingsViewQuery,
    Types.AdminProspectingBuildingsViewQueryVariables
  >(AdminProspectingBuildingsViewDocument, variables, options)

export const AdminProspectingBuildingsRowsDocument = new TypedDocumentString(`
    query AdminProspectingBuildingsRows($filters: ProspectingFilter!, $keys: [String!]!) {
  admin {
    interconnectionSegments {
      prospectingBuildings(filters: $filters) {
        rows(keys: $keys) {
          pid
          name
          streetAddress
          city
          state
          zipCode
          latitude
          longitude
          buildingType
          triageStatus
          utilityStatus
          groundSpaceCapacityStatus
          floodplainWetlandStatus
          workableFeedersStatus
          localMoratoriumStatus
          buildingSetbackStatus
          zoningSensitiveUseStatus
          orgName
          orgPid
          orgSlug
          portfolioName
          portfolioPid
          ownerName
          freeSpaceSqft
          distanceMiles
          nearestFeeder
          nearestSegmentPk
          qualifyingFeederCount
          footprintGeojson
          siteLabels {
            pid
            name
            themeColor
            isGlobal
          }
        }
      }
    }
  }
}
    `)

export const useAdminProspectingBuildingsRowsQuery = <
  TData = Types.AdminProspectingBuildingsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProspectingBuildingsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProspectingBuildingsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProspectingBuildingsRowsQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingsRows", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingsRowsQuery,
      Types.AdminProspectingBuildingsRowsQueryVariables
    >(AdminProspectingBuildingsRowsDocument, variables),
    ...options,
  })
}

useAdminProspectingBuildingsRowsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
) => ["AdminProspectingBuildingsRows", variables]

useAdminProspectingBuildingsRowsQuery.rootKey = "AdminProspectingBuildingsRows"

export const useSuspenseAdminProspectingBuildingsRowsQuery = <
  TData = Types.AdminProspectingBuildingsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProspectingBuildingsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProspectingBuildingsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProspectingBuildingsRowsQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingsRows", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingsRowsQuery,
      Types.AdminProspectingBuildingsRowsQueryVariables
    >(AdminProspectingBuildingsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminProspectingBuildingsRowsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
) => ["AdminProspectingBuildingsRows", variables]

useSuspenseAdminProspectingBuildingsRowsQuery.rootKey = "AdminProspectingBuildingsRows"

export const useInfiniteAdminProspectingBuildingsRowsQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProspectingBuildingsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProspectingBuildingsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingsRowsQuery,
            Types.AdminProspectingBuildingsRowsQueryVariables
          >(AdminProspectingBuildingsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProspectingBuildingsRowsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
) => ["AdminProspectingBuildingsRows.infinite", variables]

useInfiniteAdminProspectingBuildingsRowsQuery.rootKey =
  "AdminProspectingBuildingsRows.infinite"

export const useSuspenseInfiniteAdminProspectingBuildingsRowsQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminProspectingBuildingsRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingsRowsQuery,
            Types.AdminProspectingBuildingsRowsQueryVariables
          >(AdminProspectingBuildingsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProspectingBuildingsRowsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
) => ["AdminProspectingBuildingsRows.infinite", variables]

useSuspenseInfiniteAdminProspectingBuildingsRowsQuery.rootKey =
  "AdminProspectingBuildingsRows.infinite"

useAdminProspectingBuildingsRowsQuery.fetcher = (
  variables: Types.AdminProspectingBuildingsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProspectingBuildingsRowsQuery,
    Types.AdminProspectingBuildingsRowsQueryVariables
  >(AdminProspectingBuildingsRowsDocument, variables, options)

export const AdminProspectingSiteDetailDocument = new TypedDocumentString(`
    query AdminProspectingSiteDetail($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      propertyName
      streetAddress
      city
      state
      portfolio {
        pid
        title
        org {
          pid
          name
          slug
        }
      }
      labels {
        pid
        name
        themeColor
        isGlobal
      }
      ftmBessProspect {
        ...FtmBessProspect
      }
    }
  }
}
    fragment FtmBessProspect on FTMBessProspect {
  triageStatus
  verdict
  updatedAt
  triageStatusEdit {
    at
    by
  }
  criteria {
    key
    label
    order
    status
    notes
    contextualValue
    statusEdit {
      at
      by
    }
    notesEdit {
      at
      by
    }
  }
}`)

export const useAdminProspectingSiteDetailQuery = <
  TData = Types.AdminProspectingSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingSiteDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProspectingSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProspectingSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProspectingSiteDetailQuery, TError, TData>({
    queryKey: ["AdminProspectingSiteDetail", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingSiteDetailQuery,
      Types.AdminProspectingSiteDetailQueryVariables
    >(AdminProspectingSiteDetailDocument, variables),
    ...options,
  })
}

useAdminProspectingSiteDetailQuery.getKey = (
  variables: Types.AdminProspectingSiteDetailQueryVariables,
) => ["AdminProspectingSiteDetail", variables]

useAdminProspectingSiteDetailQuery.rootKey = "AdminProspectingSiteDetail"

export const useSuspenseAdminProspectingSiteDetailQuery = <
  TData = Types.AdminProspectingSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingSiteDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProspectingSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProspectingSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProspectingSiteDetailQuery, TError, TData>({
    queryKey: ["AdminProspectingSiteDetail", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingSiteDetailQuery,
      Types.AdminProspectingSiteDetailQueryVariables
    >(AdminProspectingSiteDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminProspectingSiteDetailQuery.getKey = (
  variables: Types.AdminProspectingSiteDetailQueryVariables,
) => ["AdminProspectingSiteDetail", variables]

useSuspenseAdminProspectingSiteDetailQuery.rootKey = "AdminProspectingSiteDetail"

export const useInfiniteAdminProspectingSiteDetailQuery = <
  TData = InfiniteData<Types.AdminProspectingSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingSiteDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProspectingSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProspectingSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProspectingSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminProspectingSiteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingSiteDetailQuery,
            Types.AdminProspectingSiteDetailQueryVariables
          >(AdminProspectingSiteDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProspectingSiteDetailQuery.getKey = (
  variables: Types.AdminProspectingSiteDetailQueryVariables,
) => ["AdminProspectingSiteDetail.infinite", variables]

useInfiniteAdminProspectingSiteDetailQuery.rootKey =
  "AdminProspectingSiteDetail.infinite"

export const useSuspenseInfiniteAdminProspectingSiteDetailQuery = <
  TData = InfiniteData<Types.AdminProspectingSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingSiteDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingSiteDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminProspectingSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminProspectingSiteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingSiteDetailQuery,
            Types.AdminProspectingSiteDetailQueryVariables
          >(AdminProspectingSiteDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProspectingSiteDetailQuery.getKey = (
  variables: Types.AdminProspectingSiteDetailQueryVariables,
) => ["AdminProspectingSiteDetail.infinite", variables]

useSuspenseInfiniteAdminProspectingSiteDetailQuery.rootKey =
  "AdminProspectingSiteDetail.infinite"

useAdminProspectingSiteDetailQuery.fetcher = (
  variables: Types.AdminProspectingSiteDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProspectingSiteDetailQuery,
    Types.AdminProspectingSiteDetailQueryVariables
  >(AdminProspectingSiteDetailDocument, variables, options)

export const AdminProspectingBuildingShapesDocument = new TypedDocumentString(`
    query AdminProspectingBuildingShapes($keys: [String!]!, $filters: ProspectingFilter!) {
  admin {
    interconnectionSegments {
      prospectingBuildingShapes(keys: $keys, filters: $filters) {
        pid
        orgPid
        orgName
        propertyName
        streetAddress
        city
        state
        portfolioPid
        portfolioTitle
        ownerName
        latitude
        longitude
        footprintGeojson
        nearestFeeder
        distanceMiles
      }
    }
  }
}
    `)

export const useAdminProspectingBuildingShapesQuery = <
  TData = Types.AdminProspectingBuildingShapesQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProspectingBuildingShapesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProspectingBuildingShapesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProspectingBuildingShapesQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingShapes", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingShapesQuery,
      Types.AdminProspectingBuildingShapesQueryVariables
    >(AdminProspectingBuildingShapesDocument, variables),
    ...options,
  })
}

useAdminProspectingBuildingShapesQuery.getKey = (
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
) => ["AdminProspectingBuildingShapes", variables]

useAdminProspectingBuildingShapesQuery.rootKey = "AdminProspectingBuildingShapes"

export const useSuspenseAdminProspectingBuildingShapesQuery = <
  TData = Types.AdminProspectingBuildingShapesQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProspectingBuildingShapesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProspectingBuildingShapesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProspectingBuildingShapesQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingShapes", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingShapesQuery,
      Types.AdminProspectingBuildingShapesQueryVariables
    >(AdminProspectingBuildingShapesDocument, variables),
    ...options,
  })
}

useSuspenseAdminProspectingBuildingShapesQuery.getKey = (
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
) => ["AdminProspectingBuildingShapes", variables]

useSuspenseAdminProspectingBuildingShapesQuery.rootKey =
  "AdminProspectingBuildingShapes"

export const useInfiniteAdminProspectingBuildingShapesQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingShapesQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProspectingBuildingShapesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProspectingBuildingShapesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProspectingBuildingShapesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingShapes.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingShapesQuery,
            Types.AdminProspectingBuildingShapesQueryVariables
          >(AdminProspectingBuildingShapesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProspectingBuildingShapesQuery.getKey = (
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
) => ["AdminProspectingBuildingShapes.infinite", variables]

useInfiniteAdminProspectingBuildingShapesQuery.rootKey =
  "AdminProspectingBuildingShapes.infinite"

export const useSuspenseInfiniteAdminProspectingBuildingShapesQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingShapesQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingShapesQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingShapesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminProspectingBuildingShapesQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingShapes.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingShapesQuery,
            Types.AdminProspectingBuildingShapesQueryVariables
          >(AdminProspectingBuildingShapesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProspectingBuildingShapesQuery.getKey = (
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
) => ["AdminProspectingBuildingShapes.infinite", variables]

useSuspenseInfiniteAdminProspectingBuildingShapesQuery.rootKey =
  "AdminProspectingBuildingShapes.infinite"

useAdminProspectingBuildingShapesQuery.fetcher = (
  variables: Types.AdminProspectingBuildingShapesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProspectingBuildingShapesQuery,
    Types.AdminProspectingBuildingShapesQueryVariables
  >(AdminProspectingBuildingShapesDocument, variables, options)

export const AdminProspectingUtilitiesDocument = new TypedDocumentString(`
    query AdminProspectingUtilities {
  admin {
    interconnectionSegments {
      prospectingUtilities {
        pid
        name
        shortName
      }
    }
  }
}
    `)

export const useAdminProspectingUtilitiesQuery = <
  TData = Types.AdminProspectingUtilitiesQuery,
  TError = unknown,
>(
  variables?: Types.AdminProspectingUtilitiesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProspectingUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProspectingUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProspectingUtilitiesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminProspectingUtilities"]
        : ["AdminProspectingUtilities", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingUtilitiesQuery,
      Types.AdminProspectingUtilitiesQueryVariables
    >(AdminProspectingUtilitiesDocument, variables),
    ...options,
  })
}

useAdminProspectingUtilitiesQuery.getKey = (
  variables?: Types.AdminProspectingUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["AdminProspectingUtilities"]
    : ["AdminProspectingUtilities", variables]

useAdminProspectingUtilitiesQuery.rootKey = "AdminProspectingUtilities"

export const useSuspenseAdminProspectingUtilitiesQuery = <
  TData = Types.AdminProspectingUtilitiesQuery,
  TError = unknown,
>(
  variables?: Types.AdminProspectingUtilitiesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProspectingUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProspectingUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProspectingUtilitiesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminProspectingUtilities"]
        : ["AdminProspectingUtilities", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingUtilitiesQuery,
      Types.AdminProspectingUtilitiesQueryVariables
    >(AdminProspectingUtilitiesDocument, variables),
    ...options,
  })
}

useSuspenseAdminProspectingUtilitiesQuery.getKey = (
  variables?: Types.AdminProspectingUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["AdminProspectingUtilities"]
    : ["AdminProspectingUtilities", variables]

useSuspenseAdminProspectingUtilitiesQuery.rootKey = "AdminProspectingUtilities"

export const useInfiniteAdminProspectingUtilitiesQuery = <
  TData = InfiniteData<Types.AdminProspectingUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingUtilitiesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProspectingUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProspectingUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProspectingUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminProspectingUtilities.infinite"]
            : ["AdminProspectingUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingUtilitiesQuery,
            Types.AdminProspectingUtilitiesQueryVariables
          >(AdminProspectingUtilitiesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProspectingUtilitiesQuery.getKey = (
  variables?: Types.AdminProspectingUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["AdminProspectingUtilities.infinite"]
    : ["AdminProspectingUtilities.infinite", variables]

useInfiniteAdminProspectingUtilitiesQuery.rootKey = "AdminProspectingUtilities.infinite"

export const useSuspenseInfiniteAdminProspectingUtilitiesQuery = <
  TData = InfiniteData<Types.AdminProspectingUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingUtilitiesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingUtilitiesQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminProspectingUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminProspectingUtilities.infinite"]
            : ["AdminProspectingUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingUtilitiesQuery,
            Types.AdminProspectingUtilitiesQueryVariables
          >(AdminProspectingUtilitiesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProspectingUtilitiesQuery.getKey = (
  variables?: Types.AdminProspectingUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["AdminProspectingUtilities.infinite"]
    : ["AdminProspectingUtilities.infinite", variables]

useSuspenseInfiniteAdminProspectingUtilitiesQuery.rootKey =
  "AdminProspectingUtilities.infinite"

useAdminProspectingUtilitiesQuery.fetcher = (
  variables?: Types.AdminProspectingUtilitiesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProspectingUtilitiesQuery,
    Types.AdminProspectingUtilitiesQueryVariables
  >(AdminProspectingUtilitiesDocument, variables, options)

export const AdminProspectingBuildingsFacetsDocument = new TypedDocumentString(`
    query AdminProspectingBuildingsFacets($filters: ProspectingFilter!) {
  admin {
    interconnectionSegments {
      prospectingBuildings(filters: $filters) {
        view {
          stats {
            orgNames
            portfolioNames
            ownerNames
            labelNames
          }
        }
      }
    }
  }
}
    `)

export const useAdminProspectingBuildingsFacetsQuery = <
  TData = Types.AdminProspectingBuildingsFacetsQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProspectingBuildingsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProspectingBuildingsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProspectingBuildingsFacetsQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingsFacets", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingsFacetsQuery,
      Types.AdminProspectingBuildingsFacetsQueryVariables
    >(AdminProspectingBuildingsFacetsDocument, variables),
    ...options,
  })
}

useAdminProspectingBuildingsFacetsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
) => ["AdminProspectingBuildingsFacets", variables]

useAdminProspectingBuildingsFacetsQuery.rootKey = "AdminProspectingBuildingsFacets"

export const useSuspenseAdminProspectingBuildingsFacetsQuery = <
  TData = Types.AdminProspectingBuildingsFacetsQuery,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProspectingBuildingsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProspectingBuildingsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProspectingBuildingsFacetsQuery, TError, TData>({
    queryKey: ["AdminProspectingBuildingsFacets", variables],
    queryFn: publicFetcher<
      Types.AdminProspectingBuildingsFacetsQuery,
      Types.AdminProspectingBuildingsFacetsQueryVariables
    >(AdminProspectingBuildingsFacetsDocument, variables),
    ...options,
  })
}

useSuspenseAdminProspectingBuildingsFacetsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
) => ["AdminProspectingBuildingsFacets", variables]

useSuspenseAdminProspectingBuildingsFacetsQuery.rootKey =
  "AdminProspectingBuildingsFacets"

export const useInfiniteAdminProspectingBuildingsFacetsQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProspectingBuildingsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProspectingBuildingsFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingsFacets.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingsFacetsQuery,
            Types.AdminProspectingBuildingsFacetsQueryVariables
          >(AdminProspectingBuildingsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProspectingBuildingsFacetsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
) => ["AdminProspectingBuildingsFacets.infinite", variables]

useInfiniteAdminProspectingBuildingsFacetsQuery.rootKey =
  "AdminProspectingBuildingsFacets.infinite"

export const useSuspenseInfiniteAdminProspectingBuildingsFacetsQuery = <
  TData = InfiniteData<Types.AdminProspectingBuildingsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsFacetsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProspectingBuildingsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminProspectingBuildingsFacetsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProspectingBuildingsFacets.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProspectingBuildingsFacetsQuery,
            Types.AdminProspectingBuildingsFacetsQueryVariables
          >(AdminProspectingBuildingsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProspectingBuildingsFacetsQuery.getKey = (
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
) => ["AdminProspectingBuildingsFacets.infinite", variables]

useSuspenseInfiniteAdminProspectingBuildingsFacetsQuery.rootKey =
  "AdminProspectingBuildingsFacets.infinite"

useAdminProspectingBuildingsFacetsQuery.fetcher = (
  variables: Types.AdminProspectingBuildingsFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProspectingBuildingsFacetsQuery,
    Types.AdminProspectingBuildingsFacetsQueryVariables
  >(AdminProspectingBuildingsFacetsDocument, variables, options)

export const MeetingNoteSyncStatusDocument = new TypedDocumentString(`
    query MeetingNoteSyncStatus {
  admin {
    meetingNoteSync {
      running
      finishedAt
      status
    }
  }
}
    `)

export const useMeetingNoteSyncStatusQuery = <
  TData = Types.MeetingNoteSyncStatusQuery,
  TError = unknown,
>(
  variables?: Types.MeetingNoteSyncStatusQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MeetingNoteSyncStatusQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MeetingNoteSyncStatusQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MeetingNoteSyncStatusQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["MeetingNoteSyncStatus"]
        : ["MeetingNoteSyncStatus", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteSyncStatusQuery,
      Types.MeetingNoteSyncStatusQueryVariables
    >(MeetingNoteSyncStatusDocument, variables),
    ...options,
  })
}

useMeetingNoteSyncStatusQuery.getKey = (
  variables?: Types.MeetingNoteSyncStatusQueryVariables,
) =>
  variables === undefined
    ? ["MeetingNoteSyncStatus"]
    : ["MeetingNoteSyncStatus", variables]

useMeetingNoteSyncStatusQuery.rootKey = "MeetingNoteSyncStatus"

export const useSuspenseMeetingNoteSyncStatusQuery = <
  TData = Types.MeetingNoteSyncStatusQuery,
  TError = unknown,
>(
  variables?: Types.MeetingNoteSyncStatusQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeetingNoteSyncStatusQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MeetingNoteSyncStatusQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeetingNoteSyncStatusQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["MeetingNoteSyncStatus"]
        : ["MeetingNoteSyncStatus", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteSyncStatusQuery,
      Types.MeetingNoteSyncStatusQueryVariables
    >(MeetingNoteSyncStatusDocument, variables),
    ...options,
  })
}

useSuspenseMeetingNoteSyncStatusQuery.getKey = (
  variables?: Types.MeetingNoteSyncStatusQueryVariables,
) =>
  variables === undefined
    ? ["MeetingNoteSyncStatus"]
    : ["MeetingNoteSyncStatus", variables]

useSuspenseMeetingNoteSyncStatusQuery.rootKey = "MeetingNoteSyncStatus"

export const useInfiniteMeetingNoteSyncStatusQuery = <
  TData = InfiniteData<Types.MeetingNoteSyncStatusQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteSyncStatusQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeetingNoteSyncStatusQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MeetingNoteSyncStatusQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeetingNoteSyncStatusQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["MeetingNoteSyncStatus.infinite"]
            : ["MeetingNoteSyncStatus.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteSyncStatusQuery,
            Types.MeetingNoteSyncStatusQueryVariables
          >(MeetingNoteSyncStatusDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeetingNoteSyncStatusQuery.getKey = (
  variables?: Types.MeetingNoteSyncStatusQueryVariables,
) =>
  variables === undefined
    ? ["MeetingNoteSyncStatus.infinite"]
    : ["MeetingNoteSyncStatus.infinite", variables]

useInfiniteMeetingNoteSyncStatusQuery.rootKey = "MeetingNoteSyncStatus.infinite"

export const useSuspenseInfiniteMeetingNoteSyncStatusQuery = <
  TData = InfiniteData<Types.MeetingNoteSyncStatusQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteSyncStatusQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeetingNoteSyncStatusQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeetingNoteSyncStatusQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeetingNoteSyncStatusQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["MeetingNoteSyncStatus.infinite"]
            : ["MeetingNoteSyncStatus.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteSyncStatusQuery,
            Types.MeetingNoteSyncStatusQueryVariables
          >(MeetingNoteSyncStatusDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeetingNoteSyncStatusQuery.getKey = (
  variables?: Types.MeetingNoteSyncStatusQueryVariables,
) =>
  variables === undefined
    ? ["MeetingNoteSyncStatus.infinite"]
    : ["MeetingNoteSyncStatus.infinite", variables]

useSuspenseInfiniteMeetingNoteSyncStatusQuery.rootKey = "MeetingNoteSyncStatus.infinite"

useMeetingNoteSyncStatusQuery.fetcher = (
  variables?: Types.MeetingNoteSyncStatusQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MeetingNoteSyncStatusQuery,
    Types.MeetingNoteSyncStatusQueryVariables
  >(MeetingNoteSyncStatusDocument, variables, options)

export const UrdbUtilityListDocument = new TypedDocumentString(`
    query UrdbUtilityList {
  urdb {
    utilities {
      ...UrdbUtilityFragment
    }
  }
}
    fragment UrdbUtilityFragment on UrdbUtility {
  id
  eiaId
  name
  rateCount
  dateLastUpdated
  genabilityUtility {
    id
    name
  }
}`)

export const useUrdbUtilityListQuery = <
  TData = Types.UrdbUtilityListQuery,
  TError = unknown,
>(
  variables?: Types.UrdbUtilityListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UrdbUtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UrdbUtilityListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UrdbUtilityListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["UrdbUtilityList"] : ["UrdbUtilityList", variables],
    queryFn: publicFetcher<
      Types.UrdbUtilityListQuery,
      Types.UrdbUtilityListQueryVariables
    >(UrdbUtilityListDocument, variables),
    ...options,
  })
}

useUrdbUtilityListQuery.getKey = (variables?: Types.UrdbUtilityListQueryVariables) =>
  variables === undefined ? ["UrdbUtilityList"] : ["UrdbUtilityList", variables]

useUrdbUtilityListQuery.rootKey = "UrdbUtilityList"

export const useSuspenseUrdbUtilityListQuery = <
  TData = Types.UrdbUtilityListQuery,
  TError = unknown,
>(
  variables?: Types.UrdbUtilityListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UrdbUtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UrdbUtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UrdbUtilityListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["UrdbUtilityList"] : ["UrdbUtilityList", variables],
    queryFn: publicFetcher<
      Types.UrdbUtilityListQuery,
      Types.UrdbUtilityListQueryVariables
    >(UrdbUtilityListDocument, variables),
    ...options,
  })
}

useSuspenseUrdbUtilityListQuery.getKey = (
  variables?: Types.UrdbUtilityListQueryVariables,
) => (variables === undefined ? ["UrdbUtilityList"] : ["UrdbUtilityList", variables])

useSuspenseUrdbUtilityListQuery.rootKey = "UrdbUtilityList"

export const useInfiniteUrdbUtilityListQuery = <
  TData = InfiniteData<Types.UrdbUtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UrdbUtilityListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UrdbUtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UrdbUtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UrdbUtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UrdbUtilityList.infinite"]
            : ["UrdbUtilityList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UrdbUtilityListQuery,
            Types.UrdbUtilityListQueryVariables
          >(UrdbUtilityListDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUrdbUtilityListQuery.getKey = (
  variables?: Types.UrdbUtilityListQueryVariables,
) =>
  variables === undefined
    ? ["UrdbUtilityList.infinite"]
    : ["UrdbUtilityList.infinite", variables]

useInfiniteUrdbUtilityListQuery.rootKey = "UrdbUtilityList.infinite"

export const useSuspenseInfiniteUrdbUtilityListQuery = <
  TData = InfiniteData<Types.UrdbUtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UrdbUtilityListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UrdbUtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UrdbUtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UrdbUtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UrdbUtilityList.infinite"]
            : ["UrdbUtilityList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UrdbUtilityListQuery,
            Types.UrdbUtilityListQueryVariables
          >(UrdbUtilityListDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUrdbUtilityListQuery.getKey = (
  variables?: Types.UrdbUtilityListQueryVariables,
) =>
  variables === undefined
    ? ["UrdbUtilityList.infinite"]
    : ["UrdbUtilityList.infinite", variables]

useSuspenseInfiniteUrdbUtilityListQuery.rootKey = "UrdbUtilityList.infinite"

useUrdbUtilityListQuery.fetcher = (
  variables?: Types.UrdbUtilityListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UrdbUtilityListQuery, Types.UrdbUtilityListQueryVariables>(
    UrdbUtilityListDocument,
    variables,
    options,
  )

export const UrdbUtilityDetailDocument = new TypedDocumentString(`
    query UrdbUtilityDetail($eiaId: Int!) {
  urdb {
    utility(eiaId: $eiaId) {
      ...UrdbUtilityFragment
      rates {
        ...UrdbRateFragment
      }
    }
  }
}
    fragment UrdbUtilityFragment on UrdbUtility {
  id
  eiaId
  name
  rateCount
  dateLastUpdated
  genabilityUtility {
    id
    name
  }
}
fragment UrdbRateFragment on UrdbRate {
  id
  urdbId
  rateName
  sector
  approved
  isDefault
  effectiveDate
  endDate
  supersedesUrdbId
  hasEnergyRates
  hasTouEnergyRates
  hasTieredEnergyRates
  hasDemandRates
  hasFlatDemandRates
  fixedChargeFirstMeter
  fixedChargeUnits
  demandMin
  demandMax
  dgRules
}`)

export const useUrdbUtilityDetailQuery = <
  TData = Types.UrdbUtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UrdbUtilityDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UrdbUtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UrdbUtilityDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UrdbUtilityDetailQuery, TError, TData>({
    queryKey: ["UrdbUtilityDetail", variables],
    queryFn: publicFetcher<
      Types.UrdbUtilityDetailQuery,
      Types.UrdbUtilityDetailQueryVariables
    >(UrdbUtilityDetailDocument, variables),
    ...options,
  })
}

useUrdbUtilityDetailQuery.getKey = (
  variables: Types.UrdbUtilityDetailQueryVariables,
) => ["UrdbUtilityDetail", variables]

useUrdbUtilityDetailQuery.rootKey = "UrdbUtilityDetail"

export const useSuspenseUrdbUtilityDetailQuery = <
  TData = Types.UrdbUtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UrdbUtilityDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UrdbUtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UrdbUtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UrdbUtilityDetailQuery, TError, TData>({
    queryKey: ["UrdbUtilityDetail", variables],
    queryFn: publicFetcher<
      Types.UrdbUtilityDetailQuery,
      Types.UrdbUtilityDetailQueryVariables
    >(UrdbUtilityDetailDocument, variables),
    ...options,
  })
}

useSuspenseUrdbUtilityDetailQuery.getKey = (
  variables: Types.UrdbUtilityDetailQueryVariables,
) => ["UrdbUtilityDetail", variables]

useSuspenseUrdbUtilityDetailQuery.rootKey = "UrdbUtilityDetail"

export const useInfiniteUrdbUtilityDetailQuery = <
  TData = InfiniteData<Types.UrdbUtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UrdbUtilityDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UrdbUtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UrdbUtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UrdbUtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UrdbUtilityDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UrdbUtilityDetailQuery,
            Types.UrdbUtilityDetailQueryVariables
          >(UrdbUtilityDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUrdbUtilityDetailQuery.getKey = (
  variables: Types.UrdbUtilityDetailQueryVariables,
) => ["UrdbUtilityDetail.infinite", variables]

useInfiniteUrdbUtilityDetailQuery.rootKey = "UrdbUtilityDetail.infinite"

export const useSuspenseInfiniteUrdbUtilityDetailQuery = <
  TData = InfiniteData<Types.UrdbUtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UrdbUtilityDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UrdbUtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UrdbUtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UrdbUtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UrdbUtilityDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UrdbUtilityDetailQuery,
            Types.UrdbUtilityDetailQueryVariables
          >(UrdbUtilityDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUrdbUtilityDetailQuery.getKey = (
  variables: Types.UrdbUtilityDetailQueryVariables,
) => ["UrdbUtilityDetail.infinite", variables]

useSuspenseInfiniteUrdbUtilityDetailQuery.rootKey = "UrdbUtilityDetail.infinite"

useUrdbUtilityDetailQuery.fetcher = (
  variables: Types.UrdbUtilityDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UrdbUtilityDetailQuery, Types.UrdbUtilityDetailQueryVariables>(
    UrdbUtilityDetailDocument,
    variables,
    options,
  )

export const UrdbRateDetailDocument = new TypedDocumentString(`
    query UrdbRateDetail($urdbId: String!) {
  urdb {
    rate(urdbId: $urdbId) {
      rate {
        ...UrdbRateFragment
      }
      utility {
        ...UrdbUtilityFragment
      }
      rawJson
      convertedJson
      convertedRates {
        ...RateTableFragment
      }
      conversionWarnings {
        code
        detail
      }
      conversionError
      supersedes {
        ...UrdbRateFragment
      }
      supersededBy {
        ...UrdbRateFragment
      }
    }
  }
}
    fragment UrdbUtilityFragment on UrdbUtility {
  id
  eiaId
  name
  rateCount
  dateLastUpdated
  genabilityUtility {
    id
    name
  }
}
fragment UrdbRateFragment on UrdbRate {
  id
  urdbId
  rateName
  sector
  approved
  isDefault
  effectiveDate
  endDate
  supersedesUrdbId
  hasEnergyRates
  hasTouEnergyRates
  hasTieredEnergyRates
  hasDemandRates
  hasFlatDemandRates
  fixedChargeFirstMeter
  fixedChargeUnits
  demandMin
  demandMax
  dgRules
}
fragment RateTableFragment on Rate {
  tariffRateId
  name
  chargeClass
  chargeType
  chargePeriod
  transactionType
  applicabilityKey
  quantityKey
  variableLimitKey
  variableRateKey
  variableFactorKey
  isTou
  bands {
    tariffRateBandId
    rateUnit
    price
    isCredit
    hasConsumptionLimit
    consumptionUpperLimit
    demandUpperLimit
    propertyUpperLimit
    calculationFactor
    applicabilityValue
  }
  season {
    seasonFromMonth
    seasonToMonth
    seasonFromDay
    seasonToDay
  }
  tou {
    touName
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    touPeriods {
      fromDayOfWeek
      toDayOfWeek
      fromHour
      toHour
      fromMinute
      toMinute
    }
  }
}`)

export const useUrdbRateDetailQuery = <
  TData = Types.UrdbRateDetailQuery,
  TError = unknown,
>(
  variables: Types.UrdbRateDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UrdbRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UrdbRateDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UrdbRateDetailQuery, TError, TData>({
    queryKey: ["UrdbRateDetail", variables],
    queryFn: publicFetcher<
      Types.UrdbRateDetailQuery,
      Types.UrdbRateDetailQueryVariables
    >(UrdbRateDetailDocument, variables),
    ...options,
  })
}

useUrdbRateDetailQuery.getKey = (variables: Types.UrdbRateDetailQueryVariables) => [
  "UrdbRateDetail",
  variables,
]

useUrdbRateDetailQuery.rootKey = "UrdbRateDetail"

export const useSuspenseUrdbRateDetailQuery = <
  TData = Types.UrdbRateDetailQuery,
  TError = unknown,
>(
  variables: Types.UrdbRateDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UrdbRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UrdbRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UrdbRateDetailQuery, TError, TData>({
    queryKey: ["UrdbRateDetail", variables],
    queryFn: publicFetcher<
      Types.UrdbRateDetailQuery,
      Types.UrdbRateDetailQueryVariables
    >(UrdbRateDetailDocument, variables),
    ...options,
  })
}

useSuspenseUrdbRateDetailQuery.getKey = (
  variables: Types.UrdbRateDetailQueryVariables,
) => ["UrdbRateDetail", variables]

useSuspenseUrdbRateDetailQuery.rootKey = "UrdbRateDetail"

export const useInfiniteUrdbRateDetailQuery = <
  TData = InfiniteData<Types.UrdbRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.UrdbRateDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UrdbRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UrdbRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UrdbRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UrdbRateDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UrdbRateDetailQuery, Types.UrdbRateDetailQueryVariables>(
            UrdbRateDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUrdbRateDetailQuery.getKey = (
  variables: Types.UrdbRateDetailQueryVariables,
) => ["UrdbRateDetail.infinite", variables]

useInfiniteUrdbRateDetailQuery.rootKey = "UrdbRateDetail.infinite"

export const useSuspenseInfiniteUrdbRateDetailQuery = <
  TData = InfiniteData<Types.UrdbRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.UrdbRateDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UrdbRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UrdbRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UrdbRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UrdbRateDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UrdbRateDetailQuery, Types.UrdbRateDetailQueryVariables>(
            UrdbRateDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUrdbRateDetailQuery.getKey = (
  variables: Types.UrdbRateDetailQueryVariables,
) => ["UrdbRateDetail.infinite", variables]

useSuspenseInfiniteUrdbRateDetailQuery.rootKey = "UrdbRateDetail.infinite"

useUrdbRateDetailQuery.fetcher = (
  variables: Types.UrdbRateDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UrdbRateDetailQuery, Types.UrdbRateDetailQueryVariables>(
    UrdbRateDetailDocument,
    variables,
    options,
  )

export const UnlinkedGenabilityLsesDocument = new TypedDocumentString(`
    query UnlinkedGenabilityLses {
  genability {
    unlinkedLses {
      lseId
      name
      lseCode
      eiaCode
      totalCustomers
      states
      fetchedAt
      suggestedUtilities {
        id
        pid
        name
        shortName
      }
    }
  }
}
    `)

export const useUnlinkedGenabilityLsesQuery = <
  TData = Types.UnlinkedGenabilityLsesQuery,
  TError = unknown,
>(
  variables?: Types.UnlinkedGenabilityLsesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UnlinkedGenabilityLsesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.UnlinkedGenabilityLsesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.UnlinkedGenabilityLsesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UnlinkedGenabilityLses"]
        : ["UnlinkedGenabilityLses", variables],
    queryFn: publicFetcher<
      Types.UnlinkedGenabilityLsesQuery,
      Types.UnlinkedGenabilityLsesQueryVariables
    >(UnlinkedGenabilityLsesDocument, variables),
    ...options,
  })
}

useUnlinkedGenabilityLsesQuery.getKey = (
  variables?: Types.UnlinkedGenabilityLsesQueryVariables,
) =>
  variables === undefined
    ? ["UnlinkedGenabilityLses"]
    : ["UnlinkedGenabilityLses", variables]

useUnlinkedGenabilityLsesQuery.rootKey = "UnlinkedGenabilityLses"

export const useSuspenseUnlinkedGenabilityLsesQuery = <
  TData = Types.UnlinkedGenabilityLsesQuery,
  TError = unknown,
>(
  variables?: Types.UnlinkedGenabilityLsesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UnlinkedGenabilityLsesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UnlinkedGenabilityLsesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UnlinkedGenabilityLsesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UnlinkedGenabilityLses"]
        : ["UnlinkedGenabilityLses", variables],
    queryFn: publicFetcher<
      Types.UnlinkedGenabilityLsesQuery,
      Types.UnlinkedGenabilityLsesQueryVariables
    >(UnlinkedGenabilityLsesDocument, variables),
    ...options,
  })
}

useSuspenseUnlinkedGenabilityLsesQuery.getKey = (
  variables?: Types.UnlinkedGenabilityLsesQueryVariables,
) =>
  variables === undefined
    ? ["UnlinkedGenabilityLses"]
    : ["UnlinkedGenabilityLses", variables]

useSuspenseUnlinkedGenabilityLsesQuery.rootKey = "UnlinkedGenabilityLses"

export const useInfiniteUnlinkedGenabilityLsesQuery = <
  TData = InfiniteData<Types.UnlinkedGenabilityLsesQuery>,
  TError = unknown,
>(
  variables: Types.UnlinkedGenabilityLsesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UnlinkedGenabilityLsesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UnlinkedGenabilityLsesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UnlinkedGenabilityLsesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UnlinkedGenabilityLses.infinite"]
            : ["UnlinkedGenabilityLses.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UnlinkedGenabilityLsesQuery,
            Types.UnlinkedGenabilityLsesQueryVariables
          >(UnlinkedGenabilityLsesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUnlinkedGenabilityLsesQuery.getKey = (
  variables?: Types.UnlinkedGenabilityLsesQueryVariables,
) =>
  variables === undefined
    ? ["UnlinkedGenabilityLses.infinite"]
    : ["UnlinkedGenabilityLses.infinite", variables]

useInfiniteUnlinkedGenabilityLsesQuery.rootKey = "UnlinkedGenabilityLses.infinite"

export const useSuspenseInfiniteUnlinkedGenabilityLsesQuery = <
  TData = InfiniteData<Types.UnlinkedGenabilityLsesQuery>,
  TError = unknown,
>(
  variables: Types.UnlinkedGenabilityLsesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UnlinkedGenabilityLsesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UnlinkedGenabilityLsesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UnlinkedGenabilityLsesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UnlinkedGenabilityLses.infinite"]
            : ["UnlinkedGenabilityLses.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.UnlinkedGenabilityLsesQuery,
            Types.UnlinkedGenabilityLsesQueryVariables
          >(UnlinkedGenabilityLsesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUnlinkedGenabilityLsesQuery.getKey = (
  variables?: Types.UnlinkedGenabilityLsesQueryVariables,
) =>
  variables === undefined
    ? ["UnlinkedGenabilityLses.infinite"]
    : ["UnlinkedGenabilityLses.infinite", variables]

useSuspenseInfiniteUnlinkedGenabilityLsesQuery.rootKey =
  "UnlinkedGenabilityLses.infinite"

useUnlinkedGenabilityLsesQuery.fetcher = (
  variables?: Types.UnlinkedGenabilityLsesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UnlinkedGenabilityLsesQuery,
    Types.UnlinkedGenabilityLsesQueryVariables
  >(UnlinkedGenabilityLsesDocument, variables, options)

export const GenabilityMirrorUtilitiesDocument = new TypedDocumentString(`
    query GenabilityMirrorUtilities {
  genability {
    utilities {
      ...GenabilityMirrorUtilityRow
    }
  }
}
    fragment GenabilityMirrorUtilityRow on GenabilityMirrorUtility {
  id
  lseId
  name
  code
  totalCustomers
  tariffCount
  fetchedAt
  dateCreated
  linkedUtility {
    id
    name
    shortName
  }
}`)

export const useGenabilityMirrorUtilitiesQuery = <
  TData = Types.GenabilityMirrorUtilitiesQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityMirrorUtilitiesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GenabilityMirrorUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GenabilityMirrorUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GenabilityMirrorUtilitiesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityMirrorUtilities"]
        : ["GenabilityMirrorUtilities", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorUtilitiesQuery,
      Types.GenabilityMirrorUtilitiesQueryVariables
    >(GenabilityMirrorUtilitiesDocument, variables),
    ...options,
  })
}

useGenabilityMirrorUtilitiesQuery.getKey = (
  variables?: Types.GenabilityMirrorUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorUtilities"]
    : ["GenabilityMirrorUtilities", variables]

useGenabilityMirrorUtilitiesQuery.rootKey = "GenabilityMirrorUtilities"

export const useSuspenseGenabilityMirrorUtilitiesQuery = <
  TData = Types.GenabilityMirrorUtilitiesQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityMirrorUtilitiesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GenabilityMirrorUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GenabilityMirrorUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GenabilityMirrorUtilitiesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityMirrorUtilities"]
        : ["GenabilityMirrorUtilities", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorUtilitiesQuery,
      Types.GenabilityMirrorUtilitiesQueryVariables
    >(GenabilityMirrorUtilitiesDocument, variables),
    ...options,
  })
}

useSuspenseGenabilityMirrorUtilitiesQuery.getKey = (
  variables?: Types.GenabilityMirrorUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorUtilities"]
    : ["GenabilityMirrorUtilities", variables]

useSuspenseGenabilityMirrorUtilitiesQuery.rootKey = "GenabilityMirrorUtilities"

export const useInfiniteGenabilityMirrorUtilitiesQuery = <
  TData = InfiniteData<Types.GenabilityMirrorUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorUtilitiesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GenabilityMirrorUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GenabilityMirrorUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GenabilityMirrorUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityMirrorUtilities.infinite"]
            : ["GenabilityMirrorUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorUtilitiesQuery,
            Types.GenabilityMirrorUtilitiesQueryVariables
          >(GenabilityMirrorUtilitiesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGenabilityMirrorUtilitiesQuery.getKey = (
  variables?: Types.GenabilityMirrorUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorUtilities.infinite"]
    : ["GenabilityMirrorUtilities.infinite", variables]

useInfiniteGenabilityMirrorUtilitiesQuery.rootKey = "GenabilityMirrorUtilities.infinite"

export const useSuspenseInfiniteGenabilityMirrorUtilitiesQuery = <
  TData = InfiniteData<Types.GenabilityMirrorUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorUtilitiesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorUtilitiesQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GenabilityMirrorUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityMirrorUtilities.infinite"]
            : ["GenabilityMirrorUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorUtilitiesQuery,
            Types.GenabilityMirrorUtilitiesQueryVariables
          >(GenabilityMirrorUtilitiesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGenabilityMirrorUtilitiesQuery.getKey = (
  variables?: Types.GenabilityMirrorUtilitiesQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorUtilities.infinite"]
    : ["GenabilityMirrorUtilities.infinite", variables]

useSuspenseInfiniteGenabilityMirrorUtilitiesQuery.rootKey =
  "GenabilityMirrorUtilities.infinite"

useGenabilityMirrorUtilitiesQuery.fetcher = (
  variables?: Types.GenabilityMirrorUtilitiesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenabilityMirrorUtilitiesQuery,
    Types.GenabilityMirrorUtilitiesQueryVariables
  >(GenabilityMirrorUtilitiesDocument, variables, options)

export const GenabilityMirrorTariffsDocument = new TypedDocumentString(`
    query GenabilityMirrorTariffs {
  genability {
    tariffs {
      ...GenabilityMirrorTariffRow
    }
  }
}
    fragment GenabilityMirrorTariffRow on GenabilityMirrorTariff {
  id
  masterTariffId
  lseId
  tariffId
  name
  code
  customerClass
  utilityName
  revisionCount
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedTariff {
    id
    name
    code
  }
}`)

export const useGenabilityMirrorTariffsQuery = <
  TData = Types.GenabilityMirrorTariffsQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityMirrorTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GenabilityMirrorTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GenabilityMirrorTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GenabilityMirrorTariffsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityMirrorTariffs"]
        : ["GenabilityMirrorTariffs", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorTariffsQuery,
      Types.GenabilityMirrorTariffsQueryVariables
    >(GenabilityMirrorTariffsDocument, variables),
    ...options,
  })
}

useGenabilityMirrorTariffsQuery.getKey = (
  variables?: Types.GenabilityMirrorTariffsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorTariffs"]
    : ["GenabilityMirrorTariffs", variables]

useGenabilityMirrorTariffsQuery.rootKey = "GenabilityMirrorTariffs"

export const useSuspenseGenabilityMirrorTariffsQuery = <
  TData = Types.GenabilityMirrorTariffsQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityMirrorTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GenabilityMirrorTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GenabilityMirrorTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GenabilityMirrorTariffsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityMirrorTariffs"]
        : ["GenabilityMirrorTariffs", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorTariffsQuery,
      Types.GenabilityMirrorTariffsQueryVariables
    >(GenabilityMirrorTariffsDocument, variables),
    ...options,
  })
}

useSuspenseGenabilityMirrorTariffsQuery.getKey = (
  variables?: Types.GenabilityMirrorTariffsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorTariffs"]
    : ["GenabilityMirrorTariffs", variables]

useSuspenseGenabilityMirrorTariffsQuery.rootKey = "GenabilityMirrorTariffs"

export const useInfiniteGenabilityMirrorTariffsQuery = <
  TData = InfiniteData<Types.GenabilityMirrorTariffsQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GenabilityMirrorTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GenabilityMirrorTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GenabilityMirrorTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityMirrorTariffs.infinite"]
            : ["GenabilityMirrorTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorTariffsQuery,
            Types.GenabilityMirrorTariffsQueryVariables
          >(GenabilityMirrorTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGenabilityMirrorTariffsQuery.getKey = (
  variables?: Types.GenabilityMirrorTariffsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorTariffs.infinite"]
    : ["GenabilityMirrorTariffs.infinite", variables]

useInfiniteGenabilityMirrorTariffsQuery.rootKey = "GenabilityMirrorTariffs.infinite"

export const useSuspenseInfiniteGenabilityMirrorTariffsQuery = <
  TData = InfiniteData<Types.GenabilityMirrorTariffsQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GenabilityMirrorTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GenabilityMirrorTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityMirrorTariffs.infinite"]
            : ["GenabilityMirrorTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorTariffsQuery,
            Types.GenabilityMirrorTariffsQueryVariables
          >(GenabilityMirrorTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGenabilityMirrorTariffsQuery.getKey = (
  variables?: Types.GenabilityMirrorTariffsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorTariffs.infinite"]
    : ["GenabilityMirrorTariffs.infinite", variables]

useSuspenseInfiniteGenabilityMirrorTariffsQuery.rootKey =
  "GenabilityMirrorTariffs.infinite"

useGenabilityMirrorTariffsQuery.fetcher = (
  variables?: Types.GenabilityMirrorTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenabilityMirrorTariffsQuery,
    Types.GenabilityMirrorTariffsQueryVariables
  >(GenabilityMirrorTariffsDocument, variables, options)

export const GenabilityMirrorRevisionsDocument = new TypedDocumentString(`
    query GenabilityMirrorRevisions {
  genability {
    tariffRevisions {
      ...GenabilityMirrorRevisionRow
    }
  }
}
    fragment GenabilityMirrorRevisionRow on GenabilityMirrorTariffRevision {
  id
  tariffId
  masterTariffId
  name
  code
  contentHash
  effectiveDate
  endDate
  payloadEffectiveDate
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedRevision {
    id
    tariffId
    startDate
  }
}`)

export const useGenabilityMirrorRevisionsQuery = <
  TData = Types.GenabilityMirrorRevisionsQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityMirrorRevisionsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GenabilityMirrorRevisionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GenabilityMirrorRevisionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GenabilityMirrorRevisionsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityMirrorRevisions"]
        : ["GenabilityMirrorRevisions", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorRevisionsQuery,
      Types.GenabilityMirrorRevisionsQueryVariables
    >(GenabilityMirrorRevisionsDocument, variables),
    ...options,
  })
}

useGenabilityMirrorRevisionsQuery.getKey = (
  variables?: Types.GenabilityMirrorRevisionsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorRevisions"]
    : ["GenabilityMirrorRevisions", variables]

useGenabilityMirrorRevisionsQuery.rootKey = "GenabilityMirrorRevisions"

export const useSuspenseGenabilityMirrorRevisionsQuery = <
  TData = Types.GenabilityMirrorRevisionsQuery,
  TError = unknown,
>(
  variables?: Types.GenabilityMirrorRevisionsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GenabilityMirrorRevisionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GenabilityMirrorRevisionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GenabilityMirrorRevisionsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GenabilityMirrorRevisions"]
        : ["GenabilityMirrorRevisions", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorRevisionsQuery,
      Types.GenabilityMirrorRevisionsQueryVariables
    >(GenabilityMirrorRevisionsDocument, variables),
    ...options,
  })
}

useSuspenseGenabilityMirrorRevisionsQuery.getKey = (
  variables?: Types.GenabilityMirrorRevisionsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorRevisions"]
    : ["GenabilityMirrorRevisions", variables]

useSuspenseGenabilityMirrorRevisionsQuery.rootKey = "GenabilityMirrorRevisions"

export const useInfiniteGenabilityMirrorRevisionsQuery = <
  TData = InfiniteData<Types.GenabilityMirrorRevisionsQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorRevisionsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GenabilityMirrorRevisionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GenabilityMirrorRevisionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GenabilityMirrorRevisionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityMirrorRevisions.infinite"]
            : ["GenabilityMirrorRevisions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorRevisionsQuery,
            Types.GenabilityMirrorRevisionsQueryVariables
          >(GenabilityMirrorRevisionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGenabilityMirrorRevisionsQuery.getKey = (
  variables?: Types.GenabilityMirrorRevisionsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorRevisions.infinite"]
    : ["GenabilityMirrorRevisions.infinite", variables]

useInfiniteGenabilityMirrorRevisionsQuery.rootKey = "GenabilityMirrorRevisions.infinite"

export const useSuspenseInfiniteGenabilityMirrorRevisionsQuery = <
  TData = InfiniteData<Types.GenabilityMirrorRevisionsQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorRevisionsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorRevisionsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorRevisionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GenabilityMirrorRevisionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GenabilityMirrorRevisions.infinite"]
            : ["GenabilityMirrorRevisions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorRevisionsQuery,
            Types.GenabilityMirrorRevisionsQueryVariables
          >(GenabilityMirrorRevisionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGenabilityMirrorRevisionsQuery.getKey = (
  variables?: Types.GenabilityMirrorRevisionsQueryVariables,
) =>
  variables === undefined
    ? ["GenabilityMirrorRevisions.infinite"]
    : ["GenabilityMirrorRevisions.infinite", variables]

useSuspenseInfiniteGenabilityMirrorRevisionsQuery.rootKey =
  "GenabilityMirrorRevisions.infinite"

useGenabilityMirrorRevisionsQuery.fetcher = (
  variables?: Types.GenabilityMirrorRevisionsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenabilityMirrorRevisionsQuery,
    Types.GenabilityMirrorRevisionsQueryVariables
  >(GenabilityMirrorRevisionsDocument, variables, options)

export const GenabilityMirrorUtilityDetailDocument = new TypedDocumentString(`
    query GenabilityMirrorUtilityDetail($lseId: Int!) {
  genability {
    utility(lseId: $lseId) {
      ...GenabilityMirrorUtilityRow
      payload
      territories {
        id
        territoryId
        state
        itemTypes
        usageType
        deregResidential
        deregCommercial
      }
    }
    tariffs(lseId: $lseId) {
      ...GenabilityMirrorTariffRow
    }
  }
}
    fragment GenabilityMirrorUtilityRow on GenabilityMirrorUtility {
  id
  lseId
  name
  code
  totalCustomers
  tariffCount
  fetchedAt
  dateCreated
  linkedUtility {
    id
    name
    shortName
  }
}
fragment GenabilityMirrorTariffRow on GenabilityMirrorTariff {
  id
  masterTariffId
  lseId
  tariffId
  name
  code
  customerClass
  utilityName
  revisionCount
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedTariff {
    id
    name
    code
  }
}`)

export const useGenabilityMirrorUtilityDetailQuery = <
  TData = Types.GenabilityMirrorUtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GenabilityMirrorUtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GenabilityMirrorUtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GenabilityMirrorUtilityDetailQuery, TError, TData>({
    queryKey: ["GenabilityMirrorUtilityDetail", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorUtilityDetailQuery,
      Types.GenabilityMirrorUtilityDetailQueryVariables
    >(GenabilityMirrorUtilityDetailDocument, variables),
    ...options,
  })
}

useGenabilityMirrorUtilityDetailQuery.getKey = (
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
) => ["GenabilityMirrorUtilityDetail", variables]

useGenabilityMirrorUtilityDetailQuery.rootKey = "GenabilityMirrorUtilityDetail"

export const useSuspenseGenabilityMirrorUtilityDetailQuery = <
  TData = Types.GenabilityMirrorUtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GenabilityMirrorUtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GenabilityMirrorUtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GenabilityMirrorUtilityDetailQuery, TError, TData>({
    queryKey: ["GenabilityMirrorUtilityDetail", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorUtilityDetailQuery,
      Types.GenabilityMirrorUtilityDetailQueryVariables
    >(GenabilityMirrorUtilityDetailDocument, variables),
    ...options,
  })
}

useSuspenseGenabilityMirrorUtilityDetailQuery.getKey = (
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
) => ["GenabilityMirrorUtilityDetail", variables]

useSuspenseGenabilityMirrorUtilityDetailQuery.rootKey = "GenabilityMirrorUtilityDetail"

export const useInfiniteGenabilityMirrorUtilityDetailQuery = <
  TData = InfiniteData<Types.GenabilityMirrorUtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GenabilityMirrorUtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GenabilityMirrorUtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GenabilityMirrorUtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GenabilityMirrorUtilityDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorUtilityDetailQuery,
            Types.GenabilityMirrorUtilityDetailQueryVariables
          >(GenabilityMirrorUtilityDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGenabilityMirrorUtilityDetailQuery.getKey = (
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
) => ["GenabilityMirrorUtilityDetail.infinite", variables]

useInfiniteGenabilityMirrorUtilityDetailQuery.rootKey =
  "GenabilityMirrorUtilityDetail.infinite"

export const useSuspenseInfiniteGenabilityMirrorUtilityDetailQuery = <
  TData = InfiniteData<Types.GenabilityMirrorUtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorUtilityDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorUtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.GenabilityMirrorUtilityDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GenabilityMirrorUtilityDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorUtilityDetailQuery,
            Types.GenabilityMirrorUtilityDetailQueryVariables
          >(GenabilityMirrorUtilityDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGenabilityMirrorUtilityDetailQuery.getKey = (
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
) => ["GenabilityMirrorUtilityDetail.infinite", variables]

useSuspenseInfiniteGenabilityMirrorUtilityDetailQuery.rootKey =
  "GenabilityMirrorUtilityDetail.infinite"

useGenabilityMirrorUtilityDetailQuery.fetcher = (
  variables: Types.GenabilityMirrorUtilityDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenabilityMirrorUtilityDetailQuery,
    Types.GenabilityMirrorUtilityDetailQueryVariables
  >(GenabilityMirrorUtilityDetailDocument, variables, options)

export const GenabilityMirrorTariffDetailDocument = new TypedDocumentString(`
    query GenabilityMirrorTariffDetail($masterTariffId: Int!) {
  genability {
    tariff(masterTariffId: $masterTariffId) {
      ...GenabilityMirrorTariffRow
      payload
    }
    tariffRevisions(masterTariffId: $masterTariffId) {
      ...GenabilityMirrorRevisionRow
    }
  }
}
    fragment GenabilityMirrorTariffRow on GenabilityMirrorTariff {
  id
  masterTariffId
  lseId
  tariffId
  name
  code
  customerClass
  utilityName
  revisionCount
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedTariff {
    id
    name
    code
  }
}
fragment GenabilityMirrorRevisionRow on GenabilityMirrorTariffRevision {
  id
  tariffId
  masterTariffId
  name
  code
  contentHash
  effectiveDate
  endDate
  payloadEffectiveDate
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedRevision {
    id
    tariffId
    startDate
  }
}`)

export const useGenabilityMirrorTariffDetailQuery = <
  TData = Types.GenabilityMirrorTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GenabilityMirrorTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GenabilityMirrorTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GenabilityMirrorTariffDetailQuery, TError, TData>({
    queryKey: ["GenabilityMirrorTariffDetail", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorTariffDetailQuery,
      Types.GenabilityMirrorTariffDetailQueryVariables
    >(GenabilityMirrorTariffDetailDocument, variables),
    ...options,
  })
}

useGenabilityMirrorTariffDetailQuery.getKey = (
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
) => ["GenabilityMirrorTariffDetail", variables]

useGenabilityMirrorTariffDetailQuery.rootKey = "GenabilityMirrorTariffDetail"

export const useSuspenseGenabilityMirrorTariffDetailQuery = <
  TData = Types.GenabilityMirrorTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GenabilityMirrorTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GenabilityMirrorTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GenabilityMirrorTariffDetailQuery, TError, TData>({
    queryKey: ["GenabilityMirrorTariffDetail", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorTariffDetailQuery,
      Types.GenabilityMirrorTariffDetailQueryVariables
    >(GenabilityMirrorTariffDetailDocument, variables),
    ...options,
  })
}

useSuspenseGenabilityMirrorTariffDetailQuery.getKey = (
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
) => ["GenabilityMirrorTariffDetail", variables]

useSuspenseGenabilityMirrorTariffDetailQuery.rootKey = "GenabilityMirrorTariffDetail"

export const useInfiniteGenabilityMirrorTariffDetailQuery = <
  TData = InfiniteData<Types.GenabilityMirrorTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GenabilityMirrorTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GenabilityMirrorTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GenabilityMirrorTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GenabilityMirrorTariffDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorTariffDetailQuery,
            Types.GenabilityMirrorTariffDetailQueryVariables
          >(GenabilityMirrorTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGenabilityMirrorTariffDetailQuery.getKey = (
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
) => ["GenabilityMirrorTariffDetail.infinite", variables]

useInfiniteGenabilityMirrorTariffDetailQuery.rootKey =
  "GenabilityMirrorTariffDetail.infinite"

export const useSuspenseInfiniteGenabilityMirrorTariffDetailQuery = <
  TData = InfiniteData<Types.GenabilityMirrorTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorTariffDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.GenabilityMirrorTariffDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GenabilityMirrorTariffDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorTariffDetailQuery,
            Types.GenabilityMirrorTariffDetailQueryVariables
          >(GenabilityMirrorTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGenabilityMirrorTariffDetailQuery.getKey = (
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
) => ["GenabilityMirrorTariffDetail.infinite", variables]

useSuspenseInfiniteGenabilityMirrorTariffDetailQuery.rootKey =
  "GenabilityMirrorTariffDetail.infinite"

useGenabilityMirrorTariffDetailQuery.fetcher = (
  variables: Types.GenabilityMirrorTariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenabilityMirrorTariffDetailQuery,
    Types.GenabilityMirrorTariffDetailQueryVariables
  >(GenabilityMirrorTariffDetailDocument, variables, options)

export const GenabilityMirrorRevisionDetailDocument = new TypedDocumentString(`
    query GenabilityMirrorRevisionDetail($id: Int!) {
  genability {
    tariffRevision(id: $id) {
      ...GenabilityMirrorRevisionRow
      payload
    }
  }
}
    fragment GenabilityMirrorRevisionRow on GenabilityMirrorTariffRevision {
  id
  tariffId
  masterTariffId
  name
  code
  contentHash
  effectiveDate
  endDate
  payloadEffectiveDate
  lastUpdatedDate
  fetchedAt
  dateCreated
  linkedRevision {
    id
    tariffId
    startDate
  }
}`)

export const useGenabilityMirrorRevisionDetailQuery = <
  TData = Types.GenabilityMirrorRevisionDetailQuery,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GenabilityMirrorRevisionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GenabilityMirrorRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GenabilityMirrorRevisionDetailQuery, TError, TData>({
    queryKey: ["GenabilityMirrorRevisionDetail", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorRevisionDetailQuery,
      Types.GenabilityMirrorRevisionDetailQueryVariables
    >(GenabilityMirrorRevisionDetailDocument, variables),
    ...options,
  })
}

useGenabilityMirrorRevisionDetailQuery.getKey = (
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
) => ["GenabilityMirrorRevisionDetail", variables]

useGenabilityMirrorRevisionDetailQuery.rootKey = "GenabilityMirrorRevisionDetail"

export const useSuspenseGenabilityMirrorRevisionDetailQuery = <
  TData = Types.GenabilityMirrorRevisionDetailQuery,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GenabilityMirrorRevisionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GenabilityMirrorRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GenabilityMirrorRevisionDetailQuery, TError, TData>({
    queryKey: ["GenabilityMirrorRevisionDetail", variables],
    queryFn: publicFetcher<
      Types.GenabilityMirrorRevisionDetailQuery,
      Types.GenabilityMirrorRevisionDetailQueryVariables
    >(GenabilityMirrorRevisionDetailDocument, variables),
    ...options,
  })
}

useSuspenseGenabilityMirrorRevisionDetailQuery.getKey = (
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
) => ["GenabilityMirrorRevisionDetail", variables]

useSuspenseGenabilityMirrorRevisionDetailQuery.rootKey =
  "GenabilityMirrorRevisionDetail"

export const useInfiniteGenabilityMirrorRevisionDetailQuery = <
  TData = InfiniteData<Types.GenabilityMirrorRevisionDetailQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GenabilityMirrorRevisionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GenabilityMirrorRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GenabilityMirrorRevisionDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GenabilityMirrorRevisionDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorRevisionDetailQuery,
            Types.GenabilityMirrorRevisionDetailQueryVariables
          >(GenabilityMirrorRevisionDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGenabilityMirrorRevisionDetailQuery.getKey = (
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
) => ["GenabilityMirrorRevisionDetail.infinite", variables]

useInfiniteGenabilityMirrorRevisionDetailQuery.rootKey =
  "GenabilityMirrorRevisionDetail.infinite"

export const useSuspenseInfiniteGenabilityMirrorRevisionDetailQuery = <
  TData = InfiniteData<Types.GenabilityMirrorRevisionDetailQuery>,
  TError = unknown,
>(
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorRevisionDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GenabilityMirrorRevisionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.GenabilityMirrorRevisionDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GenabilityMirrorRevisionDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GenabilityMirrorRevisionDetailQuery,
            Types.GenabilityMirrorRevisionDetailQueryVariables
          >(GenabilityMirrorRevisionDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGenabilityMirrorRevisionDetailQuery.getKey = (
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
) => ["GenabilityMirrorRevisionDetail.infinite", variables]

useSuspenseInfiniteGenabilityMirrorRevisionDetailQuery.rootKey =
  "GenabilityMirrorRevisionDetail.infinite"

useGenabilityMirrorRevisionDetailQuery.fetcher = (
  variables: Types.GenabilityMirrorRevisionDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenabilityMirrorRevisionDetailQuery,
    Types.GenabilityMirrorRevisionDetailQueryVariables
  >(GenabilityMirrorRevisionDetailDocument, variables, options)

export const AdminCompanyDuplicateGroupsDocument = new TypedDocumentString(`
    query AdminCompanyDuplicateGroups($offset: Int! = 0, $statuses: [CompanyDuplicateReviewFilter!], $search: String, $queue: CompanyDuplicateQueue, $portfolioPids: [String!]) {
  admin {
    companyDuplicateGroups(
      offset: $offset
      statuses: $statuses
      search: $search
      queue: $queue
      portfolioPids: $portfolioPids
      limit: 20
    ) {
      total
      queue
      queueCounts {
        all
        priority
        normal
      }
      groups {
        ...CompanyDuplicateReviewGroup
      }
    }
  }
}
    fragment CompanyDuplicateReviewGroup on CompanyDuplicateGroup {
  id
  tier
  canonicalCompanyId
  proposedDisplayName
  merging
  hasPendingApproval
  stale
  lastSeenAt
  priority
  priorityPortfolios
  companies {
    id
    pid
    canonicalName
    createdAt
    aliases
    url
    orgPid
    orgName
    hubspotId
    reonomyOwners {
      id
      name
    }
    ownedBuildings
    leasedBuildings
    states
    possiblyTruncated
  }
  pairs {
    id
    companyAId
    companyBId
    conflictOrg
    conflictHubspot
    signals
    judge {
      verdict
      confidence
      relationship
      reasoning
    }
  }
}`)

export const useAdminCompanyDuplicateGroupsQuery = <
  TData = Types.AdminCompanyDuplicateGroupsQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompanyDuplicateGroupsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyDuplicateGroupsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompanyDuplicateGroupsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyDuplicateGroupsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompanyDuplicateGroups"]
        : ["AdminCompanyDuplicateGroups", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicateGroupsQuery,
      Types.AdminCompanyDuplicateGroupsQueryVariables
    >(AdminCompanyDuplicateGroupsDocument, variables),
    ...options,
  })
}

useAdminCompanyDuplicateGroupsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateGroupsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateGroups"]
    : ["AdminCompanyDuplicateGroups", variables]

useAdminCompanyDuplicateGroupsQuery.rootKey = "AdminCompanyDuplicateGroups"

export const useSuspenseAdminCompanyDuplicateGroupsQuery = <
  TData = Types.AdminCompanyDuplicateGroupsQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompanyDuplicateGroupsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompanyDuplicateGroupsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyDuplicateGroupsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompanyDuplicateGroupsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompanyDuplicateGroups"]
        : ["AdminCompanyDuplicateGroups", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicateGroupsQuery,
      Types.AdminCompanyDuplicateGroupsQueryVariables
    >(AdminCompanyDuplicateGroupsDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompanyDuplicateGroupsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateGroupsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateGroups"]
    : ["AdminCompanyDuplicateGroups", variables]

useSuspenseAdminCompanyDuplicateGroupsQuery.rootKey = "AdminCompanyDuplicateGroups"

export const useInfiniteAdminCompanyDuplicateGroupsQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateGroupsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateGroupsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompanyDuplicateGroupsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateGroupsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompanyDuplicateGroupsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompanyDuplicateGroups.infinite"]
            : ["AdminCompanyDuplicateGroups.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateGroupsQuery,
            Types.AdminCompanyDuplicateGroupsQueryVariables
          >(AdminCompanyDuplicateGroupsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyDuplicateGroupsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateGroupsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateGroups.infinite"]
    : ["AdminCompanyDuplicateGroups.infinite", variables]

useInfiniteAdminCompanyDuplicateGroupsQuery.rootKey =
  "AdminCompanyDuplicateGroups.infinite"

export const useSuspenseInfiniteAdminCompanyDuplicateGroupsQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateGroupsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateGroupsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateGroupsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateGroupsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminCompanyDuplicateGroupsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompanyDuplicateGroups.infinite"]
            : ["AdminCompanyDuplicateGroups.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateGroupsQuery,
            Types.AdminCompanyDuplicateGroupsQueryVariables
          >(AdminCompanyDuplicateGroupsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyDuplicateGroupsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateGroupsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateGroups.infinite"]
    : ["AdminCompanyDuplicateGroups.infinite", variables]

useSuspenseInfiniteAdminCompanyDuplicateGroupsQuery.rootKey =
  "AdminCompanyDuplicateGroups.infinite"

useAdminCompanyDuplicateGroupsQuery.fetcher = (
  variables?: Types.AdminCompanyDuplicateGroupsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompanyDuplicateGroupsQuery,
    Types.AdminCompanyDuplicateGroupsQueryVariables
  >(AdminCompanyDuplicateGroupsDocument, variables, options)

export const AdminCompanyDuplicatePriorityPortfoliosDocument = new TypedDocumentString(`
    query AdminCompanyDuplicatePriorityPortfolios {
  admin {
    companyDuplicatePriorityPortfolios {
      pid
      name
    }
  }
}
    `)

export const useAdminCompanyDuplicatePriorityPortfoliosQuery = <
  TData = Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyDuplicatePriorityPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyDuplicatePriorityPortfoliosQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompanyDuplicatePriorityPortfolios"]
        : ["AdminCompanyDuplicatePriorityPortfolios", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables
    >(AdminCompanyDuplicatePriorityPortfoliosDocument, variables),
    ...options,
  })
}

useAdminCompanyDuplicatePriorityPortfoliosQuery.getKey = (
  variables?: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicatePriorityPortfolios"]
    : ["AdminCompanyDuplicatePriorityPortfolios", variables]

useAdminCompanyDuplicatePriorityPortfoliosQuery.rootKey =
  "AdminCompanyDuplicatePriorityPortfolios"

export const useSuspenseAdminCompanyDuplicatePriorityPortfoliosQuery = <
  TData = Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<
    Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
    TError,
    TData
  >({
    queryKey:
      variables === undefined
        ? ["AdminCompanyDuplicatePriorityPortfolios"]
        : ["AdminCompanyDuplicatePriorityPortfolios", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables
    >(AdminCompanyDuplicatePriorityPortfoliosDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompanyDuplicatePriorityPortfoliosQuery.getKey = (
  variables?: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicatePriorityPortfolios"]
    : ["AdminCompanyDuplicatePriorityPortfolios", variables]

useSuspenseAdminCompanyDuplicatePriorityPortfoliosQuery.rootKey =
  "AdminCompanyDuplicatePriorityPortfolios"

export const useInfiniteAdminCompanyDuplicatePriorityPortfoliosQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicatePriorityPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<
    Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompanyDuplicatePriorityPortfolios.infinite"]
            : ["AdminCompanyDuplicatePriorityPortfolios.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
            Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables
          >(AdminCompanyDuplicatePriorityPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyDuplicatePriorityPortfoliosQuery.getKey = (
  variables?: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicatePriorityPortfolios.infinite"]
    : ["AdminCompanyDuplicatePriorityPortfolios.infinite", variables]

useInfiniteAdminCompanyDuplicatePriorityPortfoliosQuery.rootKey =
  "AdminCompanyDuplicatePriorityPortfolios.infinite"

export const useSuspenseInfiniteAdminCompanyDuplicatePriorityPortfoliosQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicatePriorityPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompanyDuplicatePriorityPortfolios.infinite"]
            : ["AdminCompanyDuplicatePriorityPortfolios.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
            Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables
          >(AdminCompanyDuplicatePriorityPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyDuplicatePriorityPortfoliosQuery.getKey = (
  variables?: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicatePriorityPortfolios.infinite"]
    : ["AdminCompanyDuplicatePriorityPortfolios.infinite", variables]

useSuspenseInfiniteAdminCompanyDuplicatePriorityPortfoliosQuery.rootKey =
  "AdminCompanyDuplicatePriorityPortfolios.infinite"

useAdminCompanyDuplicatePriorityPortfoliosQuery.fetcher = (
  variables?: Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompanyDuplicatePriorityPortfoliosQuery,
    Types.AdminCompanyDuplicatePriorityPortfoliosQueryVariables
  >(AdminCompanyDuplicatePriorityPortfoliosDocument, variables, options)

export const AdminCompanyDuplicateRunsDocument = new TypedDocumentString(`
    query AdminCompanyDuplicateRuns($offset: Int = 0, $limit: Int = 1) {
  admin {
    companyDuplicateDetectionRuns(offset: $offset, limit: $limit) {
      ...CompanyDuplicateRunSummary
    }
  }
}
    fragment CompanyDuplicateRunSummary on CompanyDuplicateRun {
  id
  dryRun
  status
  stage
  startedAt
  finishedAt
  pairsFormed
  pairsJudged
  pairsToJudge
  judgeFailures
  mergesDone
  mergeFailures
  errorMessage
  costUsd
  triggeredBy
  note
  autoMergeThreshold
  deterministicAutoMerge
  anchoredAutoMergeFloor
  pairsPerTier {
    label
    count
  }
  failedMerges {
    pairId
    error
  }
}`)

export const useAdminCompanyDuplicateRunsQuery = <
  TData = Types.AdminCompanyDuplicateRunsQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompanyDuplicateRunsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyDuplicateRunsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompanyDuplicateRunsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyDuplicateRunsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompanyDuplicateRuns"]
        : ["AdminCompanyDuplicateRuns", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicateRunsQuery,
      Types.AdminCompanyDuplicateRunsQueryVariables
    >(AdminCompanyDuplicateRunsDocument, variables),
    ...options,
  })
}

useAdminCompanyDuplicateRunsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateRunsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateRuns"]
    : ["AdminCompanyDuplicateRuns", variables]

useAdminCompanyDuplicateRunsQuery.rootKey = "AdminCompanyDuplicateRuns"

export const useSuspenseAdminCompanyDuplicateRunsQuery = <
  TData = Types.AdminCompanyDuplicateRunsQuery,
  TError = unknown,
>(
  variables?: Types.AdminCompanyDuplicateRunsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompanyDuplicateRunsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyDuplicateRunsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompanyDuplicateRunsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminCompanyDuplicateRuns"]
        : ["AdminCompanyDuplicateRuns", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicateRunsQuery,
      Types.AdminCompanyDuplicateRunsQueryVariables
    >(AdminCompanyDuplicateRunsDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompanyDuplicateRunsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateRunsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateRuns"]
    : ["AdminCompanyDuplicateRuns", variables]

useSuspenseAdminCompanyDuplicateRunsQuery.rootKey = "AdminCompanyDuplicateRuns"

export const useInfiniteAdminCompanyDuplicateRunsQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateRunsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateRunsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompanyDuplicateRunsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateRunsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompanyDuplicateRunsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompanyDuplicateRuns.infinite"]
            : ["AdminCompanyDuplicateRuns.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateRunsQuery,
            Types.AdminCompanyDuplicateRunsQueryVariables
          >(AdminCompanyDuplicateRunsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyDuplicateRunsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateRunsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateRuns.infinite"]
    : ["AdminCompanyDuplicateRuns.infinite", variables]

useInfiniteAdminCompanyDuplicateRunsQuery.rootKey = "AdminCompanyDuplicateRuns.infinite"

export const useSuspenseInfiniteAdminCompanyDuplicateRunsQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateRunsQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateRunsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateRunsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateRunsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCompanyDuplicateRunsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminCompanyDuplicateRuns.infinite"]
            : ["AdminCompanyDuplicateRuns.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateRunsQuery,
            Types.AdminCompanyDuplicateRunsQueryVariables
          >(AdminCompanyDuplicateRunsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyDuplicateRunsQuery.getKey = (
  variables?: Types.AdminCompanyDuplicateRunsQueryVariables,
) =>
  variables === undefined
    ? ["AdminCompanyDuplicateRuns.infinite"]
    : ["AdminCompanyDuplicateRuns.infinite", variables]

useSuspenseInfiniteAdminCompanyDuplicateRunsQuery.rootKey =
  "AdminCompanyDuplicateRuns.infinite"

useAdminCompanyDuplicateRunsQuery.fetcher = (
  variables?: Types.AdminCompanyDuplicateRunsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompanyDuplicateRunsQuery,
    Types.AdminCompanyDuplicateRunsQueryVariables
  >(AdminCompanyDuplicateRunsDocument, variables, options)

export const AdminCompanyDuplicateRunDocument = new TypedDocumentString(`
    query AdminCompanyDuplicateRun($id: Int!, $offset: Int = 0) {
  admin {
    companyDuplicateDetectionRun(id: $id) {
      id
      merges(offset: $offset, limit: 20) {
        total
        rows {
          id
          absorbedName
          canonicalName
          canonicalPid
          source
          mergedAt
          reversedAt
        }
      }
    }
  }
}
    `)

export const useAdminCompanyDuplicateRunQuery = <
  TData = Types.AdminCompanyDuplicateRunQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyDuplicateRunQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompanyDuplicateRunQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyDuplicateRunQuery, TError, TData>({
    queryKey: ["AdminCompanyDuplicateRun", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicateRunQuery,
      Types.AdminCompanyDuplicateRunQueryVariables
    >(AdminCompanyDuplicateRunDocument, variables),
    ...options,
  })
}

useAdminCompanyDuplicateRunQuery.getKey = (
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
) => ["AdminCompanyDuplicateRun", variables]

useAdminCompanyDuplicateRunQuery.rootKey = "AdminCompanyDuplicateRun"

export const useSuspenseAdminCompanyDuplicateRunQuery = <
  TData = Types.AdminCompanyDuplicateRunQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminCompanyDuplicateRunQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyDuplicateRunQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompanyDuplicateRunQuery, TError, TData>({
    queryKey: ["AdminCompanyDuplicateRun", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicateRunQuery,
      Types.AdminCompanyDuplicateRunQueryVariables
    >(AdminCompanyDuplicateRunDocument, variables),
    ...options,
  })
}

useSuspenseAdminCompanyDuplicateRunQuery.getKey = (
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
) => ["AdminCompanyDuplicateRun", variables]

useSuspenseAdminCompanyDuplicateRunQuery.rootKey = "AdminCompanyDuplicateRun"

export const useInfiniteAdminCompanyDuplicateRunQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateRunQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminCompanyDuplicateRunQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateRunQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompanyDuplicateRunQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompanyDuplicateRun.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateRunQuery,
            Types.AdminCompanyDuplicateRunQueryVariables
          >(AdminCompanyDuplicateRunDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyDuplicateRunQuery.getKey = (
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
) => ["AdminCompanyDuplicateRun.infinite", variables]

useInfiniteAdminCompanyDuplicateRunQuery.rootKey = "AdminCompanyDuplicateRun.infinite"

export const useSuspenseInfiniteAdminCompanyDuplicateRunQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateRunQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminCompanyDuplicateRunQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateRunQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminCompanyDuplicateRunQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminCompanyDuplicateRun.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateRunQuery,
            Types.AdminCompanyDuplicateRunQueryVariables
          >(AdminCompanyDuplicateRunDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyDuplicateRunQuery.getKey = (
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
) => ["AdminCompanyDuplicateRun.infinite", variables]

useSuspenseInfiniteAdminCompanyDuplicateRunQuery.rootKey =
  "AdminCompanyDuplicateRun.infinite"

useAdminCompanyDuplicateRunQuery.fetcher = (
  variables: Types.AdminCompanyDuplicateRunQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompanyDuplicateRunQuery,
    Types.AdminCompanyDuplicateRunQueryVariables
  >(AdminCompanyDuplicateRunDocument, variables, options)

export const AdminCompanyDuplicateMergeEstimateDocument = new TypedDocumentString(`
    query AdminCompanyDuplicateMergeEstimate($autoMergeThreshold: Int, $deterministicAutoMerge: Boolean!, $anchoredAutoMergeFloor: Int) {
  admin {
    companyDuplicateMergeEstimate(
      autoMergeThreshold: $autoMergeThreshold
      deterministicAutoMerge: $deterministicAutoMerge
      anchoredAutoMergeFloor: $anchoredAutoMergeFloor
    )
    companyDuplicateJudgingEstimate {
      pairsToJudge
      costUsd
    }
  }
}
    `)

export const useAdminCompanyDuplicateMergeEstimateQuery = <
  TData = Types.AdminCompanyDuplicateMergeEstimateQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminCompanyDuplicateMergeEstimateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminCompanyDuplicateMergeEstimateQuery, TError, TData>({
    queryKey: ["AdminCompanyDuplicateMergeEstimate", variables],
    queryFn: publicFetcher<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      Types.AdminCompanyDuplicateMergeEstimateQueryVariables
    >(AdminCompanyDuplicateMergeEstimateDocument, variables),
    ...options,
  })
}

useAdminCompanyDuplicateMergeEstimateQuery.getKey = (
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
) => ["AdminCompanyDuplicateMergeEstimate", variables]

useAdminCompanyDuplicateMergeEstimateQuery.rootKey =
  "AdminCompanyDuplicateMergeEstimate"

export const useSuspenseAdminCompanyDuplicateMergeEstimateQuery = <
  TData = Types.AdminCompanyDuplicateMergeEstimateQuery,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminCompanyDuplicateMergeEstimateQuery, TError, TData>(
    {
      queryKey: ["AdminCompanyDuplicateMergeEstimate", variables],
      queryFn: publicFetcher<
        Types.AdminCompanyDuplicateMergeEstimateQuery,
        Types.AdminCompanyDuplicateMergeEstimateQueryVariables
      >(AdminCompanyDuplicateMergeEstimateDocument, variables),
      ...options,
    },
  )
}

useSuspenseAdminCompanyDuplicateMergeEstimateQuery.getKey = (
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
) => ["AdminCompanyDuplicateMergeEstimate", variables]

useSuspenseAdminCompanyDuplicateMergeEstimateQuery.rootKey =
  "AdminCompanyDuplicateMergeEstimate"

export const useInfiniteAdminCompanyDuplicateMergeEstimateQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateMergeEstimateQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminCompanyDuplicateMergeEstimateQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminCompanyDuplicateMergeEstimate.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateMergeEstimateQuery,
            Types.AdminCompanyDuplicateMergeEstimateQueryVariables
          >(AdminCompanyDuplicateMergeEstimateDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminCompanyDuplicateMergeEstimateQuery.getKey = (
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
) => ["AdminCompanyDuplicateMergeEstimate.infinite", variables]

useInfiniteAdminCompanyDuplicateMergeEstimateQuery.rootKey =
  "AdminCompanyDuplicateMergeEstimate.infinite"

export const useSuspenseInfiniteAdminCompanyDuplicateMergeEstimateQuery = <
  TData = InfiniteData<Types.AdminCompanyDuplicateMergeEstimateQuery>,
  TError = unknown,
>(
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminCompanyDuplicateMergeEstimateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminCompanyDuplicateMergeEstimateQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminCompanyDuplicateMergeEstimate.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminCompanyDuplicateMergeEstimateQuery,
            Types.AdminCompanyDuplicateMergeEstimateQueryVariables
          >(AdminCompanyDuplicateMergeEstimateDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminCompanyDuplicateMergeEstimateQuery.getKey = (
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
) => ["AdminCompanyDuplicateMergeEstimate.infinite", variables]

useSuspenseInfiniteAdminCompanyDuplicateMergeEstimateQuery.rootKey =
  "AdminCompanyDuplicateMergeEstimate.infinite"

useAdminCompanyDuplicateMergeEstimateQuery.fetcher = (
  variables: Types.AdminCompanyDuplicateMergeEstimateQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminCompanyDuplicateMergeEstimateQuery,
    Types.AdminCompanyDuplicateMergeEstimateQueryVariables
  >(AdminCompanyDuplicateMergeEstimateDocument, variables, options)

export const HeartbeatDocument = new TypedDocumentString(`
    mutation Heartbeat {
  heartbeat
}
    `)

export const useHeartbeatMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.HeartbeatMutation,
    TError,
    Types.HeartbeatMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.HeartbeatMutation,
    TError,
    Types.HeartbeatMutationVariables,
    TContext
  >({
    mutationKey: ["Heartbeat"],
    mutationFn: (variables?: Types.HeartbeatMutationVariables) =>
      publicFetcher<Types.HeartbeatMutation, Types.HeartbeatMutationVariables>(
        HeartbeatDocument,
        variables,
      )(),
    ...options,
  })
}

useHeartbeatMutation.fetcher = (
  variables?: Types.HeartbeatMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HeartbeatMutation, Types.HeartbeatMutationVariables>(
    HeartbeatDocument,
    variables,
    options,
  )

export const TrackEventDocument = new TypedDocumentString(`
    mutation TrackEvent($input: RecordUserEventInput!) {
  recordUserEvent(input: $input) {
    __typename
    message
  }
}
    `)

export const useTrackEventMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrackEventMutation,
    TError,
    Types.TrackEventMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrackEventMutation,
    TError,
    Types.TrackEventMutationVariables,
    TContext
  >({
    mutationKey: ["TrackEvent"],
    mutationFn: (variables?: Types.TrackEventMutationVariables) =>
      publicFetcher<Types.TrackEventMutation, Types.TrackEventMutationVariables>(
        TrackEventDocument,
        variables,
      )(),
    ...options,
  })
}

useTrackEventMutation.fetcher = (
  variables: Types.TrackEventMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TrackEventMutation, Types.TrackEventMutationVariables>(
    TrackEventDocument,
    variables,
    options,
  )

export const LogPageViewDocument = new TypedDocumentString(`
    mutation LogPageView($input: LogPageViewInput!) {
  logPageView(input: $input) {
    __typename
    message
  }
}
    `)

export const useLogPageViewMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.LogPageViewMutation,
    TError,
    Types.LogPageViewMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LogPageViewMutation,
    TError,
    Types.LogPageViewMutationVariables,
    TContext
  >({
    mutationKey: ["LogPageView"],
    mutationFn: (variables?: Types.LogPageViewMutationVariables) =>
      publicFetcher<Types.LogPageViewMutation, Types.LogPageViewMutationVariables>(
        LogPageViewDocument,
        variables,
      )(),
    ...options,
  })
}

useLogPageViewMutation.fetcher = (
  variables: Types.LogPageViewMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.LogPageViewMutation, Types.LogPageViewMutationVariables>(
    LogPageViewDocument,
    variables,
    options,
  )

export const InviteToOrgDocument = new TypedDocumentString(`
    mutation InviteToOrg($input: InviteToOrgInput!) {
  inviteToOrg(input: $input) {
    __typename
    pid
  }
}
    `)

export const useInviteToOrgMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToOrgMutation,
    TError,
    Types.InviteToOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToOrgMutation,
    TError,
    Types.InviteToOrgMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToOrg"],
    mutationFn: (variables?: Types.InviteToOrgMutationVariables) =>
      publicFetcher<Types.InviteToOrgMutation, Types.InviteToOrgMutationVariables>(
        InviteToOrgDocument,
        variables,
      )(),
    ...options,
  })
}

useInviteToOrgMutation.fetcher = (
  variables: Types.InviteToOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InviteToOrgMutation, Types.InviteToOrgMutationVariables>(
    InviteToOrgDocument,
    variables,
    options,
  )

export const InviteToPortfolioDocument = new TypedDocumentString(`
    mutation InviteToPortfolio($input: InviteToPortfolioInput!) {
  inviteToPortfolio(input: $input) {
    __typename
    ... on Portfolio {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `)

export const useInviteToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToPortfolio"],
    mutationFn: (variables?: Types.InviteToPortfolioMutationVariables) =>
      publicFetcher<
        Types.InviteToPortfolioMutation,
        Types.InviteToPortfolioMutationVariables
      >(InviteToPortfolioDocument, variables)(),
    ...options,
  })
}

useInviteToPortfolioMutation.fetcher = (
  variables: Types.InviteToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InviteToPortfolioMutation,
    Types.InviteToPortfolioMutationVariables
  >(InviteToPortfolioDocument, variables, options)

export const InviteToPortfoliosDocument = new TypedDocumentString(`
    mutation InviteToPortfolios($input: InviteToPortfoliosInput!) {
  inviteToPortfolios(input: $input) {
    __typename
    ... on AdminInvitation {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `)

export const useInviteToPortfoliosMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToPortfoliosMutation,
    TError,
    Types.InviteToPortfoliosMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToPortfoliosMutation,
    TError,
    Types.InviteToPortfoliosMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToPortfolios"],
    mutationFn: (variables?: Types.InviteToPortfoliosMutationVariables) =>
      publicFetcher<
        Types.InviteToPortfoliosMutation,
        Types.InviteToPortfoliosMutationVariables
      >(InviteToPortfoliosDocument, variables)(),
    ...options,
  })
}

useInviteToPortfoliosMutation.fetcher = (
  variables: Types.InviteToPortfoliosMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InviteToPortfoliosMutation,
    Types.InviteToPortfoliosMutationVariables
  >(InviteToPortfoliosDocument, variables, options)

export const CreateAndApplySiteLabelDocument = new TypedDocumentString(`
    mutation CreateAndApplySiteLabel($input: CreateAndApplySiteLabelInput!) {
  createAndApplySiteLabel(input: $input) {
    __typename
    ... on Error {
      message
    }
    ... on CreateAndApplySiteLabelOutput {
      label {
        ...SiteLabelDetail
      }
    }
  }
}
    fragment SiteLabelRef on SiteLabel {
  pid
  name
  themeColor
  isGlobal
  sites
}
fragment SiteLabelDetail on SiteLabel {
  ...SiteLabelRef
  createdBy {
    ...UserRef
  }
  perms {
    canAdd
    canRemove
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useCreateAndApplySiteLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["CreateAndApplySiteLabel"],
    mutationFn: (variables?: Types.CreateAndApplySiteLabelMutationVariables) =>
      publicFetcher<
        Types.CreateAndApplySiteLabelMutation,
        Types.CreateAndApplySiteLabelMutationVariables
      >(CreateAndApplySiteLabelDocument, variables)(),
    ...options,
  })
}

useCreateAndApplySiteLabelMutation.fetcher = (
  variables: Types.CreateAndApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateAndApplySiteLabelMutation,
    Types.CreateAndApplySiteLabelMutationVariables
  >(CreateAndApplySiteLabelDocument, variables, options)

export const ApplyLabelToSitesDocument = new TypedDocumentString(`
    mutation ApplyLabelToSites($input: ApplyLabelToSitesInput!) {
  applyLabelToSites(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `)

export const useApplyLabelToSitesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ApplyLabelToSitesMutation,
    TError,
    Types.ApplyLabelToSitesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApplyLabelToSitesMutation,
    TError,
    Types.ApplyLabelToSitesMutationVariables,
    TContext
  >({
    mutationKey: ["ApplyLabelToSites"],
    mutationFn: (variables?: Types.ApplyLabelToSitesMutationVariables) =>
      publicFetcher<
        Types.ApplyLabelToSitesMutation,
        Types.ApplyLabelToSitesMutationVariables
      >(ApplyLabelToSitesDocument, variables)(),
    ...options,
  })
}

useApplyLabelToSitesMutation.fetcher = (
  variables: Types.ApplyLabelToSitesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ApplyLabelToSitesMutation,
    Types.ApplyLabelToSitesMutationVariables
  >(ApplyLabelToSitesDocument, variables, options)

export const RemoveLabelFromSitesDocument = new TypedDocumentString(`
    mutation RemoveLabelFromSites($input: RemoveLabelFromSitesInput!) {
  removeLabelFromSites(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `)

export const useRemoveLabelFromSitesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveLabelFromSitesMutation,
    TError,
    Types.RemoveLabelFromSitesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveLabelFromSitesMutation,
    TError,
    Types.RemoveLabelFromSitesMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveLabelFromSites"],
    mutationFn: (variables?: Types.RemoveLabelFromSitesMutationVariables) =>
      publicFetcher<
        Types.RemoveLabelFromSitesMutation,
        Types.RemoveLabelFromSitesMutationVariables
      >(RemoveLabelFromSitesDocument, variables)(),
    ...options,
  })
}

useRemoveLabelFromSitesMutation.fetcher = (
  variables: Types.RemoveLabelFromSitesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveLabelFromSitesMutation,
    Types.RemoveLabelFromSitesMutationVariables
  >(RemoveLabelFromSitesDocument, variables, options)

export const DeleteSiteLabelDocument = new TypedDocumentString(`
    mutation DeleteSiteLabel($input: DeleteSiteLabelInput!) {
  deleteSiteLabel(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `)

export const useDeleteSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteSiteLabel"],
    mutationFn: (variables?: Types.DeleteSiteLabelMutationVariables) =>
      publicFetcher<
        Types.DeleteSiteLabelMutation,
        Types.DeleteSiteLabelMutationVariables
      >(DeleteSiteLabelDocument, variables)(),
    ...options,
  })
}

useDeleteSiteLabelMutation.fetcher = (
  variables: Types.DeleteSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteSiteLabelMutation, Types.DeleteSiteLabelMutationVariables>(
    DeleteSiteLabelDocument,
    variables,
    options,
  )

export const UploadDocumentDocument = new TypedDocumentString(`
    mutation UploadDocument($file: Upload!, $tag: DocumentTag, $associations: DocumentAssociationsInput!) {
  documents {
    uploadDocument(upload: $file, tag: $tag, associations: $associations) {
      __typename
      ... on Document {
        ...Document
        csvPeek
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useUploadDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UploadDocumentMutation,
    TError,
    Types.UploadDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UploadDocumentMutation,
    TError,
    Types.UploadDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["UploadDocument"],
    mutationFn: (variables?: Types.UploadDocumentMutationVariables) =>
      publicFetcher<
        Types.UploadDocumentMutation,
        Types.UploadDocumentMutationVariables
      >(UploadDocumentDocument, variables)(),
    ...options,
  })
}

useUploadDocumentMutation.fetcher = (
  variables: Types.UploadDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UploadDocumentMutation, Types.UploadDocumentMutationVariables>(
    UploadDocumentDocument,
    variables,
    options,
  )

export const CreateDocumentUploadDocument = new TypedDocumentString(`
    mutation CreateDocumentUpload($associations: DocumentAssociationsInput!) {
  documents {
    createDocumentUpload(associations: $associations) {
      __typename
      ... on CreateDocumentUpload {
        uploadUrl
        uploadId
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useCreateDocumentUploadMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateDocumentUploadMutation,
    TError,
    Types.CreateDocumentUploadMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateDocumentUploadMutation,
    TError,
    Types.CreateDocumentUploadMutationVariables,
    TContext
  >({
    mutationKey: ["CreateDocumentUpload"],
    mutationFn: (variables?: Types.CreateDocumentUploadMutationVariables) =>
      publicFetcher<
        Types.CreateDocumentUploadMutation,
        Types.CreateDocumentUploadMutationVariables
      >(CreateDocumentUploadDocument, variables)(),
    ...options,
  })
}

useCreateDocumentUploadMutation.fetcher = (
  variables: Types.CreateDocumentUploadMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateDocumentUploadMutation,
    Types.CreateDocumentUploadMutationVariables
  >(CreateDocumentUploadDocument, variables, options)

export const FinalizeDocumentUploadDocument = new TypedDocumentString(`
    mutation FinalizeDocumentUpload($uploadId: String!, $fileName: String!, $tag: DocumentTag, $associations: DocumentAssociationsInput!) {
  documents {
    finalizeDocumentUpload(
      uploadId: $uploadId
      fileName: $fileName
      tag: $tag
      associations: $associations
    ) {
      __typename
      ... on Document {
        ...Document
        csvPeek
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useFinalizeDocumentUploadMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinalizeDocumentUploadMutation,
    TError,
    Types.FinalizeDocumentUploadMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinalizeDocumentUploadMutation,
    TError,
    Types.FinalizeDocumentUploadMutationVariables,
    TContext
  >({
    mutationKey: ["FinalizeDocumentUpload"],
    mutationFn: (variables?: Types.FinalizeDocumentUploadMutationVariables) =>
      publicFetcher<
        Types.FinalizeDocumentUploadMutation,
        Types.FinalizeDocumentUploadMutationVariables
      >(FinalizeDocumentUploadDocument, variables)(),
    ...options,
  })
}

useFinalizeDocumentUploadMutation.fetcher = (
  variables: Types.FinalizeDocumentUploadMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinalizeDocumentUploadMutation,
    Types.FinalizeDocumentUploadMutationVariables
  >(FinalizeDocumentUploadDocument, variables, options)

export const UploadStagingDocumentDocument = new TypedDocumentString(`
    mutation UploadStagingDocument($file: Upload!, $orgPid: String!) {
  documents {
    uploadStagingDocument(upload: $file, orgPid: $orgPid) {
      __typename
      ... on StagingUploadResult {
        outcome
        fileName
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useUploadStagingDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UploadStagingDocumentMutation,
    TError,
    Types.UploadStagingDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UploadStagingDocumentMutation,
    TError,
    Types.UploadStagingDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["UploadStagingDocument"],
    mutationFn: (variables?: Types.UploadStagingDocumentMutationVariables) =>
      publicFetcher<
        Types.UploadStagingDocumentMutation,
        Types.UploadStagingDocumentMutationVariables
      >(UploadStagingDocumentDocument, variables)(),
    ...options,
  })
}

useUploadStagingDocumentMutation.fetcher = (
  variables: Types.UploadStagingDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UploadStagingDocumentMutation,
    Types.UploadStagingDocumentMutationVariables
  >(UploadStagingDocumentDocument, variables, options)

export const DeleteDocumentDocument = new TypedDocumentString(`
    mutation DeleteDocument($documentPid: String!) {
  documents {
    deleteDocument(documentPid: $documentPid) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useDeleteDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteDocumentMutation,
    TError,
    Types.DeleteDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteDocumentMutation,
    TError,
    Types.DeleteDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteDocument"],
    mutationFn: (variables?: Types.DeleteDocumentMutationVariables) =>
      publicFetcher<
        Types.DeleteDocumentMutation,
        Types.DeleteDocumentMutationVariables
      >(DeleteDocumentDocument, variables)(),
    ...options,
  })
}

useDeleteDocumentMutation.fetcher = (
  variables: Types.DeleteDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteDocumentMutation, Types.DeleteDocumentMutationVariables>(
    DeleteDocumentDocument,
    variables,
    options,
  )

export const RemoveDocumentFromTariffDocument = new TypedDocumentString(`
    mutation RemoveDocumentFromTariff($documentPid: String!, $tariffPid: String!) {
  documents {
    removeDocumentFromTariff(documentPid: $documentPid, tariffPid: $tariffPid) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useRemoveDocumentFromTariffMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveDocumentFromTariffMutation,
    TError,
    Types.RemoveDocumentFromTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveDocumentFromTariffMutation,
    TError,
    Types.RemoveDocumentFromTariffMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveDocumentFromTariff"],
    mutationFn: (variables?: Types.RemoveDocumentFromTariffMutationVariables) =>
      publicFetcher<
        Types.RemoveDocumentFromTariffMutation,
        Types.RemoveDocumentFromTariffMutationVariables
      >(RemoveDocumentFromTariffDocument, variables)(),
    ...options,
  })
}

useRemoveDocumentFromTariffMutation.fetcher = (
  variables: Types.RemoveDocumentFromTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveDocumentFromTariffMutation,
    Types.RemoveDocumentFromTariffMutationVariables
  >(RemoveDocumentFromTariffDocument, variables, options)

export const RemoveDocumentFromUtilityDocument = new TypedDocumentString(`
    mutation RemoveDocumentFromUtility($documentPid: String!, $utilityPid: String!) {
  documents {
    removeDocumentFromUtility(documentPid: $documentPid, utilityPid: $utilityPid) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useRemoveDocumentFromUtilityMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveDocumentFromUtilityMutation,
    TError,
    Types.RemoveDocumentFromUtilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveDocumentFromUtilityMutation,
    TError,
    Types.RemoveDocumentFromUtilityMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveDocumentFromUtility"],
    mutationFn: (variables?: Types.RemoveDocumentFromUtilityMutationVariables) =>
      publicFetcher<
        Types.RemoveDocumentFromUtilityMutation,
        Types.RemoveDocumentFromUtilityMutationVariables
      >(RemoveDocumentFromUtilityDocument, variables)(),
    ...options,
  })
}

useRemoveDocumentFromUtilityMutation.fetcher = (
  variables: Types.RemoveDocumentFromUtilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveDocumentFromUtilityMutation,
    Types.RemoveDocumentFromUtilityMutationVariables
  >(RemoveDocumentFromUtilityDocument, variables, options)

export const TrashStagingDocumentsDocument = new TypedDocumentString(`
    mutation TrashStagingDocuments($orgPid: String!) {
  documents {
    trashStagingDocuments(orgPid: $orgPid)
  }
}
    `)

export const useTrashStagingDocumentsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashStagingDocumentsMutation,
    TError,
    Types.TrashStagingDocumentsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashStagingDocumentsMutation,
    TError,
    Types.TrashStagingDocumentsMutationVariables,
    TContext
  >({
    mutationKey: ["TrashStagingDocuments"],
    mutationFn: (variables?: Types.TrashStagingDocumentsMutationVariables) =>
      publicFetcher<
        Types.TrashStagingDocumentsMutation,
        Types.TrashStagingDocumentsMutationVariables
      >(TrashStagingDocumentsDocument, variables)(),
    ...options,
  })
}

useTrashStagingDocumentsMutation.fetcher = (
  variables: Types.TrashStagingDocumentsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TrashStagingDocumentsMutation,
    Types.TrashStagingDocumentsMutationVariables
  >(TrashStagingDocumentsDocument, variables, options)

export const SetDocumentTagDocument = new TypedDocumentString(`
    mutation SetDocumentTag($pid: String!, $tag: DocumentTag!) {
  documents {
    setDocumentTag(pid: $pid, tag: $tag) {
      __typename
      ... on Document {
        pid
        tag
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useSetDocumentTagMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetDocumentTagMutation,
    TError,
    Types.SetDocumentTagMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetDocumentTagMutation,
    TError,
    Types.SetDocumentTagMutationVariables,
    TContext
  >({
    mutationKey: ["SetDocumentTag"],
    mutationFn: (variables?: Types.SetDocumentTagMutationVariables) =>
      publicFetcher<
        Types.SetDocumentTagMutation,
        Types.SetDocumentTagMutationVariables
      >(SetDocumentTagDocument, variables)(),
    ...options,
  })
}

useSetDocumentTagMutation.fetcher = (
  variables: Types.SetDocumentTagMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetDocumentTagMutation, Types.SetDocumentTagMutationVariables>(
    SetDocumentTagDocument,
    variables,
    options,
  )

export const MoveDocumentToProjectDocument = new TypedDocumentString(`
    mutation MoveDocumentToProject($documentPid: String!, $projectPid: String!) {
  documents {
    moveDocumentToProject(documentPid: $documentPid, projectPid: $projectPid) {
      __typename
      ... on Document {
        ...Document
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useMoveDocumentToProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.MoveDocumentToProjectMutation,
    TError,
    Types.MoveDocumentToProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MoveDocumentToProjectMutation,
    TError,
    Types.MoveDocumentToProjectMutationVariables,
    TContext
  >({
    mutationKey: ["MoveDocumentToProject"],
    mutationFn: (variables?: Types.MoveDocumentToProjectMutationVariables) =>
      publicFetcher<
        Types.MoveDocumentToProjectMutation,
        Types.MoveDocumentToProjectMutationVariables
      >(MoveDocumentToProjectDocument, variables)(),
    ...options,
  })
}

useMoveDocumentToProjectMutation.fetcher = (
  variables: Types.MoveDocumentToProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MoveDocumentToProjectMutation,
    Types.MoveDocumentToProjectMutationVariables
  >(MoveDocumentToProjectDocument, variables, options)

export const RemoveDocumentFromProjectDocument = new TypedDocumentString(`
    mutation RemoveDocumentFromProject($documentPid: String!, $projectPid: String!) {
  documents {
    removeDocumentFromProject(documentPid: $documentPid, projectPid: $projectPid) {
      __typename
      ... on Document {
        ...Document
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useRemoveDocumentFromProjectMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveDocumentFromProjectMutation,
    TError,
    Types.RemoveDocumentFromProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveDocumentFromProjectMutation,
    TError,
    Types.RemoveDocumentFromProjectMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveDocumentFromProject"],
    mutationFn: (variables?: Types.RemoveDocumentFromProjectMutationVariables) =>
      publicFetcher<
        Types.RemoveDocumentFromProjectMutation,
        Types.RemoveDocumentFromProjectMutationVariables
      >(RemoveDocumentFromProjectDocument, variables)(),
    ...options,
  })
}

useRemoveDocumentFromProjectMutation.fetcher = (
  variables: Types.RemoveDocumentFromProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveDocumentFromProjectMutation,
    Types.RemoveDocumentFromProjectMutationVariables
  >(RemoveDocumentFromProjectDocument, variables, options)

export const MoveDocumentToSiteDocument = new TypedDocumentString(`
    mutation MoveDocumentToSite($documentPid: String!, $sitePid: String!, $projectPid: String!) {
  documents {
    moveDocumentToSite(
      documentPid: $documentPid
      sitePid: $sitePid
      projectPid: $projectPid
    ) {
      __typename
      ... on Document {
        ...Document
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useMoveDocumentToSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.MoveDocumentToSiteMutation,
    TError,
    Types.MoveDocumentToSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MoveDocumentToSiteMutation,
    TError,
    Types.MoveDocumentToSiteMutationVariables,
    TContext
  >({
    mutationKey: ["MoveDocumentToSite"],
    mutationFn: (variables?: Types.MoveDocumentToSiteMutationVariables) =>
      publicFetcher<
        Types.MoveDocumentToSiteMutation,
        Types.MoveDocumentToSiteMutationVariables
      >(MoveDocumentToSiteDocument, variables)(),
    ...options,
  })
}

useMoveDocumentToSiteMutation.fetcher = (
  variables: Types.MoveDocumentToSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MoveDocumentToSiteMutation,
    Types.MoveDocumentToSiteMutationVariables
  >(MoveDocumentToSiteDocument, variables, options)

export const SetDeveloperPlatformStatusDocument = new TypedDocumentString(`
    mutation SetDeveloperPlatformStatus($orgPid: String!, $status: PlatformStatus!) {
  documents {
    setDeveloperPlatformStatus(orgPid: $orgPid, status: $status)
  }
}
    `)

export const useSetDeveloperPlatformStatusMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetDeveloperPlatformStatusMutation,
    TError,
    Types.SetDeveloperPlatformStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetDeveloperPlatformStatusMutation,
    TError,
    Types.SetDeveloperPlatformStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetDeveloperPlatformStatus"],
    mutationFn: (variables?: Types.SetDeveloperPlatformStatusMutationVariables) =>
      publicFetcher<
        Types.SetDeveloperPlatformStatusMutation,
        Types.SetDeveloperPlatformStatusMutationVariables
      >(SetDeveloperPlatformStatusDocument, variables)(),
    ...options,
  })
}

useSetDeveloperPlatformStatusMutation.fetcher = (
  variables: Types.SetDeveloperPlatformStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetDeveloperPlatformStatusMutation,
    Types.SetDeveloperPlatformStatusMutationVariables
  >(SetDeveloperPlatformStatusDocument, variables, options)

export const SetDeveloperOsaVariantDocument = new TypedDocumentString(`
    mutation SetDeveloperOsaVariant($orgPid: String!, $variant: OsaVariant!) {
  documents {
    setDeveloperOsaVariant(orgPid: $orgPid, variant: $variant)
  }
}
    `)

export const useSetDeveloperOsaVariantMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetDeveloperOsaVariantMutation,
    TError,
    Types.SetDeveloperOsaVariantMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetDeveloperOsaVariantMutation,
    TError,
    Types.SetDeveloperOsaVariantMutationVariables,
    TContext
  >({
    mutationKey: ["SetDeveloperOsaVariant"],
    mutationFn: (variables?: Types.SetDeveloperOsaVariantMutationVariables) =>
      publicFetcher<
        Types.SetDeveloperOsaVariantMutation,
        Types.SetDeveloperOsaVariantMutationVariables
      >(SetDeveloperOsaVariantDocument, variables)(),
    ...options,
  })
}

useSetDeveloperOsaVariantMutation.fetcher = (
  variables: Types.SetDeveloperOsaVariantMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetDeveloperOsaVariantMutation,
    Types.SetDeveloperOsaVariantMutationVariables
  >(SetDeveloperOsaVariantDocument, variables, options)

export const BundleRemoveProjectDocument = new TypedDocumentString(`
    mutation BundleRemoveProject($orgSlug: String!, $bundlePid: String!, $projectPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        removeProject(projectPid: $projectPid) {
          message
        }
      }
    }
  }
}
    `)

export const useBundleRemoveProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleRemoveProjectMutation,
    TError,
    Types.BundleRemoveProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleRemoveProjectMutation,
    TError,
    Types.BundleRemoveProjectMutationVariables,
    TContext
  >({
    mutationKey: ["BundleRemoveProject"],
    mutationFn: (variables?: Types.BundleRemoveProjectMutationVariables) =>
      publicFetcher<
        Types.BundleRemoveProjectMutation,
        Types.BundleRemoveProjectMutationVariables
      >(BundleRemoveProjectDocument, variables)(),
    ...options,
  })
}

useBundleRemoveProjectMutation.fetcher = (
  variables: Types.BundleRemoveProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleRemoveProjectMutation,
    Types.BundleRemoveProjectMutationVariables
  >(BundleRemoveProjectDocument, variables, options)

export const BundleArchiveBundleDocument = new TypedDocumentString(`
    mutation BundleArchiveBundle($orgSlug: String!, $bundlePid: String!, $archive: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        archiveBundle(archive: $archive)
      }
    }
  }
}
    `)

export const useBundleArchiveBundleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleArchiveBundleMutation,
    TError,
    Types.BundleArchiveBundleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleArchiveBundleMutation,
    TError,
    Types.BundleArchiveBundleMutationVariables,
    TContext
  >({
    mutationKey: ["BundleArchiveBundle"],
    mutationFn: (variables?: Types.BundleArchiveBundleMutationVariables) =>
      publicFetcher<
        Types.BundleArchiveBundleMutation,
        Types.BundleArchiveBundleMutationVariables
      >(BundleArchiveBundleDocument, variables)(),
    ...options,
  })
}

useBundleArchiveBundleMutation.fetcher = (
  variables: Types.BundleArchiveBundleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleArchiveBundleMutation,
    Types.BundleArchiveBundleMutationVariables
  >(BundleArchiveBundleDocument, variables, options)

export const SetMeteringDocument = new TypedDocumentString(`
    mutation SetMetering($input: SetMeteringInput!) {
  setMetering(input: $input)
}
    `)

export const useSetMeteringMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >({
    mutationKey: ["SetMetering"],
    mutationFn: (variables?: Types.SetMeteringMutationVariables) =>
      publicFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
        SetMeteringDocument,
        variables,
      )(),
    ...options,
  })
}

useSetMeteringMutation.fetcher = (
  variables: Types.SetMeteringMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
    SetMeteringDocument,
    variables,
    options,
  )

export const SaveSiteShapesDocument = new TypedDocumentString(`
    mutation SaveSiteShapes($sitePid: String!, $input: SaveSiteShapesInput!) {
  site(sitePid: $sitePid) {
    saveSiteShapes(input: $input)
  }
}
    `)

export const useSaveSiteShapesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SaveSiteShapesMutation,
    TError,
    Types.SaveSiteShapesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SaveSiteShapesMutation,
    TError,
    Types.SaveSiteShapesMutationVariables,
    TContext
  >({
    mutationKey: ["SaveSiteShapes"],
    mutationFn: (variables?: Types.SaveSiteShapesMutationVariables) =>
      publicFetcher<
        Types.SaveSiteShapesMutation,
        Types.SaveSiteShapesMutationVariables
      >(SaveSiteShapesDocument, variables)(),
    ...options,
  })
}

useSaveSiteShapesMutation.fetcher = (
  variables: Types.SaveSiteShapesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SaveSiteShapesMutation, Types.SaveSiteShapesMutationVariables>(
    SaveSiteShapesDocument,
    variables,
    options,
  )

export const UpdatePropertyDataDocument = new TypedDocumentString(`
    mutation UpdatePropertyData($input: UpdatePropertyDataInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  updatePropertyData(input: $input, sourceType: $sourceType, sourceId: $sourceId)
}
    `)

export const useUpdatePropertyDataMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdatePropertyDataMutation,
    TError,
    Types.UpdatePropertyDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdatePropertyDataMutation,
    TError,
    Types.UpdatePropertyDataMutationVariables,
    TContext
  >({
    mutationKey: ["UpdatePropertyData"],
    mutationFn: (variables?: Types.UpdatePropertyDataMutationVariables) =>
      publicFetcher<
        Types.UpdatePropertyDataMutation,
        Types.UpdatePropertyDataMutationVariables
      >(UpdatePropertyDataDocument, variables)(),
    ...options,
  })
}

useUpdatePropertyDataMutation.fetcher = (
  variables: Types.UpdatePropertyDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdatePropertyDataMutation,
    Types.UpdatePropertyDataMutationVariables
  >(UpdatePropertyDataDocument, variables, options)

export const BulkUpdatePropertyDataDocument = new TypedDocumentString(`
    mutation BulkUpdatePropertyData($input: BulkUpdatePropertyDataInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  bulkUpdatePropertyData(
    input: $input
    sourceType: $sourceType
    sourceId: $sourceId
  )
}
    `)

export const useBulkUpdatePropertyDataMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BulkUpdatePropertyDataMutation,
    TError,
    Types.BulkUpdatePropertyDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BulkUpdatePropertyDataMutation,
    TError,
    Types.BulkUpdatePropertyDataMutationVariables,
    TContext
  >({
    mutationKey: ["BulkUpdatePropertyData"],
    mutationFn: (variables?: Types.BulkUpdatePropertyDataMutationVariables) =>
      publicFetcher<
        Types.BulkUpdatePropertyDataMutation,
        Types.BulkUpdatePropertyDataMutationVariables
      >(BulkUpdatePropertyDataDocument, variables)(),
    ...options,
  })
}

useBulkUpdatePropertyDataMutation.fetcher = (
  variables: Types.BulkUpdatePropertyDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BulkUpdatePropertyDataMutation,
    Types.BulkUpdatePropertyDataMutationVariables
  >(BulkUpdatePropertyDataDocument, variables, options)

export const SetSiteStatusDocument = new TypedDocumentString(`
    mutation SetSiteStatus($orgSlug: String!, $sitePids: [String!]!, $status: SiteStatus!, $reason: StatusReason, $customReason: String, $solarLabelSource: HumanSolarLabelSource) {
  orgBySlug(slug: $orgSlug) {
    setStatus(
      status: $status
      sitePids: $sitePids
      reason: $reason
      customReason: $customReason
      solarLabelSource: $solarLabelSource
    ) {
      message
    }
  }
}
    `)

export const useSetSiteStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteStatus"],
    mutationFn: (variables?: Types.SetSiteStatusMutationVariables) =>
      publicFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
        SetSiteStatusDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSiteStatusMutation.fetcher = (
  variables: Types.SetSiteStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
    SetSiteStatusDocument,
    variables,
    options,
  )

export const NewCsvImportDocument = new TypedDocumentString(`
    mutation NewCsvImport($input: NewCsvImportInput!) {
  newCsvImport(input: $input) {
    __typename
    ... on CsvImport {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `)

export const useNewCsvImportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.NewCsvImportMutation,
    TError,
    Types.NewCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.NewCsvImportMutation,
    TError,
    Types.NewCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["NewCsvImport"],
    mutationFn: (variables?: Types.NewCsvImportMutationVariables) =>
      publicFetcher<Types.NewCsvImportMutation, Types.NewCsvImportMutationVariables>(
        NewCsvImportDocument,
        variables,
      )(),
    ...options,
  })
}

useNewCsvImportMutation.fetcher = (
  variables: Types.NewCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.NewCsvImportMutation, Types.NewCsvImportMutationVariables>(
    NewCsvImportDocument,
    variables,
    options,
  )

export const SetCsvImportSchemaDocument = new TypedDocumentString(`
    mutation SetCsvImportSchema($csvImportPid: String!, $schemaPid: String!) {
  setCsvImportSchema(csvImportPid: $csvImportPid, schemaPid: $schemaPid) {
    __typename
  }
}
    `)

export const useSetCsvImportSchemaMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCsvImportSchemaMutation,
    TError,
    Types.SetCsvImportSchemaMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCsvImportSchemaMutation,
    TError,
    Types.SetCsvImportSchemaMutationVariables,
    TContext
  >({
    mutationKey: ["SetCsvImportSchema"],
    mutationFn: (variables?: Types.SetCsvImportSchemaMutationVariables) =>
      publicFetcher<
        Types.SetCsvImportSchemaMutation,
        Types.SetCsvImportSchemaMutationVariables
      >(SetCsvImportSchemaDocument, variables)(),
    ...options,
  })
}

useSetCsvImportSchemaMutation.fetcher = (
  variables: Types.SetCsvImportSchemaMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCsvImportSchemaMutation,
    Types.SetCsvImportSchemaMutationVariables
  >(SetCsvImportSchemaDocument, variables, options)

export const UpdateCsvImportColumnMappingsDocument = new TypedDocumentString(`
    mutation UpdateCsvImportColumnMappings($csvImportPid: String!, $columnMappings: ColumnMappingsInput!) {
  updateCsvImportColumnMappings(
    pid: $csvImportPid
    columnMappings: $columnMappings
  ) {
    __typename
    ... on CsvImport {
      pid
      buildingTypes
      guessedBuildingTypeMappings {
        office
        multifamily
        hotel
        hospital
        outpatient
        retailStandalone
        retailStripMall
        quickServiceRestaurant
        fullServiceRestaurant
        primarySchool
        secondarySchool
        singleFamilyDetached
        warehouse
        dataCenter
        coldStorage
        industrial
        parkingLot
        supermarket
      }
    }
    ... on Error {
      message
    }
  }
}
    `)

export const useUpdateCsvImportColumnMappingsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateCsvImportColumnMappingsMutation,
    TError,
    Types.UpdateCsvImportColumnMappingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateCsvImportColumnMappingsMutation,
    TError,
    Types.UpdateCsvImportColumnMappingsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateCsvImportColumnMappings"],
    mutationFn: (variables?: Types.UpdateCsvImportColumnMappingsMutationVariables) =>
      publicFetcher<
        Types.UpdateCsvImportColumnMappingsMutation,
        Types.UpdateCsvImportColumnMappingsMutationVariables
      >(UpdateCsvImportColumnMappingsDocument, variables)(),
    ...options,
  })
}

useUpdateCsvImportColumnMappingsMutation.fetcher = (
  variables: Types.UpdateCsvImportColumnMappingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateCsvImportColumnMappingsMutation,
    Types.UpdateCsvImportColumnMappingsMutationVariables
  >(UpdateCsvImportColumnMappingsDocument, variables, options)

export const AddLabelToCsvImportDocument = new TypedDocumentString(`
    mutation AddLabelToCsvImport($csvImportPid: String!, $labelPid: String!) {
  addLabelToCsvImport(csvImportPid: $csvImportPid, labelPid: $labelPid) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    fragment CsvImportLabels on CsvImport {
  pid
  labels {
    pid
    themeColor
    name
  }
}`)

export const useAddLabelToCsvImportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddLabelToCsvImportMutation,
    TError,
    Types.AddLabelToCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddLabelToCsvImportMutation,
    TError,
    Types.AddLabelToCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["AddLabelToCsvImport"],
    mutationFn: (variables?: Types.AddLabelToCsvImportMutationVariables) =>
      publicFetcher<
        Types.AddLabelToCsvImportMutation,
        Types.AddLabelToCsvImportMutationVariables
      >(AddLabelToCsvImportDocument, variables)(),
    ...options,
  })
}

useAddLabelToCsvImportMutation.fetcher = (
  variables: Types.AddLabelToCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddLabelToCsvImportMutation,
    Types.AddLabelToCsvImportMutationVariables
  >(AddLabelToCsvImportDocument, variables, options)

export const RemoveLabelFromCsvImportDocument = new TypedDocumentString(`
    mutation RemoveLabelFromCsvImport($csvImportPid: String!, $labelPid: String!) {
  removeLabelFromCsvImport(csvImportPid: $csvImportPid, labelPid: $labelPid) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    fragment CsvImportLabels on CsvImport {
  pid
  labels {
    pid
    themeColor
    name
  }
}`)

export const useRemoveLabelFromCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveLabelFromCsvImportMutation,
    TError,
    Types.RemoveLabelFromCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveLabelFromCsvImportMutation,
    TError,
    Types.RemoveLabelFromCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveLabelFromCsvImport"],
    mutationFn: (variables?: Types.RemoveLabelFromCsvImportMutationVariables) =>
      publicFetcher<
        Types.RemoveLabelFromCsvImportMutation,
        Types.RemoveLabelFromCsvImportMutationVariables
      >(RemoveLabelFromCsvImportDocument, variables)(),
    ...options,
  })
}

useRemoveLabelFromCsvImportMutation.fetcher = (
  variables: Types.RemoveLabelFromCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveLabelFromCsvImportMutation,
    Types.RemoveLabelFromCsvImportMutationVariables
  >(RemoveLabelFromCsvImportDocument, variables, options)

export const CreateLabelForCsvImportDocument = new TypedDocumentString(`
    mutation CreateLabelForCsvImport($csvImportPid: String!, $labelName: String!, $labelColor: LabelColor) {
  createAndApplyLabelToCsvImport(
    csvImportPid: $csvImportPid
    labelName: $labelName
    labelColor: $labelColor
  ) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    fragment CsvImportLabels on CsvImport {
  pid
  labels {
    pid
    themeColor
    name
  }
}`)

export const useCreateLabelForCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateLabelForCsvImportMutation,
    TError,
    Types.CreateLabelForCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateLabelForCsvImportMutation,
    TError,
    Types.CreateLabelForCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["CreateLabelForCsvImport"],
    mutationFn: (variables?: Types.CreateLabelForCsvImportMutationVariables) =>
      publicFetcher<
        Types.CreateLabelForCsvImportMutation,
        Types.CreateLabelForCsvImportMutationVariables
      >(CreateLabelForCsvImportDocument, variables)(),
    ...options,
  })
}

useCreateLabelForCsvImportMutation.fetcher = (
  variables: Types.CreateLabelForCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateLabelForCsvImportMutation,
    Types.CreateLabelForCsvImportMutationVariables
  >(CreateLabelForCsvImportDocument, variables, options)

export const DeleteLabelForCsvImportDocument = new TypedDocumentString(`
    mutation DeleteLabelForCsvImport($csvImportPid: String!, $labelPid: String!) {
  deleteLabelAndRemoveFromCsvImport(
    csvImportPid: $csvImportPid
    labelPid: $labelPid
  ) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    fragment CsvImportLabels on CsvImport {
  pid
  labels {
    pid
    themeColor
    name
  }
}`)

export const useDeleteLabelForCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.DeleteLabelForCsvImportMutation,
    TError,
    Types.DeleteLabelForCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteLabelForCsvImportMutation,
    TError,
    Types.DeleteLabelForCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteLabelForCsvImport"],
    mutationFn: (variables?: Types.DeleteLabelForCsvImportMutationVariables) =>
      publicFetcher<
        Types.DeleteLabelForCsvImportMutation,
        Types.DeleteLabelForCsvImportMutationVariables
      >(DeleteLabelForCsvImportDocument, variables)(),
    ...options,
  })
}

useDeleteLabelForCsvImportMutation.fetcher = (
  variables: Types.DeleteLabelForCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteLabelForCsvImportMutation,
    Types.DeleteLabelForCsvImportMutationVariables
  >(DeleteLabelForCsvImportDocument, variables, options)

export const UpdateBuildingTypeMappingsDocument = new TypedDocumentString(`
    mutation UpdateBuildingTypeMappings($csvImportPid: String!, $mappings: BuildingTypeMappingsInput!) {
  updateBuildingTypeMappings(csvImportPid: $csvImportPid, mappings: $mappings) {
    __typename
    ... on CsvImport {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `)

export const useUpdateBuildingTypeMappingsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateBuildingTypeMappingsMutation,
    TError,
    Types.UpdateBuildingTypeMappingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateBuildingTypeMappingsMutation,
    TError,
    Types.UpdateBuildingTypeMappingsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateBuildingTypeMappings"],
    mutationFn: (variables?: Types.UpdateBuildingTypeMappingsMutationVariables) =>
      publicFetcher<
        Types.UpdateBuildingTypeMappingsMutation,
        Types.UpdateBuildingTypeMappingsMutationVariables
      >(UpdateBuildingTypeMappingsDocument, variables)(),
    ...options,
  })
}

useUpdateBuildingTypeMappingsMutation.fetcher = (
  variables: Types.UpdateBuildingTypeMappingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateBuildingTypeMappingsMutation,
    Types.UpdateBuildingTypeMappingsMutationVariables
  >(UpdateBuildingTypeMappingsDocument, variables, options)

export const DropCsvImportRowDocument = new TypedDocumentString(`
    mutation DropCsvImportRow($rowId: Int!) {
  dropCsvImportRow(rowId: $rowId) {
    id
    droppedAt
  }
}
    `)

export const useDropCsvImportRowMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DropCsvImportRowMutation,
    TError,
    Types.DropCsvImportRowMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DropCsvImportRowMutation,
    TError,
    Types.DropCsvImportRowMutationVariables,
    TContext
  >({
    mutationKey: ["DropCsvImportRow"],
    mutationFn: (variables?: Types.DropCsvImportRowMutationVariables) =>
      publicFetcher<
        Types.DropCsvImportRowMutation,
        Types.DropCsvImportRowMutationVariables
      >(DropCsvImportRowDocument, variables)(),
    ...options,
  })
}

useDropCsvImportRowMutation.fetcher = (
  variables: Types.DropCsvImportRowMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DropCsvImportRowMutation,
    Types.DropCsvImportRowMutationVariables
  >(DropCsvImportRowDocument, variables, options)

export const AmendCsvImportRowDocument = new TypedDocumentString(`
    mutation AmendCsvImportRow($rowId: Int!, $amendments: BuildingDataAmendmentsInput!) {
  amendCsvImportRow(rowId: $rowId, amendments: $amendments) {
    id
    amendments {
      assetManager
      portfolioManager
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
  }
}
    `)

export const useAmendCsvImportRowMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AmendCsvImportRowMutation,
    TError,
    Types.AmendCsvImportRowMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AmendCsvImportRowMutation,
    TError,
    Types.AmendCsvImportRowMutationVariables,
    TContext
  >({
    mutationKey: ["AmendCsvImportRow"],
    mutationFn: (variables?: Types.AmendCsvImportRowMutationVariables) =>
      publicFetcher<
        Types.AmendCsvImportRowMutation,
        Types.AmendCsvImportRowMutationVariables
      >(AmendCsvImportRowDocument, variables)(),
    ...options,
  })
}

useAmendCsvImportRowMutation.fetcher = (
  variables: Types.AmendCsvImportRowMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AmendCsvImportRowMutation,
    Types.AmendCsvImportRowMutationVariables
  >(AmendCsvImportRowDocument, variables, options)

export const SetCsvImportRowBuildingDataDocument = new TypedDocumentString(`
    mutation SetCsvImportRowBuildingData($rowId: Int!, $buildingDataId: Int!) {
  setCsvImportRowBuildingData(rowId: $rowId, buildingDataId: $buildingDataId) {
    id
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
  }
}
    `)

export const useSetCsvImportRowBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCsvImportRowBuildingDataMutation,
    TError,
    Types.SetCsvImportRowBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCsvImportRowBuildingDataMutation,
    TError,
    Types.SetCsvImportRowBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["SetCsvImportRowBuildingData"],
    mutationFn: (variables?: Types.SetCsvImportRowBuildingDataMutationVariables) =>
      publicFetcher<
        Types.SetCsvImportRowBuildingDataMutation,
        Types.SetCsvImportRowBuildingDataMutationVariables
      >(SetCsvImportRowBuildingDataDocument, variables)(),
    ...options,
  })
}

useSetCsvImportRowBuildingDataMutation.fetcher = (
  variables: Types.SetCsvImportRowBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCsvImportRowBuildingDataMutation,
    Types.SetCsvImportRowBuildingDataMutationVariables
  >(SetCsvImportRowBuildingDataDocument, variables, options)

export const UnsetCsvImportRowBuildingDataDocument = new TypedDocumentString(`
    mutation UnsetCsvImportRowBuildingData($rowId: Int!) {
  unsetCsvImportRowBuildingData(rowId: $rowId) {
    id
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    amendments {
      assetManager
      portfolioManager
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
  }
}
    `)

export const useUnsetCsvImportRowBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UnsetCsvImportRowBuildingDataMutation,
    TError,
    Types.UnsetCsvImportRowBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UnsetCsvImportRowBuildingDataMutation,
    TError,
    Types.UnsetCsvImportRowBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["UnsetCsvImportRowBuildingData"],
    mutationFn: (variables?: Types.UnsetCsvImportRowBuildingDataMutationVariables) =>
      publicFetcher<
        Types.UnsetCsvImportRowBuildingDataMutation,
        Types.UnsetCsvImportRowBuildingDataMutationVariables
      >(UnsetCsvImportRowBuildingDataDocument, variables)(),
    ...options,
  })
}

useUnsetCsvImportRowBuildingDataMutation.fetcher = (
  variables: Types.UnsetCsvImportRowBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UnsetCsvImportRowBuildingDataMutation,
    Types.UnsetCsvImportRowBuildingDataMutationVariables
  >(UnsetCsvImportRowBuildingDataDocument, variables, options)

export const CreateThirdPartySupplierDocument = new TypedDocumentString(`
    mutation CreateThirdPartySupplier($name: String!) {
  createThirdPartySupplier(name: $name) {
    id
    pid
    name
  }
}
    `)

export const useCreateThirdPartySupplierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateThirdPartySupplierMutation,
    TError,
    Types.CreateThirdPartySupplierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateThirdPartySupplierMutation,
    TError,
    Types.CreateThirdPartySupplierMutationVariables,
    TContext
  >({
    mutationKey: ["CreateThirdPartySupplier"],
    mutationFn: (variables?: Types.CreateThirdPartySupplierMutationVariables) =>
      publicFetcher<
        Types.CreateThirdPartySupplierMutation,
        Types.CreateThirdPartySupplierMutationVariables
      >(CreateThirdPartySupplierDocument, variables)(),
    ...options,
  })
}

useCreateThirdPartySupplierMutation.fetcher = (
  variables: Types.CreateThirdPartySupplierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateThirdPartySupplierMutation,
    Types.CreateThirdPartySupplierMutationVariables
  >(CreateThirdPartySupplierDocument, variables, options)

export const AddMeterDocument = new TypedDocumentString(`
    mutation AddMeter($sitePid: String!, $loadCategory: LoadCategory!, $label: String, $buildingType: BuildingType, $tariffPid: String) {
  site(sitePid: $sitePid) {
    billingMeters {
      addMeter(
        loadCategory: $loadCategory
        label: $label
        buildingType: $buildingType
        tariffPid: $tariffPid
      ) {
        ...MutBillingMeter
      }
    }
  }
}
    fragment MutBillingMeter on BillingMeter {
  __typename
  ... on BillingMeter {
    pid
    label
    config {
      loadCategory
      buildingType
    }
  }
}`)

export const useAddMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddMeterMutation,
    TError,
    Types.AddMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddMeterMutation,
    TError,
    Types.AddMeterMutationVariables,
    TContext
  >({
    mutationKey: ["AddMeter"],
    mutationFn: (variables?: Types.AddMeterMutationVariables) =>
      publicFetcher<Types.AddMeterMutation, Types.AddMeterMutationVariables>(
        AddMeterDocument,
        variables,
      )(),
    ...options,
  })
}

useAddMeterMutation.fetcher = (
  variables: Types.AddMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddMeterMutation, Types.AddMeterMutationVariables>(
    AddMeterDocument,
    variables,
    options,
  )

export const EditBillingMeterAnnualDocument = new TypedDocumentString(`
    mutation EditBillingMeterAnnual($sitePid: String!, $meterPid: String!, $kwh: Float, $kw: Float, $costUsd: Float) {
  site(sitePid: $sitePid) {
    billingMeters {
      editAnnual(meterPid: $meterPid, kwh: $kwh, kw: $kw, costUsd: $costUsd) {
        ...MutBillingMeter
      }
    }
  }
}
    fragment MutBillingMeter on BillingMeter {
  __typename
  ... on BillingMeter {
    pid
    label
    config {
      loadCategory
      buildingType
    }
  }
}`)

export const useEditBillingMeterAnnualMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterAnnualMutation,
    TError,
    Types.EditBillingMeterAnnualMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterAnnualMutation,
    TError,
    Types.EditBillingMeterAnnualMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterAnnual"],
    mutationFn: (variables?: Types.EditBillingMeterAnnualMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterAnnualMutation,
        Types.EditBillingMeterAnnualMutationVariables
      >(EditBillingMeterAnnualDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterAnnualMutation.fetcher = (
  variables: Types.EditBillingMeterAnnualMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterAnnualMutation,
    Types.EditBillingMeterAnnualMutationVariables
  >(EditBillingMeterAnnualDocument, variables, options)

export const EditBillingMeterLabelDocument = new TypedDocumentString(`
    mutation EditBillingMeterLabel($sitePid: String!, $meterPid: String!, $label: String!) {
  site(sitePid: $sitePid) {
    billingMeters {
      editLabel(meterPid: $meterPid, label: $label) {
        ...MutBillingMeter
      }
    }
  }
}
    fragment MutBillingMeter on BillingMeter {
  __typename
  ... on BillingMeter {
    pid
    label
    config {
      loadCategory
      buildingType
    }
  }
}`)

export const useEditBillingMeterLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterLabelMutation,
    TError,
    Types.EditBillingMeterLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterLabelMutation,
    TError,
    Types.EditBillingMeterLabelMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterLabel"],
    mutationFn: (variables?: Types.EditBillingMeterLabelMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterLabelMutation,
        Types.EditBillingMeterLabelMutationVariables
      >(EditBillingMeterLabelDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterLabelMutation.fetcher = (
  variables: Types.EditBillingMeterLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterLabelMutation,
    Types.EditBillingMeterLabelMutationVariables
  >(EditBillingMeterLabelDocument, variables, options)

export const EditBillingMeterBuildingTypeDocument = new TypedDocumentString(`
    mutation EditBillingMeterBuildingType($sitePid: String!, $meterPid: String!, $buildingType: BuildingType!) {
  site(sitePid: $sitePid) {
    billingMeters {
      editBuildingType(meterPid: $meterPid, buildingType: $buildingType) {
        __typename
      }
    }
  }
}
    `)

export const useEditBillingMeterBuildingTypeMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EditBillingMeterBuildingTypeMutation,
    TError,
    Types.EditBillingMeterBuildingTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterBuildingTypeMutation,
    TError,
    Types.EditBillingMeterBuildingTypeMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterBuildingType"],
    mutationFn: (variables?: Types.EditBillingMeterBuildingTypeMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterBuildingTypeMutation,
        Types.EditBillingMeterBuildingTypeMutationVariables
      >(EditBillingMeterBuildingTypeDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterBuildingTypeMutation.fetcher = (
  variables: Types.EditBillingMeterBuildingTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterBuildingTypeMutation,
    Types.EditBillingMeterBuildingTypeMutationVariables
  >(EditBillingMeterBuildingTypeDocument, variables, options)

export const EditBillingMeterTariffDocument = new TypedDocumentString(`
    mutation EditBillingMeterTariff($sitePid: String!, $meterPid: String!, $tariffPid: String!) {
  site(sitePid: $sitePid) {
    billingMeters {
      editBauTariff(meterPid: $meterPid, tariffPid: $tariffPid) {
        __typename
      }
    }
  }
}
    `)

export const useEditBillingMeterTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterTariffMutation,
    TError,
    Types.EditBillingMeterTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterTariffMutation,
    TError,
    Types.EditBillingMeterTariffMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterTariff"],
    mutationFn: (variables?: Types.EditBillingMeterTariffMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterTariffMutation,
        Types.EditBillingMeterTariffMutationVariables
      >(EditBillingMeterTariffDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterTariffMutation.fetcher = (
  variables: Types.EditBillingMeterTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterTariffMutation,
    Types.EditBillingMeterTariffMutationVariables
  >(EditBillingMeterTariffDocument, variables, options)

export const AddBillingMeterIntervalsDocument = new TypedDocumentString(`
    mutation AddBillingMeterIntervals($sitePid: String!, $input: BillingIntervalsExtraction!) {
  site(sitePid: $sitePid) {
    billingMeters {
      addIntervals(input: $input) {
        kind
        rowNumber
        row
      }
    }
  }
}
    `)

export const useAddBillingMeterIntervalsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBillingMeterIntervalsMutation,
    TError,
    Types.AddBillingMeterIntervalsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBillingMeterIntervalsMutation,
    TError,
    Types.AddBillingMeterIntervalsMutationVariables,
    TContext
  >({
    mutationKey: ["AddBillingMeterIntervals"],
    mutationFn: (variables?: Types.AddBillingMeterIntervalsMutationVariables) =>
      publicFetcher<
        Types.AddBillingMeterIntervalsMutation,
        Types.AddBillingMeterIntervalsMutationVariables
      >(AddBillingMeterIntervalsDocument, variables)(),
    ...options,
  })
}

useAddBillingMeterIntervalsMutation.fetcher = (
  variables: Types.AddBillingMeterIntervalsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBillingMeterIntervalsMutation,
    Types.AddBillingMeterIntervalsMutationVariables
  >(AddBillingMeterIntervalsDocument, variables, options)

export const AddBillingMeterMonthliesDocument = new TypedDocumentString(`
    mutation AddBillingMeterMonthlies($sitePid: String!, $input: MonthlyExtraction!) {
  site(sitePid: $sitePid) {
    billingMeters {
      addMonthlies(input: $input)
    }
  }
}
    `)

export const useAddBillingMeterMonthliesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBillingMeterMonthliesMutation,
    TError,
    Types.AddBillingMeterMonthliesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBillingMeterMonthliesMutation,
    TError,
    Types.AddBillingMeterMonthliesMutationVariables,
    TContext
  >({
    mutationKey: ["AddBillingMeterMonthlies"],
    mutationFn: (variables?: Types.AddBillingMeterMonthliesMutationVariables) =>
      publicFetcher<
        Types.AddBillingMeterMonthliesMutation,
        Types.AddBillingMeterMonthliesMutationVariables
      >(AddBillingMeterMonthliesDocument, variables)(),
    ...options,
  })
}

useAddBillingMeterMonthliesMutation.fetcher = (
  variables: Types.AddBillingMeterMonthliesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBillingMeterMonthliesMutation,
    Types.AddBillingMeterMonthliesMutationVariables
  >(AddBillingMeterMonthliesDocument, variables, options)

export const BillingMeterEditTenantDetailsDocument = new TypedDocumentString(`
    mutation BillingMeterEditTenantDetails($sitePid: String!, $meterPid: String!, $input: MFUnitBreakdownInput!) {
  site(sitePid: $sitePid) {
    billingMeters {
      editTenantDetails(meterPid: $meterPid, input: $input)
    }
  }
}
    `)

export const useBillingMeterEditTenantDetailsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.BillingMeterEditTenantDetailsMutation,
    TError,
    Types.BillingMeterEditTenantDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BillingMeterEditTenantDetailsMutation,
    TError,
    Types.BillingMeterEditTenantDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["BillingMeterEditTenantDetails"],
    mutationFn: (variables?: Types.BillingMeterEditTenantDetailsMutationVariables) =>
      publicFetcher<
        Types.BillingMeterEditTenantDetailsMutation,
        Types.BillingMeterEditTenantDetailsMutationVariables
      >(BillingMeterEditTenantDetailsDocument, variables)(),
    ...options,
  })
}

useBillingMeterEditTenantDetailsMutation.fetcher = (
  variables: Types.BillingMeterEditTenantDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BillingMeterEditTenantDetailsMutation,
    Types.BillingMeterEditTenantDetailsMutationVariables
  >(BillingMeterEditTenantDetailsDocument, variables, options)

export const RemoveBillingMeterIntervalsDocument = new TypedDocumentString(`
    mutation RemoveBillingMeterIntervals($sitePid: String!, $meterPid: String!, $intervalsId: Int!) {
  site(sitePid: $sitePid) {
    billingMeters {
      removeIntervals(meterPid: $meterPid, intervalsId: $intervalsId)
    }
  }
}
    `)

export const useRemoveBillingMeterIntervalsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBillingMeterIntervalsMutation,
    TError,
    Types.RemoveBillingMeterIntervalsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBillingMeterIntervalsMutation,
    TError,
    Types.RemoveBillingMeterIntervalsMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBillingMeterIntervals"],
    mutationFn: (variables?: Types.RemoveBillingMeterIntervalsMutationVariables) =>
      publicFetcher<
        Types.RemoveBillingMeterIntervalsMutation,
        Types.RemoveBillingMeterIntervalsMutationVariables
      >(RemoveBillingMeterIntervalsDocument, variables)(),
    ...options,
  })
}

useRemoveBillingMeterIntervalsMutation.fetcher = (
  variables: Types.RemoveBillingMeterIntervalsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBillingMeterIntervalsMutation,
    Types.RemoveBillingMeterIntervalsMutationVariables
  >(RemoveBillingMeterIntervalsDocument, variables, options)

export const ImportIntervalsFromCsvDocument = new TypedDocumentString(`
    mutation ImportIntervalsFromCsv($sitePid: String!, $documentPid: String!, $meterPid: String!) {
  site(sitePid: $sitePid) {
    billingMeters {
      importIntervalsFromCsv(documentPid: $documentPid, meterPid: $meterPid) {
        ...IntervalImportRequest
      }
    }
  }
}
    fragment IntervalImportRequest on IntervalImportRequestView {
  id
  status
  note
  errorClass
  errorMessage
  meterPid
  inputDocument {
    pid
    fileName
    downloadHref
  }
}`)

export const useImportIntervalsFromCsvMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ImportIntervalsFromCsvMutation,
    TError,
    Types.ImportIntervalsFromCsvMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ImportIntervalsFromCsvMutation,
    TError,
    Types.ImportIntervalsFromCsvMutationVariables,
    TContext
  >({
    mutationKey: ["ImportIntervalsFromCsv"],
    mutationFn: (variables?: Types.ImportIntervalsFromCsvMutationVariables) =>
      publicFetcher<
        Types.ImportIntervalsFromCsvMutation,
        Types.ImportIntervalsFromCsvMutationVariables
      >(ImportIntervalsFromCsvDocument, variables)(),
    ...options,
  })
}

useImportIntervalsFromCsvMutation.fetcher = (
  variables: Types.ImportIntervalsFromCsvMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ImportIntervalsFromCsvMutation,
    Types.ImportIntervalsFromCsvMutationVariables
  >(ImportIntervalsFromCsvDocument, variables, options)

export const SetArchivedBillingMeterDocument = new TypedDocumentString(`
    mutation SetArchivedBillingMeter($sitePid: String!, $meterPid: String!, $archived: Boolean!) {
  site(sitePid: $sitePid) {
    billingMeters {
      editArchived(meterPid: $meterPid, archived: $archived) {
        __typename
      }
    }
  }
}
    `)

export const useSetArchivedBillingMeterMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetArchivedBillingMeterMutation,
    TError,
    Types.SetArchivedBillingMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchivedBillingMeterMutation,
    TError,
    Types.SetArchivedBillingMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchivedBillingMeter"],
    mutationFn: (variables?: Types.SetArchivedBillingMeterMutationVariables) =>
      publicFetcher<
        Types.SetArchivedBillingMeterMutation,
        Types.SetArchivedBillingMeterMutationVariables
      >(SetArchivedBillingMeterDocument, variables)(),
    ...options,
  })
}

useSetArchivedBillingMeterMutation.fetcher = (
  variables: Types.SetArchivedBillingMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchivedBillingMeterMutation,
    Types.SetArchivedBillingMeterMutationVariables
  >(SetArchivedBillingMeterDocument, variables, options)

export const EditBillingMeterMonthDocument = new TypedDocumentString(`
    mutation EditBillingMeterMonth($sitePid: String!, $meterPid: String!, $start: Date!, $end: Date!, $totalKwh: Float!, $peakKw: Float, $totalCostUsd: Float, $supplyCostUsd: Float, $transmissionCostUsd: Float) {
  site(sitePid: $sitePid) {
    billingMeters {
      editMonth(
        meterPid: $meterPid
        start: $start
        end: $end
        totalKwh: $totalKwh
        peakKw: $peakKw
        totalCostUsd: $totalCostUsd
        supplyCostUsd: $supplyCostUsd
        transmissionCostUsd: $transmissionCostUsd
      ) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useEditBillingMeterMonthMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterMonthMutation,
    TError,
    Types.EditBillingMeterMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterMonthMutation,
    TError,
    Types.EditBillingMeterMonthMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterMonth"],
    mutationFn: (variables?: Types.EditBillingMeterMonthMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterMonthMutation,
        Types.EditBillingMeterMonthMutationVariables
      >(EditBillingMeterMonthDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterMonthMutation.fetcher = (
  variables: Types.EditBillingMeterMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterMonthMutation,
    Types.EditBillingMeterMonthMutationVariables
  >(EditBillingMeterMonthDocument, variables, options)

export const RemoveBillingMeterMonthDocument = new TypedDocumentString(`
    mutation RemoveBillingMeterMonth($sitePid: String!, $meterPid: String!, $monthId: Int!) {
  site(sitePid: $sitePid) {
    billingMeters {
      removeMonth(meterPid: $meterPid, monthId: $monthId) {
        __typename
      }
    }
  }
}
    `)

export const useRemoveBillingMeterMonthMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBillingMeterMonthMutation,
    TError,
    Types.RemoveBillingMeterMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBillingMeterMonthMutation,
    TError,
    Types.RemoveBillingMeterMonthMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBillingMeterMonth"],
    mutationFn: (variables?: Types.RemoveBillingMeterMonthMutationVariables) =>
      publicFetcher<
        Types.RemoveBillingMeterMonthMutation,
        Types.RemoveBillingMeterMonthMutationVariables
      >(RemoveBillingMeterMonthDocument, variables)(),
    ...options,
  })
}

useRemoveBillingMeterMonthMutation.fetcher = (
  variables: Types.RemoveBillingMeterMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBillingMeterMonthMutation,
    Types.RemoveBillingMeterMonthMutationVariables
  >(RemoveBillingMeterMonthDocument, variables, options)

export const MoveDocumentBillingMonthDocument = new TypedDocumentString(`
    mutation MoveDocumentBillingMonth($sitePid: String!, $billingMonthId: Int!, $kind: BillType!, $fromMeterPid: String, $toMeterPid: String) {
  site(sitePid: $sitePid) {
    billingMeters {
      moveDocumentBillingMonth(
        dbmId: $billingMonthId
        kind: $kind
        fromMeterPid: $fromMeterPid
        toMeterPid: $toMeterPid
      )
    }
  }
}
    `)

export const useMoveDocumentBillingMonthMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.MoveDocumentBillingMonthMutation,
    TError,
    Types.MoveDocumentBillingMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MoveDocumentBillingMonthMutation,
    TError,
    Types.MoveDocumentBillingMonthMutationVariables,
    TContext
  >({
    mutationKey: ["MoveDocumentBillingMonth"],
    mutationFn: (variables?: Types.MoveDocumentBillingMonthMutationVariables) =>
      publicFetcher<
        Types.MoveDocumentBillingMonthMutation,
        Types.MoveDocumentBillingMonthMutationVariables
      >(MoveDocumentBillingMonthDocument, variables)(),
    ...options,
  })
}

useMoveDocumentBillingMonthMutation.fetcher = (
  variables: Types.MoveDocumentBillingMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MoveDocumentBillingMonthMutation,
    Types.MoveDocumentBillingMonthMutationVariables
  >(MoveDocumentBillingMonthDocument, variables, options)

export const CreateBuildingDataFromGooglePlaceDocument = new TypedDocumentString(`
    mutation CreateBuildingDataFromGooglePlace($placeId: String!) {
  buildingData {
    createFromGooglePlace(placeId: $placeId) {
      __typename
      ... on BuildingData {
        id
        pid
        fullAddress
        buildingType
        propertyName
        roofSqft
        sqft
        numStories
        latitude
        longitude
        buildingFootprintGeojson
        yearBuilt
        numUnitsTotal
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useCreateBuildingDataFromGooglePlaceMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    TError,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    TError,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBuildingDataFromGooglePlace"],
    mutationFn: (
      variables?: Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    ) =>
      publicFetcher<
        Types.CreateBuildingDataFromGooglePlaceMutation,
        Types.CreateBuildingDataFromGooglePlaceMutationVariables
      >(CreateBuildingDataFromGooglePlaceDocument, variables)(),
    ...options,
  })
}

useCreateBuildingDataFromGooglePlaceMutation.fetcher = (
  variables: Types.CreateBuildingDataFromGooglePlaceMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables
  >(CreateBuildingDataFromGooglePlaceDocument, variables, options)

export const CreateBuildingDataFromOvertureBuildingDocument = new TypedDocumentString(`
    mutation CreateBuildingDataFromOvertureBuilding($overtureId: String!) {
  buildingData {
    createFromOvertureBuilding(overtureId: $overtureId) {
      __typename
      ... on BuildingData {
        id
        pid
        fullAddress
        buildingType
        propertyName
        roofSqft
        sqft
        latitude
        longitude
        buildingFootprintGeojson
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useCreateBuildingDataFromOvertureBuildingMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateBuildingDataFromOvertureBuildingMutation,
    TError,
    Types.CreateBuildingDataFromOvertureBuildingMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBuildingDataFromOvertureBuildingMutation,
    TError,
    Types.CreateBuildingDataFromOvertureBuildingMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBuildingDataFromOvertureBuilding"],
    mutationFn: (
      variables?: Types.CreateBuildingDataFromOvertureBuildingMutationVariables,
    ) =>
      publicFetcher<
        Types.CreateBuildingDataFromOvertureBuildingMutation,
        Types.CreateBuildingDataFromOvertureBuildingMutationVariables
      >(CreateBuildingDataFromOvertureBuildingDocument, variables)(),
    ...options,
  })
}

useCreateBuildingDataFromOvertureBuildingMutation.fetcher = (
  variables: Types.CreateBuildingDataFromOvertureBuildingMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBuildingDataFromOvertureBuildingMutation,
    Types.CreateBuildingDataFromOvertureBuildingMutationVariables
  >(CreateBuildingDataFromOvertureBuildingDocument, variables, options)

export const CreateSiteFromBuildingDataDocument = new TypedDocumentString(`
    mutation CreateSiteFromBuildingData($portfolioPid: String!, $buildingDataId: Int!, $amendments: BuildingDataAmendmentsInput!) {
  buildingData {
    createSite(
      portfolioPid: $portfolioPid
      buildingDataId: $buildingDataId
      amendments: $amendments
    ) {
      __typename
      ... on SiteRef {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useCreateSiteFromBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateSiteFromBuildingDataMutation,
    TError,
    Types.CreateSiteFromBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateSiteFromBuildingDataMutation,
    TError,
    Types.CreateSiteFromBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["CreateSiteFromBuildingData"],
    mutationFn: (variables?: Types.CreateSiteFromBuildingDataMutationVariables) =>
      publicFetcher<
        Types.CreateSiteFromBuildingDataMutation,
        Types.CreateSiteFromBuildingDataMutationVariables
      >(CreateSiteFromBuildingDataDocument, variables)(),
    ...options,
  })
}

useCreateSiteFromBuildingDataMutation.fetcher = (
  variables: Types.CreateSiteFromBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateSiteFromBuildingDataMutation,
    Types.CreateSiteFromBuildingDataMutationVariables
  >(CreateSiteFromBuildingDataDocument, variables, options)

export const EditBundleParentDocument = new TypedDocumentString(`
    mutation EditBundleParent($orgSlug: String!, $bundleParentPid: String!, $name: String!, $note: String!, $biddingOpenAt: DateTime, $biddingClosedAt: DateTime) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setName(name: $name)
        setNote(note: $note)
        setBiddingOpenAt(biddingOpenAt: $biddingOpenAt) {
          message
        }
        setBiddingClosedAt(biddingClosedAt: $biddingClosedAt) {
          message
        }
      }
    }
  }
}
    `)

export const useEditBundleParentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBundleParentMutation,
    TError,
    Types.EditBundleParentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBundleParentMutation,
    TError,
    Types.EditBundleParentMutationVariables,
    TContext
  >({
    mutationKey: ["EditBundleParent"],
    mutationFn: (variables?: Types.EditBundleParentMutationVariables) =>
      publicFetcher<
        Types.EditBundleParentMutation,
        Types.EditBundleParentMutationVariables
      >(EditBundleParentDocument, variables)(),
    ...options,
  })
}

useEditBundleParentMutation.fetcher = (
  variables: Types.EditBundleParentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBundleParentMutation,
    Types.EditBundleParentMutationVariables
  >(EditBundleParentDocument, variables, options)

export const EditBundleParentPricingDocument = new TypedDocumentString(`
    mutation EditBundleParentPricing($orgSlug: String!, $bundleParentPid: String!, $pricingNote: String!, $shortPricingNote: String!, $lumenFeeUsdPerWatt: Float, $lumenFeeUsdPerKwhStorage: Float, $revenueMilestones: [RevenueMilestoneInput!]!, $hideAutoGeneratedPricingSummary: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setPricingNote(pricingNote: $pricingNote)
        setShortPricingNote(shortPricingNote: $shortPricingNote)
        setLumenFee(
          lumenFeeUsdPerWatt: $lumenFeeUsdPerWatt
          lumenFeeUsdPerKwhStorage: $lumenFeeUsdPerKwhStorage
        ) {
          message
        }
        setRevenueMilestones(revenueMilestones: $revenueMilestones) {
          message
        }
        setHideAutoGeneratedPricingSummary(hide: $hideAutoGeneratedPricingSummary)
      }
    }
  }
}
    `)

export const useEditBundleParentPricingMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EditBundleParentPricingMutation,
    TError,
    Types.EditBundleParentPricingMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBundleParentPricingMutation,
    TError,
    Types.EditBundleParentPricingMutationVariables,
    TContext
  >({
    mutationKey: ["EditBundleParentPricing"],
    mutationFn: (variables?: Types.EditBundleParentPricingMutationVariables) =>
      publicFetcher<
        Types.EditBundleParentPricingMutation,
        Types.EditBundleParentPricingMutationVariables
      >(EditBundleParentPricingDocument, variables)(),
    ...options,
  })
}

useEditBundleParentPricingMutation.fetcher = (
  variables: Types.EditBundleParentPricingMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBundleParentPricingMutation,
    Types.EditBundleParentPricingMutationVariables
  >(EditBundleParentPricingDocument, variables, options)

export const SetBundleParentRevenueMilestonesDocument = new TypedDocumentString(`
    mutation SetBundleParentRevenueMilestones($orgSlug: String!, $bundleParentPid: String!, $revenueMilestones: [RevenueMilestoneInput!]!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setRevenueMilestones(revenueMilestones: $revenueMilestones) {
          message
        }
      }
    }
  }
}
    `)

export const useSetBundleParentRevenueMilestonesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentRevenueMilestonesMutation,
    TError,
    Types.SetBundleParentRevenueMilestonesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentRevenueMilestonesMutation,
    TError,
    Types.SetBundleParentRevenueMilestonesMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentRevenueMilestones"],
    mutationFn: (variables?: Types.SetBundleParentRevenueMilestonesMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentRevenueMilestonesMutation,
        Types.SetBundleParentRevenueMilestonesMutationVariables
      >(SetBundleParentRevenueMilestonesDocument, variables)(),
    ...options,
  })
}

useSetBundleParentRevenueMilestonesMutation.fetcher = (
  variables: Types.SetBundleParentRevenueMilestonesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentRevenueMilestonesMutation,
    Types.SetBundleParentRevenueMilestonesMutationVariables
  >(SetBundleParentRevenueMilestonesDocument, variables, options)

export const SetBundleParentDefaultRofoSignedDocument = new TypedDocumentString(`
    mutation SetBundleParentDefaultRofoSigned($orgSlug: String!, $bundleParentPid: String!, $defaultRofoSigned: Date) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setDefaultRofoSigned(defaultRofoSigned: $defaultRofoSigned)
      }
    }
  }
}
    `)

export const useSetBundleParentDefaultRofoSignedMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentDefaultRofoSignedMutation,
    TError,
    Types.SetBundleParentDefaultRofoSignedMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentDefaultRofoSignedMutation,
    TError,
    Types.SetBundleParentDefaultRofoSignedMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentDefaultRofoSigned"],
    mutationFn: (variables?: Types.SetBundleParentDefaultRofoSignedMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentDefaultRofoSignedMutation,
        Types.SetBundleParentDefaultRofoSignedMutationVariables
      >(SetBundleParentDefaultRofoSignedDocument, variables)(),
    ...options,
  })
}

useSetBundleParentDefaultRofoSignedMutation.fetcher = (
  variables: Types.SetBundleParentDefaultRofoSignedMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentDefaultRofoSignedMutation,
    Types.SetBundleParentDefaultRofoSignedMutationVariables
  >(SetBundleParentDefaultRofoSignedDocument, variables, options)

export const SetBundleParentLumenFeeDocument = new TypedDocumentString(`
    mutation SetBundleParentLumenFee($orgSlug: String!, $bundleParentPid: String!, $lumenFeeUsdPerWatt: Float, $lumenFeeUsdPerKwhStorage: Float) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setLumenFee(
          lumenFeeUsdPerWatt: $lumenFeeUsdPerWatt
          lumenFeeUsdPerKwhStorage: $lumenFeeUsdPerKwhStorage
        ) {
          message
        }
      }
    }
  }
}
    `)

export const useSetBundleParentLumenFeeMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentLumenFeeMutation,
    TError,
    Types.SetBundleParentLumenFeeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentLumenFeeMutation,
    TError,
    Types.SetBundleParentLumenFeeMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentLumenFee"],
    mutationFn: (variables?: Types.SetBundleParentLumenFeeMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentLumenFeeMutation,
        Types.SetBundleParentLumenFeeMutationVariables
      >(SetBundleParentLumenFeeDocument, variables)(),
    ...options,
  })
}

useSetBundleParentLumenFeeMutation.fetcher = (
  variables: Types.SetBundleParentLumenFeeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentLumenFeeMutation,
    Types.SetBundleParentLumenFeeMutationVariables
  >(SetBundleParentLumenFeeDocument, variables, options)

export const AddBundleParentFinancierDocument = new TypedDocumentString(`
    mutation AddBundleParentFinancier($orgSlug: String!, $bundleParentPid: String!, $financierPids: [String!]!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addFinanciers(financierPids: $financierPids)
      }
    }
  }
}
    `)

export const useAddBundleParentFinancierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBundleParentFinancierMutation,
    TError,
    Types.AddBundleParentFinancierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentFinancierMutation,
    TError,
    Types.AddBundleParentFinancierMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentFinancier"],
    mutationFn: (variables?: Types.AddBundleParentFinancierMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentFinancierMutation,
        Types.AddBundleParentFinancierMutationVariables
      >(AddBundleParentFinancierDocument, variables)(),
    ...options,
  })
}

useAddBundleParentFinancierMutation.fetcher = (
  variables: Types.AddBundleParentFinancierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentFinancierMutation,
    Types.AddBundleParentFinancierMutationVariables
  >(AddBundleParentFinancierDocument, variables, options)

export const AddBundleParentProjectDocument = new TypedDocumentString(`
    mutation AddBundleParentProject($orgSlug: String!, $bundleParentPid: String!, $input: BidPackageProjectInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addProject(input: $input) {
          message
        }
      }
    }
  }
}
    `)

export const useAddBundleParentProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleParentProjectMutation,
    TError,
    Types.AddBundleParentProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentProjectMutation,
    TError,
    Types.AddBundleParentProjectMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentProject"],
    mutationFn: (variables?: Types.AddBundleParentProjectMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentProjectMutation,
        Types.AddBundleParentProjectMutationVariables
      >(AddBundleParentProjectDocument, variables)(),
    ...options,
  })
}

useAddBundleParentProjectMutation.fetcher = (
  variables: Types.AddBundleParentProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentProjectMutation,
    Types.AddBundleParentProjectMutationVariables
  >(AddBundleParentProjectDocument, variables, options)

export const UpdateBundleParentProjectDocument = new TypedDocumentString(`
    mutation UpdateBundleParentProject($orgSlug: String!, $bundleParentPid: String!, $projectPid: String!, $input: BidPackageProjectInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        updateProject(projectPid: $projectPid, input: $input) {
          message
        }
      }
    }
  }
}
    `)

export const useUpdateBundleParentProjectMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateBundleParentProjectMutation,
    TError,
    Types.UpdateBundleParentProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateBundleParentProjectMutation,
    TError,
    Types.UpdateBundleParentProjectMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateBundleParentProject"],
    mutationFn: (variables?: Types.UpdateBundleParentProjectMutationVariables) =>
      publicFetcher<
        Types.UpdateBundleParentProjectMutation,
        Types.UpdateBundleParentProjectMutationVariables
      >(UpdateBundleParentProjectDocument, variables)(),
    ...options,
  })
}

useUpdateBundleParentProjectMutation.fetcher = (
  variables: Types.UpdateBundleParentProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateBundleParentProjectMutation,
    Types.UpdateBundleParentProjectMutationVariables
  >(UpdateBundleParentProjectDocument, variables, options)

export const RemoveBundleParentProjectDocument = new TypedDocumentString(`
    mutation RemoveBundleParentProject($orgSlug: String!, $bundleParentPid: String!, $projectPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        removeProject(projectPid: $projectPid) {
          message
        }
      }
    }
  }
}
    `)

export const useRemoveBundleParentProjectMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBundleParentProjectMutation,
    TError,
    Types.RemoveBundleParentProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBundleParentProjectMutation,
    TError,
    Types.RemoveBundleParentProjectMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBundleParentProject"],
    mutationFn: (variables?: Types.RemoveBundleParentProjectMutationVariables) =>
      publicFetcher<
        Types.RemoveBundleParentProjectMutation,
        Types.RemoveBundleParentProjectMutationVariables
      >(RemoveBundleParentProjectDocument, variables)(),
    ...options,
  })
}

useRemoveBundleParentProjectMutation.fetcher = (
  variables: Types.RemoveBundleParentProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBundleParentProjectMutation,
    Types.RemoveBundleParentProjectMutationVariables
  >(RemoveBundleParentProjectDocument, variables, options)

export const CreateBundleParentSystemDocument = new TypedDocumentString(`
    mutation CreateBundleParentSystem($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $input: AddSystemInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addSystem(sitePid: $sitePid, input: $input) {
          system {
            pid
          }
          lite {
            pid
          }
        }
      }
    }
  }
}
    `)

export const useCreateBundleParentSystemMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateBundleParentSystemMutation,
    TError,
    Types.CreateBundleParentSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBundleParentSystemMutation,
    TError,
    Types.CreateBundleParentSystemMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBundleParentSystem"],
    mutationFn: (variables?: Types.CreateBundleParentSystemMutationVariables) =>
      publicFetcher<
        Types.CreateBundleParentSystemMutation,
        Types.CreateBundleParentSystemMutationVariables
      >(CreateBundleParentSystemDocument, variables)(),
    ...options,
  })
}

useCreateBundleParentSystemMutation.fetcher = (
  variables: Types.CreateBundleParentSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBundleParentSystemMutation,
    Types.CreateBundleParentSystemMutationVariables
  >(CreateBundleParentSystemDocument, variables, options)

export const RemoveBundleParentSiteDocument = new TypedDocumentString(`
    mutation RemoveBundleParentSite($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        removeSite(sitePid: $sitePid)
      }
    }
  }
}
    `)

export const useRemoveBundleParentSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveBundleParentSiteMutation,
    TError,
    Types.RemoveBundleParentSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBundleParentSiteMutation,
    TError,
    Types.RemoveBundleParentSiteMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBundleParentSite"],
    mutationFn: (variables?: Types.RemoveBundleParentSiteMutationVariables) =>
      publicFetcher<
        Types.RemoveBundleParentSiteMutation,
        Types.RemoveBundleParentSiteMutationVariables
      >(RemoveBundleParentSiteDocument, variables)(),
    ...options,
  })
}

useRemoveBundleParentSiteMutation.fetcher = (
  variables: Types.RemoveBundleParentSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBundleParentSiteMutation,
    Types.RemoveBundleParentSiteMutationVariables
  >(RemoveBundleParentSiteDocument, variables, options)

export const SwitchBundleParentRowProjectDocument = new TypedDocumentString(`
    mutation SwitchBundleParentRowProject($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $currentProjectPid: String!, $projectPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        switchProject(
          sitePid: $sitePid
          currentProjectPid: $currentProjectPid
          projectPid: $projectPid
        ) {
          ... on Error {
            message
          }
        }
      }
    }
  }
}
    `)

export const useSwitchBundleParentRowProjectMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SwitchBundleParentRowProjectMutation,
    TError,
    Types.SwitchBundleParentRowProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SwitchBundleParentRowProjectMutation,
    TError,
    Types.SwitchBundleParentRowProjectMutationVariables,
    TContext
  >({
    mutationKey: ["SwitchBundleParentRowProject"],
    mutationFn: (variables?: Types.SwitchBundleParentRowProjectMutationVariables) =>
      publicFetcher<
        Types.SwitchBundleParentRowProjectMutation,
        Types.SwitchBundleParentRowProjectMutationVariables
      >(SwitchBundleParentRowProjectDocument, variables)(),
    ...options,
  })
}

useSwitchBundleParentRowProjectMutation.fetcher = (
  variables: Types.SwitchBundleParentRowProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SwitchBundleParentRowProjectMutation,
    Types.SwitchBundleParentRowProjectMutationVariables
  >(SwitchBundleParentRowProjectDocument, variables, options)

export const SetBundleParentAdminNoteDocument = new TypedDocumentString(`
    mutation SetBundleParentAdminNote($orgSlug: String!, $bundleParentPid: String!, $adminOnlyNote: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setAdminOnlyNote(adminOnlyNote: $adminOnlyNote)
      }
    }
  }
}
    `)

export const useSetBundleParentAdminNoteMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentAdminNoteMutation,
    TError,
    Types.SetBundleParentAdminNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentAdminNoteMutation,
    TError,
    Types.SetBundleParentAdminNoteMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentAdminNote"],
    mutationFn: (variables?: Types.SetBundleParentAdminNoteMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentAdminNoteMutation,
        Types.SetBundleParentAdminNoteMutationVariables
      >(SetBundleParentAdminNoteDocument, variables)(),
    ...options,
  })
}

useSetBundleParentAdminNoteMutation.fetcher = (
  variables: Types.SetBundleParentAdminNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentAdminNoteMutation,
    Types.SetBundleParentAdminNoteMutationVariables
  >(SetBundleParentAdminNoteDocument, variables, options)

export const LinkBundleParentNotionPageDocument = new TypedDocumentString(`
    mutation LinkBundleParentNotionPage($orgSlug: String!, $bundleParentPid: String!, $notionPageUrl: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        linkNotionPage(notionPageUrl: $notionPageUrl)
      }
    }
  }
}
    `)

export const useLinkBundleParentNotionPageMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.LinkBundleParentNotionPageMutation,
    TError,
    Types.LinkBundleParentNotionPageMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LinkBundleParentNotionPageMutation,
    TError,
    Types.LinkBundleParentNotionPageMutationVariables,
    TContext
  >({
    mutationKey: ["LinkBundleParentNotionPage"],
    mutationFn: (variables?: Types.LinkBundleParentNotionPageMutationVariables) =>
      publicFetcher<
        Types.LinkBundleParentNotionPageMutation,
        Types.LinkBundleParentNotionPageMutationVariables
      >(LinkBundleParentNotionPageDocument, variables)(),
    ...options,
  })
}

useLinkBundleParentNotionPageMutation.fetcher = (
  variables: Types.LinkBundleParentNotionPageMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.LinkBundleParentNotionPageMutation,
    Types.LinkBundleParentNotionPageMutationVariables
  >(LinkBundleParentNotionPageDocument, variables, options)

export const SetBundleParentBidRemindersSentDocument = new TypedDocumentString(`
    mutation SetBundleParentBidRemindersSent($orgSlug: String!, $bundleParentPid: String!, $bidRemindersSent: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setBidRemindersSent(bidRemindersSent: $bidRemindersSent)
      }
    }
  }
}
    `)

export const useSetBundleParentBidRemindersSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentBidRemindersSentMutation,
    TError,
    Types.SetBundleParentBidRemindersSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentBidRemindersSentMutation,
    TError,
    Types.SetBundleParentBidRemindersSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentBidRemindersSent"],
    mutationFn: (variables?: Types.SetBundleParentBidRemindersSentMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentBidRemindersSentMutation,
        Types.SetBundleParentBidRemindersSentMutationVariables
      >(SetBundleParentBidRemindersSentDocument, variables)(),
    ...options,
  })
}

useSetBundleParentBidRemindersSentMutation.fetcher = (
  variables: Types.SetBundleParentBidRemindersSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentBidRemindersSentMutation,
    Types.SetBundleParentBidRemindersSentMutationVariables
  >(SetBundleParentBidRemindersSentDocument, variables, options)

export const SetBundleParentLossNoticesSentDocument = new TypedDocumentString(`
    mutation SetBundleParentLossNoticesSent($orgSlug: String!, $bundleParentPid: String!, $lossNoticesSent: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setLossNoticesSent(lossNoticesSent: $lossNoticesSent)
      }
    }
  }
}
    `)

export const useSetBundleParentLossNoticesSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentLossNoticesSentMutation,
    TError,
    Types.SetBundleParentLossNoticesSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentLossNoticesSentMutation,
    TError,
    Types.SetBundleParentLossNoticesSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentLossNoticesSent"],
    mutationFn: (variables?: Types.SetBundleParentLossNoticesSentMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentLossNoticesSentMutation,
        Types.SetBundleParentLossNoticesSentMutationVariables
      >(SetBundleParentLossNoticesSentDocument, variables)(),
    ...options,
  })
}

useSetBundleParentLossNoticesSentMutation.fetcher = (
  variables: Types.SetBundleParentLossNoticesSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentLossNoticesSentMutation,
    Types.SetBundleParentLossNoticesSentMutationVariables
  >(SetBundleParentLossNoticesSentDocument, variables, options)

export const SetProjectChangeOrderSentDocument = new TypedDocumentString(`
    mutation SetProjectChangeOrderSent($projectPid: String!, $changeOrderSent: Boolean!) {
  project(projectPid: $projectPid) {
    setChangeOrderSent(changeOrderSent: $changeOrderSent)
  }
}
    `)

export const useSetProjectChangeOrderSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetProjectChangeOrderSentMutation,
    TError,
    Types.SetProjectChangeOrderSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProjectChangeOrderSentMutation,
    TError,
    Types.SetProjectChangeOrderSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetProjectChangeOrderSent"],
    mutationFn: (variables?: Types.SetProjectChangeOrderSentMutationVariables) =>
      publicFetcher<
        Types.SetProjectChangeOrderSentMutation,
        Types.SetProjectChangeOrderSentMutationVariables
      >(SetProjectChangeOrderSentDocument, variables)(),
    ...options,
  })
}

useSetProjectChangeOrderSentMutation.fetcher = (
  variables: Types.SetProjectChangeOrderSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProjectChangeOrderSentMutation,
    Types.SetProjectChangeOrderSentMutationVariables
  >(SetProjectChangeOrderSentDocument, variables, options)

export const SetProjectHostAwardSentDocument = new TypedDocumentString(`
    mutation SetProjectHostAwardSent($projectPid: String!, $hostAwardSent: Boolean!) {
  project(projectPid: $projectPid) {
    setHostAwardSent(hostAwardSent: $hostAwardSent)
  }
}
    `)

export const useSetProjectHostAwardSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetProjectHostAwardSentMutation,
    TError,
    Types.SetProjectHostAwardSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProjectHostAwardSentMutation,
    TError,
    Types.SetProjectHostAwardSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetProjectHostAwardSent"],
    mutationFn: (variables?: Types.SetProjectHostAwardSentMutationVariables) =>
      publicFetcher<
        Types.SetProjectHostAwardSentMutation,
        Types.SetProjectHostAwardSentMutationVariables
      >(SetProjectHostAwardSentDocument, variables)(),
    ...options,
  })
}

useSetProjectHostAwardSentMutation.fetcher = (
  variables: Types.SetProjectHostAwardSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProjectHostAwardSentMutation,
    Types.SetProjectHostAwardSentMutationVariables
  >(SetProjectHostAwardSentDocument, variables, options)

export const SetProjectScopeOfWorkSentDocument = new TypedDocumentString(`
    mutation SetProjectScopeOfWorkSent($projectPid: String!, $scopeOfWorkSent: Boolean!) {
  project(projectPid: $projectPid) {
    setScopeOfWorkSent(scopeOfWorkSent: $scopeOfWorkSent)
  }
}
    `)

export const useSetProjectScopeOfWorkSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetProjectScopeOfWorkSentMutation,
    TError,
    Types.SetProjectScopeOfWorkSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProjectScopeOfWorkSentMutation,
    TError,
    Types.SetProjectScopeOfWorkSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetProjectScopeOfWorkSent"],
    mutationFn: (variables?: Types.SetProjectScopeOfWorkSentMutationVariables) =>
      publicFetcher<
        Types.SetProjectScopeOfWorkSentMutation,
        Types.SetProjectScopeOfWorkSentMutationVariables
      >(SetProjectScopeOfWorkSentDocument, variables)(),
    ...options,
  })
}

useSetProjectScopeOfWorkSentMutation.fetcher = (
  variables: Types.SetProjectScopeOfWorkSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProjectScopeOfWorkSentMutation,
    Types.SetProjectScopeOfWorkSentMutationVariables
  >(SetProjectScopeOfWorkSentDocument, variables, options)

export const AddInvestorBidDocument = new TypedDocumentString(`
    mutation AddInvestorBid($orgSlug: String!, $bundlePid: String!, $input: InvestorBidInput!, $draftPid: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addInvestorBid(input: $input, draftPid: $draftPid) {
        __typename
        ... on InvestorBid {
          ppa {
            pid
          }
          cs {
            pid
          }
          cash {
            pid
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useAddInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddInvestorBidMutation,
    TError,
    Types.AddInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddInvestorBidMutation,
    TError,
    Types.AddInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["AddInvestorBid"],
    mutationFn: (variables?: Types.AddInvestorBidMutationVariables) =>
      publicFetcher<
        Types.AddInvestorBidMutation,
        Types.AddInvestorBidMutationVariables
      >(AddInvestorBidDocument, variables)(),
    ...options,
  })
}

useAddInvestorBidMutation.fetcher = (
  variables: Types.AddInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddInvestorBidMutation, Types.AddInvestorBidMutationVariables>(
    AddInvestorBidDocument,
    variables,
    options,
  )

export const SaveBidDraftDocument = new TypedDocumentString(`
    mutation SaveBidDraft($orgSlug: String!, $bundlePid: String!, $input: SaveBidDraftInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      saveBidDraft(input: $input) {
        __typename
        ... on SaveBidDraftResult {
          draft {
            ...BidDraft
          }
          newFiles {
            ...BidDraftFileRef
          }
          newDocuments {
            pid
            fileName
            downloadHref
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    fragment BidDraftFileRef on InvestorBidDraftFileRef {
  fileId
  fileName
  downloadHref
}
fragment BidDraft on InvestorBidDraft {
  pid
  type
  specPid
  updatedAt
  updatedByName
  schemaVersion
  formValues
  fileRefs {
    ...BidDraftFileRef
  }
}`)

export const useSaveBidDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SaveBidDraftMutation,
    TError,
    Types.SaveBidDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SaveBidDraftMutation,
    TError,
    Types.SaveBidDraftMutationVariables,
    TContext
  >({
    mutationKey: ["SaveBidDraft"],
    mutationFn: (variables?: Types.SaveBidDraftMutationVariables) =>
      publicFetcher<Types.SaveBidDraftMutation, Types.SaveBidDraftMutationVariables>(
        SaveBidDraftDocument,
        variables,
      )(),
    ...options,
  })
}

useSaveBidDraftMutation.fetcher = (
  variables: Types.SaveBidDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SaveBidDraftMutation, Types.SaveBidDraftMutationVariables>(
    SaveBidDraftDocument,
    variables,
    options,
  )

export const DeleteBidDraftDocument = new TypedDocumentString(`
    mutation DeleteBidDraft($orgSlug: String!, $bundlePid: String!, $draftPid: String!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      deleteBidDraft(draftPid: $draftPid) {
        message
      }
    }
  }
}
    `)

export const useDeleteBidDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteBidDraftMutation,
    TError,
    Types.DeleteBidDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteBidDraftMutation,
    TError,
    Types.DeleteBidDraftMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteBidDraft"],
    mutationFn: (variables?: Types.DeleteBidDraftMutationVariables) =>
      publicFetcher<
        Types.DeleteBidDraftMutation,
        Types.DeleteBidDraftMutationVariables
      >(DeleteBidDraftDocument, variables)(),
    ...options,
  })
}

useDeleteBidDraftMutation.fetcher = (
  variables: Types.DeleteBidDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteBidDraftMutation, Types.DeleteBidDraftMutationVariables>(
    DeleteBidDraftDocument,
    variables,
    options,
  )

export const EditInvestorBidDocument = new TypedDocumentString(`
    mutation EditInvestorBid($orgSlug: String!, $bundlePid: String!, $bidPid: String!, $input: InvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      editInvestorBid(bidPid: $bidPid, input: $input) {
        __typename
        ... on InvestorBid {
          ppa {
            pid
          }
          cs {
            pid
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useEditInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditInvestorBidMutation,
    TError,
    Types.EditInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditInvestorBidMutation,
    TError,
    Types.EditInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["EditInvestorBid"],
    mutationFn: (variables?: Types.EditInvestorBidMutationVariables) =>
      publicFetcher<
        Types.EditInvestorBidMutation,
        Types.EditInvestorBidMutationVariables
      >(EditInvestorBidDocument, variables)(),
    ...options,
  })
}

useEditInvestorBidMutation.fetcher = (
  variables: Types.EditInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EditInvestorBidMutation, Types.EditInvestorBidMutationVariables>(
    EditInvestorBidDocument,
    variables,
    options,
  )

export const DuplicateInvestorBidDocument = new TypedDocumentString(`
    mutation DuplicateInvestorBid($orgSlug: String!, $bundlePid: String!, $input: DuplicateInvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      duplicateInvestorBid(input: $input) {
        ppa {
          pid
        }
        cs {
          pid
        }
      }
    }
  }
}
    `)

export const useDuplicateInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DuplicateInvestorBidMutation,
    TError,
    Types.DuplicateInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DuplicateInvestorBidMutation,
    TError,
    Types.DuplicateInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["DuplicateInvestorBid"],
    mutationFn: (variables?: Types.DuplicateInvestorBidMutationVariables) =>
      publicFetcher<
        Types.DuplicateInvestorBidMutation,
        Types.DuplicateInvestorBidMutationVariables
      >(DuplicateInvestorBidDocument, variables)(),
    ...options,
  })
}

useDuplicateInvestorBidMutation.fetcher = (
  variables: Types.DuplicateInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DuplicateInvestorBidMutation,
    Types.DuplicateInvestorBidMutationVariables
  >(DuplicateInvestorBidDocument, variables, options)

export const AddSystemDocument = new TypedDocumentString(`
    mutation AddSystem($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $input: AddSystemInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addSystem(sitePid: $sitePid, input: $input) {
        system {
          pid
        }
        lite {
          pid
        }
      }
    }
  }
}
    `)

export const useAddSystemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddSystemMutation,
    TError,
    Types.AddSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddSystemMutation,
    TError,
    Types.AddSystemMutationVariables,
    TContext
  >({
    mutationKey: ["AddSystem"],
    mutationFn: (variables?: Types.AddSystemMutationVariables) =>
      publicFetcher<Types.AddSystemMutation, Types.AddSystemMutationVariables>(
        AddSystemDocument,
        variables,
      )(),
    ...options,
  })
}

useAddSystemMutation.fetcher = (
  variables: Types.AddSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddSystemMutation, Types.AddSystemMutationVariables>(
    AddSystemDocument,
    variables,
    options,
  )

export const UpdateEvOverridesDocument = new TypedDocumentString(`
    mutation UpdateEVOverrides($sitePid: String!, $projectStartDate: DateTime, $discountRate: Float, $usdPerKwhMarkup: Float, $incentivePct: Float, $salesTaxPct: Float, $omUsdPerChargerPerYr: Float, $rateManagementUsdPerChargerPerYr: Float, $cmsUsdPerChargerPerYr: Float, $demandUsdPerKw: Float, $installType: ChargerInstallType, $termYears: Int, $lowEnergyUsdPerKwh: Float, $highEnergyUsdPerKwh: Float, $onPeakChargingPct: Float, $numChargers: Float, $chargerLocation: ChargerLocation, $chargerKw: Float, $milesDrivenPerYear: Float, $milesPerKwh: Float, $maxUtilization: Float, $transactionFeePct: Float, $onSiteChargingPct: Float, $yr0NumTenantEvs: Int, $evGrowthPct: Float, $incentiveCapNumChargers: Int, $maxEvAdoptionPct: Float, $installCostUsd: Float) {
  site(sitePid: $sitePid) {
    evOverrides {
      update(
        projectStartDate: $projectStartDate
        discountRate: $discountRate
        usdPerKwhMarkup: $usdPerKwhMarkup
        incentivePct: $incentivePct
        salesTaxPct: $salesTaxPct
        omUsdPerChargerPerYr: $omUsdPerChargerPerYr
        rateManagementUsdPerChargerPerYr: $rateManagementUsdPerChargerPerYr
        cmsUsdPerChargerPerYr: $cmsUsdPerChargerPerYr
        demandUsdPerKw: $demandUsdPerKw
        installType: $installType
        termYears: $termYears
        lowEnergyUsdPerKwh: $lowEnergyUsdPerKwh
        highEnergyUsdPerKwh: $highEnergyUsdPerKwh
        onPeakChargingPct: $onPeakChargingPct
        numChargers: $numChargers
        chargerLocation: $chargerLocation
        chargerKw: $chargerKw
        milesDrivenPerYear: $milesDrivenPerYear
        milesPerKwh: $milesPerKwh
        maxUtilization: $maxUtilization
        transactionFeePct: $transactionFeePct
        onSiteChargingPct: $onSiteChargingPct
        yr0NumTenantEvs: $yr0NumTenantEvs
        evGrowthPct: $evGrowthPct
        incentiveCapNumChargers: $incentiveCapNumChargers
        maxEvAdoptionPct: $maxEvAdoptionPct
        installCostUsd: $installCostUsd
      )
    }
  }
}
    `)

export const useUpdateEvOverridesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateEvOverridesMutation,
    TError,
    Types.UpdateEvOverridesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateEvOverridesMutation,
    TError,
    Types.UpdateEvOverridesMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateEVOverrides"],
    mutationFn: (variables?: Types.UpdateEvOverridesMutationVariables) =>
      publicFetcher<
        Types.UpdateEvOverridesMutation,
        Types.UpdateEvOverridesMutationVariables
      >(UpdateEvOverridesDocument, variables)(),
    ...options,
  })
}

useUpdateEvOverridesMutation.fetcher = (
  variables: Types.UpdateEvOverridesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateEvOverridesMutation,
    Types.UpdateEvOverridesMutationVariables
  >(UpdateEvOverridesDocument, variables, options)

export const ArchiveSiteDocument = new TypedDocumentString(`
    mutation ArchiveSite($sitePid: String!, $reason: StatusReason!, $solarLabelSource: HumanSolarLabelSource! = PROPERTY_TOAST) {
  site(sitePid: $sitePid) {
    archiveSite(reason: $reason, solarLabelSource: $solarLabelSource) {
      __typename
    }
  }
}
    `)

export const useArchiveSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveSiteMutation,
    TError,
    Types.ArchiveSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveSiteMutation,
    TError,
    Types.ArchiveSiteMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveSite"],
    mutationFn: (variables?: Types.ArchiveSiteMutationVariables) =>
      publicFetcher<Types.ArchiveSiteMutation, Types.ArchiveSiteMutationVariables>(
        ArchiveSiteDocument,
        variables,
      )(),
    ...options,
  })
}

useArchiveSiteMutation.fetcher = (
  variables: Types.ArchiveSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ArchiveSiteMutation, Types.ArchiveSiteMutationVariables>(
    ArchiveSiteDocument,
    variables,
    options,
  )

export const ResolveSiteFlagDocument = new TypedDocumentString(`
    mutation ResolveSiteFlag($orgSlug: String!, $sitePid: String!, $flagType: SiteFlagType!, $solarLabelSource: HumanSolarLabelSource! = PROPERTY_TOAST) {
  orgBySlug(slug: $orgSlug) {
    flags {
      resolveSiteFlag(
        sitePid: $sitePid
        flagType: $flagType
        solarLabelSource: $solarLabelSource
      ) {
        __typename
      }
    }
  }
}
    `)

export const useResolveSiteFlagMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ResolveSiteFlagMutation,
    TError,
    Types.ResolveSiteFlagMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ResolveSiteFlagMutation,
    TError,
    Types.ResolveSiteFlagMutationVariables,
    TContext
  >({
    mutationKey: ["ResolveSiteFlag"],
    mutationFn: (variables?: Types.ResolveSiteFlagMutationVariables) =>
      publicFetcher<
        Types.ResolveSiteFlagMutation,
        Types.ResolveSiteFlagMutationVariables
      >(ResolveSiteFlagDocument, variables)(),
    ...options,
  })
}

useResolveSiteFlagMutation.fetcher = (
  variables: Types.ResolveSiteFlagMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResolveSiteFlagMutation, Types.ResolveSiteFlagMutationVariables>(
    ResolveSiteFlagDocument,
    variables,
    options,
  )

export const CreatePortfolioDocument = new TypedDocumentString(`
    mutation CreatePortfolio($orgSlug: String!, $name: String!) {
  orgBySlug(slug: $orgSlug) {
    createPortfolio(name: $name) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useCreatePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePortfolio"],
    mutationFn: (variables?: Types.CreatePortfolioMutationVariables) =>
      publicFetcher<
        Types.CreatePortfolioMutation,
        Types.CreatePortfolioMutationVariables
      >(CreatePortfolioDocument, variables)(),
    ...options,
  })
}

useCreatePortfolioMutation.fetcher = (
  variables: Types.CreatePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreatePortfolioMutation, Types.CreatePortfolioMutationVariables>(
    CreatePortfolioDocument,
    variables,
    options,
  )

export const GrantRoleDocument = new TypedDocumentString(`
    mutation GrantRole($orgSlug: String!, $input: UserRoleInput!) {
  orgBySlug(slug: $orgSlug) {
    roles {
      grantRole(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useGrantRoleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.GrantRoleMutation,
    TError,
    Types.GrantRoleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.GrantRoleMutation,
    TError,
    Types.GrantRoleMutationVariables,
    TContext
  >({
    mutationKey: ["GrantRole"],
    mutationFn: (variables?: Types.GrantRoleMutationVariables) =>
      publicFetcher<Types.GrantRoleMutation, Types.GrantRoleMutationVariables>(
        GrantRoleDocument,
        variables,
      )(),
    ...options,
  })
}

useGrantRoleMutation.fetcher = (
  variables: Types.GrantRoleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GrantRoleMutation, Types.GrantRoleMutationVariables>(
    GrantRoleDocument,
    variables,
    options,
  )

export const RevokeRoleDocument = new TypedDocumentString(`
    mutation RevokeRole($orgSlug: String!, $input: UserRoleInput!) {
  orgBySlug(slug: $orgSlug) {
    roles {
      revokeRole(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useRevokeRoleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RevokeRoleMutation,
    TError,
    Types.RevokeRoleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RevokeRoleMutation,
    TError,
    Types.RevokeRoleMutationVariables,
    TContext
  >({
    mutationKey: ["RevokeRole"],
    mutationFn: (variables?: Types.RevokeRoleMutationVariables) =>
      publicFetcher<Types.RevokeRoleMutation, Types.RevokeRoleMutationVariables>(
        RevokeRoleDocument,
        variables,
      )(),
    ...options,
  })
}

useRevokeRoleMutation.fetcher = (
  variables: Types.RevokeRoleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RevokeRoleMutation, Types.RevokeRoleMutationVariables>(
    RevokeRoleDocument,
    variables,
    options,
  )

export const RemoveUserDocument = new TypedDocumentString(`
    mutation RemoveUser($orgSlug: String!, $userPid: String!) {
  orgBySlug(slug: $orgSlug) {
    roles {
      removeUser(userPid: $userPid) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useRemoveUserMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveUserMutation,
    TError,
    Types.RemoveUserMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveUserMutation,
    TError,
    Types.RemoveUserMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveUser"],
    mutationFn: (variables?: Types.RemoveUserMutationVariables) =>
      publicFetcher<Types.RemoveUserMutation, Types.RemoveUserMutationVariables>(
        RemoveUserDocument,
        variables,
      )(),
    ...options,
  })
}

useRemoveUserMutation.fetcher = (
  variables: Types.RemoveUserMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RemoveUserMutation, Types.RemoveUserMutationVariables>(
    RemoveUserDocument,
    variables,
    options,
  )

export const UpdateInvestorProfileDocument = new TypedDocumentString(`
    mutation UpdateInvestorProfile($orgSlug: String!, $hq: String, $yearFounded: Int, $website: String, $managementTeam: [String!]!, $description: String, $distinguishingFeatures: String, $investorBacking: String, $longTermOwnershipApproach: String, $subscriptionMgmtApproach: String, $committedFunding: String, $experiences: [ExperienceInput!]!) {
  orgBySlug(slug: $orgSlug) {
    investorProfile {
      updateProfile(
        hq: $hq
        yearFounded: $yearFounded
        website: $website
        managementTeam: $managementTeam
        description: $description
        distinguishingFeatures: $distinguishingFeatures
        investorBacking: $investorBacking
        longTermOwnershipApproach: $longTermOwnershipApproach
        subscriptionMgmtApproach: $subscriptionMgmtApproach
        committedFunding: $committedFunding
        experiences: $experiences
      )
    }
  }
}
    `)

export const useUpdateInvestorProfileMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateInvestorProfileMutation,
    TError,
    Types.UpdateInvestorProfileMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateInvestorProfileMutation,
    TError,
    Types.UpdateInvestorProfileMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateInvestorProfile"],
    mutationFn: (variables?: Types.UpdateInvestorProfileMutationVariables) =>
      publicFetcher<
        Types.UpdateInvestorProfileMutation,
        Types.UpdateInvestorProfileMutationVariables
      >(UpdateInvestorProfileDocument, variables)(),
    ...options,
  })
}

useUpdateInvestorProfileMutation.fetcher = (
  variables: Types.UpdateInvestorProfileMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateInvestorProfileMutation,
    Types.UpdateInvestorProfileMutationVariables
  >(UpdateInvestorProfileDocument, variables, options)

export const UpdateInvestorBuyBoxDocument = new TypedDocumentString(`
    mutation UpdateInvestorBuyBox($orgSlug: String!, $buyBoxes: [BuyBoxInput!]!) {
  orgBySlug(slug: $orgSlug) {
    investorProfile {
      updateBuyBox(buyBoxes: $buyBoxes)
    }
  }
}
    `)

export const useUpdateInvestorBuyBoxMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateInvestorBuyBoxMutation,
    TError,
    Types.UpdateInvestorBuyBoxMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateInvestorBuyBoxMutation,
    TError,
    Types.UpdateInvestorBuyBoxMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateInvestorBuyBox"],
    mutationFn: (variables?: Types.UpdateInvestorBuyBoxMutationVariables) =>
      publicFetcher<
        Types.UpdateInvestorBuyBoxMutation,
        Types.UpdateInvestorBuyBoxMutationVariables
      >(UpdateInvestorBuyBoxDocument, variables)(),
    ...options,
  })
}

useUpdateInvestorBuyBoxMutation.fetcher = (
  variables: Types.UpdateInvestorBuyBoxMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateInvestorBuyBoxMutation,
    Types.UpdateInvestorBuyBoxMutationVariables
  >(UpdateInvestorBuyBoxDocument, variables, options)

export const EvButtonDocument = new TypedDocumentString(`
    mutation EVButton($sitePid: String!, $button: String!) {
  site(sitePid: $sitePid) {
    evButton {
      press(button: $button)
    }
  }
}
    `)

export const useEvButtonMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EvButtonMutation,
    TError,
    Types.EvButtonMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EvButtonMutation,
    TError,
    Types.EvButtonMutationVariables,
    TContext
  >({
    mutationKey: ["EVButton"],
    mutationFn: (variables?: Types.EvButtonMutationVariables) =>
      publicFetcher<Types.EvButtonMutation, Types.EvButtonMutationVariables>(
        EvButtonDocument,
        variables,
      )(),
    ...options,
  })
}

useEvButtonMutation.fetcher = (
  variables: Types.EvButtonMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EvButtonMutation, Types.EvButtonMutationVariables>(
    EvButtonDocument,
    variables,
    options,
  )

export const SetArchiveInvestorBidDocument = new TypedDocumentString(`
    mutation SetArchiveInvestorBid($orgSlug: String!, $bundlePid: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      archiveInvestorBid(archive: $value, bidPid: $bidPid)
    }
  }
}
    `)

export const useSetArchiveInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetArchiveInvestorBidMutation,
    TError,
    Types.SetArchiveInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchiveInvestorBidMutation,
    TError,
    Types.SetArchiveInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchiveInvestorBid"],
    mutationFn: (variables?: Types.SetArchiveInvestorBidMutationVariables) =>
      publicFetcher<
        Types.SetArchiveInvestorBidMutation,
        Types.SetArchiveInvestorBidMutationVariables
      >(SetArchiveInvestorBidDocument, variables)(),
    ...options,
  })
}

useSetArchiveInvestorBidMutation.fetcher = (
  variables: Types.SetArchiveInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchiveInvestorBidMutation,
    Types.SetArchiveInvestorBidMutationVariables
  >(SetArchiveInvestorBidDocument, variables, options)

export const SetBundleRowOptOutDocument = new TypedDocumentString(`
    mutation SetBundleRowOptOut($orgSlug: String!, $bundlePid: String!, $bundleRowId: Int!, $value: Boolean!, $reason: InvestorOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleOptOut(
        optOut: $value
        input: { bundleRowId: $bundleRowId }
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `)

export const useSetBundleRowOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBundleRowOptOutMutation,
    TError,
    Types.SetBundleRowOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleRowOptOutMutation,
    TError,
    Types.SetBundleRowOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleRowOptOut"],
    mutationFn: (variables?: Types.SetBundleRowOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBundleRowOptOutMutation,
        Types.SetBundleRowOptOutMutationVariables
      >(SetBundleRowOptOutDocument, variables)(),
    ...options,
  })
}

useSetBundleRowOptOutMutation.fetcher = (
  variables: Types.SetBundleRowOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleRowOptOutMutation,
    Types.SetBundleRowOptOutMutationVariables
  >(SetBundleRowOptOutDocument, variables, options)

export const SetBundleRowDeveloperNotesDocument = new TypedDocumentString(`
    mutation SetBundleRowDeveloperNotes($orgSlug: String!, $bundlePid: String!, $bundleRowPid: String!, $content: QuillDelta) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      setRowDeveloperNotes(bundleRowPid: $bundleRowPid, content: $content) {
        message
      }
    }
  }
}
    `)

export const useSetBundleRowDeveloperNotesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleRowDeveloperNotesMutation,
    TError,
    Types.SetBundleRowDeveloperNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleRowDeveloperNotesMutation,
    TError,
    Types.SetBundleRowDeveloperNotesMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleRowDeveloperNotes"],
    mutationFn: (variables?: Types.SetBundleRowDeveloperNotesMutationVariables) =>
      publicFetcher<
        Types.SetBundleRowDeveloperNotesMutation,
        Types.SetBundleRowDeveloperNotesMutationVariables
      >(SetBundleRowDeveloperNotesDocument, variables)(),
    ...options,
  })
}

useSetBundleRowDeveloperNotesMutation.fetcher = (
  variables: Types.SetBundleRowDeveloperNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleRowDeveloperNotesMutation,
    Types.SetBundleRowDeveloperNotesMutationVariables
  >(SetBundleRowDeveloperNotesDocument, variables, options)

export const ConfirmBidIntentDocument = new TypedDocumentString(`
    mutation ConfirmBidIntent($orgSlug: String!, $bundlePid: String!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      confirmBidIntent
    }
  }
}
    `)

export const useConfirmBidIntentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ConfirmBidIntentMutation,
    TError,
    Types.ConfirmBidIntentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ConfirmBidIntentMutation,
    TError,
    Types.ConfirmBidIntentMutationVariables,
    TContext
  >({
    mutationKey: ["ConfirmBidIntent"],
    mutationFn: (variables?: Types.ConfirmBidIntentMutationVariables) =>
      publicFetcher<
        Types.ConfirmBidIntentMutation,
        Types.ConfirmBidIntentMutationVariables
      >(ConfirmBidIntentDocument, variables)(),
    ...options,
  })
}

useConfirmBidIntentMutation.fetcher = (
  variables: Types.ConfirmBidIntentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ConfirmBidIntentMutation,
    Types.ConfirmBidIntentMutationVariables
  >(ConfirmBidIntentDocument, variables, options)

export const SetBundleOptOutDocument = new TypedDocumentString(`
    mutation SetBundleOptOut($orgSlug: String!, $bundlePid: String!, $value: Boolean!, $reason: InvestorOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleOptOut(
        optOut: $value
        input: { bundlePid: $bundlePid }
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `)

export const useSetBundleOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBundleOptOutMutation,
    TError,
    Types.SetBundleOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleOptOutMutation,
    TError,
    Types.SetBundleOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleOptOut"],
    mutationFn: (variables?: Types.SetBundleOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBundleOptOutMutation,
        Types.SetBundleOptOutMutationVariables
      >(SetBundleOptOutDocument, variables)(),
    ...options,
  })
}

useSetBundleOptOutMutation.fetcher = (
  variables: Types.SetBundleOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetBundleOptOutMutation, Types.SetBundleOptOutMutationVariables>(
    SetBundleOptOutDocument,
    variables,
    options,
  )

export const SetBidSpecOptOutDocument = new TypedDocumentString(`
    mutation SetBidSpecOptOut($orgSlug: String!, $bundlePid: String!, $bidSpecPid: String!, $value: Boolean!, $reason: SpecOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleSpecOptOut(
        optOut: $value
        bidSpecPid: $bidSpecPid
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `)

export const useSetBidSpecOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBidSpecOptOutMutation,
    TError,
    Types.SetBidSpecOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBidSpecOptOutMutation,
    TError,
    Types.SetBidSpecOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBidSpecOptOut"],
    mutationFn: (variables?: Types.SetBidSpecOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBidSpecOptOutMutation,
        Types.SetBidSpecOptOutMutationVariables
      >(SetBidSpecOptOutDocument, variables)(),
    ...options,
  })
}

useSetBidSpecOptOutMutation.fetcher = (
  variables: Types.SetBidSpecOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBidSpecOptOutMutation,
    Types.SetBidSpecOptOutMutationVariables
  >(SetBidSpecOptOutDocument, variables, options)

export const SubmitBundleBidsDocument = new TypedDocumentString(`
    mutation SubmitBundleBids($orgSlug: String!, $bundlePid: String!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      submitBids(pricingTermsAgreed: true)
    }
  }
}
    `)

export const useSubmitBundleBidsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SubmitBundleBidsMutation,
    TError,
    Types.SubmitBundleBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SubmitBundleBidsMutation,
    TError,
    Types.SubmitBundleBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SubmitBundleBids"],
    mutationFn: (variables?: Types.SubmitBundleBidsMutationVariables) =>
      publicFetcher<
        Types.SubmitBundleBidsMutation,
        Types.SubmitBundleBidsMutationVariables
      >(SubmitBundleBidsDocument, variables)(),
    ...options,
  })
}

useSubmitBundleBidsMutation.fetcher = (
  variables: Types.SubmitBundleBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SubmitBundleBidsMutation,
    Types.SubmitBundleBidsMutationVariables
  >(SubmitBundleBidsDocument, variables, options)

export const AddHelioscopeDocument = new TypedDocumentString(`
    mutation AddHelioscope($sitePid: String!, $bundlePid: String, $reportPdf: Upload!, $productionCsv: Upload) {
  addHelioscope(
    sitePid: $sitePid
    bundlePid: $bundlePid
    reportPdf: $reportPdf
    productionCsv: $productionCsv
  ) {
    __typename
    ... on Helioscope2 {
      ...Helioscope2Ref
    }
    ... on Error {
      message
    }
  }
}
    fragment Helioscope2Ref on Helioscope2 {
  id
  pdf {
    downloadHref
  }
  roofImage {
    viewHref
  }
  productionCsv {
    downloadHref
  }
}`)

export const useAddHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["AddHelioscope"],
    mutationFn: (variables?: Types.AddHelioscopeMutationVariables) =>
      publicFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
        AddHelioscopeDocument,
        variables,
      )(),
    ...options,
  })
}

useAddHelioscopeMutation.fetcher = (
  variables: Types.AddHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
    AddHelioscopeDocument,
    variables,
    options,
  )

export const SetArchiveBidSpecDocument = new TypedDocumentString(`
    mutation SetArchiveBidSpec($orgSlug: String!, $bundleParentPid: String!, $specPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        archiveSpec(specPid: $specPid, archive: $value)
      }
    }
  }
}
    `)

export const useSetArchiveBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetArchiveBidSpecMutation,
    TError,
    Types.SetArchiveBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchiveBidSpecMutation,
    TError,
    Types.SetArchiveBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchiveBidSpec"],
    mutationFn: (variables?: Types.SetArchiveBidSpecMutationVariables) =>
      publicFetcher<
        Types.SetArchiveBidSpecMutation,
        Types.SetArchiveBidSpecMutationVariables
      >(SetArchiveBidSpecDocument, variables)(),
    ...options,
  })
}

useSetArchiveBidSpecMutation.fetcher = (
  variables: Types.SetArchiveBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchiveBidSpecMutation,
    Types.SetArchiveBidSpecMutationVariables
  >(SetArchiveBidSpecDocument, variables, options)

export const SetPackageSiteNotesDocument = new TypedDocumentString(`
    mutation SetPackageSiteNotes($orgSlug: String!, $bundleParentPid: String!, $projectPid: String!, $notes: String) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setRowNotes(projectPid: $projectPid, notes: $notes)
      }
    }
  }
}
    `)

export const useSetPackageSiteNotesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPackageSiteNotesMutation,
    TError,
    Types.SetPackageSiteNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPackageSiteNotesMutation,
    TError,
    Types.SetPackageSiteNotesMutationVariables,
    TContext
  >({
    mutationKey: ["SetPackageSiteNotes"],
    mutationFn: (variables?: Types.SetPackageSiteNotesMutationVariables) =>
      publicFetcher<
        Types.SetPackageSiteNotesMutation,
        Types.SetPackageSiteNotesMutationVariables
      >(SetPackageSiteNotesDocument, variables)(),
    ...options,
  })
}

useSetPackageSiteNotesMutation.fetcher = (
  variables: Types.SetPackageSiteNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPackageSiteNotesMutation,
    Types.SetPackageSiteNotesMutationVariables
  >(SetPackageSiteNotesDocument, variables, options)

export const AddInvestorBidSpecDocument = new TypedDocumentString(`
    mutation AddInvestorBidSpec($orgSlug: String!, $bundleParentPid: String!, $projectPid: String!, $input: BidSpecInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addSpec(projectPid: $projectPid, input: $input) {
          ppa {
            pid
          }
          cs {
            pid
          }
          cash {
            pid
          }
        }
      }
    }
  }
}
    `)

export const useAddInvestorBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddInvestorBidSpecMutation,
    TError,
    Types.AddInvestorBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddInvestorBidSpecMutation,
    TError,
    Types.AddInvestorBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["AddInvestorBidSpec"],
    mutationFn: (variables?: Types.AddInvestorBidSpecMutationVariables) =>
      publicFetcher<
        Types.AddInvestorBidSpecMutation,
        Types.AddInvestorBidSpecMutationVariables
      >(AddInvestorBidSpecDocument, variables)(),
    ...options,
  })
}

useAddInvestorBidSpecMutation.fetcher = (
  variables: Types.AddInvestorBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddInvestorBidSpecMutation,
    Types.AddInvestorBidSpecMutationVariables
  >(AddInvestorBidSpecDocument, variables, options)

export const EditInvestorBidSpecDocument = new TypedDocumentString(`
    mutation EditInvestorBidSpec($orgSlug: String!, $bundleParentPid: String!, $projectPid: String!, $specPid: String!, $input: BidSpecInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editSpec(specPid: $specPid, projectPid: $projectPid, input: $input) {
          ppa {
            pid
          }
          cs {
            pid
          }
          cash {
            pid
          }
        }
      }
    }
  }
}
    `)

export const useEditInvestorBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditInvestorBidSpecMutation,
    TError,
    Types.EditInvestorBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditInvestorBidSpecMutation,
    TError,
    Types.EditInvestorBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["EditInvestorBidSpec"],
    mutationFn: (variables?: Types.EditInvestorBidSpecMutationVariables) =>
      publicFetcher<
        Types.EditInvestorBidSpecMutation,
        Types.EditInvestorBidSpecMutationVariables
      >(EditInvestorBidSpecDocument, variables)(),
    ...options,
  })
}

useEditInvestorBidSpecMutation.fetcher = (
  variables: Types.EditInvestorBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditInvestorBidSpecMutation,
    Types.EditInvestorBidSpecMutationVariables
  >(EditInvestorBidSpecDocument, variables, options)

export const AddBundleParentEtbDocument = new TypedDocumentString(`
    mutation AddBundleParentEtb($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $upload: Upload!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addEtb(sitePid: $sitePid, upload: $upload) {
          ...EtbExtract
        }
      }
    }
  }
}
    fragment EtbExtract on ETBExtract {
  id
  document {
    id
    pid
    fileName
    downloadHref
  }
  pv {
    sizeKwDc
    sizeKwAc
    etbMeterId
    etbMeterName
  }
  battery {
    sizeKwDc
    sizeKwh
    etbMeterId
    etbMeterName
  }
}`)

export const useAddBundleParentEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleParentEtbMutation,
    TError,
    Types.AddBundleParentEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentEtbMutation,
    TError,
    Types.AddBundleParentEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentEtb"],
    mutationFn: (variables?: Types.AddBundleParentEtbMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentEtbMutation,
        Types.AddBundleParentEtbMutationVariables
      >(AddBundleParentEtbDocument, variables)(),
    ...options,
  })
}

useAddBundleParentEtbMutation.fetcher = (
  variables: Types.AddBundleParentEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentEtbMutation,
    Types.AddBundleParentEtbMutationVariables
  >(AddBundleParentEtbDocument, variables, options)

export const AddBundleEtbDocument = new TypedDocumentString(`
    mutation AddBundleEtb($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $upload: Upload!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addEtb(sitePid: $sitePid, upload: $upload) {
        ...EtbExtract
      }
    }
  }
}
    fragment EtbExtract on ETBExtract {
  id
  document {
    id
    pid
    fileName
    downloadHref
  }
  pv {
    sizeKwDc
    sizeKwAc
    etbMeterId
    etbMeterName
  }
  battery {
    sizeKwDc
    sizeKwh
    etbMeterId
    etbMeterName
  }
}`)

export const useAddBundleEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleEtbMutation,
    TError,
    Types.AddBundleEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleEtbMutation,
    TError,
    Types.AddBundleEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleEtb"],
    mutationFn: (variables?: Types.AddBundleEtbMutationVariables) =>
      publicFetcher<Types.AddBundleEtbMutation, Types.AddBundleEtbMutationVariables>(
        AddBundleEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useAddBundleEtbMutation.fetcher = (
  variables: Types.AddBundleEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddBundleEtbMutation, Types.AddBundleEtbMutationVariables>(
    AddBundleEtbDocument,
    variables,
    options,
  )

export const SetPackageRowStatusDocument = new TypedDocumentString(`
    mutation SetPackageRowStatus($bundleParentPid: String!, $projectPid: String!, $status: PackageRowStatus, $closed: Boolean, $closeReason: PackageRowCloseReason, $closeCustomReason: String, $closedAt: DateTime) {
  setPackageRowStatus(
    bundleParentPid: $bundleParentPid
    projectPid: $projectPid
    status: $status
    closed: $closed
    closeReason: $closeReason
    customCloseReason: $closeCustomReason
    closedAt: $closedAt
  )
}
    `)

export const useSetPackageRowStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPackageRowStatusMutation,
    TError,
    Types.SetPackageRowStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPackageRowStatusMutation,
    TError,
    Types.SetPackageRowStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetPackageRowStatus"],
    mutationFn: (variables?: Types.SetPackageRowStatusMutationVariables) =>
      publicFetcher<
        Types.SetPackageRowStatusMutation,
        Types.SetPackageRowStatusMutationVariables
      >(SetPackageRowStatusDocument, variables)(),
    ...options,
  })
}

useSetPackageRowStatusMutation.fetcher = (
  variables: Types.SetPackageRowStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPackageRowStatusMutation,
    Types.SetPackageRowStatusMutationVariables
  >(SetPackageRowStatusDocument, variables, options)

export const AwardInvestorBidDocument = new TypedDocumentString(`
    mutation AwardInvestorBid($orgSlug: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    awardInvestorBid(bidPid: $bidPid, value: $value)
  }
}
    `)

export const useAwardInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AwardInvestorBidMutation,
    TError,
    Types.AwardInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AwardInvestorBidMutation,
    TError,
    Types.AwardInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["AwardInvestorBid"],
    mutationFn: (variables?: Types.AwardInvestorBidMutationVariables) =>
      publicFetcher<
        Types.AwardInvestorBidMutation,
        Types.AwardInvestorBidMutationVariables
      >(AwardInvestorBidDocument, variables)(),
    ...options,
  })
}

useAwardInvestorBidMutation.fetcher = (
  variables: Types.AwardInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardInvestorBidMutation,
    Types.AwardInvestorBidMutationVariables
  >(AwardInvestorBidDocument, variables, options)

export const RecommendInvestorBidDocument = new TypedDocumentString(`
    mutation RecommendInvestorBid($orgSlug: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    recommendInvestorBid(bidPid: $bidPid, value: $value)
  }
}
    `)

export const useRecommendInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RecommendInvestorBidMutation,
    TError,
    Types.RecommendInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RecommendInvestorBidMutation,
    TError,
    Types.RecommendInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["RecommendInvestorBid"],
    mutationFn: (variables?: Types.RecommendInvestorBidMutationVariables) =>
      publicFetcher<
        Types.RecommendInvestorBidMutation,
        Types.RecommendInvestorBidMutationVariables
      >(RecommendInvestorBidDocument, variables)(),
    ...options,
  })
}

useRecommendInvestorBidMutation.fetcher = (
  variables: Types.RecommendInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RecommendInvestorBidMutation,
    Types.RecommendInvestorBidMutationVariables
  >(RecommendInvestorBidDocument, variables, options)

export const ArchiveBundleParentDocument = new TypedDocumentString(`
    mutation ArchiveBundleParent($orgSlug: String!, $bundleParentPid: String!, $archive: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        archiveBundleParent(archive: $archive) {
          message
        }
      }
    }
  }
}
    `)

export const useArchiveBundleParentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveBundleParentMutation,
    TError,
    Types.ArchiveBundleParentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveBundleParentMutation,
    TError,
    Types.ArchiveBundleParentMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveBundleParent"],
    mutationFn: (variables?: Types.ArchiveBundleParentMutationVariables) =>
      publicFetcher<
        Types.ArchiveBundleParentMutation,
        Types.ArchiveBundleParentMutationVariables
      >(ArchiveBundleParentDocument, variables)(),
    ...options,
  })
}

useArchiveBundleParentMutation.fetcher = (
  variables: Types.ArchiveBundleParentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ArchiveBundleParentMutation,
    Types.ArchiveBundleParentMutationVariables
  >(ArchiveBundleParentDocument, variables, options)

export const SetBundleParentHiddenFromFinancierDocument = new TypedDocumentString(`
    mutation SetBundleParentHiddenFromFinancier($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setHiddenFromFinancier(hidden: $value)
      }
    }
  }
}
    `)

export const useSetBundleParentHiddenFromFinancierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentHiddenFromFinancierMutation,
    TError,
    Types.SetBundleParentHiddenFromFinancierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentHiddenFromFinancierMutation,
    TError,
    Types.SetBundleParentHiddenFromFinancierMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentHiddenFromFinancier"],
    mutationFn: (
      variables?: Types.SetBundleParentHiddenFromFinancierMutationVariables,
    ) =>
      publicFetcher<
        Types.SetBundleParentHiddenFromFinancierMutation,
        Types.SetBundleParentHiddenFromFinancierMutationVariables
      >(SetBundleParentHiddenFromFinancierDocument, variables)(),
    ...options,
  })
}

useSetBundleParentHiddenFromFinancierMutation.fetcher = (
  variables: Types.SetBundleParentHiddenFromFinancierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentHiddenFromFinancierMutation,
    Types.SetBundleParentHiddenFromFinancierMutationVariables
  >(SetBundleParentHiddenFromFinancierDocument, variables, options)

export const SetBundleParentHiddenFromReceiverDocument = new TypedDocumentString(`
    mutation SetBundleParentHiddenFromReceiver($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setHiddenFromRecvr(hidden: $value)
      }
    }
  }
}
    `)

export const useSetBundleParentHiddenFromReceiverMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentHiddenFromReceiverMutation,
    TError,
    Types.SetBundleParentHiddenFromReceiverMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentHiddenFromReceiverMutation,
    TError,
    Types.SetBundleParentHiddenFromReceiverMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentHiddenFromReceiver"],
    mutationFn: (
      variables?: Types.SetBundleParentHiddenFromReceiverMutationVariables,
    ) =>
      publicFetcher<
        Types.SetBundleParentHiddenFromReceiverMutation,
        Types.SetBundleParentHiddenFromReceiverMutationVariables
      >(SetBundleParentHiddenFromReceiverDocument, variables)(),
    ...options,
  })
}

useSetBundleParentHiddenFromReceiverMutation.fetcher = (
  variables: Types.SetBundleParentHiddenFromReceiverMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentHiddenFromReceiverMutation,
    Types.SetBundleParentHiddenFromReceiverMutationVariables
  >(SetBundleParentHiddenFromReceiverDocument, variables, options)

export const LaunchBundleParentDocument = new TypedDocumentString(`
    mutation LaunchBundleParent($orgSlug: String!, $bundleParentPid: String!, $biddingClosedAt: DateTime) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        launch(biddingClosedAt: $biddingClosedAt) {
          message
        }
      }
    }
  }
}
    `)

export const useLaunchBundleParentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.LaunchBundleParentMutation,
    TError,
    Types.LaunchBundleParentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LaunchBundleParentMutation,
    TError,
    Types.LaunchBundleParentMutationVariables,
    TContext
  >({
    mutationKey: ["LaunchBundleParent"],
    mutationFn: (variables?: Types.LaunchBundleParentMutationVariables) =>
      publicFetcher<
        Types.LaunchBundleParentMutation,
        Types.LaunchBundleParentMutationVariables
      >(LaunchBundleParentDocument, variables)(),
    ...options,
  })
}

useLaunchBundleParentMutation.fetcher = (
  variables: Types.LaunchBundleParentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.LaunchBundleParentMutation,
    Types.LaunchBundleParentMutationVariables
  >(LaunchBundleParentDocument, variables, options)

export const SetBundleParentCanAddNewBidsDocument = new TypedDocumentString(`
    mutation SetBundleParentCanAddNewBids($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setCanAddNewBids(can: $value)
      }
    }
  }
}
    `)

export const useSetBundleParentCanAddNewBidsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentCanAddNewBidsMutation,
    TError,
    Types.SetBundleParentCanAddNewBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentCanAddNewBidsMutation,
    TError,
    Types.SetBundleParentCanAddNewBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentCanAddNewBids"],
    mutationFn: (variables?: Types.SetBundleParentCanAddNewBidsMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentCanAddNewBidsMutation,
        Types.SetBundleParentCanAddNewBidsMutationVariables
      >(SetBundleParentCanAddNewBidsDocument, variables)(),
    ...options,
  })
}

useSetBundleParentCanAddNewBidsMutation.fetcher = (
  variables: Types.SetBundleParentCanAddNewBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentCanAddNewBidsMutation,
    Types.SetBundleParentCanAddNewBidsMutationVariables
  >(SetBundleParentCanAddNewBidsDocument, variables, options)

export const SetBundleParentCanEditBidsDocument = new TypedDocumentString(`
    mutation SetBundleParentCanEditBids($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setCanEditBids(can: $value)
      }
    }
  }
}
    `)

export const useSetBundleParentCanEditBidsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentCanEditBidsMutation,
    TError,
    Types.SetBundleParentCanEditBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentCanEditBidsMutation,
    TError,
    Types.SetBundleParentCanEditBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentCanEditBids"],
    mutationFn: (variables?: Types.SetBundleParentCanEditBidsMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentCanEditBidsMutation,
        Types.SetBundleParentCanEditBidsMutationVariables
      >(SetBundleParentCanEditBidsDocument, variables)(),
    ...options,
  })
}

useSetBundleParentCanEditBidsMutation.fetcher = (
  variables: Types.SetBundleParentCanEditBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentCanEditBidsMutation,
    Types.SetBundleParentCanEditBidsMutationVariables
  >(SetBundleParentCanEditBidsDocument, variables, options)

export const SetBundleParentAllowDeveloperProjectNotesAndDocumentsDocument =
  new TypedDocumentString(`
    mutation SetBundleParentAllowDeveloperProjectNotesAndDocuments($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setAllowDeveloperProjectNotesAndDocuments(value: $value)
      }
    }
  }
}
    `)

export const useSetBundleParentAllowDeveloperProjectNotesAndDocumentsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutation,
    TError,
    Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutation,
    TError,
    Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentAllowDeveloperProjectNotesAndDocuments"],
    mutationFn: (
      variables?: Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutationVariables,
    ) =>
      publicFetcher<
        Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutation,
        Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutationVariables
      >(SetBundleParentAllowDeveloperProjectNotesAndDocumentsDocument, variables)(),
    ...options,
  })
}

useSetBundleParentAllowDeveloperProjectNotesAndDocumentsMutation.fetcher = (
  variables: Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutation,
    Types.SetBundleParentAllowDeveloperProjectNotesAndDocumentsMutationVariables
  >(SetBundleParentAllowDeveloperProjectNotesAndDocumentsDocument, variables, options)

export const SetBundleParentShowAllBidsReportDocument = new TypedDocumentString(`
    mutation SetBundleParentShowAllBidsReport($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setShowAllBidsReport(value: $value)
      }
    }
  }
}
    `)

export const useSetBundleParentShowAllBidsReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentShowAllBidsReportMutation,
    TError,
    Types.SetBundleParentShowAllBidsReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentShowAllBidsReportMutation,
    TError,
    Types.SetBundleParentShowAllBidsReportMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentShowAllBidsReport"],
    mutationFn: (variables?: Types.SetBundleParentShowAllBidsReportMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentShowAllBidsReportMutation,
        Types.SetBundleParentShowAllBidsReportMutationVariables
      >(SetBundleParentShowAllBidsReportDocument, variables)(),
    ...options,
  })
}

useSetBundleParentShowAllBidsReportMutation.fetcher = (
  variables: Types.SetBundleParentShowAllBidsReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentShowAllBidsReportMutation,
    Types.SetBundleParentShowAllBidsReportMutationVariables
  >(SetBundleParentShowAllBidsReportDocument, variables, options)

export const SetBundleParentOpsOwnerDocument = new TypedDocumentString(`
    mutation SetBundleParentOpsOwner($orgSlug: String!, $bundleParentPid: String!, $opsOwnerPid: String) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setOpsOwner(opsOwnerPid: $opsOwnerPid)
      }
    }
  }
}
    `)

export const useSetBundleParentOpsOwnerMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentOpsOwnerMutation,
    TError,
    Types.SetBundleParentOpsOwnerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentOpsOwnerMutation,
    TError,
    Types.SetBundleParentOpsOwnerMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentOpsOwner"],
    mutationFn: (variables?: Types.SetBundleParentOpsOwnerMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentOpsOwnerMutation,
        Types.SetBundleParentOpsOwnerMutationVariables
      >(SetBundleParentOpsOwnerDocument, variables)(),
    ...options,
  })
}

useSetBundleParentOpsOwnerMutation.fetcher = (
  variables: Types.SetBundleParentOpsOwnerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentOpsOwnerMutation,
    Types.SetBundleParentOpsOwnerMutationVariables
  >(SetBundleParentOpsOwnerDocument, variables, options)

export const UpdateSiteDetailsDocument = new TypedDocumentString(`
    mutation UpdateSiteDetails($sitePid: String!, $input: UpdateDetailsInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  siteDetails(sitePid: $sitePid) {
    updateDetails(input: $input, sourceType: $sourceType, sourceId: $sourceId)
  }
}
    `)

export const useUpdateSiteDetailsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateSiteDetailsMutation,
    TError,
    Types.UpdateSiteDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateSiteDetailsMutation,
    TError,
    Types.UpdateSiteDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateSiteDetails"],
    mutationFn: (variables?: Types.UpdateSiteDetailsMutationVariables) =>
      publicFetcher<
        Types.UpdateSiteDetailsMutation,
        Types.UpdateSiteDetailsMutationVariables
      >(UpdateSiteDetailsDocument, variables)(),
    ...options,
  })
}

useUpdateSiteDetailsMutation.fetcher = (
  variables: Types.UpdateSiteDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateSiteDetailsMutation,
    Types.UpdateSiteDetailsMutationVariables
  >(UpdateSiteDetailsDocument, variables, options)

export const UpdateSiteAddressDocument = new TypedDocumentString(`
    mutation UpdateSiteAddress($sitePid: String!, $input: SetAddressInput!) {
  site(sitePid: $sitePid) {
    setAddress(input: $input)
  }
}
    `)

export const useUpdateSiteAddressMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateSiteAddressMutation,
    TError,
    Types.UpdateSiteAddressMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateSiteAddressMutation,
    TError,
    Types.UpdateSiteAddressMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateSiteAddress"],
    mutationFn: (variables?: Types.UpdateSiteAddressMutationVariables) =>
      publicFetcher<
        Types.UpdateSiteAddressMutation,
        Types.UpdateSiteAddressMutationVariables
      >(UpdateSiteAddressDocument, variables)(),
    ...options,
  })
}

useUpdateSiteAddressMutation.fetcher = (
  variables: Types.UpdateSiteAddressMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateSiteAddressMutation,
    Types.UpdateSiteAddressMutationVariables
  >(UpdateSiteAddressDocument, variables, options)

export const CreateCustomBidReportDocument = new TypedDocumentString(`
    mutation CreateCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $name: String!, $developerNotes: String!, $buildingOwnerNotes: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        createCustomBidReport(
          name: $name
          developerNotes: $developerNotes
          buildingOwnerNotes: $buildingOwnerNotes
        ) {
          pid
          name
          notes
          buildingOwnerNotes
        }
      }
    }
  }
}
    `)

export const useCreateCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomBidReportMutation,
    TError,
    Types.CreateCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomBidReportMutation,
    TError,
    Types.CreateCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomBidReport"],
    mutationFn: (variables?: Types.CreateCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.CreateCustomBidReportMutation,
        Types.CreateCustomBidReportMutationVariables
      >(CreateCustomBidReportDocument, variables)(),
    ...options,
  })
}

useCreateCustomBidReportMutation.fetcher = (
  variables: Types.CreateCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomBidReportMutation,
    Types.CreateCustomBidReportMutationVariables
  >(CreateCustomBidReportDocument, variables, options)

export const EditCustomBidReportDocument = new TypedDocumentString(`
    mutation EditCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $name: String!, $developerNotes: String!, $buildingOwnerNotes: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setName(value: $name)
          setDeveloperNotes(value: $developerNotes)
          setBuildingOwnerNotes(value: $buildingOwnerNotes)
        }
      }
    }
  }
}
    `)

export const useEditCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditCustomBidReportMutation,
    TError,
    Types.EditCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditCustomBidReportMutation,
    TError,
    Types.EditCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["EditCustomBidReport"],
    mutationFn: (variables?: Types.EditCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.EditCustomBidReportMutation,
        Types.EditCustomBidReportMutationVariables
      >(EditCustomBidReportDocument, variables)(),
    ...options,
  })
}

useEditCustomBidReportMutation.fetcher = (
  variables: Types.EditCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditCustomBidReportMutation,
    Types.EditCustomBidReportMutationVariables
  >(EditCustomBidReportDocument, variables, options)

export const SetCustomBidReportVisibleToDevelopersDocument = new TypedDocumentString(`
    mutation SetCustomBidReportVisibleToDevelopers($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setVisibleToDevelopers(value: $value)
        }
      }
    }
  }
}
    `)

export const useSetCustomBidReportVisibleToDevelopersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    TError,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    TError,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomBidReportVisibleToDevelopers"],
    mutationFn: (
      variables?: Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    ) =>
      publicFetcher<
        Types.SetCustomBidReportVisibleToDevelopersMutation,
        Types.SetCustomBidReportVisibleToDevelopersMutationVariables
      >(SetCustomBidReportVisibleToDevelopersDocument, variables)(),
    ...options,
  })
}

useSetCustomBidReportVisibleToDevelopersMutation.fetcher = (
  variables: Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables
  >(SetCustomBidReportVisibleToDevelopersDocument, variables, options)

export const SetCustomBidReportVisibleToBuildingOwnersDocument =
  new TypedDocumentString(`
    mutation SetCustomBidReportVisibleToBuildingOwners($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setVisibleToBuildingOwners(value: $value)
        }
      }
    }
  }
}
    `)

export const useSetCustomBidReportVisibleToBuildingOwnersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    TError,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    TError,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomBidReportVisibleToBuildingOwners"],
    mutationFn: (
      variables?: Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    ) =>
      publicFetcher<
        Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
        Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables
      >(SetCustomBidReportVisibleToBuildingOwnersDocument, variables)(),
    ...options,
  })
}

useSetCustomBidReportVisibleToBuildingOwnersMutation.fetcher = (
  variables: Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables
  >(SetCustomBidReportVisibleToBuildingOwnersDocument, variables, options)

export const DeleteCustomBidReportDocument = new TypedDocumentString(`
    mutation DeleteCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          delete
        }
      }
    }
  }
}
    `)

export const useDeleteCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteCustomBidReportMutation,
    TError,
    Types.DeleteCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteCustomBidReportMutation,
    TError,
    Types.DeleteCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteCustomBidReport"],
    mutationFn: (variables?: Types.DeleteCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.DeleteCustomBidReportMutation,
        Types.DeleteCustomBidReportMutationVariables
      >(DeleteCustomBidReportDocument, variables)(),
    ...options,
  })
}

useDeleteCustomBidReportMutation.fetcher = (
  variables: Types.DeleteCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteCustomBidReportMutation,
    Types.DeleteCustomBidReportMutationVariables
  >(DeleteCustomBidReportDocument, variables, options)

export const AddBidToCustomBidReportDocument = new TypedDocumentString(`
    mutation AddBidToCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $bidPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          upsertBid(bidPid: $bidPid)
        }
      }
    }
  }
}
    `)

export const useAddBidToCustomBidReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBidToCustomBidReportMutation,
    TError,
    Types.AddBidToCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBidToCustomBidReportMutation,
    TError,
    Types.AddBidToCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["AddBidToCustomBidReport"],
    mutationFn: (variables?: Types.AddBidToCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.AddBidToCustomBidReportMutation,
        Types.AddBidToCustomBidReportMutationVariables
      >(AddBidToCustomBidReportDocument, variables)(),
    ...options,
  })
}

useAddBidToCustomBidReportMutation.fetcher = (
  variables: Types.AddBidToCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBidToCustomBidReportMutation,
    Types.AddBidToCustomBidReportMutationVariables
  >(AddBidToCustomBidReportDocument, variables, options)

export const RemoveBidFromCustomBidReportDocument = new TypedDocumentString(`
    mutation RemoveBidFromCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $bidPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          removeBid(bidPid: $bidPid)
        }
      }
    }
  }
}
    `)

export const useRemoveBidFromCustomBidReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBidFromCustomBidReportMutation,
    TError,
    Types.RemoveBidFromCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBidFromCustomBidReportMutation,
    TError,
    Types.RemoveBidFromCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBidFromCustomBidReport"],
    mutationFn: (variables?: Types.RemoveBidFromCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.RemoveBidFromCustomBidReportMutation,
        Types.RemoveBidFromCustomBidReportMutationVariables
      >(RemoveBidFromCustomBidReportDocument, variables)(),
    ...options,
  })
}

useRemoveBidFromCustomBidReportMutation.fetcher = (
  variables: Types.RemoveBidFromCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBidFromCustomBidReportMutation,
    Types.RemoveBidFromCustomBidReportMutationVariables
  >(RemoveBidFromCustomBidReportDocument, variables, options)

export const UpdateCustomTariffDocument = new TypedDocumentString(`
    mutation UpdateCustomTariff($sitePid: String!, $customTariffId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!, $name: String!) {
  site(sitePid: $sitePid) {
    customTariffs {
      update(
        customTariffId: $customTariffId
        addRateOps: $addRateOps
        subRateOps: $subRateOps
        propertyOps: $propertyOps
        name: $name
      ) {
        id
      }
    }
  }
}
    `)

export const useUpdateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateCustomTariffMutation,
    TError,
    Types.UpdateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateCustomTariffMutation,
    TError,
    Types.UpdateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateCustomTariff"],
    mutationFn: (variables?: Types.UpdateCustomTariffMutationVariables) =>
      publicFetcher<
        Types.UpdateCustomTariffMutation,
        Types.UpdateCustomTariffMutationVariables
      >(UpdateCustomTariffDocument, variables)(),
    ...options,
  })
}

useUpdateCustomTariffMutation.fetcher = (
  variables: Types.UpdateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateCustomTariffMutation,
    Types.UpdateCustomTariffMutationVariables
  >(UpdateCustomTariffDocument, variables, options)

export const CreateCustomTariffDocument = new TypedDocumentString(`
    mutation CreateCustomTariff($sitePid: String!, $name: String!, $rateId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  site(sitePid: $sitePid) {
    customTariffs {
      create(
        name: $name
        rateId: $rateId
        addRateOps: $addRateOps
        subRateOps: $subRateOps
        propertyOps: $propertyOps
      ) {
        id
      }
    }
  }
}
    `)

export const useCreateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariff"],
    mutationFn: (variables?: Types.CreateCustomTariffMutationVariables) =>
      publicFetcher<
        Types.CreateCustomTariffMutation,
        Types.CreateCustomTariffMutationVariables
      >(CreateCustomTariffDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffMutation.fetcher = (
  variables: Types.CreateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomTariffMutation,
    Types.CreateCustomTariffMutationVariables
  >(CreateCustomTariffDocument, variables, options)

export const CreateCustomTariffScratchDocument = new TypedDocumentString(`
    mutation CreateCustomTariffScratch($sitePid: String!, $name: String!, $tariffClass: TariffClass!, $utilityPid: String!, $addRateOps: [AddRateInput!]!) {
  site(sitePid: $sitePid) {
    customTariffs {
      createFromScratch(
        name: $name
        tariffClass: $tariffClass
        utilityPid: $utilityPid
        addRateOps: $addRateOps
      ) {
        id
      }
    }
  }
}
    `)

export const useCreateCustomTariffScratchMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffScratchMutation,
    TError,
    Types.CreateCustomTariffScratchMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffScratchMutation,
    TError,
    Types.CreateCustomTariffScratchMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariffScratch"],
    mutationFn: (variables?: Types.CreateCustomTariffScratchMutationVariables) =>
      publicFetcher<
        Types.CreateCustomTariffScratchMutation,
        Types.CreateCustomTariffScratchMutationVariables
      >(CreateCustomTariffScratchDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffScratchMutation.fetcher = (
  variables: Types.CreateCustomTariffScratchMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomTariffScratchMutation,
    Types.CreateCustomTariffScratchMutationVariables
  >(CreateCustomTariffScratchDocument, variables, options)

export const SetIsBehindTheMeterDocument = new TypedDocumentString(`
    mutation SetIsBehindTheMeter($sitePid: String!, $isBehindTheMeter: Boolean) {
  site(sitePid: $sitePid) {
    setIsBehindTheMeter(isBehindTheMeter: $isBehindTheMeter)
  }
}
    `)

export const useSetIsBehindTheMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetIsBehindTheMeterMutation,
    TError,
    Types.SetIsBehindTheMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetIsBehindTheMeterMutation,
    TError,
    Types.SetIsBehindTheMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetIsBehindTheMeter"],
    mutationFn: (variables?: Types.SetIsBehindTheMeterMutationVariables) =>
      publicFetcher<
        Types.SetIsBehindTheMeterMutation,
        Types.SetIsBehindTheMeterMutationVariables
      >(SetIsBehindTheMeterDocument, variables)(),
    ...options,
  })
}

useSetIsBehindTheMeterMutation.fetcher = (
  variables: Types.SetIsBehindTheMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetIsBehindTheMeterMutation,
    Types.SetIsBehindTheMeterMutationVariables
  >(SetIsBehindTheMeterDocument, variables, options)

export const SetMarketplaceSolarLocationsDocument = new TypedDocumentString(`
    mutation SetMarketplaceSolarLocations($sitePid: String!, $marketplaceSolarLocations: [SolarLocation!]) {
  site(sitePid: $sitePid) {
    setMarketplaceSolarLocations(
      marketplaceSolarLocations: $marketplaceSolarLocations
    )
  }
}
    `)

export const useSetMarketplaceSolarLocationsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetMarketplaceSolarLocationsMutation,
    TError,
    Types.SetMarketplaceSolarLocationsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMarketplaceSolarLocationsMutation,
    TError,
    Types.SetMarketplaceSolarLocationsMutationVariables,
    TContext
  >({
    mutationKey: ["SetMarketplaceSolarLocations"],
    mutationFn: (variables?: Types.SetMarketplaceSolarLocationsMutationVariables) =>
      publicFetcher<
        Types.SetMarketplaceSolarLocationsMutation,
        Types.SetMarketplaceSolarLocationsMutationVariables
      >(SetMarketplaceSolarLocationsDocument, variables)(),
    ...options,
  })
}

useSetMarketplaceSolarLocationsMutation.fetcher = (
  variables: Types.SetMarketplaceSolarLocationsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetMarketplaceSolarLocationsMutation,
    Types.SetMarketplaceSolarLocationsMutationVariables
  >(SetMarketplaceSolarLocationsDocument, variables, options)

export const SetMarketplaceIncludeStorageDocument = new TypedDocumentString(`
    mutation SetMarketplaceIncludeStorage($sitePid: String!, $marketplaceIncludeStorage: Boolean) {
  site(sitePid: $sitePid) {
    setMarketplaceIncludeStorage(
      marketplaceIncludeStorage: $marketplaceIncludeStorage
    )
  }
}
    `)

export const useSetMarketplaceIncludeStorageMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetMarketplaceIncludeStorageMutation,
    TError,
    Types.SetMarketplaceIncludeStorageMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMarketplaceIncludeStorageMutation,
    TError,
    Types.SetMarketplaceIncludeStorageMutationVariables,
    TContext
  >({
    mutationKey: ["SetMarketplaceIncludeStorage"],
    mutationFn: (variables?: Types.SetMarketplaceIncludeStorageMutationVariables) =>
      publicFetcher<
        Types.SetMarketplaceIncludeStorageMutation,
        Types.SetMarketplaceIncludeStorageMutationVariables
      >(SetMarketplaceIncludeStorageDocument, variables)(),
    ...options,
  })
}

useSetMarketplaceIncludeStorageMutation.fetcher = (
  variables: Types.SetMarketplaceIncludeStorageMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetMarketplaceIncludeStorageMutation,
    Types.SetMarketplaceIncludeStorageMutationVariables
  >(SetMarketplaceIncludeStorageDocument, variables, options)

export const FinalizeEnergyPageUploadsDocument = new TypedDocumentString(`
    mutation FinalizeEnergyPageUploads($sitePid: String!) {
  site(sitePid: $sitePid) {
    finalizeEnergyPageUpload
  }
}
    `)

export const useFinalizeEnergyPageUploadsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.FinalizeEnergyPageUploadsMutation,
    TError,
    Types.FinalizeEnergyPageUploadsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinalizeEnergyPageUploadsMutation,
    TError,
    Types.FinalizeEnergyPageUploadsMutationVariables,
    TContext
  >({
    mutationKey: ["FinalizeEnergyPageUploads"],
    mutationFn: (variables?: Types.FinalizeEnergyPageUploadsMutationVariables) =>
      publicFetcher<
        Types.FinalizeEnergyPageUploadsMutation,
        Types.FinalizeEnergyPageUploadsMutationVariables
      >(FinalizeEnergyPageUploadsDocument, variables)(),
    ...options,
  })
}

useFinalizeEnergyPageUploadsMutation.fetcher = (
  variables: Types.FinalizeEnergyPageUploadsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinalizeEnergyPageUploadsMutation,
    Types.FinalizeEnergyPageUploadsMutationVariables
  >(FinalizeEnergyPageUploadsDocument, variables, options)

export const CreateEmailDraftDocument = new TypedDocumentString(`
    mutation CreateEmailDraft($draftParams: DraftEmailInput!) {
  admin {
    googleApi {
      draftEmail(draftParams: $draftParams)
    }
  }
}
    `)

export const useCreateEmailDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >({
    mutationKey: ["CreateEmailDraft"],
    mutationFn: (variables?: Types.CreateEmailDraftMutationVariables) =>
      publicFetcher<
        Types.CreateEmailDraftMutation,
        Types.CreateEmailDraftMutationVariables
      >(CreateEmailDraftDocument, variables)(),
    ...options,
  })
}

useCreateEmailDraftMutation.fetcher = (
  variables: Types.CreateEmailDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateEmailDraftMutation,
    Types.CreateEmailDraftMutationVariables
  >(CreateEmailDraftDocument, variables, options)

export const AddFinmodelOverrideDocument = new TypedDocumentString(`
    mutation AddFinmodelOverride($sitePid: String, $portfolioPid: String, $overrideInput: FinmodelMutationInput!) {
  finmodel {
    addOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      overrideInput: $overrideInput
    ) {
      __typename
      ... on Error {
        message
      }
      ... on FinmodelInputOverride {
        inputName
        inputValue
      }
    }
  }
}
    `)

export const useAddFinmodelOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddFinmodelOverrideMutation,
    TError,
    Types.AddFinmodelOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddFinmodelOverrideMutation,
    TError,
    Types.AddFinmodelOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["AddFinmodelOverride"],
    mutationFn: (variables?: Types.AddFinmodelOverrideMutationVariables) =>
      publicFetcher<
        Types.AddFinmodelOverrideMutation,
        Types.AddFinmodelOverrideMutationVariables
      >(AddFinmodelOverrideDocument, variables)(),
    ...options,
  })
}

useAddFinmodelOverrideMutation.fetcher = (
  variables: Types.AddFinmodelOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddFinmodelOverrideMutation,
    Types.AddFinmodelOverrideMutationVariables
  >(AddFinmodelOverrideDocument, variables, options)

export const SetEnergyProjectMilestoneDocument = new TypedDocumentString(`
    mutation SetEnergyProjectMilestone($projectPid: String!, $milestoneType: MilestoneType!, $milestoneInput: ProjectMilestoneInput!) {
  project(projectPid: $projectPid) {
    setMilestone(milestoneType: $milestoneType, milestoneInput: $milestoneInput)
  }
}
    `)

export const useSetEnergyProjectMilestoneMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectMilestoneMutation,
    TError,
    Types.SetEnergyProjectMilestoneMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectMilestoneMutation,
    TError,
    Types.SetEnergyProjectMilestoneMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectMilestone"],
    mutationFn: (variables?: Types.SetEnergyProjectMilestoneMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectMilestoneMutation,
        Types.SetEnergyProjectMilestoneMutationVariables
      >(SetEnergyProjectMilestoneDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectMilestoneMutation.fetcher = (
  variables: Types.SetEnergyProjectMilestoneMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectMilestoneMutation,
    Types.SetEnergyProjectMilestoneMutationVariables
  >(SetEnergyProjectMilestoneDocument, variables, options)

export const BumpEnergyProjectMilestonesDocument = new TypedDocumentString(`
    mutation BumpEnergyProjectMilestones($projectPid: String!, $milestoneTypes: [MilestoneType!]!, $days: Int!) {
  project(projectPid: $projectPid) {
    bumpMilestones(milestoneTypes: $milestoneTypes, days: $days)
  }
}
    `)

export const useBumpEnergyProjectMilestonesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.BumpEnergyProjectMilestonesMutation,
    TError,
    Types.BumpEnergyProjectMilestonesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BumpEnergyProjectMilestonesMutation,
    TError,
    Types.BumpEnergyProjectMilestonesMutationVariables,
    TContext
  >({
    mutationKey: ["BumpEnergyProjectMilestones"],
    mutationFn: (variables?: Types.BumpEnergyProjectMilestonesMutationVariables) =>
      publicFetcher<
        Types.BumpEnergyProjectMilestonesMutation,
        Types.BumpEnergyProjectMilestonesMutationVariables
      >(BumpEnergyProjectMilestonesDocument, variables)(),
    ...options,
  })
}

useBumpEnergyProjectMilestonesMutation.fetcher = (
  variables: Types.BumpEnergyProjectMilestonesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BumpEnergyProjectMilestonesMutation,
    Types.BumpEnergyProjectMilestonesMutationVariables
  >(BumpEnergyProjectMilestonesDocument, variables, options)

export const SetEnergyProjectSystemDocument = new TypedDocumentString(`
    mutation SetEnergyProjectSystem($projectPid: String!, $input: AddSystemInput!) {
  project(projectPid: $projectPid) {
    setSystem(input: $input) {
      system {
        pid
      }
      lite {
        pid
      }
    }
  }
}
    `)

export const useSetEnergyProjectSystemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectSystemMutation,
    TError,
    Types.SetEnergyProjectSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectSystemMutation,
    TError,
    Types.SetEnergyProjectSystemMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectSystem"],
    mutationFn: (variables?: Types.SetEnergyProjectSystemMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectSystemMutation,
        Types.SetEnergyProjectSystemMutationVariables
      >(SetEnergyProjectSystemDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectSystemMutation.fetcher = (
  variables: Types.SetEnergyProjectSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectSystemMutation,
    Types.SetEnergyProjectSystemMutationVariables
  >(SetEnergyProjectSystemDocument, variables, options)

export const SetEnergyProjectDetailsDocument = new TypedDocumentString(`
    mutation SetEnergyProjectDetails($projectPid: String!, $input: EnergyProjectDetails!) {
  project(projectPid: $projectPid) {
    setProjectDetails(input: $input) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useSetEnergyProjectDetailsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectDetailsMutation,
    TError,
    Types.SetEnergyProjectDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectDetailsMutation,
    TError,
    Types.SetEnergyProjectDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectDetails"],
    mutationFn: (variables?: Types.SetEnergyProjectDetailsMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectDetailsMutation,
        Types.SetEnergyProjectDetailsMutationVariables
      >(SetEnergyProjectDetailsDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectDetailsMutation.fetcher = (
  variables: Types.SetEnergyProjectDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectDetailsMutation,
    Types.SetEnergyProjectDetailsMutationVariables
  >(SetEnergyProjectDetailsDocument, variables, options)

export const SetEnergyProjectFieldDocument = new TypedDocumentString(`
    mutation SetEnergyProjectField($projectPid: String!, $input: SetProjectFieldInput!) {
  project(projectPid: $projectPid) {
    setField(input: $input) {
      __typename
      ... on EnergyProject {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useSetEnergyProjectFieldMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectFieldMutation,
    TError,
    Types.SetEnergyProjectFieldMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectFieldMutation,
    TError,
    Types.SetEnergyProjectFieldMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectField"],
    mutationFn: (variables?: Types.SetEnergyProjectFieldMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectFieldMutation,
        Types.SetEnergyProjectFieldMutationVariables
      >(SetEnergyProjectFieldDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectFieldMutation.fetcher = (
  variables: Types.SetEnergyProjectFieldMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectFieldMutation,
    Types.SetEnergyProjectFieldMutationVariables
  >(SetEnergyProjectFieldDocument, variables, options)

export const CreateHubspotDealDocument = new TypedDocumentString(`
    mutation CreateHubspotDeal($projectPid: String!, $financingType: HubspotFinancingType, $leadSource: HubspotLeadSource) {
  project(projectPid: $projectPid) {
    createHubspotDeal(financingType: $financingType, leadSource: $leadSource) {
      __typename
      ... on EnergyProject {
        pid
        hubspotRecordId
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useCreateHubspotDealMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateHubspotDealMutation,
    TError,
    Types.CreateHubspotDealMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateHubspotDealMutation,
    TError,
    Types.CreateHubspotDealMutationVariables,
    TContext
  >({
    mutationKey: ["CreateHubspotDeal"],
    mutationFn: (variables?: Types.CreateHubspotDealMutationVariables) =>
      publicFetcher<
        Types.CreateHubspotDealMutation,
        Types.CreateHubspotDealMutationVariables
      >(CreateHubspotDealDocument, variables)(),
    ...options,
  })
}

useCreateHubspotDealMutation.fetcher = (
  variables: Types.CreateHubspotDealMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateHubspotDealMutation,
    Types.CreateHubspotDealMutationVariables
  >(CreateHubspotDealDocument, variables, options)

export const SetEnergyProjectRevenueBreakdownDocument = new TypedDocumentString(`
    mutation SetEnergyProjectRevenueBreakdown($projectPid: String!, $revenueMilestones: [RevenueMilestoneInput!]) {
  project(projectPid: $projectPid) {
    setRevenueBreakdown(revenueMilestones: $revenueMilestones) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useSetEnergyProjectRevenueBreakdownMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectRevenueBreakdownMutation,
    TError,
    Types.SetEnergyProjectRevenueBreakdownMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectRevenueBreakdownMutation,
    TError,
    Types.SetEnergyProjectRevenueBreakdownMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectRevenueBreakdown"],
    mutationFn: (variables?: Types.SetEnergyProjectRevenueBreakdownMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectRevenueBreakdownMutation,
        Types.SetEnergyProjectRevenueBreakdownMutationVariables
      >(SetEnergyProjectRevenueBreakdownDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectRevenueBreakdownMutation.fetcher = (
  variables: Types.SetEnergyProjectRevenueBreakdownMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectRevenueBreakdownMutation,
    Types.SetEnergyProjectRevenueBreakdownMutationVariables
  >(SetEnergyProjectRevenueBreakdownDocument, variables, options)

export const SetEnergyProjectCsSpecificationsDocument = new TypedDocumentString(`
    mutation SetEnergyProjectCSSpecifications($projectPid: String!, $input: CSProjectSpecificationInput!) {
  project(projectPid: $projectPid) {
    setCsSpecificationDetails(input: $input)
  }
}
    `)

export const useSetEnergyProjectCsSpecificationsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectCsSpecificationsMutation,
    TError,
    Types.SetEnergyProjectCsSpecificationsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectCsSpecificationsMutation,
    TError,
    Types.SetEnergyProjectCsSpecificationsMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectCSSpecifications"],
    mutationFn: (variables?: Types.SetEnergyProjectCsSpecificationsMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectCsSpecificationsMutation,
        Types.SetEnergyProjectCsSpecificationsMutationVariables
      >(SetEnergyProjectCsSpecificationsDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectCsSpecificationsMutation.fetcher = (
  variables: Types.SetEnergyProjectCsSpecificationsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectCsSpecificationsMutation,
    Types.SetEnergyProjectCsSpecificationsMutationVariables
  >(SetEnergyProjectCsSpecificationsDocument, variables, options)

export const SetEnergyProjectPpaSpecificationsDocument = new TypedDocumentString(`
    mutation SetEnergyProjectPPASpecifications($projectPid: String!, $input: PPAProjectSpecificationInput!) {
  project(projectPid: $projectPid) {
    setPpaSpecificationDetails(input: $input)
  }
}
    `)

export const useSetEnergyProjectPpaSpecificationsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectPpaSpecificationsMutation,
    TError,
    Types.SetEnergyProjectPpaSpecificationsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectPpaSpecificationsMutation,
    TError,
    Types.SetEnergyProjectPpaSpecificationsMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectPPASpecifications"],
    mutationFn: (
      variables?: Types.SetEnergyProjectPpaSpecificationsMutationVariables,
    ) =>
      publicFetcher<
        Types.SetEnergyProjectPpaSpecificationsMutation,
        Types.SetEnergyProjectPpaSpecificationsMutationVariables
      >(SetEnergyProjectPpaSpecificationsDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectPpaSpecificationsMutation.fetcher = (
  variables: Types.SetEnergyProjectPpaSpecificationsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectPpaSpecificationsMutation,
    Types.SetEnergyProjectPpaSpecificationsMutationVariables
  >(SetEnergyProjectPpaSpecificationsDocument, variables, options)

export const SetEnergyProjectCashSpecificationsDocument = new TypedDocumentString(`
    mutation SetEnergyProjectCashSpecifications($projectPid: String!, $input: CashProjectSpecificationInput!) {
  project(projectPid: $projectPid) {
    setCashSpecificationDetails(input: $input)
  }
}
    `)

export const useSetEnergyProjectCashSpecificationsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectCashSpecificationsMutation,
    TError,
    Types.SetEnergyProjectCashSpecificationsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectCashSpecificationsMutation,
    TError,
    Types.SetEnergyProjectCashSpecificationsMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectCashSpecifications"],
    mutationFn: (
      variables?: Types.SetEnergyProjectCashSpecificationsMutationVariables,
    ) =>
      publicFetcher<
        Types.SetEnergyProjectCashSpecificationsMutation,
        Types.SetEnergyProjectCashSpecificationsMutationVariables
      >(SetEnergyProjectCashSpecificationsDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectCashSpecificationsMutation.fetcher = (
  variables: Types.SetEnergyProjectCashSpecificationsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectCashSpecificationsMutation,
    Types.SetEnergyProjectCashSpecificationsMutationVariables
  >(SetEnergyProjectCashSpecificationsDocument, variables, options)

export const SetEnergyProjectStatusDocument = new TypedDocumentString(`
    mutation SetEnergyProjectStatus($projectPid: String!, $status: ProjectStatus, $closed: Boolean, $closeReason: ProjectCloseReason, $closeCustomReason: String, $closedAt: DateTime) {
  project(projectPid: $projectPid) {
    setProjectStatus(
      status: $status
      closed: $closed
      closeReason: $closeReason
      customCloseReason: $closeCustomReason
      closedAt: $closedAt
    )
  }
}
    `)

export const useSetEnergyProjectStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectStatusMutation,
    TError,
    Types.SetEnergyProjectStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectStatusMutation,
    TError,
    Types.SetEnergyProjectStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectStatus"],
    mutationFn: (variables?: Types.SetEnergyProjectStatusMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectStatusMutation,
        Types.SetEnergyProjectStatusMutationVariables
      >(SetEnergyProjectStatusDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectStatusMutation.fetcher = (
  variables: Types.SetEnergyProjectStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectStatusMutation,
    Types.SetEnergyProjectStatusMutationVariables
  >(SetEnergyProjectStatusDocument, variables, options)

export const ArchiveEnergyProjectDocument = new TypedDocumentString(`
    mutation ArchiveEnergyProject($projectPid: String!, $archive: Boolean!) {
  project(projectPid: $projectPid) {
    archiveProject(archive: $archive)
  }
}
    `)

export const useArchiveEnergyProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveEnergyProjectMutation,
    TError,
    Types.ArchiveEnergyProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveEnergyProjectMutation,
    TError,
    Types.ArchiveEnergyProjectMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveEnergyProject"],
    mutationFn: (variables?: Types.ArchiveEnergyProjectMutationVariables) =>
      publicFetcher<
        Types.ArchiveEnergyProjectMutation,
        Types.ArchiveEnergyProjectMutationVariables
      >(ArchiveEnergyProjectDocument, variables)(),
    ...options,
  })
}

useArchiveEnergyProjectMutation.fetcher = (
  variables: Types.ArchiveEnergyProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ArchiveEnergyProjectMutation,
    Types.ArchiveEnergyProjectMutationVariables
  >(ArchiveEnergyProjectDocument, variables, options)

export const CreateFtmProgramDocument = new TypedDocumentString(`
    mutation CreateFtmProgram($program: FTMProgramInput!) {
  ftmPrograms {
    new(program: $program) {
      state
      name
    }
  }
}
    `)

export const useCreateFtmProgramMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateFtmProgramMutation,
    TError,
    Types.CreateFtmProgramMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateFtmProgramMutation,
    TError,
    Types.CreateFtmProgramMutationVariables,
    TContext
  >({
    mutationKey: ["CreateFtmProgram"],
    mutationFn: (variables?: Types.CreateFtmProgramMutationVariables) =>
      publicFetcher<
        Types.CreateFtmProgramMutation,
        Types.CreateFtmProgramMutationVariables
      >(CreateFtmProgramDocument, variables)(),
    ...options,
  })
}

useCreateFtmProgramMutation.fetcher = (
  variables: Types.CreateFtmProgramMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateFtmProgramMutation,
    Types.CreateFtmProgramMutationVariables
  >(CreateFtmProgramDocument, variables, options)

export const EditCsPriceDocument = new TypedDocumentString(`
    mutation EditCsPrice($id: Int!, $input: EditCsPriceInput!) {
  ftmPrograms {
    editCsPrice(id: $id, input: $input) {
      usdPerKwPerYear
      termYears
      minPvKw
      maxPvKw
      escalator
    }
  }
}
    `)

export const useEditCsPriceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditCsPriceMutation,
    TError,
    Types.EditCsPriceMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditCsPriceMutation,
    TError,
    Types.EditCsPriceMutationVariables,
    TContext
  >({
    mutationKey: ["EditCsPrice"],
    mutationFn: (variables?: Types.EditCsPriceMutationVariables) =>
      publicFetcher<Types.EditCsPriceMutation, Types.EditCsPriceMutationVariables>(
        EditCsPriceDocument,
        variables,
      )(),
    ...options,
  })
}

useEditCsPriceMutation.fetcher = (
  variables: Types.EditCsPriceMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EditCsPriceMutation, Types.EditCsPriceMutationVariables>(
    EditCsPriceDocument,
    variables,
    options,
  )

export const CreateCsPriceDocument = new TypedDocumentString(`
    mutation CreateCsPrice($input: CreateCsPriceInput!) {
  ftmPrograms {
    createCsPrice(input: $input) {
      utility {
        id
        name
      }
      state
      pvType
      usdPerKwPerYear
      termYears
      minPvKw
      maxPvKw
      escalator
    }
  }
}
    `)

export const useCreateCsPriceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCsPriceMutation,
    TError,
    Types.CreateCsPriceMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCsPriceMutation,
    TError,
    Types.CreateCsPriceMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCsPrice"],
    mutationFn: (variables?: Types.CreateCsPriceMutationVariables) =>
      publicFetcher<Types.CreateCsPriceMutation, Types.CreateCsPriceMutationVariables>(
        CreateCsPriceDocument,
        variables,
      )(),
    ...options,
  })
}

useCreateCsPriceMutation.fetcher = (
  variables: Types.CreateCsPriceMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreateCsPriceMutation, Types.CreateCsPriceMutationVariables>(
    CreateCsPriceDocument,
    variables,
    options,
  )

export const CreateBatteryProductDocument = new TypedDocumentString(`
    mutation CreateBatteryProduct($input: BatteryProductAdminInput!) {
  batteryProducts {
    create(input: $input) {
      id
      sizeKw
      sizeKwh
      vendor
      equipmentCost
      laborCost
      includeInOptimizer
      disabled
    }
  }
}
    `)

export const useCreateBatteryProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateBatteryProductMutation,
    TError,
    Types.CreateBatteryProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBatteryProductMutation,
    TError,
    Types.CreateBatteryProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBatteryProduct"],
    mutationFn: (variables?: Types.CreateBatteryProductMutationVariables) =>
      publicFetcher<
        Types.CreateBatteryProductMutation,
        Types.CreateBatteryProductMutationVariables
      >(CreateBatteryProductDocument, variables)(),
    ...options,
  })
}

useCreateBatteryProductMutation.fetcher = (
  variables: Types.CreateBatteryProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBatteryProductMutation,
    Types.CreateBatteryProductMutationVariables
  >(CreateBatteryProductDocument, variables, options)

export const UpdateBatteryProductDocument = new TypedDocumentString(`
    mutation UpdateBatteryProduct($id: Int!, $input: BatteryProductAdminInput!) {
  batteryProducts {
    update(id: $id, input: $input) {
      id
      sizeKw
      sizeKwh
      vendor
      equipmentCost
      laborCost
      includeInOptimizer
      disabled
    }
  }
}
    `)

export const useUpdateBatteryProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateBatteryProductMutation,
    TError,
    Types.UpdateBatteryProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateBatteryProductMutation,
    TError,
    Types.UpdateBatteryProductMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateBatteryProduct"],
    mutationFn: (variables?: Types.UpdateBatteryProductMutationVariables) =>
      publicFetcher<
        Types.UpdateBatteryProductMutation,
        Types.UpdateBatteryProductMutationVariables
      >(UpdateBatteryProductDocument, variables)(),
    ...options,
  })
}

useUpdateBatteryProductMutation.fetcher = (
  variables: Types.UpdateBatteryProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateBatteryProductMutation,
    Types.UpdateBatteryProductMutationVariables
  >(UpdateBatteryProductDocument, variables, options)

export const CreateWorkstreamDocument = new TypedDocumentString(`
    mutation CreateWorkstream($input: EnergyProjectTaskWorkstreamInput!) {
  workstreams {
    create(input: $input) {
      id
      pid
      name
      description
      projectStage
      rank
      supportedProjectTypes
    }
  }
}
    `)

export const useCreateWorkstreamMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateWorkstreamMutation,
    TError,
    Types.CreateWorkstreamMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateWorkstreamMutation,
    TError,
    Types.CreateWorkstreamMutationVariables,
    TContext
  >({
    mutationKey: ["CreateWorkstream"],
    mutationFn: (variables?: Types.CreateWorkstreamMutationVariables) =>
      publicFetcher<
        Types.CreateWorkstreamMutation,
        Types.CreateWorkstreamMutationVariables
      >(CreateWorkstreamDocument, variables)(),
    ...options,
  })
}

useCreateWorkstreamMutation.fetcher = (
  variables: Types.CreateWorkstreamMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateWorkstreamMutation,
    Types.CreateWorkstreamMutationVariables
  >(CreateWorkstreamDocument, variables, options)

export const UpdateWorkstreamDocument = new TypedDocumentString(`
    mutation UpdateWorkstream($pid: String!, $input: EnergyProjectTaskWorkstreamInput!) {
  workstreams {
    update(pid: $pid, input: $input) {
      id
      pid
      name
      description
      projectStage
      rank
      supportedProjectTypes
    }
  }
}
    `)

export const useUpdateWorkstreamMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateWorkstreamMutation,
    TError,
    Types.UpdateWorkstreamMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateWorkstreamMutation,
    TError,
    Types.UpdateWorkstreamMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateWorkstream"],
    mutationFn: (variables?: Types.UpdateWorkstreamMutationVariables) =>
      publicFetcher<
        Types.UpdateWorkstreamMutation,
        Types.UpdateWorkstreamMutationVariables
      >(UpdateWorkstreamDocument, variables)(),
    ...options,
  })
}

useUpdateWorkstreamMutation.fetcher = (
  variables: Types.UpdateWorkstreamMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateWorkstreamMutation,
    Types.UpdateWorkstreamMutationVariables
  >(UpdateWorkstreamDocument, variables, options)

export const SetWorkstreamRankDocument = new TypedDocumentString(`
    mutation SetWorkstreamRank($pid: String!, $rank: Int!) {
  workstreams {
    setRank(pid: $pid, rank: $rank)
  }
}
    `)

export const useSetWorkstreamRankMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetWorkstreamRankMutation,
    TError,
    Types.SetWorkstreamRankMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetWorkstreamRankMutation,
    TError,
    Types.SetWorkstreamRankMutationVariables,
    TContext
  >({
    mutationKey: ["SetWorkstreamRank"],
    mutationFn: (variables?: Types.SetWorkstreamRankMutationVariables) =>
      publicFetcher<
        Types.SetWorkstreamRankMutation,
        Types.SetWorkstreamRankMutationVariables
      >(SetWorkstreamRankDocument, variables)(),
    ...options,
  })
}

useSetWorkstreamRankMutation.fetcher = (
  variables: Types.SetWorkstreamRankMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetWorkstreamRankMutation,
    Types.SetWorkstreamRankMutationVariables
  >(SetWorkstreamRankDocument, variables, options)

export const DeleteWorkstreamDocument = new TypedDocumentString(`
    mutation DeleteWorkstream($pid: String!) {
  workstreams {
    delete(pid: $pid) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useDeleteWorkstreamMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteWorkstreamMutation,
    TError,
    Types.DeleteWorkstreamMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteWorkstreamMutation,
    TError,
    Types.DeleteWorkstreamMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteWorkstream"],
    mutationFn: (variables?: Types.DeleteWorkstreamMutationVariables) =>
      publicFetcher<
        Types.DeleteWorkstreamMutation,
        Types.DeleteWorkstreamMutationVariables
      >(DeleteWorkstreamDocument, variables)(),
    ...options,
  })
}

useDeleteWorkstreamMutation.fetcher = (
  variables: Types.DeleteWorkstreamMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteWorkstreamMutation,
    Types.DeleteWorkstreamMutationVariables
  >(DeleteWorkstreamDocument, variables, options)

export const CreateTaskDocument = new TypedDocumentString(`
    mutation CreateTask($projectPid: String!, $input: EnergyProjectTaskInput!) {
  tasks {
    create(projectPid: $projectPid, input: $input) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useCreateTaskMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateTaskMutation,
    TError,
    Types.CreateTaskMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateTaskMutation,
    TError,
    Types.CreateTaskMutationVariables,
    TContext
  >({
    mutationKey: ["CreateTask"],
    mutationFn: (variables?: Types.CreateTaskMutationVariables) =>
      publicFetcher<Types.CreateTaskMutation, Types.CreateTaskMutationVariables>(
        CreateTaskDocument,
        variables,
      )(),
    ...options,
  })
}

useCreateTaskMutation.fetcher = (
  variables: Types.CreateTaskMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreateTaskMutation, Types.CreateTaskMutationVariables>(
    CreateTaskDocument,
    variables,
    options,
  )

export const UpdateTaskDocument = new TypedDocumentString(`
    mutation UpdateTask($pid: String!, $input: EnergyProjectTaskInput!) {
  tasks {
    update(pid: $pid, input: $input) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useUpdateTaskMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateTaskMutation,
    TError,
    Types.UpdateTaskMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateTaskMutation,
    TError,
    Types.UpdateTaskMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateTask"],
    mutationFn: (variables?: Types.UpdateTaskMutationVariables) =>
      publicFetcher<Types.UpdateTaskMutation, Types.UpdateTaskMutationVariables>(
        UpdateTaskDocument,
        variables,
      )(),
    ...options,
  })
}

useUpdateTaskMutation.fetcher = (
  variables: Types.UpdateTaskMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UpdateTaskMutation, Types.UpdateTaskMutationVariables>(
    UpdateTaskDocument,
    variables,
    options,
  )

export const SetTaskVisibilityDocument = new TypedDocumentString(`
    mutation SetTaskVisibility($pid: String!, $hiddenFromDeveloper: Boolean!, $hiddenFromBuildingOwner: Boolean!) {
  tasks {
    setVisibility(
      pid: $pid
      hiddenFromDeveloper: $hiddenFromDeveloper
      hiddenFromBuildingOwner: $hiddenFromBuildingOwner
    ) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useSetTaskVisibilityMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetTaskVisibilityMutation,
    TError,
    Types.SetTaskVisibilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetTaskVisibilityMutation,
    TError,
    Types.SetTaskVisibilityMutationVariables,
    TContext
  >({
    mutationKey: ["SetTaskVisibility"],
    mutationFn: (variables?: Types.SetTaskVisibilityMutationVariables) =>
      publicFetcher<
        Types.SetTaskVisibilityMutation,
        Types.SetTaskVisibilityMutationVariables
      >(SetTaskVisibilityDocument, variables)(),
    ...options,
  })
}

useSetTaskVisibilityMutation.fetcher = (
  variables: Types.SetTaskVisibilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetTaskVisibilityMutation,
    Types.SetTaskVisibilityMutationVariables
  >(SetTaskVisibilityDocument, variables, options)

export const CompleteTaskDocument = new TypedDocumentString(`
    mutation CompleteTask($pid: String!) {
  tasks {
    complete(pid: $pid) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useCompleteTaskMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CompleteTaskMutation,
    TError,
    Types.CompleteTaskMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CompleteTaskMutation,
    TError,
    Types.CompleteTaskMutationVariables,
    TContext
  >({
    mutationKey: ["CompleteTask"],
    mutationFn: (variables?: Types.CompleteTaskMutationVariables) =>
      publicFetcher<Types.CompleteTaskMutation, Types.CompleteTaskMutationVariables>(
        CompleteTaskDocument,
        variables,
      )(),
    ...options,
  })
}

useCompleteTaskMutation.fetcher = (
  variables: Types.CompleteTaskMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CompleteTaskMutation, Types.CompleteTaskMutationVariables>(
    CompleteTaskDocument,
    variables,
    options,
  )

export const UncompleteTaskDocument = new TypedDocumentString(`
    mutation UncompleteTask($pid: String!) {
  tasks {
    uncomplete(pid: $pid) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useUncompleteTaskMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UncompleteTaskMutation,
    TError,
    Types.UncompleteTaskMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UncompleteTaskMutation,
    TError,
    Types.UncompleteTaskMutationVariables,
    TContext
  >({
    mutationKey: ["UncompleteTask"],
    mutationFn: (variables?: Types.UncompleteTaskMutationVariables) =>
      publicFetcher<
        Types.UncompleteTaskMutation,
        Types.UncompleteTaskMutationVariables
      >(UncompleteTaskDocument, variables)(),
    ...options,
  })
}

useUncompleteTaskMutation.fetcher = (
  variables: Types.UncompleteTaskMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UncompleteTaskMutation, Types.UncompleteTaskMutationVariables>(
    UncompleteTaskDocument,
    variables,
    options,
  )

export const DeleteTaskDocument = new TypedDocumentString(`
    mutation DeleteTask($pid: String!) {
  tasks {
    delete(pid: $pid) {
      message
    }
  }
}
    `)

export const useDeleteTaskMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteTaskMutation,
    TError,
    Types.DeleteTaskMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteTaskMutation,
    TError,
    Types.DeleteTaskMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteTask"],
    mutationFn: (variables?: Types.DeleteTaskMutationVariables) =>
      publicFetcher<Types.DeleteTaskMutation, Types.DeleteTaskMutationVariables>(
        DeleteTaskDocument,
        variables,
      )(),
    ...options,
  })
}

useDeleteTaskMutation.fetcher = (
  variables: Types.DeleteTaskMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteTaskMutation, Types.DeleteTaskMutationVariables>(
    DeleteTaskDocument,
    variables,
    options,
  )

export const AddTaskNoteDocument = new TypedDocumentString(`
    mutation AddTaskNote($taskPid: String!, $content: QuillDelta!, $audiences: [ProjectAudience!]) {
  tasks {
    addNote(taskPid: $taskPid, content: $content, audiences: $audiences) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAddTaskNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddTaskNoteMutation,
    TError,
    Types.AddTaskNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddTaskNoteMutation,
    TError,
    Types.AddTaskNoteMutationVariables,
    TContext
  >({
    mutationKey: ["AddTaskNote"],
    mutationFn: (variables?: Types.AddTaskNoteMutationVariables) =>
      publicFetcher<Types.AddTaskNoteMutation, Types.AddTaskNoteMutationVariables>(
        AddTaskNoteDocument,
        variables,
      )(),
    ...options,
  })
}

useAddTaskNoteMutation.fetcher = (
  variables: Types.AddTaskNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddTaskNoteMutation, Types.AddTaskNoteMutationVariables>(
    AddTaskNoteDocument,
    variables,
    options,
  )

export const EditTaskNoteDocument = new TypedDocumentString(`
    mutation EditTaskNote($notePid: String!, $content: QuillDelta!, $audiences: [ProjectAudience!]) {
  tasks {
    editNote(notePid: $notePid, content: $content, audiences: $audiences) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useEditTaskNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditTaskNoteMutation,
    TError,
    Types.EditTaskNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditTaskNoteMutation,
    TError,
    Types.EditTaskNoteMutationVariables,
    TContext
  >({
    mutationKey: ["EditTaskNote"],
    mutationFn: (variables?: Types.EditTaskNoteMutationVariables) =>
      publicFetcher<Types.EditTaskNoteMutation, Types.EditTaskNoteMutationVariables>(
        EditTaskNoteDocument,
        variables,
      )(),
    ...options,
  })
}

useEditTaskNoteMutation.fetcher = (
  variables: Types.EditTaskNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EditTaskNoteMutation, Types.EditTaskNoteMutationVariables>(
    EditTaskNoteDocument,
    variables,
    options,
  )

export const TrashTaskNoteDocument = new TypedDocumentString(`
    mutation TrashTaskNote($notePid: String!) {
  tasks {
    trashNote(notePid: $notePid) {
      __typename
      ... on EnergyProjectTask {
        ...EnergyProjectTask
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useTrashTaskNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashTaskNoteMutation,
    TError,
    Types.TrashTaskNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashTaskNoteMutation,
    TError,
    Types.TrashTaskNoteMutationVariables,
    TContext
  >({
    mutationKey: ["TrashTaskNote"],
    mutationFn: (variables?: Types.TrashTaskNoteMutationVariables) =>
      publicFetcher<Types.TrashTaskNoteMutation, Types.TrashTaskNoteMutationVariables>(
        TrashTaskNoteDocument,
        variables,
      )(),
    ...options,
  })
}

useTrashTaskNoteMutation.fetcher = (
  variables: Types.TrashTaskNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TrashTaskNoteMutation, Types.TrashTaskNoteMutationVariables>(
    TrashTaskNoteDocument,
    variables,
    options,
  )

export const AddProjectDocument = new TypedDocumentString(`
    mutation AddProject($sitePid: String!, $input: ProjectInput!) {
  addProject(sitePid: $sitePid, input: $input) {
    __typename
    ... on EnergyProject {
      ...EnergyProject
    }
    ... on Error {
      message
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  workableIxResultsReceivedEstimated
  workableIxResultsReceivedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
  calc {
    roofRent {
      ...RoofRentEconomics
    }
  }
}
fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
fragment EnergyProjectCashSpecification on EnergyProjectCashSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  system {
    ...System
  }
}
fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment EnergyProject on EnergyProject {
  pid
  name
  description
  hubspotRecordId
  hubspotRevenueRecordId
  status
  closedAt
  closeReason
  closeCustomReason
  lumenFeeUsdPerWatt
  lumenRevenue
  finproductType
  solarLocations
  includeStorage
  archivedAt
  archivedBy {
    ...UserRef
  }
  site {
    pid
    streetAddress
  }
  projectSpecification {
    cs {
      ...EnergyProjectCSSpecification
    }
    ppa {
      ...EnergyProjectPPASpecification
    }
    cash {
      ...EnergyProjectCashSpecification
    }
  }
  milestoneSummary {
    ...MilestoneSummary
  }
  developer {
    name
    pid
  }
  progressNotes {
    ...ProgressNotes
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAddProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddProjectMutation,
    TError,
    Types.AddProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddProjectMutation,
    TError,
    Types.AddProjectMutationVariables,
    TContext
  >({
    mutationKey: ["AddProject"],
    mutationFn: (variables?: Types.AddProjectMutationVariables) =>
      publicFetcher<Types.AddProjectMutation, Types.AddProjectMutationVariables>(
        AddProjectDocument,
        variables,
      )(),
    ...options,
  })
}

useAddProjectMutation.fetcher = (
  variables: Types.AddProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddProjectMutation, Types.AddProjectMutationVariables>(
    AddProjectDocument,
    variables,
    options,
  )

export const SetProgressPriorityDocument = new TypedDocumentString(`
    mutation SetProgressPriority($projectPid: String!, $priority: ProgressPriority) {
  progressNotes {
    setPriority(projectPid: $projectPid, priority: $priority) {
      __typename
      ... on ProgressNotesType {
        ...ProgressNotes
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useSetProgressPriorityMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetProgressPriorityMutation,
    TError,
    Types.SetProgressPriorityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProgressPriorityMutation,
    TError,
    Types.SetProgressPriorityMutationVariables,
    TContext
  >({
    mutationKey: ["SetProgressPriority"],
    mutationFn: (variables?: Types.SetProgressPriorityMutationVariables) =>
      publicFetcher<
        Types.SetProgressPriorityMutation,
        Types.SetProgressPriorityMutationVariables
      >(SetProgressPriorityDocument, variables)(),
    ...options,
  })
}

useSetProgressPriorityMutation.fetcher = (
  variables: Types.SetProgressPriorityMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProgressPriorityMutation,
    Types.SetProgressPriorityMutationVariables
  >(SetProgressPriorityDocument, variables, options)

export const AddProgressNoteDocument = new TypedDocumentString(`
    mutation AddProgressNote($projectPid: String!, $content: QuillDelta!) {
  progressNotes {
    addNote(projectPid: $projectPid, content: $content) {
      __typename
      ... on ProgressNotesType {
        ...ProgressNotes
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAddProgressNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddProgressNoteMutation,
    TError,
    Types.AddProgressNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddProgressNoteMutation,
    TError,
    Types.AddProgressNoteMutationVariables,
    TContext
  >({
    mutationKey: ["AddProgressNote"],
    mutationFn: (variables?: Types.AddProgressNoteMutationVariables) =>
      publicFetcher<
        Types.AddProgressNoteMutation,
        Types.AddProgressNoteMutationVariables
      >(AddProgressNoteDocument, variables)(),
    ...options,
  })
}

useAddProgressNoteMutation.fetcher = (
  variables: Types.AddProgressNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddProgressNoteMutation, Types.AddProgressNoteMutationVariables>(
    AddProgressNoteDocument,
    variables,
    options,
  )

export const AddProgressActionItemDocument = new TypedDocumentString(`
    mutation AddProgressActionItem($projectPid: String!, $content: QuillDelta!) {
  progressNotes {
    addActionItem(projectPid: $projectPid, content: $content) {
      __typename
      ... on ProgressNotesType {
        ...ProgressNotes
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAddProgressActionItemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddProgressActionItemMutation,
    TError,
    Types.AddProgressActionItemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddProgressActionItemMutation,
    TError,
    Types.AddProgressActionItemMutationVariables,
    TContext
  >({
    mutationKey: ["AddProgressActionItem"],
    mutationFn: (variables?: Types.AddProgressActionItemMutationVariables) =>
      publicFetcher<
        Types.AddProgressActionItemMutation,
        Types.AddProgressActionItemMutationVariables
      >(AddProgressActionItemDocument, variables)(),
    ...options,
  })
}

useAddProgressActionItemMutation.fetcher = (
  variables: Types.AddProgressActionItemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddProgressActionItemMutation,
    Types.AddProgressActionItemMutationVariables
  >(AddProgressActionItemDocument, variables, options)

export const EditProgressNoteDocument = new TypedDocumentString(`
    mutation EditProgressNote($projectPid: String!, $notePid: String!, $content: QuillDelta!) {
  progressNotes {
    editNote(projectPid: $projectPid, notePid: $notePid, content: $content) {
      __typename
      ... on ProgressNotesType {
        ...ProgressNotes
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useEditProgressNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditProgressNoteMutation,
    TError,
    Types.EditProgressNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditProgressNoteMutation,
    TError,
    Types.EditProgressNoteMutationVariables,
    TContext
  >({
    mutationKey: ["EditProgressNote"],
    mutationFn: (variables?: Types.EditProgressNoteMutationVariables) =>
      publicFetcher<
        Types.EditProgressNoteMutation,
        Types.EditProgressNoteMutationVariables
      >(EditProgressNoteDocument, variables)(),
    ...options,
  })
}

useEditProgressNoteMutation.fetcher = (
  variables: Types.EditProgressNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditProgressNoteMutation,
    Types.EditProgressNoteMutationVariables
  >(EditProgressNoteDocument, variables, options)

export const TrashProgressNoteDocument = new TypedDocumentString(`
    mutation TrashProgressNote($projectPid: String!, $notePid: String!) {
  progressNotes {
    trashNote(projectPid: $projectPid, notePid: $notePid) {
      __typename
      ... on ProgressNotesType {
        ...ProgressNotes
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useTrashProgressNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashProgressNoteMutation,
    TError,
    Types.TrashProgressNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashProgressNoteMutation,
    TError,
    Types.TrashProgressNoteMutationVariables,
    TContext
  >({
    mutationKey: ["TrashProgressNote"],
    mutationFn: (variables?: Types.TrashProgressNoteMutationVariables) =>
      publicFetcher<
        Types.TrashProgressNoteMutation,
        Types.TrashProgressNoteMutationVariables
      >(TrashProgressNoteDocument, variables)(),
    ...options,
  })
}

useTrashProgressNoteMutation.fetcher = (
  variables: Types.TrashProgressNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.TrashProgressNoteMutation,
    Types.TrashProgressNoteMutationVariables
  >(TrashProgressNoteDocument, variables, options)

export const UploadAdminHelioscopeDocument = new TypedDocumentString(`
    mutation UploadAdminHelioscope($input: HelioscopeUploadInput!) {
  admin {
    helioscopes {
      uploadHelioscope(uploadInput: $input) {
        __typename
        ... on Helioscope {
          pid
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useUploadAdminHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UploadAdminHelioscopeMutation,
    TError,
    Types.UploadAdminHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UploadAdminHelioscopeMutation,
    TError,
    Types.UploadAdminHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["UploadAdminHelioscope"],
    mutationFn: (variables?: Types.UploadAdminHelioscopeMutationVariables) =>
      publicFetcher<
        Types.UploadAdminHelioscopeMutation,
        Types.UploadAdminHelioscopeMutationVariables
      >(UploadAdminHelioscopeDocument, variables)(),
    ...options,
  })
}

useUploadAdminHelioscopeMutation.fetcher = (
  variables: Types.UploadAdminHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UploadAdminHelioscopeMutation,
    Types.UploadAdminHelioscopeMutationVariables
  >(UploadAdminHelioscopeDocument, variables, options)

export const SetBuildingFieldDocument = new TypedDocumentString(`
    mutation SetBuildingField($pid: String!, $input: SetBuildingFieldInput!) {
  buildingData {
    setField(pid: $pid, input: $input) {
      __typename
    }
  }
}
    `)

export const useSetBuildingFieldMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBuildingFieldMutation,
    TError,
    Types.SetBuildingFieldMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBuildingFieldMutation,
    TError,
    Types.SetBuildingFieldMutationVariables,
    TContext
  >({
    mutationKey: ["SetBuildingField"],
    mutationFn: (variables?: Types.SetBuildingFieldMutationVariables) =>
      publicFetcher<
        Types.SetBuildingFieldMutation,
        Types.SetBuildingFieldMutationVariables
      >(SetBuildingFieldDocument, variables)(),
    ...options,
  })
}

useSetBuildingFieldMutation.fetcher = (
  variables: Types.SetBuildingFieldMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBuildingFieldMutation,
    Types.SetBuildingFieldMutationVariables
  >(SetBuildingFieldDocument, variables, options)

export const SetSiteOwnerNameDocument = new TypedDocumentString(`
    mutation SetSiteOwnerName($sitePid: String!, $ownerName: String) {
  buildingData {
    setSiteOwnerName(sitePid: $sitePid, ownerName: $ownerName)
  }
}
    `)

export const useSetSiteOwnerNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteOwnerNameMutation,
    TError,
    Types.SetSiteOwnerNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteOwnerNameMutation,
    TError,
    Types.SetSiteOwnerNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteOwnerName"],
    mutationFn: (variables?: Types.SetSiteOwnerNameMutationVariables) =>
      publicFetcher<
        Types.SetSiteOwnerNameMutation,
        Types.SetSiteOwnerNameMutationVariables
      >(SetSiteOwnerNameDocument, variables)(),
    ...options,
  })
}

useSetSiteOwnerNameMutation.fetcher = (
  variables: Types.SetSiteOwnerNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetSiteOwnerNameMutation,
    Types.SetSiteOwnerNameMutationVariables
  >(SetSiteOwnerNameDocument, variables, options)

export const AddOwnerDocument = new TypedDocumentString(`
    mutation AddOwner($sitePid: String, $buildingDataPid: String, $companyPid: String, $newCompanyName: String, $primary: Boolean) {
  buildingData {
    addOwner(
      sitePid: $sitePid
      buildingDataPid: $buildingDataPid
      companyPid: $companyPid
      newCompanyName: $newCompanyName
      primary: $primary
    ) {
      __typename
      ... on CompanyToBuilding {
        id
        isPrimary
        company {
          pid
          canonicalName
        }
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useAddOwnerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddOwnerMutation,
    TError,
    Types.AddOwnerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddOwnerMutation,
    TError,
    Types.AddOwnerMutationVariables,
    TContext
  >({
    mutationKey: ["AddOwner"],
    mutationFn: (variables?: Types.AddOwnerMutationVariables) =>
      publicFetcher<Types.AddOwnerMutation, Types.AddOwnerMutationVariables>(
        AddOwnerDocument,
        variables,
      )(),
    ...options,
  })
}

useAddOwnerMutation.fetcher = (
  variables?: Types.AddOwnerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddOwnerMutation, Types.AddOwnerMutationVariables>(
    AddOwnerDocument,
    variables,
    options,
  )

export const RemoveOwnerDocument = new TypedDocumentString(`
    mutation RemoveOwner($relationshipId: Int!) {
  admin {
    companyRelationship(relationshipId: $relationshipId) {
      remove
    }
  }
}
    `)

export const useRemoveOwnerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveOwnerMutation,
    TError,
    Types.RemoveOwnerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveOwnerMutation,
    TError,
    Types.RemoveOwnerMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveOwner"],
    mutationFn: (variables?: Types.RemoveOwnerMutationVariables) =>
      publicFetcher<Types.RemoveOwnerMutation, Types.RemoveOwnerMutationVariables>(
        RemoveOwnerDocument,
        variables,
      )(),
    ...options,
  })
}

useRemoveOwnerMutation.fetcher = (
  variables: Types.RemoveOwnerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RemoveOwnerMutation, Types.RemoveOwnerMutationVariables>(
    RemoveOwnerDocument,
    variables,
    options,
  )

export const AnnotateAffiliationDocument = new TypedDocumentString(`
    mutation AnnotateAffiliation($input: AnnotateAffiliationInput!) {
  buildingData {
    annotateAffiliation(input: $input) {
      __typename
      ... on BuildingAffiliationAnnotation {
        id
        noteText
        companies {
          type
          company {
            pid
          }
        }
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useAnnotateAffiliationMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AnnotateAffiliationMutation,
    TError,
    Types.AnnotateAffiliationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AnnotateAffiliationMutation,
    TError,
    Types.AnnotateAffiliationMutationVariables,
    TContext
  >({
    mutationKey: ["AnnotateAffiliation"],
    mutationFn: (variables?: Types.AnnotateAffiliationMutationVariables) =>
      publicFetcher<
        Types.AnnotateAffiliationMutation,
        Types.AnnotateAffiliationMutationVariables
      >(AnnotateAffiliationDocument, variables)(),
    ...options,
  })
}

useAnnotateAffiliationMutation.fetcher = (
  variables: Types.AnnotateAffiliationMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AnnotateAffiliationMutation,
    Types.AnnotateAffiliationMutationVariables
  >(AnnotateAffiliationDocument, variables, options)

export const EnrichBuildingDocument = new TypedDocumentString(`
    mutation EnrichBuilding($pid: String!) {
  buildingData {
    enrich(pid: $pid) {
      taskId
    }
  }
}
    `)

export const useEnrichBuildingMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EnrichBuildingMutation,
    TError,
    Types.EnrichBuildingMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EnrichBuildingMutation,
    TError,
    Types.EnrichBuildingMutationVariables,
    TContext
  >({
    mutationKey: ["EnrichBuilding"],
    mutationFn: (variables?: Types.EnrichBuildingMutationVariables) =>
      publicFetcher<
        Types.EnrichBuildingMutation,
        Types.EnrichBuildingMutationVariables
      >(EnrichBuildingDocument, variables)(),
    ...options,
  })
}

useEnrichBuildingMutation.fetcher = (
  variables: Types.EnrichBuildingMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EnrichBuildingMutation, Types.EnrichBuildingMutationVariables>(
    EnrichBuildingDocument,
    variables,
    options,
  )

export const ResearchBuildingAffiliationDocument = new TypedDocumentString(`
    mutation ResearchBuildingAffiliation($pid: String!) {
  buildingData {
    researchAffiliation(pid: $pid) {
      taskId
    }
  }
}
    `)

export const useResearchBuildingAffiliationMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ResearchBuildingAffiliationMutation,
    TError,
    Types.ResearchBuildingAffiliationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ResearchBuildingAffiliationMutation,
    TError,
    Types.ResearchBuildingAffiliationMutationVariables,
    TContext
  >({
    mutationKey: ["ResearchBuildingAffiliation"],
    mutationFn: (variables?: Types.ResearchBuildingAffiliationMutationVariables) =>
      publicFetcher<
        Types.ResearchBuildingAffiliationMutation,
        Types.ResearchBuildingAffiliationMutationVariables
      >(ResearchBuildingAffiliationDocument, variables)(),
    ...options,
  })
}

useResearchBuildingAffiliationMutation.fetcher = (
  variables: Types.ResearchBuildingAffiliationMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ResearchBuildingAffiliationMutation,
    Types.ResearchBuildingAffiliationMutationVariables
  >(ResearchBuildingAffiliationDocument, variables, options)

export const ReonomyLookupDocument = new TypedDocumentString(`
    mutation ReonomyLookup($pid: String!) {
  buildingData {
    reonomyLookup(pid: $pid) {
      __typename
      ... on BuildingData {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useReonomyLookupMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ReonomyLookupMutation,
    TError,
    Types.ReonomyLookupMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ReonomyLookupMutation,
    TError,
    Types.ReonomyLookupMutationVariables,
    TContext
  >({
    mutationKey: ["ReonomyLookup"],
    mutationFn: (variables?: Types.ReonomyLookupMutationVariables) =>
      publicFetcher<Types.ReonomyLookupMutation, Types.ReonomyLookupMutationVariables>(
        ReonomyLookupDocument,
        variables,
      )(),
    ...options,
  })
}

useReonomyLookupMutation.fetcher = (
  variables: Types.ReonomyLookupMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ReonomyLookupMutation, Types.ReonomyLookupMutationVariables>(
    ReonomyLookupDocument,
    variables,
    options,
  )

export const SetMyNameDocument = new TypedDocumentString(`
    mutation SetMyName($name: String!) {
  me {
    setName(name: $name) {
      pid
      name
    }
  }
}
    `)

export const useSetMyNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetMyNameMutation,
    TError,
    Types.SetMyNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMyNameMutation,
    TError,
    Types.SetMyNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetMyName"],
    mutationFn: (variables?: Types.SetMyNameMutationVariables) =>
      publicFetcher<Types.SetMyNameMutation, Types.SetMyNameMutationVariables>(
        SetMyNameDocument,
        variables,
      )(),
    ...options,
  })
}

useSetMyNameMutation.fetcher = (
  variables: Types.SetMyNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetMyNameMutation, Types.SetMyNameMutationVariables>(
    SetMyNameDocument,
    variables,
    options,
  )

export const SetMyMeetingSchedulingUrlDocument = new TypedDocumentString(`
    mutation SetMyMeetingSchedulingUrl($url: String) {
  me {
    setMeetingSchedulingUrl(url: $url) {
      __typename
      ... on AuthUser {
        pid
        meetingSchedulingUrl
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useSetMyMeetingSchedulingUrlMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetMyMeetingSchedulingUrlMutation,
    TError,
    Types.SetMyMeetingSchedulingUrlMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMyMeetingSchedulingUrlMutation,
    TError,
    Types.SetMyMeetingSchedulingUrlMutationVariables,
    TContext
  >({
    mutationKey: ["SetMyMeetingSchedulingUrl"],
    mutationFn: (variables?: Types.SetMyMeetingSchedulingUrlMutationVariables) =>
      publicFetcher<
        Types.SetMyMeetingSchedulingUrlMutation,
        Types.SetMyMeetingSchedulingUrlMutationVariables
      >(SetMyMeetingSchedulingUrlDocument, variables)(),
    ...options,
  })
}

useSetMyMeetingSchedulingUrlMutation.fetcher = (
  variables?: Types.SetMyMeetingSchedulingUrlMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetMyMeetingSchedulingUrlMutation,
    Types.SetMyMeetingSchedulingUrlMutationVariables
  >(SetMyMeetingSchedulingUrlDocument, variables, options)

export const SetMyAvatarDocument = new TypedDocumentString(`
    mutation SetMyAvatar($upload: Upload!) {
  me {
    setAvatar(upload: $upload) {
      __typename
      ... on AuthUser {
        pid
        avatarUrl
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useSetMyAvatarMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetMyAvatarMutation,
    TError,
    Types.SetMyAvatarMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMyAvatarMutation,
    TError,
    Types.SetMyAvatarMutationVariables,
    TContext
  >({
    mutationKey: ["SetMyAvatar"],
    mutationFn: (variables?: Types.SetMyAvatarMutationVariables) =>
      publicFetcher<Types.SetMyAvatarMutation, Types.SetMyAvatarMutationVariables>(
        SetMyAvatarDocument,
        variables,
      )(),
    ...options,
  })
}

useSetMyAvatarMutation.fetcher = (
  variables: Types.SetMyAvatarMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetMyAvatarMutation, Types.SetMyAvatarMutationVariables>(
    SetMyAvatarDocument,
    variables,
    options,
  )

export const SetCompanyFieldDocument = new TypedDocumentString(`
    mutation SetCompanyField($pid: String!, $input: SetCompanyFieldInput!) {
  admin {
    companies {
      setField(pid: $pid, input: $input) {
        pid
        canonicalName
        description
        reonomyOwnerId
        url
        hubspotId
        org {
          pid
          name
        }
      }
    }
  }
}
    `)

export const useSetCompanyFieldMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCompanyFieldMutation,
    TError,
    Types.SetCompanyFieldMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCompanyFieldMutation,
    TError,
    Types.SetCompanyFieldMutationVariables,
    TContext
  >({
    mutationKey: ["SetCompanyField"],
    mutationFn: (variables?: Types.SetCompanyFieldMutationVariables) =>
      publicFetcher<
        Types.SetCompanyFieldMutation,
        Types.SetCompanyFieldMutationVariables
      >(SetCompanyFieldDocument, variables)(),
    ...options,
  })
}

useSetCompanyFieldMutation.fetcher = (
  variables: Types.SetCompanyFieldMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetCompanyFieldMutation, Types.SetCompanyFieldMutationVariables>(
    SetCompanyFieldDocument,
    variables,
    options,
  )

export const SetCompanyRelationshipPrimaryDocument = new TypedDocumentString(`
    mutation SetCompanyRelationshipPrimary($relationshipId: Int!) {
  admin {
    companyRelationship(relationshipId: $relationshipId) {
      setPrimary {
        id
        isPrimary
      }
    }
  }
}
    `)

export const useSetCompanyRelationshipPrimaryMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCompanyRelationshipPrimaryMutation,
    TError,
    Types.SetCompanyRelationshipPrimaryMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCompanyRelationshipPrimaryMutation,
    TError,
    Types.SetCompanyRelationshipPrimaryMutationVariables,
    TContext
  >({
    mutationKey: ["SetCompanyRelationshipPrimary"],
    mutationFn: (variables?: Types.SetCompanyRelationshipPrimaryMutationVariables) =>
      publicFetcher<
        Types.SetCompanyRelationshipPrimaryMutation,
        Types.SetCompanyRelationshipPrimaryMutationVariables
      >(SetCompanyRelationshipPrimaryDocument, variables)(),
    ...options,
  })
}

useSetCompanyRelationshipPrimaryMutation.fetcher = (
  variables: Types.SetCompanyRelationshipPrimaryMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCompanyRelationshipPrimaryMutation,
    Types.SetCompanyRelationshipPrimaryMutationVariables
  >(SetCompanyRelationshipPrimaryDocument, variables, options)

export const RunCompanyAiAnalysisDocument = new TypedDocumentString(`
    mutation RunCompanyAIAnalysis($pid: String!, $dimension: Dimension!) {
  admin {
    companies {
      runAiAnalysis(pid: $pid, dimension: $dimension) {
        id
        status
        note
      }
    }
  }
}
    `)

export const useRunCompanyAiAnalysisMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunCompanyAiAnalysisMutation,
    TError,
    Types.RunCompanyAiAnalysisMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunCompanyAiAnalysisMutation,
    TError,
    Types.RunCompanyAiAnalysisMutationVariables,
    TContext
  >({
    mutationKey: ["RunCompanyAIAnalysis"],
    mutationFn: (variables?: Types.RunCompanyAiAnalysisMutationVariables) =>
      publicFetcher<
        Types.RunCompanyAiAnalysisMutation,
        Types.RunCompanyAiAnalysisMutationVariables
      >(RunCompanyAiAnalysisDocument, variables)(),
    ...options,
  })
}

useRunCompanyAiAnalysisMutation.fetcher = (
  variables: Types.RunCompanyAiAnalysisMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RunCompanyAiAnalysisMutation,
    Types.RunCompanyAiAnalysisMutationVariables
  >(RunCompanyAiAnalysisDocument, variables, options)

export const NewCompanyDocument = new TypedDocumentString(`
    mutation NewCompany($input: NewCompanyInput!) {
  admin {
    companies {
      new(input: $input) {
        __typename
        ... on Company {
          pid
          canonicalName
          description
          reonomyOwnerId
          url
          hubspotId
          org {
            pid
            name
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `)

export const useNewCompanyMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.NewCompanyMutation,
    TError,
    Types.NewCompanyMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.NewCompanyMutation,
    TError,
    Types.NewCompanyMutationVariables,
    TContext
  >({
    mutationKey: ["NewCompany"],
    mutationFn: (variables?: Types.NewCompanyMutationVariables) =>
      publicFetcher<Types.NewCompanyMutation, Types.NewCompanyMutationVariables>(
        NewCompanyDocument,
        variables,
      )(),
    ...options,
  })
}

useNewCompanyMutation.fetcher = (
  variables: Types.NewCompanyMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.NewCompanyMutation, Types.NewCompanyMutationVariables>(
    NewCompanyDocument,
    variables,
    options,
  )

export const GenerateHostAwardsDocument = new TypedDocumentString(`
    mutation GenerateHostAwards($pid: String!) {
  bidPackages {
    generateHostAwards(pid: $pid) {
      __typename
      ... on BidPackage {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useGenerateHostAwardsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.GenerateHostAwardsMutation,
    TError,
    Types.GenerateHostAwardsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.GenerateHostAwardsMutation,
    TError,
    Types.GenerateHostAwardsMutationVariables,
    TContext
  >({
    mutationKey: ["GenerateHostAwards"],
    mutationFn: (variables?: Types.GenerateHostAwardsMutationVariables) =>
      publicFetcher<
        Types.GenerateHostAwardsMutation,
        Types.GenerateHostAwardsMutationVariables
      >(GenerateHostAwardsDocument, variables)(),
    ...options,
  })
}

useGenerateHostAwardsMutation.fetcher = (
  variables: Types.GenerateHostAwardsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GenerateHostAwardsMutation,
    Types.GenerateHostAwardsMutationVariables
  >(GenerateHostAwardsDocument, variables, options)

export const CreateBidPackageDocument = new TypedDocumentString(`
    mutation CreateBidPackage($input: NewBidPackageInput!) {
  bidPackages {
    new(input: $input) {
      __typename
      ... on BidPackage {
        ...BasicBidPackage
      }
      ... on Error {
        message
      }
    }
  }
}
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`)

export const useCreateBidPackageMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateBidPackageMutation,
    TError,
    Types.CreateBidPackageMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBidPackageMutation,
    TError,
    Types.CreateBidPackageMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBidPackage"],
    mutationFn: (variables?: Types.CreateBidPackageMutationVariables) =>
      publicFetcher<
        Types.CreateBidPackageMutation,
        Types.CreateBidPackageMutationVariables
      >(CreateBidPackageDocument, variables)(),
    ...options,
  })
}

useCreateBidPackageMutation.fetcher = (
  variables: Types.CreateBidPackageMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBidPackageMutation,
    Types.CreateBidPackageMutationVariables
  >(CreateBidPackageDocument, variables, options)

export const EnqueueBuildingDataLookupDocument = new TypedDocumentString(`
    mutation EnqueueBuildingDataLookup($csvImportPid: String!) {
  enqueueBuildingDataLookup(csvImportPid: $csvImportPid) {
    ...CeleryEnqueueResult
  }
}
    fragment CeleryEnqueueResult on CeleryTaskError {
  __typename
  ... on CeleryTask {
    taskId
  }
  ... on Error {
    message
  }
}`)

export const useEnqueueBuildingDataLookupMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EnqueueBuildingDataLookupMutation,
    TError,
    Types.EnqueueBuildingDataLookupMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EnqueueBuildingDataLookupMutation,
    TError,
    Types.EnqueueBuildingDataLookupMutationVariables,
    TContext
  >({
    mutationKey: ["EnqueueBuildingDataLookup"],
    mutationFn: (variables?: Types.EnqueueBuildingDataLookupMutationVariables) =>
      publicFetcher<
        Types.EnqueueBuildingDataLookupMutation,
        Types.EnqueueBuildingDataLookupMutationVariables
      >(EnqueueBuildingDataLookupDocument, variables)(),
    ...options,
  })
}

useEnqueueBuildingDataLookupMutation.fetcher = (
  variables: Types.EnqueueBuildingDataLookupMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EnqueueBuildingDataLookupMutation,
    Types.EnqueueBuildingDataLookupMutationVariables
  >(EnqueueBuildingDataLookupDocument, variables, options)

export const EnqueueCreateSitesDocument = new TypedDocumentString(`
    mutation EnqueueCreateSites($csvImportPid: String!) {
  enqueueCreateSites(csvImportPid: $csvImportPid) {
    ...CeleryEnqueueResult
  }
}
    fragment CeleryEnqueueResult on CeleryTaskError {
  __typename
  ... on CeleryTask {
    taskId
  }
  ... on Error {
    message
  }
}`)

export const useEnqueueCreateSitesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EnqueueCreateSitesMutation,
    TError,
    Types.EnqueueCreateSitesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EnqueueCreateSitesMutation,
    TError,
    Types.EnqueueCreateSitesMutationVariables,
    TContext
  >({
    mutationKey: ["EnqueueCreateSites"],
    mutationFn: (variables?: Types.EnqueueCreateSitesMutationVariables) =>
      publicFetcher<
        Types.EnqueueCreateSitesMutation,
        Types.EnqueueCreateSitesMutationVariables
      >(EnqueueCreateSitesDocument, variables)(),
    ...options,
  })
}

useEnqueueCreateSitesMutation.fetcher = (
  variables: Types.EnqueueCreateSitesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EnqueueCreateSitesMutation,
    Types.EnqueueCreateSitesMutationVariables
  >(EnqueueCreateSitesDocument, variables, options)

export const EnqueueReonomyOwnerLookupDocument = new TypedDocumentString(`
    mutation EnqueueReonomyOwnerLookup($input: ReonomyOwnerLookupInput!) {
  enqueueReonomyOwnerLookup(input: $input) {
    ...CeleryEnqueueResult
  }
}
    fragment CeleryEnqueueResult on CeleryTaskError {
  __typename
  ... on CeleryTask {
    taskId
  }
  ... on Error {
    message
  }
}`)

export const useEnqueueReonomyOwnerLookupMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EnqueueReonomyOwnerLookupMutation,
    TError,
    Types.EnqueueReonomyOwnerLookupMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EnqueueReonomyOwnerLookupMutation,
    TError,
    Types.EnqueueReonomyOwnerLookupMutationVariables,
    TContext
  >({
    mutationKey: ["EnqueueReonomyOwnerLookup"],
    mutationFn: (variables?: Types.EnqueueReonomyOwnerLookupMutationVariables) =>
      publicFetcher<
        Types.EnqueueReonomyOwnerLookupMutation,
        Types.EnqueueReonomyOwnerLookupMutationVariables
      >(EnqueueReonomyOwnerLookupDocument, variables)(),
    ...options,
  })
}

useEnqueueReonomyOwnerLookupMutation.fetcher = (
  variables: Types.EnqueueReonomyOwnerLookupMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EnqueueReonomyOwnerLookupMutation,
    Types.EnqueueReonomyOwnerLookupMutationVariables
  >(EnqueueReonomyOwnerLookupDocument, variables, options)

export const EnqueueExtractBillLineItemsDocument = new TypedDocumentString(`
    mutation EnqueueExtractBillLineItems($documentBillingMonthId: Int!) {
  enqueueExtractBillLineItems(documentBillingMonthId: $documentBillingMonthId) {
    ...CeleryEnqueueResult
  }
}
    fragment CeleryEnqueueResult on CeleryTaskError {
  __typename
  ... on CeleryTask {
    taskId
  }
  ... on Error {
    message
  }
}`)

export const useEnqueueExtractBillLineItemsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EnqueueExtractBillLineItemsMutation,
    TError,
    Types.EnqueueExtractBillLineItemsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EnqueueExtractBillLineItemsMutation,
    TError,
    Types.EnqueueExtractBillLineItemsMutationVariables,
    TContext
  >({
    mutationKey: ["EnqueueExtractBillLineItems"],
    mutationFn: (variables?: Types.EnqueueExtractBillLineItemsMutationVariables) =>
      publicFetcher<
        Types.EnqueueExtractBillLineItemsMutation,
        Types.EnqueueExtractBillLineItemsMutationVariables
      >(EnqueueExtractBillLineItemsDocument, variables)(),
    ...options,
  })
}

useEnqueueExtractBillLineItemsMutation.fetcher = (
  variables: Types.EnqueueExtractBillLineItemsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EnqueueExtractBillLineItemsMutation,
    Types.EnqueueExtractBillLineItemsMutationVariables
  >(EnqueueExtractBillLineItemsDocument, variables, options)

export const StartImpersonationDocument = new TypedDocumentString(`
    mutation StartImpersonation($userPid: String!) {
  impersonation {
    start(userPid: $userPid) {
      __typename
      ... on ImpersonationSession {
        redirectUrl
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useStartImpersonationMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.StartImpersonationMutation,
    TError,
    Types.StartImpersonationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.StartImpersonationMutation,
    TError,
    Types.StartImpersonationMutationVariables,
    TContext
  >({
    mutationKey: ["StartImpersonation"],
    mutationFn: (variables?: Types.StartImpersonationMutationVariables) =>
      publicFetcher<
        Types.StartImpersonationMutation,
        Types.StartImpersonationMutationVariables
      >(StartImpersonationDocument, variables)(),
    ...options,
  })
}

useStartImpersonationMutation.fetcher = (
  variables: Types.StartImpersonationMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.StartImpersonationMutation,
    Types.StartImpersonationMutationVariables
  >(StartImpersonationDocument, variables, options)

export const StopImpersonationDocument = new TypedDocumentString(`
    mutation StopImpersonation {
  impersonation {
    stop
  }
}
    `)

export const useStopImpersonationMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.StopImpersonationMutation,
    TError,
    Types.StopImpersonationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.StopImpersonationMutation,
    TError,
    Types.StopImpersonationMutationVariables,
    TContext
  >({
    mutationKey: ["StopImpersonation"],
    mutationFn: (variables?: Types.StopImpersonationMutationVariables) =>
      publicFetcher<
        Types.StopImpersonationMutation,
        Types.StopImpersonationMutationVariables
      >(StopImpersonationDocument, variables)(),
    ...options,
  })
}

useStopImpersonationMutation.fetcher = (
  variables?: Types.StopImpersonationMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.StopImpersonationMutation,
    Types.StopImpersonationMutationVariables
  >(StopImpersonationDocument, variables, options)

export const CreateApiKeyDocument = new TypedDocumentString(`
    mutation CreateApiKey($input: ApiKeyInput!) {
  apiKeys {
    new(input: $input) {
      key
      apiKey {
        id
        pid
        label
        isEnabled
        createdAt
      }
    }
  }
}
    `)

export const useCreateApiKeyMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateApiKeyMutation,
    TError,
    Types.CreateApiKeyMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateApiKeyMutation,
    TError,
    Types.CreateApiKeyMutationVariables,
    TContext
  >({
    mutationKey: ["CreateApiKey"],
    mutationFn: (variables?: Types.CreateApiKeyMutationVariables) =>
      publicFetcher<Types.CreateApiKeyMutation, Types.CreateApiKeyMutationVariables>(
        CreateApiKeyDocument,
        variables,
      )(),
    ...options,
  })
}

useCreateApiKeyMutation.fetcher = (
  variables: Types.CreateApiKeyMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreateApiKeyMutation, Types.CreateApiKeyMutationVariables>(
    CreateApiKeyDocument,
    variables,
    options,
  )

export const UpdateApiKeyDocument = new TypedDocumentString(`
    mutation UpdateApiKey($pid: String!, $input: ApiKeyInput!) {
  apiKeys {
    update(pid: $pid, input: $input) {
      ... on UserApiKey {
        id
        pid
        label
        isEnabled
        createdAt
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useUpdateApiKeyMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateApiKeyMutation,
    TError,
    Types.UpdateApiKeyMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateApiKeyMutation,
    TError,
    Types.UpdateApiKeyMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateApiKey"],
    mutationFn: (variables?: Types.UpdateApiKeyMutationVariables) =>
      publicFetcher<Types.UpdateApiKeyMutation, Types.UpdateApiKeyMutationVariables>(
        UpdateApiKeyDocument,
        variables,
      )(),
    ...options,
  })
}

useUpdateApiKeyMutation.fetcher = (
  variables: Types.UpdateApiKeyMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UpdateApiKeyMutation, Types.UpdateApiKeyMutationVariables>(
    UpdateApiKeyDocument,
    variables,
    options,
  )

export const DisableApiKeyDocument = new TypedDocumentString(`
    mutation DisableApiKey($pid: String!) {
  apiKeys {
    disable(pid: $pid) {
      ... on UserApiKey {
        id
        pid
        label
        isEnabled
        createdAt
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useDisableApiKeyMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DisableApiKeyMutation,
    TError,
    Types.DisableApiKeyMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DisableApiKeyMutation,
    TError,
    Types.DisableApiKeyMutationVariables,
    TContext
  >({
    mutationKey: ["DisableApiKey"],
    mutationFn: (variables?: Types.DisableApiKeyMutationVariables) =>
      publicFetcher<Types.DisableApiKeyMutation, Types.DisableApiKeyMutationVariables>(
        DisableApiKeyDocument,
        variables,
      )(),
    ...options,
  })
}

useDisableApiKeyMutation.fetcher = (
  variables: Types.DisableApiKeyMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DisableApiKeyMutation, Types.DisableApiKeyMutationVariables>(
    DisableApiKeyDocument,
    variables,
    options,
  )

export const RemindSignatureRequestDocument = new TypedDocumentString(`
    mutation RemindSignatureRequest($signingPid: String!) {
  signing {
    remind(signingPid: $signingPid) {
      __typename
      ... on RemindedSignatureRequest {
        signingPid
        remindedEmails
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useRemindSignatureRequestMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemindSignatureRequestMutation,
    TError,
    Types.RemindSignatureRequestMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemindSignatureRequestMutation,
    TError,
    Types.RemindSignatureRequestMutationVariables,
    TContext
  >({
    mutationKey: ["RemindSignatureRequest"],
    mutationFn: (variables?: Types.RemindSignatureRequestMutationVariables) =>
      publicFetcher<
        Types.RemindSignatureRequestMutation,
        Types.RemindSignatureRequestMutationVariables
      >(RemindSignatureRequestDocument, variables)(),
    ...options,
  })
}

useRemindSignatureRequestMutation.fetcher = (
  variables: Types.RemindSignatureRequestMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemindSignatureRequestMutation,
    Types.RemindSignatureRequestMutationVariables
  >(RemindSignatureRequestDocument, variables, options)

export const VoidSignatureRequestDocument = new TypedDocumentString(`
    mutation VoidSignatureRequest($signingPid: String!, $reason: String) {
  signing {
    void(signingPid: $signingPid, reason: $reason) {
      __typename
      ... on VoidedSignatureRequest {
        signingPid
        status
        documentPid
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useVoidSignatureRequestMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.VoidSignatureRequestMutation,
    TError,
    Types.VoidSignatureRequestMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.VoidSignatureRequestMutation,
    TError,
    Types.VoidSignatureRequestMutationVariables,
    TContext
  >({
    mutationKey: ["VoidSignatureRequest"],
    mutationFn: (variables?: Types.VoidSignatureRequestMutationVariables) =>
      publicFetcher<
        Types.VoidSignatureRequestMutation,
        Types.VoidSignatureRequestMutationVariables
      >(VoidSignatureRequestDocument, variables)(),
    ...options,
  })
}

useVoidSignatureRequestMutation.fetcher = (
  variables: Types.VoidSignatureRequestMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.VoidSignatureRequestMutation,
    Types.VoidSignatureRequestMutationVariables
  >(VoidSignatureRequestDocument, variables, options)

export const MarkDocumentExecutedDocument = new TypedDocumentString(`
    mutation MarkDocumentExecuted($documentPid: String!, $executedOn: Date, $note: String, $signedCopy: Upload) {
  signing {
    markExecuted(
      documentPid: $documentPid
      executedOn: $executedOn
      note: $note
      signedCopy: $signedCopy
    ) {
      __typename
      ... on MarkedExecutedDocument {
        signingPid
        status
        documentPid
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useMarkDocumentExecutedMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.MarkDocumentExecutedMutation,
    TError,
    Types.MarkDocumentExecutedMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MarkDocumentExecutedMutation,
    TError,
    Types.MarkDocumentExecutedMutationVariables,
    TContext
  >({
    mutationKey: ["MarkDocumentExecuted"],
    mutationFn: (variables?: Types.MarkDocumentExecutedMutationVariables) =>
      publicFetcher<
        Types.MarkDocumentExecutedMutation,
        Types.MarkDocumentExecutedMutationVariables
      >(MarkDocumentExecutedDocument, variables)(),
    ...options,
  })
}

useMarkDocumentExecutedMutation.fetcher = (
  variables: Types.MarkDocumentExecutedMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarkDocumentExecutedMutation,
    Types.MarkDocumentExecutedMutationVariables
  >(MarkDocumentExecutedDocument, variables, options)

export const RevokeManualExecutionDocument = new TypedDocumentString(`
    mutation RevokeManualExecution($signingPid: String!) {
  signing {
    revoke(signingPid: $signingPid) {
      __typename
      ... on RevokedManualExecution {
        signingPid
        status
        documentPid
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useRevokeManualExecutionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RevokeManualExecutionMutation,
    TError,
    Types.RevokeManualExecutionMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RevokeManualExecutionMutation,
    TError,
    Types.RevokeManualExecutionMutationVariables,
    TContext
  >({
    mutationKey: ["RevokeManualExecution"],
    mutationFn: (variables?: Types.RevokeManualExecutionMutationVariables) =>
      publicFetcher<
        Types.RevokeManualExecutionMutation,
        Types.RevokeManualExecutionMutationVariables
      >(RevokeManualExecutionDocument, variables)(),
    ...options,
  })
}

useRevokeManualExecutionMutation.fetcher = (
  variables: Types.RevokeManualExecutionMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevokeManualExecutionMutation,
    Types.RevokeManualExecutionMutationVariables
  >(RevokeManualExecutionDocument, variables, options)

export const SendDocumentForSignatureDocument = new TypedDocumentString(`
    mutation SendDocumentForSignature($documentPid: String!, $developer: SignerInput!, $lumen: SignerInput!, $copyUserPids: [String!], $emailBlurb: String, $expireAfterDays: Int!, $expireWarnDays: Int!) {
  signing {
    send(
      documentPid: $documentPid
      developer: $developer
      lumen: $lumen
      copyUserPids: $copyUserPids
      emailBlurb: $emailBlurb
      expireAfterDays: $expireAfterDays
      expireWarnDays: $expireWarnDays
    ) {
      __typename
      ... on SentSignatureRequest {
        signingPid
        status
        developerEmail
        lumenEmail
      }
      ... on Error {
        message
      }
    }
  }
}
    `)

export const useSendDocumentForSignatureMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SendDocumentForSignatureMutation,
    TError,
    Types.SendDocumentForSignatureMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SendDocumentForSignatureMutation,
    TError,
    Types.SendDocumentForSignatureMutationVariables,
    TContext
  >({
    mutationKey: ["SendDocumentForSignature"],
    mutationFn: (variables?: Types.SendDocumentForSignatureMutationVariables) =>
      publicFetcher<
        Types.SendDocumentForSignatureMutation,
        Types.SendDocumentForSignatureMutationVariables
      >(SendDocumentForSignatureDocument, variables)(),
    ...options,
  })
}

useSendDocumentForSignatureMutation.fetcher = (
  variables: Types.SendDocumentForSignatureMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SendDocumentForSignatureMutation,
    Types.SendDocumentForSignatureMutationVariables
  >(SendDocumentForSignatureDocument, variables, options)

export const ApplyMeetingNoteExtractionResultsDocument = new TypedDocumentString(`
    mutation ApplyMeetingNoteExtractionResults($orgSlug: String!, $input: ExtractionResultProcessInput!) {
  orgBySlug(slug: $orgSlug) {
    meetingNotes {
      applyExtractionResults(input: $input) {
        message
      }
    }
  }
}
    `)

export const useApplyMeetingNoteExtractionResultsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ApplyMeetingNoteExtractionResultsMutation,
    TError,
    Types.ApplyMeetingNoteExtractionResultsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApplyMeetingNoteExtractionResultsMutation,
    TError,
    Types.ApplyMeetingNoteExtractionResultsMutationVariables,
    TContext
  >({
    mutationKey: ["ApplyMeetingNoteExtractionResults"],
    mutationFn: (
      variables?: Types.ApplyMeetingNoteExtractionResultsMutationVariables,
    ) =>
      publicFetcher<
        Types.ApplyMeetingNoteExtractionResultsMutation,
        Types.ApplyMeetingNoteExtractionResultsMutationVariables
      >(ApplyMeetingNoteExtractionResultsDocument, variables)(),
    ...options,
  })
}

useApplyMeetingNoteExtractionResultsMutation.fetcher = (
  variables: Types.ApplyMeetingNoteExtractionResultsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ApplyMeetingNoteExtractionResultsMutation,
    Types.ApplyMeetingNoteExtractionResultsMutationVariables
  >(ApplyMeetingNoteExtractionResultsDocument, variables, options)

export const ListUtilitiesDocument = new TypedDocumentString(`
    query ListUtilities($zipCode: String!, $state: String!) {
  utilitiesForZip(zipCode: $zipCode) {
    __typename
    ... on UtilityList {
      refs {
        id
        pid
        name
        shortName
      }
    }
    ... on Error {
      message
    }
  }
  utilitiesForState(state: $state) {
    __typename
    ... on UtilityList {
      refs {
        id
        pid
        name
        shortName
      }
    }
    ... on Error {
      message
    }
  }
}
    `)

export const useListUtilitiesQuery = <
  TData = Types.ListUtilitiesQuery,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ListUtilitiesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ListUtilitiesQuery, TError, TData>({
    queryKey: ["ListUtilities", variables],
    queryFn: publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
      ListUtilitiesDocument,
      variables,
    ),
    ...options,
  })
}

useListUtilitiesQuery.getKey = (variables: Types.ListUtilitiesQueryVariables) => [
  "ListUtilities",
  variables,
]

useListUtilitiesQuery.rootKey = "ListUtilities"

export const useSuspenseListUtilitiesQuery = <
  TData = Types.ListUtilitiesQuery,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ListUtilitiesQuery, TError, TData>({
    queryKey: ["ListUtilities", variables],
    queryFn: publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
      ListUtilitiesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilities", variables]

useSuspenseListUtilitiesQuery.rootKey = "ListUtilities"

export const useInfiniteListUtilitiesQuery = <
  TData = InfiniteData<Types.ListUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ListUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ListUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
            ListUtilitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilities.infinite", variables]

useInfiniteListUtilitiesQuery.rootKey = "ListUtilities.infinite"

export const useSuspenseInfiniteListUtilitiesQuery = <
  TData = InfiniteData<Types.ListUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ListUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ListUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
            ListUtilitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilities.infinite", variables]

useSuspenseInfiniteListUtilitiesQuery.rootKey = "ListUtilities.infinite"

useListUtilitiesQuery.fetcher = (
  variables: Types.ListUtilitiesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
    ListUtilitiesDocument,
    variables,
    options,
  )

export const OrgSettingsDetailBySlugDocument = new TypedDocumentString(`
    query OrgSettingsDetailBySlug($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    name
    slug
    currentUserPermissions
    portfolios {
      pid
      title
    }
    investorProfile {
      updatedAt
      updatedByName
      hq
      yearFounded
      website
      managementTeam
      description
      distinguishingFeatures
      investorBacking
      longTermOwnershipApproach
      subscriptionMgmtApproach
      committedFunding
      experiences {
        state
        ftmEnergizedPvKw
        ftmAwardedPvKw
        ftmEnergizedBessKwh
        ftmAwardedBessKwh
        btmEnergizedPvKw
        btmAwardedPvKw
        btmEnergizedBessKwh
        btmAwardedBessKwh
      }
      buyBoxes {
        state
        ftmMinPortfolioSize
        ftmMinProjectSize
        ftmMinBessPortfolioSize
        ftmMinBessProjectSize
        ftmSeekingProjects
        btmMinPortfolioSize
        btmMinProjectSize
        btmMinBessPortfolioSize
        btmMinBessProjectSize
        btmSeekingProjects
        utilityProgramNotes
      }
    }
  }
}
    `)

export const useOrgSettingsDetailBySlugQuery = <
  TData = Types.OrgSettingsDetailBySlugQuery,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>({
    queryKey: ["OrgSettingsDetailBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSettingsDetailBySlugQuery,
      Types.OrgSettingsDetailBySlugQueryVariables
    >(OrgSettingsDetailBySlugDocument, variables),
    ...options,
  })
}

useOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug", variables]

useOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug"

export const useSuspenseOrgSettingsDetailBySlugQuery = <
  TData = Types.OrgSettingsDetailBySlugQuery,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>({
    queryKey: ["OrgSettingsDetailBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSettingsDetailBySlugQuery,
      Types.OrgSettingsDetailBySlugQueryVariables
    >(OrgSettingsDetailBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug", variables]

useSuspenseOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug"

export const useInfiniteOrgSettingsDetailBySlugQuery = <
  TData = InfiniteData<Types.OrgSettingsDetailBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSettingsDetailBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSettingsDetailBySlugQuery,
            Types.OrgSettingsDetailBySlugQueryVariables
          >(OrgSettingsDetailBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug.infinite", variables]

useInfiniteOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug.infinite"

export const useSuspenseInfiniteOrgSettingsDetailBySlugQuery = <
  TData = InfiniteData<Types.OrgSettingsDetailBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSettingsDetailBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSettingsDetailBySlugQuery,
            Types.OrgSettingsDetailBySlugQueryVariables
          >(OrgSettingsDetailBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug.infinite", variables]

useSuspenseInfiniteOrgSettingsDetailBySlugQuery.rootKey =
  "OrgSettingsDetailBySlug.infinite"

useOrgSettingsDetailBySlugQuery.fetcher = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OrgSettingsDetailBySlugQuery,
    Types.OrgSettingsDetailBySlugQueryVariables
  >(OrgSettingsDetailBySlugDocument, variables, options)

export const OrgUsersDocument = new TypedDocumentString(`
    query OrgUsers($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    name
    currentUserPermissions
    portfolios {
      pid
      title
    }
    orgUsers {
      user {
        pid
        name
        firstName
        email
      }
      userRoles {
        role
        portfolio {
          pid
          title
        }
      }
    }
    unclaimedInvites {
      dateCreated
      createdBy {
        ...UserRef
      }
      targetUser {
        ...UserRef
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgUsersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsers", variables],
    queryFn: publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers",
  variables,
]

useOrgUsersQuery.rootKey = "OrgUsers"

export const useSuspenseOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsers", variables],
    queryFn: publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers",
  variables,
]

useSuspenseOrgUsersQuery.rootKey = "OrgUsers"

export const useInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infinite",
  variables,
]

useInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

export const useSuspenseInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infinite",
  variables,
]

useSuspenseInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

useOrgUsersQuery.fetcher = (
  variables: Types.OrgUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
    OrgUsersDocument,
    variables,
    options,
  )

export const AdminPortfolioUsersDocument = new TypedDocumentString(`
    query AdminPortfolioUsers($portfolioPid: String!) {
  admin {
    portfolio(pid: $portfolioPid) {
      pid
      title
      users {
        ...AdminUserRef
      }
    }
  }
}
    fragment AdminUserRef on AdminUser {
  id
  pid
  email
  name
  isAdmin
  avatarUrl
}`)

export const useAdminPortfolioUsersQuery = <
  TData = Types.AdminPortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminPortfolioUsersQuery, TError, TData>({
    queryKey: ["AdminPortfolioUsers", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioUsersQuery,
      Types.AdminPortfolioUsersQueryVariables
    >(AdminPortfolioUsersDocument, variables),
    ...options,
  })
}

useAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsers", variables]

useAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers"

export const useSuspenseAdminPortfolioUsersQuery = <
  TData = Types.AdminPortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminPortfolioUsersQuery, TError, TData>({
    queryKey: ["AdminPortfolioUsers", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioUsersQuery,
      Types.AdminPortfolioUsersQueryVariables
    >(AdminPortfolioUsersDocument, variables),
    ...options,
  })
}

useSuspenseAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsers", variables]

useSuspenseAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers"

export const useInfiniteAdminPortfolioUsersQuery = <
  TData = InfiniteData<Types.AdminPortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminPortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminPortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioUsersQuery,
            Types.AdminPortfolioUsersQueryVariables
          >(AdminPortfolioUsersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsers.infinite", variables]

useInfiniteAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers.infinite"

export const useSuspenseInfiniteAdminPortfolioUsersQuery = <
  TData = InfiniteData<Types.AdminPortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminPortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminPortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioUsersQuery,
            Types.AdminPortfolioUsersQueryVariables
          >(AdminPortfolioUsersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsers.infinite", variables]

useSuspenseInfiniteAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers.infinite"

useAdminPortfolioUsersQuery.fetcher = (
  variables: Types.AdminPortfolioUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminPortfolioUsersQuery,
    Types.AdminPortfolioUsersQueryVariables
  >(AdminPortfolioUsersDocument, variables, options)

export const PortfolioUsersDocument = new TypedDocumentString(`
    query PortfolioUsers($portfolioPid: String!) {
  portfolio(pid: $portfolioPid) {
    pid
    title
    users {
      ...UserRef
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const usePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsers", variables],
    queryFn: publicFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

usePortfolioUsersQuery.getKey = (variables: Types.PortfolioUsersQueryVariables) => [
  "PortfolioUsers",
  variables,
]

usePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useSuspensePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsers", variables],
    queryFn: publicFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

useSuspensePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers", variables]

useSuspensePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infinite", variables]

useInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

export const useSuspenseInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infinite", variables]

useSuspenseInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

usePortfolioUsersQuery.fetcher = (
  variables: Types.PortfolioUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
    PortfolioUsersDocument,
    variables,
    options,
  )

export const OrgSiteLabelsBySlugDocument = new TypedDocumentString(`
    query OrgSiteLabelsBySlug($orgSlug: String) {
  orgLabels(slug: $orgSlug) {
    ...SiteLabelRef
  }
}
    fragment SiteLabelRef on SiteLabel {
  pid
  name
  themeColor
  isGlobal
  sites
}`)

export const useOrgSiteLabelsBySlugQuery = <
  TData = Types.OrgSiteLabelsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteLabelsBySlug"]
        : ["OrgSiteLabelsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteLabelsBySlugQuery,
      Types.OrgSiteLabelsBySlugQueryVariables
    >(OrgSiteLabelsBySlugDocument, variables),
    ...options,
  })
}

useOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteLabelsBySlug"] : ["OrgSiteLabelsBySlug", variables]

useOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug"

export const useSuspenseOrgSiteLabelsBySlugQuery = <
  TData = Types.OrgSiteLabelsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteLabelsBySlug"]
        : ["OrgSiteLabelsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteLabelsBySlugQuery,
      Types.OrgSiteLabelsBySlugQueryVariables
    >(OrgSiteLabelsBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteLabelsBySlug"] : ["OrgSiteLabelsBySlug", variables]

useSuspenseOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug"

export const useInfiniteOrgSiteLabelsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteLabelsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteLabelsBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteLabelsBySlug.infinite"]
            : ["OrgSiteLabelsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteLabelsBySlugQuery,
            Types.OrgSiteLabelsBySlugQueryVariables
          >(OrgSiteLabelsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlug.infinite"]
    : ["OrgSiteLabelsBySlug.infinite", variables]

useInfiniteOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug.infinite"

export const useSuspenseInfiniteOrgSiteLabelsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteLabelsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteLabelsBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteLabelsBySlug.infinite"]
            : ["OrgSiteLabelsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteLabelsBySlugQuery,
            Types.OrgSiteLabelsBySlugQueryVariables
          >(OrgSiteLabelsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlug.infinite"]
    : ["OrgSiteLabelsBySlug.infinite", variables]

useSuspenseInfiniteOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug.infinite"

useOrgSiteLabelsBySlugQuery.fetcher = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OrgSiteLabelsBySlugQuery,
    Types.OrgSiteLabelsBySlugQueryVariables
  >(OrgSiteLabelsBySlugDocument, variables, options)

export const OrgLabelOptionsDocument = new TypedDocumentString(`
    query OrgLabelOptions($orgSlug: String) {
  orgLabels(slug: $orgSlug) {
    pid
    name
    themeColor
  }
}
    `)

export const useOrgLabelOptionsQuery = <
  TData = Types.OrgLabelOptionsQuery,
  TError = unknown,
>(
  variables?: Types.OrgLabelOptionsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgLabelOptionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgLabelOptionsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgLabelOptionsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["OrgLabelOptions"] : ["OrgLabelOptions", variables],
    queryFn: publicFetcher<
      Types.OrgLabelOptionsQuery,
      Types.OrgLabelOptionsQueryVariables
    >(OrgLabelOptionsDocument, variables),
    ...options,
  })
}

useOrgLabelOptionsQuery.getKey = (variables?: Types.OrgLabelOptionsQueryVariables) =>
  variables === undefined ? ["OrgLabelOptions"] : ["OrgLabelOptions", variables]

useOrgLabelOptionsQuery.rootKey = "OrgLabelOptions"

export const useSuspenseOrgLabelOptionsQuery = <
  TData = Types.OrgLabelOptionsQuery,
  TError = unknown,
>(
  variables?: Types.OrgLabelOptionsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgLabelOptionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgLabelOptionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgLabelOptionsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["OrgLabelOptions"] : ["OrgLabelOptions", variables],
    queryFn: publicFetcher<
      Types.OrgLabelOptionsQuery,
      Types.OrgLabelOptionsQueryVariables
    >(OrgLabelOptionsDocument, variables),
    ...options,
  })
}

useSuspenseOrgLabelOptionsQuery.getKey = (
  variables?: Types.OrgLabelOptionsQueryVariables,
) => (variables === undefined ? ["OrgLabelOptions"] : ["OrgLabelOptions", variables])

useSuspenseOrgLabelOptionsQuery.rootKey = "OrgLabelOptions"

export const useInfiniteOrgLabelOptionsQuery = <
  TData = InfiniteData<Types.OrgLabelOptionsQuery>,
  TError = unknown,
>(
  variables: Types.OrgLabelOptionsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgLabelOptionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgLabelOptionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgLabelOptionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgLabelOptions.infinite"]
            : ["OrgLabelOptions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgLabelOptionsQuery,
            Types.OrgLabelOptionsQueryVariables
          >(OrgLabelOptionsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgLabelOptionsQuery.getKey = (
  variables?: Types.OrgLabelOptionsQueryVariables,
) =>
  variables === undefined
    ? ["OrgLabelOptions.infinite"]
    : ["OrgLabelOptions.infinite", variables]

useInfiniteOrgLabelOptionsQuery.rootKey = "OrgLabelOptions.infinite"

export const useSuspenseInfiniteOrgLabelOptionsQuery = <
  TData = InfiniteData<Types.OrgLabelOptionsQuery>,
  TError = unknown,
>(
  variables: Types.OrgLabelOptionsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgLabelOptionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgLabelOptionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgLabelOptionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgLabelOptions.infinite"]
            : ["OrgLabelOptions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgLabelOptionsQuery,
            Types.OrgLabelOptionsQueryVariables
          >(OrgLabelOptionsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgLabelOptionsQuery.getKey = (
  variables?: Types.OrgLabelOptionsQueryVariables,
) =>
  variables === undefined
    ? ["OrgLabelOptions.infinite"]
    : ["OrgLabelOptions.infinite", variables]

useSuspenseInfiniteOrgLabelOptionsQuery.rootKey = "OrgLabelOptions.infinite"

useOrgLabelOptionsQuery.fetcher = (
  variables?: Types.OrgLabelOptionsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgLabelOptionsQuery, Types.OrgLabelOptionsQueryVariables>(
    OrgLabelOptionsDocument,
    variables,
    options,
  )

export const OrgSiteDocsBySlugDocument = new TypedDocumentString(`
    query OrgSiteDocsBySlug($orgSlug: String) {
  orgSiteDocuments(slug: $orgSlug) {
    sitePid
    documents {
      ...Document
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useOrgSiteDocsBySlugQuery = <
  TData = Types.OrgSiteDocsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteDocsBySlug"]
        : ["OrgSiteDocsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteDocsBySlugQuery,
      Types.OrgSiteDocsBySlugQueryVariables
    >(OrgSiteDocsBySlugDocument, variables),
    ...options,
  })
}

useOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteDocsBySlug"] : ["OrgSiteDocsBySlug", variables]

useOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug"

export const useSuspenseOrgSiteDocsBySlugQuery = <
  TData = Types.OrgSiteDocsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteDocsBySlug"]
        : ["OrgSiteDocsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteDocsBySlugQuery,
      Types.OrgSiteDocsBySlugQueryVariables
    >(OrgSiteDocsBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteDocsBySlug"] : ["OrgSiteDocsBySlug", variables]

useSuspenseOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug"

export const useInfiniteOrgSiteDocsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteDocsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteDocsBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteDocsBySlug.infinite"]
            : ["OrgSiteDocsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteDocsBySlugQuery,
            Types.OrgSiteDocsBySlugQueryVariables
          >(OrgSiteDocsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlug.infinite"]
    : ["OrgSiteDocsBySlug.infinite", variables]

useInfiniteOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug.infinite"

export const useSuspenseInfiniteOrgSiteDocsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteDocsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteDocsBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteDocsBySlug.infinite"]
            : ["OrgSiteDocsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteDocsBySlugQuery,
            Types.OrgSiteDocsBySlugQueryVariables
          >(OrgSiteDocsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlug.infinite"]
    : ["OrgSiteDocsBySlug.infinite", variables]

useSuspenseInfiniteOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug.infinite"

useOrgSiteDocsBySlugQuery.fetcher = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteDocsBySlugQuery, Types.OrgSiteDocsBySlugQueryVariables>(
    OrgSiteDocsBySlugDocument,
    variables,
    options,
  )

export const CurrentUserDocument = new TypedDocumentString(`
    query CurrentUser {
  currentUser {
    pid
    isAuthenticated
    isAdmin
    email
    name
    avatarUrl
    meetingSchedulingUrl
    isImpersonated
    org {
      pid
      slug
      name
      logoUrl
      logomarkUrl
      irrFlavor
      inactivityTimeoutMs
    }
    orgs {
      pid
      name
      slug
      isDeveloper
      logomarkUrl
    }
  }
}
    `)

export const useCurrentUserQuery = <TData = Types.CurrentUserQuery, TError = unknown>(
  variables?: Types.CurrentUserQueryVariables,
  options?: Omit<UseQueryOptions<Types.CurrentUserQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.CurrentUserQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CurrentUserQuery, TError, TData>({
    queryKey: variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables],
    queryFn: publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
      CurrentUserDocument,
      variables,
    ),
    ...options,
  })
}

useCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables]

useCurrentUserQuery.rootKey = "CurrentUser"

export const useSuspenseCurrentUserQuery = <
  TData = Types.CurrentUserQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentUserQuery, TError, TData>({
    queryKey: variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables],
    queryFn: publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
      CurrentUserDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables]

useSuspenseCurrentUserQuery.rootKey = "CurrentUser"

export const useInfiniteCurrentUserQuery = <
  TData = InfiniteData<Types.CurrentUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUser.infinite"]
            : ["CurrentUser.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
            CurrentUserDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined
    ? ["CurrentUser.infinite"]
    : ["CurrentUser.infinite", variables]

useInfiniteCurrentUserQuery.rootKey = "CurrentUser.infinite"

export const useSuspenseInfiniteCurrentUserQuery = <
  TData = InfiniteData<Types.CurrentUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CurrentUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUser.infinite"]
            : ["CurrentUser.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
            CurrentUserDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentUserQuery.getKey = (
  variables?: Types.CurrentUserQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUser.infinite"]
    : ["CurrentUser.infinite", variables]

useSuspenseInfiniteCurrentUserQuery.rootKey = "CurrentUser.infinite"

useCurrentUserQuery.fetcher = (
  variables?: Types.CurrentUserQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
    CurrentUserDocument,
    variables,
    options,
  )

export const PropertyHubViewDocument = new TypedDocumentString(`
    query PropertyHubView($orgSlug: String!, $filterBy: OrgSiteTableFilter!, $sortBy: [OrgSiteTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    sites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const usePropertyHubViewQuery = <
  TData = Types.PropertyHubViewQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PropertyHubViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PropertyHubViewQuery, TError, TData>({
    queryKey: ["PropertyHubView", variables],
    queryFn: publicFetcher<
      Types.PropertyHubViewQuery,
      Types.PropertyHubViewQueryVariables
    >(PropertyHubViewDocument, variables),
    ...options,
  })
}

usePropertyHubViewQuery.getKey = (variables: Types.PropertyHubViewQueryVariables) => [
  "PropertyHubView",
  variables,
]

usePropertyHubViewQuery.rootKey = "PropertyHubView"

export const useSuspensePropertyHubViewQuery = <
  TData = Types.PropertyHubViewQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PropertyHubViewQuery, TError, TData>({
    queryKey: ["PropertyHubView", variables],
    queryFn: publicFetcher<
      Types.PropertyHubViewQuery,
      Types.PropertyHubViewQueryVariables
    >(PropertyHubViewDocument, variables),
    ...options,
  })
}

useSuspensePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubView", variables]

useSuspensePropertyHubViewQuery.rootKey = "PropertyHubView"

export const useInfinitePropertyHubViewQuery = <
  TData = InfiniteData<Types.PropertyHubViewQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PropertyHubViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubViewQuery,
            Types.PropertyHubViewQueryVariables
          >(PropertyHubViewDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubView.infinite", variables]

useInfinitePropertyHubViewQuery.rootKey = "PropertyHubView.infinite"

export const useSuspenseInfinitePropertyHubViewQuery = <
  TData = InfiniteData<Types.PropertyHubViewQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PropertyHubViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubViewQuery,
            Types.PropertyHubViewQueryVariables
          >(PropertyHubViewDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubView.infinite", variables]

useSuspenseInfinitePropertyHubViewQuery.rootKey = "PropertyHubView.infinite"

usePropertyHubViewQuery.fetcher = (
  variables: Types.PropertyHubViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PropertyHubViewQuery, Types.PropertyHubViewQueryVariables>(
    PropertyHubViewDocument,
    variables,
    options,
  )

export const PropertyHubRowsDocument = new TypedDocumentString(`
    query PropertyHubRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    sites {
      rows(keys: $keys) {
        pid
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        portfolioManager
        propertyFundName
        ownerName
        ownershipNotes {
          text
          createdAt
          createdBy {
            email
            name
          }
        }
        customerId
        reonomyPropertyId
        reonomyPropertyLink
        ownerNames {
          companyName
          isPrimary
          relationshipId
          companyPid
        }
        tenants
        portfolio
        status
        statusReason
        statusCustomReason
        flagSummary {
          __typename
          utilityNotFound
          tariffNotFound
          improbableEnergyCosts
          existingSolarLikely
          duplicateAddressFound
        }
        otherPortfolios
        salePlans
        salePlannedYear
        mortgageLender
        sqft
        buildingType
        buildingClass
        numStories
        numUnitsTotal
        yearBuilt
        buildingFootprintSqft
        freeSpaceSqft
        buildingFootprintRoofSqftError
        buildingSqftRoofSqftError
        buildingFootprintCount
        existingSolarDetectionScore
        inEnergyCommunity
        inLowIncomeCommunity
        inWetland
        roofCondition
        roofReplacedYr
        roofSqft
        usableRoofSqft
        metering
        loadOrigin
        utilityName
        utilityCagr
        meterCount
        loadKwh
        loadUsd
        loadUsdPerKwh
        soleTariffName
        bauCo2EmissionsTons
        bauCo2EmissionsTonsPerMwh
        ppaOfftaker
        ppaSignatory
        occupancyType
        tenantLeaseEnds
        leaseType
        tenantInterest
        energyThings {
          canRooftopSolar
          canCarportSolar
          canGroundmountSolar
          canStorage
        }
        financingThings {
          canSelfInvest
          canLoan
          canPpa
          canLease
          canCommunitySolar
        }
        noiSelfMax
        irrSelfMax
        solarLocationSelfMax
        includeStorageSelfMax
        pvSizeKwSelfMax
        storageSizeKwSelfMax
        storageSizeKwhSelfMax
        leDevFeeSelfMax
        gpvSelfMax
        lifetimeCapexSelfMax
        lifetimeUtilitySavingsSelfMax
        co2EmissionsReductionTonnesSelfMax
        noiPpaMax
        solarLocationPpaMax
        includeStoragePpaMax
        pvSizeKwPpaMax
        storageSizeKwPpaMax
        storageSizeKwhPpaMax
        leDevFeePpaMax
        gpvPpaMax
        lifetimeUtilitySavingsPpaMax
        costPerKwhPpaMax
        ppaEscPpaMax
        yearOneOfftakerSavingsPpaMax
        co2EmissionsReductionTonnesPpaMax
        noiLoanMax
        leDevFeeLoanMax
        gpvLoanMax
        noiCsMax
        noiCsP10
        noiCsFirstQuartile
        noiCsMedian
        noiCsThirdQuartile
        noiCsP90
        pvSizeKwCsMax
        roofMaxPvKw
        groundMaxPvKw
        carportMaxPvKw
        marketplaceStatus
        triageStatus
      }
    }
  }
}
    `)

export const usePropertyHubRowsQuery = <
  TData = Types.PropertyHubRowsQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PropertyHubRowsQuery, TError, TData>({
    queryKey: ["PropertyHubRows", variables],
    queryFn: publicFetcher<
      Types.PropertyHubRowsQuery,
      Types.PropertyHubRowsQueryVariables
    >(PropertyHubRowsDocument, variables),
    ...options,
  })
}

usePropertyHubRowsQuery.getKey = (variables: Types.PropertyHubRowsQueryVariables) => [
  "PropertyHubRows",
  variables,
]

usePropertyHubRowsQuery.rootKey = "PropertyHubRows"

export const useSuspensePropertyHubRowsQuery = <
  TData = Types.PropertyHubRowsQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PropertyHubRowsQuery, TError, TData>({
    queryKey: ["PropertyHubRows", variables],
    queryFn: publicFetcher<
      Types.PropertyHubRowsQuery,
      Types.PropertyHubRowsQueryVariables
    >(PropertyHubRowsDocument, variables),
    ...options,
  })
}

useSuspensePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRows", variables]

useSuspensePropertyHubRowsQuery.rootKey = "PropertyHubRows"

export const useInfinitePropertyHubRowsQuery = <
  TData = InfiniteData<Types.PropertyHubRowsQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PropertyHubRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubRowsQuery,
            Types.PropertyHubRowsQueryVariables
          >(PropertyHubRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRows.infinite", variables]

useInfinitePropertyHubRowsQuery.rootKey = "PropertyHubRows.infinite"

export const useSuspenseInfinitePropertyHubRowsQuery = <
  TData = InfiniteData<Types.PropertyHubRowsQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PropertyHubRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubRowsQuery,
            Types.PropertyHubRowsQueryVariables
          >(PropertyHubRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRows.infinite", variables]

useSuspenseInfinitePropertyHubRowsQuery.rootKey = "PropertyHubRows.infinite"

usePropertyHubRowsQuery.fetcher = (
  variables: Types.PropertyHubRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PropertyHubRowsQuery, Types.PropertyHubRowsQueryVariables>(
    PropertyHubRowsDocument,
    variables,
    options,
  )

export const OrgPropertySearchDocument = new TypedDocumentString(`
    query OrgPropertySearch($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    slug
    siteSearchTerms {
      propertyName
      address
      pid
    }
  }
}
    `)

export const useOrgPropertySearchQuery = <
  TData = Types.OrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgPropertySearchQuery, TError, TData>({
    queryKey: ["OrgPropertySearch", variables],
    queryFn: publicFetcher<
      Types.OrgPropertySearchQuery,
      Types.OrgPropertySearchQueryVariables
    >(OrgPropertySearchDocument, variables),
    ...options,
  })
}

useOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch", variables]

useOrgPropertySearchQuery.rootKey = "OrgPropertySearch"

export const useSuspenseOrgPropertySearchQuery = <
  TData = Types.OrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgPropertySearchQuery, TError, TData>({
    queryKey: ["OrgPropertySearch", variables],
    queryFn: publicFetcher<
      Types.OrgPropertySearchQuery,
      Types.OrgPropertySearchQueryVariables
    >(OrgPropertySearchDocument, variables),
    ...options,
  })
}

useSuspenseOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch", variables]

useSuspenseOrgPropertySearchQuery.rootKey = "OrgPropertySearch"

export const useInfiniteOrgPropertySearchQuery = <
  TData = InfiniteData<Types.OrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgPropertySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPropertySearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgPropertySearchQuery,
            Types.OrgPropertySearchQueryVariables
          >(OrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch.infinite", variables]

useInfiniteOrgPropertySearchQuery.rootKey = "OrgPropertySearch.infinite"

export const useSuspenseInfiniteOrgPropertySearchQuery = <
  TData = InfiniteData<Types.OrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgPropertySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPropertySearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgPropertySearchQuery,
            Types.OrgPropertySearchQueryVariables
          >(OrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch.infinite", variables]

useSuspenseInfiniteOrgPropertySearchQuery.rootKey = "OrgPropertySearch.infinite"

useOrgPropertySearchQuery.fetcher = (
  variables: Types.OrgPropertySearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgPropertySearchQuery, Types.OrgPropertySearchQueryVariables>(
    OrgPropertySearchDocument,
    variables,
    options,
  )

export const OrgHomepageDocument = new TypedDocumentString(`
    query OrgHomepage($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    logoUrl
    mapView {
      pid
      lat
      lon
    }
    sites {
      view(filterBy: { status: { value: ACTIVE } }) {
        count
        stats {
          viableCount
          leaseCount
        }
      }
    }
  }
}
    `)

export const useOrgHomepageQuery = <TData = Types.OrgHomepageQuery, TError = unknown>(
  variables: Types.OrgHomepageQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgHomepageQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgHomepageQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgHomepageQuery, TError, TData>({
    queryKey: ["OrgHomepage", variables],
    queryFn: publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
      OrgHomepageDocument,
      variables,
    ),
    ...options,
  })
}

useOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepage",
  variables,
]

useOrgHomepageQuery.rootKey = "OrgHomepage"

export const useSuspenseOrgHomepageQuery = <
  TData = Types.OrgHomepageQuery,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgHomepageQuery, TError, TData>({
    queryKey: ["OrgHomepage", variables],
    queryFn: publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
      OrgHomepageDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepage",
  variables,
]

useSuspenseOrgHomepageQuery.rootKey = "OrgHomepage"

export const useInfiniteOrgHomepageQuery = <
  TData = InfiniteData<Types.OrgHomepageQuery>,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgHomepageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgHomepage.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
            OrgHomepageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepage.infinite",
  variables,
]

useInfiniteOrgHomepageQuery.rootKey = "OrgHomepage.infinite"

export const useSuspenseInfiniteOrgHomepageQuery = <
  TData = InfiniteData<Types.OrgHomepageQuery>,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgHomepageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgHomepage.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
            OrgHomepageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgHomepageQuery.getKey = (
  variables: Types.OrgHomepageQueryVariables,
) => ["OrgHomepage.infinite", variables]

useSuspenseInfiniteOrgHomepageQuery.rootKey = "OrgHomepage.infinite"

useOrgHomepageQuery.fetcher = (
  variables: Types.OrgHomepageQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
    OrgHomepageDocument,
    variables,
    options,
  )

export const OrgFacetsDocument = new TypedDocumentString(`
    query OrgFacets($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    sites {
      view {
        stats {
          portfolios
          coveredStates
          buildingClasses
          buildingTypes
          counties
          cbsaNames
          assetManagers
          portfolioManagers
          propertyFundNames
          utilityNames
          ownerNames
        }
      }
    }
    finproducts {
      id
      type
      name
    }
  }
}
    `)

export const useOrgFacetsQuery = <TData = Types.OrgFacetsQuery, TError = unknown>(
  variables: Types.OrgFacetsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgFacetsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgFacetsQuery, TError, TData>({
    queryKey: ["OrgFacets", variables],
    queryFn: publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
      OrgFacetsDocument,
      variables,
    ),
    ...options,
  })
}

useOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacets",
  variables,
]

useOrgFacetsQuery.rootKey = "OrgFacets"

export const useSuspenseOrgFacetsQuery = <
  TData = Types.OrgFacetsQuery,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgFacetsQuery, TError, TData>({
    queryKey: ["OrgFacets", variables],
    queryFn: publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
      OrgFacetsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacets",
  variables,
]

useSuspenseOrgFacetsQuery.rootKey = "OrgFacets"

export const useInfiniteOrgFacetsQuery = <
  TData = InfiniteData<Types.OrgFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
            OrgFacetsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacets.infinite",
  variables,
]

useInfiniteOrgFacetsQuery.rootKey = "OrgFacets.infinite"

export const useSuspenseInfiniteOrgFacetsQuery = <
  TData = InfiniteData<Types.OrgFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
            OrgFacetsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgFacetsQuery.getKey = (
  variables: Types.OrgFacetsQueryVariables,
) => ["OrgFacets.infinite", variables]

useSuspenseInfiniteOrgFacetsQuery.rootKey = "OrgFacets.infinite"

useOrgFacetsQuery.fetcher = (
  variables: Types.OrgFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
    OrgFacetsDocument,
    variables,
    options,
  )

export const OrgSiteTableDocument = new TypedDocumentString(`
    query OrgSiteTable($orgSlug: String!, $filterBy: OrgSiteTableFilter, $sortBy: [OrgSiteTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    name
    sites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          annualNoi
        }
      }
    }
  }
}
    `)

export const useOrgSiteTableQuery = <TData = Types.OrgSiteTableQuery, TError = unknown>(
  variables: Types.OrgSiteTableQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteTableQuery, TError, TData>({
    queryKey: ["OrgSiteTable", variables],
    queryFn: publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
      OrgSiteTableDocument,
      variables,
    ),
    ...options,
  })
}

useOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTable",
  variables,
]

useOrgSiteTableQuery.rootKey = "OrgSiteTable"

export const useSuspenseOrgSiteTableQuery = <
  TData = Types.OrgSiteTableQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteTableQuery, TError, TData>({
    queryKey: ["OrgSiteTable", variables],
    queryFn: publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
      OrgSiteTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTable",
  variables,
]

useSuspenseOrgSiteTableQuery.rootKey = "OrgSiteTable"

export const useInfiniteOrgSiteTableQuery = <
  TData = InfiniteData<Types.OrgSiteTableQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
            OrgSiteTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTable.infinite",
  variables,
]

useInfiniteOrgSiteTableQuery.rootKey = "OrgSiteTable.infinite"

export const useSuspenseInfiniteOrgSiteTableQuery = <
  TData = InfiniteData<Types.OrgSiteTableQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
            OrgSiteTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteTableQuery.getKey = (
  variables: Types.OrgSiteTableQueryVariables,
) => ["OrgSiteTable.infinite", variables]

useSuspenseInfiniteOrgSiteTableQuery.rootKey = "OrgSiteTable.infinite"

useOrgSiteTableQuery.fetcher = (
  variables: Types.OrgSiteTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
    OrgSiteTableDocument,
    variables,
    options,
  )

export const PortfolioOpportunityPdfDocument = new TypedDocumentString(`
    query PortfolioOpportunityPdf($orgSlug: String!, $includeBtm: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    name
    sites {
      all: view(filterBy: { status: { value: ACTIVE } }, sortBy: []) {
        count
      }
      opportunities: view(
        filterBy: { status: { value: ACTIVE }, noiCsMax: { gte: 0.01 } }
        sortBy: [{ attr: NOI_CS_MAX, order: DESC }]
      ) {
        keys
        stats {
          csAnnualNoiP10
          csAnnualNoiP90
        }
      }
      cs: view(
        filterBy: { status: { value: ACTIVE }, noiCsMax: { gte: 50000 } }
        sortBy: []
      ) {
        count
        stats {
          csAnnualNoi
          csMaxPvSizeKw
        }
      }
      battery: view(
        filterBy: { status: { value: ACTIVE }, batteryAnnualRentUsd: { gte: 50000 } }
        sortBy: []
      ) @include(if: $includeBtm) {
        count
        stats {
          batteryAnnualRentUsd
          batteryCapacityMwh
        }
      }
      ppa: view(
        filterBy: { status: { value: ACTIVE }, noiPpaMax: { gte: 50000 } }
        sortBy: []
      ) @include(if: $includeBtm) {
        count
        stats {
          ppaAnnualNoi
          ppaMaxPvSizeKw
          ppaCo2EmissionsReductionTonnes
        }
      }
      cash: view(
        filterBy: { status: { value: ACTIVE }, irrSelfMax: { gte: 0.15 } }
        sortBy: []
      ) @include(if: $includeBtm) {
        count
        stats {
          cashAnnualNoi
          cashMaxPvSizeKw
          cashCo2EmissionsReductionTonnes
        }
      }
    }
  }
}
    `)

export const usePortfolioOpportunityPdfQuery = <
  TData = Types.PortfolioOpportunityPdfQuery,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPdfQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioOpportunityPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PortfolioOpportunityPdfQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioOpportunityPdfQuery, TError, TData>({
    queryKey: ["PortfolioOpportunityPdf", variables],
    queryFn: publicFetcher<
      Types.PortfolioOpportunityPdfQuery,
      Types.PortfolioOpportunityPdfQueryVariables
    >(PortfolioOpportunityPdfDocument, variables),
    ...options,
  })
}

usePortfolioOpportunityPdfQuery.getKey = (
  variables: Types.PortfolioOpportunityPdfQueryVariables,
) => ["PortfolioOpportunityPdf", variables]

usePortfolioOpportunityPdfQuery.rootKey = "PortfolioOpportunityPdf"

export const useSuspensePortfolioOpportunityPdfQuery = <
  TData = Types.PortfolioOpportunityPdfQuery,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPdfQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioOpportunityPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioOpportunityPdfQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioOpportunityPdfQuery, TError, TData>({
    queryKey: ["PortfolioOpportunityPdf", variables],
    queryFn: publicFetcher<
      Types.PortfolioOpportunityPdfQuery,
      Types.PortfolioOpportunityPdfQueryVariables
    >(PortfolioOpportunityPdfDocument, variables),
    ...options,
  })
}

useSuspensePortfolioOpportunityPdfQuery.getKey = (
  variables: Types.PortfolioOpportunityPdfQueryVariables,
) => ["PortfolioOpportunityPdf", variables]

useSuspensePortfolioOpportunityPdfQuery.rootKey = "PortfolioOpportunityPdf"

export const useInfinitePortfolioOpportunityPdfQuery = <
  TData = InfiniteData<Types.PortfolioOpportunityPdfQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPdfQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioOpportunityPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioOpportunityPdfQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioOpportunityPdfQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioOpportunityPdf.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioOpportunityPdfQuery,
            Types.PortfolioOpportunityPdfQueryVariables
          >(PortfolioOpportunityPdfDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioOpportunityPdfQuery.getKey = (
  variables: Types.PortfolioOpportunityPdfQueryVariables,
) => ["PortfolioOpportunityPdf.infinite", variables]

useInfinitePortfolioOpportunityPdfQuery.rootKey = "PortfolioOpportunityPdf.infinite"

export const useSuspenseInfinitePortfolioOpportunityPdfQuery = <
  TData = InfiniteData<Types.PortfolioOpportunityPdfQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPdfQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioOpportunityPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioOpportunityPdfQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioOpportunityPdfQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioOpportunityPdf.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioOpportunityPdfQuery,
            Types.PortfolioOpportunityPdfQueryVariables
          >(PortfolioOpportunityPdfDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioOpportunityPdfQuery.getKey = (
  variables: Types.PortfolioOpportunityPdfQueryVariables,
) => ["PortfolioOpportunityPdf.infinite", variables]

useSuspenseInfinitePortfolioOpportunityPdfQuery.rootKey =
  "PortfolioOpportunityPdf.infinite"

usePortfolioOpportunityPdfQuery.fetcher = (
  variables: Types.PortfolioOpportunityPdfQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PortfolioOpportunityPdfQuery,
    Types.PortfolioOpportunityPdfQueryVariables
  >(PortfolioOpportunityPdfDocument, variables, options)

export const PortfolioOpportunityPropertyDocument = new TypedDocumentString(`
    query PortfolioOpportunityProperty($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      pid
      name
      streetAddress
      city
      state
      roofSqft
      buildingType
      activeResults(financingType: CS) {
        financing {
          status
        }
        result {
          __typename
          ... on RevampCS {
            buildingOwner {
              noiAnnual
              noiYearOne
            }
            leaseProjections {
              systemSizeKw
              annualRentLowerRangeUsd
              annualRentMedianUsd
              annualRentUpperRangeUsd
              annualRentP10Usd
              annualRentP90Usd
              avgAwardedAnnualRentUsd
            }
          }
        }
      }
    }
  }
}
    `)

export const usePortfolioOpportunityPropertyQuery = <
  TData = Types.PortfolioOpportunityPropertyQuery,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioOpportunityPropertyQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PortfolioOpportunityPropertyQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioOpportunityPropertyQuery, TError, TData>({
    queryKey: ["PortfolioOpportunityProperty", variables],
    queryFn: publicFetcher<
      Types.PortfolioOpportunityPropertyQuery,
      Types.PortfolioOpportunityPropertyQueryVariables
    >(PortfolioOpportunityPropertyDocument, variables),
    ...options,
  })
}

usePortfolioOpportunityPropertyQuery.getKey = (
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
) => ["PortfolioOpportunityProperty", variables]

usePortfolioOpportunityPropertyQuery.rootKey = "PortfolioOpportunityProperty"

export const useSuspensePortfolioOpportunityPropertyQuery = <
  TData = Types.PortfolioOpportunityPropertyQuery,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioOpportunityPropertyQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioOpportunityPropertyQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioOpportunityPropertyQuery, TError, TData>({
    queryKey: ["PortfolioOpportunityProperty", variables],
    queryFn: publicFetcher<
      Types.PortfolioOpportunityPropertyQuery,
      Types.PortfolioOpportunityPropertyQueryVariables
    >(PortfolioOpportunityPropertyDocument, variables),
    ...options,
  })
}

useSuspensePortfolioOpportunityPropertyQuery.getKey = (
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
) => ["PortfolioOpportunityProperty", variables]

useSuspensePortfolioOpportunityPropertyQuery.rootKey = "PortfolioOpportunityProperty"

export const useInfinitePortfolioOpportunityPropertyQuery = <
  TData = InfiniteData<Types.PortfolioOpportunityPropertyQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioOpportunityPropertyQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioOpportunityPropertyQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioOpportunityPropertyQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "PortfolioOpportunityProperty.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioOpportunityPropertyQuery,
            Types.PortfolioOpportunityPropertyQueryVariables
          >(PortfolioOpportunityPropertyDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioOpportunityPropertyQuery.getKey = (
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
) => ["PortfolioOpportunityProperty.infinite", variables]

useInfinitePortfolioOpportunityPropertyQuery.rootKey =
  "PortfolioOpportunityProperty.infinite"

export const useSuspenseInfinitePortfolioOpportunityPropertyQuery = <
  TData = InfiniteData<Types.PortfolioOpportunityPropertyQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.PortfolioOpportunityPropertyQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioOpportunityPropertyQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.PortfolioOpportunityPropertyQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "PortfolioOpportunityProperty.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioOpportunityPropertyQuery,
            Types.PortfolioOpportunityPropertyQueryVariables
          >(PortfolioOpportunityPropertyDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioOpportunityPropertyQuery.getKey = (
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
) => ["PortfolioOpportunityProperty.infinite", variables]

useSuspenseInfinitePortfolioOpportunityPropertyQuery.rootKey =
  "PortfolioOpportunityProperty.infinite"

usePortfolioOpportunityPropertyQuery.fetcher = (
  variables: Types.PortfolioOpportunityPropertyQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PortfolioOpportunityPropertyQuery,
    Types.PortfolioOpportunityPropertyQueryVariables
  >(PortfolioOpportunityPropertyDocument, variables, options)

export const PortfolioPdfReportStatsDocument = new TypedDocumentString(`
    query PortfolioPdfReportStats($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    mapView {
      pid
      lat
      lon
    }
    sites {
      view(
        filterBy: { status: { value: ACTIVE } }
        sortBy: [{ attr: NOI_CS_MAX, order: DESC }]
      ) {
        count
        stats {
          annualNoi
          co2EmissionsReductionTonnes
        }
      }
      highlight: view(
        filterBy: {
          status: { value: ACTIVE }
          irrSelfMax: { gte: 0.1 }
          noiPpaMax: { gte: 0 }
          noiCsMax: { gte: 0 }
        }
        sortBy: [{ attr: NOI_CS_MAX, order: DESC }]
      ) {
        keys
      }
      cs: view(
        filterBy: { status: { value: ACTIVE }, noiCsMax: { gte: 20000 } }
        sortBy: []
      ) {
        count
        stats {
          csAnnualNoi
          csMaxPvSizeKw
        }
      }
      csAfter: view(
        filterBy: { status: { value: ACTIVE }, noiCsMax: { gte: 33333 } }
        sortBy: []
      ) {
        count
        stats {
          csAnnualNoi
          csMaxPvSizeKw
        }
      }
      ppa: view(
        filterBy: { status: { value: ACTIVE }, noiPpaMax: { gte: 0 } }
        sortBy: []
      ) {
        count
        stats {
          ppaAnnualNoi
          ppaMaxPvSizeKw
          ppaCo2EmissionsReductionTonnes
        }
      }
      cash: view(
        filterBy: { status: { value: ACTIVE }, irrSelfMax: { gte: 0.1 } }
        sortBy: []
      ) {
        count
        stats {
          cashAnnualNoi
          cashMaxPvSizeKw
          cashCo2EmissionsReductionTonnes
        }
      }
    }
    bestCsSite {
      streetAddress
      state
      noiCsMax
    }
  }
}
    `)

export const usePortfolioPdfReportStatsQuery = <
  TData = Types.PortfolioPdfReportStatsQuery,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>({
    queryKey: ["PortfolioPdfReportStats", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfReportStatsQuery,
      Types.PortfolioPdfReportStatsQueryVariables
    >(PortfolioPdfReportStatsDocument, variables),
    ...options,
  })
}

usePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats", variables]

usePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats"

export const useSuspensePortfolioPdfReportStatsQuery = <
  TData = Types.PortfolioPdfReportStatsQuery,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>({
    queryKey: ["PortfolioPdfReportStats", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfReportStatsQuery,
      Types.PortfolioPdfReportStatsQueryVariables
    >(PortfolioPdfReportStatsDocument, variables),
    ...options,
  })
}

useSuspensePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats", variables]

useSuspensePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats"

export const useInfinitePortfolioPdfReportStatsQuery = <
  TData = InfiniteData<Types.PortfolioPdfReportStatsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioPdfReportStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfReportStatsQuery,
            Types.PortfolioPdfReportStatsQueryVariables
          >(PortfolioPdfReportStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats.infinite", variables]

useInfinitePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats.infinite"

export const useSuspenseInfinitePortfolioPdfReportStatsQuery = <
  TData = InfiniteData<Types.PortfolioPdfReportStatsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioPdfReportStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfReportStatsQuery,
            Types.PortfolioPdfReportStatsQueryVariables
          >(PortfolioPdfReportStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats.infinite", variables]

useSuspenseInfinitePortfolioPdfReportStatsQuery.rootKey =
  "PortfolioPdfReportStats.infinite"

usePortfolioPdfReportStatsQuery.fetcher = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PortfolioPdfReportStatsQuery,
    Types.PortfolioPdfReportStatsQueryVariables
  >(PortfolioPdfReportStatsDocument, variables, options)

export const OrgSiteTableRowsDocument = new TypedDocumentString(`
    query OrgSiteTableRows($orgSlug: String!, $sitePids: [String!]!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    sites {
      rows(keys: $sitePids) {
        pid
        name
        portfolio
        assetManager
        portfolioManager
        streetAddress
        city
        state
        zipCode
        noiRangeMin
        noiRangeMax
        latitude
        longitude
        irrSelfMax
        noiLoanMax
        noiPpaMax
        noiCsMax
        roofMaxPvKw
        energyThings {
          canRooftopSolar
          canCarportSolar
          canGroundmountSolar
          canStorage
        }
        financingThings {
          canSelfInvest
          canLoan
          canPpa
          canLease
          canCommunitySolar
        }
        footprint {
          latitude
          longitude
          roofShape
          roofShapeGeojson
        }
      }
    }
  }
}
    `)

export const useOrgSiteTableRowsQuery = <
  TData = Types.OrgSiteTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteTableRowsQuery, TError, TData>({
    queryKey: ["OrgSiteTableRows", variables],
    queryFn: publicFetcher<
      Types.OrgSiteTableRowsQuery,
      Types.OrgSiteTableRowsQueryVariables
    >(OrgSiteTableRowsDocument, variables),
    ...options,
  })
}

useOrgSiteTableRowsQuery.getKey = (variables: Types.OrgSiteTableRowsQueryVariables) => [
  "OrgSiteTableRows",
  variables,
]

useOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows"

export const useSuspenseOrgSiteTableRowsQuery = <
  TData = Types.OrgSiteTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteTableRowsQuery, TError, TData>({
    queryKey: ["OrgSiteTableRows", variables],
    queryFn: publicFetcher<
      Types.OrgSiteTableRowsQuery,
      Types.OrgSiteTableRowsQueryVariables
    >(OrgSiteTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRows", variables]

useSuspenseOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows"

export const useInfiniteOrgSiteTableRowsQuery = <
  TData = InfiniteData<Types.OrgSiteTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteTableRowsQuery,
            Types.OrgSiteTableRowsQueryVariables
          >(OrgSiteTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRows.infinite", variables]

useInfiniteOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows.infinite"

export const useSuspenseInfiniteOrgSiteTableRowsQuery = <
  TData = InfiniteData<Types.OrgSiteTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteTableRowsQuery,
            Types.OrgSiteTableRowsQueryVariables
          >(OrgSiteTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRows.infinite", variables]

useSuspenseInfiniteOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows.infinite"

useOrgSiteTableRowsQuery.fetcher = (
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteTableRowsQuery, Types.OrgSiteTableRowsQueryVariables>(
    OrgSiteTableRowsDocument,
    variables,
    options,
  )

export const RevampSiteDocument = new TypedDocumentString(`
    query RevampSite($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    site(pid: $sitePid) {
      ...RevampSiteBasics
      batteryMarketAvailable
      batteryGroundLease {
        ...BatteryGroundLeaseFields
      }
      flags {
        type
      }
      duplicateSites {
        pid
        displayName
      }
      labels {
        name
        pid
        themeColor
      }
      highlightedResults {
        ...ResultSummary
      }
      activeResults {
        isPinned
        ...Result
      }
      resultPerLocation {
        ...ResultRef
        helioscope {
          ...Helioscope
        }
      }
    }
  }
}
    fragment BatteryGroundLeaseFields on BatteryGroundLease {
  annualRentUsd
  ratePerKwhPerYear
  energyKwh
  leasedSqft
  termYears
  escalator
  projections {
    __typename
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment ResultRef on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
}
fragment Result on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
fragment RevampFinmodelSummary on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  offerStatus
  fmvStr
  goal
  isVnem
  isGreenLease
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      totalUtilitySavings
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanTermYears
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    termYears
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  systems {
    ...RevampSystem
  }
}
fragment ResultSummary on Result {
  id
  pid
  isPinned
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodelSummary
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`)

export const useRevampSiteQuery = <TData = Types.RevampSiteQuery, TError = unknown>(
  variables: Types.RevampSiteQueryVariables,
  options?: Omit<UseQueryOptions<Types.RevampSiteQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteQuery, TError, TData>({
    queryKey: ["RevampSite", variables],
    queryFn: publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
      RevampSiteDocument,
      variables,
    ),
    ...options,
  })
}

useRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSite",
  variables,
]

useRevampSiteQuery.rootKey = "RevampSite"

export const useSuspenseRevampSiteQuery = <
  TData = Types.RevampSiteQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteQuery, TError, TData>({
    queryKey: ["RevampSite", variables],
    queryFn: publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
      RevampSiteDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSite",
  variables,
]

useSuspenseRevampSiteQuery.rootKey = "RevampSite"

export const useInfiniteRevampSiteQuery = <
  TData = InfiniteData<Types.RevampSiteQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSite.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
            RevampSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSite.infinite",
  variables,
]

useInfiniteRevampSiteQuery.rootKey = "RevampSite.infinite"

export const useSuspenseInfiniteRevampSiteQuery = <
  TData = InfiniteData<Types.RevampSiteQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSite.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
            RevampSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteQuery.getKey = (
  variables: Types.RevampSiteQueryVariables,
) => ["RevampSite.infinite", variables]

useSuspenseInfiniteRevampSiteQuery.rootKey = "RevampSite.infinite"

useRevampSiteQuery.fetcher = (
  variables: Types.RevampSiteQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
    RevampSiteDocument,
    variables,
    options,
  )

export const ResultDetailDocument = new TypedDocumentString(`
    query ResultDetail($orgSlug: String!, $sitePid: String!, $resultPid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    name
    site(pid: $sitePid, includeResults: false) {
      pid
      name
      roofMaxPvKw
      footprint {
        ...Footprint
      }
      result(pid: $resultPid) {
        ...Result
      }
    }
  }
}
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment Result on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`)

export const useResultDetailQuery = <TData = Types.ResultDetailQuery, TError = unknown>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetail", variables],
    queryFn: publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail",
  variables,
]

useResultDetailQuery.rootKey = "ResultDetail"

export const useSuspenseResultDetailQuery = <
  TData = Types.ResultDetailQuery,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetail", variables],
    queryFn: publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail",
  variables,
]

useSuspenseResultDetailQuery.rootKey = "ResultDetail"

export const useInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail.infinite",
  variables,
]

useInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

export const useSuspenseInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultDetailQuery.getKey = (
  variables: Types.ResultDetailQueryVariables,
) => ["ResultDetail.infinite", variables]

useSuspenseInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

useResultDetailQuery.fetcher = (
  variables: Types.ResultDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
    ResultDetailDocument,
    variables,
    options,
  )

export const ResultCashFlowDocument = new TypedDocumentString(`
    query ResultCashFlow($orgSlug: String!, $sitePid: String!, $resultPid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid, includeResults: false) {
      result(pid: $resultPid) {
        result {
          __typename
          ... on RevampFinmodel {
            buildingOwnerXlsx
          }
        }
      }
    }
  }
}
    `)

export const useResultCashFlowQuery = <
  TData = Types.ResultCashFlowQuery,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultCashFlowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultCashFlowQuery, TError, TData>({
    queryKey: ["ResultCashFlow", variables],
    queryFn: publicFetcher<
      Types.ResultCashFlowQuery,
      Types.ResultCashFlowQueryVariables
    >(ResultCashFlowDocument, variables),
    ...options,
  })
}

useResultCashFlowQuery.getKey = (variables: Types.ResultCashFlowQueryVariables) => [
  "ResultCashFlow",
  variables,
]

useResultCashFlowQuery.rootKey = "ResultCashFlow"

export const useSuspenseResultCashFlowQuery = <
  TData = Types.ResultCashFlowQuery,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultCashFlowQuery, TError, TData>({
    queryKey: ["ResultCashFlow", variables],
    queryFn: publicFetcher<
      Types.ResultCashFlowQuery,
      Types.ResultCashFlowQueryVariables
    >(ResultCashFlowDocument, variables),
    ...options,
  })
}

useSuspenseResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlow", variables]

useSuspenseResultCashFlowQuery.rootKey = "ResultCashFlow"

export const useInfiniteResultCashFlowQuery = <
  TData = InfiniteData<Types.ResultCashFlowQuery>,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultCashFlowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultCashFlow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
            ResultCashFlowDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlow.infinite", variables]

useInfiniteResultCashFlowQuery.rootKey = "ResultCashFlow.infinite"

export const useSuspenseInfiniteResultCashFlowQuery = <
  TData = InfiniteData<Types.ResultCashFlowQuery>,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultCashFlowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultCashFlow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
            ResultCashFlowDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlow.infinite", variables]

useSuspenseInfiniteResultCashFlowQuery.rootKey = "ResultCashFlow.infinite"

useResultCashFlowQuery.fetcher = (
  variables: Types.ResultCashFlowQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
    ResultCashFlowDocument,
    variables,
    options,
  )

export const RevampSiteDocumentsDocument = new TypedDocumentString(`
    query RevampSiteDocuments($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    site(pid: $sitePid) {
      pid
      documents {
        canDelete
        contentType
        dateCreated
        fileName
        ownerName
        pid
        tag
        downloadHref
      }
    }
  }
}
    `)

export const useRevampSiteDocumentsQuery = <
  TData = Types.RevampSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteDocumentsQuery, TError, TData>({
    queryKey: ["RevampSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.RevampSiteDocumentsQuery,
      Types.RevampSiteDocumentsQueryVariables
    >(RevampSiteDocumentsDocument, variables),
    ...options,
  })
}

useRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments", variables]

useRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments"

export const useSuspenseRevampSiteDocumentsQuery = <
  TData = Types.RevampSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteDocumentsQuery, TError, TData>({
    queryKey: ["RevampSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.RevampSiteDocumentsQuery,
      Types.RevampSiteDocumentsQueryVariables
    >(RevampSiteDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments", variables]

useSuspenseRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments"

export const useInfiniteRevampSiteDocumentsQuery = <
  TData = InfiniteData<Types.RevampSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteDocumentsQuery,
            Types.RevampSiteDocumentsQueryVariables
          >(RevampSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments.infinite", variables]

useInfiniteRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments.infinite"

export const useSuspenseInfiniteRevampSiteDocumentsQuery = <
  TData = InfiniteData<Types.RevampSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteDocumentsQuery,
            Types.RevampSiteDocumentsQueryVariables
          >(RevampSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments.infinite", variables]

useSuspenseInfiniteRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments.infinite"

useRevampSiteDocumentsQuery.fetcher = (
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteDocumentsQuery,
    Types.RevampSiteDocumentsQueryVariables
  >(RevampSiteDocumentsDocument, variables, options)

export const SiteProjectDocumentsDocument = new TypedDocumentString(`
    query SiteProjectDocuments($sitePid: String!, $orgSlug: String!) {
  projects {
    siteProjects(sitePid: $sitePid) {
      pid
      name
      finproductType
      projectSpecification {
        ppa {
          system {
            totalPvSizeKw
          }
        }
        cs {
          systemLite {
            totalPvSizeKw
          }
        }
      }
      documents(orgSlug: $orgSlug) {
        canDelete
        contentType
        dateCreated
        fileName
        ownerName
        pid
        tag
        downloadHref
      }
    }
  }
}
    `)

export const useSiteProjectDocumentsQuery = <
  TData = Types.SiteProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteProjectDocumentsQuery, TError, TData>({
    queryKey: ["SiteProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.SiteProjectDocumentsQuery,
      Types.SiteProjectDocumentsQueryVariables
    >(SiteProjectDocumentsDocument, variables),
    ...options,
  })
}

useSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocuments", variables]

useSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments"

export const useSuspenseSiteProjectDocumentsQuery = <
  TData = Types.SiteProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteProjectDocumentsQuery, TError, TData>({
    queryKey: ["SiteProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.SiteProjectDocumentsQuery,
      Types.SiteProjectDocumentsQueryVariables
    >(SiteProjectDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocuments", variables]

useSuspenseSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments"

export const useInfiniteSiteProjectDocumentsQuery = <
  TData = InfiniteData<Types.SiteProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteProjectDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteProjectDocumentsQuery,
            Types.SiteProjectDocumentsQueryVariables
          >(SiteProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocuments.infinite", variables]

useInfiniteSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments.infinite"

export const useSuspenseInfiniteSiteProjectDocumentsQuery = <
  TData = InfiniteData<Types.SiteProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteProjectDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteProjectDocumentsQuery,
            Types.SiteProjectDocumentsQueryVariables
          >(SiteProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocuments.infinite", variables]

useSuspenseInfiniteSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments.infinite"

useSiteProjectDocumentsQuery.fetcher = (
  variables: Types.SiteProjectDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteProjectDocumentsQuery,
    Types.SiteProjectDocumentsQueryVariables
  >(SiteProjectDocumentsDocument, variables, options)

export const RevampSiteBelowTheFoldDocument = new TypedDocumentString(`
    query RevampSiteBelowTheFold($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    name
    pid
    site(pid: $sitePid) {
      pid
      recommendedResult {
        ...Result
      }
      policies {
        ...PolicyBrief
      }
    }
  }
}
    fragment PolicyBrief on Policy {
  pid
  name
  publicName
  description
  publicDescription
  sources {
    text
    url
  }
  userVisible
  application {
    status
  }
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment Result on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`)

export const useRevampSiteBelowTheFoldQuery = <
  TData = Types.RevampSiteBelowTheFoldQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>({
    queryKey: ["RevampSiteBelowTheFold", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBelowTheFoldQuery,
      Types.RevampSiteBelowTheFoldQueryVariables
    >(RevampSiteBelowTheFoldDocument, variables),
    ...options,
  })
}

useRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold", variables]

useRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold"

export const useSuspenseRevampSiteBelowTheFoldQuery = <
  TData = Types.RevampSiteBelowTheFoldQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>({
    queryKey: ["RevampSiteBelowTheFold", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBelowTheFoldQuery,
      Types.RevampSiteBelowTheFoldQueryVariables
    >(RevampSiteBelowTheFoldDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold", variables]

useSuspenseRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold"

export const useInfiniteRevampSiteBelowTheFoldQuery = <
  TData = InfiniteData<Types.RevampSiteBelowTheFoldQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBelowTheFold.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBelowTheFoldQuery,
            Types.RevampSiteBelowTheFoldQueryVariables
          >(RevampSiteBelowTheFoldDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold.infinite", variables]

useInfiniteRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold.infinite"

export const useSuspenseInfiniteRevampSiteBelowTheFoldQuery = <
  TData = InfiniteData<Types.RevampSiteBelowTheFoldQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBelowTheFold.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBelowTheFoldQuery,
            Types.RevampSiteBelowTheFoldQueryVariables
          >(RevampSiteBelowTheFoldDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold.infinite", variables]

useSuspenseInfiniteRevampSiteBelowTheFoldQuery.rootKey =
  "RevampSiteBelowTheFold.infinite"

useRevampSiteBelowTheFoldQuery.fetcher = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteBelowTheFoldQuery,
    Types.RevampSiteBelowTheFoldQueryVariables
  >(RevampSiteBelowTheFoldDocument, variables, options)

export const SiteFootprintDocument = new TypedDocumentString(`
    query SiteFootprint($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid, includeResults: false) {
      pid
      footprint {
        ...Footprint
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`)

export const useSiteFootprintQuery = <
  TData = Types.SiteFootprintQuery,
  TError = unknown,
>(
  variables: Types.SiteFootprintQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteFootprintQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteFootprintQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteFootprintQuery, TError, TData>({
    queryKey: ["SiteFootprint", variables],
    queryFn: publicFetcher<Types.SiteFootprintQuery, Types.SiteFootprintQueryVariables>(
      SiteFootprintDocument,
      variables,
    ),
    ...options,
  })
}

useSiteFootprintQuery.getKey = (variables: Types.SiteFootprintQueryVariables) => [
  "SiteFootprint",
  variables,
]

useSiteFootprintQuery.rootKey = "SiteFootprint"

export const useSuspenseSiteFootprintQuery = <
  TData = Types.SiteFootprintQuery,
  TError = unknown,
>(
  variables: Types.SiteFootprintQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteFootprintQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteFootprintQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteFootprintQuery, TError, TData>({
    queryKey: ["SiteFootprint", variables],
    queryFn: publicFetcher<Types.SiteFootprintQuery, Types.SiteFootprintQueryVariables>(
      SiteFootprintDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteFootprintQuery.getKey = (
  variables: Types.SiteFootprintQueryVariables,
) => ["SiteFootprint", variables]

useSuspenseSiteFootprintQuery.rootKey = "SiteFootprint"

export const useInfiniteSiteFootprintQuery = <
  TData = InfiniteData<Types.SiteFootprintQuery>,
  TError = unknown,
>(
  variables: Types.SiteFootprintQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteFootprintQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteFootprintQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteFootprintQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFootprint.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteFootprintQuery, Types.SiteFootprintQueryVariables>(
            SiteFootprintDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteFootprintQuery.getKey = (
  variables: Types.SiteFootprintQueryVariables,
) => ["SiteFootprint.infinite", variables]

useInfiniteSiteFootprintQuery.rootKey = "SiteFootprint.infinite"

export const useSuspenseInfiniteSiteFootprintQuery = <
  TData = InfiniteData<Types.SiteFootprintQuery>,
  TError = unknown,
>(
  variables: Types.SiteFootprintQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteFootprintQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteFootprintQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteFootprintQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFootprint.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteFootprintQuery, Types.SiteFootprintQueryVariables>(
            SiteFootprintDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteFootprintQuery.getKey = (
  variables: Types.SiteFootprintQueryVariables,
) => ["SiteFootprint.infinite", variables]

useSuspenseInfiniteSiteFootprintQuery.rootKey = "SiteFootprint.infinite"

useSiteFootprintQuery.fetcher = (
  variables: Types.SiteFootprintQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteFootprintQuery, Types.SiteFootprintQueryVariables>(
    SiteFootprintDocument,
    variables,
    options,
  )

export const SiteMinimalDocument = new TypedDocumentString(`
    query SiteMinimal($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    site(pid: $sitePid, includeResults: false) {
      ...SiteMinimalFields
      status
      statusDisplayText
      flags {
        type
      }
      duplicateSites {
        pid
        displayName
      }
      labels {
        name
        pid
        themeColor
      }
    }
  }
}
    fragment BatteryGroundLeaseFields on BatteryGroundLease {
  annualRentUsd
  ratePerKwhPerYear
  energyKwh
  leasedSqft
  termYears
  escalator
  projections {
    __typename
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment SiteMinimalFields on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  portfolioManager
  numParkingSpaces
  buildingType
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  batteryMarketAvailable
  batteryGroundLease {
    ...BatteryGroundLeaseFields
  }
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  occupancyType
  leaseType
  tenantLeaseTerm
  buildingData {
    yearRenovated
    parentCompany
  }
  footprint {
    ...FootprintGeometry
  }
  thirdPartySupplier {
    pid
    name
  }
  targetUtility {
    lseId
    name
    pid
  }
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}`)

export const useSiteMinimalQuery = <TData = Types.SiteMinimalQuery, TError = unknown>(
  variables: Types.SiteMinimalQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteMinimalQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteMinimalQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteMinimalQuery, TError, TData>({
    queryKey: ["SiteMinimal", variables],
    queryFn: publicFetcher<Types.SiteMinimalQuery, Types.SiteMinimalQueryVariables>(
      SiteMinimalDocument,
      variables,
    ),
    ...options,
  })
}

useSiteMinimalQuery.getKey = (variables: Types.SiteMinimalQueryVariables) => [
  "SiteMinimal",
  variables,
]

useSiteMinimalQuery.rootKey = "SiteMinimal"

export const useSuspenseSiteMinimalQuery = <
  TData = Types.SiteMinimalQuery,
  TError = unknown,
>(
  variables: Types.SiteMinimalQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteMinimalQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteMinimalQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteMinimalQuery, TError, TData>({
    queryKey: ["SiteMinimal", variables],
    queryFn: publicFetcher<Types.SiteMinimalQuery, Types.SiteMinimalQueryVariables>(
      SiteMinimalDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteMinimalQuery.getKey = (variables: Types.SiteMinimalQueryVariables) => [
  "SiteMinimal",
  variables,
]

useSuspenseSiteMinimalQuery.rootKey = "SiteMinimal"

export const useInfiniteSiteMinimalQuery = <
  TData = InfiniteData<Types.SiteMinimalQuery>,
  TError = unknown,
>(
  variables: Types.SiteMinimalQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteMinimalQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteMinimalQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteMinimalQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMinimal.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMinimalQuery, Types.SiteMinimalQueryVariables>(
            SiteMinimalDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteMinimalQuery.getKey = (variables: Types.SiteMinimalQueryVariables) => [
  "SiteMinimal.infinite",
  variables,
]

useInfiniteSiteMinimalQuery.rootKey = "SiteMinimal.infinite"

export const useSuspenseInfiniteSiteMinimalQuery = <
  TData = InfiniteData<Types.SiteMinimalQuery>,
  TError = unknown,
>(
  variables: Types.SiteMinimalQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteMinimalQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteMinimalQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteMinimalQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMinimal.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMinimalQuery, Types.SiteMinimalQueryVariables>(
            SiteMinimalDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteMinimalQuery.getKey = (
  variables: Types.SiteMinimalQueryVariables,
) => ["SiteMinimal.infinite", variables]

useSuspenseInfiniteSiteMinimalQuery.rootKey = "SiteMinimal.infinite"

useSiteMinimalQuery.fetcher = (
  variables: Types.SiteMinimalQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteMinimalQuery, Types.SiteMinimalQueryVariables>(
    SiteMinimalDocument,
    variables,
    options,
  )

export const SiteResultsDocument = new TypedDocumentString(`
    query SiteResults($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      pid
      highlightedResults {
        ...ResultSummary
      }
    }
  }
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment RevampFinmodelSummary on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  offerStatus
  fmvStr
  goal
  isVnem
  isGreenLease
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      totalUtilitySavings
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanTermYears
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    termYears
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  systems {
    ...RevampSystem
  }
}
fragment ResultSummary on Result {
  id
  pid
  isPinned
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodelSummary
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`)

export const useSiteResultsQuery = <TData = Types.SiteResultsQuery, TError = unknown>(
  variables: Types.SiteResultsQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteResultsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteResultsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteResultsQuery, TError, TData>({
    queryKey: ["SiteResults", variables],
    queryFn: publicFetcher<Types.SiteResultsQuery, Types.SiteResultsQueryVariables>(
      SiteResultsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteResultsQuery.getKey = (variables: Types.SiteResultsQueryVariables) => [
  "SiteResults",
  variables,
]

useSiteResultsQuery.rootKey = "SiteResults"

export const useSuspenseSiteResultsQuery = <
  TData = Types.SiteResultsQuery,
  TError = unknown,
>(
  variables: Types.SiteResultsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteResultsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteResultsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteResultsQuery, TError, TData>({
    queryKey: ["SiteResults", variables],
    queryFn: publicFetcher<Types.SiteResultsQuery, Types.SiteResultsQueryVariables>(
      SiteResultsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteResultsQuery.getKey = (variables: Types.SiteResultsQueryVariables) => [
  "SiteResults",
  variables,
]

useSuspenseSiteResultsQuery.rootKey = "SiteResults"

export const useInfiniteSiteResultsQuery = <
  TData = InfiniteData<Types.SiteResultsQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteResultsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteResultsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteResultsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResults.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteResultsQuery, Types.SiteResultsQueryVariables>(
            SiteResultsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteResultsQuery.getKey = (variables: Types.SiteResultsQueryVariables) => [
  "SiteResults.infinite",
  variables,
]

useInfiniteSiteResultsQuery.rootKey = "SiteResults.infinite"

export const useSuspenseInfiniteSiteResultsQuery = <
  TData = InfiniteData<Types.SiteResultsQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteResultsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteResultsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteResultsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResults.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteResultsQuery, Types.SiteResultsQueryVariables>(
            SiteResultsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteResultsQuery.getKey = (
  variables: Types.SiteResultsQueryVariables,
) => ["SiteResults.infinite", variables]

useSuspenseInfiniteSiteResultsQuery.rootKey = "SiteResults.infinite"

useSiteResultsQuery.fetcher = (
  variables: Types.SiteResultsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteResultsQuery, Types.SiteResultsQueryVariables>(
    SiteResultsDocument,
    variables,
    options,
  )

export const RevampSiteBillingMetersDocument = new TypedDocumentString(`
    query RevampSiteBillingMeters($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      billingMeters {
        ...BillingMeter
      }
    }
  }
}
    fragment BillingMeter on BillingMeter {
  id
  pid
  view {
    name
    utilityMeterId
    supplyUtilityMeterId
    buildingType
    loadCategory
    isDisabled
    isArchived
    utility {
      pid
      name
      shortName
    }
    tariff {
      id
      pid
      name
      code
    }
    tenantDetails {
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    numBills
    numIntervals
    totalAnnualKwh {
      ...FancyFloat
    }
    peakAnnualKw {
      ...FancyFloat
    }
    weeklyRollup
    months {
      ...BillingMonthView
    }
    calc2 {
      __typename
      ... on E2Error {
        msg
      }
      ... on Projected {
        ...BillingMeterProjected
      }
    }
    intervals {
      ...BillingIntervalView
    }
  }
}
fragment BillingMeterProjected on Projected {
  year
  annualKwh
  peakKw
  annualCostUsd
  monthlyUsageKwh
  monthlyCostUsd
  kwh8760 {
    timeZone
    start
    year
  }
  costAndKwhDownloadHref
  tariff {
    id
    pid
    name
    code
  }
  monthlyBills {
    start
    end
    sections {
      ...BillGroup
    }
  }
}
fragment BillGroup on BillGroup {
  group
  items {
    ...GroupedLineItem
  }
}
fragment GroupedLineItem on GroupedLineItem {
  cost
  usage
  name
  rate
  group
  unit
}
fragment BillingIntervalView on BillingIntervalView {
  id
  start
  end
  timeZone
  units
  numbers
  timestamps
  document {
    id
    pid
    fileName
    downloadHref
  }
}
fragment BillingMonthView on BillingMonthView {
  id
  start
  end
  usageKwh {
    ...FancyFloat
  }
  peakKw {
    ...FancyFloat
  }
  totalCostUsd {
    ...FancyFloat
  }
  supplyCostUsd {
    ...FancyFloat
  }
  transmissionCostUsd {
    ...FancyFloat
  }
  provider {
    ...Provider
  }
  supplyProvider {
    ...Provider
  }
}
fragment FancyFloat on FancyFloat {
  source
  number
}
fragment Provider on Provider {
  id
  createdAt
  user {
    ...UserRef
  }
  document {
    pid
    fileName
    contentType
    downloadHref
    viewHref
  }
  espmConsumption {
    id
    createdAt
    lastUpdatedAt
    externalConsumptionId
    externalData
    start
    end
    usageKwh
    costUsd
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useRevampSiteBillingMetersQuery = <
  TData = Types.RevampSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteBillingMetersQuery, TError, TData>({
    queryKey: ["RevampSiteBillingMeters", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBillingMetersQuery,
      Types.RevampSiteBillingMetersQueryVariables
    >(RevampSiteBillingMetersDocument, variables),
    ...options,
  })
}

useRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters", variables]

useRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters"

export const useSuspenseRevampSiteBillingMetersQuery = <
  TData = Types.RevampSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteBillingMetersQuery, TError, TData>({
    queryKey: ["RevampSiteBillingMeters", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBillingMetersQuery,
      Types.RevampSiteBillingMetersQueryVariables
    >(RevampSiteBillingMetersDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters", variables]

useSuspenseRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters"

export const useInfiniteRevampSiteBillingMetersQuery = <
  TData = InfiniteData<Types.RevampSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBillingMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBillingMetersQuery,
            Types.RevampSiteBillingMetersQueryVariables
          >(RevampSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters.infinite", variables]

useInfiniteRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters.infinite"

export const useSuspenseInfiniteRevampSiteBillingMetersQuery = <
  TData = InfiniteData<Types.RevampSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBillingMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBillingMetersQuery,
            Types.RevampSiteBillingMetersQueryVariables
          >(RevampSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters.infinite", variables]

useSuspenseInfiniteRevampSiteBillingMetersQuery.rootKey =
  "RevampSiteBillingMeters.infinite"

useRevampSiteBillingMetersQuery.fetcher = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteBillingMetersQuery,
    Types.RevampSiteBillingMetersQueryVariables
  >(RevampSiteBillingMetersDocument, variables, options)

export const RevampSiteGridEscalatorDocument = new TypedDocumentString(`
    query RevampSiteGridEscalator($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      gridEscalator
    }
  }
}
    `)

export const useRevampSiteGridEscalatorQuery = <
  TData = Types.RevampSiteGridEscalatorQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>({
    queryKey: ["RevampSiteGridEscalator", variables],
    queryFn: publicFetcher<
      Types.RevampSiteGridEscalatorQuery,
      Types.RevampSiteGridEscalatorQueryVariables
    >(RevampSiteGridEscalatorDocument, variables),
    ...options,
  })
}

useRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator", variables]

useRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator"

export const useSuspenseRevampSiteGridEscalatorQuery = <
  TData = Types.RevampSiteGridEscalatorQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>({
    queryKey: ["RevampSiteGridEscalator", variables],
    queryFn: publicFetcher<
      Types.RevampSiteGridEscalatorQuery,
      Types.RevampSiteGridEscalatorQueryVariables
    >(RevampSiteGridEscalatorDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator", variables]

useSuspenseRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator"

export const useInfiniteRevampSiteGridEscalatorQuery = <
  TData = InfiniteData<Types.RevampSiteGridEscalatorQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteGridEscalator.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteGridEscalatorQuery,
            Types.RevampSiteGridEscalatorQueryVariables
          >(RevampSiteGridEscalatorDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator.infinite", variables]

useInfiniteRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator.infinite"

export const useSuspenseInfiniteRevampSiteGridEscalatorQuery = <
  TData = InfiniteData<Types.RevampSiteGridEscalatorQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteGridEscalator.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteGridEscalatorQuery,
            Types.RevampSiteGridEscalatorQueryVariables
          >(RevampSiteGridEscalatorDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator.infinite", variables]

useSuspenseInfiniteRevampSiteGridEscalatorQuery.rootKey =
  "RevampSiteGridEscalator.infinite"

useRevampSiteGridEscalatorQuery.fetcher = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteGridEscalatorQuery,
    Types.RevampSiteGridEscalatorQueryVariables
  >(RevampSiteGridEscalatorDocument, variables, options)

export const SiteMetersDocument = new TypedDocumentString(`
    query SiteMeters($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      billingMeters {
        ...BillingMeterOption
      }
    }
  }
}
    fragment BillingMeterOption on BillingMeter {
  pid
  bauMeterPid
  view {
    name
    isArchived
    calc2 {
      __typename
      ... on Projected {
        annualKwh
      }
    }
  }
}`)

export const useSiteMetersQuery = <TData = Types.SiteMetersQuery, TError = unknown>(
  variables: Types.SiteMetersQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteMetersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteMetersQuery, TError, TData>({
    queryKey: ["SiteMeters", variables],
    queryFn: publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
      SiteMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMeters",
  variables,
]

useSiteMetersQuery.rootKey = "SiteMeters"

export const useSuspenseSiteMetersQuery = <
  TData = Types.SiteMetersQuery,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteMetersQuery, TError, TData>({
    queryKey: ["SiteMeters", variables],
    queryFn: publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
      SiteMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMeters",
  variables,
]

useSuspenseSiteMetersQuery.rootKey = "SiteMeters"

export const useInfiniteSiteMetersQuery = <
  TData = InfiniteData<Types.SiteMetersQuery>,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
            SiteMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMeters.infinite",
  variables,
]

useInfiniteSiteMetersQuery.rootKey = "SiteMeters.infinite"

export const useSuspenseInfiniteSiteMetersQuery = <
  TData = InfiniteData<Types.SiteMetersQuery>,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
            SiteMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteMetersQuery.getKey = (
  variables: Types.SiteMetersQueryVariables,
) => ["SiteMeters.infinite", variables]

useSuspenseInfiniteSiteMetersQuery.rootKey = "SiteMeters.infinite"

useSiteMetersQuery.fetcher = (
  variables: Types.SiteMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
    SiteMetersDocument,
    variables,
    options,
  )

export const BillingMeterDocumentParseRequestsDocument = new TypedDocumentString(`
    query BillingMeterDocumentParseRequests($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      documentParseRequests {
        ...DocumentParseRequest
      }
    }
  }
}
    fragment DocumentParseRequest on DocumentParseRequest {
  id
  createdAt
  status
  errorClass
  errorMessage
  document {
    id
    pid
    fileName
    contentType
    downloadHref
    viewHref
    ownerName
    ownerEmail
    dateCreated
  }
  results {
    parse {
      id
      start
      end
      totalKwh
      peakKw
      totalCostUsd
      supplyCostUsd
      transmissionCostUsd
      meterId
      tariffLabel
      kind
    }
    target {
      id
      pid
      label
      utilityMeterId
    }
    kind
  }
}`)

export const useBillingMeterDocumentParseRequestsQuery = <
  TData = Types.BillingMeterDocumentParseRequestsQuery,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>({
    queryKey: ["BillingMeterDocumentParseRequests", variables],
    queryFn: publicFetcher<
      Types.BillingMeterDocumentParseRequestsQuery,
      Types.BillingMeterDocumentParseRequestsQueryVariables
    >(BillingMeterDocumentParseRequestsDocument, variables),
    ...options,
  })
}

useBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests", variables]

useBillingMeterDocumentParseRequestsQuery.rootKey = "BillingMeterDocumentParseRequests"

export const useSuspenseBillingMeterDocumentParseRequestsQuery = <
  TData = Types.BillingMeterDocumentParseRequestsQuery,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>({
    queryKey: ["BillingMeterDocumentParseRequests", variables],
    queryFn: publicFetcher<
      Types.BillingMeterDocumentParseRequestsQuery,
      Types.BillingMeterDocumentParseRequestsQueryVariables
    >(BillingMeterDocumentParseRequestsDocument, variables),
    ...options,
  })
}

useSuspenseBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests", variables]

useSuspenseBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests"

export const useInfiniteBillingMeterDocumentParseRequestsQuery = <
  TData = InfiniteData<Types.BillingMeterDocumentParseRequestsQuery>,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BillingMeterDocumentParseRequests.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BillingMeterDocumentParseRequestsQuery,
            Types.BillingMeterDocumentParseRequestsQueryVariables
          >(BillingMeterDocumentParseRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests.infinite", variables]

useInfiniteBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests.infinite"

export const useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery = <
  TData = InfiniteData<Types.BillingMeterDocumentParseRequestsQuery>,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.BillingMeterDocumentParseRequestsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BillingMeterDocumentParseRequests.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BillingMeterDocumentParseRequestsQuery,
            Types.BillingMeterDocumentParseRequestsQueryVariables
          >(BillingMeterDocumentParseRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests.infinite", variables]

useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests.infinite"

useBillingMeterDocumentParseRequestsQuery.fetcher = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BillingMeterDocumentParseRequestsQuery,
    Types.BillingMeterDocumentParseRequestsQueryVariables
  >(BillingMeterDocumentParseRequestsDocument, variables, options)

export const SiteIntervalImportRequestsDocument = new TypedDocumentString(`
    query SiteIntervalImportRequests($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      intervalImportRequests {
        ...IntervalImportRequest
      }
    }
  }
}
    fragment IntervalImportRequest on IntervalImportRequestView {
  id
  status
  note
  errorClass
  errorMessage
  meterPid
  inputDocument {
    pid
    fileName
    downloadHref
  }
}`)

export const useSiteIntervalImportRequestsQuery = <
  TData = Types.SiteIntervalImportRequestsQuery,
  TError = unknown,
>(
  variables: Types.SiteIntervalImportRequestsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteIntervalImportRequestsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteIntervalImportRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteIntervalImportRequestsQuery, TError, TData>({
    queryKey: ["SiteIntervalImportRequests", variables],
    queryFn: publicFetcher<
      Types.SiteIntervalImportRequestsQuery,
      Types.SiteIntervalImportRequestsQueryVariables
    >(SiteIntervalImportRequestsDocument, variables),
    ...options,
  })
}

useSiteIntervalImportRequestsQuery.getKey = (
  variables: Types.SiteIntervalImportRequestsQueryVariables,
) => ["SiteIntervalImportRequests", variables]

useSiteIntervalImportRequestsQuery.rootKey = "SiteIntervalImportRequests"

export const useSuspenseSiteIntervalImportRequestsQuery = <
  TData = Types.SiteIntervalImportRequestsQuery,
  TError = unknown,
>(
  variables: Types.SiteIntervalImportRequestsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteIntervalImportRequestsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteIntervalImportRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteIntervalImportRequestsQuery, TError, TData>({
    queryKey: ["SiteIntervalImportRequests", variables],
    queryFn: publicFetcher<
      Types.SiteIntervalImportRequestsQuery,
      Types.SiteIntervalImportRequestsQueryVariables
    >(SiteIntervalImportRequestsDocument, variables),
    ...options,
  })
}

useSuspenseSiteIntervalImportRequestsQuery.getKey = (
  variables: Types.SiteIntervalImportRequestsQueryVariables,
) => ["SiteIntervalImportRequests", variables]

useSuspenseSiteIntervalImportRequestsQuery.rootKey = "SiteIntervalImportRequests"

export const useInfiniteSiteIntervalImportRequestsQuery = <
  TData = InfiniteData<Types.SiteIntervalImportRequestsQuery>,
  TError = unknown,
>(
  variables: Types.SiteIntervalImportRequestsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteIntervalImportRequestsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteIntervalImportRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteIntervalImportRequestsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIntervalImportRequests.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIntervalImportRequestsQuery,
            Types.SiteIntervalImportRequestsQueryVariables
          >(SiteIntervalImportRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteIntervalImportRequestsQuery.getKey = (
  variables: Types.SiteIntervalImportRequestsQueryVariables,
) => ["SiteIntervalImportRequests.infinite", variables]

useInfiniteSiteIntervalImportRequestsQuery.rootKey =
  "SiteIntervalImportRequests.infinite"

export const useSuspenseInfiniteSiteIntervalImportRequestsQuery = <
  TData = InfiniteData<Types.SiteIntervalImportRequestsQuery>,
  TError = unknown,
>(
  variables: Types.SiteIntervalImportRequestsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.SiteIntervalImportRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteIntervalImportRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteIntervalImportRequestsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIntervalImportRequests.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIntervalImportRequestsQuery,
            Types.SiteIntervalImportRequestsQueryVariables
          >(SiteIntervalImportRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteIntervalImportRequestsQuery.getKey = (
  variables: Types.SiteIntervalImportRequestsQueryVariables,
) => ["SiteIntervalImportRequests.infinite", variables]

useSuspenseInfiniteSiteIntervalImportRequestsQuery.rootKey =
  "SiteIntervalImportRequests.infinite"

useSiteIntervalImportRequestsQuery.fetcher = (
  variables: Types.SiteIntervalImportRequestsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteIntervalImportRequestsQuery,
    Types.SiteIntervalImportRequestsQueryVariables
  >(SiteIntervalImportRequestsDocument, variables, options)

export const SiteIntervalDocumentsDocument = new TypedDocumentString(`
    query SiteIntervalDocuments($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      intervalDocuments {
        intervalsId
        meterPid
        meterName
        start
        end
        timeZone
        units
        originalDocument {
          pid
          fileName
          downloadHref
        }
        normalizedDocument {
          pid
          fileName
          downloadHref
        }
      }
    }
  }
}
    `)

export const useSiteIntervalDocumentsQuery = <
  TData = Types.SiteIntervalDocumentsQuery,
  TError = unknown,
>(
  variables: Types.SiteIntervalDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteIntervalDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteIntervalDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteIntervalDocumentsQuery, TError, TData>({
    queryKey: ["SiteIntervalDocuments", variables],
    queryFn: publicFetcher<
      Types.SiteIntervalDocumentsQuery,
      Types.SiteIntervalDocumentsQueryVariables
    >(SiteIntervalDocumentsDocument, variables),
    ...options,
  })
}

useSiteIntervalDocumentsQuery.getKey = (
  variables: Types.SiteIntervalDocumentsQueryVariables,
) => ["SiteIntervalDocuments", variables]

useSiteIntervalDocumentsQuery.rootKey = "SiteIntervalDocuments"

export const useSuspenseSiteIntervalDocumentsQuery = <
  TData = Types.SiteIntervalDocumentsQuery,
  TError = unknown,
>(
  variables: Types.SiteIntervalDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteIntervalDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteIntervalDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteIntervalDocumentsQuery, TError, TData>({
    queryKey: ["SiteIntervalDocuments", variables],
    queryFn: publicFetcher<
      Types.SiteIntervalDocumentsQuery,
      Types.SiteIntervalDocumentsQueryVariables
    >(SiteIntervalDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseSiteIntervalDocumentsQuery.getKey = (
  variables: Types.SiteIntervalDocumentsQueryVariables,
) => ["SiteIntervalDocuments", variables]

useSuspenseSiteIntervalDocumentsQuery.rootKey = "SiteIntervalDocuments"

export const useInfiniteSiteIntervalDocumentsQuery = <
  TData = InfiniteData<Types.SiteIntervalDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteIntervalDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteIntervalDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteIntervalDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteIntervalDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIntervalDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIntervalDocumentsQuery,
            Types.SiteIntervalDocumentsQueryVariables
          >(SiteIntervalDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteIntervalDocumentsQuery.getKey = (
  variables: Types.SiteIntervalDocumentsQueryVariables,
) => ["SiteIntervalDocuments.infinite", variables]

useInfiniteSiteIntervalDocumentsQuery.rootKey = "SiteIntervalDocuments.infinite"

export const useSuspenseInfiniteSiteIntervalDocumentsQuery = <
  TData = InfiniteData<Types.SiteIntervalDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteIntervalDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteIntervalDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteIntervalDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteIntervalDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIntervalDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIntervalDocumentsQuery,
            Types.SiteIntervalDocumentsQueryVariables
          >(SiteIntervalDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteIntervalDocumentsQuery.getKey = (
  variables: Types.SiteIntervalDocumentsQueryVariables,
) => ["SiteIntervalDocuments.infinite", variables]

useSuspenseInfiniteSiteIntervalDocumentsQuery.rootKey = "SiteIntervalDocuments.infinite"

useSiteIntervalDocumentsQuery.fetcher = (
  variables: Types.SiteIntervalDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteIntervalDocumentsQuery,
    Types.SiteIntervalDocumentsQueryVariables
  >(SiteIntervalDocumentsDocument, variables, options)

export const OrgDetailDocument = new TypedDocumentString(`
    query OrgDetail($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    slug
    name
    logomarkUrl
    logoUrl
    defaultHubspotLeadSource
    isChannelPartner
    portfolios {
      pid
      title
    }
  }
}
    `)

export const useOrgDetailQuery = <TData = Types.OrgDetailQuery, TError = unknown>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgDetailQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetail", variables],
    queryFn: publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail",
  variables,
]

useOrgDetailQuery.rootKey = "OrgDetail"

export const useSuspenseOrgDetailQuery = <
  TData = Types.OrgDetailQuery,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetail", variables],
    queryFn: publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail",
  variables,
]

useSuspenseOrgDetailQuery.rootKey = "OrgDetail"

export const useInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail.infinite",
  variables,
]

useInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

export const useSuspenseInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgDetailQuery.getKey = (
  variables: Types.OrgDetailQueryVariables,
) => ["OrgDetail.infinite", variables]

useSuspenseInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

useOrgDetailQuery.fetcher = (
  variables: Types.OrgDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
    OrgDetailDocument,
    variables,
    options,
  )

export const HubspotFinancingTypeDefaultDocument = new TypedDocumentString(`
    query HubspotFinancingTypeDefault($finProductType: FinProductType) {
  projects {
    hubspotFinancingTypeDefault(finProductType: $finProductType)
  }
}
    `)

export const useHubspotFinancingTypeDefaultQuery = <
  TData = Types.HubspotFinancingTypeDefaultQuery,
  TError = unknown,
>(
  variables?: Types.HubspotFinancingTypeDefaultQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HubspotFinancingTypeDefaultQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.HubspotFinancingTypeDefaultQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.HubspotFinancingTypeDefaultQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["HubspotFinancingTypeDefault"]
        : ["HubspotFinancingTypeDefault", variables],
    queryFn: publicFetcher<
      Types.HubspotFinancingTypeDefaultQuery,
      Types.HubspotFinancingTypeDefaultQueryVariables
    >(HubspotFinancingTypeDefaultDocument, variables),
    ...options,
  })
}

useHubspotFinancingTypeDefaultQuery.getKey = (
  variables?: Types.HubspotFinancingTypeDefaultQueryVariables,
) =>
  variables === undefined
    ? ["HubspotFinancingTypeDefault"]
    : ["HubspotFinancingTypeDefault", variables]

useHubspotFinancingTypeDefaultQuery.rootKey = "HubspotFinancingTypeDefault"

export const useSuspenseHubspotFinancingTypeDefaultQuery = <
  TData = Types.HubspotFinancingTypeDefaultQuery,
  TError = unknown,
>(
  variables?: Types.HubspotFinancingTypeDefaultQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HubspotFinancingTypeDefaultQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HubspotFinancingTypeDefaultQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HubspotFinancingTypeDefaultQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["HubspotFinancingTypeDefault"]
        : ["HubspotFinancingTypeDefault", variables],
    queryFn: publicFetcher<
      Types.HubspotFinancingTypeDefaultQuery,
      Types.HubspotFinancingTypeDefaultQueryVariables
    >(HubspotFinancingTypeDefaultDocument, variables),
    ...options,
  })
}

useSuspenseHubspotFinancingTypeDefaultQuery.getKey = (
  variables?: Types.HubspotFinancingTypeDefaultQueryVariables,
) =>
  variables === undefined
    ? ["HubspotFinancingTypeDefault"]
    : ["HubspotFinancingTypeDefault", variables]

useSuspenseHubspotFinancingTypeDefaultQuery.rootKey = "HubspotFinancingTypeDefault"

export const useInfiniteHubspotFinancingTypeDefaultQuery = <
  TData = InfiniteData<Types.HubspotFinancingTypeDefaultQuery>,
  TError = unknown,
>(
  variables: Types.HubspotFinancingTypeDefaultQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HubspotFinancingTypeDefaultQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HubspotFinancingTypeDefaultQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HubspotFinancingTypeDefaultQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["HubspotFinancingTypeDefault.infinite"]
            : ["HubspotFinancingTypeDefault.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HubspotFinancingTypeDefaultQuery,
            Types.HubspotFinancingTypeDefaultQueryVariables
          >(HubspotFinancingTypeDefaultDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHubspotFinancingTypeDefaultQuery.getKey = (
  variables?: Types.HubspotFinancingTypeDefaultQueryVariables,
) =>
  variables === undefined
    ? ["HubspotFinancingTypeDefault.infinite"]
    : ["HubspotFinancingTypeDefault.infinite", variables]

useInfiniteHubspotFinancingTypeDefaultQuery.rootKey =
  "HubspotFinancingTypeDefault.infinite"

export const useSuspenseInfiniteHubspotFinancingTypeDefaultQuery = <
  TData = InfiniteData<Types.HubspotFinancingTypeDefaultQuery>,
  TError = unknown,
>(
  variables: Types.HubspotFinancingTypeDefaultQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.HubspotFinancingTypeDefaultQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HubspotFinancingTypeDefaultQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.HubspotFinancingTypeDefaultQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["HubspotFinancingTypeDefault.infinite"]
            : ["HubspotFinancingTypeDefault.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HubspotFinancingTypeDefaultQuery,
            Types.HubspotFinancingTypeDefaultQueryVariables
          >(HubspotFinancingTypeDefaultDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHubspotFinancingTypeDefaultQuery.getKey = (
  variables?: Types.HubspotFinancingTypeDefaultQueryVariables,
) =>
  variables === undefined
    ? ["HubspotFinancingTypeDefault.infinite"]
    : ["HubspotFinancingTypeDefault.infinite", variables]

useSuspenseInfiniteHubspotFinancingTypeDefaultQuery.rootKey =
  "HubspotFinancingTypeDefault.infinite"

useHubspotFinancingTypeDefaultQuery.fetcher = (
  variables?: Types.HubspotFinancingTypeDefaultQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.HubspotFinancingTypeDefaultQuery,
    Types.HubspotFinancingTypeDefaultQueryVariables
  >(HubspotFinancingTypeDefaultDocument, variables, options)

export const HubspotDealPropertyPreviewDocument = new TypedDocumentString(`
    query HubspotDealPropertyPreview($projectPid: String!, $financingType: HubspotFinancingType, $leadSource: HubspotLeadSource) {
  projects {
    hubspotDealPropertyPreview(
      projectPid: $projectPid
      financingType: $financingType
      leadSource: $leadSource
    ) {
      name
      label
      value
      isMissing
      group
    }
  }
}
    `)

export const useHubspotDealPropertyPreviewQuery = <
  TData = Types.HubspotDealPropertyPreviewQuery,
  TError = unknown,
>(
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HubspotDealPropertyPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.HubspotDealPropertyPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.HubspotDealPropertyPreviewQuery, TError, TData>({
    queryKey: ["HubspotDealPropertyPreview", variables],
    queryFn: publicFetcher<
      Types.HubspotDealPropertyPreviewQuery,
      Types.HubspotDealPropertyPreviewQueryVariables
    >(HubspotDealPropertyPreviewDocument, variables),
    ...options,
  })
}

useHubspotDealPropertyPreviewQuery.getKey = (
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
) => ["HubspotDealPropertyPreview", variables]

useHubspotDealPropertyPreviewQuery.rootKey = "HubspotDealPropertyPreview"

export const useSuspenseHubspotDealPropertyPreviewQuery = <
  TData = Types.HubspotDealPropertyPreviewQuery,
  TError = unknown,
>(
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HubspotDealPropertyPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HubspotDealPropertyPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HubspotDealPropertyPreviewQuery, TError, TData>({
    queryKey: ["HubspotDealPropertyPreview", variables],
    queryFn: publicFetcher<
      Types.HubspotDealPropertyPreviewQuery,
      Types.HubspotDealPropertyPreviewQueryVariables
    >(HubspotDealPropertyPreviewDocument, variables),
    ...options,
  })
}

useSuspenseHubspotDealPropertyPreviewQuery.getKey = (
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
) => ["HubspotDealPropertyPreview", variables]

useSuspenseHubspotDealPropertyPreviewQuery.rootKey = "HubspotDealPropertyPreview"

export const useInfiniteHubspotDealPropertyPreviewQuery = <
  TData = InfiniteData<Types.HubspotDealPropertyPreviewQuery>,
  TError = unknown,
>(
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HubspotDealPropertyPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HubspotDealPropertyPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HubspotDealPropertyPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HubspotDealPropertyPreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HubspotDealPropertyPreviewQuery,
            Types.HubspotDealPropertyPreviewQueryVariables
          >(HubspotDealPropertyPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHubspotDealPropertyPreviewQuery.getKey = (
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
) => ["HubspotDealPropertyPreview.infinite", variables]

useInfiniteHubspotDealPropertyPreviewQuery.rootKey =
  "HubspotDealPropertyPreview.infinite"

export const useSuspenseInfiniteHubspotDealPropertyPreviewQuery = <
  TData = InfiniteData<Types.HubspotDealPropertyPreviewQuery>,
  TError = unknown,
>(
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.HubspotDealPropertyPreviewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HubspotDealPropertyPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HubspotDealPropertyPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HubspotDealPropertyPreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HubspotDealPropertyPreviewQuery,
            Types.HubspotDealPropertyPreviewQueryVariables
          >(HubspotDealPropertyPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHubspotDealPropertyPreviewQuery.getKey = (
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
) => ["HubspotDealPropertyPreview.infinite", variables]

useSuspenseInfiniteHubspotDealPropertyPreviewQuery.rootKey =
  "HubspotDealPropertyPreview.infinite"

useHubspotDealPropertyPreviewQuery.fetcher = (
  variables: Types.HubspotDealPropertyPreviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.HubspotDealPropertyPreviewQuery,
    Types.HubspotDealPropertyPreviewQueryVariables
  >(HubspotDealPropertyPreviewDocument, variables, options)

export const MultiMeterDetailDocument = new TypedDocumentString(`
    query MultiMeterDetail($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    site(pid: $sitePid) {
      ...SiteMeterSet
    }
  }
}
    fragment SiteMeterSet on RevampSite {
  meterSet {
    intervalsBauXlsxHref
    loadKwh
    loadMaxKw
    loadOrigin
    totalCost
    rollup {
      season
      days
      numbers
    }
    meters {
      ...SiteBauMeter
    }
  }
}
fragment SiteBauMeter on BauMeter {
  id
  pid
  userVisibleLabel
  loadOrigin
  loadCategory
  formattedBill {
    ...FormattedBill
  }
  tariff {
    id
    name
    code
  }
  rollup {
    season
    days
    numbers
  }
  intervals {
    maxKw
    year
    units
    totalKwh
  }
  tariffUsdPerKwh
  totalCost
  importsKwh
}
fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...BillCostItem
  }
}
fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}
fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}`)

export const useMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetail", variables],
    queryFn: publicFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useMultiMeterDetailQuery.getKey = (variables: Types.MultiMeterDetailQueryVariables) => [
  "MultiMeterDetail",
  variables,
]

useMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useSuspenseMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetail", variables],
    queryFn: publicFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useSuspenseMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail", variables]

useSuspenseMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infinite", variables]

useInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

export const useSuspenseInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infinite", variables]

useSuspenseInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

useMultiMeterDetailQuery.fetcher = (
  variables: Types.MultiMeterDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MultiMeterDetailQuery, Types.MultiMeterDetailQueryVariables>(
    MultiMeterDetailDocument,
    variables,
    options,
  )

export const OrgBundlesDocument = new TypedDocumentString(`
    query OrgBundles($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    logoUrl
    bundles {
      ...BundleListItem
      rows {
        ...BundleRowDetailLite
      }
    }
  }
}
    fragment BundleListItem on Bundle {
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  archivedAt
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
fragment BundleRowDetailLite on BundleRow {
  projectPid
  optOut {
    ...InvestorOptOut
  }
  site {
    ...RevampSiteBasics
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`)

export const useOrgBundlesQuery = <TData = Types.OrgBundlesQuery, TError = unknown>(
  variables: Types.OrgBundlesQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgBundlesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgBundlesQuery, TError, TData>({
    queryKey: ["OrgBundles", variables],
    queryFn: publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
      OrgBundlesDocument,
      variables,
    ),
    ...options,
  })
}

useOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundles",
  variables,
]

useOrgBundlesQuery.rootKey = "OrgBundles"

export const useSuspenseOrgBundlesQuery = <
  TData = Types.OrgBundlesQuery,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgBundlesQuery, TError, TData>({
    queryKey: ["OrgBundles", variables],
    queryFn: publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
      OrgBundlesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundles",
  variables,
]

useSuspenseOrgBundlesQuery.rootKey = "OrgBundles"

export const useInfiniteOrgBundlesQuery = <
  TData = InfiniteData<Types.OrgBundlesQuery>,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgBundlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgBundles.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
            OrgBundlesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundles.infinite",
  variables,
]

useInfiniteOrgBundlesQuery.rootKey = "OrgBundles.infinite"

export const useSuspenseInfiniteOrgBundlesQuery = <
  TData = InfiniteData<Types.OrgBundlesQuery>,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgBundlesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgBundlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgBundles.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
            OrgBundlesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgBundlesQuery.getKey = (
  variables: Types.OrgBundlesQueryVariables,
) => ["OrgBundles.infinite", variables]

useSuspenseInfiniteOrgBundlesQuery.rootKey = "OrgBundles.infinite"

useOrgBundlesQuery.fetcher = (
  variables: Types.OrgBundlesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
    OrgBundlesDocument,
    variables,
    options,
  )

export const SiteBidsDocument = new TypedDocumentString(`
    query SiteBids($orgSlug: String!, $sitePid: String!, $projectPid: String) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      bidPackages(projectPid: $projectPid) {
        ...BasicBidPackage
      }
    }
  }
}
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`)

export const useSiteBidsQuery = <TData = Types.SiteBidsQuery, TError = unknown>(
  variables: Types.SiteBidsQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteBidsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsQuery, TError, TData>({
    queryKey: ["SiteBids", variables],
    queryFn: publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
      SiteBidsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids",
  variables,
]

useSiteBidsQuery.rootKey = "SiteBids"

export const useSuspenseSiteBidsQuery = <TData = Types.SiteBidsQuery, TError = unknown>(
  variables: Types.SiteBidsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsQuery, TError, TData>({
    queryKey: ["SiteBids", variables],
    queryFn: publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
      SiteBidsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids",
  variables,
]

useSuspenseSiteBidsQuery.rootKey = "SiteBids"

export const useInfiniteSiteBidsQuery = <
  TData = InfiniteData<Types.SiteBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
            SiteBidsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids.infinite",
  variables,
]

useInfiniteSiteBidsQuery.rootKey = "SiteBids.infinite"

export const useSuspenseInfiniteSiteBidsQuery = <
  TData = InfiniteData<Types.SiteBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
            SiteBidsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids.infinite",
  variables,
]

useSuspenseInfiniteSiteBidsQuery.rootKey = "SiteBids.infinite"

useSiteBidsQuery.fetcher = (
  variables: Types.SiteBidsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
    SiteBidsDocument,
    variables,
    options,
  )

export const FinancierBundleSiteDocumentsDocument = new TypedDocumentString(`
    query FinancierBundleSiteDocuments($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleSiteDocuments(orgSlug: $orgSlug, bundlePid: $bundlePid, sitePid: $sitePid) {
    ...Document
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useFinancierBundleSiteDocumentsQuery = <
  TData = Types.FinancierBundleSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierBundleSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinancierBundleSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinancierBundleSiteDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleSiteDocumentsQuery,
      Types.FinancierBundleSiteDocumentsQueryVariables
    >(FinancierBundleSiteDocumentsDocument, variables),
    ...options,
  })
}

useFinancierBundleSiteDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
) => ["FinancierBundleSiteDocuments", variables]

useFinancierBundleSiteDocumentsQuery.rootKey = "FinancierBundleSiteDocuments"

export const useSuspenseFinancierBundleSiteDocumentsQuery = <
  TData = Types.FinancierBundleSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierBundleSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierBundleSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierBundleSiteDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleSiteDocumentsQuery,
      Types.FinancierBundleSiteDocumentsQueryVariables
    >(FinancierBundleSiteDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseFinancierBundleSiteDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
) => ["FinancierBundleSiteDocuments", variables]

useSuspenseFinancierBundleSiteDocumentsQuery.rootKey = "FinancierBundleSiteDocuments"

export const useInfiniteFinancierBundleSiteDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierBundleSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierBundleSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierBundleSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleSiteDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleSiteDocumentsQuery,
            Types.FinancierBundleSiteDocumentsQueryVariables
          >(FinancierBundleSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierBundleSiteDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
) => ["FinancierBundleSiteDocuments.infinite", variables]

useInfiniteFinancierBundleSiteDocumentsQuery.rootKey =
  "FinancierBundleSiteDocuments.infinite"

export const useSuspenseInfiniteFinancierBundleSiteDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleSiteDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.FinancierBundleSiteDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleSiteDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleSiteDocumentsQuery,
            Types.FinancierBundleSiteDocumentsQueryVariables
          >(FinancierBundleSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierBundleSiteDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
) => ["FinancierBundleSiteDocuments.infinite", variables]

useSuspenseInfiniteFinancierBundleSiteDocumentsQuery.rootKey =
  "FinancierBundleSiteDocuments.infinite"

useFinancierBundleSiteDocumentsQuery.fetcher = (
  variables: Types.FinancierBundleSiteDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinancierBundleSiteDocumentsQuery,
    Types.FinancierBundleSiteDocumentsQueryVariables
  >(FinancierBundleSiteDocumentsDocument, variables, options)

export const FinancierBundleSitesDocumentsDocument = new TypedDocumentString(`
    query FinancierBundleSitesDocuments($orgSlug: String!, $bundlePid: String!) {
  bundleSitesDocuments(orgSlug: $orgSlug, bundlePid: $bundlePid) {
    sitePid
    documents {
      ...Document
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useFinancierBundleSitesDocumentsQuery = <
  TData = Types.FinancierBundleSitesDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierBundleSitesDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinancierBundleSitesDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinancierBundleSitesDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleSitesDocuments", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleSitesDocumentsQuery,
      Types.FinancierBundleSitesDocumentsQueryVariables
    >(FinancierBundleSitesDocumentsDocument, variables),
    ...options,
  })
}

useFinancierBundleSitesDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
) => ["FinancierBundleSitesDocuments", variables]

useFinancierBundleSitesDocumentsQuery.rootKey = "FinancierBundleSitesDocuments"

export const useSuspenseFinancierBundleSitesDocumentsQuery = <
  TData = Types.FinancierBundleSitesDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierBundleSitesDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierBundleSitesDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierBundleSitesDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleSitesDocuments", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleSitesDocumentsQuery,
      Types.FinancierBundleSitesDocumentsQueryVariables
    >(FinancierBundleSitesDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseFinancierBundleSitesDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
) => ["FinancierBundleSitesDocuments", variables]

useSuspenseFinancierBundleSitesDocumentsQuery.rootKey = "FinancierBundleSitesDocuments"

export const useInfiniteFinancierBundleSitesDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleSitesDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierBundleSitesDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierBundleSitesDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierBundleSitesDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleSitesDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleSitesDocumentsQuery,
            Types.FinancierBundleSitesDocumentsQueryVariables
          >(FinancierBundleSitesDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierBundleSitesDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
) => ["FinancierBundleSitesDocuments.infinite", variables]

useInfiniteFinancierBundleSitesDocumentsQuery.rootKey =
  "FinancierBundleSitesDocuments.infinite"

export const useSuspenseInfiniteFinancierBundleSitesDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleSitesDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleSitesDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleSitesDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.FinancierBundleSitesDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleSitesDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleSitesDocumentsQuery,
            Types.FinancierBundleSitesDocumentsQueryVariables
          >(FinancierBundleSitesDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierBundleSitesDocumentsQuery.getKey = (
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
) => ["FinancierBundleSitesDocuments.infinite", variables]

useSuspenseInfiniteFinancierBundleSitesDocumentsQuery.rootKey =
  "FinancierBundleSitesDocuments.infinite"

useFinancierBundleSitesDocumentsQuery.fetcher = (
  variables: Types.FinancierBundleSitesDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinancierBundleSitesDocumentsQuery,
    Types.FinancierBundleSitesDocumentsQueryVariables
  >(FinancierBundleSitesDocumentsDocument, variables, options)

export const ProjectDetailDocument = new TypedDocumentString(`
    query ProjectDetail($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      finproductType
      isAwardedToDeveloper
      site {
        ...RevampSiteBasics
      }
      helioscopes {
        ...Helioscope2
      }
      awardedBidFinmodel {
        ...RevampFinmodel
      }
    }
  }
}
    fragment Helioscope2 on Helioscope2 {
  pid
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  groundPvKw
  dcNameplateKw
  acNameplateKw
  annualProductionAcKwh
  helioscopeKw
  pdfHref
  roofImageHref
  roofImageName
  roofImage {
    id
  }
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`)

export const useProjectDetailQuery = <
  TData = Types.ProjectDetailQuery,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ProjectDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectDetailQuery, TError, TData>({
    queryKey: ["ProjectDetail", variables],
    queryFn: publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
      ProjectDetailDocument,
      variables,
    ),
    ...options,
  })
}

useProjectDetailQuery.getKey = (variables: Types.ProjectDetailQueryVariables) => [
  "ProjectDetail",
  variables,
]

useProjectDetailQuery.rootKey = "ProjectDetail"

export const useSuspenseProjectDetailQuery = <
  TData = Types.ProjectDetailQuery,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectDetailQuery, TError, TData>({
    queryKey: ["ProjectDetail", variables],
    queryFn: publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
      ProjectDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseProjectDetailQuery.getKey = (
  variables: Types.ProjectDetailQueryVariables,
) => ["ProjectDetail", variables]

useSuspenseProjectDetailQuery.rootKey = "ProjectDetail"

export const useInfiniteProjectDetailQuery = <
  TData = InfiniteData<Types.ProjectDetailQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
            ProjectDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectDetailQuery.getKey = (
  variables: Types.ProjectDetailQueryVariables,
) => ["ProjectDetail.infinite", variables]

useInfiniteProjectDetailQuery.rootKey = "ProjectDetail.infinite"

export const useSuspenseInfiniteProjectDetailQuery = <
  TData = InfiniteData<Types.ProjectDetailQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
            ProjectDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectDetailQuery.getKey = (
  variables: Types.ProjectDetailQueryVariables,
) => ["ProjectDetail.infinite", variables]

useSuspenseInfiniteProjectDetailQuery.rootKey = "ProjectDetail.infinite"

useProjectDetailQuery.fetcher = (
  variables: Types.ProjectDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
    ProjectDetailDocument,
    variables,
    options,
  )

export const ProjectAllDocumentsDocument = new TypedDocumentString(`
    query ProjectAllDocuments($orgSlug: String!, $projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      canUploadDocuments
      site {
        pid
        documents {
          ...Document
          canDelete
        }
      }
      documents(orgSlug: $orgSlug) {
        ...Document
        canDelete
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useProjectAllDocumentsQuery = <
  TData = Types.ProjectAllDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectAllDocumentsQuery, TError, TData>({
    queryKey: ["ProjectAllDocuments", variables],
    queryFn: publicFetcher<
      Types.ProjectAllDocumentsQuery,
      Types.ProjectAllDocumentsQueryVariables
    >(ProjectAllDocumentsDocument, variables),
    ...options,
  })
}

useProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocuments", variables]

useProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments"

export const useSuspenseProjectAllDocumentsQuery = <
  TData = Types.ProjectAllDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectAllDocumentsQuery, TError, TData>({
    queryKey: ["ProjectAllDocuments", variables],
    queryFn: publicFetcher<
      Types.ProjectAllDocumentsQuery,
      Types.ProjectAllDocumentsQueryVariables
    >(ProjectAllDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocuments", variables]

useSuspenseProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments"

export const useInfiniteProjectAllDocumentsQuery = <
  TData = InfiniteData<Types.ProjectAllDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectAllDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectAllDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectAllDocumentsQuery,
            Types.ProjectAllDocumentsQueryVariables
          >(ProjectAllDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocuments.infinite", variables]

useInfiniteProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments.infinite"

export const useSuspenseInfiniteProjectAllDocumentsQuery = <
  TData = InfiniteData<Types.ProjectAllDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectAllDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectAllDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectAllDocumentsQuery,
            Types.ProjectAllDocumentsQueryVariables
          >(ProjectAllDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocuments.infinite", variables]

useSuspenseInfiniteProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments.infinite"

useProjectAllDocumentsQuery.fetcher = (
  variables: Types.ProjectAllDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectAllDocumentsQuery,
    Types.ProjectAllDocumentsQueryVariables
  >(ProjectAllDocumentsDocument, variables, options)

export const ProjectDocumentsBatchDocument = new TypedDocumentString(`
    query ProjectDocumentsBatch($orgSlug: String!, $projectPids: [String!]!, $tags: [DocumentTag!]) {
  projects {
    documentsBatch(projectPids: $projectPids, orgSlug: $orgSlug, tags: $tags) {
      projectPid
      documents {
        ...Document
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useProjectDocumentsBatchQuery = <
  TData = Types.ProjectDocumentsBatchQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectDocumentsBatchQuery, TError, TData>({
    queryKey: ["ProjectDocumentsBatch", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsBatchQuery,
      Types.ProjectDocumentsBatchQueryVariables
    >(ProjectDocumentsBatchDocument, variables),
    ...options,
  })
}

useProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatch", variables]

useProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch"

export const useSuspenseProjectDocumentsBatchQuery = <
  TData = Types.ProjectDocumentsBatchQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectDocumentsBatchQuery, TError, TData>({
    queryKey: ["ProjectDocumentsBatch", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsBatchQuery,
      Types.ProjectDocumentsBatchQueryVariables
    >(ProjectDocumentsBatchDocument, variables),
    ...options,
  })
}

useSuspenseProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatch", variables]

useSuspenseProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch"

export const useInfiniteProjectDocumentsBatchQuery = <
  TData = InfiniteData<Types.ProjectDocumentsBatchQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectDocumentsBatchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDocumentsBatch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsBatchQuery,
            Types.ProjectDocumentsBatchQueryVariables
          >(ProjectDocumentsBatchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatch.infinite", variables]

useInfiniteProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch.infinite"

export const useSuspenseInfiniteProjectDocumentsBatchQuery = <
  TData = InfiniteData<Types.ProjectDocumentsBatchQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectDocumentsBatchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDocumentsBatch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsBatchQuery,
            Types.ProjectDocumentsBatchQueryVariables
          >(ProjectDocumentsBatchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatch.infinite", variables]

useSuspenseInfiniteProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch.infinite"

useProjectDocumentsBatchQuery.fetcher = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectDocumentsBatchQuery,
    Types.ProjectDocumentsBatchQueryVariables
  >(ProjectDocumentsBatchDocument, variables, options)

export const ProjectMultiMeterDocument = new TypedDocumentString(`
    query ProjectMultiMeter($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      site {
        ...SiteMeterSet
      }
    }
  }
}
    fragment SiteMeterSet on RevampSite {
  meterSet {
    intervalsBauXlsxHref
    loadKwh
    loadMaxKw
    loadOrigin
    totalCost
    rollup {
      season
      days
      numbers
    }
    meters {
      ...SiteBauMeter
    }
  }
}
fragment SiteBauMeter on BauMeter {
  id
  pid
  userVisibleLabel
  loadOrigin
  loadCategory
  formattedBill {
    ...FormattedBill
  }
  tariff {
    id
    name
    code
  }
  rollup {
    season
    days
    numbers
  }
  intervals {
    maxKw
    year
    units
    totalKwh
  }
  tariffUsdPerKwh
  totalCost
  importsKwh
}
fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...BillCostItem
  }
}
fragment BillCostItem on CostItem {
  __typename
  name
  rateId
  chargeType
  chargeCategory
  volume
  rate
  cost
}
fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}`)

export const useProjectMultiMeterQuery = <
  TData = Types.ProjectMultiMeterQuery,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectMultiMeterQuery, TError, TData>({
    queryKey: ["ProjectMultiMeter", variables],
    queryFn: publicFetcher<
      Types.ProjectMultiMeterQuery,
      Types.ProjectMultiMeterQueryVariables
    >(ProjectMultiMeterDocument, variables),
    ...options,
  })
}

useProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeter", variables]

useProjectMultiMeterQuery.rootKey = "ProjectMultiMeter"

export const useSuspenseProjectMultiMeterQuery = <
  TData = Types.ProjectMultiMeterQuery,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectMultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectMultiMeterQuery, TError, TData>({
    queryKey: ["ProjectMultiMeter", variables],
    queryFn: publicFetcher<
      Types.ProjectMultiMeterQuery,
      Types.ProjectMultiMeterQueryVariables
    >(ProjectMultiMeterDocument, variables),
    ...options,
  })
}

useSuspenseProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeter", variables]

useSuspenseProjectMultiMeterQuery.rootKey = "ProjectMultiMeter"

export const useInfiniteProjectMultiMeterQuery = <
  TData = InfiniteData<Types.ProjectMultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectMultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectMultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectMultiMeter.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectMultiMeterQuery,
            Types.ProjectMultiMeterQueryVariables
          >(ProjectMultiMeterDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeter.infinite", variables]

useInfiniteProjectMultiMeterQuery.rootKey = "ProjectMultiMeter.infinite"

export const useSuspenseInfiniteProjectMultiMeterQuery = <
  TData = InfiniteData<Types.ProjectMultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectMultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectMultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectMultiMeter.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectMultiMeterQuery,
            Types.ProjectMultiMeterQueryVariables
          >(ProjectMultiMeterDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeter.infinite", variables]

useSuspenseInfiniteProjectMultiMeterQuery.rootKey = "ProjectMultiMeter.infinite"

useProjectMultiMeterQuery.fetcher = (
  variables: Types.ProjectMultiMeterQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectMultiMeterQuery, Types.ProjectMultiMeterQueryVariables>(
    ProjectMultiMeterDocument,
    variables,
    options,
  )

export const SidebarDataDocument = new TypedDocumentString(`
    query SidebarData($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    sites {
      view {
        count
      }
    }
    shine {
      stats {
        numProperties
      }
    }
    isDeveloper
  }
}
    `)

export const useSidebarDataQuery = <TData = Types.SidebarDataQuery, TError = unknown>(
  variables: Types.SidebarDataQueryVariables,
  options?: Omit<UseQueryOptions<Types.SidebarDataQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SidebarDataQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SidebarDataQuery, TError, TData>({
    queryKey: ["SidebarData", variables],
    queryFn: publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
      SidebarDataDocument,
      variables,
    ),
    ...options,
  })
}

useSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarData",
  variables,
]

useSidebarDataQuery.rootKey = "SidebarData"

export const useSuspenseSidebarDataQuery = <
  TData = Types.SidebarDataQuery,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SidebarDataQuery, TError, TData>({
    queryKey: ["SidebarData", variables],
    queryFn: publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
      SidebarDataDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarData",
  variables,
]

useSuspenseSidebarDataQuery.rootKey = "SidebarData"

export const useInfiniteSidebarDataQuery = <
  TData = InfiniteData<Types.SidebarDataQuery>,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SidebarDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SidebarData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
            SidebarDataDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarData.infinite",
  variables,
]

useInfiniteSidebarDataQuery.rootKey = "SidebarData.infinite"

export const useSuspenseInfiniteSidebarDataQuery = <
  TData = InfiniteData<Types.SidebarDataQuery>,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SidebarDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SidebarData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
            SidebarDataDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSidebarDataQuery.getKey = (
  variables: Types.SidebarDataQueryVariables,
) => ["SidebarData.infinite", variables]

useSuspenseInfiniteSidebarDataQuery.rootKey = "SidebarData.infinite"

useSidebarDataQuery.fetcher = (
  variables: Types.SidebarDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
    SidebarDataDocument,
    variables,
    options,
  )

export const ThirdPartySuppliersDocument = new TypedDocumentString(`
    query ThirdPartySuppliers {
  thirdPartySuppliers {
    id
    pid
    name
  }
}
    `)

export const useThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliers"]
        : ["ThirdPartySuppliers", variables],
    queryFn: publicFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined ? ["ThirdPartySuppliers"] : ["ThirdPartySuppliers", variables]

useThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useSuspenseThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliers"]
        : ["ThirdPartySuppliers", variables],
    queryFn: publicFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useSuspenseThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined ? ["ThirdPartySuppliers"] : ["ThirdPartySuppliers", variables]

useSuspenseThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infinite"]
            : ["ThirdPartySuppliers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infinite"]
    : ["ThirdPartySuppliers.infinite", variables]

useInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

export const useSuspenseInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infinite"]
            : ["ThirdPartySuppliers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infinite"]
    : ["ThirdPartySuppliers.infinite", variables]

useSuspenseInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

useThirdPartySuppliersQuery.fetcher = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ThirdPartySuppliersQuery,
    Types.ThirdPartySuppliersQueryVariables
  >(ThirdPartySuppliersDocument, variables, options)

export const ShineSiteDetailDocument = new TypedDocumentString(`
    query ShineSiteDetail($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $sitePid) {
      pid
      name
      fullAddress
      footprint {
        ...Footprint
      }
      shine {
        status
        stats {
          ...ShineStats
        }
      }
    }
  }
}
    fragment ShineStats on ShineStats {
  numProperties
  pvKwInstalled
  totalCo2ReducedTonnes
  totalGrossRevenue
  totalPvProductionKwh
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`)

export const useShineSiteDetailQuery = <
  TData = Types.ShineSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ShineSiteDetailQuery, TError, TData>({
    queryKey: ["ShineSiteDetail", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailQuery,
      Types.ShineSiteDetailQueryVariables
    >(ShineSiteDetailDocument, variables),
    ...options,
  })
}

useShineSiteDetailQuery.getKey = (variables: Types.ShineSiteDetailQueryVariables) => [
  "ShineSiteDetail",
  variables,
]

useShineSiteDetailQuery.rootKey = "ShineSiteDetail"

export const useSuspenseShineSiteDetailQuery = <
  TData = Types.ShineSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ShineSiteDetailQuery, TError, TData>({
    queryKey: ["ShineSiteDetail", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailQuery,
      Types.ShineSiteDetailQueryVariables
    >(ShineSiteDetailDocument, variables),
    ...options,
  })
}

useSuspenseShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetail", variables]

useSuspenseShineSiteDetailQuery.rootKey = "ShineSiteDetail"

export const useInfiniteShineSiteDetailQuery = <
  TData = InfiniteData<Types.ShineSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ShineSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailQuery,
            Types.ShineSiteDetailQueryVariables
          >(ShineSiteDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetail.infinite", variables]

useInfiniteShineSiteDetailQuery.rootKey = "ShineSiteDetail.infinite"

export const useSuspenseInfiniteShineSiteDetailQuery = <
  TData = InfiniteData<Types.ShineSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ShineSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailQuery,
            Types.ShineSiteDetailQueryVariables
          >(ShineSiteDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetail.infinite", variables]

useSuspenseInfiniteShineSiteDetailQuery.rootKey = "ShineSiteDetail.infinite"

useShineSiteDetailQuery.fetcher = (
  variables: Types.ShineSiteDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ShineSiteDetailQuery, Types.ShineSiteDetailQueryVariables>(
    ShineSiteDetailDocument,
    variables,
    options,
  )

export const OrgShineDocument = new TypedDocumentString(`
    query OrgShine($orgSlug: String!, $duration: ShineDuration!) {
  orgBySlug(slug: $orgSlug) {
    name
    slug
    shine {
      status
      stats {
        ...ShineStats
      }
      units(duration: $duration) {
        ...ShineUnit
      }
      siteRefs {
        pid
        lat
        lon
        monthlyGrossRevenue
        status
      }
    }
  }
}
    fragment ShineStats on ShineStats {
  numProperties
  pvKwInstalled
  totalCo2ReducedTonnes
  totalGrossRevenue
  totalPvProductionKwh
}
fragment ShineUnit on ShineUnit {
  duration
  start
  actualPvProductionKwh
  expectedPvProductionKwh
  actualGrossRevenue
  expectedGrossRevenue
  co2EmissionsReductionTonnes
}`)

export const useOrgShineQuery = <TData = Types.OrgShineQuery, TError = unknown>(
  variables: Types.OrgShineQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgShineQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgShineQuery, TError, TData>({
    queryKey: ["OrgShine", variables],
    queryFn: publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
      OrgShineDocument,
      variables,
    ),
    ...options,
  })
}

useOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine",
  variables,
]

useOrgShineQuery.rootKey = "OrgShine"

export const useSuspenseOrgShineQuery = <TData = Types.OrgShineQuery, TError = unknown>(
  variables: Types.OrgShineQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgShineQuery, TError, TData>({
    queryKey: ["OrgShine", variables],
    queryFn: publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
      OrgShineDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine",
  variables,
]

useSuspenseOrgShineQuery.rootKey = "OrgShine"

export const useInfiniteOrgShineQuery = <
  TData = InfiniteData<Types.OrgShineQuery>,
  TError = unknown,
>(
  variables: Types.OrgShineQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgShineQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgShine.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
            OrgShineDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine.infinite",
  variables,
]

useInfiniteOrgShineQuery.rootKey = "OrgShine.infinite"

export const useSuspenseInfiniteOrgShineQuery = <
  TData = InfiniteData<Types.OrgShineQuery>,
  TError = unknown,
>(
  variables: Types.OrgShineQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgShineQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgShineQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgShine.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
            OrgShineDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine.infinite",
  variables,
]

useSuspenseInfiniteOrgShineQuery.rootKey = "OrgShine.infinite"

useOrgShineQuery.fetcher = (
  variables: Types.OrgShineQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
    OrgShineDocument,
    variables,
    options,
  )

export const ShineSiteDetailUnitsDocument = new TypedDocumentString(`
    query ShineSiteDetailUnits($orgSlug: String!, $sitePid: String!, $duration: ShineDuration!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $sitePid) {
      pid
      shine {
        units(duration: $duration) {
          ...ShineUnit
        }
      }
    }
  }
}
    fragment ShineUnit on ShineUnit {
  duration
  start
  actualPvProductionKwh
  expectedPvProductionKwh
  actualGrossRevenue
  expectedGrossRevenue
  co2EmissionsReductionTonnes
}`)

export const useShineSiteDetailUnitsQuery = <
  TData = Types.ShineSiteDetailUnitsQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>({
    queryKey: ["ShineSiteDetailUnits", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailUnitsQuery,
      Types.ShineSiteDetailUnitsQueryVariables
    >(ShineSiteDetailUnitsDocument, variables),
    ...options,
  })
}

useShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits", variables]

useShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits"

export const useSuspenseShineSiteDetailUnitsQuery = <
  TData = Types.ShineSiteDetailUnitsQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>({
    queryKey: ["ShineSiteDetailUnits", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailUnitsQuery,
      Types.ShineSiteDetailUnitsQueryVariables
    >(ShineSiteDetailUnitsDocument, variables),
    ...options,
  })
}

useSuspenseShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits", variables]

useSuspenseShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits"

export const useInfiniteShineSiteDetailUnitsQuery = <
  TData = InfiniteData<Types.ShineSiteDetailUnitsQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetailUnits.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailUnitsQuery,
            Types.ShineSiteDetailUnitsQueryVariables
          >(ShineSiteDetailUnitsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits.infinite", variables]

useInfiniteShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits.infinite"

export const useSuspenseInfiniteShineSiteDetailUnitsQuery = <
  TData = InfiniteData<Types.ShineSiteDetailUnitsQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetailUnits.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailUnitsQuery,
            Types.ShineSiteDetailUnitsQueryVariables
          >(ShineSiteDetailUnitsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits.infinite", variables]

useSuspenseInfiniteShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits.infinite"

useShineSiteDetailUnitsQuery.fetcher = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ShineSiteDetailUnitsQuery,
    Types.ShineSiteDetailUnitsQueryVariables
  >(ShineSiteDetailUnitsDocument, variables, options)

export const SiteSolarPanelVisualDocument = new TypedDocumentString(`
    query SiteSolarPanelVisual($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      visuals {
        ...SiteVisuals
      }
    }
  }
}
    fragment SiteVisuals on SiteVisuals {
  solarPanels {
    latitude
    longitude
    angle
  }
}`)

export const useSiteSolarPanelVisualQuery = <
  TData = Types.SiteSolarPanelVisualQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteSolarPanelVisualQuery, TError, TData>({
    queryKey: ["SiteSolarPanelVisual", variables],
    queryFn: publicFetcher<
      Types.SiteSolarPanelVisualQuery,
      Types.SiteSolarPanelVisualQueryVariables
    >(SiteSolarPanelVisualDocument, variables),
    ...options,
  })
}

useSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual", variables]

useSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual"

export const useSuspenseSiteSolarPanelVisualQuery = <
  TData = Types.SiteSolarPanelVisualQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteSolarPanelVisualQuery, TError, TData>({
    queryKey: ["SiteSolarPanelVisual", variables],
    queryFn: publicFetcher<
      Types.SiteSolarPanelVisualQuery,
      Types.SiteSolarPanelVisualQueryVariables
    >(SiteSolarPanelVisualDocument, variables),
    ...options,
  })
}

useSuspenseSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual", variables]

useSuspenseSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual"

export const useInfiniteSiteSolarPanelVisualQuery = <
  TData = InfiniteData<Types.SiteSolarPanelVisualQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteSolarPanelVisualQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteSolarPanelVisual.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarPanelVisualQuery,
            Types.SiteSolarPanelVisualQueryVariables
          >(SiteSolarPanelVisualDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual.infinite", variables]

useInfiniteSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual.infinite"

export const useSuspenseInfiniteSiteSolarPanelVisualQuery = <
  TData = InfiniteData<Types.SiteSolarPanelVisualQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteSolarPanelVisualQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteSolarPanelVisual.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarPanelVisualQuery,
            Types.SiteSolarPanelVisualQueryVariables
          >(SiteSolarPanelVisualDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual.infinite", variables]

useSuspenseInfiniteSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual.infinite"

useSiteSolarPanelVisualQuery.fetcher = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteSolarPanelVisualQuery,
    Types.SiteSolarPanelVisualQueryVariables
  >(SiteSolarPanelVisualDocument, variables, options)

export const CsvImportsListDocument = new TypedDocumentString(`
    query CsvImportsList($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    csvImports {
      all {
        pid
        fileName
        createdAt
        createdBy {
          email
        }
        portfolio {
          pid
          title
        }
        rowStats {
          total
          imported
        }
      }
    }
  }
}
    `)

export const useCsvImportsListQuery = <
  TData = Types.CsvImportsListQuery,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsvImportsListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsvImportsListQuery, TError, TData>({
    queryKey: ["CsvImportsList", variables],
    queryFn: publicFetcher<
      Types.CsvImportsListQuery,
      Types.CsvImportsListQueryVariables
    >(CsvImportsListDocument, variables),
    ...options,
  })
}

useCsvImportsListQuery.getKey = (variables: Types.CsvImportsListQueryVariables) => [
  "CsvImportsList",
  variables,
]

useCsvImportsListQuery.rootKey = "CsvImportsList"

export const useSuspenseCsvImportsListQuery = <
  TData = Types.CsvImportsListQuery,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsvImportsListQuery, TError, TData>({
    queryKey: ["CsvImportsList", variables],
    queryFn: publicFetcher<
      Types.CsvImportsListQuery,
      Types.CsvImportsListQueryVariables
    >(CsvImportsListDocument, variables),
    ...options,
  })
}

useSuspenseCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsList", variables]

useSuspenseCsvImportsListQuery.rootKey = "CsvImportsList"

export const useInfiniteCsvImportsListQuery = <
  TData = InfiniteData<Types.CsvImportsListQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsvImportsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportsList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
            CsvImportsListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsList.infinite", variables]

useInfiniteCsvImportsListQuery.rootKey = "CsvImportsList.infinite"

export const useSuspenseInfiniteCsvImportsListQuery = <
  TData = InfiniteData<Types.CsvImportsListQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsvImportsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportsList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
            CsvImportsListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsList.infinite", variables]

useSuspenseInfiniteCsvImportsListQuery.rootKey = "CsvImportsList.infinite"

useCsvImportsListQuery.fetcher = (
  variables: Types.CsvImportsListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
    CsvImportsListDocument,
    variables,
    options,
  )

export const SiteEvDocument = new TypedDocumentString(`
    query SiteEV($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $sitePid) {
      evCalc {
        ...BasicEVCalc
      }
      fullAddress
      yearBuilt
      numParkingSpaces
      buildingClass
      numUnitsTotal
    }
  }
}
    fragment BasicEVCalc on EVCalc {
  id
  createdAt
  projectInputs {
    projectStartDate
    installType
    termYears
    chargerKw
    yr0NumTenantEvs
    discountRate
  }
  outputs {
    irr
    npv
    yr0NumTenantEvs
    breakevenYear
    numChargersDemandedSeries
    cashflowSeries
    incentiveUsd
  }
  numChargers
  optimalNumChargers
  npv
  markupUsdPerKwh
  tariffRate {
    parent {
      name
      code
    }
  }
  utility {
    shortName
  }
  timeZone
  dayRates
  seasonalDayRates {
    name
    startMonth
    endMonth
    dayRates
  }
  downloadHourlyRatesHref
  xlsx
  downloadXlsxHref
  optIters {
    numChargers
    npv
  }
  yr3Revenue
  yr3Cashflow
  yr3NumEvs
  parkingRatio
  year3Expenses {
    label
    value
  }
  installCosts {
    label
    value
  }
}`)

export const useSiteEvQuery = <TData = Types.SiteEvQuery, TError = unknown>(
  variables: Types.SiteEvQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteEvQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEvQuery, TError, TData>({
    queryKey: ["SiteEV", variables],
    queryFn: publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
      SiteEvDocument,
      variables,
    ),
    ...options,
  })
}

useSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => ["SiteEV", variables]

useSiteEvQuery.rootKey = "SiteEV"

export const useSuspenseSiteEvQuery = <TData = Types.SiteEvQuery, TError = unknown>(
  variables: Types.SiteEvQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEvQuery, TError, TData>({
    queryKey: ["SiteEV", variables],
    queryFn: publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
      SiteEvDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEV",
  variables,
]

useSuspenseSiteEvQuery.rootKey = "SiteEV"

export const useInfiniteSiteEvQuery = <
  TData = InfiniteData<Types.SiteEvQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEV.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(SiteEvDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEV.infinite",
  variables,
]

useInfiniteSiteEvQuery.rootKey = "SiteEV.infinite"

export const useSuspenseInfiniteSiteEvQuery = <
  TData = InfiniteData<Types.SiteEvQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEV.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(SiteEvDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEV.infinite",
  variables,
]

useSuspenseInfiniteSiteEvQuery.rootKey = "SiteEV.infinite"

useSiteEvQuery.fetcher = (
  variables: Types.SiteEvQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
    SiteEvDocument,
    variables,
    options,
  )

export const SiteEvIncentivesDocument = new TypedDocumentString(`
    query SiteEvIncentives($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      evCalc {
        incentives {
          programName
          state
          url
          eligibleAreas
          incentiveAmount
          programConstraints
          incentiveCap
          calculation
        }
      }
    }
  }
}
    `)

export const useSiteEvIncentivesQuery = <
  TData = Types.SiteEvIncentivesQuery,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEvIncentivesQuery, TError, TData>({
    queryKey: ["SiteEvIncentives", variables],
    queryFn: publicFetcher<
      Types.SiteEvIncentivesQuery,
      Types.SiteEvIncentivesQueryVariables
    >(SiteEvIncentivesDocument, variables),
    ...options,
  })
}

useSiteEvIncentivesQuery.getKey = (variables: Types.SiteEvIncentivesQueryVariables) => [
  "SiteEvIncentives",
  variables,
]

useSiteEvIncentivesQuery.rootKey = "SiteEvIncentives"

export const useSuspenseSiteEvIncentivesQuery = <
  TData = Types.SiteEvIncentivesQuery,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEvIncentivesQuery, TError, TData>({
    queryKey: ["SiteEvIncentives", variables],
    queryFn: publicFetcher<
      Types.SiteEvIncentivesQuery,
      Types.SiteEvIncentivesQueryVariables
    >(SiteEvIncentivesDocument, variables),
    ...options,
  })
}

useSuspenseSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentives", variables]

useSuspenseSiteEvIncentivesQuery.rootKey = "SiteEvIncentives"

export const useInfiniteSiteEvIncentivesQuery = <
  TData = InfiniteData<Types.SiteEvIncentivesQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEvIncentivesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEvIncentives.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteEvIncentivesQuery,
            Types.SiteEvIncentivesQueryVariables
          >(SiteEvIncentivesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentives.infinite", variables]

useInfiniteSiteEvIncentivesQuery.rootKey = "SiteEvIncentives.infinite"

export const useSuspenseInfiniteSiteEvIncentivesQuery = <
  TData = InfiniteData<Types.SiteEvIncentivesQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEvIncentivesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEvIncentives.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteEvIncentivesQuery,
            Types.SiteEvIncentivesQueryVariables
          >(SiteEvIncentivesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentives.infinite", variables]

useSuspenseInfiniteSiteEvIncentivesQuery.rootKey = "SiteEvIncentives.infinite"

useSiteEvIncentivesQuery.fetcher = (
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEvIncentivesQuery, Types.SiteEvIncentivesQueryVariables>(
    SiteEvIncentivesDocument,
    variables,
    options,
  )

export const SiteRecalculatingDocument = new TypedDocumentString(`
    query SiteRecalculating($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid, includeResults: false) {
      isRecalculating
    }
  }
}
    `)

export const useSiteRecalculatingQuery = <
  TData = Types.SiteRecalculatingQuery,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteRecalculatingQuery, TError, TData>({
    queryKey: ["SiteRecalculating", variables],
    queryFn: publicFetcher<
      Types.SiteRecalculatingQuery,
      Types.SiteRecalculatingQueryVariables
    >(SiteRecalculatingDocument, variables),
    ...options,
  })
}

useSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating", variables]

useSiteRecalculatingQuery.rootKey = "SiteRecalculating"

export const useSuspenseSiteRecalculatingQuery = <
  TData = Types.SiteRecalculatingQuery,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteRecalculatingQuery, TError, TData>({
    queryKey: ["SiteRecalculating", variables],
    queryFn: publicFetcher<
      Types.SiteRecalculatingQuery,
      Types.SiteRecalculatingQueryVariables
    >(SiteRecalculatingDocument, variables),
    ...options,
  })
}

useSuspenseSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating", variables]

useSuspenseSiteRecalculatingQuery.rootKey = "SiteRecalculating"

export const useInfiniteSiteRecalculatingQuery = <
  TData = InfiniteData<Types.SiteRecalculatingQuery>,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteRecalculatingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteRecalculating.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteRecalculatingQuery,
            Types.SiteRecalculatingQueryVariables
          >(SiteRecalculatingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating.infinite", variables]

useInfiniteSiteRecalculatingQuery.rootKey = "SiteRecalculating.infinite"

export const useSuspenseInfiniteSiteRecalculatingQuery = <
  TData = InfiniteData<Types.SiteRecalculatingQuery>,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteRecalculatingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteRecalculating.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteRecalculatingQuery,
            Types.SiteRecalculatingQueryVariables
          >(SiteRecalculatingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating.infinite", variables]

useSuspenseInfiniteSiteRecalculatingQuery.rootKey = "SiteRecalculating.infinite"

useSiteRecalculatingQuery.fetcher = (
  variables: Types.SiteRecalculatingQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteRecalculatingQuery, Types.SiteRecalculatingQueryVariables>(
    SiteRecalculatingDocument,
    variables,
    options,
  )

export const ScenarioListDocument = new TypedDocumentString(`
    query ScenarioList($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      fullAddress
      scenarios {
        name
        pid
        solarLocation
        loadCategory
        includeStorage
      }
    }
  }
}
    `)

export const useScenarioListQuery = <TData = Types.ScenarioListQuery, TError = unknown>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ScenarioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioList", variables],
    queryFn: publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList",
  variables,
]

useScenarioListQuery.rootKey = "ScenarioList"

export const useSuspenseScenarioListQuery = <
  TData = Types.ScenarioListQuery,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioList", variables],
    queryFn: publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList",
  variables,
]

useSuspenseScenarioListQuery.rootKey = "ScenarioList"

export const useInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList.infinite",
  variables,
]

useInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

export const useSuspenseInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteScenarioListQuery.getKey = (
  variables: Types.ScenarioListQueryVariables,
) => ["ScenarioList.infinite", variables]

useSuspenseInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

useScenarioListQuery.fetcher = (
  variables: Types.ScenarioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
    ScenarioListDocument,
    variables,
    options,
  )

export const SiteFromPortfolioBuildingDataDocument = new TypedDocumentString(`
    query SiteFromPortfolioBuildingData($orgSlug: String!, $portfolioPid: String!, $buildingDataId: Int!) {
  orgBySlug(slug: $orgSlug) {
    siteFromPortfolioBuildingData(
      portfolioPid: $portfolioPid
      buildingDataId: $buildingDataId
    ) {
      __typename
      ... on SiteRef {
        pid
      }
    }
  }
}
    `)

export const useSiteFromPortfolioBuildingDataQuery = <
  TData = Types.SiteFromPortfolioBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>({
    queryKey: ["SiteFromPortfolioBuildingData", variables],
    queryFn: publicFetcher<
      Types.SiteFromPortfolioBuildingDataQuery,
      Types.SiteFromPortfolioBuildingDataQueryVariables
    >(SiteFromPortfolioBuildingDataDocument, variables),
    ...options,
  })
}

useSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData", variables]

useSiteFromPortfolioBuildingDataQuery.rootKey = "SiteFromPortfolioBuildingData"

export const useSuspenseSiteFromPortfolioBuildingDataQuery = <
  TData = Types.SiteFromPortfolioBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>({
    queryKey: ["SiteFromPortfolioBuildingData", variables],
    queryFn: publicFetcher<
      Types.SiteFromPortfolioBuildingDataQuery,
      Types.SiteFromPortfolioBuildingDataQueryVariables
    >(SiteFromPortfolioBuildingDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData", variables]

useSuspenseSiteFromPortfolioBuildingDataQuery.rootKey = "SiteFromPortfolioBuildingData"

export const useInfiniteSiteFromPortfolioBuildingDataQuery = <
  TData = InfiniteData<Types.SiteFromPortfolioBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteFromPortfolioBuildingData.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFromPortfolioBuildingDataQuery,
            Types.SiteFromPortfolioBuildingDataQueryVariables
          >(SiteFromPortfolioBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData.infinite", variables]

useInfiniteSiteFromPortfolioBuildingDataQuery.rootKey =
  "SiteFromPortfolioBuildingData.infinite"

export const useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery = <
  TData = InfiniteData<Types.SiteFromPortfolioBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.SiteFromPortfolioBuildingDataQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteFromPortfolioBuildingData.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFromPortfolioBuildingDataQuery,
            Types.SiteFromPortfolioBuildingDataQueryVariables
          >(SiteFromPortfolioBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData.infinite", variables]

useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery.rootKey =
  "SiteFromPortfolioBuildingData.infinite"

useSiteFromPortfolioBuildingDataQuery.fetcher = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteFromPortfolioBuildingDataQuery,
    Types.SiteFromPortfolioBuildingDataQueryVariables
  >(SiteFromPortfolioBuildingDataDocument, variables, options)

export const UtilityTariffsDocument = new TypedDocumentString(`
    query UtilityTariffs($utilityPid: String!) {
  tariffs2(utilityPid: $utilityPid) {
    ...PublicTariff
  }
}
    fragment PublicTariff on PublicTariff {
  id
  pid
  name
  code
  tariffType
  selectionStatus
  isNem2
  isClosed
  isPrimaryConnection
  customTariffId
  notes
  updatedAt
  trashedAt
  requiresPv
  requiresStorage
  hasTouRates
  hasNetMetering
  customerLikelihood
  customerCount
  customerClass
  minMonthlyKw
  maxMonthlyKw
  minMonthlyKwh
  maxMonthlyKwh
  latestRateId
}`)

export const useUtilityTariffsQuery = <
  TData = Types.UtilityTariffsQuery,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilityTariffsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityTariffsQuery, TError, TData>({
    queryKey: ["UtilityTariffs", variables],
    queryFn: publicFetcher<
      Types.UtilityTariffsQuery,
      Types.UtilityTariffsQueryVariables
    >(UtilityTariffsDocument, variables),
    ...options,
  })
}

useUtilityTariffsQuery.getKey = (variables: Types.UtilityTariffsQueryVariables) => [
  "UtilityTariffs",
  variables,
]

useUtilityTariffsQuery.rootKey = "UtilityTariffs"

export const useSuspenseUtilityTariffsQuery = <
  TData = Types.UtilityTariffsQuery,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityTariffsQuery, TError, TData>({
    queryKey: ["UtilityTariffs", variables],
    queryFn: publicFetcher<
      Types.UtilityTariffsQuery,
      Types.UtilityTariffsQueryVariables
    >(UtilityTariffsDocument, variables),
    ...options,
  })
}

useSuspenseUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffs", variables]

useSuspenseUtilityTariffsQuery.rootKey = "UtilityTariffs"

export const useInfiniteUtilityTariffsQuery = <
  TData = InfiniteData<Types.UtilityTariffsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
            UtilityTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffs.infinite", variables]

useInfiniteUtilityTariffsQuery.rootKey = "UtilityTariffs.infinite"

export const useSuspenseInfiniteUtilityTariffsQuery = <
  TData = InfiniteData<Types.UtilityTariffsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
            UtilityTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffs.infinite", variables]

useSuspenseInfiniteUtilityTariffsQuery.rootKey = "UtilityTariffs.infinite"

useUtilityTariffsQuery.fetcher = (
  variables: Types.UtilityTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
    UtilityTariffsDocument,
    variables,
    options,
  )

export const CustomTariffsDocument = new TypedDocumentString(`
    query CustomTariffs($sitePid: String!, $utilityPid: String!) {
  customTariffs(sitePid: $sitePid, utilityPid: $utilityPid) {
    ...PublicTariff
  }
}
    fragment PublicTariff on PublicTariff {
  id
  pid
  name
  code
  tariffType
  selectionStatus
  isNem2
  isClosed
  isPrimaryConnection
  customTariffId
  notes
  updatedAt
  trashedAt
  requiresPv
  requiresStorage
  hasTouRates
  hasNetMetering
  customerLikelihood
  customerCount
  customerClass
  minMonthlyKw
  maxMonthlyKw
  minMonthlyKwh
  maxMonthlyKwh
  latestRateId
}`)

export const useCustomTariffsQuery = <
  TData = Types.CustomTariffsQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffsQuery, TError, TData>({
    queryKey: ["CustomTariffs", variables],
    queryFn: publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
      CustomTariffsDocument,
      variables,
    ),
    ...options,
  })
}

useCustomTariffsQuery.getKey = (variables: Types.CustomTariffsQueryVariables) => [
  "CustomTariffs",
  variables,
]

useCustomTariffsQuery.rootKey = "CustomTariffs"

export const useSuspenseCustomTariffsQuery = <
  TData = Types.CustomTariffsQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffsQuery, TError, TData>({
    queryKey: ["CustomTariffs", variables],
    queryFn: publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
      CustomTariffsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffs", variables]

useSuspenseCustomTariffsQuery.rootKey = "CustomTariffs"

export const useInfiniteCustomTariffsQuery = <
  TData = InfiniteData<Types.CustomTariffsQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
            CustomTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffs.infinite", variables]

useInfiniteCustomTariffsQuery.rootKey = "CustomTariffs.infinite"

export const useSuspenseInfiniteCustomTariffsQuery = <
  TData = InfiniteData<Types.CustomTariffsQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
            CustomTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffs.infinite", variables]

useSuspenseInfiniteCustomTariffsQuery.rootKey = "CustomTariffs.infinite"

useCustomTariffsQuery.fetcher = (
  variables: Types.CustomTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
    CustomTariffsDocument,
    variables,
    options,
  )

export const CustomTariffDetailDocument = new TypedDocumentString(`
    query CustomTariffDetail($orgSlug: String!, $sitePid: String!, $tariffId: Int!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      customTariff(tariffId: $tariffId) {
        id
        name
        code
        customerClass
        ops {
          ...TariffOp
        }
        preOpDetails {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
        }
        postOpDetails {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
        }
      }
    }
  }
}
    fragment Rate on Rate {
  name
  groupName
  tariffRateId
  chargeClass
  chargeType
  transactionType
  chargePeriod
  applicabilityKey
  applicabilityValues
  quantityKey
  applicableBands {
    ...RateBand
  }
  bands {
    ...RateBand
  }
  tou {
    touName
    touType
    touPeriods {
      fromHour
      fromMinute
      fromDayOfWeek
      toHour
      toMinute
      toDayOfWeek
    }
    season {
      ...Season
    }
  }
  season {
    ...Season
  }
}
fragment RateBand on RateBand {
  tariffRateBandId
  rateUnit
  price
  isCredit
  applicabilityValue
}
fragment Season on Season {
  seasonFromDay
  seasonFromMonth
  seasonToDay
  seasonToMonth
}
fragment TariffOp on AddRateSubRateOverrideProperty {
  __typename
  ... on AddRate {
    rate {
      ...CustomRate
    }
    op
  }
  ... on SubRate {
    chargeClass
    rateId
    op
  }
  ... on OverrideProperty {
    keyName
    propertyValue
    op
  }
}
fragment CustomRate on CustomRate {
  name
  chargeClass
  chargeType
  transactionType
  price
  rateUnit
  chargePeriod
}`)

export const useCustomTariffDetailQuery = <
  TData = Types.CustomTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffDetailQuery, TError, TData>({
    queryKey: ["CustomTariffDetail", variables],
    queryFn: publicFetcher<
      Types.CustomTariffDetailQuery,
      Types.CustomTariffDetailQueryVariables
    >(CustomTariffDetailDocument, variables),
    ...options,
  })
}

useCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail", variables]

useCustomTariffDetailQuery.rootKey = "CustomTariffDetail"

export const useSuspenseCustomTariffDetailQuery = <
  TData = Types.CustomTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffDetailQuery, TError, TData>({
    queryKey: ["CustomTariffDetail", variables],
    queryFn: publicFetcher<
      Types.CustomTariffDetailQuery,
      Types.CustomTariffDetailQueryVariables
    >(CustomTariffDetailDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail", variables]

useSuspenseCustomTariffDetailQuery.rootKey = "CustomTariffDetail"

export const useInfiniteCustomTariffDetailQuery = <
  TData = InfiniteData<Types.CustomTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffDetailQuery,
            Types.CustomTariffDetailQueryVariables
          >(CustomTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail.infinite", variables]

useInfiniteCustomTariffDetailQuery.rootKey = "CustomTariffDetail.infinite"

export const useSuspenseInfiniteCustomTariffDetailQuery = <
  TData = InfiniteData<Types.CustomTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffDetailQuery,
            Types.CustomTariffDetailQueryVariables
          >(CustomTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail.infinite", variables]

useSuspenseInfiniteCustomTariffDetailQuery.rootKey = "CustomTariffDetail.infinite"

useCustomTariffDetailQuery.fetcher = (
  variables: Types.CustomTariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffDetailQuery, Types.CustomTariffDetailQueryVariables>(
    CustomTariffDetailDocument,
    variables,
    options,
  )

export const CustomTariffPreviewDocument = new TypedDocumentString(`
    query CustomTariffPreview($orgSlug: String!, $sitePid: String!, $tariffId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      customTariff(tariffId: $tariffId) {
        id
        name
        previewDetails(
          addRateOps: $addRateOps
          subRateOps: $subRateOps
          propertyOps: $propertyOps
        ) {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
          properties {
            keyName
            dataType
            propertyValue
            propertyValues
            formulaDetail
          }
        }
      }
    }
  }
}
    fragment Rate on Rate {
  name
  groupName
  tariffRateId
  chargeClass
  chargeType
  transactionType
  chargePeriod
  applicabilityKey
  applicabilityValues
  quantityKey
  applicableBands {
    ...RateBand
  }
  bands {
    ...RateBand
  }
  tou {
    touName
    touType
    touPeriods {
      fromHour
      fromMinute
      fromDayOfWeek
      toHour
      toMinute
      toDayOfWeek
    }
    season {
      ...Season
    }
  }
  season {
    ...Season
  }
}
fragment RateBand on RateBand {
  tariffRateBandId
  rateUnit
  price
  isCredit
  applicabilityValue
}
fragment Season on Season {
  seasonFromDay
  seasonFromMonth
  seasonToDay
  seasonToMonth
}`)

export const useCustomTariffPreviewQuery = <
  TData = Types.CustomTariffPreviewQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffPreviewQuery, TError, TData>({
    queryKey: ["CustomTariffPreview", variables],
    queryFn: publicFetcher<
      Types.CustomTariffPreviewQuery,
      Types.CustomTariffPreviewQueryVariables
    >(CustomTariffPreviewDocument, variables),
    ...options,
  })
}

useCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview", variables]

useCustomTariffPreviewQuery.rootKey = "CustomTariffPreview"

export const useSuspenseCustomTariffPreviewQuery = <
  TData = Types.CustomTariffPreviewQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffPreviewQuery, TError, TData>({
    queryKey: ["CustomTariffPreview", variables],
    queryFn: publicFetcher<
      Types.CustomTariffPreviewQuery,
      Types.CustomTariffPreviewQueryVariables
    >(CustomTariffPreviewDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview", variables]

useSuspenseCustomTariffPreviewQuery.rootKey = "CustomTariffPreview"

export const useInfiniteCustomTariffPreviewQuery = <
  TData = InfiniteData<Types.CustomTariffPreviewQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffPreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffPreviewQuery,
            Types.CustomTariffPreviewQueryVariables
          >(CustomTariffPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview.infinite", variables]

useInfiniteCustomTariffPreviewQuery.rootKey = "CustomTariffPreview.infinite"

export const useSuspenseInfiniteCustomTariffPreviewQuery = <
  TData = InfiniteData<Types.CustomTariffPreviewQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffPreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffPreviewQuery,
            Types.CustomTariffPreviewQueryVariables
          >(CustomTariffPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview.infinite", variables]

useSuspenseInfiniteCustomTariffPreviewQuery.rootKey = "CustomTariffPreview.infinite"

useCustomTariffPreviewQuery.fetcher = (
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CustomTariffPreviewQuery,
    Types.CustomTariffPreviewQueryVariables
  >(CustomTariffPreviewDocument, variables, options)

export const CustomTariffBillCalcDocument = new TypedDocumentString(`
    query CustomTariffBillCalc($sitePid: String!, $customTariffId: Int!, $bauMeterPid: String!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  customTariff(sitePid: $sitePid, customTariffId: $customTariffId) {
    billCalc(
      bauMeterPid: $bauMeterPid
      addRateOps: $addRateOps
      subRateOps: $subRateOps
      propertyOps: $propertyOps
    ) {
      start
      end
      summary {
        chargeType
        costUsd
      }
      details {
        group
        items {
          cost
          usage
          name
          rate
          unit
        }
      }
    }
  }
}
    `)

export const useCustomTariffBillCalcQuery = <
  TData = Types.CustomTariffBillCalcQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffBillCalcQuery, TError, TData>({
    queryKey: ["CustomTariffBillCalc", variables],
    queryFn: publicFetcher<
      Types.CustomTariffBillCalcQuery,
      Types.CustomTariffBillCalcQueryVariables
    >(CustomTariffBillCalcDocument, variables),
    ...options,
  })
}

useCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc", variables]

useCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc"

export const useSuspenseCustomTariffBillCalcQuery = <
  TData = Types.CustomTariffBillCalcQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffBillCalcQuery, TError, TData>({
    queryKey: ["CustomTariffBillCalc", variables],
    queryFn: publicFetcher<
      Types.CustomTariffBillCalcQuery,
      Types.CustomTariffBillCalcQueryVariables
    >(CustomTariffBillCalcDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc", variables]

useSuspenseCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc"

export const useInfiniteCustomTariffBillCalcQuery = <
  TData = InfiniteData<Types.CustomTariffBillCalcQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffBillCalcQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffBillCalc.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffBillCalcQuery,
            Types.CustomTariffBillCalcQueryVariables
          >(CustomTariffBillCalcDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc.infinite", variables]

useInfiniteCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc.infinite"

export const useSuspenseInfiniteCustomTariffBillCalcQuery = <
  TData = InfiniteData<Types.CustomTariffBillCalcQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffBillCalcQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffBillCalc.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffBillCalcQuery,
            Types.CustomTariffBillCalcQueryVariables
          >(CustomTariffBillCalcDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc.infinite", variables]

useSuspenseInfiniteCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc.infinite"

useCustomTariffBillCalcQuery.fetcher = (
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CustomTariffBillCalcQuery,
    Types.CustomTariffBillCalcQueryVariables
  >(CustomTariffBillCalcDocument, variables, options)

export const BundleParentsDocument = new TypedDocumentString(`
    query BundleParents($orgSlug: String!) {
  bundleParents(orgSlug: $orgSlug) {
    ...BasicBundleParent
  }
}
    fragment BasicBundleParent on BundleParent {
  id
  createdAt
  pid
  name
  type
  isExploratory
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  defaultRofoSigned
  hideAutoGeneratedPricingSummary
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  biddingOpenAt
  biddingClosedAt
  archivedAt
  salesOwner {
    ...UserRef
  }
  opsOwner {
    ...UserRef
  }
  recvr {
    pid
    name
  }
  canAddNewBids
  canEditBids
  allowDeveloperProjectNotesAndDocuments
  hiddenFromRecvr
  hiddenFromFinancier
  showAllBidsReport
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useBundleParentsQuery = <
  TData = Types.BundleParentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentsQuery, TError, TData>({
    queryKey: ["BundleParents", variables],
    queryFn: publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
      BundleParentsDocument,
      variables,
    ),
    ...options,
  })
}

useBundleParentsQuery.getKey = (variables: Types.BundleParentsQueryVariables) => [
  "BundleParents",
  variables,
]

useBundleParentsQuery.rootKey = "BundleParents"

export const useSuspenseBundleParentsQuery = <
  TData = Types.BundleParentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentsQuery, TError, TData>({
    queryKey: ["BundleParents", variables],
    queryFn: publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
      BundleParentsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParents", variables]

useSuspenseBundleParentsQuery.rootKey = "BundleParents"

export const useInfiniteBundleParentsQuery = <
  TData = InfiniteData<Types.BundleParentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParents.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
            BundleParentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParents.infinite", variables]

useInfiniteBundleParentsQuery.rootKey = "BundleParents.infinite"

export const useSuspenseInfiniteBundleParentsQuery = <
  TData = InfiniteData<Types.BundleParentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParents.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
            BundleParentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParents.infinite", variables]

useSuspenseInfiniteBundleParentsQuery.rootKey = "BundleParents.infinite"

useBundleParentsQuery.fetcher = (
  variables: Types.BundleParentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
    BundleParentsDocument,
    variables,
    options,
  )

export const BundleParentDetailDocument = new TypedDocumentString(`
    query BundleParentDetail($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    ...BasicBundleParent
    adminOnlyNote
    notionPageUrl
    bidRemindersSent
    lossNoticesSent
    hasBatterySpecs
  }
}
    fragment BasicBundleParent on BundleParent {
  id
  createdAt
  pid
  name
  type
  isExploratory
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  defaultRofoSigned
  hideAutoGeneratedPricingSummary
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  biddingOpenAt
  biddingClosedAt
  archivedAt
  salesOwner {
    ...UserRef
  }
  opsOwner {
    ...UserRef
  }
  recvr {
    pid
    name
  }
  canAddNewBids
  canEditBids
  allowDeveloperProjectNotesAndDocuments
  hiddenFromRecvr
  hiddenFromFinancier
  showAllBidsReport
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useBundleParentDetailQuery = <
  TData = Types.BundleParentDetailQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentDetailQuery, TError, TData>({
    queryKey: ["BundleParentDetail", variables],
    queryFn: publicFetcher<
      Types.BundleParentDetailQuery,
      Types.BundleParentDetailQueryVariables
    >(BundleParentDetailDocument, variables),
    ...options,
  })
}

useBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail", variables]

useBundleParentDetailQuery.rootKey = "BundleParentDetail"

export const useSuspenseBundleParentDetailQuery = <
  TData = Types.BundleParentDetailQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentDetailQuery, TError, TData>({
    queryKey: ["BundleParentDetail", variables],
    queryFn: publicFetcher<
      Types.BundleParentDetailQuery,
      Types.BundleParentDetailQueryVariables
    >(BundleParentDetailDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail", variables]

useSuspenseBundleParentDetailQuery.rootKey = "BundleParentDetail"

export const useInfiniteBundleParentDetailQuery = <
  TData = InfiniteData<Types.BundleParentDetailQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDetailQuery,
            Types.BundleParentDetailQueryVariables
          >(BundleParentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail.infinite", variables]

useInfiniteBundleParentDetailQuery.rootKey = "BundleParentDetail.infinite"

export const useSuspenseInfiniteBundleParentDetailQuery = <
  TData = InfiniteData<Types.BundleParentDetailQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDetailQuery,
            Types.BundleParentDetailQueryVariables
          >(BundleParentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail.infinite", variables]

useSuspenseInfiniteBundleParentDetailQuery.rootKey = "BundleParentDetail.infinite"

useBundleParentDetailQuery.fetcher = (
  variables: Types.BundleParentDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentDetailQuery, Types.BundleParentDetailQueryVariables>(
    BundleParentDetailDocument,
    variables,
    options,
  )

export const BundleParentDocumentsDocument = new TypedDocumentString(`
    query BundleParentDocuments($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    documents {
      id
      pid
      tag
      fileName
      contentType
      dateCreated
      downloadHref
      viewHref
      canDelete
      ownerName
    }
  }
}
    `)

export const useBundleParentDocumentsQuery = <
  TData = Types.BundleParentDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentDocumentsQuery, TError, TData>({
    queryKey: ["BundleParentDocuments", variables],
    queryFn: publicFetcher<
      Types.BundleParentDocumentsQuery,
      Types.BundleParentDocumentsQueryVariables
    >(BundleParentDocumentsDocument, variables),
    ...options,
  })
}

useBundleParentDocumentsQuery.getKey = (
  variables: Types.BundleParentDocumentsQueryVariables,
) => ["BundleParentDocuments", variables]

useBundleParentDocumentsQuery.rootKey = "BundleParentDocuments"

export const useSuspenseBundleParentDocumentsQuery = <
  TData = Types.BundleParentDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentDocumentsQuery, TError, TData>({
    queryKey: ["BundleParentDocuments", variables],
    queryFn: publicFetcher<
      Types.BundleParentDocumentsQuery,
      Types.BundleParentDocumentsQueryVariables
    >(BundleParentDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentDocumentsQuery.getKey = (
  variables: Types.BundleParentDocumentsQueryVariables,
) => ["BundleParentDocuments", variables]

useSuspenseBundleParentDocumentsQuery.rootKey = "BundleParentDocuments"

export const useInfiniteBundleParentDocumentsQuery = <
  TData = InfiniteData<Types.BundleParentDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDocumentsQuery,
            Types.BundleParentDocumentsQueryVariables
          >(BundleParentDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentDocumentsQuery.getKey = (
  variables: Types.BundleParentDocumentsQueryVariables,
) => ["BundleParentDocuments.infinite", variables]

useInfiniteBundleParentDocumentsQuery.rootKey = "BundleParentDocuments.infinite"

export const useSuspenseInfiniteBundleParentDocumentsQuery = <
  TData = InfiniteData<Types.BundleParentDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDocumentsQuery,
            Types.BundleParentDocumentsQueryVariables
          >(BundleParentDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentDocumentsQuery.getKey = (
  variables: Types.BundleParentDocumentsQueryVariables,
) => ["BundleParentDocuments.infinite", variables]

useSuspenseInfiniteBundleParentDocumentsQuery.rootKey = "BundleParentDocuments.infinite"

useBundleParentDocumentsQuery.fetcher = (
  variables: Types.BundleParentDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentDocumentsQuery,
    Types.BundleParentDocumentsQueryVariables
  >(BundleParentDocumentsDocument, variables, options)

export const BundleParentProjectDocumentsDocument = new TypedDocumentString(`
    query BundleParentProjectDocuments($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    projectDocuments {
      projectAssociations {
        project {
          pid
          name
        }
        viewableByDeveloper
        viewableByBuildingOwner
      }
      document {
        id
        pid
        tag
        fileName
        contentType
        dateCreated
        downloadHref
        canDelete
        ownerName
      }
    }
  }
}
    `)

export const useBundleParentProjectDocumentsQuery = <
  TData = Types.BundleParentProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentProjectDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentProjectDocumentsQuery, TError, TData>({
    queryKey: ["BundleParentProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.BundleParentProjectDocumentsQuery,
      Types.BundleParentProjectDocumentsQueryVariables
    >(BundleParentProjectDocumentsDocument, variables),
    ...options,
  })
}

useBundleParentProjectDocumentsQuery.getKey = (
  variables: Types.BundleParentProjectDocumentsQueryVariables,
) => ["BundleParentProjectDocuments", variables]

useBundleParentProjectDocumentsQuery.rootKey = "BundleParentProjectDocuments"

export const useSuspenseBundleParentProjectDocumentsQuery = <
  TData = Types.BundleParentProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentProjectDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentProjectDocumentsQuery, TError, TData>({
    queryKey: ["BundleParentProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.BundleParentProjectDocumentsQuery,
      Types.BundleParentProjectDocumentsQueryVariables
    >(BundleParentProjectDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentProjectDocumentsQuery.getKey = (
  variables: Types.BundleParentProjectDocumentsQueryVariables,
) => ["BundleParentProjectDocuments", variables]

useSuspenseBundleParentProjectDocumentsQuery.rootKey = "BundleParentProjectDocuments"

export const useInfiniteBundleParentProjectDocumentsQuery = <
  TData = InfiniteData<Types.BundleParentProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentProjectDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BundleParentProjectDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentProjectDocumentsQuery,
            Types.BundleParentProjectDocumentsQueryVariables
          >(BundleParentProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentProjectDocumentsQuery.getKey = (
  variables: Types.BundleParentProjectDocumentsQueryVariables,
) => ["BundleParentProjectDocuments.infinite", variables]

useInfiniteBundleParentProjectDocumentsQuery.rootKey =
  "BundleParentProjectDocuments.infinite"

export const useSuspenseInfiniteBundleParentProjectDocumentsQuery = <
  TData = InfiniteData<Types.BundleParentProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentProjectDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.BundleParentProjectDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.BundleParentProjectDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BundleParentProjectDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentProjectDocumentsQuery,
            Types.BundleParentProjectDocumentsQueryVariables
          >(BundleParentProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentProjectDocumentsQuery.getKey = (
  variables: Types.BundleParentProjectDocumentsQueryVariables,
) => ["BundleParentProjectDocuments.infinite", variables]

useSuspenseInfiniteBundleParentProjectDocumentsQuery.rootKey =
  "BundleParentProjectDocuments.infinite"

useBundleParentProjectDocumentsQuery.fetcher = (
  variables: Types.BundleParentProjectDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentProjectDocumentsQuery,
    Types.BundleParentProjectDocumentsQueryVariables
  >(BundleParentProjectDocumentsDocument, variables, options)

export const BundleParentDevelopersDocument = new TypedDocumentString(`
    query BundleParentDevelopers($orgSlug: String!, $pid: String!) {
  bundleParentDevelopers(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    bundles {
      ...BundleListItem
      lastViewed
      rows {
        optOut {
          createdAt
        }
      }
    }
  }
}
    fragment BundleListItem on Bundle {
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  archivedAt
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useBundleParentDevelopersQuery = <
  TData = Types.BundleParentDevelopersQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDevelopersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentDevelopersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentDevelopersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentDevelopersQuery, TError, TData>({
    queryKey: ["BundleParentDevelopers", variables],
    queryFn: publicFetcher<
      Types.BundleParentDevelopersQuery,
      Types.BundleParentDevelopersQueryVariables
    >(BundleParentDevelopersDocument, variables),
    ...options,
  })
}

useBundleParentDevelopersQuery.getKey = (
  variables: Types.BundleParentDevelopersQueryVariables,
) => ["BundleParentDevelopers", variables]

useBundleParentDevelopersQuery.rootKey = "BundleParentDevelopers"

export const useSuspenseBundleParentDevelopersQuery = <
  TData = Types.BundleParentDevelopersQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDevelopersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentDevelopersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentDevelopersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentDevelopersQuery, TError, TData>({
    queryKey: ["BundleParentDevelopers", variables],
    queryFn: publicFetcher<
      Types.BundleParentDevelopersQuery,
      Types.BundleParentDevelopersQueryVariables
    >(BundleParentDevelopersDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentDevelopersQuery.getKey = (
  variables: Types.BundleParentDevelopersQueryVariables,
) => ["BundleParentDevelopers", variables]

useSuspenseBundleParentDevelopersQuery.rootKey = "BundleParentDevelopers"

export const useInfiniteBundleParentDevelopersQuery = <
  TData = InfiniteData<Types.BundleParentDevelopersQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDevelopersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentDevelopersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentDevelopersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentDevelopersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDevelopers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDevelopersQuery,
            Types.BundleParentDevelopersQueryVariables
          >(BundleParentDevelopersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentDevelopersQuery.getKey = (
  variables: Types.BundleParentDevelopersQueryVariables,
) => ["BundleParentDevelopers.infinite", variables]

useInfiniteBundleParentDevelopersQuery.rootKey = "BundleParentDevelopers.infinite"

export const useSuspenseInfiniteBundleParentDevelopersQuery = <
  TData = InfiniteData<Types.BundleParentDevelopersQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDevelopersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentDevelopersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentDevelopersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentDevelopersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDevelopers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDevelopersQuery,
            Types.BundleParentDevelopersQueryVariables
          >(BundleParentDevelopersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentDevelopersQuery.getKey = (
  variables: Types.BundleParentDevelopersQueryVariables,
) => ["BundleParentDevelopers.infinite", variables]

useSuspenseInfiniteBundleParentDevelopersQuery.rootKey =
  "BundleParentDevelopers.infinite"

useBundleParentDevelopersQuery.fetcher = (
  variables: Types.BundleParentDevelopersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentDevelopersQuery,
    Types.BundleParentDevelopersQueryVariables
  >(BundleParentDevelopersDocument, variables, options)

export const BundleParentAwardBidsDocument = new TypedDocumentString(`
    query BundleParentAwardBids($orgSlug: String!, $pid: String!) {
  bundleParentDevelopers(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    bundles {
      ...BundleListItem
      lastViewed
      rows {
        optOut {
          createdAt
        }
        projectPid
        site {
          pid
        }
        bids {
          ppa {
            pid
            name
            ppaPriceY1Usd
            roofRentY1Usd
            ppaPriceEscalator
            roofRentEscalator
            buildingOwnerExtraPaymentsUsd
            termYears
            roofReplacementYear
            itcPercentageAssumption
            interconnectionCostUsdPerWatt
            interconnectionCostUtilityMinUsd
            interconnectionCostUtilityMaxUsd
            interconnectionCostNonUtilityUsd
            estimatedIxApproval
            itcAdders
            itcObbbStrategy
            itcObbbCustomStrategy
            recOwner
            recValueUsdPerMwh
            system {
              pvSpecs {
                id
                pvTypes
                sizeKwDc
                sizeKwAc
              }
              batterySpecs {
                id
                sizeKwh
                sizeKwDc
              }
            }
          }
          cs {
            pid
            name
            roofRentY1Usd
            roofRentEscalator
            buildingOwnerExtraPaymentsUsd
            termYears
            roofReplacementYear
            itcPercentageAssumption
            interconnectionCostUsdPerWatt
            interconnectionCostUtilityMinUsd
            interconnectionCostUtilityMaxUsd
            interconnectionCostNonUtilityUsd
            estimatedIxApproval
            itcAdders
            itcObbbStrategy
            itcObbbCustomStrategy
            recOwner
            recValueUsdPerMwh
            subscriberDiscountPct
            ftmProgram {
              id
              name
            }
            systemLite {
              pvSpecs {
                id
                pvTypes
                sizeKwDc
                sizeKwAc
              }
              batterySpecs {
                id
                sizeKwh
                sizeKwDc
              }
            }
          }
          cash {
            pid
            name
            itcPercentageAssumption
            interconnectionCostUsdPerWatt
            interconnectionCostUtilityMinUsd
            interconnectionCostUtilityMaxUsd
            interconnectionCostNonUtilityUsd
            estimatedIxApproval
            itcAdders
            itcObbbStrategy
            itcObbbCustomStrategy
            recValueUsdPerMwh
            totalPvCostUsd
            totalStorageCostUsd
            system {
              pvSpecs {
                id
                pvTypes
                sizeKwDc
                sizeKwAc
              }
              batterySpecs {
                id
                sizeKwh
                sizeKwDc
              }
            }
          }
        }
      }
    }
  }
}
    fragment BundleListItem on Bundle {
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  archivedAt
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useBundleParentAwardBidsQuery = <
  TData = Types.BundleParentAwardBidsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentAwardBidsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentAwardBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentAwardBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentAwardBidsQuery, TError, TData>({
    queryKey: ["BundleParentAwardBids", variables],
    queryFn: publicFetcher<
      Types.BundleParentAwardBidsQuery,
      Types.BundleParentAwardBidsQueryVariables
    >(BundleParentAwardBidsDocument, variables),
    ...options,
  })
}

useBundleParentAwardBidsQuery.getKey = (
  variables: Types.BundleParentAwardBidsQueryVariables,
) => ["BundleParentAwardBids", variables]

useBundleParentAwardBidsQuery.rootKey = "BundleParentAwardBids"

export const useSuspenseBundleParentAwardBidsQuery = <
  TData = Types.BundleParentAwardBidsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentAwardBidsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentAwardBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentAwardBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentAwardBidsQuery, TError, TData>({
    queryKey: ["BundleParentAwardBids", variables],
    queryFn: publicFetcher<
      Types.BundleParentAwardBidsQuery,
      Types.BundleParentAwardBidsQueryVariables
    >(BundleParentAwardBidsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentAwardBidsQuery.getKey = (
  variables: Types.BundleParentAwardBidsQueryVariables,
) => ["BundleParentAwardBids", variables]

useSuspenseBundleParentAwardBidsQuery.rootKey = "BundleParentAwardBids"

export const useInfiniteBundleParentAwardBidsQuery = <
  TData = InfiniteData<Types.BundleParentAwardBidsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentAwardBidsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentAwardBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentAwardBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentAwardBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentAwardBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentAwardBidsQuery,
            Types.BundleParentAwardBidsQueryVariables
          >(BundleParentAwardBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentAwardBidsQuery.getKey = (
  variables: Types.BundleParentAwardBidsQueryVariables,
) => ["BundleParentAwardBids.infinite", variables]

useInfiniteBundleParentAwardBidsQuery.rootKey = "BundleParentAwardBids.infinite"

export const useSuspenseInfiniteBundleParentAwardBidsQuery = <
  TData = InfiniteData<Types.BundleParentAwardBidsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentAwardBidsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentAwardBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentAwardBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentAwardBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentAwardBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentAwardBidsQuery,
            Types.BundleParentAwardBidsQueryVariables
          >(BundleParentAwardBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentAwardBidsQuery.getKey = (
  variables: Types.BundleParentAwardBidsQueryVariables,
) => ["BundleParentAwardBids.infinite", variables]

useSuspenseInfiniteBundleParentAwardBidsQuery.rootKey = "BundleParentAwardBids.infinite"

useBundleParentAwardBidsQuery.fetcher = (
  variables: Types.BundleParentAwardBidsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentAwardBidsQuery,
    Types.BundleParentAwardBidsQueryVariables
  >(BundleParentAwardBidsDocument, variables, options)

export const BundleParentPropertiesDocument = new TypedDocumentString(`
    query BundleParentProperties($orgSlug: String!, $pid: String!) {
  bundleParentProperties(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    type
    rows {
      ...BundleParentPropertyRow
    }
  }
}
    fragment BundleParentPropertyRow on BundleParentRow {
  specs {
    ...BidSpec
  }
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
  }
  siteProjects {
    pid
    name
    finProductType
  }
  notes
  status
  closedAt
  closeReason
  closeCustomReason
  salesOwner {
    pid
  }
  opsOwner {
    pid
  }
  awardedFinancier {
    pid
    name
    slug
    logoUrl
    logomarkUrl
    requiresChangeOrders
  }
  recommendedBid {
    ppa {
      pid
    }
    cs {
      pid
    }
  }
  energyProject {
    pid
    name
    status
    closedAt
    archivedAt
    changeOrderSent
    scopeOfWorkSent
    hostAwardSent
    lumenFeeUsdPerWatt
    lumenFeeUsdPerKwhStorage
    hubspotRecordId
    milestoneSummary {
      rofoSignedActual
      rofoSignedEstimated
      leaseSignedEstimated
      ixApprovedEstimated
      csProgramApprovedEstimated
      ntpEstimated
      codEstimated
    }
    projectSpecification {
      ppa {
        roofRentY1Usd
      }
      cs {
        roofRentY1Usd
      }
      systemSummary {
        pvSpecs {
          pvTypes
          sizeKwDc
          sizeKwAc
          helioscope2 {
            ...Helioscope2
          }
        }
        batterySpecs {
          sizeKwh
          sizeKwDc
        }
      }
    }
  }
}
fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment Helioscope2 on Helioscope2 {
  pid
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  groundPvKw
  dcNameplateKw
  acNameplateKw
  annualProductionAcKwh
  helioscopeKw
  pdfHref
  roofImageHref
  roofImageName
  roofImage {
    id
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useBundleParentPropertiesQuery = <
  TData = Types.BundleParentPropertiesQuery,
  TError = unknown,
>(
  variables: Types.BundleParentPropertiesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentPropertiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentPropertiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentPropertiesQuery, TError, TData>({
    queryKey: ["BundleParentProperties", variables],
    queryFn: publicFetcher<
      Types.BundleParentPropertiesQuery,
      Types.BundleParentPropertiesQueryVariables
    >(BundleParentPropertiesDocument, variables),
    ...options,
  })
}

useBundleParentPropertiesQuery.getKey = (
  variables: Types.BundleParentPropertiesQueryVariables,
) => ["BundleParentProperties", variables]

useBundleParentPropertiesQuery.rootKey = "BundleParentProperties"

export const useSuspenseBundleParentPropertiesQuery = <
  TData = Types.BundleParentPropertiesQuery,
  TError = unknown,
>(
  variables: Types.BundleParentPropertiesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentPropertiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentPropertiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentPropertiesQuery, TError, TData>({
    queryKey: ["BundleParentProperties", variables],
    queryFn: publicFetcher<
      Types.BundleParentPropertiesQuery,
      Types.BundleParentPropertiesQueryVariables
    >(BundleParentPropertiesDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentPropertiesQuery.getKey = (
  variables: Types.BundleParentPropertiesQueryVariables,
) => ["BundleParentProperties", variables]

useSuspenseBundleParentPropertiesQuery.rootKey = "BundleParentProperties"

export const useInfiniteBundleParentPropertiesQuery = <
  TData = InfiniteData<Types.BundleParentPropertiesQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentPropertiesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentPropertiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentPropertiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentPropertiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentProperties.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentPropertiesQuery,
            Types.BundleParentPropertiesQueryVariables
          >(BundleParentPropertiesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentPropertiesQuery.getKey = (
  variables: Types.BundleParentPropertiesQueryVariables,
) => ["BundleParentProperties.infinite", variables]

useInfiniteBundleParentPropertiesQuery.rootKey = "BundleParentProperties.infinite"

export const useSuspenseInfiniteBundleParentPropertiesQuery = <
  TData = InfiniteData<Types.BundleParentPropertiesQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentPropertiesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentPropertiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentPropertiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentPropertiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentProperties.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentPropertiesQuery,
            Types.BundleParentPropertiesQueryVariables
          >(BundleParentPropertiesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentPropertiesQuery.getKey = (
  variables: Types.BundleParentPropertiesQueryVariables,
) => ["BundleParentProperties.infinite", variables]

useSuspenseInfiniteBundleParentPropertiesQuery.rootKey =
  "BundleParentProperties.infinite"

useBundleParentPropertiesQuery.fetcher = (
  variables: Types.BundleParentPropertiesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentPropertiesQuery,
    Types.BundleParentPropertiesQueryVariables
  >(BundleParentPropertiesDocument, variables, options)

export const BundleSitesDocument = new TypedDocumentString(`
    query BundleSites($financierSlug: String!, $bundlePid: String!) {
  bundleSites(financierSlug: $financierSlug, bundlePid: $bundlePid) {
    pid
    name
    type
    archivedAt
    financier {
      pid
      name
      orgSlug
    }
    rows {
      projectPid
      site {
        ...RevampSiteBasics
      }
    }
  }
}
    fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`)

export const useBundleSitesQuery = <TData = Types.BundleSitesQuery, TError = unknown>(
  variables: Types.BundleSitesQueryVariables,
  options?: Omit<UseQueryOptions<Types.BundleSitesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.BundleSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleSitesQuery, TError, TData>({
    queryKey: ["BundleSites", variables],
    queryFn: publicFetcher<Types.BundleSitesQuery, Types.BundleSitesQueryVariables>(
      BundleSitesDocument,
      variables,
    ),
    ...options,
  })
}

useBundleSitesQuery.getKey = (variables: Types.BundleSitesQueryVariables) => [
  "BundleSites",
  variables,
]

useBundleSitesQuery.rootKey = "BundleSites"

export const useSuspenseBundleSitesQuery = <
  TData = Types.BundleSitesQuery,
  TError = unknown,
>(
  variables: Types.BundleSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleSitesQuery, TError, TData>({
    queryKey: ["BundleSites", variables],
    queryFn: publicFetcher<Types.BundleSitesQuery, Types.BundleSitesQueryVariables>(
      BundleSitesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBundleSitesQuery.getKey = (variables: Types.BundleSitesQueryVariables) => [
  "BundleSites",
  variables,
]

useSuspenseBundleSitesQuery.rootKey = "BundleSites"

export const useInfiniteBundleSitesQuery = <
  TData = InfiniteData<Types.BundleSitesQuery>,
  TError = unknown,
>(
  variables: Types.BundleSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleSitesQuery, Types.BundleSitesQueryVariables>(
            BundleSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleSitesQuery.getKey = (variables: Types.BundleSitesQueryVariables) => [
  "BundleSites.infinite",
  variables,
]

useInfiniteBundleSitesQuery.rootKey = "BundleSites.infinite"

export const useSuspenseInfiniteBundleSitesQuery = <
  TData = InfiniteData<Types.BundleSitesQuery>,
  TError = unknown,
>(
  variables: Types.BundleSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleSitesQuery, Types.BundleSitesQueryVariables>(
            BundleSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleSitesQuery.getKey = (
  variables: Types.BundleSitesQueryVariables,
) => ["BundleSites.infinite", variables]

useSuspenseInfiniteBundleSitesQuery.rootKey = "BundleSites.infinite"

useBundleSitesQuery.fetcher = (
  variables: Types.BundleSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleSitesQuery, Types.BundleSitesQueryVariables>(
    BundleSitesDocument,
    variables,
    options,
  )

export const BundleParentReportsDocument = new TypedDocumentString(`
    query BundleParentReports($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    type
    customReports {
      ...CustomBidReport
    }
  }
}
    fragment CustomBidReport on CustomBidReport {
  pid
  name
  notes
  buildingOwnerNotes
  visibleToDevelopers
  visibleToBuildingOwners
  bids {
    ppa {
      pid
      financier {
        orgSlug
      }
    }
    cs {
      pid
      financier {
        orgSlug
      }
    }
  }
}`)

export const useBundleParentReportsQuery = <
  TData = Types.BundleParentReportsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentReportsQuery, TError, TData>({
    queryKey: ["BundleParentReports", variables],
    queryFn: publicFetcher<
      Types.BundleParentReportsQuery,
      Types.BundleParentReportsQueryVariables
    >(BundleParentReportsDocument, variables),
    ...options,
  })
}

useBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports", variables]

useBundleParentReportsQuery.rootKey = "BundleParentReports"

export const useSuspenseBundleParentReportsQuery = <
  TData = Types.BundleParentReportsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentReportsQuery, TError, TData>({
    queryKey: ["BundleParentReports", variables],
    queryFn: publicFetcher<
      Types.BundleParentReportsQuery,
      Types.BundleParentReportsQueryVariables
    >(BundleParentReportsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports", variables]

useSuspenseBundleParentReportsQuery.rootKey = "BundleParentReports"

export const useInfiniteBundleParentReportsQuery = <
  TData = InfiniteData<Types.BundleParentReportsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentReportsQuery,
            Types.BundleParentReportsQueryVariables
          >(BundleParentReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports.infinite", variables]

useInfiniteBundleParentReportsQuery.rootKey = "BundleParentReports.infinite"

export const useSuspenseInfiniteBundleParentReportsQuery = <
  TData = InfiniteData<Types.BundleParentReportsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentReportsQuery,
            Types.BundleParentReportsQueryVariables
          >(BundleParentReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports.infinite", variables]

useSuspenseInfiniteBundleParentReportsQuery.rootKey = "BundleParentReports.infinite"

useBundleParentReportsQuery.fetcher = (
  variables: Types.BundleParentReportsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentReportsQuery,
    Types.BundleParentReportsQueryVariables
  >(BundleParentReportsDocument, variables, options)

export const BidReportsDocument = new TypedDocumentString(`
    query BidReports {
  bidReports {
    pid
    name
    notes
    buildingOwnerNotes
    parent {
      pid
    }
    bids {
      ppa {
        pid
      }
      cs {
        pid
      }
    }
  }
}
    `)

export const useBidReportsQuery = <TData = Types.BidReportsQuery, TError = unknown>(
  variables?: Types.BidReportsQueryVariables,
  options?: Omit<UseQueryOptions<Types.BidReportsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidReportsQuery, TError, TData>({
    queryKey: variables === undefined ? ["BidReports"] : ["BidReports", variables],
    queryFn: publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
      BidReportsDocument,
      variables,
    ),
    ...options,
  })
}

useBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReports"] : ["BidReports", variables]

useBidReportsQuery.rootKey = "BidReports"

export const useSuspenseBidReportsQuery = <
  TData = Types.BidReportsQuery,
  TError = unknown,
>(
  variables?: Types.BidReportsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidReportsQuery, TError, TData>({
    queryKey: variables === undefined ? ["BidReports"] : ["BidReports", variables],
    queryFn: publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
      BidReportsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReports"] : ["BidReports", variables]

useSuspenseBidReportsQuery.rootKey = "BidReports"

export const useInfiniteBidReportsQuery = <
  TData = InfiniteData<Types.BidReportsQuery>,
  TError = unknown,
>(
  variables: Types.BidReportsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidReports.infinite"]
            : ["BidReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
            BidReportsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReports.infinite"] : ["BidReports.infinite", variables]

useInfiniteBidReportsQuery.rootKey = "BidReports.infinite"

export const useSuspenseInfiniteBidReportsQuery = <
  TData = InfiniteData<Types.BidReportsQuery>,
  TError = unknown,
>(
  variables: Types.BidReportsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidReports.infinite"]
            : ["BidReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
            BidReportsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidReportsQuery.getKey = (
  variables?: Types.BidReportsQueryVariables,
) =>
  variables === undefined ? ["BidReports.infinite"] : ["BidReports.infinite", variables]

useSuspenseInfiniteBidReportsQuery.rootKey = "BidReports.infinite"

useBidReportsQuery.fetcher = (
  variables?: Types.BidReportsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
    BidReportsDocument,
    variables,
    options,
  )

export const EvOverridesDocument = new TypedDocumentString(`
    query EVOverrides($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      evOverrides {
        current {
          chargerKw
          chargerLocation
          cmsUsdPerChargerPerYr
          demandUsdPerKw
          discountRate
          evGrowthPct
          highEnergyUsdPerKwh
          incentivePct
          installType
          lowEnergyUsdPerKwh
          maxUtilization
          milesDrivenPerYear
          milesPerKwh
          numChargers
          omUsdPerChargerPerYr
          onPeakChargingPct
          onSiteChargingPct
          projectStartDate
          rateManagementUsdPerChargerPerYr
          salesTaxPct
          termYears
          transactionFeePct
          usdPerKwhMarkup
          yr0NumTenantEvs
          incentiveCapNumChargers
          maxEvAdoptionPct
          installCostUsd
        }
        defaults {
          chargerKw
          chargerLocation
          cmsUsdPerChargerPerYr
          demandUsdPerKw
          discountRate
          evGrowthPct
          highEnergyUsdPerKwh
          incentivePct
          installType
          lowEnergyUsdPerKwh
          maxUtilization
          milesDrivenPerYear
          milesPerKwh
          numChargers
          omUsdPerChargerPerYr
          onPeakChargingPct
          onSiteChargingPct
          projectStartDate
          rateManagementUsdPerChargerPerYr
          salesTaxPct
          termYears
          transactionFeePct
          usdPerKwhMarkup
          yr0NumTenantEvs
          incentiveCapNumChargers
          maxEvAdoptionPct
          installCostUsd
        }
      }
    }
  }
}
    `)

export const useEvOverridesQuery = <TData = Types.EvOverridesQuery, TError = unknown>(
  variables: Types.EvOverridesQueryVariables,
  options?: Omit<UseQueryOptions<Types.EvOverridesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EvOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EvOverridesQuery, TError, TData>({
    queryKey: ["EVOverrides", variables],
    queryFn: publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
      EvOverridesDocument,
      variables,
    ),
    ...options,
  })
}

useEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverrides",
  variables,
]

useEvOverridesQuery.rootKey = "EVOverrides"

export const useSuspenseEvOverridesQuery = <
  TData = Types.EvOverridesQuery,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EvOverridesQuery, TError, TData>({
    queryKey: ["EVOverrides", variables],
    queryFn: publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
      EvOverridesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverrides",
  variables,
]

useSuspenseEvOverridesQuery.rootKey = "EVOverrides"

export const useInfiniteEvOverridesQuery = <
  TData = InfiniteData<Types.EvOverridesQuery>,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EvOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EVOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
            EvOverridesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverrides.infinite",
  variables,
]

useInfiniteEvOverridesQuery.rootKey = "EVOverrides.infinite"

export const useSuspenseInfiniteEvOverridesQuery = <
  TData = InfiniteData<Types.EvOverridesQuery>,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EvOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EVOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
            EvOverridesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEvOverridesQuery.getKey = (
  variables: Types.EvOverridesQueryVariables,
) => ["EVOverrides.infinite", variables]

useSuspenseInfiniteEvOverridesQuery.rootKey = "EVOverrides.infinite"

useEvOverridesQuery.fetcher = (
  variables: Types.EvOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
    EvOverridesDocument,
    variables,
    options,
  )

export const InvestorBundleListDocument = new TypedDocumentString(`
    query InvestorBundleList($orgSlug: String!) {
  bundlesForFinancier(slug: $orgSlug) {
    ...BundleListItem
  }
}
    fragment BundleListItem on Bundle {
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  archivedAt
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useInvestorBundleListQuery = <
  TData = Types.InvestorBundleListQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.InvestorBundleListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleListQuery, TError, TData>({
    queryKey: ["InvestorBundleList", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleListQuery,
      Types.InvestorBundleListQueryVariables
    >(InvestorBundleListDocument, variables),
    ...options,
  })
}

useInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList", variables]

useInvestorBundleListQuery.rootKey = "InvestorBundleList"

export const useSuspenseInvestorBundleListQuery = <
  TData = Types.InvestorBundleListQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleListQuery, TError, TData>({
    queryKey: ["InvestorBundleList", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleListQuery,
      Types.InvestorBundleListQueryVariables
    >(InvestorBundleListDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList", variables]

useSuspenseInvestorBundleListQuery.rootKey = "InvestorBundleList"

export const useInfiniteInvestorBundleListQuery = <
  TData = InfiniteData<Types.InvestorBundleListQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleListQuery,
            Types.InvestorBundleListQueryVariables
          >(InvestorBundleListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList.infinite", variables]

useInfiniteInvestorBundleListQuery.rootKey = "InvestorBundleList.infinite"

export const useSuspenseInfiniteInvestorBundleListQuery = <
  TData = InfiniteData<Types.InvestorBundleListQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleListQuery,
            Types.InvestorBundleListQueryVariables
          >(InvestorBundleListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList.infinite", variables]

useSuspenseInfiniteInvestorBundleListQuery.rootKey = "InvestorBundleList.infinite"

useInvestorBundleListQuery.fetcher = (
  variables: Types.InvestorBundleListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InvestorBundleListQuery, Types.InvestorBundleListQueryVariables>(
    InvestorBundleListDocument,
    variables,
    options,
  )

export const InvestorBundleDetailDocument = new TypedDocumentString(`
    query InvestorBundleDetail($orgSlug: String!, $bundlePid: String!) {
  bundleDetail(slug: $orgSlug, bundlePid: $bundlePid) {
    ...BundleDetail
  }
}
    fragment BundleListItem on Bundle {
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  revenueMilestones {
    milestoneType
    percentage
  }
  state
  archivedAt
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
fragment BundleDetail on Bundle {
  ...BundleListItem
  intendsToBid
  opsOwner {
    ...UserRef
    meetingSchedulingUrl
  }
  parentRef {
    pid
  }
  documents {
    canDelete
    contentType
    dateCreated
    fileName
    ownerName
    pid
    tag
    downloadHref
    viewHref
    belongsToBundleParent
    investorBid {
      pid
      name
    }
    ownerOrg {
      orgSlug
    }
  }
  downloadBundleDocsZipHref
  downloadHelioscopesDotZipHref
  downloadBundleDotXlsxHref
  rows {
    site {
      pid
      state
    }
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useInvestorBundleDetailQuery = <
  TData = Types.InvestorBundleDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleDetailQuery,
      Types.InvestorBundleDetailQueryVariables
    >(InvestorBundleDetailDocument, variables),
    ...options,
  })
}

useInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail", variables]

useInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail"

export const useSuspenseInvestorBundleDetailQuery = <
  TData = Types.InvestorBundleDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleDetailQuery,
      Types.InvestorBundleDetailQueryVariables
    >(InvestorBundleDetailDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail", variables]

useSuspenseInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail"

export const useInfiniteInvestorBundleDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleDetailQuery,
            Types.InvestorBundleDetailQueryVariables
          >(InvestorBundleDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail.infinite", variables]

useInfiniteInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail.infinite"

export const useSuspenseInfiniteInvestorBundleDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleDetailQuery,
            Types.InvestorBundleDetailQueryVariables
          >(InvestorBundleDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail.infinite", variables]

useSuspenseInfiniteInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail.infinite"

useInvestorBundleDetailQuery.fetcher = (
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InvestorBundleDetailQuery,
    Types.InvestorBundleDetailQueryVariables
  >(InvestorBundleDetailDocument, variables, options)

export const InvestorBundleBidsDocument = new TypedDocumentString(`
    query InvestorBundleBids($orgSlug: String!, $bundlePid: String!) {
  bundleBids(slug: $orgSlug, bundlePid: $bundlePid) {
    ...BundleBids
  }
}
    fragment BundleBids on Bundle {
  pid
  pricingNote
  shortPricingNote
  defaultLumenFeeUsdPerWatt
  defaultLumenFeeUsdPerKwhStorage
  hideAutoGeneratedPricingSummary
  hasBatterySpecs
  allowDeveloperProjectNotesAndDocuments
  revenueMilestones {
    milestoneType
    percentage
  }
  type
  recvr {
    name
  }
  optOut {
    ...InvestorOptOut
  }
  rows {
    ...BundleDetailRow
    pid
    developerNotesContent
    developerDocuments {
      ...Document
      canDelete
    }
    site {
      pid
      name
      fullAddress
      streetAddress
      city
      state
      zipCode
      county
      cbsaName
      buildingData {
        ownerName
      }
      buildingType
      siteDetails {
        roofReplacedYr
        roofCondition
      }
      yearBuilt
      targetUtility {
        pid
        name
        hostingCapacityMaps {
          url
          requiresLogin
          types
        }
      }
      isEnergyCommunity
      isLowIncomeCommunity
      sqft
      roofSqft
      roofMaxPvKw
      numStories
      buildingClass
    }
  }
}
fragment BundleDetailRow on BundleRow {
  id
  projectPid
  projectName
  ppaType
  state
  reason
  customReason
  notes
  optOut {
    ...InvestorOptOut
  }
  bids {
    ...InvestorBid
  }
  specs {
    ...BidSpec
  }
}
fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useInvestorBundleBidsQuery = <
  TData = Types.InvestorBundleBidsQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleBidsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.InvestorBundleBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleBidsQuery, TError, TData>({
    queryKey: ["InvestorBundleBids", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleBidsQuery,
      Types.InvestorBundleBidsQueryVariables
    >(InvestorBundleBidsDocument, variables),
    ...options,
  })
}

useInvestorBundleBidsQuery.getKey = (
  variables: Types.InvestorBundleBidsQueryVariables,
) => ["InvestorBundleBids", variables]

useInvestorBundleBidsQuery.rootKey = "InvestorBundleBids"

export const useSuspenseInvestorBundleBidsQuery = <
  TData = Types.InvestorBundleBidsQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleBidsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleBidsQuery, TError, TData>({
    queryKey: ["InvestorBundleBids", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleBidsQuery,
      Types.InvestorBundleBidsQueryVariables
    >(InvestorBundleBidsDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleBidsQuery.getKey = (
  variables: Types.InvestorBundleBidsQueryVariables,
) => ["InvestorBundleBids", variables]

useSuspenseInvestorBundleBidsQuery.rootKey = "InvestorBundleBids"

export const useInfiniteInvestorBundleBidsQuery = <
  TData = InfiniteData<Types.InvestorBundleBidsQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleBidsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleBidsQuery,
            Types.InvestorBundleBidsQueryVariables
          >(InvestorBundleBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleBidsQuery.getKey = (
  variables: Types.InvestorBundleBidsQueryVariables,
) => ["InvestorBundleBids.infinite", variables]

useInfiniteInvestorBundleBidsQuery.rootKey = "InvestorBundleBids.infinite"

export const useSuspenseInfiniteInvestorBundleBidsQuery = <
  TData = InfiniteData<Types.InvestorBundleBidsQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleBidsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleBidsQuery,
            Types.InvestorBundleBidsQueryVariables
          >(InvestorBundleBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleBidsQuery.getKey = (
  variables: Types.InvestorBundleBidsQueryVariables,
) => ["InvestorBundleBids.infinite", variables]

useSuspenseInfiniteInvestorBundleBidsQuery.rootKey = "InvestorBundleBids.infinite"

useInvestorBundleBidsQuery.fetcher = (
  variables: Types.InvestorBundleBidsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InvestorBundleBidsQuery, Types.InvestorBundleBidsQueryVariables>(
    InvestorBundleBidsDocument,
    variables,
    options,
  )

export const InvestorBundleReportsDocument = new TypedDocumentString(`
    query InvestorBundleReports($orgSlug: String!, $bundlePid: String!) {
  bundleDetail(slug: $orgSlug, bundlePid: $bundlePid) {
    ...BundleReports
  }
}
    fragment BidReport on PackageBidReport {
  pid
  name
  notes
  visible
  totalDevelopers
  totalBids
  cs {
    ...CSSiteBidReport
  }
  ppa {
    ...PPASiteBidReport
  }
  cash {
    ...CashSiteBidReport
  }
}
fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    ppaPriceEscalator
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      ppa {
        tenantFlows {
          totalUtilitySavings
        }
        ppaCostPerKwh
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
  ppaPriceY1Usd {
    ...BoxplotStats
  }
  lifetimeSavingsUsd {
    ...BoxplotStats
  }
}
fragment CashSiteBidReport on CashSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    incentivesNotes
    interconnectionNotes
    otherNotes
    result {
      selfInvest {
        npv
        irrs {
          postTaxItcDepr
        }
        flows {
          totalCapex
          totalUtilitySavings
        }
      }
    }
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  totalLifetimeSavings {
    ...BoxplotStats
  }
  totalProjectCost {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment BundleReports on Bundle {
  name
  bidReports {
    ...BidReport
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`)

export const useInvestorBundleReportsQuery = <
  TData = Types.InvestorBundleReportsQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleReportsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InvestorBundleReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleReportsQuery, TError, TData>({
    queryKey: ["InvestorBundleReports", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleReportsQuery,
      Types.InvestorBundleReportsQueryVariables
    >(InvestorBundleReportsDocument, variables),
    ...options,
  })
}

useInvestorBundleReportsQuery.getKey = (
  variables: Types.InvestorBundleReportsQueryVariables,
) => ["InvestorBundleReports", variables]

useInvestorBundleReportsQuery.rootKey = "InvestorBundleReports"

export const useSuspenseInvestorBundleReportsQuery = <
  TData = Types.InvestorBundleReportsQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleReportsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleReportsQuery, TError, TData>({
    queryKey: ["InvestorBundleReports", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleReportsQuery,
      Types.InvestorBundleReportsQueryVariables
    >(InvestorBundleReportsDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleReportsQuery.getKey = (
  variables: Types.InvestorBundleReportsQueryVariables,
) => ["InvestorBundleReports", variables]

useSuspenseInvestorBundleReportsQuery.rootKey = "InvestorBundleReports"

export const useInfiniteInvestorBundleReportsQuery = <
  TData = InfiniteData<Types.InvestorBundleReportsQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleReportsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleReportsQuery,
            Types.InvestorBundleReportsQueryVariables
          >(InvestorBundleReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleReportsQuery.getKey = (
  variables: Types.InvestorBundleReportsQueryVariables,
) => ["InvestorBundleReports.infinite", variables]

useInfiniteInvestorBundleReportsQuery.rootKey = "InvestorBundleReports.infinite"

export const useSuspenseInfiniteInvestorBundleReportsQuery = <
  TData = InfiniteData<Types.InvestorBundleReportsQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleReportsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleReportsQuery,
            Types.InvestorBundleReportsQueryVariables
          >(InvestorBundleReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleReportsQuery.getKey = (
  variables: Types.InvestorBundleReportsQueryVariables,
) => ["InvestorBundleReports.infinite", variables]

useSuspenseInfiniteInvestorBundleReportsQuery.rootKey = "InvestorBundleReports.infinite"

useInvestorBundleReportsQuery.fetcher = (
  variables: Types.InvestorBundleReportsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InvestorBundleReportsQuery,
    Types.InvestorBundleReportsQueryVariables
  >(InvestorBundleReportsDocument, variables, options)

export const InvestorBundleRowDetailDocument = new TypedDocumentString(`
    query InvestorBundleRowDetail($orgSlug: String!, $bundlePid: String!, $projectPid: String!) {
  bundleRowDetail(
    orgSlug: $orgSlug
    bundlePid: $bundlePid
    projectPid: $projectPid
  ) {
    ...BundleRowDetail
  }
}
    fragment BillingMeterOption on BillingMeter {
  pid
  bauMeterPid
  view {
    name
    isArchived
    calc2 {
      __typename
      ... on Projected {
        annualKwh
      }
    }
  }
}
fragment BundleDetailRow on BundleRow {
  id
  projectPid
  projectName
  ppaType
  state
  reason
  customReason
  notes
  optOut {
    ...InvestorOptOut
  }
  bids {
    ...InvestorBid
  }
  specs {
    ...BidSpec
  }
}
fragment BidSpec on BidSpec {
  archivedAt
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
  systemSummary {
    batterySpecs {
      sizeKwh
    }
  }
  previewImage {
    pid
    viewHref
    signedViewUrl
  }
}
fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  requiresStorage
  ftmProgram {
    id
    name
  }
}
fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment BidDraftFileRef on InvestorBidDraftFileRef {
  fileId
  fileName
  downloadHref
}
fragment BidDraft on InvestorBidDraft {
  pid
  type
  specPid
  updatedAt
  updatedByName
  schemaVersion
  formValues
  fileRefs {
    ...BidDraftFileRef
  }
}
fragment BundleRowDetail on BundleRow {
  ...BundleDetailRow
  site {
    ...RevampSiteBasics
    meterSet {
      totalCost
      loadKwh
    }
    billingMeters {
      ...BillingMeterOption
    }
  }
  bidDrafts {
    ...BidDraft
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  portfolioManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  batteryPadMw
  batteryPadMwh
  batteryDurationHours
  batteryMaxMw
  batteryPadSizingMethod
  batteryPadBlockCount
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    lseId
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}
fragment FootprintGeometry on Footprint {
  latitude
  longitude
  roofShape
  roofShapeGeojson
  roofSetbackShape
  roofSetbackShapeGeojson
  carportShape
  carportShapeGeojson
  groundmountShape
  groundmountShapeGeojson
  keepoutsShape
  keepoutsShapeGeojson
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadBlocksGeojson
  groundsShapeGeojson
  groundsShapeSqft
}
fragment Footprint on Footprint {
  ...FootprintGeometry
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}`)

export const useInvestorBundleRowDetailQuery = <
  TData = Types.InvestorBundleRowDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleRowDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleRowDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleRowDetailQuery,
      Types.InvestorBundleRowDetailQueryVariables
    >(InvestorBundleRowDetailDocument, variables),
    ...options,
  })
}

useInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail", variables]

useInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail"

export const useSuspenseInvestorBundleRowDetailQuery = <
  TData = Types.InvestorBundleRowDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleRowDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleRowDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleRowDetailQuery,
      Types.InvestorBundleRowDetailQueryVariables
    >(InvestorBundleRowDetailDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail", variables]

useSuspenseInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail"

export const useInfiniteInvestorBundleRowDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleRowDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleRowDetailQuery,
            Types.InvestorBundleRowDetailQueryVariables
          >(InvestorBundleRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail.infinite", variables]

useInfiniteInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail.infinite"

export const useSuspenseInfiniteInvestorBundleRowDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleRowDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleRowDetailQuery,
            Types.InvestorBundleRowDetailQueryVariables
          >(InvestorBundleRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail.infinite", variables]

useSuspenseInfiniteInvestorBundleRowDetailQuery.rootKey =
  "InvestorBundleRowDetail.infinite"

useInvestorBundleRowDetailQuery.fetcher = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InvestorBundleRowDetailQuery,
    Types.InvestorBundleRowDetailQueryVariables
  >(InvestorBundleRowDetailDocument, variables, options)

export const CsvImportDetailDocument = new TypedDocumentString(`
    query CsvImportDetail($orgSlug: String!, $csvImportPid: String!) {
  orgBySlug(slug: $orgSlug) {
    csvImports {
      one(pid: $csvImportPid) {
        ...CsvImportDetail
      }
    }
  }
}
    fragment DirtyRowData on DirtyRowData {
  name
  assetManager
  portfolioManager
  customerId
  numStories
  streetAddress
  city
  state
  zipCode
  buildingType
  sqft
  yearBuilt
}
fragment CsvImportDetail on CsvImport {
  pid
  rawCsvWithPidsHref
  columnIds
  columnMappings {
    assetManagerColumnId
    portfolioManagerColumnId
    buildingTypeColumnId
    cityColumnId
    customerIdColumnId
    latitudeColumnId
    longitudeColumnId
    nameColumnId
    numStoriesColumnId
    numUnits1BdColumnId
    numUnits2BdColumnId
    numUnits3BdColumnId
    numUnits4BdColumnId
    numUnitsStudioColumnId
    numUnitsTotalColumnId
    sqftColumnId
    stateColumnId
    streetAddressColumnId
    yearBuiltColumnId
    zipCodeColumnId
  }
  buildingTypes
  buildingTypeMappings {
    office
    multifamily
    hotel
    hospital
    outpatient
    retailStandalone
    retailStripMall
    quickServiceRestaurant
    fullServiceRestaurant
    primarySchool
    secondarySchool
    singleFamilyDetached
    warehouse
    dataCenter
    coldStorage
    industrial
    parkingLot
    supermarket
  }
  guessedSchema {
    pid
    origin
  }
  labels {
    pid
    themeColor
    name
  }
  rows {
    id
    droppedAt
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    amendments {
      assetManager
      portfolioManager
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
    site {
      pid
    }
    dirty {
      ...DirtyRowData
    }
    errors {
      type
      message
    }
    warnings {
      type
      message
    }
  }
}`)

export const useCsvImportDetailQuery = <
  TData = Types.CsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsvImportDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsvImportDetailQuery, TError, TData>({
    queryKey: ["CsvImportDetail", variables],
    queryFn: publicFetcher<
      Types.CsvImportDetailQuery,
      Types.CsvImportDetailQueryVariables
    >(CsvImportDetailDocument, variables),
    ...options,
  })
}

useCsvImportDetailQuery.getKey = (variables: Types.CsvImportDetailQueryVariables) => [
  "CsvImportDetail",
  variables,
]

useCsvImportDetailQuery.rootKey = "CsvImportDetail"

export const useSuspenseCsvImportDetailQuery = <
  TData = Types.CsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsvImportDetailQuery, TError, TData>({
    queryKey: ["CsvImportDetail", variables],
    queryFn: publicFetcher<
      Types.CsvImportDetailQuery,
      Types.CsvImportDetailQueryVariables
    >(CsvImportDetailDocument, variables),
    ...options,
  })
}

useSuspenseCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetail", variables]

useSuspenseCsvImportDetailQuery.rootKey = "CsvImportDetail"

export const useInfiniteCsvImportDetailQuery = <
  TData = InfiniteData<Types.CsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsvImportDetailQuery,
            Types.CsvImportDetailQueryVariables
          >(CsvImportDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetail.infinite", variables]

useInfiniteCsvImportDetailQuery.rootKey = "CsvImportDetail.infinite"

export const useSuspenseInfiniteCsvImportDetailQuery = <
  TData = InfiniteData<Types.CsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsvImportDetailQuery,
            Types.CsvImportDetailQueryVariables
          >(CsvImportDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetail.infinite", variables]

useSuspenseInfiniteCsvImportDetailQuery.rootKey = "CsvImportDetail.infinite"

useCsvImportDetailQuery.fetcher = (
  variables: Types.CsvImportDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsvImportDetailQuery, Types.CsvImportDetailQueryVariables>(
    CsvImportDetailDocument,
    variables,
    options,
  )

export const MarketplacePackagesDocument = new TypedDocumentString(`
    query MarketplacePackages($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    bidPackages {
      ...BasicBidPackage
    }
  }
}
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}`)

export const useMarketplacePackagesQuery = <
  TData = Types.MarketplacePackagesQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackagesQuery, TError, TData>({
    queryKey: ["MarketplacePackages", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackagesQuery,
      Types.MarketplacePackagesQueryVariables
    >(MarketplacePackagesDocument, variables),
    ...options,
  })
}

useMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages", variables]

useMarketplacePackagesQuery.rootKey = "MarketplacePackages"

export const useSuspenseMarketplacePackagesQuery = <
  TData = Types.MarketplacePackagesQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackagesQuery, TError, TData>({
    queryKey: ["MarketplacePackages", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackagesQuery,
      Types.MarketplacePackagesQueryVariables
    >(MarketplacePackagesDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages", variables]

useSuspenseMarketplacePackagesQuery.rootKey = "MarketplacePackages"

export const useInfiniteMarketplacePackagesQuery = <
  TData = InfiniteData<Types.MarketplacePackagesQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackagesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackages.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackagesQuery,
            Types.MarketplacePackagesQueryVariables
          >(MarketplacePackagesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages.infinite", variables]

useInfiniteMarketplacePackagesQuery.rootKey = "MarketplacePackages.infinite"

export const useSuspenseInfiniteMarketplacePackagesQuery = <
  TData = InfiniteData<Types.MarketplacePackagesQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplacePackagesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackages.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackagesQuery,
            Types.MarketplacePackagesQueryVariables
          >(MarketplacePackagesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages.infinite", variables]

useSuspenseInfiniteMarketplacePackagesQuery.rootKey = "MarketplacePackages.infinite"

useMarketplacePackagesQuery.fetcher = (
  variables: Types.MarketplacePackagesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackagesQuery,
    Types.MarketplacePackagesQueryVariables
  >(MarketplacePackagesDocument, variables, options)

export const MarketplaceSpecsViewDocument = new TypedDocumentString(`
    query MarketplaceSpecsView($orgSlug: String!, $filterBy: BidSpecsTableFilter!, $sortBy: [BidSpecsTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSpecs {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useMarketplaceSpecsViewQuery = <
  TData = Types.MarketplaceSpecsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSpecsViewQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsViewQuery,
      Types.MarketplaceSpecsViewQueryVariables
    >(MarketplaceSpecsViewDocument, variables),
    ...options,
  })
}

useMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView", variables]

useMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView"

export const useSuspenseMarketplaceSpecsViewQuery = <
  TData = Types.MarketplaceSpecsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSpecsViewQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsViewQuery,
      Types.MarketplaceSpecsViewQueryVariables
    >(MarketplaceSpecsViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView", variables]

useSuspenseMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView"

export const useInfiniteMarketplaceSpecsViewQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSpecsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsViewQuery,
            Types.MarketplaceSpecsViewQueryVariables
          >(MarketplaceSpecsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView.infinite", variables]

useInfiniteMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView.infinite"

export const useSuspenseInfiniteMarketplaceSpecsViewQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSpecsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsViewQuery,
            Types.MarketplaceSpecsViewQueryVariables
          >(MarketplaceSpecsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView.infinite", variables]

useSuspenseInfiniteMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView.infinite"

useMarketplaceSpecsViewQuery.fetcher = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSpecsViewQuery,
    Types.MarketplaceSpecsViewQueryVariables
  >(MarketplaceSpecsViewDocument, variables, options)

export const MarketplaceSpecsRowsDocument = new TypedDocumentString(`
    query MarketplaceSpecsRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSpecs {
      rows(keys: $keys) {
        pid
        sitePid
        projectPid
        bidName
        bidPackagePid
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        portfolioManager
        packageRowStatus
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        csSystem {
          ...SystemLiteRow
        }
        ppaSystem {
          ...SystemRow
        }
        csRoofRentEscalator
        ppaRoofRentEscalator
        ppaPriceEscalator
        csRoofRentY1Usd
        ppaRoofRentY1Usd
        ppaPriceY1Usd
        termYears
      }
    }
  }
}
    fragment PvSystemSpecRow on PVSpec {
  id
  pvTypes
  sizeKwDc
  sizeKwAc
  roofImage {
    viewHref
    signedViewUrl
  }
}
fragment SystemRow on System {
  id
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpecRow
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
  }
}
fragment SystemLiteRow on SystemLite {
  id
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpecRow
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
  }
}`)

export const useMarketplaceSpecsRowsQuery = <
  TData = Types.MarketplaceSpecsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsRowsQuery,
      Types.MarketplaceSpecsRowsQueryVariables
    >(MarketplaceSpecsRowsDocument, variables),
    ...options,
  })
}

useMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows", variables]

useMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows"

export const useSuspenseMarketplaceSpecsRowsQuery = <
  TData = Types.MarketplaceSpecsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsRowsQuery,
      Types.MarketplaceSpecsRowsQueryVariables
    >(MarketplaceSpecsRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows", variables]

useSuspenseMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows"

export const useInfiniteMarketplaceSpecsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsRowsQuery,
            Types.MarketplaceSpecsRowsQueryVariables
          >(MarketplaceSpecsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows.infinite", variables]

useInfiniteMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows.infinite"

export const useSuspenseInfiniteMarketplaceSpecsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsRowsQuery,
            Types.MarketplaceSpecsRowsQueryVariables
          >(MarketplaceSpecsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows.infinite", variables]

useSuspenseInfiniteMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows.infinite"

useMarketplaceSpecsRowsQuery.fetcher = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSpecsRowsQuery,
    Types.MarketplaceSpecsRowsQueryVariables
  >(MarketplaceSpecsRowsDocument, variables, options)

export const MarketplaceSitesViewDocument = new TypedDocumentString(`
    query MarketplaceSitesView($orgSlug: String!, $filterBy: BidPackageSitesTableFilter!, $sortBy: [BidPackageSitesTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    name
    bidPackageSites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useMarketplaceSitesViewQuery = <
  TData = Types.MarketplaceSitesViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSitesViewQuery, TError, TData>({
    queryKey: ["MarketplaceSitesView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesViewQuery,
      Types.MarketplaceSitesViewQueryVariables
    >(MarketplaceSitesViewDocument, variables),
    ...options,
  })
}

useMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView", variables]

useMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView"

export const useSuspenseMarketplaceSitesViewQuery = <
  TData = Types.MarketplaceSitesViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSitesViewQuery, TError, TData>({
    queryKey: ["MarketplaceSitesView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesViewQuery,
      Types.MarketplaceSitesViewQueryVariables
    >(MarketplaceSitesViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView", variables]

useSuspenseMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView"

export const useInfiniteMarketplaceSitesViewQuery = <
  TData = InfiniteData<Types.MarketplaceSitesViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSitesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesViewQuery,
            Types.MarketplaceSitesViewQueryVariables
          >(MarketplaceSitesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView.infinite", variables]

useInfiniteMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView.infinite"

export const useSuspenseInfiniteMarketplaceSitesViewQuery = <
  TData = InfiniteData<Types.MarketplaceSitesViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSitesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesViewQuery,
            Types.MarketplaceSitesViewQueryVariables
          >(MarketplaceSitesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView.infinite", variables]

useSuspenseInfiniteMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView.infinite"

useMarketplaceSitesViewQuery.fetcher = (
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSitesViewQuery,
    Types.MarketplaceSitesViewQueryVariables
  >(MarketplaceSitesViewDocument, variables, options)

export const MarketplaceSitesRowsDocument = new TypedDocumentString(`
    query MarketplaceSitesRows($orgSlug: String!, $keys: [Int!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSites {
      rows(keys: $keys) {
        id
        sitePid
        projectPid
        bidPackagePid
        bidPackageName
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        portfolioManager
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        salePlans
        buildingLegalName
        propertyFundName
        leaseSignatory
        counselApproval
        leaseApprovers
        externalLeaseApprovers
        needsTenantApproval
        roofRightsHolder
        propertyLenders
        propertyInsurer
        constructionEmail
        propertySiteVisitContacts
        leasePropertyLegalDescription
        leaseLegalOwnerName
        signatureBlocks
        isFannieMaeLender
        isFmGlobalInsurer
        creditRating
        occupancyType
        leaseType
        tenantLeaseTerm
        financierCount
        bidCount
        ppaNoiMin
        ppaNoiAvg
        ppaNoiMax
        csNoiMin
        csNoiAvg
        csNoiMax
        targetUtility {
          pid
          name
        }
        awardedDeveloper
        awardedBidPvSizeKwDc
        awardedBidPvSizeKwAc
        awardedBidPvTypes
        csEstimatedAwardDate
        estimatedNtp
        estimatedCod
        csNpv
        roofRentY1Usd
        roofRentEscalator
        noiLifetimeUsd
        financingType
        solarLocations
        includeStorage
        marketplaceSolarLocations
        marketplaceIncludeStorage
        isMultifamily
      }
    }
  }
}
    `)

export const useMarketplaceSitesRowsQuery = <
  TData = Types.MarketplaceSitesRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSitesRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesRowsQuery,
      Types.MarketplaceSitesRowsQueryVariables
    >(MarketplaceSitesRowsDocument, variables),
    ...options,
  })
}

useMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows", variables]

useMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows"

export const useSuspenseMarketplaceSitesRowsQuery = <
  TData = Types.MarketplaceSitesRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSitesRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesRowsQuery,
      Types.MarketplaceSitesRowsQueryVariables
    >(MarketplaceSitesRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows", variables]

useSuspenseMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows"

export const useInfiniteMarketplaceSitesRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSitesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesRowsQuery,
            Types.MarketplaceSitesRowsQueryVariables
          >(MarketplaceSitesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows.infinite", variables]

useInfiniteMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows.infinite"

export const useSuspenseInfiniteMarketplaceSitesRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSitesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesRowsQuery,
            Types.MarketplaceSitesRowsQueryVariables
          >(MarketplaceSitesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows.infinite", variables]

useSuspenseInfiniteMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows.infinite"

useMarketplaceSitesRowsQuery.fetcher = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSitesRowsQuery,
    Types.MarketplaceSitesRowsQueryVariables
  >(MarketplaceSitesRowsDocument, variables, options)

export const MarketplaceBidsViewDocument = new TypedDocumentString(`
    query MarketplaceBidsView($orgSlug: String!, $filterBy: InvestorBidsTableFilter!, $sortBy: [InvestorBidsTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useMarketplaceBidsViewQuery = <
  TData = Types.MarketplaceBidsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsViewQuery, TError, TData>({
    queryKey: ["MarketplaceBidsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsViewQuery,
      Types.MarketplaceBidsViewQueryVariables
    >(MarketplaceBidsViewDocument, variables),
    ...options,
  })
}

useMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView", variables]

useMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView"

export const useSuspenseMarketplaceBidsViewQuery = <
  TData = Types.MarketplaceBidsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsViewQuery, TError, TData>({
    queryKey: ["MarketplaceBidsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsViewQuery,
      Types.MarketplaceBidsViewQueryVariables
    >(MarketplaceBidsViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView", variables]

useSuspenseMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView"

export const useInfiniteMarketplaceBidsViewQuery = <
  TData = InfiniteData<Types.MarketplaceBidsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsViewQuery,
            Types.MarketplaceBidsViewQueryVariables
          >(MarketplaceBidsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView.infinite", variables]

useInfiniteMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView.infinite"

export const useSuspenseInfiniteMarketplaceBidsViewQuery = <
  TData = InfiniteData<Types.MarketplaceBidsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsViewQuery,
            Types.MarketplaceBidsViewQueryVariables
          >(MarketplaceBidsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView.infinite", variables]

useSuspenseInfiniteMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView.infinite"

useMarketplaceBidsViewQuery.fetcher = (
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsViewQuery,
    Types.MarketplaceBidsViewQueryVariables
  >(MarketplaceBidsViewDocument, variables, options)

export const MarketplaceBidsFacetsDocument = new TypedDocumentString(`
    query MarketplaceBidsFacets($orgSlug: String!, $filterBy: InvestorBidsTableFilter!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      view(filterBy: $filterBy) {
        stats {
          financiers
          siteNames
          siteStates
          siteStreetAddresses
        }
      }
    }
  }
}
    `)

export const useMarketplaceBidsFacetsQuery = <
  TData = Types.MarketplaceBidsFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsFacets", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsFacetsQuery,
      Types.MarketplaceBidsFacetsQueryVariables
    >(MarketplaceBidsFacetsDocument, variables),
    ...options,
  })
}

useMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets", variables]

useMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets"

export const useSuspenseMarketplaceBidsFacetsQuery = <
  TData = Types.MarketplaceBidsFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsFacets", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsFacetsQuery,
      Types.MarketplaceBidsFacetsQueryVariables
    >(MarketplaceBidsFacetsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets", variables]

useSuspenseMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets"

export const useInfiniteMarketplaceBidsFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsFacetsQuery,
            Types.MarketplaceBidsFacetsQueryVariables
          >(MarketplaceBidsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets.infinite", variables]

useInfiniteMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets.infinite"

export const useSuspenseInfiniteMarketplaceBidsFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsFacetsQuery,
            Types.MarketplaceBidsFacetsQueryVariables
          >(MarketplaceBidsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets.infinite", variables]

useSuspenseInfiniteMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets.infinite"

useMarketplaceBidsFacetsQuery.fetcher = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsFacetsQuery,
    Types.MarketplaceBidsFacetsQueryVariables
  >(MarketplaceBidsFacetsDocument, variables, options)

export const MarketplaceBidsRowsDocument = new TypedDocumentString(`
    query MarketplaceBidsRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      rows(keys: $keys) {
        pid
        bidName
        sitePid
        projectPid
        bundleRowPid
        bundleParentPid
        name
        streetAddress
        city
        state
        zipCode
        latitude
        longitude
        county
        cbsaName
        assetManager
        portfolioManager
        packageRowIsClosed
        packageRowStatus
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        isAwardedBid
        isRecommendedBid
        bidPackagePid
        bidPackageName
        biddingOpenAt
        biddingClosedAt
        type
        csEstimatedAwardDate
        estimatedCod
        estimatedNtp
        itcPercentageAssumption
        interconnectionCostUsdPerWatt
        interconnectionCostUtilityMinUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostNonUtilityUsd
        interconnectionNotes
        interconnectionRepriceProcessNotes
        incentivesNotes
        otherNotes
        termYears
        recOwner
        buildingOwnerExtraPaymentsUsd
        extraPaymentsNotes
        roofReplacementYear
        csRoofRentEscalator
        csRoofRentY1Usd
        csBuildingOwnerNoiLifetimeUsd
        csRoofRentNpv
        systemLite {
          id
          ...SystemLite
        }
        ppaRoofRentEscalator
        ppaRoofRentY1Usd
        ppaPriceEscalator
        ppaPriceY1Usd
        cashTotalPvCostUsd
        cashTotalStorageCostUsd
        cashNpv
        cashPostTaxItcDeprIrr
        system {
          id
          ...System
        }
        financierPid
        financierName
        financierLogoUrl
        financierLogomarkUrl
        ppaBuildingOwnerNoiLifetimeUsd
        ppaOfftakerLifetimeSavingsUsd
        ppaYearOneOfftakerSavingsUsd
        ppaYearOneRoofRentalFee
        itcAdders
        itcObbbStrategy
        itcObbbCustomStrategy
        recValueUsdPerMwh
        estimatedIxApproval
        csSubscriberDiscountPct
        csFtmProgramName
      }
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}`)

export const useMarketplaceBidsRowsQuery = <
  TData = Types.MarketplaceBidsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsRowsQuery,
      Types.MarketplaceBidsRowsQueryVariables
    >(MarketplaceBidsRowsDocument, variables),
    ...options,
  })
}

useMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows", variables]

useMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows"

export const useSuspenseMarketplaceBidsRowsQuery = <
  TData = Types.MarketplaceBidsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsRowsQuery,
      Types.MarketplaceBidsRowsQueryVariables
    >(MarketplaceBidsRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows", variables]

useSuspenseMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows"

export const useInfiniteMarketplaceBidsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsRowsQuery,
            Types.MarketplaceBidsRowsQueryVariables
          >(MarketplaceBidsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows.infinite", variables]

useInfiniteMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows.infinite"

export const useSuspenseInfiniteMarketplaceBidsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsRowsQuery,
            Types.MarketplaceBidsRowsQueryVariables
          >(MarketplaceBidsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows.infinite", variables]

useSuspenseInfiniteMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows.infinite"

useMarketplaceBidsRowsQuery.fetcher = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsRowsQuery,
    Types.MarketplaceBidsRowsQueryVariables
  >(MarketplaceBidsRowsDocument, variables, options)

export const MarketplacePackageDetailDocument = new TypedDocumentString(`
    query MarketplacePackageDetail($orgSlug: String!, $bundlePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      ...BidPackageDetail
    }
  }
}
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment PackageDocument on Document {
  canDelete
  contentType
  dateCreated
  fileName
  ownerName
  pid
  tag
  downloadHref
  belongsToBundleParent
  ownerOrg {
    orgSlug
    name
  }
  investorBid {
    pid
    name
  }
}
fragment BidPackageDetail on BidPackage {
  ...BasicBidPackage
  documents {
    ...PackageDocument
  }
  bidDocuments {
    ...PackageDocument
  }
}`)

export const useMarketplacePackageDetailQuery = <
  TData = Types.MarketplacePackageDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageDetailQuery,
      Types.MarketplacePackageDetailQueryVariables
    >(MarketplacePackageDetailDocument, variables),
    ...options,
  })
}

useMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail", variables]

useMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail"

export const useSuspenseMarketplacePackageDetailQuery = <
  TData = Types.MarketplacePackageDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageDetailQuery,
      Types.MarketplacePackageDetailQueryVariables
    >(MarketplacePackageDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail", variables]

useSuspenseMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail"

export const useInfiniteMarketplacePackageDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackageDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageDetailQuery,
            Types.MarketplacePackageDetailQueryVariables
          >(MarketplacePackageDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail.infinite", variables]

useInfiniteMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail.infinite"

export const useSuspenseInfiniteMarketplacePackageDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplacePackageDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackageDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageDetailQuery,
            Types.MarketplacePackageDetailQueryVariables
          >(MarketplacePackageDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail.infinite", variables]

useSuspenseInfiniteMarketplacePackageDetailQuery.rootKey =
  "MarketplacePackageDetail.infinite"

useMarketplacePackageDetailQuery.fetcher = (
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageDetailQuery,
    Types.MarketplacePackageDetailQueryVariables
  >(MarketplacePackageDetailDocument, variables, options)

export const MarketplacePackageBidReviewDocument = new TypedDocumentString(`
    query MarketplacePackageBidReview($orgSlug: String!, $bundlePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      bidReview {
        ...BidReview
      }
    }
  }
}
    fragment BidReport on PackageBidReport {
  pid
  name
  notes
  visible
  totalDevelopers
  totalBids
  cs {
    ...CSSiteBidReport
  }
  ppa {
    ...PPASiteBidReport
  }
  cash {
    ...CashSiteBidReport
  }
}
fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    ppaPriceEscalator
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      ppa {
        tenantFlows {
          totalUtilitySavings
        }
        ppaCostPerKwh
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
  ppaPriceY1Usd {
    ...BoxplotStats
  }
  lifetimeSavingsUsd {
    ...BoxplotStats
  }
}
fragment CashSiteBidReport on CashSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  projectPid
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    incentivesNotes
    interconnectionNotes
    otherNotes
    result {
      selfInvest {
        npv
        irrs {
          postTaxItcDepr
        }
        flows {
          totalCapex
          totalUtilitySavings
        }
      }
    }
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  totalLifetimeSavings {
    ...BoxplotStats
  }
  totalProjectCost {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment BidReview on BidReview {
  recommendedBidPids
  reports {
    properties
    bidders
    uniqueBids
    avgRoofRentY1Usd
    isCustom
    report {
      ...BidReport
    }
  }
}`)

export const useMarketplacePackageBidReviewQuery = <
  TData = Types.MarketplacePackageBidReviewQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>({
    queryKey: ["MarketplacePackageBidReview", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageBidReviewQuery,
      Types.MarketplacePackageBidReviewQueryVariables
    >(MarketplacePackageBidReviewDocument, variables),
    ...options,
  })
}

useMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview", variables]

useMarketplacePackageBidReviewQuery.rootKey = "MarketplacePackageBidReview"

export const useSuspenseMarketplacePackageBidReviewQuery = <
  TData = Types.MarketplacePackageBidReviewQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>({
    queryKey: ["MarketplacePackageBidReview", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageBidReviewQuery,
      Types.MarketplacePackageBidReviewQueryVariables
    >(MarketplacePackageBidReviewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview", variables]

useSuspenseMarketplacePackageBidReviewQuery.rootKey = "MarketplacePackageBidReview"

export const useInfiniteMarketplacePackageBidReviewQuery = <
  TData = InfiniteData<Types.MarketplacePackageBidReviewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageBidReview.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageBidReviewQuery,
            Types.MarketplacePackageBidReviewQueryVariables
          >(MarketplacePackageBidReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview.infinite", variables]

useInfiniteMarketplacePackageBidReviewQuery.rootKey =
  "MarketplacePackageBidReview.infinite"

export const useSuspenseInfiniteMarketplacePackageBidReviewQuery = <
  TData = InfiniteData<Types.MarketplacePackageBidReviewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MarketplacePackageBidReviewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageBidReview.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageBidReviewQuery,
            Types.MarketplacePackageBidReviewQueryVariables
          >(MarketplacePackageBidReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview.infinite", variables]

useSuspenseInfiniteMarketplacePackageBidReviewQuery.rootKey =
  "MarketplacePackageBidReview.infinite"

useMarketplacePackageBidReviewQuery.fetcher = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageBidReviewQuery,
    Types.MarketplacePackageBidReviewQueryVariables
  >(MarketplacePackageBidReviewDocument, variables, options)

export const MarketplacePackageRowDetailDocument = new TypedDocumentString(`
    query MarketplacePackageRowDetail($orgSlug: String!, $bundlePid: String!, $projectPid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      ...BidPackageDetail
      row(projectPid: $projectPid) {
        status
        closedAt
        closeReason
        closeCustomReason
        projectPid
        site {
          pid
          name
          streetAddress
          city
          state
          zipCode
          county
          fullAddress
          targetUtility {
            name
          }
          buildingType
          yearBuilt
          sqft
          numStories
          buildingClass
          roofSqft
          siteDetails {
            roofReplacedYr
            roofCondition
          }
          roofMaxPvKw
          meterSet {
            loadKwh
          }
        }
      }
    }
  }
}
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment PackageDocument on Document {
  canDelete
  contentType
  dateCreated
  fileName
  ownerName
  pid
  tag
  downloadHref
  belongsToBundleParent
  ownerOrg {
    orgSlug
    name
  }
  investorBid {
    pid
    name
  }
}
fragment BidPackageDetail on BidPackage {
  ...BasicBidPackage
  documents {
    ...PackageDocument
  }
  bidDocuments {
    ...PackageDocument
  }
}`)

export const useMarketplacePackageRowDetailQuery = <
  TData = Types.MarketplacePackageRowDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageRowDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageRowDetailQuery,
      Types.MarketplacePackageRowDetailQueryVariables
    >(MarketplacePackageRowDetailDocument, variables),
    ...options,
  })
}

useMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail", variables]

useMarketplacePackageRowDetailQuery.rootKey = "MarketplacePackageRowDetail"

export const useSuspenseMarketplacePackageRowDetailQuery = <
  TData = Types.MarketplacePackageRowDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageRowDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageRowDetailQuery,
      Types.MarketplacePackageRowDetailQueryVariables
    >(MarketplacePackageRowDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail", variables]

useSuspenseMarketplacePackageRowDetailQuery.rootKey = "MarketplacePackageRowDetail"

export const useInfiniteMarketplacePackageRowDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageRowDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageRowDetailQuery,
            Types.MarketplacePackageRowDetailQueryVariables
          >(MarketplacePackageRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail.infinite", variables]

useInfiniteMarketplacePackageRowDetailQuery.rootKey =
  "MarketplacePackageRowDetail.infinite"

export const useSuspenseInfiniteMarketplacePackageRowDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MarketplacePackageRowDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageRowDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageRowDetailQuery,
            Types.MarketplacePackageRowDetailQueryVariables
          >(MarketplacePackageRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail.infinite", variables]

useSuspenseInfiniteMarketplacePackageRowDetailQuery.rootKey =
  "MarketplacePackageRowDetail.infinite"

useMarketplacePackageRowDetailQuery.fetcher = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageRowDetailQuery,
    Types.MarketplacePackageRowDetailQueryVariables
  >(MarketplacePackageRowDetailDocument, variables, options)

export const MarketplaceBidDetailDocument = new TypedDocumentString(`
    query MarketplaceBidDetail($orgSlug: String!, $bidPid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bid(pid: $bidPid) {
      ...InvestorBid
      ppa {
        site {
          ...BidRevampSite
        }
        financier {
          pid
          name
          orgSlug
          logoUrl
          logomarkUrl
        }
      }
      cs {
        site {
          ...BidRevampSite
        }
        financier {
          pid
          name
          orgSlug
          logoUrl
          logomarkUrl
        }
      }
      cash {
        site {
          ...BidRevampSite
        }
      }
    }
  }
}
    fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCSBase
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  monetizationRequiredPathways
  monetizationOptionalPathways
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    ftmEnergizedBessKwh
    ftmAwardedBessKwh
    btmEnergizedPvKw
    btmAwardedPvKw
    btmEnergizedBessKwh
    btmAwardedBessKwh
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmMinBessPortfolioSize
    ftmMinBessProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmMinBessPortfolioSize
    btmMinBessProjectSize
    btmSeekingProjects
  }
}
fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  tariffDataUnavailableMessage
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
        tenantSavingsPct
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
fragment BidRevampSite on RevampSite {
  pid
  name
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  portfolioManager
  buildingType
  meterSet {
    totalCost
    loadKwh
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  siteDetails {
    salePlans
    roofCondition
    roofReplacedYr
  }
  roofMaxPvKw
  buildingClass
  targetUtility {
    name
    pid
    id
  }
  ppaType
}`)

export const useMarketplaceBidDetailQuery = <
  TData = Types.MarketplaceBidDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidDetailQuery, TError, TData>({
    queryKey: ["MarketplaceBidDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidDetailQuery,
      Types.MarketplaceBidDetailQueryVariables
    >(MarketplaceBidDetailDocument, variables),
    ...options,
  })
}

useMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail", variables]

useMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail"

export const useSuspenseMarketplaceBidDetailQuery = <
  TData = Types.MarketplaceBidDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidDetailQuery, TError, TData>({
    queryKey: ["MarketplaceBidDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidDetailQuery,
      Types.MarketplaceBidDetailQueryVariables
    >(MarketplaceBidDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail", variables]

useSuspenseMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail"

export const useInfiniteMarketplaceBidDetailQuery = <
  TData = InfiniteData<Types.MarketplaceBidDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidDetailQuery,
            Types.MarketplaceBidDetailQueryVariables
          >(MarketplaceBidDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail.infinite", variables]

useInfiniteMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail.infinite"

export const useSuspenseInfiniteMarketplaceBidDetailQuery = <
  TData = InfiniteData<Types.MarketplaceBidDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidDetailQuery,
            Types.MarketplaceBidDetailQueryVariables
          >(MarketplaceBidDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail.infinite", variables]

useSuspenseInfiniteMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail.infinite"

useMarketplaceBidDetailQuery.fetcher = (
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidDetailQuery,
    Types.MarketplaceBidDetailQueryVariables
  >(MarketplaceBidDetailDocument, variables, options)

export const CurrentUserGoogleAccessDocument = new TypedDocumentString(`
    query CurrentUserGoogleAccess {
  currentUser {
    pid
    hasGoogleAccessToken
  }
}
    `)

export const useCurrentUserGoogleAccessQuery = <
  TData = Types.CurrentUserGoogleAccessQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserGoogleAccess"]
        : ["CurrentUserGoogleAccess", variables],
    queryFn: publicFetcher<
      Types.CurrentUserGoogleAccessQuery,
      Types.CurrentUserGoogleAccessQueryVariables
    >(CurrentUserGoogleAccessDocument, variables),
    ...options,
  })
}

useCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess"]
    : ["CurrentUserGoogleAccess", variables]

useCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess"

export const useSuspenseCurrentUserGoogleAccessQuery = <
  TData = Types.CurrentUserGoogleAccessQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserGoogleAccess"]
        : ["CurrentUserGoogleAccess", variables],
    queryFn: publicFetcher<
      Types.CurrentUserGoogleAccessQuery,
      Types.CurrentUserGoogleAccessQueryVariables
    >(CurrentUserGoogleAccessDocument, variables),
    ...options,
  })
}

useSuspenseCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess"]
    : ["CurrentUserGoogleAccess", variables]

useSuspenseCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess"

export const useInfiniteCurrentUserGoogleAccessQuery = <
  TData = InfiniteData<Types.CurrentUserGoogleAccessQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserGoogleAccessQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUserGoogleAccess.infinite"]
            : ["CurrentUserGoogleAccess.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentUserGoogleAccessQuery,
            Types.CurrentUserGoogleAccessQueryVariables
          >(CurrentUserGoogleAccessDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess.infinite"]
    : ["CurrentUserGoogleAccess.infinite", variables]

useInfiniteCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess.infinite"

export const useSuspenseInfiniteCurrentUserGoogleAccessQuery = <
  TData = InfiniteData<Types.CurrentUserGoogleAccessQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserGoogleAccessQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUserGoogleAccess.infinite"]
            : ["CurrentUserGoogleAccess.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentUserGoogleAccessQuery,
            Types.CurrentUserGoogleAccessQueryVariables
          >(CurrentUserGoogleAccessDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess.infinite"]
    : ["CurrentUserGoogleAccess.infinite", variables]

useSuspenseInfiniteCurrentUserGoogleAccessQuery.rootKey =
  "CurrentUserGoogleAccess.infinite"

useCurrentUserGoogleAccessQuery.fetcher = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CurrentUserGoogleAccessQuery,
    Types.CurrentUserGoogleAccessQueryVariables
  >(CurrentUserGoogleAccessDocument, variables, options)

export const AwardedProjectsTableViewDocument = new TypedDocumentString(`
    query AwardedProjectsTableView($orgSlug: String!, $filterBy: AwardedProjectTableFilter, $sortBy: [AwardedProjectTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    pid
    awardedProjects {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          buildingOwners
          utilities
          states
          negotiatingContractsCount
          developingProjectCount
          underConstructionCount
          activePvSizeKwDc
          negotiatingContractsPvSizeKwDc
          developingProjectPvSizeKwDc
          underConstructionPvSizeKwDc
          overdueMilestonesCount
          upcomingMilestonesCount
          overdueProjectsCount
          upcomingProjectsCount
          missingMilestonesCount
          missingMilestonesProjectsCount
          milestonesLastUpdatedAt
          milestonesLastUpdatedBy
        }
      }
    }
  }
}
    `)

export const useAwardedProjectsTableViewQuery = <
  TData = Types.AwardedProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AwardedProjectsTableViewQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableViewQuery,
      Types.AwardedProjectsTableViewQueryVariables
    >(AwardedProjectsTableViewDocument, variables),
    ...options,
  })
}

useAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView", variables]

useAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView"

export const useSuspenseAwardedProjectsTableViewQuery = <
  TData = Types.AwardedProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AwardedProjectsTableViewQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableViewQuery,
      Types.AwardedProjectsTableViewQueryVariables
    >(AwardedProjectsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView", variables]

useSuspenseAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView"

export const useInfiniteAwardedProjectsTableViewQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AwardedProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableViewQuery,
            Types.AwardedProjectsTableViewQueryVariables
          >(AwardedProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView.infinite", variables]

useInfiniteAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView.infinite"

export const useSuspenseInfiniteAwardedProjectsTableViewQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AwardedProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableViewQuery,
            Types.AwardedProjectsTableViewQueryVariables
          >(AwardedProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView.infinite", variables]

useSuspenseInfiniteAwardedProjectsTableViewQuery.rootKey =
  "AwardedProjectsTableView.infinite"

useAwardedProjectsTableViewQuery.fetcher = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardedProjectsTableViewQuery,
    Types.AwardedProjectsTableViewQueryVariables
  >(AwardedProjectsTableViewDocument, variables, options)

export const AwardedProjectsTableRowsDocument = new TypedDocumentString(`
    query AwardedProjectsTableRows($orgSlug: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    awardedProjects {
      rows(keys: $keys) {
        id
        orgName
        utilityName
        projectPid
        sitePid
        bundlePid
        name
        streetAddress
        city
        state
        zipCode
        projectName
        projectStatus
        projectIsClosed
        projectClosedAt
        projectCloseReason
        projectCloseCustomReason
        lumenRevenue
        finproductType
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        rofoSignedDate
        rofoSignedEstimated
        rofoSignedActual
        preBidMeetingDate
        preBidMeetingEstimated
        preBidMeetingActual
        biddingOpenedDate
        biddingOpenedEstimated
        biddingOpenedActual
        biddingClosedDate
        biddingClosedEstimated
        biddingClosedActual
        bidsPresentedDate
        bidsPresentedEstimated
        bidsPresentedActual
        ixSubmittedDate
        ixSubmittedEstimated
        ixSubmittedActual
        workableIxResultsReceivedDate
        workableIxResultsReceivedEstimated
        workableIxResultsReceivedActual
        ixApprovedDate
        ixApprovedEstimated
        ixApprovedActual
        csProgramSubmittedDate
        csProgramSubmittedEstimated
        csProgramSubmittedActual
        csProgramApprovedDate
        csProgramApprovedEstimated
        csProgramApprovedActual
        awardedToDeveloperDate
        awardedToDeveloperEstimated
        awardedToDeveloperActual
        leaseSignedDate
        leaseSignedEstimated
        leaseSignedActual
        ntpDate
        ntpEstimated
        ntpActual
        codDate
        codEstimated
        codActual
        nextMilestoneDate
        nextUpDate
        nextUpTasks {
          pid
          description
          dueDate
          overdue
          isEstimate
          assigneeGroup
        }
      }
    }
  }
}
    `)

export const useAwardedProjectsTableRowsQuery = <
  TData = Types.AwardedProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableRowsQuery,
      Types.AwardedProjectsTableRowsQueryVariables
    >(AwardedProjectsTableRowsDocument, variables),
    ...options,
  })
}

useAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows", variables]

useAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows"

export const useSuspenseAwardedProjectsTableRowsQuery = <
  TData = Types.AwardedProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableRowsQuery,
      Types.AwardedProjectsTableRowsQueryVariables
    >(AwardedProjectsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows", variables]

useSuspenseAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows"

export const useInfiniteAwardedProjectsTableRowsQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableRowsQuery,
            Types.AwardedProjectsTableRowsQueryVariables
          >(AwardedProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows.infinite", variables]

useInfiniteAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows.infinite"

export const useSuspenseInfiniteAwardedProjectsTableRowsQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableRowsQuery,
            Types.AwardedProjectsTableRowsQueryVariables
          >(AwardedProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows.infinite", variables]

useSuspenseInfiniteAwardedProjectsTableRowsQuery.rootKey =
  "AwardedProjectsTableRows.infinite"

useAwardedProjectsTableRowsQuery.fetcher = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardedProjectsTableRowsQuery,
    Types.AwardedProjectsTableRowsQueryVariables
  >(AwardedProjectsTableRowsDocument, variables, options)

export const ProjectTasksForEditingDocument = new TypedDocumentString(`
    query ProjectTasksForEditing($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      status
      finproductType
      executionPlanTasks {
        ...EnergyProjectTask
      }
    }
  }
}
    fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useProjectTasksForEditingQuery = <
  TData = Types.ProjectTasksForEditingQuery,
  TError = unknown,
>(
  variables: Types.ProjectTasksForEditingQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectTasksForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectTasksForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectTasksForEditingQuery, TError, TData>({
    queryKey: ["ProjectTasksForEditing", variables],
    queryFn: publicFetcher<
      Types.ProjectTasksForEditingQuery,
      Types.ProjectTasksForEditingQueryVariables
    >(ProjectTasksForEditingDocument, variables),
    ...options,
  })
}

useProjectTasksForEditingQuery.getKey = (
  variables: Types.ProjectTasksForEditingQueryVariables,
) => ["ProjectTasksForEditing", variables]

useProjectTasksForEditingQuery.rootKey = "ProjectTasksForEditing"

export const useSuspenseProjectTasksForEditingQuery = <
  TData = Types.ProjectTasksForEditingQuery,
  TError = unknown,
>(
  variables: Types.ProjectTasksForEditingQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectTasksForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectTasksForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectTasksForEditingQuery, TError, TData>({
    queryKey: ["ProjectTasksForEditing", variables],
    queryFn: publicFetcher<
      Types.ProjectTasksForEditingQuery,
      Types.ProjectTasksForEditingQueryVariables
    >(ProjectTasksForEditingDocument, variables),
    ...options,
  })
}

useSuspenseProjectTasksForEditingQuery.getKey = (
  variables: Types.ProjectTasksForEditingQueryVariables,
) => ["ProjectTasksForEditing", variables]

useSuspenseProjectTasksForEditingQuery.rootKey = "ProjectTasksForEditing"

export const useInfiniteProjectTasksForEditingQuery = <
  TData = InfiniteData<Types.ProjectTasksForEditingQuery>,
  TError = unknown,
>(
  variables: Types.ProjectTasksForEditingQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectTasksForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectTasksForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectTasksForEditingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectTasksForEditing.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectTasksForEditingQuery,
            Types.ProjectTasksForEditingQueryVariables
          >(ProjectTasksForEditingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectTasksForEditingQuery.getKey = (
  variables: Types.ProjectTasksForEditingQueryVariables,
) => ["ProjectTasksForEditing.infinite", variables]

useInfiniteProjectTasksForEditingQuery.rootKey = "ProjectTasksForEditing.infinite"

export const useSuspenseInfiniteProjectTasksForEditingQuery = <
  TData = InfiniteData<Types.ProjectTasksForEditingQuery>,
  TError = unknown,
>(
  variables: Types.ProjectTasksForEditingQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectTasksForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectTasksForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectTasksForEditingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectTasksForEditing.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectTasksForEditingQuery,
            Types.ProjectTasksForEditingQueryVariables
          >(ProjectTasksForEditingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectTasksForEditingQuery.getKey = (
  variables: Types.ProjectTasksForEditingQueryVariables,
) => ["ProjectTasksForEditing.infinite", variables]

useSuspenseInfiniteProjectTasksForEditingQuery.rootKey =
  "ProjectTasksForEditing.infinite"

useProjectTasksForEditingQuery.fetcher = (
  variables: Types.ProjectTasksForEditingQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectTasksForEditingQuery,
    Types.ProjectTasksForEditingQueryVariables
  >(ProjectTasksForEditingDocument, variables, options)

export const OwnerProjectsTableViewDocument = new TypedDocumentString(`
    query OwnerProjectsTableView($orgSlug: String!, $filterBy: OwnerProjectTableFilter, $sortBy: [OwnerProjectTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    pid
    name
    ownerProjects {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useOwnerProjectsTableViewQuery = <
  TData = Types.OwnerProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OwnerProjectsTableViewQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableViewQuery,
      Types.OwnerProjectsTableViewQueryVariables
    >(OwnerProjectsTableViewDocument, variables),
    ...options,
  })
}

useOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableView", variables]

useOwnerProjectsTableViewQuery.rootKey = "OwnerProjectsTableView"

export const useSuspenseOwnerProjectsTableViewQuery = <
  TData = Types.OwnerProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OwnerProjectsTableViewQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableViewQuery,
      Types.OwnerProjectsTableViewQueryVariables
    >(OwnerProjectsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableView", variables]

useSuspenseOwnerProjectsTableViewQuery.rootKey = "OwnerProjectsTableView"

export const useInfiniteOwnerProjectsTableViewQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OwnerProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableViewQuery,
            Types.OwnerProjectsTableViewQueryVariables
          >(OwnerProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableView.infinite", variables]

useInfiniteOwnerProjectsTableViewQuery.rootKey = "OwnerProjectsTableView.infinite"

export const useSuspenseInfiniteOwnerProjectsTableViewQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OwnerProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableViewQuery,
            Types.OwnerProjectsTableViewQueryVariables
          >(OwnerProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableView.infinite", variables]

useSuspenseInfiniteOwnerProjectsTableViewQuery.rootKey =
  "OwnerProjectsTableView.infinite"

useOwnerProjectsTableViewQuery.fetcher = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OwnerProjectsTableViewQuery,
    Types.OwnerProjectsTableViewQueryVariables
  >(OwnerProjectsTableViewDocument, variables, options)

export const OwnerProjectsTableFacetsDocument = new TypedDocumentString(`
    query OwnerProjectsTableFacets($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    ownerProjects {
      view {
        count
        stats {
          awardedDevelopers
          states
          vettingInterestCount
          preparingLaunchCount
          collectingBidsCount
          reviewingBidsCount
          negotiatingContractsCount
          developingProjectCount
          underConstructionCount
          shippedCount
        }
      }
    }
  }
}
    `)

export const useOwnerProjectsTableFacetsQuery = <
  TData = Types.OwnerProjectsTableFacetsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableFacets", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableFacetsQuery,
      Types.OwnerProjectsTableFacetsQueryVariables
    >(OwnerProjectsTableFacetsDocument, variables),
    ...options,
  })
}

useOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacets", variables]

useOwnerProjectsTableFacetsQuery.rootKey = "OwnerProjectsTableFacets"

export const useSuspenseOwnerProjectsTableFacetsQuery = <
  TData = Types.OwnerProjectsTableFacetsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableFacets", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableFacetsQuery,
      Types.OwnerProjectsTableFacetsQueryVariables
    >(OwnerProjectsTableFacetsDocument, variables),
    ...options,
  })
}

useSuspenseOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacets", variables]

useSuspenseOwnerProjectsTableFacetsQuery.rootKey = "OwnerProjectsTableFacets"

export const useInfiniteOwnerProjectsTableFacetsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableFacetsQuery,
            Types.OwnerProjectsTableFacetsQueryVariables
          >(OwnerProjectsTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacets.infinite", variables]

useInfiniteOwnerProjectsTableFacetsQuery.rootKey = "OwnerProjectsTableFacets.infinite"

export const useSuspenseInfiniteOwnerProjectsTableFacetsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableFacetsQuery,
            Types.OwnerProjectsTableFacetsQueryVariables
          >(OwnerProjectsTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacets.infinite", variables]

useSuspenseInfiniteOwnerProjectsTableFacetsQuery.rootKey =
  "OwnerProjectsTableFacets.infinite"

useOwnerProjectsTableFacetsQuery.fetcher = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OwnerProjectsTableFacetsQuery,
    Types.OwnerProjectsTableFacetsQueryVariables
  >(OwnerProjectsTableFacetsDocument, variables, options)

export const OwnerMarketplaceSummaryDocument = new TypedDocumentString(`
    query OwnerMarketplaceSummary($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    ownerProjects {
      view {
        stats {
          activeCount
        }
      }
    }
  }
}
    `)

export const useOwnerMarketplaceSummaryQuery = <
  TData = Types.OwnerMarketplaceSummaryQuery,
  TError = unknown,
>(
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OwnerMarketplaceSummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OwnerMarketplaceSummaryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OwnerMarketplaceSummaryQuery, TError, TData>({
    queryKey: ["OwnerMarketplaceSummary", variables],
    queryFn: publicFetcher<
      Types.OwnerMarketplaceSummaryQuery,
      Types.OwnerMarketplaceSummaryQueryVariables
    >(OwnerMarketplaceSummaryDocument, variables),
    ...options,
  })
}

useOwnerMarketplaceSummaryQuery.getKey = (
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
) => ["OwnerMarketplaceSummary", variables]

useOwnerMarketplaceSummaryQuery.rootKey = "OwnerMarketplaceSummary"

export const useSuspenseOwnerMarketplaceSummaryQuery = <
  TData = Types.OwnerMarketplaceSummaryQuery,
  TError = unknown,
>(
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OwnerMarketplaceSummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OwnerMarketplaceSummaryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OwnerMarketplaceSummaryQuery, TError, TData>({
    queryKey: ["OwnerMarketplaceSummary", variables],
    queryFn: publicFetcher<
      Types.OwnerMarketplaceSummaryQuery,
      Types.OwnerMarketplaceSummaryQueryVariables
    >(OwnerMarketplaceSummaryDocument, variables),
    ...options,
  })
}

useSuspenseOwnerMarketplaceSummaryQuery.getKey = (
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
) => ["OwnerMarketplaceSummary", variables]

useSuspenseOwnerMarketplaceSummaryQuery.rootKey = "OwnerMarketplaceSummary"

export const useInfiniteOwnerMarketplaceSummaryQuery = <
  TData = InfiniteData<Types.OwnerMarketplaceSummaryQuery>,
  TError = unknown,
>(
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OwnerMarketplaceSummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OwnerMarketplaceSummaryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OwnerMarketplaceSummaryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerMarketplaceSummary.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerMarketplaceSummaryQuery,
            Types.OwnerMarketplaceSummaryQueryVariables
          >(OwnerMarketplaceSummaryDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOwnerMarketplaceSummaryQuery.getKey = (
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
) => ["OwnerMarketplaceSummary.infinite", variables]

useInfiniteOwnerMarketplaceSummaryQuery.rootKey = "OwnerMarketplaceSummary.infinite"

export const useSuspenseInfiniteOwnerMarketplaceSummaryQuery = <
  TData = InfiniteData<Types.OwnerMarketplaceSummaryQuery>,
  TError = unknown,
>(
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OwnerMarketplaceSummaryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OwnerMarketplaceSummaryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OwnerMarketplaceSummaryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerMarketplaceSummary.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerMarketplaceSummaryQuery,
            Types.OwnerMarketplaceSummaryQueryVariables
          >(OwnerMarketplaceSummaryDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOwnerMarketplaceSummaryQuery.getKey = (
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
) => ["OwnerMarketplaceSummary.infinite", variables]

useSuspenseInfiniteOwnerMarketplaceSummaryQuery.rootKey =
  "OwnerMarketplaceSummary.infinite"

useOwnerMarketplaceSummaryQuery.fetcher = (
  variables: Types.OwnerMarketplaceSummaryQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OwnerMarketplaceSummaryQuery,
    Types.OwnerMarketplaceSummaryQueryVariables
  >(OwnerMarketplaceSummaryDocument, variables, options)

export const OwnerProjectsTableRowsDocument = new TypedDocumentString(`
    query OwnerProjectsTableRows($orgSlug: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    name
    ownerProjects {
      rows(keys: $keys) {
        id
        nextUpDate
        nextUpTasks {
          pid
          description
          dueDate
          overdue
          isEstimate
          assigneeGroup
        }
        developerName
        projectPid
        sitePid
        name
        projectName
        streetAddress
        city
        state
        zipCode
        projectStatus
        projectIsClosed
        projectClosedAt
        projectCloseReason
        projectCloseCustomReason
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        utilityName
        buildingType
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        buildingClass
        yearBuilt
        assetManager
        portfolioManager
        roofReplacedYr
        roofCondition
        projectValueY1Usd
        roofRentY1Usd
        roofRentEscalator
        ppaPriceEscalator
        ppaPriceY1Usd
        termYears
        financingType
        specFinproductType
        awardedToDeveloperDate
        awardedToDeveloperEstimated
        awardedToDeveloperActual
        leaseSignedDate
        leaseSignedEstimated
        leaseSignedActual
        ntpDate
        ntpEstimated
        ntpActual
        codDate
        codEstimated
        codActual
        csProgramApprovedDate
        csProgramApprovedEstimated
        csProgramApprovedActual
        ixApprovedDate
        ixApprovedEstimated
        ixApprovedActual
        bidPackagePid
        bidPackageName
        storageSizeKwh
        progressNotes {
          ...ProgressNotes
        }
        hasAction
        progressActionItemUpdatedAt
        progressPriority
        isFannieMaeLender
        propertyLenders
        isFmGlobalInsurer
        propertyInsurer
        propertyFundName
        leaseSignatory
      }
    }
  }
}
    fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useOwnerProjectsTableRowsQuery = <
  TData = Types.OwnerProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableRowsQuery,
      Types.OwnerProjectsTableRowsQueryVariables
    >(OwnerProjectsTableRowsDocument, variables),
    ...options,
  })
}

useOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRows", variables]

useOwnerProjectsTableRowsQuery.rootKey = "OwnerProjectsTableRows"

export const useSuspenseOwnerProjectsTableRowsQuery = <
  TData = Types.OwnerProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableRowsQuery,
      Types.OwnerProjectsTableRowsQueryVariables
    >(OwnerProjectsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRows", variables]

useSuspenseOwnerProjectsTableRowsQuery.rootKey = "OwnerProjectsTableRows"

export const useInfiniteOwnerProjectsTableRowsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableRowsQuery,
            Types.OwnerProjectsTableRowsQueryVariables
          >(OwnerProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRows.infinite", variables]

useInfiniteOwnerProjectsTableRowsQuery.rootKey = "OwnerProjectsTableRows.infinite"

export const useSuspenseInfiniteOwnerProjectsTableRowsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableRowsQuery,
            Types.OwnerProjectsTableRowsQueryVariables
          >(OwnerProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRows.infinite", variables]

useSuspenseInfiniteOwnerProjectsTableRowsQuery.rootKey =
  "OwnerProjectsTableRows.infinite"

useOwnerProjectsTableRowsQuery.fetcher = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OwnerProjectsTableRowsQuery,
    Types.OwnerProjectsTableRowsQueryVariables
  >(OwnerProjectsTableRowsDocument, variables, options)

export const SiteBidsTableViewDocument = new TypedDocumentString(`
    query SiteBidsTableView($orgSlug: String!, $sitePid: String!, $filterBy: SiteBidsTableFilter, $sortBy: [SiteBidsTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    siteBids(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useSiteBidsTableViewQuery = <
  TData = Types.SiteBidsTableViewQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsTableViewQuery, TError, TData>({
    queryKey: ["SiteBidsTableView", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableViewQuery,
      Types.SiteBidsTableViewQueryVariables
    >(SiteBidsTableViewDocument, variables),
    ...options,
  })
}

useSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView", variables]

useSiteBidsTableViewQuery.rootKey = "SiteBidsTableView"

export const useSuspenseSiteBidsTableViewQuery = <
  TData = Types.SiteBidsTableViewQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsTableViewQuery, TError, TData>({
    queryKey: ["SiteBidsTableView", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableViewQuery,
      Types.SiteBidsTableViewQueryVariables
    >(SiteBidsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView", variables]

useSuspenseSiteBidsTableViewQuery.rootKey = "SiteBidsTableView"

export const useInfiniteSiteBidsTableViewQuery = <
  TData = InfiniteData<Types.SiteBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableViewQuery,
            Types.SiteBidsTableViewQueryVariables
          >(SiteBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView.infinite", variables]

useInfiniteSiteBidsTableViewQuery.rootKey = "SiteBidsTableView.infinite"

export const useSuspenseInfiniteSiteBidsTableViewQuery = <
  TData = InfiniteData<Types.SiteBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableViewQuery,
            Types.SiteBidsTableViewQueryVariables
          >(SiteBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView.infinite", variables]

useSuspenseInfiniteSiteBidsTableViewQuery.rootKey = "SiteBidsTableView.infinite"

useSiteBidsTableViewQuery.fetcher = (
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsTableViewQuery, Types.SiteBidsTableViewQueryVariables>(
    SiteBidsTableViewDocument,
    variables,
    options,
  )

export const SiteBidsTableRowsDocument = new TypedDocumentString(`
    query SiteBidsTableRows($orgSlug: String!, $sitePid: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    siteBids(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        bidPackagePid
        bundlePid
        bidPackageName
        name
        isAwarded
        isCustom
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        estimatedCod
        estimatedNtp
        csEstimatedAwardDate
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        termYears
        roofRentY1Usd
        roofRentEscalator
        buildingOwnerExtraPaymentsUsd
        extraPaymentsNotes
        buildingOwnerNoiLifetimeUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostUtilityMinUsd
        interconnectionCostNonUtilityUsd
        interconnectionCostUsdPerWatt
        interconnectionNotes
        itcPercentageAssumption
        recOwner
        incentivesNotes
        otherNotes
      }
    }
  }
}
    `)

export const useSiteBidsTableRowsQuery = <
  TData = Types.SiteBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsTableRowsQuery, TError, TData>({
    queryKey: ["SiteBidsTableRows", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableRowsQuery,
      Types.SiteBidsTableRowsQueryVariables
    >(SiteBidsTableRowsDocument, variables),
    ...options,
  })
}

useSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows", variables]

useSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows"

export const useSuspenseSiteBidsTableRowsQuery = <
  TData = Types.SiteBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsTableRowsQuery, TError, TData>({
    queryKey: ["SiteBidsTableRows", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableRowsQuery,
      Types.SiteBidsTableRowsQueryVariables
    >(SiteBidsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows", variables]

useSuspenseSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows"

export const useInfiniteSiteBidsTableRowsQuery = <
  TData = InfiniteData<Types.SiteBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableRowsQuery,
            Types.SiteBidsTableRowsQueryVariables
          >(SiteBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows.infinite", variables]

useInfiniteSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows.infinite"

export const useSuspenseInfiniteSiteBidsTableRowsQuery = <
  TData = InfiniteData<Types.SiteBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableRowsQuery,
            Types.SiteBidsTableRowsQueryVariables
          >(SiteBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows.infinite", variables]

useSuspenseInfiniteSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows.infinite"

useSiteBidsTableRowsQuery.fetcher = (
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsTableRowsQuery, Types.SiteBidsTableRowsQueryVariables>(
    SiteBidsTableRowsDocument,
    variables,
    options,
  )

export const SiteFieldChangeLogDocument = new TypedDocumentString(`
    query SiteFieldChangeLog($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    name
    pid
    site(pid: $sitePid) {
      pid
      fieldHistory {
        ...SiteFieldChangeLog
      }
      siteDetails {
        fieldHistory {
          ...SiteDetailsFieldChangeLog
        }
      }
    }
  }
}
    fragment ChangeLogEntry on ChangeLogEntry {
  source
  sourceId
  oldValue
  newValue
  columnType
  changedAt
}
fragment FieldChangeLogHistory on FieldChangeLogHistory {
  latest {
    ...ChangeLogEntry
  }
  history {
    ...ChangeLogEntry
  }
}
fragment SiteFieldChangeLog on SiteFieldChangeLog {
  occupancyType {
    ...FieldChangeLogHistory
  }
  leaseType {
    ...FieldChangeLogHistory
  }
  targetUtilityId {
    ...FieldChangeLogHistory
  }
  tenantLeaseTerm {
    ...FieldChangeLogHistory
  }
}
fragment SiteDetailsFieldChangeLog on SiteDetailsFieldChangeLog {
  buildingLegalName {
    ...FieldChangeLogHistory
  }
  constructionEmail {
    ...FieldChangeLogHistory
  }
  counselApproval {
    ...FieldChangeLogHistory
  }
  externalLeaseApprovers {
    ...FieldChangeLogHistory
  }
  isFannieMaeLender {
    ...FieldChangeLogHistory
  }
  isFmGlobalInsurer {
    ...FieldChangeLogHistory
  }
  creditRating {
    ...FieldChangeLogHistory
  }
  leaseApprovers {
    ...FieldChangeLogHistory
  }
  leaseLegalOwnerName {
    ...FieldChangeLogHistory
  }
  leasePropertyLegalDescription {
    ...FieldChangeLogHistory
  }
  leaseSignatory {
    ...FieldChangeLogHistory
  }
  needsTenantApproval {
    ...FieldChangeLogHistory
  }
  propertyFundName {
    ...FieldChangeLogHistory
  }
  propertyInsurer {
    ...FieldChangeLogHistory
  }
  propertyLenders {
    ...FieldChangeLogHistory
  }
  propertySiteVisitContacts {
    ...FieldChangeLogHistory
  }
  roofCondition {
    ...FieldChangeLogHistory
  }
  roofReplacedYr {
    ...FieldChangeLogHistory
  }
  roofRightsHolder {
    ...FieldChangeLogHistory
  }
  salePlannedYear {
    ...FieldChangeLogHistory
  }
  salePlans {
    ...FieldChangeLogHistory
  }
  signatureBlocks {
    ...FieldChangeLogHistory
  }
}`)

export const useSiteFieldChangeLogQuery = <
  TData = Types.SiteFieldChangeLogQuery,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteFieldChangeLogQuery, TError, TData>({
    queryKey: ["SiteFieldChangeLog", variables],
    queryFn: publicFetcher<
      Types.SiteFieldChangeLogQuery,
      Types.SiteFieldChangeLogQueryVariables
    >(SiteFieldChangeLogDocument, variables),
    ...options,
  })
}

useSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog", variables]

useSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog"

export const useSuspenseSiteFieldChangeLogQuery = <
  TData = Types.SiteFieldChangeLogQuery,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteFieldChangeLogQuery, TError, TData>({
    queryKey: ["SiteFieldChangeLog", variables],
    queryFn: publicFetcher<
      Types.SiteFieldChangeLogQuery,
      Types.SiteFieldChangeLogQueryVariables
    >(SiteFieldChangeLogDocument, variables),
    ...options,
  })
}

useSuspenseSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog", variables]

useSuspenseSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog"

export const useInfiniteSiteFieldChangeLogQuery = <
  TData = InfiniteData<Types.SiteFieldChangeLogQuery>,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteFieldChangeLogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFieldChangeLog.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFieldChangeLogQuery,
            Types.SiteFieldChangeLogQueryVariables
          >(SiteFieldChangeLogDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog.infinite", variables]

useInfiniteSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog.infinite"

export const useSuspenseInfiniteSiteFieldChangeLogQuery = <
  TData = InfiniteData<Types.SiteFieldChangeLogQuery>,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteFieldChangeLogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFieldChangeLog.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFieldChangeLogQuery,
            Types.SiteFieldChangeLogQueryVariables
          >(SiteFieldChangeLogDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog.infinite", variables]

useSuspenseInfiniteSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog.infinite"

useSiteFieldChangeLogQuery.fetcher = (
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteFieldChangeLogQuery, Types.SiteFieldChangeLogQueryVariables>(
    SiteFieldChangeLogDocument,
    variables,
    options,
  )

export const AdminUsersDocument = new TypedDocumentString(`
    query AdminUsers($jobFunction: JobFunction) {
  admin {
    activeAdmins(jobFunction: $jobFunction) {
      ...UserRef
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAdminUsersQuery = <TData = Types.AdminUsersQuery, TError = unknown>(
  variables?: Types.AdminUsersQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminUsersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminUsersQuery, TError, TData>({
    queryKey: variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables],
    queryFn: publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
      AdminUsersDocument,
      variables,
    ),
    ...options,
  })
}

useAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables]

useAdminUsersQuery.rootKey = "AdminUsers"

export const useSuspenseAdminUsersQuery = <
  TData = Types.AdminUsersQuery,
  TError = unknown,
>(
  variables?: Types.AdminUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUsersQuery, TError, TData>({
    queryKey: variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables],
    queryFn: publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
      AdminUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables]

useSuspenseAdminUsersQuery.rootKey = "AdminUsers"

export const useInfiniteAdminUsersQuery = <
  TData = InfiniteData<Types.AdminUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUsers.infinite"]
            : ["AdminUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
            AdminUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsers.infinite"] : ["AdminUsers.infinite", variables]

useInfiniteAdminUsersQuery.rootKey = "AdminUsers.infinite"

export const useSuspenseInfiniteAdminUsersQuery = <
  TData = InfiniteData<Types.AdminUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUsers.infinite"]
            : ["AdminUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
            AdminUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUsersQuery.getKey = (
  variables?: Types.AdminUsersQueryVariables,
) =>
  variables === undefined ? ["AdminUsers.infinite"] : ["AdminUsers.infinite", variables]

useSuspenseInfiniteAdminUsersQuery.rootKey = "AdminUsers.infinite"

useAdminUsersQuery.fetcher = (
  variables?: Types.AdminUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
    AdminUsersDocument,
    variables,
    options,
  )

export const PortfolioPdfAdvisorsDocument = new TypedDocumentString(`
    query PortfolioPdfAdvisors {
  admin {
    activeAdmins {
      ...UserRef
      meetingSchedulingUrl
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const usePortfolioPdfAdvisorsQuery = <
  TData = Types.PortfolioPdfAdvisorsQuery,
  TError = unknown,
>(
  variables?: Types.PortfolioPdfAdvisorsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioPdfAdvisorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PortfolioPdfAdvisorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioPdfAdvisorsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["PortfolioPdfAdvisors"]
        : ["PortfolioPdfAdvisors", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfAdvisorsQuery,
      Types.PortfolioPdfAdvisorsQueryVariables
    >(PortfolioPdfAdvisorsDocument, variables),
    ...options,
  })
}

usePortfolioPdfAdvisorsQuery.getKey = (
  variables?: Types.PortfolioPdfAdvisorsQueryVariables,
) =>
  variables === undefined
    ? ["PortfolioPdfAdvisors"]
    : ["PortfolioPdfAdvisors", variables]

usePortfolioPdfAdvisorsQuery.rootKey = "PortfolioPdfAdvisors"

export const useSuspensePortfolioPdfAdvisorsQuery = <
  TData = Types.PortfolioPdfAdvisorsQuery,
  TError = unknown,
>(
  variables?: Types.PortfolioPdfAdvisorsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioPdfAdvisorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioPdfAdvisorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioPdfAdvisorsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["PortfolioPdfAdvisors"]
        : ["PortfolioPdfAdvisors", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfAdvisorsQuery,
      Types.PortfolioPdfAdvisorsQueryVariables
    >(PortfolioPdfAdvisorsDocument, variables),
    ...options,
  })
}

useSuspensePortfolioPdfAdvisorsQuery.getKey = (
  variables?: Types.PortfolioPdfAdvisorsQueryVariables,
) =>
  variables === undefined
    ? ["PortfolioPdfAdvisors"]
    : ["PortfolioPdfAdvisors", variables]

useSuspensePortfolioPdfAdvisorsQuery.rootKey = "PortfolioPdfAdvisors"

export const useInfinitePortfolioPdfAdvisorsQuery = <
  TData = InfiniteData<Types.PortfolioPdfAdvisorsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfAdvisorsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioPdfAdvisorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioPdfAdvisorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioPdfAdvisorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["PortfolioPdfAdvisors.infinite"]
            : ["PortfolioPdfAdvisors.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfAdvisorsQuery,
            Types.PortfolioPdfAdvisorsQueryVariables
          >(PortfolioPdfAdvisorsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioPdfAdvisorsQuery.getKey = (
  variables?: Types.PortfolioPdfAdvisorsQueryVariables,
) =>
  variables === undefined
    ? ["PortfolioPdfAdvisors.infinite"]
    : ["PortfolioPdfAdvisors.infinite", variables]

useInfinitePortfolioPdfAdvisorsQuery.rootKey = "PortfolioPdfAdvisors.infinite"

export const useSuspenseInfinitePortfolioPdfAdvisorsQuery = <
  TData = InfiniteData<Types.PortfolioPdfAdvisorsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfAdvisorsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioPdfAdvisorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioPdfAdvisorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioPdfAdvisorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["PortfolioPdfAdvisors.infinite"]
            : ["PortfolioPdfAdvisors.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfAdvisorsQuery,
            Types.PortfolioPdfAdvisorsQueryVariables
          >(PortfolioPdfAdvisorsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioPdfAdvisorsQuery.getKey = (
  variables?: Types.PortfolioPdfAdvisorsQueryVariables,
) =>
  variables === undefined
    ? ["PortfolioPdfAdvisors.infinite"]
    : ["PortfolioPdfAdvisors.infinite", variables]

useSuspenseInfinitePortfolioPdfAdvisorsQuery.rootKey = "PortfolioPdfAdvisors.infinite"

usePortfolioPdfAdvisorsQuery.fetcher = (
  variables?: Types.PortfolioPdfAdvisorsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PortfolioPdfAdvisorsQuery,
    Types.PortfolioPdfAdvisorsQueryVariables
  >(PortfolioPdfAdvisorsDocument, variables, options)

export const CollectingBidsNoticeDocument = new TypedDocumentString(`
    query CollectingBidsNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    biddingClosedAt
    recvr {
      orgUsers {
        ...OrgUser
      }
    }
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useCollectingBidsNoticeQuery = <
  TData = Types.CollectingBidsNoticeQuery,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CollectingBidsNoticeQuery, TError, TData>({
    queryKey: ["CollectingBidsNotice", variables],
    queryFn: publicFetcher<
      Types.CollectingBidsNoticeQuery,
      Types.CollectingBidsNoticeQueryVariables
    >(CollectingBidsNoticeDocument, variables),
    ...options,
  })
}

useCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice", variables]

useCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice"

export const useSuspenseCollectingBidsNoticeQuery = <
  TData = Types.CollectingBidsNoticeQuery,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CollectingBidsNoticeQuery, TError, TData>({
    queryKey: ["CollectingBidsNotice", variables],
    queryFn: publicFetcher<
      Types.CollectingBidsNoticeQuery,
      Types.CollectingBidsNoticeQueryVariables
    >(CollectingBidsNoticeDocument, variables),
    ...options,
  })
}

useSuspenseCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice", variables]

useSuspenseCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice"

export const useInfiniteCollectingBidsNoticeQuery = <
  TData = InfiniteData<Types.CollectingBidsNoticeQuery>,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CollectingBidsNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CollectingBidsNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CollectingBidsNoticeQuery,
            Types.CollectingBidsNoticeQueryVariables
          >(CollectingBidsNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice.infinite", variables]

useInfiniteCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice.infinite"

export const useSuspenseInfiniteCollectingBidsNoticeQuery = <
  TData = InfiniteData<Types.CollectingBidsNoticeQuery>,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CollectingBidsNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CollectingBidsNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CollectingBidsNoticeQuery,
            Types.CollectingBidsNoticeQueryVariables
          >(CollectingBidsNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice.infinite", variables]

useSuspenseInfiniteCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice.infinite"

useCollectingBidsNoticeQuery.fetcher = (
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CollectingBidsNoticeQuery,
    Types.CollectingBidsNoticeQueryVariables
  >(CollectingBidsNoticeDocument, variables, options)

export const LeaseExecutionDetailsRequestDocument = new TypedDocumentString(`
    query LeaseExecutionDetailsRequest($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    recvr {
      orgUsers {
        ...OrgUser
      }
    }
    rows {
      id
      site {
        pid
        name
      }
      portfolio {
        pid
        title
      }
      awardedFinancier {
        name
      }
    }
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useLeaseExecutionDetailsRequestQuery = <
  TData = Types.LeaseExecutionDetailsRequestQuery,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>({
    queryKey: ["LeaseExecutionDetailsRequest", variables],
    queryFn: publicFetcher<
      Types.LeaseExecutionDetailsRequestQuery,
      Types.LeaseExecutionDetailsRequestQueryVariables
    >(LeaseExecutionDetailsRequestDocument, variables),
    ...options,
  })
}

useLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest", variables]

useLeaseExecutionDetailsRequestQuery.rootKey = "LeaseExecutionDetailsRequest"

export const useSuspenseLeaseExecutionDetailsRequestQuery = <
  TData = Types.LeaseExecutionDetailsRequestQuery,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>({
    queryKey: ["LeaseExecutionDetailsRequest", variables],
    queryFn: publicFetcher<
      Types.LeaseExecutionDetailsRequestQuery,
      Types.LeaseExecutionDetailsRequestQueryVariables
    >(LeaseExecutionDetailsRequestDocument, variables),
    ...options,
  })
}

useSuspenseLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest", variables]

useSuspenseLeaseExecutionDetailsRequestQuery.rootKey = "LeaseExecutionDetailsRequest"

export const useInfiniteLeaseExecutionDetailsRequestQuery = <
  TData = InfiniteData<Types.LeaseExecutionDetailsRequestQuery>,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "LeaseExecutionDetailsRequest.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.LeaseExecutionDetailsRequestQuery,
            Types.LeaseExecutionDetailsRequestQueryVariables
          >(LeaseExecutionDetailsRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest.infinite", variables]

useInfiniteLeaseExecutionDetailsRequestQuery.rootKey =
  "LeaseExecutionDetailsRequest.infinite"

export const useSuspenseInfiniteLeaseExecutionDetailsRequestQuery = <
  TData = InfiniteData<Types.LeaseExecutionDetailsRequestQuery>,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.LeaseExecutionDetailsRequestQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "LeaseExecutionDetailsRequest.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.LeaseExecutionDetailsRequestQuery,
            Types.LeaseExecutionDetailsRequestQueryVariables
          >(LeaseExecutionDetailsRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest.infinite", variables]

useSuspenseInfiniteLeaseExecutionDetailsRequestQuery.rootKey =
  "LeaseExecutionDetailsRequest.infinite"

useLeaseExecutionDetailsRequestQuery.fetcher = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.LeaseExecutionDetailsRequestQuery,
    Types.LeaseExecutionDetailsRequestQueryVariables
  >(LeaseExecutionDetailsRequestDocument, variables, options)

export const DeveloperDiligenceRequestDocument = new TypedDocumentString(`
    query DeveloperDiligenceRequest($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    recvr {
      orgUsers {
        ...OrgUser
      }
    }
    rows {
      id
      site {
        pid
        name
      }
      awardedFinancier {
        name
      }
    }
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useDeveloperDiligenceRequestQuery = <
  TData = Types.DeveloperDiligenceRequestQuery,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>({
    queryKey: ["DeveloperDiligenceRequest", variables],
    queryFn: publicFetcher<
      Types.DeveloperDiligenceRequestQuery,
      Types.DeveloperDiligenceRequestQueryVariables
    >(DeveloperDiligenceRequestDocument, variables),
    ...options,
  })
}

useDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest", variables]

useDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest"

export const useSuspenseDeveloperDiligenceRequestQuery = <
  TData = Types.DeveloperDiligenceRequestQuery,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>({
    queryKey: ["DeveloperDiligenceRequest", variables],
    queryFn: publicFetcher<
      Types.DeveloperDiligenceRequestQuery,
      Types.DeveloperDiligenceRequestQueryVariables
    >(DeveloperDiligenceRequestDocument, variables),
    ...options,
  })
}

useSuspenseDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest", variables]

useSuspenseDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest"

export const useInfiniteDeveloperDiligenceRequestQuery = <
  TData = InfiniteData<Types.DeveloperDiligenceRequestQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperDiligenceRequest.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperDiligenceRequestQuery,
            Types.DeveloperDiligenceRequestQueryVariables
          >(DeveloperDiligenceRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest.infinite", variables]

useInfiniteDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest.infinite"

export const useSuspenseInfiniteDeveloperDiligenceRequestQuery = <
  TData = InfiniteData<Types.DeveloperDiligenceRequestQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperDiligenceRequest.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperDiligenceRequestQuery,
            Types.DeveloperDiligenceRequestQueryVariables
          >(DeveloperDiligenceRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest.infinite", variables]

useSuspenseInfiniteDeveloperDiligenceRequestQuery.rootKey =
  "DeveloperDiligenceRequest.infinite"

useDeveloperDiligenceRequestQuery.fetcher = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeveloperDiligenceRequestQuery,
    Types.DeveloperDiligenceRequestQueryVariables
  >(DeveloperDiligenceRequestDocument, variables, options)

export const EmailSenderDocument = new TypedDocumentString(`
    query EmailSender {
  currentUser {
    firstName
    meetingSchedulingUrl
  }
  googleApi {
    sendAs {
      signature
    }
  }
}
    `)

export const useEmailSenderQuery = <TData = Types.EmailSenderQuery, TError = unknown>(
  variables?: Types.EmailSenderQueryVariables,
  options?: Omit<UseQueryOptions<Types.EmailSenderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EmailSenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EmailSenderQuery, TError, TData>({
    queryKey: variables === undefined ? ["EmailSender"] : ["EmailSender", variables],
    queryFn: publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
      EmailSenderDocument,
      variables,
    ),
    ...options,
  })
}

useEmailSenderQuery.getKey = (variables?: Types.EmailSenderQueryVariables) =>
  variables === undefined ? ["EmailSender"] : ["EmailSender", variables]

useEmailSenderQuery.rootKey = "EmailSender"

export const useSuspenseEmailSenderQuery = <
  TData = Types.EmailSenderQuery,
  TError = unknown,
>(
  variables?: Types.EmailSenderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EmailSenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.EmailSenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EmailSenderQuery, TError, TData>({
    queryKey: variables === undefined ? ["EmailSender"] : ["EmailSender", variables],
    queryFn: publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
      EmailSenderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEmailSenderQuery.getKey = (variables?: Types.EmailSenderQueryVariables) =>
  variables === undefined ? ["EmailSender"] : ["EmailSender", variables]

useSuspenseEmailSenderQuery.rootKey = "EmailSender"

export const useInfiniteEmailSenderQuery = <
  TData = InfiniteData<Types.EmailSenderQuery>,
  TError = unknown,
>(
  variables: Types.EmailSenderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EmailSenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.EmailSenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EmailSenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["EmailSender.infinite"]
            : ["EmailSender.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
            EmailSenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEmailSenderQuery.getKey = (variables?: Types.EmailSenderQueryVariables) =>
  variables === undefined
    ? ["EmailSender.infinite"]
    : ["EmailSender.infinite", variables]

useInfiniteEmailSenderQuery.rootKey = "EmailSender.infinite"

export const useSuspenseInfiniteEmailSenderQuery = <
  TData = InfiniteData<Types.EmailSenderQuery>,
  TError = unknown,
>(
  variables: Types.EmailSenderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EmailSenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EmailSenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EmailSenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["EmailSender.infinite"]
            : ["EmailSender.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
            EmailSenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEmailSenderQuery.getKey = (
  variables?: Types.EmailSenderQueryVariables,
) =>
  variables === undefined
    ? ["EmailSender.infinite"]
    : ["EmailSender.infinite", variables]

useSuspenseInfiniteEmailSenderQuery.rootKey = "EmailSender.infinite"

useEmailSenderQuery.fetcher = (
  variables?: Types.EmailSenderQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
    EmailSenderDocument,
    variables,
    options,
  )

export const ReminderToBidDocument = new TypedDocumentString(`
    query ReminderToBid($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    biddingClosedAt
    type
    totalPvSizeKwDc
    shortPricingNote
    recvr {
      slug
      name
    }
    bundles {
      pid
      financier {
        orgSlug
        name
        orgUsers {
          ...OrgUser
        }
      }
      totalBids
      optOut {
        archivedAt
      }
      archivedAt
      rows {
        specs {
          optOut {
            archivedAt
          }
        }
      }
    }
    rows {
      id
      specs {
        ppa {
          id
          system {
            pvSpecs {
              pvTypes
            }
          }
        }
        cs {
          id
          systemLite {
            pvSpecs {
              pvTypes
            }
          }
        }
      }
      site {
        state
      }
    }
  }
  currentUser {
    pid
    name
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useReminderToBidQuery = <
  TData = Types.ReminderToBidQuery,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ReminderToBidQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ReminderToBidQuery, TError, TData>({
    queryKey: ["ReminderToBid", variables],
    queryFn: publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
      ReminderToBidDocument,
      variables,
    ),
    ...options,
  })
}

useReminderToBidQuery.getKey = (variables: Types.ReminderToBidQueryVariables) => [
  "ReminderToBid",
  variables,
]

useReminderToBidQuery.rootKey = "ReminderToBid"

export const useSuspenseReminderToBidQuery = <
  TData = Types.ReminderToBidQuery,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ReminderToBidQuery, TError, TData>({
    queryKey: ["ReminderToBid", variables],
    queryFn: publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
      ReminderToBidDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBid", variables]

useSuspenseReminderToBidQuery.rootKey = "ReminderToBid"

export const useInfiniteReminderToBidQuery = <
  TData = InfiniteData<Types.ReminderToBidQuery>,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ReminderToBidQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ReminderToBid.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
            ReminderToBidDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBid.infinite", variables]

useInfiniteReminderToBidQuery.rootKey = "ReminderToBid.infinite"

export const useSuspenseInfiniteReminderToBidQuery = <
  TData = InfiniteData<Types.ReminderToBidQuery>,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ReminderToBidQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ReminderToBid.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
            ReminderToBidDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBid.infinite", variables]

useSuspenseInfiniteReminderToBidQuery.rootKey = "ReminderToBid.infinite"

useReminderToBidQuery.fetcher = (
  variables: Types.ReminderToBidQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
    ReminderToBidDocument,
    variables,
    options,
  )

export const BidResultForLosersDocument = new TypedDocumentString(`
    query BidResultForLosers($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    recvr {
      name
    }
    bundles {
      pid
      totalBids
      financier {
        pid
        orgSlug
        name
        orgUsers {
          ...OrgUser
        }
      }
    }
    rows {
      id
      awardedFinancier {
        pid
      }
    }
  }
  currentUser {
    pid
    name
    meetingSchedulingUrl
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useBidResultForLosersQuery = <
  TData = Types.BidResultForLosersQuery,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BidResultForLosersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidResultForLosersQuery, TError, TData>({
    queryKey: ["BidResultForLosers", variables],
    queryFn: publicFetcher<
      Types.BidResultForLosersQuery,
      Types.BidResultForLosersQueryVariables
    >(BidResultForLosersDocument, variables),
    ...options,
  })
}

useBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers", variables]

useBidResultForLosersQuery.rootKey = "BidResultForLosers"

export const useSuspenseBidResultForLosersQuery = <
  TData = Types.BidResultForLosersQuery,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidResultForLosersQuery, TError, TData>({
    queryKey: ["BidResultForLosers", variables],
    queryFn: publicFetcher<
      Types.BidResultForLosersQuery,
      Types.BidResultForLosersQueryVariables
    >(BidResultForLosersDocument, variables),
    ...options,
  })
}

useSuspenseBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers", variables]

useSuspenseBidResultForLosersQuery.rootKey = "BidResultForLosers"

export const useInfiniteBidResultForLosersQuery = <
  TData = InfiniteData<Types.BidResultForLosersQuery>,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidResultForLosersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidResultForLosers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidResultForLosersQuery,
            Types.BidResultForLosersQueryVariables
          >(BidResultForLosersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers.infinite", variables]

useInfiniteBidResultForLosersQuery.rootKey = "BidResultForLosers.infinite"

export const useSuspenseInfiniteBidResultForLosersQuery = <
  TData = InfiniteData<Types.BidResultForLosersQuery>,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidResultForLosersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidResultForLosers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidResultForLosersQuery,
            Types.BidResultForLosersQueryVariables
          >(BidResultForLosersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers.infinite", variables]

useSuspenseInfiniteBidResultForLosersQuery.rootKey = "BidResultForLosers.infinite"

useBidResultForLosersQuery.fetcher = (
  variables: Types.BidResultForLosersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidResultForLosersQuery, Types.BidResultForLosersQueryVariables>(
    BidResultForLosersDocument,
    variables,
    options,
  )

export const BidLaunchNoticeDocument = new TypedDocumentString(`
    query BidLaunchNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    totalPvSizeKwDc
    shortPricingNote
    biddingClosedAt
    type
    recvr {
      name
    }
    bundles {
      pid
      financier {
        orgSlug
        name
        orgUsers {
          ...OrgUser
        }
      }
    }
    rows {
      id
      specs {
        ppa {
          id
          system {
            pvSpecs {
              pvTypes
            }
          }
        }
        cs {
          id
          systemLite {
            pvSpecs {
              pvTypes
            }
          }
        }
      }
      site {
        state
      }
    }
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useBidLaunchNoticeQuery = <
  TData = Types.BidLaunchNoticeQuery,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidLaunchNoticeQuery, TError, TData>({
    queryKey: ["BidLaunchNotice", variables],
    queryFn: publicFetcher<
      Types.BidLaunchNoticeQuery,
      Types.BidLaunchNoticeQueryVariables
    >(BidLaunchNoticeDocument, variables),
    ...options,
  })
}

useBidLaunchNoticeQuery.getKey = (variables: Types.BidLaunchNoticeQueryVariables) => [
  "BidLaunchNotice",
  variables,
]

useBidLaunchNoticeQuery.rootKey = "BidLaunchNotice"

export const useSuspenseBidLaunchNoticeQuery = <
  TData = Types.BidLaunchNoticeQuery,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidLaunchNoticeQuery, TError, TData>({
    queryKey: ["BidLaunchNotice", variables],
    queryFn: publicFetcher<
      Types.BidLaunchNoticeQuery,
      Types.BidLaunchNoticeQueryVariables
    >(BidLaunchNoticeDocument, variables),
    ...options,
  })
}

useSuspenseBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNotice", variables]

useSuspenseBidLaunchNoticeQuery.rootKey = "BidLaunchNotice"

export const useInfiniteBidLaunchNoticeQuery = <
  TData = InfiniteData<Types.BidLaunchNoticeQuery>,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidLaunchNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidLaunchNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidLaunchNoticeQuery,
            Types.BidLaunchNoticeQueryVariables
          >(BidLaunchNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNotice.infinite", variables]

useInfiniteBidLaunchNoticeQuery.rootKey = "BidLaunchNotice.infinite"

export const useSuspenseInfiniteBidLaunchNoticeQuery = <
  TData = InfiniteData<Types.BidLaunchNoticeQuery>,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidLaunchNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidLaunchNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidLaunchNoticeQuery,
            Types.BidLaunchNoticeQueryVariables
          >(BidLaunchNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNotice.infinite", variables]

useSuspenseInfiniteBidLaunchNoticeQuery.rootKey = "BidLaunchNotice.infinite"

useBidLaunchNoticeQuery.fetcher = (
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidLaunchNoticeQuery, Types.BidLaunchNoticeQueryVariables>(
    BidLaunchNoticeDocument,
    variables,
    options,
  )

export const HostAwardNoticeDocument = new TypedDocumentString(`
    query HostAwardNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    name
    recvr {
      name
    }
    salesOwner {
      email
    }
    opsOwner {
      email
    }
    rows {
      awardedFinancier {
        name
        slug
        requiresChangeOrders
        orgUsers {
          ...OrgUser
        }
      }
      portfolio {
        title
      }
      energyProject {
        solarLocations
        includeStorage
        hostAward {
          pid
          fileName
        }
      }
      site {
        name
        state
      }
    }
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useHostAwardNoticeQuery = <
  TData = Types.HostAwardNoticeQuery,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HostAwardNoticeQuery, TError, TData>({
    queryKey: ["HostAwardNotice", variables],
    queryFn: publicFetcher<
      Types.HostAwardNoticeQuery,
      Types.HostAwardNoticeQueryVariables
    >(HostAwardNoticeDocument, variables),
    ...options,
  })
}

useHostAwardNoticeQuery.getKey = (variables: Types.HostAwardNoticeQueryVariables) => [
  "HostAwardNotice",
  variables,
]

useHostAwardNoticeQuery.rootKey = "HostAwardNotice"

export const useSuspenseHostAwardNoticeQuery = <
  TData = Types.HostAwardNoticeQuery,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HostAwardNoticeQuery, TError, TData>({
    queryKey: ["HostAwardNotice", variables],
    queryFn: publicFetcher<
      Types.HostAwardNoticeQuery,
      Types.HostAwardNoticeQueryVariables
    >(HostAwardNoticeDocument, variables),
    ...options,
  })
}

useSuspenseHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNotice", variables]

useSuspenseHostAwardNoticeQuery.rootKey = "HostAwardNotice"

export const useInfiniteHostAwardNoticeQuery = <
  TData = InfiniteData<Types.HostAwardNoticeQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HostAwardNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HostAwardNoticeQuery,
            Types.HostAwardNoticeQueryVariables
          >(HostAwardNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNotice.infinite", variables]

useInfiniteHostAwardNoticeQuery.rootKey = "HostAwardNotice.infinite"

export const useSuspenseInfiniteHostAwardNoticeQuery = <
  TData = InfiniteData<Types.HostAwardNoticeQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HostAwardNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HostAwardNoticeQuery,
            Types.HostAwardNoticeQueryVariables
          >(HostAwardNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNotice.infinite", variables]

useSuspenseInfiniteHostAwardNoticeQuery.rootKey = "HostAwardNotice.infinite"

useHostAwardNoticeQuery.fetcher = (
  variables: Types.HostAwardNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HostAwardNoticeQuery, Types.HostAwardNoticeQueryVariables>(
    HostAwardNoticeDocument,
    variables,
    options,
  )

export const ProjectKickoffNoticeDocument = new TypedDocumentString(`
    query ProjectKickoffNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    recvr {
      orgUsers {
        ...OrgUser
      }
    }
    rows {
      awardedFinancier {
        name
      }
    }
  }
}
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useProjectKickoffNoticeQuery = <
  TData = Types.ProjectKickoffNoticeQuery,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectKickoffNoticeQuery, TError, TData>({
    queryKey: ["ProjectKickoffNotice", variables],
    queryFn: publicFetcher<
      Types.ProjectKickoffNoticeQuery,
      Types.ProjectKickoffNoticeQueryVariables
    >(ProjectKickoffNoticeDocument, variables),
    ...options,
  })
}

useProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice", variables]

useProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice"

export const useSuspenseProjectKickoffNoticeQuery = <
  TData = Types.ProjectKickoffNoticeQuery,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectKickoffNoticeQuery, TError, TData>({
    queryKey: ["ProjectKickoffNotice", variables],
    queryFn: publicFetcher<
      Types.ProjectKickoffNoticeQuery,
      Types.ProjectKickoffNoticeQueryVariables
    >(ProjectKickoffNoticeDocument, variables),
    ...options,
  })
}

useSuspenseProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice", variables]

useSuspenseProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice"

export const useInfiniteProjectKickoffNoticeQuery = <
  TData = InfiniteData<Types.ProjectKickoffNoticeQuery>,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectKickoffNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectKickoffNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectKickoffNoticeQuery,
            Types.ProjectKickoffNoticeQueryVariables
          >(ProjectKickoffNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice.infinite", variables]

useInfiniteProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice.infinite"

export const useSuspenseInfiniteProjectKickoffNoticeQuery = <
  TData = InfiniteData<Types.ProjectKickoffNoticeQuery>,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectKickoffNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectKickoffNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectKickoffNoticeQuery,
            Types.ProjectKickoffNoticeQueryVariables
          >(ProjectKickoffNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice.infinite", variables]

useSuspenseInfiniteProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice.infinite"

useProjectKickoffNoticeQuery.fetcher = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectKickoffNoticeQuery,
    Types.ProjectKickoffNoticeQueryVariables
  >(ProjectKickoffNoticeDocument, variables, options)

export const ProjectDocument = new TypedDocumentString(`
    query Project($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      ...EnergyProject
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  workableIxResultsReceivedEstimated
  workableIxResultsReceivedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
  calc {
    roofRent {
      ...RoofRentEconomics
    }
  }
}
fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
fragment EnergyProjectCashSpecification on EnergyProjectCashSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  system {
    ...System
  }
}
fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment EnergyProject on EnergyProject {
  pid
  name
  description
  hubspotRecordId
  hubspotRevenueRecordId
  status
  closedAt
  closeReason
  closeCustomReason
  lumenFeeUsdPerWatt
  lumenRevenue
  finproductType
  solarLocations
  includeStorage
  archivedAt
  archivedBy {
    ...UserRef
  }
  site {
    pid
    streetAddress
  }
  projectSpecification {
    cs {
      ...EnergyProjectCSSpecification
    }
    ppa {
      ...EnergyProjectPPASpecification
    }
    cash {
      ...EnergyProjectCashSpecification
    }
  }
  milestoneSummary {
    ...MilestoneSummary
  }
  developer {
    name
    pid
  }
  progressNotes {
    ...ProgressNotes
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useProjectQuery = <TData = Types.ProjectQuery, TError = unknown>(
  variables: Types.ProjectQueryVariables,
  options?: Omit<UseQueryOptions<Types.ProjectQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ProjectQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectQuery, TError, TData>({
    queryKey: ["Project", variables],
    queryFn: publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
      ProjectDocument,
      variables,
    ),
    ...options,
  })
}

useProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "Project",
  variables,
]

useProjectQuery.rootKey = "Project"

export const useSuspenseProjectQuery = <TData = Types.ProjectQuery, TError = unknown>(
  variables: Types.ProjectQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.ProjectQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectQuery, TError, TData>({
    queryKey: ["Project", variables],
    queryFn: publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
      ProjectDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "Project",
  variables,
]

useSuspenseProjectQuery.rootKey = "Project"

export const useInfiniteProjectQuery = <
  TData = InfiniteData<Types.ProjectQuery>,
  TError = unknown,
>(
  variables: Types.ProjectQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.ProjectQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Project.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
            ProjectDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "Project.infinite",
  variables,
]

useInfiniteProjectQuery.rootKey = "Project.infinite"

export const useSuspenseInfiniteProjectQuery = <
  TData = InfiniteData<Types.ProjectQuery>,
  TError = unknown,
>(
  variables: Types.ProjectQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Project.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
            ProjectDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "Project.infinite",
  variables,
]

useSuspenseInfiniteProjectQuery.rootKey = "Project.infinite"

useProjectQuery.fetcher = (
  variables: Types.ProjectQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
    ProjectDocument,
    variables,
    options,
  )

export const SiteProjectsDocument = new TypedDocumentString(`
    query SiteProjects($sitePid: String!) {
  projects {
    siteProjects(sitePid: $sitePid) {
      ...EnergyProject
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  workableIxResultsReceivedEstimated
  workableIxResultsReceivedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
  calc {
    roofRent {
      ...RoofRentEconomics
    }
  }
}
fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
fragment EnergyProjectCashSpecification on EnergyProjectCashSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  system {
    ...System
  }
}
fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment EnergyProject on EnergyProject {
  pid
  name
  description
  hubspotRecordId
  hubspotRevenueRecordId
  status
  closedAt
  closeReason
  closeCustomReason
  lumenFeeUsdPerWatt
  lumenRevenue
  finproductType
  solarLocations
  includeStorage
  archivedAt
  archivedBy {
    ...UserRef
  }
  site {
    pid
    streetAddress
  }
  projectSpecification {
    cs {
      ...EnergyProjectCSSpecification
    }
    ppa {
      ...EnergyProjectPPASpecification
    }
    cash {
      ...EnergyProjectCashSpecification
    }
  }
  milestoneSummary {
    ...MilestoneSummary
  }
  developer {
    name
    pid
  }
  progressNotes {
    ...ProgressNotes
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useSiteProjectsQuery = <TData = Types.SiteProjectsQuery, TError = unknown>(
  variables: Types.SiteProjectsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteProjectsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteProjectsQuery, TError, TData>({
    queryKey: ["SiteProjects", variables],
    queryFn: publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
      SiteProjectsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteProjectsQuery.getKey = (variables: Types.SiteProjectsQueryVariables) => [
  "SiteProjects",
  variables,
]

useSiteProjectsQuery.rootKey = "SiteProjects"

export const useSuspenseSiteProjectsQuery = <
  TData = Types.SiteProjectsQuery,
  TError = unknown,
>(
  variables: Types.SiteProjectsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteProjectsQuery, TError, TData>({
    queryKey: ["SiteProjects", variables],
    queryFn: publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
      SiteProjectsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteProjectsQuery.getKey = (variables: Types.SiteProjectsQueryVariables) => [
  "SiteProjects",
  variables,
]

useSuspenseSiteProjectsQuery.rootKey = "SiteProjects"

export const useInfiniteSiteProjectsQuery = <
  TData = InfiniteData<Types.SiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
            SiteProjectsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteProjectsQuery.getKey = (variables: Types.SiteProjectsQueryVariables) => [
  "SiteProjects.infinite",
  variables,
]

useInfiniteSiteProjectsQuery.rootKey = "SiteProjects.infinite"

export const useSuspenseInfiniteSiteProjectsQuery = <
  TData = InfiniteData<Types.SiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
            SiteProjectsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteProjectsQuery.getKey = (
  variables: Types.SiteProjectsQueryVariables,
) => ["SiteProjects.infinite", variables]

useSuspenseInfiniteSiteProjectsQuery.rootKey = "SiteProjects.infinite"

useSiteProjectsQuery.fetcher = (
  variables: Types.SiteProjectsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
    SiteProjectsDocument,
    variables,
    options,
  )

export const SiteAwardedCsProjectRoofRentDocument = new TypedDocumentString(`
    query SiteAwardedCsProjectRoofRent($sitePid: String!) {
  projects {
    siteProjects(sitePid: $sitePid) {
      ...AwardedCsProjectRoofRent
    }
  }
}
    fragment AwardedCsProjectRoofRent on EnergyProject {
  pid
  projectSpecification {
    cs {
      calc {
        roofRent {
          ...RoofRentEconomics
        }
      }
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}`)

export const useSiteAwardedCsProjectRoofRentQuery = <
  TData = Types.SiteAwardedCsProjectRoofRentQuery,
  TError = unknown,
>(
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteAwardedCsProjectRoofRentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteAwardedCsProjectRoofRentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteAwardedCsProjectRoofRentQuery, TError, TData>({
    queryKey: ["SiteAwardedCsProjectRoofRent", variables],
    queryFn: publicFetcher<
      Types.SiteAwardedCsProjectRoofRentQuery,
      Types.SiteAwardedCsProjectRoofRentQueryVariables
    >(SiteAwardedCsProjectRoofRentDocument, variables),
    ...options,
  })
}

useSiteAwardedCsProjectRoofRentQuery.getKey = (
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
) => ["SiteAwardedCsProjectRoofRent", variables]

useSiteAwardedCsProjectRoofRentQuery.rootKey = "SiteAwardedCsProjectRoofRent"

export const useSuspenseSiteAwardedCsProjectRoofRentQuery = <
  TData = Types.SiteAwardedCsProjectRoofRentQuery,
  TError = unknown,
>(
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteAwardedCsProjectRoofRentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteAwardedCsProjectRoofRentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteAwardedCsProjectRoofRentQuery, TError, TData>({
    queryKey: ["SiteAwardedCsProjectRoofRent", variables],
    queryFn: publicFetcher<
      Types.SiteAwardedCsProjectRoofRentQuery,
      Types.SiteAwardedCsProjectRoofRentQueryVariables
    >(SiteAwardedCsProjectRoofRentDocument, variables),
    ...options,
  })
}

useSuspenseSiteAwardedCsProjectRoofRentQuery.getKey = (
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
) => ["SiteAwardedCsProjectRoofRent", variables]

useSuspenseSiteAwardedCsProjectRoofRentQuery.rootKey = "SiteAwardedCsProjectRoofRent"

export const useInfiniteSiteAwardedCsProjectRoofRentQuery = <
  TData = InfiniteData<Types.SiteAwardedCsProjectRoofRentQuery>,
  TError = unknown,
>(
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteAwardedCsProjectRoofRentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteAwardedCsProjectRoofRentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteAwardedCsProjectRoofRentQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteAwardedCsProjectRoofRent.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteAwardedCsProjectRoofRentQuery,
            Types.SiteAwardedCsProjectRoofRentQueryVariables
          >(SiteAwardedCsProjectRoofRentDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteAwardedCsProjectRoofRentQuery.getKey = (
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
) => ["SiteAwardedCsProjectRoofRent.infinite", variables]

useInfiniteSiteAwardedCsProjectRoofRentQuery.rootKey =
  "SiteAwardedCsProjectRoofRent.infinite"

export const useSuspenseInfiniteSiteAwardedCsProjectRoofRentQuery = <
  TData = InfiniteData<Types.SiteAwardedCsProjectRoofRentQuery>,
  TError = unknown,
>(
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.SiteAwardedCsProjectRoofRentQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteAwardedCsProjectRoofRentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.SiteAwardedCsProjectRoofRentQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteAwardedCsProjectRoofRent.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteAwardedCsProjectRoofRentQuery,
            Types.SiteAwardedCsProjectRoofRentQueryVariables
          >(SiteAwardedCsProjectRoofRentDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteAwardedCsProjectRoofRentQuery.getKey = (
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
) => ["SiteAwardedCsProjectRoofRent.infinite", variables]

useSuspenseInfiniteSiteAwardedCsProjectRoofRentQuery.rootKey =
  "SiteAwardedCsProjectRoofRent.infinite"

useSiteAwardedCsProjectRoofRentQuery.fetcher = (
  variables: Types.SiteAwardedCsProjectRoofRentQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteAwardedCsProjectRoofRentQuery,
    Types.SiteAwardedCsProjectRoofRentQueryVariables
  >(SiteAwardedCsProjectRoofRentDocument, variables, options)

export const AdminSiteProjectsDocument = new TypedDocumentString(`
    query AdminSiteProjects($sitePid: String!, $previewAsAudience: ProjectAudience = null) {
  projects {
    adminSiteProjects(sitePid: $sitePid) {
      ...AdminEnergyProject
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  workableIxResultsReceivedEstimated
  workableIxResultsReceivedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
  calc {
    roofRent {
      ...RoofRentEconomics
    }
  }
}
fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
fragment EnergyProjectCashSpecification on EnergyProjectCashSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  system {
    ...System
  }
}
fragment Note on Note {
  pid
  text
  content
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
}
fragment ProgressNotes on ProgressNotesType {
  pid
  priority
  notes {
    ...Note
  }
  actionItems {
    ...Note
  }
}
fragment EnergyProject on EnergyProject {
  pid
  name
  description
  hubspotRecordId
  hubspotRevenueRecordId
  status
  closedAt
  closeReason
  closeCustomReason
  lumenFeeUsdPerWatt
  lumenRevenue
  finproductType
  solarLocations
  includeStorage
  archivedAt
  archivedBy {
    ...UserRef
  }
  site {
    pid
    streetAddress
  }
  projectSpecification {
    cs {
      ...EnergyProjectCSSpecification
    }
    ppa {
      ...EnergyProjectPPASpecification
    }
    cash {
      ...EnergyProjectCashSpecification
    }
  }
  milestoneSummary {
    ...MilestoneSummary
  }
  developer {
    name
    pid
  }
  progressNotes {
    ...ProgressNotes
  }
}
fragment EnergyProjectTaskNote on EnergyProjectTaskNote {
  pid
  createdAt
  createdBy {
    ...UserRef
  }
  updatedAt
  updatedBy {
    ...UserRef
  }
  text
  content
  audiences
}
fragment EnergyProjectTask on EnergyProjectTask {
  id
  pid
  createdAt
  projectStage
  description
  complete
  hiddenFromDeveloper
  hiddenFromBuildingOwner
  deadline
  estimatedCompletion
  completedAt
  assigneeGroup
  workstream {
    id
    pid
    name
  }
  assigneeUser {
    ...UserRef
  }
  createdBy {
    ...UserRef
  }
  notes {
    ...EnergyProjectTaskNote
  }
}
fragment AdminEnergyProject on EnergyProject {
  ...EnergyProject
  lumenFeeUsdPerKwhStorage
  hostAwardSent
  changeOrderSent
  scopeOfWorkSent
  executionPlanTasks(previewAsAudience: $previewAsAudience) {
    ...EnergyProjectTask
  }
  revenueBreakdown {
    revenueMilestones {
      milestoneType
      percentage
    }
  }
  developer {
    slug
    requiresChangeOrders
  }
  bundleParentRow {
    id
    status
    notes
    parent {
      pid
      name
      recvr {
        slug
        name
      }
    }
  }
  awardedBid {
    ppa {
      pid
      name
      bidPackagePid
      bidPackageName
      bidPackageOrgSlug
      system {
        ...System
      }
    }
    cs {
      pid
      name
      bidPackagePid
      bidPackageName
      bidPackageOrgSlug
      systemLite {
        ...SystemLite
      }
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAdminSiteProjectsQuery = <
  TData = Types.AdminSiteProjectsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteProjectsQuery, TError, TData>({
    queryKey: ["AdminSiteProjects", variables],
    queryFn: publicFetcher<
      Types.AdminSiteProjectsQuery,
      Types.AdminSiteProjectsQueryVariables
    >(AdminSiteProjectsDocument, variables),
    ...options,
  })
}

useAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjects", variables]

useAdminSiteProjectsQuery.rootKey = "AdminSiteProjects"

export const useSuspenseAdminSiteProjectsQuery = <
  TData = Types.AdminSiteProjectsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteProjectsQuery, TError, TData>({
    queryKey: ["AdminSiteProjects", variables],
    queryFn: publicFetcher<
      Types.AdminSiteProjectsQuery,
      Types.AdminSiteProjectsQueryVariables
    >(AdminSiteProjectsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjects", variables]

useSuspenseAdminSiteProjectsQuery.rootKey = "AdminSiteProjects"

export const useInfiniteAdminSiteProjectsQuery = <
  TData = InfiniteData<Types.AdminSiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteProjectsQuery,
            Types.AdminSiteProjectsQueryVariables
          >(AdminSiteProjectsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjects.infinite", variables]

useInfiniteAdminSiteProjectsQuery.rootKey = "AdminSiteProjects.infinite"

export const useSuspenseInfiniteAdminSiteProjectsQuery = <
  TData = InfiniteData<Types.AdminSiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteProjectsQuery,
            Types.AdminSiteProjectsQueryVariables
          >(AdminSiteProjectsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjects.infinite", variables]

useSuspenseInfiniteAdminSiteProjectsQuery.rootKey = "AdminSiteProjects.infinite"

useAdminSiteProjectsQuery.fetcher = (
  variables: Types.AdminSiteProjectsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminSiteProjectsQuery, Types.AdminSiteProjectsQueryVariables>(
    AdminSiteProjectsDocument,
    variables,
    options,
  )

export const AllFtmProgramsDocument = new TypedDocumentString(`
    query AllFtmPrograms {
  ftmPrograms {
    all {
      state
      name
    }
  }
}
    `)

export const useAllFtmProgramsQuery = <
  TData = Types.AllFtmProgramsQuery,
  TError = unknown,
>(
  variables?: Types.AllFtmProgramsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AllFtmProgramsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AllFtmProgramsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AllFtmPrograms"] : ["AllFtmPrograms", variables],
    queryFn: publicFetcher<
      Types.AllFtmProgramsQuery,
      Types.AllFtmProgramsQueryVariables
    >(AllFtmProgramsDocument, variables),
    ...options,
  })
}

useAllFtmProgramsQuery.getKey = (variables?: Types.AllFtmProgramsQueryVariables) =>
  variables === undefined ? ["AllFtmPrograms"] : ["AllFtmPrograms", variables]

useAllFtmProgramsQuery.rootKey = "AllFtmPrograms"

export const useSuspenseAllFtmProgramsQuery = <
  TData = Types.AllFtmProgramsQuery,
  TError = unknown,
>(
  variables?: Types.AllFtmProgramsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AllFtmProgramsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AllFtmProgramsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AllFtmPrograms"] : ["AllFtmPrograms", variables],
    queryFn: publicFetcher<
      Types.AllFtmProgramsQuery,
      Types.AllFtmProgramsQueryVariables
    >(AllFtmProgramsDocument, variables),
    ...options,
  })
}

useSuspenseAllFtmProgramsQuery.getKey = (
  variables?: Types.AllFtmProgramsQueryVariables,
) => (variables === undefined ? ["AllFtmPrograms"] : ["AllFtmPrograms", variables])

useSuspenseAllFtmProgramsQuery.rootKey = "AllFtmPrograms"

export const useInfiniteAllFtmProgramsQuery = <
  TData = InfiniteData<Types.AllFtmProgramsQuery>,
  TError = unknown,
>(
  variables: Types.AllFtmProgramsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AllFtmProgramsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AllFtmProgramsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllFtmPrograms.infinite"]
            : ["AllFtmPrograms.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllFtmProgramsQuery, Types.AllFtmProgramsQueryVariables>(
            AllFtmProgramsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAllFtmProgramsQuery.getKey = (
  variables?: Types.AllFtmProgramsQueryVariables,
) =>
  variables === undefined
    ? ["AllFtmPrograms.infinite"]
    : ["AllFtmPrograms.infinite", variables]

useInfiniteAllFtmProgramsQuery.rootKey = "AllFtmPrograms.infinite"

export const useSuspenseInfiniteAllFtmProgramsQuery = <
  TData = InfiniteData<Types.AllFtmProgramsQuery>,
  TError = unknown,
>(
  variables: Types.AllFtmProgramsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AllFtmProgramsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AllFtmProgramsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllFtmPrograms.infinite"]
            : ["AllFtmPrograms.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllFtmProgramsQuery, Types.AllFtmProgramsQueryVariables>(
            AllFtmProgramsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAllFtmProgramsQuery.getKey = (
  variables?: Types.AllFtmProgramsQueryVariables,
) =>
  variables === undefined
    ? ["AllFtmPrograms.infinite"]
    : ["AllFtmPrograms.infinite", variables]

useSuspenseInfiniteAllFtmProgramsQuery.rootKey = "AllFtmPrograms.infinite"

useAllFtmProgramsQuery.fetcher = (
  variables?: Types.AllFtmProgramsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AllFtmProgramsQuery, Types.AllFtmProgramsQueryVariables>(
    AllFtmProgramsDocument,
    variables,
    options,
  )

export const FtmProgramsForStateDocument = new TypedDocumentString(`
    query FtmProgramsForState($state: USState) {
  ftmPrograms {
    all(state: $state) {
      id
      name
    }
  }
}
    `)

export const useFtmProgramsForStateQuery = <
  TData = Types.FtmProgramsForStateQuery,
  TError = unknown,
>(
  variables?: Types.FtmProgramsForStateQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FtmProgramsForStateQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FtmProgramsForState"]
        : ["FtmProgramsForState", variables],
    queryFn: publicFetcher<
      Types.FtmProgramsForStateQuery,
      Types.FtmProgramsForStateQueryVariables
    >(FtmProgramsForStateDocument, variables),
    ...options,
  })
}

useFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined ? ["FtmProgramsForState"] : ["FtmProgramsForState", variables]

useFtmProgramsForStateQuery.rootKey = "FtmProgramsForState"

export const useSuspenseFtmProgramsForStateQuery = <
  TData = Types.FtmProgramsForStateQuery,
  TError = unknown,
>(
  variables?: Types.FtmProgramsForStateQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FtmProgramsForStateQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FtmProgramsForState"]
        : ["FtmProgramsForState", variables],
    queryFn: publicFetcher<
      Types.FtmProgramsForStateQuery,
      Types.FtmProgramsForStateQueryVariables
    >(FtmProgramsForStateDocument, variables),
    ...options,
  })
}

useSuspenseFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined ? ["FtmProgramsForState"] : ["FtmProgramsForState", variables]

useSuspenseFtmProgramsForStateQuery.rootKey = "FtmProgramsForState"

export const useInfiniteFtmProgramsForStateQuery = <
  TData = InfiniteData<Types.FtmProgramsForStateQuery>,
  TError = unknown,
>(
  variables: Types.FtmProgramsForStateQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FtmProgramsForStateQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FtmProgramsForState.infinite"]
            : ["FtmProgramsForState.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FtmProgramsForStateQuery,
            Types.FtmProgramsForStateQueryVariables
          >(FtmProgramsForStateDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined
    ? ["FtmProgramsForState.infinite"]
    : ["FtmProgramsForState.infinite", variables]

useInfiniteFtmProgramsForStateQuery.rootKey = "FtmProgramsForState.infinite"

export const useSuspenseInfiniteFtmProgramsForStateQuery = <
  TData = InfiniteData<Types.FtmProgramsForStateQuery>,
  TError = unknown,
>(
  variables: Types.FtmProgramsForStateQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FtmProgramsForStateQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FtmProgramsForState.infinite"]
            : ["FtmProgramsForState.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FtmProgramsForStateQuery,
            Types.FtmProgramsForStateQueryVariables
          >(FtmProgramsForStateDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined
    ? ["FtmProgramsForState.infinite"]
    : ["FtmProgramsForState.infinite", variables]

useSuspenseInfiniteFtmProgramsForStateQuery.rootKey = "FtmProgramsForState.infinite"

useFtmProgramsForStateQuery.fetcher = (
  variables?: Types.FtmProgramsForStateQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FtmProgramsForStateQuery,
    Types.FtmProgramsForStateQueryVariables
  >(FtmProgramsForStateDocument, variables, options)

export const CsPriceTableViewDocument = new TypedDocumentString(`
    query CsPriceTableView($filterBy: CSPriceTableFilter, $sortBy: [CSPriceTableSort!]) {
  ftmPrograms {
    csPrices {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          utilityNames
          states
        }
      }
    }
  }
}
    `)

export const useCsPriceTableViewQuery = <
  TData = Types.CsPriceTableViewQuery,
  TError = unknown,
>(
  variables?: Types.CsPriceTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsPriceTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsPriceTableViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsPriceTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["CsPriceTableView"] : ["CsPriceTableView", variables],
    queryFn: publicFetcher<
      Types.CsPriceTableViewQuery,
      Types.CsPriceTableViewQueryVariables
    >(CsPriceTableViewDocument, variables),
    ...options,
  })
}

useCsPriceTableViewQuery.getKey = (variables?: Types.CsPriceTableViewQueryVariables) =>
  variables === undefined ? ["CsPriceTableView"] : ["CsPriceTableView", variables]

useCsPriceTableViewQuery.rootKey = "CsPriceTableView"

export const useSuspenseCsPriceTableViewQuery = <
  TData = Types.CsPriceTableViewQuery,
  TError = unknown,
>(
  variables?: Types.CsPriceTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsPriceTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsPriceTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsPriceTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["CsPriceTableView"] : ["CsPriceTableView", variables],
    queryFn: publicFetcher<
      Types.CsPriceTableViewQuery,
      Types.CsPriceTableViewQueryVariables
    >(CsPriceTableViewDocument, variables),
    ...options,
  })
}

useSuspenseCsPriceTableViewQuery.getKey = (
  variables?: Types.CsPriceTableViewQueryVariables,
) => (variables === undefined ? ["CsPriceTableView"] : ["CsPriceTableView", variables])

useSuspenseCsPriceTableViewQuery.rootKey = "CsPriceTableView"

export const useInfiniteCsPriceTableViewQuery = <
  TData = InfiniteData<Types.CsPriceTableViewQuery>,
  TError = unknown,
>(
  variables: Types.CsPriceTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsPriceTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsPriceTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsPriceTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CsPriceTableView.infinite"]
            : ["CsPriceTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsPriceTableViewQuery,
            Types.CsPriceTableViewQueryVariables
          >(CsPriceTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsPriceTableViewQuery.getKey = (
  variables?: Types.CsPriceTableViewQueryVariables,
) =>
  variables === undefined
    ? ["CsPriceTableView.infinite"]
    : ["CsPriceTableView.infinite", variables]

useInfiniteCsPriceTableViewQuery.rootKey = "CsPriceTableView.infinite"

export const useSuspenseInfiniteCsPriceTableViewQuery = <
  TData = InfiniteData<Types.CsPriceTableViewQuery>,
  TError = unknown,
>(
  variables: Types.CsPriceTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsPriceTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsPriceTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsPriceTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CsPriceTableView.infinite"]
            : ["CsPriceTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsPriceTableViewQuery,
            Types.CsPriceTableViewQueryVariables
          >(CsPriceTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsPriceTableViewQuery.getKey = (
  variables?: Types.CsPriceTableViewQueryVariables,
) =>
  variables === undefined
    ? ["CsPriceTableView.infinite"]
    : ["CsPriceTableView.infinite", variables]

useSuspenseInfiniteCsPriceTableViewQuery.rootKey = "CsPriceTableView.infinite"

useCsPriceTableViewQuery.fetcher = (
  variables?: Types.CsPriceTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsPriceTableViewQuery, Types.CsPriceTableViewQueryVariables>(
    CsPriceTableViewDocument,
    variables,
    options,
  )

export const CsPriceTableRowsDocument = new TypedDocumentString(`
    query CsPriceTableRows($keys: [Int!]!) {
  ftmPrograms {
    csPrices {
      rows(keys: $keys) {
        id
        state
        pvType
        leaseRateUsdPerKwPerYear
        leaseRateSource
        termYears
        minPvKw
        maxPvKw
        escalator
        utilityId
        utilityName
        avgAwardedAnnualRentPerKw
        p10RatePerKw
        firstQuartileRatePerKw
        medianRatePerKw
        thirdQuartileRatePerKw
        p90RatePerKw
        numConsideredBids
        numConsideredAwardedBids
      }
    }
  }
}
    `)

export const useCsPriceTableRowsQuery = <
  TData = Types.CsPriceTableRowsQuery,
  TError = unknown,
>(
  variables: Types.CsPriceTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsPriceTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsPriceTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsPriceTableRowsQuery, TError, TData>({
    queryKey: ["CsPriceTableRows", variables],
    queryFn: publicFetcher<
      Types.CsPriceTableRowsQuery,
      Types.CsPriceTableRowsQueryVariables
    >(CsPriceTableRowsDocument, variables),
    ...options,
  })
}

useCsPriceTableRowsQuery.getKey = (variables: Types.CsPriceTableRowsQueryVariables) => [
  "CsPriceTableRows",
  variables,
]

useCsPriceTableRowsQuery.rootKey = "CsPriceTableRows"

export const useSuspenseCsPriceTableRowsQuery = <
  TData = Types.CsPriceTableRowsQuery,
  TError = unknown,
>(
  variables: Types.CsPriceTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsPriceTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsPriceTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsPriceTableRowsQuery, TError, TData>({
    queryKey: ["CsPriceTableRows", variables],
    queryFn: publicFetcher<
      Types.CsPriceTableRowsQuery,
      Types.CsPriceTableRowsQueryVariables
    >(CsPriceTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseCsPriceTableRowsQuery.getKey = (
  variables: Types.CsPriceTableRowsQueryVariables,
) => ["CsPriceTableRows", variables]

useSuspenseCsPriceTableRowsQuery.rootKey = "CsPriceTableRows"

export const useInfiniteCsPriceTableRowsQuery = <
  TData = InfiniteData<Types.CsPriceTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.CsPriceTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsPriceTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsPriceTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsPriceTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsPriceTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsPriceTableRowsQuery,
            Types.CsPriceTableRowsQueryVariables
          >(CsPriceTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsPriceTableRowsQuery.getKey = (
  variables: Types.CsPriceTableRowsQueryVariables,
) => ["CsPriceTableRows.infinite", variables]

useInfiniteCsPriceTableRowsQuery.rootKey = "CsPriceTableRows.infinite"

export const useSuspenseInfiniteCsPriceTableRowsQuery = <
  TData = InfiniteData<Types.CsPriceTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.CsPriceTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsPriceTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsPriceTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsPriceTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsPriceTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsPriceTableRowsQuery,
            Types.CsPriceTableRowsQueryVariables
          >(CsPriceTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsPriceTableRowsQuery.getKey = (
  variables: Types.CsPriceTableRowsQueryVariables,
) => ["CsPriceTableRows.infinite", variables]

useSuspenseInfiniteCsPriceTableRowsQuery.rootKey = "CsPriceTableRows.infinite"

useCsPriceTableRowsQuery.fetcher = (
  variables: Types.CsPriceTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsPriceTableRowsQuery, Types.CsPriceTableRowsQueryVariables>(
    CsPriceTableRowsDocument,
    variables,
    options,
  )

export const BatteryProductsDocument = new TypedDocumentString(`
    query BatteryProducts {
  batteryProducts {
    id
    sizeKw
    sizeKwh
    vendor
    equipmentCost
    laborCost
    includeInOptimizer
    disabled
  }
}
    `)

export const useBatteryProductsQuery = <
  TData = Types.BatteryProductsQuery,
  TError = unknown,
>(
  variables?: Types.BatteryProductsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BatteryProductsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BatteryProductsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["BatteryProducts"] : ["BatteryProducts", variables],
    queryFn: publicFetcher<
      Types.BatteryProductsQuery,
      Types.BatteryProductsQueryVariables
    >(BatteryProductsDocument, variables),
    ...options,
  })
}

useBatteryProductsQuery.getKey = (variables?: Types.BatteryProductsQueryVariables) =>
  variables === undefined ? ["BatteryProducts"] : ["BatteryProducts", variables]

useBatteryProductsQuery.rootKey = "BatteryProducts"

export const useSuspenseBatteryProductsQuery = <
  TData = Types.BatteryProductsQuery,
  TError = unknown,
>(
  variables?: Types.BatteryProductsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BatteryProductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BatteryProductsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["BatteryProducts"] : ["BatteryProducts", variables],
    queryFn: publicFetcher<
      Types.BatteryProductsQuery,
      Types.BatteryProductsQueryVariables
    >(BatteryProductsDocument, variables),
    ...options,
  })
}

useSuspenseBatteryProductsQuery.getKey = (
  variables?: Types.BatteryProductsQueryVariables,
) => (variables === undefined ? ["BatteryProducts"] : ["BatteryProducts", variables])

useSuspenseBatteryProductsQuery.rootKey = "BatteryProducts"

export const useInfiniteBatteryProductsQuery = <
  TData = InfiniteData<Types.BatteryProductsQuery>,
  TError = unknown,
>(
  variables: Types.BatteryProductsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BatteryProductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BatteryProductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BatteryProducts.infinite"]
            : ["BatteryProducts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatteryProductsQuery,
            Types.BatteryProductsQueryVariables
          >(BatteryProductsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBatteryProductsQuery.getKey = (
  variables?: Types.BatteryProductsQueryVariables,
) =>
  variables === undefined
    ? ["BatteryProducts.infinite"]
    : ["BatteryProducts.infinite", variables]

useInfiniteBatteryProductsQuery.rootKey = "BatteryProducts.infinite"

export const useSuspenseInfiniteBatteryProductsQuery = <
  TData = InfiniteData<Types.BatteryProductsQuery>,
  TError = unknown,
>(
  variables: Types.BatteryProductsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BatteryProductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BatteryProductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BatteryProducts.infinite"]
            : ["BatteryProducts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatteryProductsQuery,
            Types.BatteryProductsQueryVariables
          >(BatteryProductsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBatteryProductsQuery.getKey = (
  variables?: Types.BatteryProductsQueryVariables,
) =>
  variables === undefined
    ? ["BatteryProducts.infinite"]
    : ["BatteryProducts.infinite", variables]

useSuspenseInfiniteBatteryProductsQuery.rootKey = "BatteryProducts.infinite"

useBatteryProductsQuery.fetcher = (
  variables?: Types.BatteryProductsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BatteryProductsQuery, Types.BatteryProductsQueryVariables>(
    BatteryProductsDocument,
    variables,
    options,
  )

export const WorkstreamsDocument = new TypedDocumentString(`
    query Workstreams {
  workstreams {
    id
    pid
    name
    description
    projectStage
    rank
    supportedProjectTypes
  }
}
    `)

export const useWorkstreamsQuery = <TData = Types.WorkstreamsQuery, TError = unknown>(
  variables?: Types.WorkstreamsQueryVariables,
  options?: Omit<UseQueryOptions<Types.WorkstreamsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.WorkstreamsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.WorkstreamsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Workstreams"] : ["Workstreams", variables],
    queryFn: publicFetcher<Types.WorkstreamsQuery, Types.WorkstreamsQueryVariables>(
      WorkstreamsDocument,
      variables,
    ),
    ...options,
  })
}

useWorkstreamsQuery.getKey = (variables?: Types.WorkstreamsQueryVariables) =>
  variables === undefined ? ["Workstreams"] : ["Workstreams", variables]

useWorkstreamsQuery.rootKey = "Workstreams"

export const useSuspenseWorkstreamsQuery = <
  TData = Types.WorkstreamsQuery,
  TError = unknown,
>(
  variables?: Types.WorkstreamsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.WorkstreamsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.WorkstreamsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.WorkstreamsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Workstreams"] : ["Workstreams", variables],
    queryFn: publicFetcher<Types.WorkstreamsQuery, Types.WorkstreamsQueryVariables>(
      WorkstreamsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseWorkstreamsQuery.getKey = (variables?: Types.WorkstreamsQueryVariables) =>
  variables === undefined ? ["Workstreams"] : ["Workstreams", variables]

useSuspenseWorkstreamsQuery.rootKey = "Workstreams"

export const useInfiniteWorkstreamsQuery = <
  TData = InfiniteData<Types.WorkstreamsQuery>,
  TError = unknown,
>(
  variables: Types.WorkstreamsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.WorkstreamsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.WorkstreamsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.WorkstreamsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Workstreams.infinite"]
            : ["Workstreams.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.WorkstreamsQuery, Types.WorkstreamsQueryVariables>(
            WorkstreamsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteWorkstreamsQuery.getKey = (variables?: Types.WorkstreamsQueryVariables) =>
  variables === undefined
    ? ["Workstreams.infinite"]
    : ["Workstreams.infinite", variables]

useInfiniteWorkstreamsQuery.rootKey = "Workstreams.infinite"

export const useSuspenseInfiniteWorkstreamsQuery = <
  TData = InfiniteData<Types.WorkstreamsQuery>,
  TError = unknown,
>(
  variables: Types.WorkstreamsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.WorkstreamsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.WorkstreamsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.WorkstreamsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Workstreams.infinite"]
            : ["Workstreams.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.WorkstreamsQuery, Types.WorkstreamsQueryVariables>(
            WorkstreamsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteWorkstreamsQuery.getKey = (
  variables?: Types.WorkstreamsQueryVariables,
) =>
  variables === undefined
    ? ["Workstreams.infinite"]
    : ["Workstreams.infinite", variables]

useSuspenseInfiniteWorkstreamsQuery.rootKey = "Workstreams.infinite"

useWorkstreamsQuery.fetcher = (
  variables?: Types.WorkstreamsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.WorkstreamsQuery, Types.WorkstreamsQueryVariables>(
    WorkstreamsDocument,
    variables,
    options,
  )

export const AssignableUsersDocument = new TypedDocumentString(`
    query AssignableUsers($projectPid: String!) {
  projects {
    assignableUsers(projectPid: $projectPid) {
      label
      assigneeGroup
      users {
        ...UserRef
      }
    }
  }
}
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
  jobFunctions
}`)

export const useAssignableUsersQuery = <
  TData = Types.AssignableUsersQuery,
  TError = unknown,
>(
  variables: Types.AssignableUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AssignableUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AssignableUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AssignableUsersQuery, TError, TData>({
    queryKey: ["AssignableUsers", variables],
    queryFn: publicFetcher<
      Types.AssignableUsersQuery,
      Types.AssignableUsersQueryVariables
    >(AssignableUsersDocument, variables),
    ...options,
  })
}

useAssignableUsersQuery.getKey = (variables: Types.AssignableUsersQueryVariables) => [
  "AssignableUsers",
  variables,
]

useAssignableUsersQuery.rootKey = "AssignableUsers"

export const useSuspenseAssignableUsersQuery = <
  TData = Types.AssignableUsersQuery,
  TError = unknown,
>(
  variables: Types.AssignableUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AssignableUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AssignableUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AssignableUsersQuery, TError, TData>({
    queryKey: ["AssignableUsers", variables],
    queryFn: publicFetcher<
      Types.AssignableUsersQuery,
      Types.AssignableUsersQueryVariables
    >(AssignableUsersDocument, variables),
    ...options,
  })
}

useSuspenseAssignableUsersQuery.getKey = (
  variables: Types.AssignableUsersQueryVariables,
) => ["AssignableUsers", variables]

useSuspenseAssignableUsersQuery.rootKey = "AssignableUsers"

export const useInfiniteAssignableUsersQuery = <
  TData = InfiniteData<Types.AssignableUsersQuery>,
  TError = unknown,
>(
  variables: Types.AssignableUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AssignableUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AssignableUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AssignableUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AssignableUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AssignableUsersQuery,
            Types.AssignableUsersQueryVariables
          >(AssignableUsersDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAssignableUsersQuery.getKey = (
  variables: Types.AssignableUsersQueryVariables,
) => ["AssignableUsers.infinite", variables]

useInfiniteAssignableUsersQuery.rootKey = "AssignableUsers.infinite"

export const useSuspenseInfiniteAssignableUsersQuery = <
  TData = InfiniteData<Types.AssignableUsersQuery>,
  TError = unknown,
>(
  variables: Types.AssignableUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AssignableUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AssignableUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AssignableUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AssignableUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AssignableUsersQuery,
            Types.AssignableUsersQueryVariables
          >(AssignableUsersDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAssignableUsersQuery.getKey = (
  variables: Types.AssignableUsersQueryVariables,
) => ["AssignableUsers.infinite", variables]

useSuspenseInfiniteAssignableUsersQuery.rootKey = "AssignableUsers.infinite"

useAssignableUsersQuery.fetcher = (
  variables: Types.AssignableUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AssignableUsersQuery, Types.AssignableUsersQueryVariables>(
    AssignableUsersDocument,
    variables,
    options,
  )

export const DefaultRevenueBreakdownDocument = new TypedDocumentString(`
    query DefaultRevenueBreakdown($finProductType: FinProductType!) {
  bidPackages {
    defaultRevenueBreakdown(finProductType: $finProductType) {
      revenueMilestones {
        milestoneType
        percentage
      }
    }
  }
}
    `)

export const useDefaultRevenueBreakdownQuery = <
  TData = Types.DefaultRevenueBreakdownQuery,
  TError = unknown,
>(
  variables: Types.DefaultRevenueBreakdownQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DefaultRevenueBreakdownQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.DefaultRevenueBreakdownQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.DefaultRevenueBreakdownQuery, TError, TData>({
    queryKey: ["DefaultRevenueBreakdown", variables],
    queryFn: publicFetcher<
      Types.DefaultRevenueBreakdownQuery,
      Types.DefaultRevenueBreakdownQueryVariables
    >(DefaultRevenueBreakdownDocument, variables),
    ...options,
  })
}

useDefaultRevenueBreakdownQuery.getKey = (
  variables: Types.DefaultRevenueBreakdownQueryVariables,
) => ["DefaultRevenueBreakdown", variables]

useDefaultRevenueBreakdownQuery.rootKey = "DefaultRevenueBreakdown"

export const useSuspenseDefaultRevenueBreakdownQuery = <
  TData = Types.DefaultRevenueBreakdownQuery,
  TError = unknown,
>(
  variables: Types.DefaultRevenueBreakdownQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DefaultRevenueBreakdownQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DefaultRevenueBreakdownQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DefaultRevenueBreakdownQuery, TError, TData>({
    queryKey: ["DefaultRevenueBreakdown", variables],
    queryFn: publicFetcher<
      Types.DefaultRevenueBreakdownQuery,
      Types.DefaultRevenueBreakdownQueryVariables
    >(DefaultRevenueBreakdownDocument, variables),
    ...options,
  })
}

useSuspenseDefaultRevenueBreakdownQuery.getKey = (
  variables: Types.DefaultRevenueBreakdownQueryVariables,
) => ["DefaultRevenueBreakdown", variables]

useSuspenseDefaultRevenueBreakdownQuery.rootKey = "DefaultRevenueBreakdown"

export const useInfiniteDefaultRevenueBreakdownQuery = <
  TData = InfiniteData<Types.DefaultRevenueBreakdownQuery>,
  TError = unknown,
>(
  variables: Types.DefaultRevenueBreakdownQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DefaultRevenueBreakdownQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DefaultRevenueBreakdownQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DefaultRevenueBreakdownQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DefaultRevenueBreakdown.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DefaultRevenueBreakdownQuery,
            Types.DefaultRevenueBreakdownQueryVariables
          >(DefaultRevenueBreakdownDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDefaultRevenueBreakdownQuery.getKey = (
  variables: Types.DefaultRevenueBreakdownQueryVariables,
) => ["DefaultRevenueBreakdown.infinite", variables]

useInfiniteDefaultRevenueBreakdownQuery.rootKey = "DefaultRevenueBreakdown.infinite"

export const useSuspenseInfiniteDefaultRevenueBreakdownQuery = <
  TData = InfiniteData<Types.DefaultRevenueBreakdownQuery>,
  TError = unknown,
>(
  variables: Types.DefaultRevenueBreakdownQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.DefaultRevenueBreakdownQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DefaultRevenueBreakdownQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DefaultRevenueBreakdownQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DefaultRevenueBreakdown.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DefaultRevenueBreakdownQuery,
            Types.DefaultRevenueBreakdownQueryVariables
          >(DefaultRevenueBreakdownDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDefaultRevenueBreakdownQuery.getKey = (
  variables: Types.DefaultRevenueBreakdownQueryVariables,
) => ["DefaultRevenueBreakdown.infinite", variables]

useSuspenseInfiniteDefaultRevenueBreakdownQuery.rootKey =
  "DefaultRevenueBreakdown.infinite"

useDefaultRevenueBreakdownQuery.fetcher = (
  variables: Types.DefaultRevenueBreakdownQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DefaultRevenueBreakdownQuery,
    Types.DefaultRevenueBreakdownQueryVariables
  >(DefaultRevenueBreakdownDocument, variables, options)

export const BidPackageTableFacetsDocument = new TypedDocumentString(`
    query BidPackageTableFacets($orgSlug: String) {
  bidPackages {
    table(orgSlug: $orgSlug) {
      view {
        stats {
          buildingOwnerNames
          opsOwnerNames
        }
      }
    }
  }
}
    `)

export const useBidPackageTableFacetsQuery = <
  TData = Types.BidPackageTableFacetsQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BidPackageTableFacetsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableFacets"]
        : ["BidPackageTableFacets", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableFacetsQuery,
      Types.BidPackageTableFacetsQueryVariables
    >(BidPackageTableFacetsDocument, variables),
    ...options,
  })
}

useBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacets"]
    : ["BidPackageTableFacets", variables]

useBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets"

export const useSuspenseBidPackageTableFacetsQuery = <
  TData = Types.BidPackageTableFacetsQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidPackageTableFacetsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableFacets"]
        : ["BidPackageTableFacets", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableFacetsQuery,
      Types.BidPackageTableFacetsQueryVariables
    >(BidPackageTableFacetsDocument, variables),
    ...options,
  })
}

useSuspenseBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacets"]
    : ["BidPackageTableFacets", variables]

useSuspenseBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets"

export const useInfiniteBidPackageTableFacetsQuery = <
  TData = InfiniteData<Types.BidPackageTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidPackageTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableFacets.infinite"]
            : ["BidPackageTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableFacetsQuery,
            Types.BidPackageTableFacetsQueryVariables
          >(BidPackageTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacets.infinite"]
    : ["BidPackageTableFacets.infinite", variables]

useInfiniteBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets.infinite"

export const useSuspenseInfiniteBidPackageTableFacetsQuery = <
  TData = InfiniteData<Types.BidPackageTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidPackageTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableFacets.infinite"]
            : ["BidPackageTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableFacetsQuery,
            Types.BidPackageTableFacetsQueryVariables
          >(BidPackageTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacets.infinite"]
    : ["BidPackageTableFacets.infinite", variables]

useSuspenseInfiniteBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets.infinite"

useBidPackageTableFacetsQuery.fetcher = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BidPackageTableFacetsQuery,
    Types.BidPackageTableFacetsQueryVariables
  >(BidPackageTableFacetsDocument, variables, options)

export const BidPackageTableViewDocument = new TypedDocumentString(`
    query BidPackageTableView($orgSlug: String, $filterBy: BidPackageTableFilter, $sortBy: [BidPackageTableSort!]) {
  bidPackages {
    table(orgSlug: $orgSlug) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useBidPackageTableViewQuery = <
  TData = Types.BidPackageTableViewQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BidPackageTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableView"]
        : ["BidPackageTableView", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableViewQuery,
      Types.BidPackageTableViewQueryVariables
    >(BidPackageTableViewDocument, variables),
    ...options,
  })
}

useBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined ? ["BidPackageTableView"] : ["BidPackageTableView", variables]

useBidPackageTableViewQuery.rootKey = "BidPackageTableView"

export const useSuspenseBidPackageTableViewQuery = <
  TData = Types.BidPackageTableViewQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidPackageTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableView"]
        : ["BidPackageTableView", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableViewQuery,
      Types.BidPackageTableViewQueryVariables
    >(BidPackageTableViewDocument, variables),
    ...options,
  })
}

useSuspenseBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined ? ["BidPackageTableView"] : ["BidPackageTableView", variables]

useSuspenseBidPackageTableViewQuery.rootKey = "BidPackageTableView"

export const useInfiniteBidPackageTableViewQuery = <
  TData = InfiniteData<Types.BidPackageTableViewQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidPackageTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableView.infinite"]
            : ["BidPackageTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableViewQuery,
            Types.BidPackageTableViewQueryVariables
          >(BidPackageTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableView.infinite"]
    : ["BidPackageTableView.infinite", variables]

useInfiniteBidPackageTableViewQuery.rootKey = "BidPackageTableView.infinite"

export const useSuspenseInfiniteBidPackageTableViewQuery = <
  TData = InfiniteData<Types.BidPackageTableViewQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidPackageTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableView.infinite"]
            : ["BidPackageTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableViewQuery,
            Types.BidPackageTableViewQueryVariables
          >(BidPackageTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableView.infinite"]
    : ["BidPackageTableView.infinite", variables]

useSuspenseInfiniteBidPackageTableViewQuery.rootKey = "BidPackageTableView.infinite"

useBidPackageTableViewQuery.fetcher = (
  variables?: Types.BidPackageTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BidPackageTableViewQuery,
    Types.BidPackageTableViewQueryVariables
  >(BidPackageTableViewDocument, variables, options)

export const BidPackageTableRowsDocument = new TypedDocumentString(`
    query BidPackageTableRows($orgSlug: String, $keys: [String!]!) {
  bidPackages {
    table(orgSlug: $orgSlug) {
      rows(keys: $keys) {
        pid
        name
        buildingOwnerName
        orgSlug
        type
        propertyCount
        activeProjectCount
        opsOwnerName
        notionPageUrl
        biddingStatus
        invitedDeveloperCount
        participatingDeveloperCount
        biddingOpenAt
        biddingClosedAt
        bidReportVisible
        bidRemindersSent
        lossNoticesSent
      }
    }
  }
}
    `)

export const useBidPackageTableRowsQuery = <
  TData = Types.BidPackageTableRowsQuery,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BidPackageTableRowsQuery, TError, TData>({
    queryKey: ["BidPackageTableRows", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableRowsQuery,
      Types.BidPackageTableRowsQueryVariables
    >(BidPackageTableRowsDocument, variables),
    ...options,
  })
}

useBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRows", variables]

useBidPackageTableRowsQuery.rootKey = "BidPackageTableRows"

export const useSuspenseBidPackageTableRowsQuery = <
  TData = Types.BidPackageTableRowsQuery,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidPackageTableRowsQuery, TError, TData>({
    queryKey: ["BidPackageTableRows", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableRowsQuery,
      Types.BidPackageTableRowsQueryVariables
    >(BidPackageTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRows", variables]

useSuspenseBidPackageTableRowsQuery.rootKey = "BidPackageTableRows"

export const useInfiniteBidPackageTableRowsQuery = <
  TData = InfiniteData<Types.BidPackageTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidPackageTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidPackageTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableRowsQuery,
            Types.BidPackageTableRowsQueryVariables
          >(BidPackageTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRows.infinite", variables]

useInfiniteBidPackageTableRowsQuery.rootKey = "BidPackageTableRows.infinite"

export const useSuspenseInfiniteBidPackageTableRowsQuery = <
  TData = InfiniteData<Types.BidPackageTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidPackageTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidPackageTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableRowsQuery,
            Types.BidPackageTableRowsQueryVariables
          >(BidPackageTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRows.infinite", variables]

useSuspenseInfiniteBidPackageTableRowsQuery.rootKey = "BidPackageTableRows.infinite"

useBidPackageTableRowsQuery.fetcher = (
  variables: Types.BidPackageTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BidPackageTableRowsQuery,
    Types.BidPackageTableRowsQueryVariables
  >(BidPackageTableRowsDocument, variables, options)

export const SiteInvestorBidsDocument = new TypedDocumentString(`
    query SiteInvestorBids($sitePid: String!) {
  admin {
    siteInvestorBids(sitePid: $sitePid) {
      pid
      bidType
      systemSizeKw
      developerName
      createdAt
    }
  }
}
    `)

export const useSiteInvestorBidsQuery = <
  TData = Types.SiteInvestorBidsQuery,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteInvestorBidsQuery, TError, TData>({
    queryKey: ["SiteInvestorBids", variables],
    queryFn: publicFetcher<
      Types.SiteInvestorBidsQuery,
      Types.SiteInvestorBidsQueryVariables
    >(SiteInvestorBidsDocument, variables),
    ...options,
  })
}

useSiteInvestorBidsQuery.getKey = (variables: Types.SiteInvestorBidsQueryVariables) => [
  "SiteInvestorBids",
  variables,
]

useSiteInvestorBidsQuery.rootKey = "SiteInvestorBids"

export const useSuspenseSiteInvestorBidsQuery = <
  TData = Types.SiteInvestorBidsQuery,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteInvestorBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteInvestorBidsQuery, TError, TData>({
    queryKey: ["SiteInvestorBids", variables],
    queryFn: publicFetcher<
      Types.SiteInvestorBidsQuery,
      Types.SiteInvestorBidsQueryVariables
    >(SiteInvestorBidsDocument, variables),
    ...options,
  })
}

useSuspenseSiteInvestorBidsQuery.getKey = (
  variables: Types.SiteInvestorBidsQueryVariables,
) => ["SiteInvestorBids", variables]

useSuspenseSiteInvestorBidsQuery.rootKey = "SiteInvestorBids"

export const useInfiniteSiteInvestorBidsQuery = <
  TData = InfiniteData<Types.SiteInvestorBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteInvestorBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteInvestorBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteInvestorBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteInvestorBidsQuery,
            Types.SiteInvestorBidsQueryVariables
          >(SiteInvestorBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteInvestorBidsQuery.getKey = (
  variables: Types.SiteInvestorBidsQueryVariables,
) => ["SiteInvestorBids.infinite", variables]

useInfiniteSiteInvestorBidsQuery.rootKey = "SiteInvestorBids.infinite"

export const useSuspenseInfiniteSiteInvestorBidsQuery = <
  TData = InfiniteData<Types.SiteInvestorBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteInvestorBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteInvestorBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteInvestorBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteInvestorBidsQuery,
            Types.SiteInvestorBidsQueryVariables
          >(SiteInvestorBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteInvestorBidsQuery.getKey = (
  variables: Types.SiteInvestorBidsQueryVariables,
) => ["SiteInvestorBids.infinite", variables]

useSuspenseInfiniteSiteInvestorBidsQuery.rootKey = "SiteInvestorBids.infinite"

useSiteInvestorBidsQuery.fetcher = (
  variables: Types.SiteInvestorBidsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteInvestorBidsQuery, Types.SiteInvestorBidsQueryVariables>(
    SiteInvestorBidsDocument,
    variables,
    options,
  )

export const ProjectDocumentsDocument = new TypedDocumentString(`
    query ProjectDocuments($projectPid: String!, $orgSlug: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      documents(orgSlug: $orgSlug) {
        ...Document
      }
    }
  }
}
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}`)

export const useProjectDocumentsQuery = <
  TData = Types.ProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ProjectDocumentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectDocumentsQuery, TError, TData>({
    queryKey: ["ProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsQuery,
      Types.ProjectDocumentsQueryVariables
    >(ProjectDocumentsDocument, variables),
    ...options,
  })
}

useProjectDocumentsQuery.getKey = (variables: Types.ProjectDocumentsQueryVariables) => [
  "ProjectDocuments",
  variables,
]

useProjectDocumentsQuery.rootKey = "ProjectDocuments"

export const useSuspenseProjectDocumentsQuery = <
  TData = Types.ProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectDocumentsQuery, TError, TData>({
    queryKey: ["ProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsQuery,
      Types.ProjectDocumentsQueryVariables
    >(ProjectDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseProjectDocumentsQuery.getKey = (
  variables: Types.ProjectDocumentsQueryVariables,
) => ["ProjectDocuments", variables]

useSuspenseProjectDocumentsQuery.rootKey = "ProjectDocuments"

export const useInfiniteProjectDocumentsQuery = <
  TData = InfiniteData<Types.ProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsQuery,
            Types.ProjectDocumentsQueryVariables
          >(ProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectDocumentsQuery.getKey = (
  variables: Types.ProjectDocumentsQueryVariables,
) => ["ProjectDocuments.infinite", variables]

useInfiniteProjectDocumentsQuery.rootKey = "ProjectDocuments.infinite"

export const useSuspenseInfiniteProjectDocumentsQuery = <
  TData = InfiniteData<Types.ProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsQuery,
            Types.ProjectDocumentsQueryVariables
          >(ProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectDocumentsQuery.getKey = (
  variables: Types.ProjectDocumentsQueryVariables,
) => ["ProjectDocuments.infinite", variables]

useSuspenseInfiniteProjectDocumentsQuery.rootKey = "ProjectDocuments.infinite"

useProjectDocumentsQuery.fetcher = (
  variables: Types.ProjectDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectDocumentsQuery, Types.ProjectDocumentsQueryVariables>(
    ProjectDocumentsDocument,
    variables,
    options,
  )

export const DeveloperContractsListDocument = new TypedDocumentString(`
    query DeveloperContractsList {
  developerContracts {
    orgPid
    name
    platformStatus
    osaVariant
    counts {
      unassigned
      investorOriginationServicesAgreement
      investorChangeOrder
      investorHostAward
      investorScopeOfWork
      executedRoofLease
    }
    missing {
      missingOsa
      projectsMissingAward
      projectsMissingRoofLease
    }
    signing {
      total
      byStatus {
        status
        count
      }
    }
    assignedProjectCount
    bidPackagesAdded
    bidPackagesParticipated
  }
}
    `)

export const useDeveloperContractsListQuery = <
  TData = Types.DeveloperContractsListQuery,
  TError = unknown,
>(
  variables?: Types.DeveloperContractsListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DeveloperContractsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.DeveloperContractsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.DeveloperContractsListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["DeveloperContractsList"]
        : ["DeveloperContractsList", variables],
    queryFn: publicFetcher<
      Types.DeveloperContractsListQuery,
      Types.DeveloperContractsListQueryVariables
    >(DeveloperContractsListDocument, variables),
    ...options,
  })
}

useDeveloperContractsListQuery.getKey = (
  variables?: Types.DeveloperContractsListQueryVariables,
) =>
  variables === undefined
    ? ["DeveloperContractsList"]
    : ["DeveloperContractsList", variables]

useDeveloperContractsListQuery.rootKey = "DeveloperContractsList"

export const useSuspenseDeveloperContractsListQuery = <
  TData = Types.DeveloperContractsListQuery,
  TError = unknown,
>(
  variables?: Types.DeveloperContractsListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DeveloperContractsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DeveloperContractsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DeveloperContractsListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["DeveloperContractsList"]
        : ["DeveloperContractsList", variables],
    queryFn: publicFetcher<
      Types.DeveloperContractsListQuery,
      Types.DeveloperContractsListQueryVariables
    >(DeveloperContractsListDocument, variables),
    ...options,
  })
}

useSuspenseDeveloperContractsListQuery.getKey = (
  variables?: Types.DeveloperContractsListQueryVariables,
) =>
  variables === undefined
    ? ["DeveloperContractsList"]
    : ["DeveloperContractsList", variables]

useSuspenseDeveloperContractsListQuery.rootKey = "DeveloperContractsList"

export const useInfiniteDeveloperContractsListQuery = <
  TData = InfiniteData<Types.DeveloperContractsListQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperContractsListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DeveloperContractsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DeveloperContractsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DeveloperContractsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["DeveloperContractsList.infinite"]
            : ["DeveloperContractsList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperContractsListQuery,
            Types.DeveloperContractsListQueryVariables
          >(DeveloperContractsListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDeveloperContractsListQuery.getKey = (
  variables?: Types.DeveloperContractsListQueryVariables,
) =>
  variables === undefined
    ? ["DeveloperContractsList.infinite"]
    : ["DeveloperContractsList.infinite", variables]

useInfiniteDeveloperContractsListQuery.rootKey = "DeveloperContractsList.infinite"

export const useSuspenseInfiniteDeveloperContractsListQuery = <
  TData = InfiniteData<Types.DeveloperContractsListQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperContractsListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.DeveloperContractsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DeveloperContractsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DeveloperContractsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["DeveloperContractsList.infinite"]
            : ["DeveloperContractsList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperContractsListQuery,
            Types.DeveloperContractsListQueryVariables
          >(DeveloperContractsListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDeveloperContractsListQuery.getKey = (
  variables?: Types.DeveloperContractsListQueryVariables,
) =>
  variables === undefined
    ? ["DeveloperContractsList.infinite"]
    : ["DeveloperContractsList.infinite", variables]

useSuspenseInfiniteDeveloperContractsListQuery.rootKey =
  "DeveloperContractsList.infinite"

useDeveloperContractsListQuery.fetcher = (
  variables?: Types.DeveloperContractsListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeveloperContractsListQuery,
    Types.DeveloperContractsListQueryVariables
  >(DeveloperContractsListDocument, variables, options)

export const DeveloperContractDetailDocument = new TypedDocumentString(`
    query DeveloperContractDetail($orgPid: String!) {
  developerContract(orgPid: $orgPid) {
    orgPid
    name
    platformStatus
    osaVariant
    groups {
      tag
      documents {
        documentPid
        fileName
        buildingOwnerName
        dateCreated
        uploadedBy
        fileSize
        contentType
        hasAiAnalysis
        executionDate
        osaVariant
        onFile
        signing {
          ...SigningStateFields
        }
        projects {
          pid
          name
          address
          sitePid
        }
      }
    }
  }
}
    fragment SigningStateFields on DocumentSigningState {
  signingPid
  status
  source
  markedBy
  executedOn
  note
  dateSent
  expiresOn
  awaiting {
    name
    hasViewed
  }
  declinedBy {
    name
  }
  signers {
    name
    email
    status
    hasViewed
    signsForLumen
    signaturePage
  }
}`)

export const useDeveloperContractDetailQuery = <
  TData = Types.DeveloperContractDetailQuery,
  TError = unknown,
>(
  variables: Types.DeveloperContractDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DeveloperContractDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.DeveloperContractDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.DeveloperContractDetailQuery, TError, TData>({
    queryKey: ["DeveloperContractDetail", variables],
    queryFn: publicFetcher<
      Types.DeveloperContractDetailQuery,
      Types.DeveloperContractDetailQueryVariables
    >(DeveloperContractDetailDocument, variables),
    ...options,
  })
}

useDeveloperContractDetailQuery.getKey = (
  variables: Types.DeveloperContractDetailQueryVariables,
) => ["DeveloperContractDetail", variables]

useDeveloperContractDetailQuery.rootKey = "DeveloperContractDetail"

export const useSuspenseDeveloperContractDetailQuery = <
  TData = Types.DeveloperContractDetailQuery,
  TError = unknown,
>(
  variables: Types.DeveloperContractDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DeveloperContractDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DeveloperContractDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DeveloperContractDetailQuery, TError, TData>({
    queryKey: ["DeveloperContractDetail", variables],
    queryFn: publicFetcher<
      Types.DeveloperContractDetailQuery,
      Types.DeveloperContractDetailQueryVariables
    >(DeveloperContractDetailDocument, variables),
    ...options,
  })
}

useSuspenseDeveloperContractDetailQuery.getKey = (
  variables: Types.DeveloperContractDetailQueryVariables,
) => ["DeveloperContractDetail", variables]

useSuspenseDeveloperContractDetailQuery.rootKey = "DeveloperContractDetail"

export const useInfiniteDeveloperContractDetailQuery = <
  TData = InfiniteData<Types.DeveloperContractDetailQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperContractDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DeveloperContractDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DeveloperContractDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DeveloperContractDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperContractDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperContractDetailQuery,
            Types.DeveloperContractDetailQueryVariables
          >(DeveloperContractDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDeveloperContractDetailQuery.getKey = (
  variables: Types.DeveloperContractDetailQueryVariables,
) => ["DeveloperContractDetail.infinite", variables]

useInfiniteDeveloperContractDetailQuery.rootKey = "DeveloperContractDetail.infinite"

export const useSuspenseInfiniteDeveloperContractDetailQuery = <
  TData = InfiniteData<Types.DeveloperContractDetailQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperContractDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.DeveloperContractDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DeveloperContractDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DeveloperContractDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperContractDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperContractDetailQuery,
            Types.DeveloperContractDetailQueryVariables
          >(DeveloperContractDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDeveloperContractDetailQuery.getKey = (
  variables: Types.DeveloperContractDetailQueryVariables,
) => ["DeveloperContractDetail.infinite", variables]

useSuspenseInfiniteDeveloperContractDetailQuery.rootKey =
  "DeveloperContractDetail.infinite"

useDeveloperContractDetailQuery.fetcher = (
  variables: Types.DeveloperContractDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeveloperContractDetailQuery,
    Types.DeveloperContractDetailQueryVariables
  >(DeveloperContractDetailDocument, variables, options)

export const ChangeOrderAuditDocument = new TypedDocumentString(`
    query ChangeOrderAudit($orgPid: String!) {
  changeOrderAudit(orgPid: $orgPid) {
    orgPid
    name
    changeOrders {
      documentPid
      fileName
      title
      executionDate
      hasAiAnalysis
      priorHistory {
        kind
        referencedDate
      }
    }
    missing {
      referencedDate
      kinds
      referencedBy {
        documentPid
        fileName
      }
    }
  }
}
    `)

export const useChangeOrderAuditQuery = <
  TData = Types.ChangeOrderAuditQuery,
  TError = unknown,
>(
  variables: Types.ChangeOrderAuditQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ChangeOrderAuditQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ChangeOrderAuditQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ChangeOrderAuditQuery, TError, TData>({
    queryKey: ["ChangeOrderAudit", variables],
    queryFn: publicFetcher<
      Types.ChangeOrderAuditQuery,
      Types.ChangeOrderAuditQueryVariables
    >(ChangeOrderAuditDocument, variables),
    ...options,
  })
}

useChangeOrderAuditQuery.getKey = (variables: Types.ChangeOrderAuditQueryVariables) => [
  "ChangeOrderAudit",
  variables,
]

useChangeOrderAuditQuery.rootKey = "ChangeOrderAudit"

export const useSuspenseChangeOrderAuditQuery = <
  TData = Types.ChangeOrderAuditQuery,
  TError = unknown,
>(
  variables: Types.ChangeOrderAuditQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ChangeOrderAuditQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ChangeOrderAuditQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ChangeOrderAuditQuery, TError, TData>({
    queryKey: ["ChangeOrderAudit", variables],
    queryFn: publicFetcher<
      Types.ChangeOrderAuditQuery,
      Types.ChangeOrderAuditQueryVariables
    >(ChangeOrderAuditDocument, variables),
    ...options,
  })
}

useSuspenseChangeOrderAuditQuery.getKey = (
  variables: Types.ChangeOrderAuditQueryVariables,
) => ["ChangeOrderAudit", variables]

useSuspenseChangeOrderAuditQuery.rootKey = "ChangeOrderAudit"

export const useInfiniteChangeOrderAuditQuery = <
  TData = InfiniteData<Types.ChangeOrderAuditQuery>,
  TError = unknown,
>(
  variables: Types.ChangeOrderAuditQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ChangeOrderAuditQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ChangeOrderAuditQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ChangeOrderAuditQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ChangeOrderAudit.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ChangeOrderAuditQuery,
            Types.ChangeOrderAuditQueryVariables
          >(ChangeOrderAuditDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteChangeOrderAuditQuery.getKey = (
  variables: Types.ChangeOrderAuditQueryVariables,
) => ["ChangeOrderAudit.infinite", variables]

useInfiniteChangeOrderAuditQuery.rootKey = "ChangeOrderAudit.infinite"

export const useSuspenseInfiniteChangeOrderAuditQuery = <
  TData = InfiniteData<Types.ChangeOrderAuditQuery>,
  TError = unknown,
>(
  variables: Types.ChangeOrderAuditQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ChangeOrderAuditQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ChangeOrderAuditQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ChangeOrderAuditQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ChangeOrderAudit.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ChangeOrderAuditQuery,
            Types.ChangeOrderAuditQueryVariables
          >(ChangeOrderAuditDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteChangeOrderAuditQuery.getKey = (
  variables: Types.ChangeOrderAuditQueryVariables,
) => ["ChangeOrderAudit.infinite", variables]

useSuspenseInfiniteChangeOrderAuditQuery.rootKey = "ChangeOrderAudit.infinite"

useChangeOrderAuditQuery.fetcher = (
  variables: Types.ChangeOrderAuditQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ChangeOrderAuditQuery, Types.ChangeOrderAuditQueryVariables>(
    ChangeOrderAuditDocument,
    variables,
    options,
  )

export const DeveloperStagingDocumentsDocument = new TypedDocumentString(`
    query DeveloperStagingDocuments($orgPid: String!) {
  developerStagingDocuments(orgPid: $orgPid) {
    documentPid
    fileName
    suggestion {
      tag
      tagReasoning
      projects {
        pid
        name
        address
        sitePid
        reasoning
      }
    }
    analysis {
      status
      note
      errorMessage
    }
  }
}
    `)

export const useDeveloperStagingDocumentsQuery = <
  TData = Types.DeveloperStagingDocumentsQuery,
  TError = unknown,
>(
  variables: Types.DeveloperStagingDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DeveloperStagingDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.DeveloperStagingDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.DeveloperStagingDocumentsQuery, TError, TData>({
    queryKey: ["DeveloperStagingDocuments", variables],
    queryFn: publicFetcher<
      Types.DeveloperStagingDocumentsQuery,
      Types.DeveloperStagingDocumentsQueryVariables
    >(DeveloperStagingDocumentsDocument, variables),
    ...options,
  })
}

useDeveloperStagingDocumentsQuery.getKey = (
  variables: Types.DeveloperStagingDocumentsQueryVariables,
) => ["DeveloperStagingDocuments", variables]

useDeveloperStagingDocumentsQuery.rootKey = "DeveloperStagingDocuments"

export const useSuspenseDeveloperStagingDocumentsQuery = <
  TData = Types.DeveloperStagingDocumentsQuery,
  TError = unknown,
>(
  variables: Types.DeveloperStagingDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DeveloperStagingDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DeveloperStagingDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DeveloperStagingDocumentsQuery, TError, TData>({
    queryKey: ["DeveloperStagingDocuments", variables],
    queryFn: publicFetcher<
      Types.DeveloperStagingDocumentsQuery,
      Types.DeveloperStagingDocumentsQueryVariables
    >(DeveloperStagingDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseDeveloperStagingDocumentsQuery.getKey = (
  variables: Types.DeveloperStagingDocumentsQueryVariables,
) => ["DeveloperStagingDocuments", variables]

useSuspenseDeveloperStagingDocumentsQuery.rootKey = "DeveloperStagingDocuments"

export const useInfiniteDeveloperStagingDocumentsQuery = <
  TData = InfiniteData<Types.DeveloperStagingDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperStagingDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DeveloperStagingDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DeveloperStagingDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DeveloperStagingDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperStagingDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperStagingDocumentsQuery,
            Types.DeveloperStagingDocumentsQueryVariables
          >(DeveloperStagingDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDeveloperStagingDocumentsQuery.getKey = (
  variables: Types.DeveloperStagingDocumentsQueryVariables,
) => ["DeveloperStagingDocuments.infinite", variables]

useInfiniteDeveloperStagingDocumentsQuery.rootKey = "DeveloperStagingDocuments.infinite"

export const useSuspenseInfiniteDeveloperStagingDocumentsQuery = <
  TData = InfiniteData<Types.DeveloperStagingDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperStagingDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.DeveloperStagingDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DeveloperStagingDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DeveloperStagingDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperStagingDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperStagingDocumentsQuery,
            Types.DeveloperStagingDocumentsQueryVariables
          >(DeveloperStagingDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDeveloperStagingDocumentsQuery.getKey = (
  variables: Types.DeveloperStagingDocumentsQueryVariables,
) => ["DeveloperStagingDocuments.infinite", variables]

useSuspenseInfiniteDeveloperStagingDocumentsQuery.rootKey =
  "DeveloperStagingDocuments.infinite"

useDeveloperStagingDocumentsQuery.fetcher = (
  variables: Types.DeveloperStagingDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeveloperStagingDocumentsQuery,
    Types.DeveloperStagingDocumentsQueryVariables
  >(DeveloperStagingDocumentsDocument, variables, options)

export const DeveloperProjectsDocument = new TypedDocumentString(`
    query DeveloperProjects($orgPid: String!) {
  developerProjects(orgPid: $orgPid) {
    pid
    name
    buildingOwnerName
    siteAddress
    siteName
    sitePid
    status
    closedAt
    closeReason
    closeCustomReason
    projectType
    systemSizeKwDc
    systemSizeKwAc
    missingAward
    missingRoofLease
    changeOrders {
      ...ProjectContractDocumentFields
    }
    hostAwards {
      ...ProjectContractDocumentFields
    }
    scopeOfWorks {
      ...ProjectContractDocumentFields
    }
    executedRoofLeases {
      ...ProjectContractDocumentFields
    }
  }
}
    fragment SigningStateFields on DocumentSigningState {
  signingPid
  status
  source
  markedBy
  executedOn
  note
  dateSent
  expiresOn
  awaiting {
    name
    hasViewed
  }
  declinedBy {
    name
  }
  signers {
    name
    email
    status
    hasViewed
    signsForLumen
    signaturePage
  }
}
fragment ProjectContractDocumentFields on ProjectContractDocument {
  documentPid
  fileName
  onFile
  signing {
    ...SigningStateFields
  }
}`)

export const useDeveloperProjectsQuery = <
  TData = Types.DeveloperProjectsQuery,
  TError = unknown,
>(
  variables: Types.DeveloperProjectsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DeveloperProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.DeveloperProjectsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.DeveloperProjectsQuery, TError, TData>({
    queryKey: ["DeveloperProjects", variables],
    queryFn: publicFetcher<
      Types.DeveloperProjectsQuery,
      Types.DeveloperProjectsQueryVariables
    >(DeveloperProjectsDocument, variables),
    ...options,
  })
}

useDeveloperProjectsQuery.getKey = (
  variables: Types.DeveloperProjectsQueryVariables,
) => ["DeveloperProjects", variables]

useDeveloperProjectsQuery.rootKey = "DeveloperProjects"

export const useSuspenseDeveloperProjectsQuery = <
  TData = Types.DeveloperProjectsQuery,
  TError = unknown,
>(
  variables: Types.DeveloperProjectsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DeveloperProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DeveloperProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DeveloperProjectsQuery, TError, TData>({
    queryKey: ["DeveloperProjects", variables],
    queryFn: publicFetcher<
      Types.DeveloperProjectsQuery,
      Types.DeveloperProjectsQueryVariables
    >(DeveloperProjectsDocument, variables),
    ...options,
  })
}

useSuspenseDeveloperProjectsQuery.getKey = (
  variables: Types.DeveloperProjectsQueryVariables,
) => ["DeveloperProjects", variables]

useSuspenseDeveloperProjectsQuery.rootKey = "DeveloperProjects"

export const useInfiniteDeveloperProjectsQuery = <
  TData = InfiniteData<Types.DeveloperProjectsQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperProjectsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DeveloperProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DeveloperProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DeveloperProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperProjectsQuery,
            Types.DeveloperProjectsQueryVariables
          >(DeveloperProjectsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDeveloperProjectsQuery.getKey = (
  variables: Types.DeveloperProjectsQueryVariables,
) => ["DeveloperProjects.infinite", variables]

useInfiniteDeveloperProjectsQuery.rootKey = "DeveloperProjects.infinite"

export const useSuspenseInfiniteDeveloperProjectsQuery = <
  TData = InfiniteData<Types.DeveloperProjectsQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperProjectsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.DeveloperProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DeveloperProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DeveloperProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperProjectsQuery,
            Types.DeveloperProjectsQueryVariables
          >(DeveloperProjectsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDeveloperProjectsQuery.getKey = (
  variables: Types.DeveloperProjectsQueryVariables,
) => ["DeveloperProjects.infinite", variables]

useSuspenseInfiniteDeveloperProjectsQuery.rootKey = "DeveloperProjects.infinite"

useDeveloperProjectsQuery.fetcher = (
  variables: Types.DeveloperProjectsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeveloperProjectsQuery, Types.DeveloperProjectsQueryVariables>(
    DeveloperProjectsDocument,
    variables,
    options,
  )

export const InterconnectionSegmentLayerDocument = new TypedDocumentString(`
    query InterconnectionSegmentLayer($filters: InterconnectionSegmentFilterInput!) {
  referenceMaps {
    interconnectionSegment {
      layer(filters: $filters) {
        pk
        segmentId
        shapeGeojson
        utilityShortName
        feeder
        substation
        pvVoltageRaw
        pvVoltageKv
        pvHostingCapacityKw
        pvFeederInstalledCapacityKw
        pvFeederQueuedCapacityKw
        pvSubstationHostingCapacityKw
        pvSubstationQueuedCapacityKw
        pvSubstationQueuedApplicationsCount
        storageChargeCapacityKw
        storageMinChargeKw
        storageMaxChargeKw
        storageDischargeCapacityKw
        storageMinDischargeKw
        storageMaxDischargeKw
      }
    }
  }
}
    `)

export const useInterconnectionSegmentLayerQuery = <
  TData = Types.InterconnectionSegmentLayerQuery,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentLayerQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InterconnectionSegmentLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InterconnectionSegmentLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InterconnectionSegmentLayerQuery, TError, TData>({
    queryKey: ["InterconnectionSegmentLayer", variables],
    queryFn: publicFetcher<
      Types.InterconnectionSegmentLayerQuery,
      Types.InterconnectionSegmentLayerQueryVariables
    >(InterconnectionSegmentLayerDocument, variables),
    ...options,
  })
}

useInterconnectionSegmentLayerQuery.getKey = (
  variables: Types.InterconnectionSegmentLayerQueryVariables,
) => ["InterconnectionSegmentLayer", variables]

useInterconnectionSegmentLayerQuery.rootKey = "InterconnectionSegmentLayer"

export const useSuspenseInterconnectionSegmentLayerQuery = <
  TData = Types.InterconnectionSegmentLayerQuery,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentLayerQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InterconnectionSegmentLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InterconnectionSegmentLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InterconnectionSegmentLayerQuery, TError, TData>({
    queryKey: ["InterconnectionSegmentLayer", variables],
    queryFn: publicFetcher<
      Types.InterconnectionSegmentLayerQuery,
      Types.InterconnectionSegmentLayerQueryVariables
    >(InterconnectionSegmentLayerDocument, variables),
    ...options,
  })
}

useSuspenseInterconnectionSegmentLayerQuery.getKey = (
  variables: Types.InterconnectionSegmentLayerQueryVariables,
) => ["InterconnectionSegmentLayer", variables]

useSuspenseInterconnectionSegmentLayerQuery.rootKey = "InterconnectionSegmentLayer"

export const useInfiniteInterconnectionSegmentLayerQuery = <
  TData = InfiniteData<Types.InterconnectionSegmentLayerQuery>,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentLayerQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InterconnectionSegmentLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InterconnectionSegmentLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InterconnectionSegmentLayerQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InterconnectionSegmentLayer.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InterconnectionSegmentLayerQuery,
            Types.InterconnectionSegmentLayerQueryVariables
          >(InterconnectionSegmentLayerDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInterconnectionSegmentLayerQuery.getKey = (
  variables: Types.InterconnectionSegmentLayerQueryVariables,
) => ["InterconnectionSegmentLayer.infinite", variables]

useInfiniteInterconnectionSegmentLayerQuery.rootKey =
  "InterconnectionSegmentLayer.infinite"

export const useSuspenseInfiniteInterconnectionSegmentLayerQuery = <
  TData = InfiniteData<Types.InterconnectionSegmentLayerQuery>,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentLayerQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.InterconnectionSegmentLayerQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InterconnectionSegmentLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.InterconnectionSegmentLayerQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InterconnectionSegmentLayer.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InterconnectionSegmentLayerQuery,
            Types.InterconnectionSegmentLayerQueryVariables
          >(InterconnectionSegmentLayerDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInterconnectionSegmentLayerQuery.getKey = (
  variables: Types.InterconnectionSegmentLayerQueryVariables,
) => ["InterconnectionSegmentLayer.infinite", variables]

useSuspenseInfiniteInterconnectionSegmentLayerQuery.rootKey =
  "InterconnectionSegmentLayer.infinite"

useInterconnectionSegmentLayerQuery.fetcher = (
  variables: Types.InterconnectionSegmentLayerQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InterconnectionSegmentLayerQuery,
    Types.InterconnectionSegmentLayerQueryVariables
  >(InterconnectionSegmentLayerDocument, variables, options)

export const InterconnectionSegmentStatsDocument = new TypedDocumentString(`
    query InterconnectionSegmentStats($filters: InterconnectionSegmentFilterInput!) {
  referenceMaps {
    interconnectionSegment {
      stats(filters: $filters) {
        pvHostingCapacityKw {
          min
          max
        }
        netOverallPvCapacityKw {
          min
          max
        }
      }
    }
  }
}
    `)

export const useInterconnectionSegmentStatsQuery = <
  TData = Types.InterconnectionSegmentStatsQuery,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InterconnectionSegmentStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InterconnectionSegmentStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InterconnectionSegmentStatsQuery, TError, TData>({
    queryKey: ["InterconnectionSegmentStats", variables],
    queryFn: publicFetcher<
      Types.InterconnectionSegmentStatsQuery,
      Types.InterconnectionSegmentStatsQueryVariables
    >(InterconnectionSegmentStatsDocument, variables),
    ...options,
  })
}

useInterconnectionSegmentStatsQuery.getKey = (
  variables: Types.InterconnectionSegmentStatsQueryVariables,
) => ["InterconnectionSegmentStats", variables]

useInterconnectionSegmentStatsQuery.rootKey = "InterconnectionSegmentStats"

export const useSuspenseInterconnectionSegmentStatsQuery = <
  TData = Types.InterconnectionSegmentStatsQuery,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InterconnectionSegmentStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InterconnectionSegmentStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InterconnectionSegmentStatsQuery, TError, TData>({
    queryKey: ["InterconnectionSegmentStats", variables],
    queryFn: publicFetcher<
      Types.InterconnectionSegmentStatsQuery,
      Types.InterconnectionSegmentStatsQueryVariables
    >(InterconnectionSegmentStatsDocument, variables),
    ...options,
  })
}

useSuspenseInterconnectionSegmentStatsQuery.getKey = (
  variables: Types.InterconnectionSegmentStatsQueryVariables,
) => ["InterconnectionSegmentStats", variables]

useSuspenseInterconnectionSegmentStatsQuery.rootKey = "InterconnectionSegmentStats"

export const useInfiniteInterconnectionSegmentStatsQuery = <
  TData = InfiniteData<Types.InterconnectionSegmentStatsQuery>,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InterconnectionSegmentStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InterconnectionSegmentStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InterconnectionSegmentStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InterconnectionSegmentStats.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InterconnectionSegmentStatsQuery,
            Types.InterconnectionSegmentStatsQueryVariables
          >(InterconnectionSegmentStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInterconnectionSegmentStatsQuery.getKey = (
  variables: Types.InterconnectionSegmentStatsQueryVariables,
) => ["InterconnectionSegmentStats.infinite", variables]

useInfiniteInterconnectionSegmentStatsQuery.rootKey =
  "InterconnectionSegmentStats.infinite"

export const useSuspenseInfiniteInterconnectionSegmentStatsQuery = <
  TData = InfiniteData<Types.InterconnectionSegmentStatsQuery>,
  TError = unknown,
>(
  variables: Types.InterconnectionSegmentStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.InterconnectionSegmentStatsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InterconnectionSegmentStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.InterconnectionSegmentStatsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InterconnectionSegmentStats.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InterconnectionSegmentStatsQuery,
            Types.InterconnectionSegmentStatsQueryVariables
          >(InterconnectionSegmentStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInterconnectionSegmentStatsQuery.getKey = (
  variables: Types.InterconnectionSegmentStatsQueryVariables,
) => ["InterconnectionSegmentStats.infinite", variables]

useSuspenseInfiniteInterconnectionSegmentStatsQuery.rootKey =
  "InterconnectionSegmentStats.infinite"

useInterconnectionSegmentStatsQuery.fetcher = (
  variables: Types.InterconnectionSegmentStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InterconnectionSegmentStatsQuery,
    Types.InterconnectionSegmentStatsQueryVariables
  >(InterconnectionSegmentStatsDocument, variables, options)

export const WetlandsLayerDocument = new TypedDocumentString(`
    query WetlandsLayer($filters: WetlandFilterInput!) {
  referenceMaps {
    wetlands {
      layer(filters: $filters) {
        shapeGeojson
        areaSqft
        wetlandType
      }
    }
  }
}
    `)

export const useWetlandsLayerQuery = <
  TData = Types.WetlandsLayerQuery,
  TError = unknown,
>(
  variables: Types.WetlandsLayerQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.WetlandsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.WetlandsLayerQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.WetlandsLayerQuery, TError, TData>({
    queryKey: ["WetlandsLayer", variables],
    queryFn: publicFetcher<Types.WetlandsLayerQuery, Types.WetlandsLayerQueryVariables>(
      WetlandsLayerDocument,
      variables,
    ),
    ...options,
  })
}

useWetlandsLayerQuery.getKey = (variables: Types.WetlandsLayerQueryVariables) => [
  "WetlandsLayer",
  variables,
]

useWetlandsLayerQuery.rootKey = "WetlandsLayer"

export const useSuspenseWetlandsLayerQuery = <
  TData = Types.WetlandsLayerQuery,
  TError = unknown,
>(
  variables: Types.WetlandsLayerQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.WetlandsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.WetlandsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.WetlandsLayerQuery, TError, TData>({
    queryKey: ["WetlandsLayer", variables],
    queryFn: publicFetcher<Types.WetlandsLayerQuery, Types.WetlandsLayerQueryVariables>(
      WetlandsLayerDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseWetlandsLayerQuery.getKey = (
  variables: Types.WetlandsLayerQueryVariables,
) => ["WetlandsLayer", variables]

useSuspenseWetlandsLayerQuery.rootKey = "WetlandsLayer"

export const useInfiniteWetlandsLayerQuery = <
  TData = InfiniteData<Types.WetlandsLayerQuery>,
  TError = unknown,
>(
  variables: Types.WetlandsLayerQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.WetlandsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.WetlandsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.WetlandsLayerQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["WetlandsLayer.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.WetlandsLayerQuery, Types.WetlandsLayerQueryVariables>(
            WetlandsLayerDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteWetlandsLayerQuery.getKey = (
  variables: Types.WetlandsLayerQueryVariables,
) => ["WetlandsLayer.infinite", variables]

useInfiniteWetlandsLayerQuery.rootKey = "WetlandsLayer.infinite"

export const useSuspenseInfiniteWetlandsLayerQuery = <
  TData = InfiniteData<Types.WetlandsLayerQuery>,
  TError = unknown,
>(
  variables: Types.WetlandsLayerQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.WetlandsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.WetlandsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.WetlandsLayerQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["WetlandsLayer.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.WetlandsLayerQuery, Types.WetlandsLayerQueryVariables>(
            WetlandsLayerDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteWetlandsLayerQuery.getKey = (
  variables: Types.WetlandsLayerQueryVariables,
) => ["WetlandsLayer.infinite", variables]

useSuspenseInfiniteWetlandsLayerQuery.rootKey = "WetlandsLayer.infinite"

useWetlandsLayerQuery.fetcher = (
  variables: Types.WetlandsLayerQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.WetlandsLayerQuery, Types.WetlandsLayerQueryVariables>(
    WetlandsLayerDocument,
    variables,
    options,
  )

export const SearchOrgSitesDocument = new TypedDocumentString(`
    query SearchOrgSites($orgSlug: String!, $searchTerm: String!) {
  orgBySlug(slug: $orgSlug) {
    searchSites(searchTerm: $searchTerm) {
      pid
      name
      streetAddress
      city
      state
      zipCode
    }
  }
}
    `)

export const useSearchOrgSitesQuery = <
  TData = Types.SearchOrgSitesQuery,
  TError = unknown,
>(
  variables: Types.SearchOrgSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SearchOrgSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SearchOrgSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SearchOrgSitesQuery, TError, TData>({
    queryKey: ["SearchOrgSites", variables],
    queryFn: publicFetcher<
      Types.SearchOrgSitesQuery,
      Types.SearchOrgSitesQueryVariables
    >(SearchOrgSitesDocument, variables),
    ...options,
  })
}

useSearchOrgSitesQuery.getKey = (variables: Types.SearchOrgSitesQueryVariables) => [
  "SearchOrgSites",
  variables,
]

useSearchOrgSitesQuery.rootKey = "SearchOrgSites"

export const useSuspenseSearchOrgSitesQuery = <
  TData = Types.SearchOrgSitesQuery,
  TError = unknown,
>(
  variables: Types.SearchOrgSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SearchOrgSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SearchOrgSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SearchOrgSitesQuery, TError, TData>({
    queryKey: ["SearchOrgSites", variables],
    queryFn: publicFetcher<
      Types.SearchOrgSitesQuery,
      Types.SearchOrgSitesQueryVariables
    >(SearchOrgSitesDocument, variables),
    ...options,
  })
}

useSuspenseSearchOrgSitesQuery.getKey = (
  variables: Types.SearchOrgSitesQueryVariables,
) => ["SearchOrgSites", variables]

useSuspenseSearchOrgSitesQuery.rootKey = "SearchOrgSites"

export const useInfiniteSearchOrgSitesQuery = <
  TData = InfiniteData<Types.SearchOrgSitesQuery>,
  TError = unknown,
>(
  variables: Types.SearchOrgSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SearchOrgSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SearchOrgSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SearchOrgSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SearchOrgSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SearchOrgSitesQuery, Types.SearchOrgSitesQueryVariables>(
            SearchOrgSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSearchOrgSitesQuery.getKey = (
  variables: Types.SearchOrgSitesQueryVariables,
) => ["SearchOrgSites.infinite", variables]

useInfiniteSearchOrgSitesQuery.rootKey = "SearchOrgSites.infinite"

export const useSuspenseInfiniteSearchOrgSitesQuery = <
  TData = InfiniteData<Types.SearchOrgSitesQuery>,
  TError = unknown,
>(
  variables: Types.SearchOrgSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SearchOrgSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SearchOrgSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SearchOrgSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SearchOrgSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SearchOrgSitesQuery, Types.SearchOrgSitesQueryVariables>(
            SearchOrgSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSearchOrgSitesQuery.getKey = (
  variables: Types.SearchOrgSitesQueryVariables,
) => ["SearchOrgSites.infinite", variables]

useSuspenseInfiniteSearchOrgSitesQuery.rootKey = "SearchOrgSites.infinite"

useSearchOrgSitesQuery.fetcher = (
  variables: Types.SearchOrgSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SearchOrgSitesQuery, Types.SearchOrgSitesQueryVariables>(
    SearchOrgSitesDocument,
    variables,
    options,
  )

export const BidPackageSiteDocument = new TypedDocumentString(`
    query BidPackageSite($orgSlug: String!, $sitePid: String!, $financingType: FinProductType!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid, includeResults: false) {
      pid
      activeResults(financingType: $financingType) {
        ...ResultSummary
      }
    }
  }
}
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    strategy
    assumptions {
      battChemName
    }
  }
}
fragment RoofRentEconomics on RoofRentEconomics {
  lifetimeUsd
  annualUsd
  usdPerSqft
  npv
  discountRate
  roofRentPerYear
}
fragment RevampCSBase on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    ...RoofRentEconomics
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  pvRentRateSource
  pvRentRatePerKwPerYear
}
fragment RevampCS on RevampCS {
  ...RevampCSBase
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    strategy
  }
}
fragment RevampFinmodelSummary on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  offerStatus
  fmvStr
  goal
  isVnem
  isGreenLease
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      totalUtilitySavings
      totalNet
      netCashflowPerYear
      tenantSavingsPct
    }
  }
  loan {
    __typename
    loanTermYears
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  selfInvest {
    __typename
    npv
    termYears
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    flows {
      totalUtilitySavings
      totalCapex
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalUtilitySavings
        tenantSavingsPct
      }
    }
  }
  systems {
    ...RevampSystem
  }
}
fragment ResultSummary on Result {
  id
  pid
  isPinned
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodelSummary
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}`)

export const useBidPackageSiteQuery = <
  TData = Types.BidPackageSiteQuery,
  TError = unknown,
>(
  variables: Types.BidPackageSiteQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidPackageSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BidPackageSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidPackageSiteQuery, TError, TData>({
    queryKey: ["BidPackageSite", variables],
    queryFn: publicFetcher<
      Types.BidPackageSiteQuery,
      Types.BidPackageSiteQueryVariables
    >(BidPackageSiteDocument, variables),
    ...options,
  })
}

useBidPackageSiteQuery.getKey = (variables: Types.BidPackageSiteQueryVariables) => [
  "BidPackageSite",
  variables,
]

useBidPackageSiteQuery.rootKey = "BidPackageSite"

export const useSuspenseBidPackageSiteQuery = <
  TData = Types.BidPackageSiteQuery,
  TError = unknown,
>(
  variables: Types.BidPackageSiteQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidPackageSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidPackageSiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidPackageSiteQuery, TError, TData>({
    queryKey: ["BidPackageSite", variables],
    queryFn: publicFetcher<
      Types.BidPackageSiteQuery,
      Types.BidPackageSiteQueryVariables
    >(BidPackageSiteDocument, variables),
    ...options,
  })
}

useSuspenseBidPackageSiteQuery.getKey = (
  variables: Types.BidPackageSiteQueryVariables,
) => ["BidPackageSite", variables]

useSuspenseBidPackageSiteQuery.rootKey = "BidPackageSite"

export const useInfiniteBidPackageSiteQuery = <
  TData = InfiniteData<Types.BidPackageSiteQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageSiteQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidPackageSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidPackageSiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidPackageSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidPackageSite.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidPackageSiteQuery, Types.BidPackageSiteQueryVariables>(
            BidPackageSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidPackageSiteQuery.getKey = (
  variables: Types.BidPackageSiteQueryVariables,
) => ["BidPackageSite.infinite", variables]

useInfiniteBidPackageSiteQuery.rootKey = "BidPackageSite.infinite"

export const useSuspenseInfiniteBidPackageSiteQuery = <
  TData = InfiniteData<Types.BidPackageSiteQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageSiteQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidPackageSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidPackageSiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidPackageSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidPackageSite.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidPackageSiteQuery, Types.BidPackageSiteQueryVariables>(
            BidPackageSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidPackageSiteQuery.getKey = (
  variables: Types.BidPackageSiteQueryVariables,
) => ["BidPackageSite.infinite", variables]

useSuspenseInfiniteBidPackageSiteQuery.rootKey = "BidPackageSite.infinite"

useBidPackageSiteQuery.fetcher = (
  variables: Types.BidPackageSiteQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidPackageSiteQuery, Types.BidPackageSiteQueryVariables>(
    BidPackageSiteDocument,
    variables,
    options,
  )

export const ProjectMilestonesForEditingDocument = new TypedDocumentString(`
    query ProjectMilestonesForEditing($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      milestoneSummary {
        leaseSignedEstimated
        leaseSignedActual
        ixSubmittedEstimated
        ixSubmittedActual
        workableIxResultsReceivedEstimated
        workableIxResultsReceivedActual
        ixApprovedEstimated
        ixApprovedActual
        csProgramSubmittedEstimated
        csProgramSubmittedActual
        csProgramApprovedEstimated
        csProgramApprovedActual
        ntpEstimated
        ntpActual
        codEstimated
        codActual
        updatedAt
        updatedByName
      }
    }
  }
}
    `)

export const useProjectMilestonesForEditingQuery = <
  TData = Types.ProjectMilestonesForEditingQuery,
  TError = unknown,
>(
  variables: Types.ProjectMilestonesForEditingQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectMilestonesForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectMilestonesForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectMilestonesForEditingQuery, TError, TData>({
    queryKey: ["ProjectMilestonesForEditing", variables],
    queryFn: publicFetcher<
      Types.ProjectMilestonesForEditingQuery,
      Types.ProjectMilestonesForEditingQueryVariables
    >(ProjectMilestonesForEditingDocument, variables),
    ...options,
  })
}

useProjectMilestonesForEditingQuery.getKey = (
  variables: Types.ProjectMilestonesForEditingQueryVariables,
) => ["ProjectMilestonesForEditing", variables]

useProjectMilestonesForEditingQuery.rootKey = "ProjectMilestonesForEditing"

export const useSuspenseProjectMilestonesForEditingQuery = <
  TData = Types.ProjectMilestonesForEditingQuery,
  TError = unknown,
>(
  variables: Types.ProjectMilestonesForEditingQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectMilestonesForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectMilestonesForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectMilestonesForEditingQuery, TError, TData>({
    queryKey: ["ProjectMilestonesForEditing", variables],
    queryFn: publicFetcher<
      Types.ProjectMilestonesForEditingQuery,
      Types.ProjectMilestonesForEditingQueryVariables
    >(ProjectMilestonesForEditingDocument, variables),
    ...options,
  })
}

useSuspenseProjectMilestonesForEditingQuery.getKey = (
  variables: Types.ProjectMilestonesForEditingQueryVariables,
) => ["ProjectMilestonesForEditing", variables]

useSuspenseProjectMilestonesForEditingQuery.rootKey = "ProjectMilestonesForEditing"

export const useInfiniteProjectMilestonesForEditingQuery = <
  TData = InfiniteData<Types.ProjectMilestonesForEditingQuery>,
  TError = unknown,
>(
  variables: Types.ProjectMilestonesForEditingQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectMilestonesForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectMilestonesForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectMilestonesForEditingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ProjectMilestonesForEditing.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectMilestonesForEditingQuery,
            Types.ProjectMilestonesForEditingQueryVariables
          >(ProjectMilestonesForEditingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectMilestonesForEditingQuery.getKey = (
  variables: Types.ProjectMilestonesForEditingQueryVariables,
) => ["ProjectMilestonesForEditing.infinite", variables]

useInfiniteProjectMilestonesForEditingQuery.rootKey =
  "ProjectMilestonesForEditing.infinite"

export const useSuspenseInfiniteProjectMilestonesForEditingQuery = <
  TData = InfiniteData<Types.ProjectMilestonesForEditingQuery>,
  TError = unknown,
>(
  variables: Types.ProjectMilestonesForEditingQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.ProjectMilestonesForEditingQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectMilestonesForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.ProjectMilestonesForEditingQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ProjectMilestonesForEditing.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectMilestonesForEditingQuery,
            Types.ProjectMilestonesForEditingQueryVariables
          >(ProjectMilestonesForEditingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectMilestonesForEditingQuery.getKey = (
  variables: Types.ProjectMilestonesForEditingQueryVariables,
) => ["ProjectMilestonesForEditing.infinite", variables]

useSuspenseInfiniteProjectMilestonesForEditingQuery.rootKey =
  "ProjectMilestonesForEditing.infinite"

useProjectMilestonesForEditingQuery.fetcher = (
  variables: Types.ProjectMilestonesForEditingQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectMilestonesForEditingQuery,
    Types.ProjectMilestonesForEditingQueryVariables
  >(ProjectMilestonesForEditingDocument, variables, options)

export const ProjectSystemForEditingDocument = new TypedDocumentString(`
    query ProjectSystemForEditing($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      site {
        pid
      }
      projectSpecification {
        cs {
          systemLite {
            ...SystemLite
          }
        }
        ppa {
          system {
            ...System
          }
        }
        cash {
          system {
            ...System
          }
        }
      }
    }
  }
}
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    footprintGeojson
    footprintSqft
    footprintDocument {
      pid
      fileName
      downloadHref
    }
  }
}
fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    __typename
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
    signedViewUrl
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}`)

export const useProjectSystemForEditingQuery = <
  TData = Types.ProjectSystemForEditingQuery,
  TError = unknown,
>(
  variables: Types.ProjectSystemForEditingQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectSystemForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectSystemForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectSystemForEditingQuery, TError, TData>({
    queryKey: ["ProjectSystemForEditing", variables],
    queryFn: publicFetcher<
      Types.ProjectSystemForEditingQuery,
      Types.ProjectSystemForEditingQueryVariables
    >(ProjectSystemForEditingDocument, variables),
    ...options,
  })
}

useProjectSystemForEditingQuery.getKey = (
  variables: Types.ProjectSystemForEditingQueryVariables,
) => ["ProjectSystemForEditing", variables]

useProjectSystemForEditingQuery.rootKey = "ProjectSystemForEditing"

export const useSuspenseProjectSystemForEditingQuery = <
  TData = Types.ProjectSystemForEditingQuery,
  TError = unknown,
>(
  variables: Types.ProjectSystemForEditingQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectSystemForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectSystemForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectSystemForEditingQuery, TError, TData>({
    queryKey: ["ProjectSystemForEditing", variables],
    queryFn: publicFetcher<
      Types.ProjectSystemForEditingQuery,
      Types.ProjectSystemForEditingQueryVariables
    >(ProjectSystemForEditingDocument, variables),
    ...options,
  })
}

useSuspenseProjectSystemForEditingQuery.getKey = (
  variables: Types.ProjectSystemForEditingQueryVariables,
) => ["ProjectSystemForEditing", variables]

useSuspenseProjectSystemForEditingQuery.rootKey = "ProjectSystemForEditing"

export const useInfiniteProjectSystemForEditingQuery = <
  TData = InfiniteData<Types.ProjectSystemForEditingQuery>,
  TError = unknown,
>(
  variables: Types.ProjectSystemForEditingQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectSystemForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectSystemForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectSystemForEditingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectSystemForEditing.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectSystemForEditingQuery,
            Types.ProjectSystemForEditingQueryVariables
          >(ProjectSystemForEditingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectSystemForEditingQuery.getKey = (
  variables: Types.ProjectSystemForEditingQueryVariables,
) => ["ProjectSystemForEditing.infinite", variables]

useInfiniteProjectSystemForEditingQuery.rootKey = "ProjectSystemForEditing.infinite"

export const useSuspenseInfiniteProjectSystemForEditingQuery = <
  TData = InfiniteData<Types.ProjectSystemForEditingQuery>,
  TError = unknown,
>(
  variables: Types.ProjectSystemForEditingQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectSystemForEditingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectSystemForEditingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectSystemForEditingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectSystemForEditing.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectSystemForEditingQuery,
            Types.ProjectSystemForEditingQueryVariables
          >(ProjectSystemForEditingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectSystemForEditingQuery.getKey = (
  variables: Types.ProjectSystemForEditingQueryVariables,
) => ["ProjectSystemForEditing.infinite", variables]

useSuspenseInfiniteProjectSystemForEditingQuery.rootKey =
  "ProjectSystemForEditing.infinite"

useProjectSystemForEditingQuery.fetcher = (
  variables: Types.ProjectSystemForEditingQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectSystemForEditingQuery,
    Types.ProjectSystemForEditingQueryVariables
  >(ProjectSystemForEditingDocument, variables, options)

export const CeleryTaskDocument = new TypedDocumentString(`
    query CeleryTask($taskId: String!) {
  celery {
    task(taskId: $taskId) {
      taskId
      state
    }
  }
}
    `)

export const useCeleryTaskQuery = <TData = Types.CeleryTaskQuery, TError = unknown>(
  variables: Types.CeleryTaskQueryVariables,
  options?: Omit<UseQueryOptions<Types.CeleryTaskQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.CeleryTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CeleryTaskQuery, TError, TData>({
    queryKey: ["CeleryTask", variables],
    queryFn: publicFetcher<Types.CeleryTaskQuery, Types.CeleryTaskQueryVariables>(
      CeleryTaskDocument,
      variables,
    ),
    ...options,
  })
}

useCeleryTaskQuery.getKey = (variables: Types.CeleryTaskQueryVariables) => [
  "CeleryTask",
  variables,
]

useCeleryTaskQuery.rootKey = "CeleryTask"

export const useSuspenseCeleryTaskQuery = <
  TData = Types.CeleryTaskQuery,
  TError = unknown,
>(
  variables: Types.CeleryTaskQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CeleryTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.CeleryTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CeleryTaskQuery, TError, TData>({
    queryKey: ["CeleryTask", variables],
    queryFn: publicFetcher<Types.CeleryTaskQuery, Types.CeleryTaskQueryVariables>(
      CeleryTaskDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCeleryTaskQuery.getKey = (variables: Types.CeleryTaskQueryVariables) => [
  "CeleryTask",
  variables,
]

useSuspenseCeleryTaskQuery.rootKey = "CeleryTask"

export const useInfiniteCeleryTaskQuery = <
  TData = InfiniteData<Types.CeleryTaskQuery>,
  TError = unknown,
>(
  variables: Types.CeleryTaskQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CeleryTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.CeleryTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CeleryTaskQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CeleryTask.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CeleryTaskQuery, Types.CeleryTaskQueryVariables>(
            CeleryTaskDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCeleryTaskQuery.getKey = (variables: Types.CeleryTaskQueryVariables) => [
  "CeleryTask.infinite",
  variables,
]

useInfiniteCeleryTaskQuery.rootKey = "CeleryTask.infinite"

export const useSuspenseInfiniteCeleryTaskQuery = <
  TData = InfiniteData<Types.CeleryTaskQuery>,
  TError = unknown,
>(
  variables: Types.CeleryTaskQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CeleryTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CeleryTaskQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CeleryTaskQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CeleryTask.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CeleryTaskQuery, Types.CeleryTaskQueryVariables>(
            CeleryTaskDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCeleryTaskQuery.getKey = (
  variables: Types.CeleryTaskQueryVariables,
) => ["CeleryTask.infinite", variables]

useSuspenseInfiniteCeleryTaskQuery.rootKey = "CeleryTask.infinite"

useCeleryTaskQuery.fetcher = (
  variables: Types.CeleryTaskQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CeleryTaskQuery, Types.CeleryTaskQueryVariables>(
    CeleryTaskDocument,
    variables,
    options,
  )

export const CurrentImpersonationSessionDocument = new TypedDocumentString(`
    query CurrentImpersonationSession {
  impersonations {
    current {
      adminUser {
        pid
        email
        name
      }
      targetUser {
        pid
        email
        name
      }
    }
  }
}
    `)

export const useCurrentImpersonationSessionQuery = <
  TData = Types.CurrentImpersonationSessionQuery,
  TError = unknown,
>(
  variables?: Types.CurrentImpersonationSessionQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CurrentImpersonationSessionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CurrentImpersonationSessionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CurrentImpersonationSessionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentImpersonationSession"]
        : ["CurrentImpersonationSession", variables],
    queryFn: publicFetcher<
      Types.CurrentImpersonationSessionQuery,
      Types.CurrentImpersonationSessionQueryVariables
    >(CurrentImpersonationSessionDocument, variables),
    ...options,
  })
}

useCurrentImpersonationSessionQuery.getKey = (
  variables?: Types.CurrentImpersonationSessionQueryVariables,
) =>
  variables === undefined
    ? ["CurrentImpersonationSession"]
    : ["CurrentImpersonationSession", variables]

useCurrentImpersonationSessionQuery.rootKey = "CurrentImpersonationSession"

export const useSuspenseCurrentImpersonationSessionQuery = <
  TData = Types.CurrentImpersonationSessionQuery,
  TError = unknown,
>(
  variables?: Types.CurrentImpersonationSessionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentImpersonationSessionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentImpersonationSessionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentImpersonationSessionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentImpersonationSession"]
        : ["CurrentImpersonationSession", variables],
    queryFn: publicFetcher<
      Types.CurrentImpersonationSessionQuery,
      Types.CurrentImpersonationSessionQueryVariables
    >(CurrentImpersonationSessionDocument, variables),
    ...options,
  })
}

useSuspenseCurrentImpersonationSessionQuery.getKey = (
  variables?: Types.CurrentImpersonationSessionQueryVariables,
) =>
  variables === undefined
    ? ["CurrentImpersonationSession"]
    : ["CurrentImpersonationSession", variables]

useSuspenseCurrentImpersonationSessionQuery.rootKey = "CurrentImpersonationSession"

export const useInfiniteCurrentImpersonationSessionQuery = <
  TData = InfiniteData<Types.CurrentImpersonationSessionQuery>,
  TError = unknown,
>(
  variables: Types.CurrentImpersonationSessionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentImpersonationSessionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentImpersonationSessionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentImpersonationSessionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentImpersonationSession.infinite"]
            : ["CurrentImpersonationSession.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentImpersonationSessionQuery,
            Types.CurrentImpersonationSessionQueryVariables
          >(CurrentImpersonationSessionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentImpersonationSessionQuery.getKey = (
  variables?: Types.CurrentImpersonationSessionQueryVariables,
) =>
  variables === undefined
    ? ["CurrentImpersonationSession.infinite"]
    : ["CurrentImpersonationSession.infinite", variables]

useInfiniteCurrentImpersonationSessionQuery.rootKey =
  "CurrentImpersonationSession.infinite"

export const useSuspenseInfiniteCurrentImpersonationSessionQuery = <
  TData = InfiniteData<Types.CurrentImpersonationSessionQuery>,
  TError = unknown,
>(
  variables: Types.CurrentImpersonationSessionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.CurrentImpersonationSessionQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentImpersonationSessionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.CurrentImpersonationSessionQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentImpersonationSession.infinite"]
            : ["CurrentImpersonationSession.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentImpersonationSessionQuery,
            Types.CurrentImpersonationSessionQueryVariables
          >(CurrentImpersonationSessionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentImpersonationSessionQuery.getKey = (
  variables?: Types.CurrentImpersonationSessionQueryVariables,
) =>
  variables === undefined
    ? ["CurrentImpersonationSession.infinite"]
    : ["CurrentImpersonationSession.infinite", variables]

useSuspenseInfiniteCurrentImpersonationSessionQuery.rootKey =
  "CurrentImpersonationSession.infinite"

useCurrentImpersonationSessionQuery.fetcher = (
  variables?: Types.CurrentImpersonationSessionQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CurrentImpersonationSessionQuery,
    Types.CurrentImpersonationSessionQueryVariables
  >(CurrentImpersonationSessionDocument, variables, options)

export const ApiKeysDocument = new TypedDocumentString(`
    query ApiKeys {
  apiKeys {
    currentUserKeys {
      id
      pid
      label
      isEnabled
      createdAt
      expiresAt
    }
  }
}
    `)

export const useApiKeysQuery = <TData = Types.ApiKeysQuery, TError = unknown>(
  variables?: Types.ApiKeysQueryVariables,
  options?: Omit<UseQueryOptions<Types.ApiKeysQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ApiKeysQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ApiKeysQuery, TError, TData>({
    queryKey: variables === undefined ? ["ApiKeys"] : ["ApiKeys", variables],
    queryFn: publicFetcher<Types.ApiKeysQuery, Types.ApiKeysQueryVariables>(
      ApiKeysDocument,
      variables,
    ),
    ...options,
  })
}

useApiKeysQuery.getKey = (variables?: Types.ApiKeysQueryVariables) =>
  variables === undefined ? ["ApiKeys"] : ["ApiKeys", variables]

useApiKeysQuery.rootKey = "ApiKeys"

export const useSuspenseApiKeysQuery = <TData = Types.ApiKeysQuery, TError = unknown>(
  variables?: Types.ApiKeysQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ApiKeysQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.ApiKeysQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ApiKeysQuery, TError, TData>({
    queryKey: variables === undefined ? ["ApiKeys"] : ["ApiKeys", variables],
    queryFn: publicFetcher<Types.ApiKeysQuery, Types.ApiKeysQueryVariables>(
      ApiKeysDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseApiKeysQuery.getKey = (variables?: Types.ApiKeysQueryVariables) =>
  variables === undefined ? ["ApiKeys"] : ["ApiKeys", variables]

useSuspenseApiKeysQuery.rootKey = "ApiKeys"

export const useInfiniteApiKeysQuery = <
  TData = InfiniteData<Types.ApiKeysQuery>,
  TError = unknown,
>(
  variables: Types.ApiKeysQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ApiKeysQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.ApiKeysQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ApiKeysQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ApiKeys.infinite"]
            : ["ApiKeys.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ApiKeysQuery, Types.ApiKeysQueryVariables>(
            ApiKeysDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteApiKeysQuery.getKey = (variables?: Types.ApiKeysQueryVariables) =>
  variables === undefined ? ["ApiKeys.infinite"] : ["ApiKeys.infinite", variables]

useInfiniteApiKeysQuery.rootKey = "ApiKeys.infinite"

export const useSuspenseInfiniteApiKeysQuery = <
  TData = InfiniteData<Types.ApiKeysQuery>,
  TError = unknown,
>(
  variables: Types.ApiKeysQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ApiKeysQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ApiKeysQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ApiKeysQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ApiKeys.infinite"]
            : ["ApiKeys.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ApiKeysQuery, Types.ApiKeysQueryVariables>(
            ApiKeysDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteApiKeysQuery.getKey = (variables?: Types.ApiKeysQueryVariables) =>
  variables === undefined ? ["ApiKeys.infinite"] : ["ApiKeys.infinite", variables]

useSuspenseInfiniteApiKeysQuery.rootKey = "ApiKeys.infinite"

useApiKeysQuery.fetcher = (
  variables?: Types.ApiKeysQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ApiKeysQuery, Types.ApiKeysQueryVariables>(
    ApiKeysDocument,
    variables,
    options,
  )

export const HostAwardPdfDocument = new TypedDocumentString(`
    query HostAwardPdf($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    recvr {
      name
    }
    shortPricingNote
    rows {
      site {
        pid
        name
        streetAddress
        city
        state
        zipCode
      }
      energyProject {
        pid
      }
      awardedFinancier {
        slug
        name
        orgUsers {
          user {
            name
          }
          userRoles {
            role
          }
        }
      }
      awardedBid {
        cs {
          pid
          name
          roofRentY1Usd
          roofRentEscalator
          termYears
          itcPercentageAssumption
          roofReplacementYear
          incentivesNotes
          interconnectionCostUsdPerWatt
          interconnectionCostUtilityMinUsd
          interconnectionCostUtilityMaxUsd
          interconnectionCostNonUtilityUsd
          estimatedAwardDate
          estimatedNtp
          estimatedCod
          systemLite {
            totalPvSizeKw
            pvSpecs {
              pvTypes
              sizeKwDc
              sizeKwAc
            }
          }
          result {
            roofRent {
              lifetimeUsd
              npv
            }
          }
        }
        ppa {
          pid
          name
          roofRentY1Usd
          roofRentEscalator
          termYears
          itcPercentageAssumption
          roofReplacementYear
          incentivesNotes
          interconnectionCostUsdPerWatt
          interconnectionCostUtilityMinUsd
          interconnectionCostUtilityMaxUsd
          interconnectionCostNonUtilityUsd
          estimatedNtp
          estimatedCod
          system {
            totalPvSizeKw
            pvSpecs {
              pvTypes
              sizeKwDc
              sizeKwAc
            }
          }
          result {
            buildingOwner {
              noiLifetime
            }
          }
        }
      }
    }
  }
}
    `)

export const useHostAwardPdfQuery = <TData = Types.HostAwardPdfQuery, TError = unknown>(
  variables: Types.HostAwardPdfQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HostAwardPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HostAwardPdfQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HostAwardPdfQuery, TError, TData>({
    queryKey: ["HostAwardPdf", variables],
    queryFn: publicFetcher<Types.HostAwardPdfQuery, Types.HostAwardPdfQueryVariables>(
      HostAwardPdfDocument,
      variables,
    ),
    ...options,
  })
}

useHostAwardPdfQuery.getKey = (variables: Types.HostAwardPdfQueryVariables) => [
  "HostAwardPdf",
  variables,
]

useHostAwardPdfQuery.rootKey = "HostAwardPdf"

export const useSuspenseHostAwardPdfQuery = <
  TData = Types.HostAwardPdfQuery,
  TError = unknown,
>(
  variables: Types.HostAwardPdfQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HostAwardPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HostAwardPdfQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HostAwardPdfQuery, TError, TData>({
    queryKey: ["HostAwardPdf", variables],
    queryFn: publicFetcher<Types.HostAwardPdfQuery, Types.HostAwardPdfQueryVariables>(
      HostAwardPdfDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseHostAwardPdfQuery.getKey = (variables: Types.HostAwardPdfQueryVariables) => [
  "HostAwardPdf",
  variables,
]

useSuspenseHostAwardPdfQuery.rootKey = "HostAwardPdf"

export const useInfiniteHostAwardPdfQuery = <
  TData = InfiniteData<Types.HostAwardPdfQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardPdfQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HostAwardPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HostAwardPdfQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HostAwardPdfQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardPdf.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.HostAwardPdfQuery, Types.HostAwardPdfQueryVariables>(
            HostAwardPdfDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHostAwardPdfQuery.getKey = (variables: Types.HostAwardPdfQueryVariables) => [
  "HostAwardPdf.infinite",
  variables,
]

useInfiniteHostAwardPdfQuery.rootKey = "HostAwardPdf.infinite"

export const useSuspenseInfiniteHostAwardPdfQuery = <
  TData = InfiniteData<Types.HostAwardPdfQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardPdfQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HostAwardPdfQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HostAwardPdfQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HostAwardPdfQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardPdf.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.HostAwardPdfQuery, Types.HostAwardPdfQueryVariables>(
            HostAwardPdfDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHostAwardPdfQuery.getKey = (
  variables: Types.HostAwardPdfQueryVariables,
) => ["HostAwardPdf.infinite", variables]

useSuspenseInfiniteHostAwardPdfQuery.rootKey = "HostAwardPdf.infinite"

useHostAwardPdfQuery.fetcher = (
  variables: Types.HostAwardPdfQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HostAwardPdfQuery, Types.HostAwardPdfQueryVariables>(
    HostAwardPdfDocument,
    variables,
    options,
  )

export const SignaturePreviewDocument = new TypedDocumentString(`
    query SignaturePreview($documentPid: String!) {
  signing {
    preview(documentPid: $documentPid) {
      documentPid
      contractType
      canSend
      canMark
      markRefusalReason
      refusalReason
      openRequest {
        state {
          signingPid
          ...SigningStateFields
        }
        copyEmails
        canRemind
        remindRefusalReason
        canVoid
        voidRefusalReason
        canRevoke
        revokeRefusalReason
      }
      developerSignsOnPage
      lumenSignsOnPage
      developerCandidates {
        name
        email
        userPid
      }
      copyCandidates {
        name
        email
        userPid
      }
      defaultLumenSigner {
        name
        email
        userPid
      }
      defaultExpireAfterDays
      defaultExpireWarnDays
    }
  }
}
    fragment SigningStateFields on DocumentSigningState {
  signingPid
  status
  source
  markedBy
  executedOn
  note
  dateSent
  expiresOn
  awaiting {
    name
    hasViewed
  }
  declinedBy {
    name
  }
  signers {
    name
    email
    status
    hasViewed
    signsForLumen
    signaturePage
  }
}`)

export const useSignaturePreviewQuery = <
  TData = Types.SignaturePreviewQuery,
  TError = unknown,
>(
  variables: Types.SignaturePreviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SignaturePreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SignaturePreviewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SignaturePreviewQuery, TError, TData>({
    queryKey: ["SignaturePreview", variables],
    queryFn: publicFetcher<
      Types.SignaturePreviewQuery,
      Types.SignaturePreviewQueryVariables
    >(SignaturePreviewDocument, variables),
    ...options,
  })
}

useSignaturePreviewQuery.getKey = (variables: Types.SignaturePreviewQueryVariables) => [
  "SignaturePreview",
  variables,
]

useSignaturePreviewQuery.rootKey = "SignaturePreview"

export const useSuspenseSignaturePreviewQuery = <
  TData = Types.SignaturePreviewQuery,
  TError = unknown,
>(
  variables: Types.SignaturePreviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SignaturePreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SignaturePreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SignaturePreviewQuery, TError, TData>({
    queryKey: ["SignaturePreview", variables],
    queryFn: publicFetcher<
      Types.SignaturePreviewQuery,
      Types.SignaturePreviewQueryVariables
    >(SignaturePreviewDocument, variables),
    ...options,
  })
}

useSuspenseSignaturePreviewQuery.getKey = (
  variables: Types.SignaturePreviewQueryVariables,
) => ["SignaturePreview", variables]

useSuspenseSignaturePreviewQuery.rootKey = "SignaturePreview"

export const useInfiniteSignaturePreviewQuery = <
  TData = InfiniteData<Types.SignaturePreviewQuery>,
  TError = unknown,
>(
  variables: Types.SignaturePreviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SignaturePreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SignaturePreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SignaturePreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SignaturePreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SignaturePreviewQuery,
            Types.SignaturePreviewQueryVariables
          >(SignaturePreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSignaturePreviewQuery.getKey = (
  variables: Types.SignaturePreviewQueryVariables,
) => ["SignaturePreview.infinite", variables]

useInfiniteSignaturePreviewQuery.rootKey = "SignaturePreview.infinite"

export const useSuspenseInfiniteSignaturePreviewQuery = <
  TData = InfiniteData<Types.SignaturePreviewQuery>,
  TError = unknown,
>(
  variables: Types.SignaturePreviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SignaturePreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SignaturePreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SignaturePreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SignaturePreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SignaturePreviewQuery,
            Types.SignaturePreviewQueryVariables
          >(SignaturePreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSignaturePreviewQuery.getKey = (
  variables: Types.SignaturePreviewQueryVariables,
) => ["SignaturePreview.infinite", variables]

useSuspenseInfiniteSignaturePreviewQuery.rootKey = "SignaturePreview.infinite"

useSignaturePreviewQuery.fetcher = (
  variables: Types.SignaturePreviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SignaturePreviewQuery, Types.SignaturePreviewQueryVariables>(
    SignaturePreviewDocument,
    variables,
    options,
  )

export const OvertureBuildingsLayerDocument = new TypedDocumentString(`
    query OvertureBuildingsLayer($filters: OvertureBuildingFilterInput!) {
  referenceMaps {
    overtureBuildings {
      layer(filters: $filters) {
        shapeGeojson
        primaryName
        buildingClass
        addressLine1
        city
        state
        zipCode
        county
        areaSqft
      }
    }
  }
}
    `)

export const useOvertureBuildingsLayerQuery = <
  TData = Types.OvertureBuildingsLayerQuery,
  TError = unknown,
>(
  variables: Types.OvertureBuildingsLayerQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OvertureBuildingsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OvertureBuildingsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OvertureBuildingsLayerQuery, TError, TData>({
    queryKey: ["OvertureBuildingsLayer", variables],
    queryFn: publicFetcher<
      Types.OvertureBuildingsLayerQuery,
      Types.OvertureBuildingsLayerQueryVariables
    >(OvertureBuildingsLayerDocument, variables),
    ...options,
  })
}

useOvertureBuildingsLayerQuery.getKey = (
  variables: Types.OvertureBuildingsLayerQueryVariables,
) => ["OvertureBuildingsLayer", variables]

useOvertureBuildingsLayerQuery.rootKey = "OvertureBuildingsLayer"

export const useSuspenseOvertureBuildingsLayerQuery = <
  TData = Types.OvertureBuildingsLayerQuery,
  TError = unknown,
>(
  variables: Types.OvertureBuildingsLayerQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OvertureBuildingsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OvertureBuildingsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OvertureBuildingsLayerQuery, TError, TData>({
    queryKey: ["OvertureBuildingsLayer", variables],
    queryFn: publicFetcher<
      Types.OvertureBuildingsLayerQuery,
      Types.OvertureBuildingsLayerQueryVariables
    >(OvertureBuildingsLayerDocument, variables),
    ...options,
  })
}

useSuspenseOvertureBuildingsLayerQuery.getKey = (
  variables: Types.OvertureBuildingsLayerQueryVariables,
) => ["OvertureBuildingsLayer", variables]

useSuspenseOvertureBuildingsLayerQuery.rootKey = "OvertureBuildingsLayer"

export const useInfiniteOvertureBuildingsLayerQuery = <
  TData = InfiniteData<Types.OvertureBuildingsLayerQuery>,
  TError = unknown,
>(
  variables: Types.OvertureBuildingsLayerQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OvertureBuildingsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OvertureBuildingsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OvertureBuildingsLayerQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OvertureBuildingsLayer.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OvertureBuildingsLayerQuery,
            Types.OvertureBuildingsLayerQueryVariables
          >(OvertureBuildingsLayerDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOvertureBuildingsLayerQuery.getKey = (
  variables: Types.OvertureBuildingsLayerQueryVariables,
) => ["OvertureBuildingsLayer.infinite", variables]

useInfiniteOvertureBuildingsLayerQuery.rootKey = "OvertureBuildingsLayer.infinite"

export const useSuspenseInfiniteOvertureBuildingsLayerQuery = <
  TData = InfiniteData<Types.OvertureBuildingsLayerQuery>,
  TError = unknown,
>(
  variables: Types.OvertureBuildingsLayerQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OvertureBuildingsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OvertureBuildingsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OvertureBuildingsLayerQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OvertureBuildingsLayer.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OvertureBuildingsLayerQuery,
            Types.OvertureBuildingsLayerQueryVariables
          >(OvertureBuildingsLayerDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOvertureBuildingsLayerQuery.getKey = (
  variables: Types.OvertureBuildingsLayerQueryVariables,
) => ["OvertureBuildingsLayer.infinite", variables]

useSuspenseInfiniteOvertureBuildingsLayerQuery.rootKey =
  "OvertureBuildingsLayer.infinite"

useOvertureBuildingsLayerQuery.fetcher = (
  variables: Types.OvertureBuildingsLayerQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OvertureBuildingsLayerQuery,
    Types.OvertureBuildingsLayerQueryVariables
  >(OvertureBuildingsLayerDocument, variables, options)

export const ParcelsLayerDocument = new TypedDocumentString(`
    query ParcelsLayer($filters: GisParcelFilterInput!) {
  referenceMaps {
    parcels {
      layer(filters: $filters) {
        id
        shapeGeojson
        sqft
      }
    }
  }
}
    `)

export const useParcelsLayerQuery = <TData = Types.ParcelsLayerQuery, TError = unknown>(
  variables: Types.ParcelsLayerQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ParcelsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ParcelsLayerQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ParcelsLayerQuery, TError, TData>({
    queryKey: ["ParcelsLayer", variables],
    queryFn: publicFetcher<Types.ParcelsLayerQuery, Types.ParcelsLayerQueryVariables>(
      ParcelsLayerDocument,
      variables,
    ),
    ...options,
  })
}

useParcelsLayerQuery.getKey = (variables: Types.ParcelsLayerQueryVariables) => [
  "ParcelsLayer",
  variables,
]

useParcelsLayerQuery.rootKey = "ParcelsLayer"

export const useSuspenseParcelsLayerQuery = <
  TData = Types.ParcelsLayerQuery,
  TError = unknown,
>(
  variables: Types.ParcelsLayerQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ParcelsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ParcelsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ParcelsLayerQuery, TError, TData>({
    queryKey: ["ParcelsLayer", variables],
    queryFn: publicFetcher<Types.ParcelsLayerQuery, Types.ParcelsLayerQueryVariables>(
      ParcelsLayerDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseParcelsLayerQuery.getKey = (variables: Types.ParcelsLayerQueryVariables) => [
  "ParcelsLayer",
  variables,
]

useSuspenseParcelsLayerQuery.rootKey = "ParcelsLayer"

export const useInfiniteParcelsLayerQuery = <
  TData = InfiniteData<Types.ParcelsLayerQuery>,
  TError = unknown,
>(
  variables: Types.ParcelsLayerQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ParcelsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ParcelsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ParcelsLayerQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ParcelsLayer.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ParcelsLayerQuery, Types.ParcelsLayerQueryVariables>(
            ParcelsLayerDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteParcelsLayerQuery.getKey = (variables: Types.ParcelsLayerQueryVariables) => [
  "ParcelsLayer.infinite",
  variables,
]

useInfiniteParcelsLayerQuery.rootKey = "ParcelsLayer.infinite"

export const useSuspenseInfiniteParcelsLayerQuery = <
  TData = InfiniteData<Types.ParcelsLayerQuery>,
  TError = unknown,
>(
  variables: Types.ParcelsLayerQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ParcelsLayerQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ParcelsLayerQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ParcelsLayerQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ParcelsLayer.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ParcelsLayerQuery, Types.ParcelsLayerQueryVariables>(
            ParcelsLayerDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteParcelsLayerQuery.getKey = (
  variables: Types.ParcelsLayerQueryVariables,
) => ["ParcelsLayer.infinite", variables]

useSuspenseInfiniteParcelsLayerQuery.rootKey = "ParcelsLayer.infinite"

useParcelsLayerQuery.fetcher = (
  variables: Types.ParcelsLayerQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ParcelsLayerQuery, Types.ParcelsLayerQueryVariables>(
    ParcelsLayerDocument,
    variables,
    options,
  )

export const MeetingNotesTableViewDocument = new TypedDocumentString(`
    query MeetingNotesTableView($orgSlug: String!, $filterBy: MeetingNotesTableFilter, $sortBy: [MeetingNotesTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    pid
    meetingNotes {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `)

export const useMeetingNotesTableViewQuery = <
  TData = Types.MeetingNotesTableViewQuery,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MeetingNotesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MeetingNotesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MeetingNotesTableViewQuery, TError, TData>({
    queryKey: ["MeetingNotesTableView", variables],
    queryFn: publicFetcher<
      Types.MeetingNotesTableViewQuery,
      Types.MeetingNotesTableViewQueryVariables
    >(MeetingNotesTableViewDocument, variables),
    ...options,
  })
}

useMeetingNotesTableViewQuery.getKey = (
  variables: Types.MeetingNotesTableViewQueryVariables,
) => ["MeetingNotesTableView", variables]

useMeetingNotesTableViewQuery.rootKey = "MeetingNotesTableView"

export const useSuspenseMeetingNotesTableViewQuery = <
  TData = Types.MeetingNotesTableViewQuery,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeetingNotesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MeetingNotesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeetingNotesTableViewQuery, TError, TData>({
    queryKey: ["MeetingNotesTableView", variables],
    queryFn: publicFetcher<
      Types.MeetingNotesTableViewQuery,
      Types.MeetingNotesTableViewQueryVariables
    >(MeetingNotesTableViewDocument, variables),
    ...options,
  })
}

useSuspenseMeetingNotesTableViewQuery.getKey = (
  variables: Types.MeetingNotesTableViewQueryVariables,
) => ["MeetingNotesTableView", variables]

useSuspenseMeetingNotesTableViewQuery.rootKey = "MeetingNotesTableView"

export const useInfiniteMeetingNotesTableViewQuery = <
  TData = InfiniteData<Types.MeetingNotesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeetingNotesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MeetingNotesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeetingNotesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNotesTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNotesTableViewQuery,
            Types.MeetingNotesTableViewQueryVariables
          >(MeetingNotesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeetingNotesTableViewQuery.getKey = (
  variables: Types.MeetingNotesTableViewQueryVariables,
) => ["MeetingNotesTableView.infinite", variables]

useInfiniteMeetingNotesTableViewQuery.rootKey = "MeetingNotesTableView.infinite"

export const useSuspenseInfiniteMeetingNotesTableViewQuery = <
  TData = InfiniteData<Types.MeetingNotesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeetingNotesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeetingNotesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeetingNotesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNotesTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNotesTableViewQuery,
            Types.MeetingNotesTableViewQueryVariables
          >(MeetingNotesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeetingNotesTableViewQuery.getKey = (
  variables: Types.MeetingNotesTableViewQueryVariables,
) => ["MeetingNotesTableView.infinite", variables]

useSuspenseInfiniteMeetingNotesTableViewQuery.rootKey = "MeetingNotesTableView.infinite"

useMeetingNotesTableViewQuery.fetcher = (
  variables: Types.MeetingNotesTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MeetingNotesTableViewQuery,
    Types.MeetingNotesTableViewQueryVariables
  >(MeetingNotesTableViewDocument, variables, options)

export const MeetingNotesTableRowsDocument = new TypedDocumentString(`
    query MeetingNotesTableRows($orgSlug: String!, $keys: [String!]!) {
  orgBySlug(slug: $orgSlug) {
    meetingNotes {
      rows(keys: $keys) {
        pid
        title
        meetingDate
        attendees
        source
        createdAt
        lastExtractionAt
      }
    }
  }
}
    `)

export const useMeetingNotesTableRowsQuery = <
  TData = Types.MeetingNotesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MeetingNotesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MeetingNotesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MeetingNotesTableRowsQuery, TError, TData>({
    queryKey: ["MeetingNotesTableRows", variables],
    queryFn: publicFetcher<
      Types.MeetingNotesTableRowsQuery,
      Types.MeetingNotesTableRowsQueryVariables
    >(MeetingNotesTableRowsDocument, variables),
    ...options,
  })
}

useMeetingNotesTableRowsQuery.getKey = (
  variables: Types.MeetingNotesTableRowsQueryVariables,
) => ["MeetingNotesTableRows", variables]

useMeetingNotesTableRowsQuery.rootKey = "MeetingNotesTableRows"

export const useSuspenseMeetingNotesTableRowsQuery = <
  TData = Types.MeetingNotesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeetingNotesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MeetingNotesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeetingNotesTableRowsQuery, TError, TData>({
    queryKey: ["MeetingNotesTableRows", variables],
    queryFn: publicFetcher<
      Types.MeetingNotesTableRowsQuery,
      Types.MeetingNotesTableRowsQueryVariables
    >(MeetingNotesTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseMeetingNotesTableRowsQuery.getKey = (
  variables: Types.MeetingNotesTableRowsQueryVariables,
) => ["MeetingNotesTableRows", variables]

useSuspenseMeetingNotesTableRowsQuery.rootKey = "MeetingNotesTableRows"

export const useInfiniteMeetingNotesTableRowsQuery = <
  TData = InfiniteData<Types.MeetingNotesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeetingNotesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MeetingNotesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeetingNotesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNotesTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNotesTableRowsQuery,
            Types.MeetingNotesTableRowsQueryVariables
          >(MeetingNotesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeetingNotesTableRowsQuery.getKey = (
  variables: Types.MeetingNotesTableRowsQueryVariables,
) => ["MeetingNotesTableRows.infinite", variables]

useInfiniteMeetingNotesTableRowsQuery.rootKey = "MeetingNotesTableRows.infinite"

export const useSuspenseInfiniteMeetingNotesTableRowsQuery = <
  TData = InfiniteData<Types.MeetingNotesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNotesTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeetingNotesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeetingNotesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeetingNotesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNotesTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNotesTableRowsQuery,
            Types.MeetingNotesTableRowsQueryVariables
          >(MeetingNotesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeetingNotesTableRowsQuery.getKey = (
  variables: Types.MeetingNotesTableRowsQueryVariables,
) => ["MeetingNotesTableRows.infinite", variables]

useSuspenseInfiniteMeetingNotesTableRowsQuery.rootKey = "MeetingNotesTableRows.infinite"

useMeetingNotesTableRowsQuery.fetcher = (
  variables: Types.MeetingNotesTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MeetingNotesTableRowsQuery,
    Types.MeetingNotesTableRowsQueryVariables
  >(MeetingNotesTableRowsDocument, variables, options)

export const MeetingNoteDetailDocument = new TypedDocumentString(`
    query MeetingNoteDetail($orgSlug: String!, $pid: String!) {
  orgBySlug(slug: $orgSlug) {
    meetingNote(pid: $pid) {
      pid
      title
      meetingDate
      attendees
      source
      content
      text
      notionUrl
      createdAt
      updatedAt
      notionLastEditedAt
      createdBy {
        pid
        name
        email
      }
      projectOptions {
        pid
        siteStreetAddress
        siteName
        status
      }
      extractionResults {
        pid
        createdAt
      }
      latestExtractionResult {
        ...ExtractionResultDetail
      }
    }
  }
}
    fragment ExtractionResultDetail on MeetingNoteExtractionResult {
  pid
  createdAt
  processedAt
  taskCreations {
    pid
    status
    description
    projectStage
    deadline
    assigneeUser {
      name
      email
      isAdmin
    }
    assigneeGroup
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskAssigneeChanges {
    pid
    status
    task {
      pid
      description
    }
    assigneeUser {
      name
      email
      isAdmin
    }
    assigneeGroup
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskDueDateChanges {
    pid
    status
    task {
      pid
      description
    }
    deadline
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskCompletionChanges {
    pid
    status
    task {
      pid
      description
    }
    complete
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskAddNotes {
    pid
    status
    task {
      pid
      description
    }
    noteText
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskDeletions {
    pid
    status
    task {
      pid
      description
    }
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  milestoneUpdates {
    pid
    status
    milestoneType
    milestoneKind
    milestoneDate
    currentEstimatedDate
    currentActualDate
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
}`)

export const useMeetingNoteDetailQuery = <
  TData = Types.MeetingNoteDetailQuery,
  TError = unknown,
>(
  variables: Types.MeetingNoteDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MeetingNoteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.MeetingNoteDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MeetingNoteDetailQuery, TError, TData>({
    queryKey: ["MeetingNoteDetail", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteDetailQuery,
      Types.MeetingNoteDetailQueryVariables
    >(MeetingNoteDetailDocument, variables),
    ...options,
  })
}

useMeetingNoteDetailQuery.getKey = (
  variables: Types.MeetingNoteDetailQueryVariables,
) => ["MeetingNoteDetail", variables]

useMeetingNoteDetailQuery.rootKey = "MeetingNoteDetail"

export const useSuspenseMeetingNoteDetailQuery = <
  TData = Types.MeetingNoteDetailQuery,
  TError = unknown,
>(
  variables: Types.MeetingNoteDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeetingNoteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MeetingNoteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeetingNoteDetailQuery, TError, TData>({
    queryKey: ["MeetingNoteDetail", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteDetailQuery,
      Types.MeetingNoteDetailQueryVariables
    >(MeetingNoteDetailDocument, variables),
    ...options,
  })
}

useSuspenseMeetingNoteDetailQuery.getKey = (
  variables: Types.MeetingNoteDetailQueryVariables,
) => ["MeetingNoteDetail", variables]

useSuspenseMeetingNoteDetailQuery.rootKey = "MeetingNoteDetail"

export const useInfiniteMeetingNoteDetailQuery = <
  TData = InfiniteData<Types.MeetingNoteDetailQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeetingNoteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MeetingNoteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeetingNoteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNoteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteDetailQuery,
            Types.MeetingNoteDetailQueryVariables
          >(MeetingNoteDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeetingNoteDetailQuery.getKey = (
  variables: Types.MeetingNoteDetailQueryVariables,
) => ["MeetingNoteDetail.infinite", variables]

useInfiniteMeetingNoteDetailQuery.rootKey = "MeetingNoteDetail.infinite"

export const useSuspenseInfiniteMeetingNoteDetailQuery = <
  TData = InfiniteData<Types.MeetingNoteDetailQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeetingNoteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeetingNoteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeetingNoteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNoteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteDetailQuery,
            Types.MeetingNoteDetailQueryVariables
          >(MeetingNoteDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeetingNoteDetailQuery.getKey = (
  variables: Types.MeetingNoteDetailQueryVariables,
) => ["MeetingNoteDetail.infinite", variables]

useSuspenseInfiniteMeetingNoteDetailQuery.rootKey = "MeetingNoteDetail.infinite"

useMeetingNoteDetailQuery.fetcher = (
  variables: Types.MeetingNoteDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MeetingNoteDetailQuery, Types.MeetingNoteDetailQueryVariables>(
    MeetingNoteDetailDocument,
    variables,
    options,
  )

export const MeetingNoteExtractionsDocument = new TypedDocumentString(`
    query MeetingNoteExtractions($orgSlug: String!, $pid: String!) {
  orgBySlug(slug: $orgSlug) {
    meetingNote(pid: $pid) {
      pid
      title
      extractionResults {
        pid
        createdAt
        processedAt
      }
    }
  }
}
    `)

export const useMeetingNoteExtractionsQuery = <
  TData = Types.MeetingNoteExtractionsQuery,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MeetingNoteExtractionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MeetingNoteExtractionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MeetingNoteExtractionsQuery, TError, TData>({
    queryKey: ["MeetingNoteExtractions", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteExtractionsQuery,
      Types.MeetingNoteExtractionsQueryVariables
    >(MeetingNoteExtractionsDocument, variables),
    ...options,
  })
}

useMeetingNoteExtractionsQuery.getKey = (
  variables: Types.MeetingNoteExtractionsQueryVariables,
) => ["MeetingNoteExtractions", variables]

useMeetingNoteExtractionsQuery.rootKey = "MeetingNoteExtractions"

export const useSuspenseMeetingNoteExtractionsQuery = <
  TData = Types.MeetingNoteExtractionsQuery,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeetingNoteExtractionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MeetingNoteExtractionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeetingNoteExtractionsQuery, TError, TData>({
    queryKey: ["MeetingNoteExtractions", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteExtractionsQuery,
      Types.MeetingNoteExtractionsQueryVariables
    >(MeetingNoteExtractionsDocument, variables),
    ...options,
  })
}

useSuspenseMeetingNoteExtractionsQuery.getKey = (
  variables: Types.MeetingNoteExtractionsQueryVariables,
) => ["MeetingNoteExtractions", variables]

useSuspenseMeetingNoteExtractionsQuery.rootKey = "MeetingNoteExtractions"

export const useInfiniteMeetingNoteExtractionsQuery = <
  TData = InfiniteData<Types.MeetingNoteExtractionsQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeetingNoteExtractionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MeetingNoteExtractionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeetingNoteExtractionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNoteExtractions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteExtractionsQuery,
            Types.MeetingNoteExtractionsQueryVariables
          >(MeetingNoteExtractionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeetingNoteExtractionsQuery.getKey = (
  variables: Types.MeetingNoteExtractionsQueryVariables,
) => ["MeetingNoteExtractions.infinite", variables]

useInfiniteMeetingNoteExtractionsQuery.rootKey = "MeetingNoteExtractions.infinite"

export const useSuspenseInfiniteMeetingNoteExtractionsQuery = <
  TData = InfiniteData<Types.MeetingNoteExtractionsQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeetingNoteExtractionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeetingNoteExtractionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeetingNoteExtractionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeetingNoteExtractions.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteExtractionsQuery,
            Types.MeetingNoteExtractionsQueryVariables
          >(MeetingNoteExtractionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeetingNoteExtractionsQuery.getKey = (
  variables: Types.MeetingNoteExtractionsQueryVariables,
) => ["MeetingNoteExtractions.infinite", variables]

useSuspenseInfiniteMeetingNoteExtractionsQuery.rootKey =
  "MeetingNoteExtractions.infinite"

useMeetingNoteExtractionsQuery.fetcher = (
  variables: Types.MeetingNoteExtractionsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MeetingNoteExtractionsQuery,
    Types.MeetingNoteExtractionsQueryVariables
  >(MeetingNoteExtractionsDocument, variables, options)

export const MeetingNoteExtractionDetailDocument = new TypedDocumentString(`
    query MeetingNoteExtractionDetail($orgSlug: String!, $pid: String!, $extractionPid: String!) {
  orgBySlug(slug: $orgSlug) {
    meetingNote(pid: $pid) {
      pid
      title
      text
      projectOptions {
        pid
        siteStreetAddress
        siteName
        status
      }
      extractionResult(pid: $extractionPid) {
        __typename
        ... on MeetingNoteExtractionResult {
          ...ExtractionResultDetail
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    fragment ExtractionResultDetail on MeetingNoteExtractionResult {
  pid
  createdAt
  processedAt
  taskCreations {
    pid
    status
    description
    projectStage
    deadline
    assigneeUser {
      name
      email
      isAdmin
    }
    assigneeGroup
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskAssigneeChanges {
    pid
    status
    task {
      pid
      description
    }
    assigneeUser {
      name
      email
      isAdmin
    }
    assigneeGroup
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskDueDateChanges {
    pid
    status
    task {
      pid
      description
    }
    deadline
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskCompletionChanges {
    pid
    status
    task {
      pid
      description
    }
    complete
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskAddNotes {
    pid
    status
    task {
      pid
      description
    }
    noteText
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  taskDeletions {
    pid
    status
    task {
      pid
      description
    }
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
  milestoneUpdates {
    pid
    status
    milestoneType
    milestoneKind
    milestoneDate
    currentEstimatedDate
    currentActualDate
    citations
    project {
      pid
      siteStreetAddress
      siteName
      status
    }
  }
}`)

export const useMeetingNoteExtractionDetailQuery = <
  TData = Types.MeetingNoteExtractionDetailQuery,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MeetingNoteExtractionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MeetingNoteExtractionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MeetingNoteExtractionDetailQuery, TError, TData>({
    queryKey: ["MeetingNoteExtractionDetail", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteExtractionDetailQuery,
      Types.MeetingNoteExtractionDetailQueryVariables
    >(MeetingNoteExtractionDetailDocument, variables),
    ...options,
  })
}

useMeetingNoteExtractionDetailQuery.getKey = (
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
) => ["MeetingNoteExtractionDetail", variables]

useMeetingNoteExtractionDetailQuery.rootKey = "MeetingNoteExtractionDetail"

export const useSuspenseMeetingNoteExtractionDetailQuery = <
  TData = Types.MeetingNoteExtractionDetailQuery,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeetingNoteExtractionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MeetingNoteExtractionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeetingNoteExtractionDetailQuery, TError, TData>({
    queryKey: ["MeetingNoteExtractionDetail", variables],
    queryFn: publicFetcher<
      Types.MeetingNoteExtractionDetailQuery,
      Types.MeetingNoteExtractionDetailQueryVariables
    >(MeetingNoteExtractionDetailDocument, variables),
    ...options,
  })
}

useSuspenseMeetingNoteExtractionDetailQuery.getKey = (
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
) => ["MeetingNoteExtractionDetail", variables]

useSuspenseMeetingNoteExtractionDetailQuery.rootKey = "MeetingNoteExtractionDetail"

export const useInfiniteMeetingNoteExtractionDetailQuery = <
  TData = InfiniteData<Types.MeetingNoteExtractionDetailQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeetingNoteExtractionDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MeetingNoteExtractionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeetingNoteExtractionDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MeetingNoteExtractionDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteExtractionDetailQuery,
            Types.MeetingNoteExtractionDetailQueryVariables
          >(MeetingNoteExtractionDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeetingNoteExtractionDetailQuery.getKey = (
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
) => ["MeetingNoteExtractionDetail.infinite", variables]

useInfiniteMeetingNoteExtractionDetailQuery.rootKey =
  "MeetingNoteExtractionDetail.infinite"

export const useSuspenseInfiniteMeetingNoteExtractionDetailQuery = <
  TData = InfiniteData<Types.MeetingNoteExtractionDetailQuery>,
  TError = unknown,
>(
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MeetingNoteExtractionDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeetingNoteExtractionDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MeetingNoteExtractionDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MeetingNoteExtractionDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MeetingNoteExtractionDetailQuery,
            Types.MeetingNoteExtractionDetailQueryVariables
          >(MeetingNoteExtractionDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeetingNoteExtractionDetailQuery.getKey = (
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
) => ["MeetingNoteExtractionDetail.infinite", variables]

useSuspenseInfiniteMeetingNoteExtractionDetailQuery.rootKey =
  "MeetingNoteExtractionDetail.infinite"

useMeetingNoteExtractionDetailQuery.fetcher = (
  variables: Types.MeetingNoteExtractionDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MeetingNoteExtractionDetailQuery,
    Types.MeetingNoteExtractionDetailQueryVariables
  >(MeetingNoteExtractionDetailDocument, variables, options)
